// @ts-nocheck
/* tslint:disable */
/* eslint-disable */
/**
 * Business Radar
 * The Business Radar API provides powerful tools to screen & monitor companies using news & compliance, allowing you to integrate real-time insights into your own applications.   #### Getting Started To use this API, you need an API Key. Please contact [support@businessradar.com](mailto:support@businessradar.com) to request your key. Once you have a key, you can enter it by clicking the **Authorize** button on the right to start making requests directly from this documentation.  #### Authentication The API key can be provided through the `Authorization` header when making raw requests. For our SDKs, the API key can also be saved as an environmental variable. In this documentation, each endpoint shows the full request, including headers, when invoked.  #### SDKs We offer officially supported SDKs to help you get started quickly: - **Python**: [businessradar-sdk-python](https://github.com/businessradar/businessradar-sdk-python) - **TypeScript / JavaScript**: [businessradar-sdk-typescript](https://github.com/businessradar/businessradar-sdk-typescript) - **PHP**: [businessradar-sdk-php](https://github.com/businessradar/businessradar-sdk-php)  #### Pagination The API uses cursor-based pagination for high performance and reliability. When a request returns more results than the page limit, a `next_key` is included in the response. To fetch the next set of results, include this `next_key` as a query parameter in your next request. If `next_key` is `null`, it means you have reached the end of the available results. The `total_results` field provides the total count of matches found for your query.  #### Asynchronous Processing Several endpoints in the Business Radar API are asynchronous. This means that once a request is posted, Business Radar processes it in the background. To check the progress and/or retrieve the final result, you can use the equivalent GET endpoint for that resource.  #### Webhooks Webhooks allow Business Radar to push real-time event notifications to your application the moment something changes, without polling. When an event occurs, Business Radar sends an HTTP POST request containing a JSON payload to the URL you registered.  **Payload envelope**  Every delivery body carries an `event_type` field identifying the subscription that triggered it, alongside the event-specific payload under a named field (see the table below):  ```json {\"event_type\": \"compliance_check.status_changed\", \"compliance_check\": { ... }} ```  `event_type` always matches the subscription, and the payload field (e.g. `compliance_check`, `registration`) holds the event data.  **Setup**  Create a webhook and subscribe to one or more event types via the `/ext/v3/webhooks/` endpoints. Each webhook has a secret used to sign deliveries so you can verify they originated from Business Radar.  **Event types**  | Event type | Trigger | Payload field | Field schema | |---|---|---|---| | `compliance_check.status_changed` | Every status update on a compliance check | `compliance_check` | [ComplianceCheckList](#model-ComplianceCheckList) | | `compliance_check.status_completed` | Compliance check reaches `completed` status | `compliance_check` | [ComplianceCheckList](#model-ComplianceCheckList) | | `compliance_check.results.new` | New compliance results identified during monitoring | `compliance_check` | [ComplianceCheckList](#model-ComplianceCheckList) | | `company_registration.status_changed` | Every status update on a company registration | `registration` | [Registration](#model-Registration) | | `company_registration.status_registered` | Registration reaches `registered` status | `registration` | [Registration](#model-Registration) | | `company.updated` | A tracked company\'s attributes change (e.g. a D&B refresh) | `company`, `attribute_changes` | [Company](#model-Company), [CompanyAttributeChange](#model-CompanyAttributeChange) |  `company.updated` subscriptions are **portfolio-scoped**: provide a `portfolio` when subscribing, and the event fires only for changes to companies in that portfolio.  **Payload verification**  Every delivery includes an `X-Signature-SHA256` header of the form `sha256=<hex>`. To verify authenticity, compute `HMAC-SHA256` over the raw request body using your webhook secret and compare it to the value after `sha256=`. Reject any delivery where the signatures do not match.  The secret is shown once when you create the webhook and once each time you call the `regenerate_secret` endpoint. **Rotating the secret immediately invalidates the old one** — update your verification code with the new value before rotating, or deliveries will start failing signature checks.  <details> <summary>Python verification example</summary>  ```python import hashlib import hmac  def verify_signature(raw_body: bytes, secret: str, header: str) -> bool:     \"\"\"Return True if the X-Signature-SHA256 header is valid.\"\"\"     digest = hmac.new(secret.encode(), raw_body, hashlib.sha256)     expected = digest.hexdigest()     received = header.removeprefix(\"sha256=\")     return hmac.compare_digest(expected, received) ```  In a Django view:  ```python import json  def webhook(request):     if not verify_signature(         request.body,         secret=YOUR_WEBHOOK_SECRET,         header=request.headers.get(\"X-Signature-SHA256\", \"\"),     ):         return HttpResponse(status=400)     payload = json.loads(request.body)     event_type = payload[\"event_type\"]     # payload also holds the event-specific field, e.g. payload[\"compliance_check\"]     # process event ... ```  </details>  **Retries**  If your endpoint does not respond with a 2xx status code, Business Radar retries the delivery up to 3 times with exponential back-off. After all retries are exhausted the delivery is marked `failed`. Deliveries are deduplicated — the same event is never delivered more than once per subscription.
 *
 * The version of the OpenAPI document: 0.0.1 (general)
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import { Configuration } from './configuration';
import globalAxios, { AxiosPromise, AxiosInstance, AxiosRequestConfig } from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from './base';

/**
 * * `ADD` - ADD * `EXCLUDE` - EXCLUDE
 * @export
 * @enum {string}
 */

export const ActionEnum = {
    Add: 'ADD',
    Exclude: 'EXCLUDE'
} as const;

export type ActionEnum = typeof ActionEnum[keyof typeof ActionEnum];


/**
 * * `EMAIL` - E-mail * `SMS` - Sms * `PUSH_NOTIFICATION` - Push Notification
 * @export
 * @enum {string}
 */

export const ActionTypeEnum = {
    Email: 'EMAIL',
    Sms: 'SMS',
    PushNotification: 'PUSH_NOTIFICATION'
} as const;

export type ActionTypeEnum = typeof ActionTypeEnum[keyof typeof ActionTypeEnum];


/**
 * * `AF` - Afghanistan * `AX` - Aland Islands * `AL` - Albania * `DZ` - Algeria * `AS` - American Samoa * `AD` - Andorra * `AO` - Angola * `AI` - Anguilla * `AQ` - Antarctica * `AG` - Antigua and Barbuda * `AR` - Argentina * `AM` - Armenia * `AW` - Aruba * `AU` - Australia * `AT` - Austria * `AZ` - Azerbaijan * `BS` - Bahamas * `BH` - Bahrain * `BD` - Bangladesh * `BB` - Barbados * `BY` - Belarus * `BE` - Belgium * `BZ` - Belize * `BJ` - Benin * `BM` - Bermuda * `BT` - Bhutan * `BO` - Bolivia * `BQ` - Bonaire * `BA` - Bosnia and Herzegovina * `BW` - Botswana * `BV` - Bouvet Island * `BR` - Brazil * `IO` - British Indian Ocean Territory * `BN` - Brunei Darussalam * `BG` - Bulgaria * `BF` - Burkina Faso * `BI` - Burundi * `CV` - Cabo Verde * `KH` - Cambodia * `CM` - Cameroon * `CA` - Canada * `KY` - Cayman Islands * `CF` - Central African Republic * `TD` - Chad * `CL` - Chile * `CN` - China * `CX` - Christmas Island * `CC` - Cocos Keeling Islands * `CO` - Colombia * `KM` - Comoros * `CG` - Congo * `CD` - Congo Democratic Republic * `CK` - Cook Islands * `CR` - Costa Rica * `CI` - Cote d\'Ivoire * `HR` - Croatia * `CU` - Cuba * `CW` - Curacao * `CY` - Cyprus * `CZ` - Czechia * `DK` - Denmark * `DJ` - Djibouti * `DM` - Dominica * `DO` - Dominican Republic * `EC` - Ecuador * `EG` - Egypt * `SV` - El Salvador * `GQ` - Equatorial Guinea * `ER` - Eritrea * `EE` - Estonia * `SZ` - Eswatini * `ET` - Ethiopia * `FK` - Falkland Islands * `FO` - Faroe Islands * `FJ` - Fiji * `FI` - Finland * `FR` - France * `GF` - French Guiana * `PF` - French Polynesia * `TF` - French Southern Territories * `GA` - Gabon * `GM` - Gambia * `GE` - Georgia * `DE` - Germany * `GH` - Ghana * `GI` - Gibraltar * `GR` - Greece * `GL` - Greenland * `GD` - Grenada * `GP` - Guadeloupe * `GU` - Guam * `GT` - Guatemala * `GG` - Guernsey * `GN` - Guinea * `GW` - Guinea-Bissau * `GY` - Guyana * `HT` - Haiti * `HM` - Heard Island and McDonald Islands * `VA` - Holy See * `HN` - Honduras * `HK` - Hong Kong * `HU` - Hungary * `IS` - Iceland * `IN` - India * `ID` - Indonesia * `IR` - Iran (Islamic Republic of) * `IQ` - Iraq * `IE` - Ireland * `IM` - Isle of Man * `IL` - Israel * `IT` - Italy * `JM` - Jamaica * `JP` - Japan * `JE` - Jersey * `JO` - Jordan * `KZ` - Kazakhstan * `KE` - Kenya * `KI` - Kiribati * `KP` - Korea (the Democratic People\'s Republic of) * `KR` - Korea (the Republic of) * `XK` - Kosovo * `KW` - Kuwait * `KG` - Kyrgyzstan * `LA` - Lao People\'s Democratic Republic * `LV` - Latvia * `LB` - Lebanon * `LS` - Lesotho * `LR` - Liberia * `LY` - Libya * `LI` - Liechtenstein * `LT` - Lithuania * `LU` - Luxembourg * `MO` - Macao * `MG` - Madagascar * `MW` - Malawi * `MY` - Malaysia * `MV` - Maldives * `ML` - Mali * `MT` - Malta * `MH` - Marshall Islands * `MQ` - Martinique * `MR` - Mauritania * `MU` - Mauritius * `YT` - Mayotte * `MX` - Mexico * `FM` - Micronesia * `MD` - Moldova * `MC` - Monaco * `MN` - Mongolia * `ME` - Montenegro * `MS` - Montserrat * `MA` - Morocco * `MZ` - Mozambique * `MM` - Myanmar * `NA` - Namibia * `NR` - Nauru * `NP` - Nepal * `NL` - Netherlands * `NC` - New Caledonia * `NZ` - New Zealand * `NI` - Nicaragua * `NE` - Niger * `NG` - Nigeria * `NU` - Niue * `NF` - Norfolk Island * `MK` - North Macedonia * `MP` - Northern Mariana Islands * `NO` - Norway * `OM` - Oman * `PK` - Pakistan * `PW` - Palau * `PS` - Palestine, State of * `PA` - Panama * `PG` - Papua New Guinea * `PY` - Paraguay * `PE` - Peru * `PH` - Philippines * `PN` - Pitcairn * `PL` - Poland * `PT` - Portugal * `PR` - Puerto Rico * `QA` - Qatar * `RE` - Réunion * `RO` - Romania * `RU` - Russian Federation * `RW` - Rwanda * `BL` - Saint Barthélemy * `SH` - Saint Helena * `KN` - Saint Kitts and Nevis * `LC` - Saint Lucia * `MF` - Saint Martin * `PM` - Saint Pierre and Miquelon * `VC` - Saint Vincent and the Grenadines * `WS` - Samoa * `SM` - San Marino * `ST` - Sao Tome and Principe * `SA` - Saudi Arabia * `SN` - Senegal * `RS` - Serbia * `SC` - Seychelles * `SL` - Sierra Leone * `SG` - Singapore * `SX` - Sint Maarten * `SK` - Slovakia * `SI` - Slovenia * `SB` - Solomon Islands * `SO` - Somalia * `ZA` - South Africa * `GS` - South Georgia and the South Sandwich Islands * `SS` - South Sudan * `ES` - Spain * `LK` - Sri Lanka * `SD` - Sudan * `SR` - Suriname * `SJ` - Svalbard and Jan Mayen * `SE` - Sweden * `CH` - Switzerland * `SY` - Syrian Arab Republic * `TW` - Taiwan * `TJ` - Tajikistan * `TZ` - Tanzania * `TH` - Thailand * `TL` - Timor-Leste * `TG` - Togo * `TK` - Tokelau * `TO` - Tonga * `TT` - Trinidad and Tobago * `TN` - Tunisia * `TR` - Turkey * `TM` - Turkmenistan * `TC` - Turks and Caicos Islands * `TV` - Tuvalu * `UG` - Uganda * `UA` - Ukraine * `AE` - United Arab Emirates * `GB` - United Kingdom * `UM` - United States Minor Outlying Islands * `US` - United States of America * `UY` - Uruguay * `UZ` - Uzbekistan * `VU` - Vanuatu * `VE` - Venezuela * `VN` - Viet Nam * `VG` - Virgin Islands * `VI` - Virgin Islands * `WF` - Wallis and Futuna * `EH` - Western Sahara * `YE` - Yemen * `ZM` - Zambia * `ZW` - Zimbabwe
 * @export
 * @enum {string}
 */

export const AddressCountryEnum = {
    Af: 'AF',
    Ax: 'AX',
    Al: 'AL',
    Dz: 'DZ',
    As: 'AS',
    Ad: 'AD',
    Ao: 'AO',
    Ai: 'AI',
    Aq: 'AQ',
    Ag: 'AG',
    Ar: 'AR',
    Am: 'AM',
    Aw: 'AW',
    Au: 'AU',
    At: 'AT',
    Az: 'AZ',
    Bs: 'BS',
    Bh: 'BH',
    Bd: 'BD',
    Bb: 'BB',
    By: 'BY',
    Be: 'BE',
    Bz: 'BZ',
    Bj: 'BJ',
    Bm: 'BM',
    Bt: 'BT',
    Bo: 'BO',
    Bq: 'BQ',
    Ba: 'BA',
    Bw: 'BW',
    Bv: 'BV',
    Br: 'BR',
    Io: 'IO',
    Bn: 'BN',
    Bg: 'BG',
    Bf: 'BF',
    Bi: 'BI',
    Cv: 'CV',
    Kh: 'KH',
    Cm: 'CM',
    Ca: 'CA',
    Ky: 'KY',
    Cf: 'CF',
    Td: 'TD',
    Cl: 'CL',
    Cn: 'CN',
    Cx: 'CX',
    Cc: 'CC',
    Co: 'CO',
    Km: 'KM',
    Cg: 'CG',
    Cd: 'CD',
    Ck: 'CK',
    Cr: 'CR',
    Ci: 'CI',
    Hr: 'HR',
    Cu: 'CU',
    Cw: 'CW',
    Cy: 'CY',
    Cz: 'CZ',
    Dk: 'DK',
    Dj: 'DJ',
    Dm: 'DM',
    Do: 'DO',
    Ec: 'EC',
    Eg: 'EG',
    Sv: 'SV',
    Gq: 'GQ',
    Er: 'ER',
    Ee: 'EE',
    Sz: 'SZ',
    Et: 'ET',
    Fk: 'FK',
    Fo: 'FO',
    Fj: 'FJ',
    Fi: 'FI',
    Fr: 'FR',
    Gf: 'GF',
    Pf: 'PF',
    Tf: 'TF',
    Ga: 'GA',
    Gm: 'GM',
    Ge: 'GE',
    De: 'DE',
    Gh: 'GH',
    Gi: 'GI',
    Gr: 'GR',
    Gl: 'GL',
    Gd: 'GD',
    Gp: 'GP',
    Gu: 'GU',
    Gt: 'GT',
    Gg: 'GG',
    Gn: 'GN',
    Gw: 'GW',
    Gy: 'GY',
    Ht: 'HT',
    Hm: 'HM',
    Va: 'VA',
    Hn: 'HN',
    Hk: 'HK',
    Hu: 'HU',
    Is: 'IS',
    In: 'IN',
    Id: 'ID',
    Ir: 'IR',
    Iq: 'IQ',
    Ie: 'IE',
    Im: 'IM',
    Il: 'IL',
    It: 'IT',
    Jm: 'JM',
    Jp: 'JP',
    Je: 'JE',
    Jo: 'JO',
    Kz: 'KZ',
    Ke: 'KE',
    Ki: 'KI',
    Kp: 'KP',
    Kr: 'KR',
    Xk: 'XK',
    Kw: 'KW',
    Kg: 'KG',
    La: 'LA',
    Lv: 'LV',
    Lb: 'LB',
    Ls: 'LS',
    Lr: 'LR',
    Ly: 'LY',
    Li: 'LI',
    Lt: 'LT',
    Lu: 'LU',
    Mo: 'MO',
    Mg: 'MG',
    Mw: 'MW',
    My: 'MY',
    Mv: 'MV',
    Ml: 'ML',
    Mt: 'MT',
    Mh: 'MH',
    Mq: 'MQ',
    Mr: 'MR',
    Mu: 'MU',
    Yt: 'YT',
    Mx: 'MX',
    Fm: 'FM',
    Md: 'MD',
    Mc: 'MC',
    Mn: 'MN',
    Me: 'ME',
    Ms: 'MS',
    Ma: 'MA',
    Mz: 'MZ',
    Mm: 'MM',
    Na: 'NA',
    Nr: 'NR',
    Np: 'NP',
    Nl: 'NL',
    Nc: 'NC',
    Nz: 'NZ',
    Ni: 'NI',
    Ne: 'NE',
    Ng: 'NG',
    Nu: 'NU',
    Nf: 'NF',
    Mk: 'MK',
    Mp: 'MP',
    No: 'NO',
    Om: 'OM',
    Pk: 'PK',
    Pw: 'PW',
    Ps: 'PS',
    Pa: 'PA',
    Pg: 'PG',
    Py: 'PY',
    Pe: 'PE',
    Ph: 'PH',
    Pn: 'PN',
    Pl: 'PL',
    Pt: 'PT',
    Pr: 'PR',
    Qa: 'QA',
    Re: 'RE',
    Ro: 'RO',
    Ru: 'RU',
    Rw: 'RW',
    Bl: 'BL',
    Sh: 'SH',
    Kn: 'KN',
    Lc: 'LC',
    Mf: 'MF',
    Pm: 'PM',
    Vc: 'VC',
    Ws: 'WS',
    Sm: 'SM',
    St: 'ST',
    Sa: 'SA',
    Sn: 'SN',
    Rs: 'RS',
    Sc: 'SC',
    Sl: 'SL',
    Sg: 'SG',
    Sx: 'SX',
    Sk: 'SK',
    Si: 'SI',
    Sb: 'SB',
    So: 'SO',
    Za: 'ZA',
    Gs: 'GS',
    Ss: 'SS',
    Es: 'ES',
    Lk: 'LK',
    Sd: 'SD',
    Sr: 'SR',
    Sj: 'SJ',
    Se: 'SE',
    Ch: 'CH',
    Sy: 'SY',
    Tw: 'TW',
    Tj: 'TJ',
    Tz: 'TZ',
    Th: 'TH',
    Tl: 'TL',
    Tg: 'TG',
    Tk: 'TK',
    To: 'TO',
    Tt: 'TT',
    Tn: 'TN',
    Tr: 'TR',
    Tm: 'TM',
    Tc: 'TC',
    Tv: 'TV',
    Ug: 'UG',
    Ua: 'UA',
    Ae: 'AE',
    Gb: 'GB',
    Um: 'UM',
    Us: 'US',
    Uy: 'UY',
    Uz: 'UZ',
    Vu: 'VU',
    Ve: 'VE',
    Vn: 'VN',
    Vg: 'VG',
    Vi: 'VI',
    Wf: 'WF',
    Eh: 'EH',
    Ye: 'YE',
    Zm: 'ZM',
    Zw: 'ZW'
} as const;

export type AddressCountryEnum = typeof AddressCountryEnum[keyof typeof AddressCountryEnum];


/**
 * * `disabled` - Disabled * `pending` - Pending * `active` - Active * `paused` - Paused * `deactivating` - Deactivating
 * @export
 * @enum {string}
 */

export const AdverseMediaMonitoringStatusEnum = {
    Disabled: 'disabled',
    Pending: 'pending',
    Active: 'active',
    Paused: 'paused',
    Deactivating: 'deactivating'
} as const;

export type AdverseMediaMonitoringStatusEnum = typeof AdverseMediaMonitoringStatusEnum[keyof typeof AdverseMediaMonitoringStatusEnum];


/**
 * Alert Serializer.
 * @export
 * @interface Alert
 */
export interface Alert {
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'name': string;
    /**
     * Get if User is subscribed.
     * @type {boolean}
     * @memberof Alert
     */
    'is_subscribed': boolean;
    /**
     * Action when processing this alert, sending email of sms  * `EMAIL` - E-mail * `SMS` - Sms * `PUSH_NOTIFICATION` - Push Notification
     * @type {ActionTypeEnum}
     * @memberof Alert
     */
    'action_type': ActionTypeEnum;
    /**
     * 
     * @type {AlertArticleFilters}
     * @memberof Alert
     */
    'article_filters'?: AlertArticleFilters | null;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof Alert
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {AlertUser}
     * @memberof Alert
     */
    'user': AlertUser;
    /**
     * Only send alerts on business days, not weekends.
     * @type {boolean}
     * @memberof Alert
     */
    'business_days'?: boolean;
    /**
     * Send Push notifications about this alert to the user.
     * @type {boolean}
     * @memberof Alert
     */
    'push_notifications'?: boolean;
    /**
     * Generate summary of alert.
     * @type {boolean}
     * @memberof Alert
     */
    'generate_summary'?: boolean;
    /**
     * Free-text instruction that steers both which articles are included and how the summary is written (e.g. \'Focus on M&A and executive changes; ignore product launches\'). When set, an LLM curates the candidate articles against this instruction and produces a summary in one combined call.
     * @type {string}
     * @memberof Alert
     */
    'ai_instruction'?: string | null;
    /**
     * 
     * @type {IntervalEnum}
     * @memberof Alert
     */
    'interval': IntervalEnum;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'start_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'time': string;
    /**
     * 
     * @type {AlertTimezone}
     * @memberof Alert
     */
    'timezone'?: AlertTimezone | null;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'last_send_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'last_checked_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Alert
     */
    'updated_at': string;
}
/**
 * 
 * @export
 * @interface AlertArticleFilters
 */
export interface AlertArticleFilters {
    /**
     * 
     * @type {string}
     * @memberof AlertArticleFilters
     */
    'query'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'portfolios'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'countries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'languages'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'duns_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'excluded_companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'global_ultimates'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'registration_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'industries'?: Array<string> | null;
    /**
     * 
     * @type {ArticleFiltersMediaType}
     * @memberof AlertArticleFilters
     */
    'media_type'?: ArticleFiltersMediaType | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof AlertArticleFilters
     */
    'category_set'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertArticleFilters
     */
    'excluded_categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof AlertArticleFilters
     */
    'parent_category'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof AlertArticleFilters
     */
    'include_clustered_articles'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AlertArticleFilters
     */
    'sentiment'?: boolean | null;
    /**
     * 
     * @type {string}
     * @memberof AlertArticleFilters
     */
    'min_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertArticleFilters
     */
    'max_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertArticleFilters
     */
    'min_created_at'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof AlertArticleFilters
     */
    'is_material'?: boolean | null;
}
/**
 * Alert Feedback Serializer.
 * @export
 * @interface AlertFeedback
 */
export interface AlertFeedback {
    /**
     * 
     * @type {AlertFeedbackTypeEnum}
     * @memberof AlertFeedback
     */
    'alert_feedback_type': AlertFeedbackTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof AlertFeedback
     */
    'article_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertFeedback
     */
    'company_id'?: string | null;
    /**
     * 
     * @type {AlertFeedbackArticleFeedbackType}
     * @memberof AlertFeedback
     */
    'article_feedback_type'?: AlertFeedbackArticleFeedbackType | null;
    /**
     * 
     * @type {AlertFeedbackCompanyFeedbackType}
     * @memberof AlertFeedback
     */
    'company_feedback_type'?: AlertFeedbackCompanyFeedbackType | null;
    /**
     * 
     * @type {string}
     * @memberof AlertFeedback
     */
    'comment'?: string | null;
}
/**
 * @type AlertFeedbackArticleFeedbackType
 * @export
 */
export type AlertFeedbackArticleFeedbackType = ArticleFeedbackTypeEnum | BlankEnum | NullEnum;

/**
 * @type AlertFeedbackCompanyFeedbackType
 * @export
 */
export type AlertFeedbackCompanyFeedbackType = BlankEnum | CompanyFeedbackTypeEnum | NullEnum;

/**
 * Alert Feedback Serializer.
 * @export
 * @interface AlertFeedbackRequest
 */
export interface AlertFeedbackRequest {
    /**
     * 
     * @type {AlertFeedbackTypeEnum}
     * @memberof AlertFeedbackRequest
     */
    'alert_feedback_type': AlertFeedbackTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof AlertFeedbackRequest
     */
    'article_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertFeedbackRequest
     */
    'company_id'?: string | null;
    /**
     * 
     * @type {AlertFeedbackArticleFeedbackType}
     * @memberof AlertFeedbackRequest
     */
    'article_feedback_type'?: AlertFeedbackArticleFeedbackType | null;
    /**
     * 
     * @type {AlertFeedbackCompanyFeedbackType}
     * @memberof AlertFeedbackRequest
     */
    'company_feedback_type'?: AlertFeedbackCompanyFeedbackType | null;
    /**
     * 
     * @type {string}
     * @memberof AlertFeedbackRequest
     */
    'comment'?: string | null;
}
/**
 * * `article_feedback` - article_feedback * `company_feedback` - company_feedback * `company_exclusion` - company_exclusion
 * @export
 * @enum {string}
 */

export const AlertFeedbackTypeEnum = {
    ArticleFeedback: 'article_feedback',
    CompanyFeedback: 'company_feedback',
    CompanyExclusion: 'company_exclusion'
} as const;

export type AlertFeedbackTypeEnum = typeof AlertFeedbackTypeEnum[keyof typeof AlertFeedbackTypeEnum];


/**
 * Alert Serializer.
 * @export
 * @interface AlertRequest
 */
export interface AlertRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertRequest
     */
    'name': string;
    /**
     * Action when processing this alert, sending email of sms  * `EMAIL` - E-mail * `SMS` - Sms * `PUSH_NOTIFICATION` - Push Notification
     * @type {ActionTypeEnum}
     * @memberof AlertRequest
     */
    'action_type': ActionTypeEnum;
    /**
     * 
     * @type {AlertRequestArticleFilters}
     * @memberof AlertRequest
     */
    'article_filters'?: AlertRequestArticleFilters | null;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof AlertRequest
     */
    'customer_reference'?: string | null;
    /**
     * Only send alerts on business days, not weekends.
     * @type {boolean}
     * @memberof AlertRequest
     */
    'business_days'?: boolean;
    /**
     * Send Push notifications about this alert to the user.
     * @type {boolean}
     * @memberof AlertRequest
     */
    'push_notifications'?: boolean;
    /**
     * Generate summary of alert.
     * @type {boolean}
     * @memberof AlertRequest
     */
    'generate_summary'?: boolean;
    /**
     * Free-text instruction that steers both which articles are included and how the summary is written (e.g. \'Focus on M&A and executive changes; ignore product launches\'). When set, an LLM curates the candidate articles against this instruction and produces a summary in one combined call.
     * @type {string}
     * @memberof AlertRequest
     */
    'ai_instruction'?: string | null;
    /**
     * 
     * @type {IntervalEnum}
     * @memberof AlertRequest
     */
    'interval': IntervalEnum;
    /**
     * 
     * @type {string}
     * @memberof AlertRequest
     */
    'start_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertRequest
     */
    'time': string;
    /**
     * 
     * @type {AlertTimezone}
     * @memberof AlertRequest
     */
    'timezone'?: AlertTimezone | null;
}
/**
 * 
 * @export
 * @interface AlertRequestArticleFilters
 */
export interface AlertRequestArticleFilters {
    /**
     * 
     * @type {string}
     * @memberof AlertRequestArticleFilters
     */
    'query'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'portfolios'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'countries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'languages'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'duns_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'excluded_companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'global_ultimates'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'registration_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'industries'?: Array<string> | null;
    /**
     * 
     * @type {ArticleFiltersMediaType}
     * @memberof AlertRequestArticleFilters
     */
    'media_type'?: ArticleFiltersMediaType | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof AlertRequestArticleFilters
     */
    'category_set'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof AlertRequestArticleFilters
     */
    'excluded_categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof AlertRequestArticleFilters
     */
    'parent_category'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof AlertRequestArticleFilters
     */
    'include_clustered_articles'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AlertRequestArticleFilters
     */
    'sentiment'?: boolean | null;
    /**
     * 
     * @type {string}
     * @memberof AlertRequestArticleFilters
     */
    'min_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertRequestArticleFilters
     */
    'max_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof AlertRequestArticleFilters
     */
    'min_created_at'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof AlertRequestArticleFilters
     */
    'is_material'?: boolean | null;
}
/**
 * @type AlertTimezone
 * Custom timezone for handling when to send alert in local time.  * `Africa/Abidjan` - Africa/Abidjan * `Africa/Accra` - Africa/Accra * `Africa/Addis_Ababa` - Africa/Addis_Ababa * `Africa/Algiers` - Africa/Algiers * `Africa/Asmara` - Africa/Asmara * `Africa/Asmera` - Africa/Asmera * `Africa/Bamako` - Africa/Bamako * `Africa/Bangui` - Africa/Bangui * `Africa/Banjul` - Africa/Banjul * `Africa/Bissau` - Africa/Bissau * `Africa/Blantyre` - Africa/Blantyre * `Africa/Brazzaville` - Africa/Brazzaville * `Africa/Bujumbura` - Africa/Bujumbura * `Africa/Cairo` - Africa/Cairo * `Africa/Casablanca` - Africa/Casablanca * `Africa/Ceuta` - Africa/Ceuta * `Africa/Conakry` - Africa/Conakry * `Africa/Dakar` - Africa/Dakar * `Africa/Dar_es_Salaam` - Africa/Dar_es_Salaam * `Africa/Djibouti` - Africa/Djibouti * `Africa/Douala` - Africa/Douala * `Africa/El_Aaiun` - Africa/El_Aaiun * `Africa/Freetown` - Africa/Freetown * `Africa/Gaborone` - Africa/Gaborone * `Africa/Harare` - Africa/Harare * `Africa/Johannesburg` - Africa/Johannesburg * `Africa/Juba` - Africa/Juba * `Africa/Kampala` - Africa/Kampala * `Africa/Khartoum` - Africa/Khartoum * `Africa/Kigali` - Africa/Kigali * `Africa/Kinshasa` - Africa/Kinshasa * `Africa/Lagos` - Africa/Lagos * `Africa/Libreville` - Africa/Libreville * `Africa/Lome` - Africa/Lome * `Africa/Luanda` - Africa/Luanda * `Africa/Lubumbashi` - Africa/Lubumbashi * `Africa/Lusaka` - Africa/Lusaka * `Africa/Malabo` - Africa/Malabo * `Africa/Maputo` - Africa/Maputo * `Africa/Maseru` - Africa/Maseru * `Africa/Mbabane` - Africa/Mbabane * `Africa/Mogadishu` - Africa/Mogadishu * `Africa/Monrovia` - Africa/Monrovia * `Africa/Nairobi` - Africa/Nairobi * `Africa/Ndjamena` - Africa/Ndjamena * `Africa/Niamey` - Africa/Niamey * `Africa/Nouakchott` - Africa/Nouakchott * `Africa/Ouagadougou` - Africa/Ouagadougou * `Africa/Porto-Novo` - Africa/Porto-Novo * `Africa/Sao_Tome` - Africa/Sao_Tome * `Africa/Timbuktu` - Africa/Timbuktu * `Africa/Tripoli` - Africa/Tripoli * `Africa/Tunis` - Africa/Tunis * `Africa/Windhoek` - Africa/Windhoek * `America/Adak` - America/Adak * `America/Anchorage` - America/Anchorage * `America/Anguilla` - America/Anguilla * `America/Antigua` - America/Antigua * `America/Araguaina` - America/Araguaina * `America/Argentina/Buenos_Aires` - America/Argentina/Buenos_Aires * `America/Argentina/Catamarca` - America/Argentina/Catamarca * `America/Argentina/ComodRivadavia` - America/Argentina/ComodRivadavia * `America/Argentina/Cordoba` - America/Argentina/Cordoba * `America/Argentina/Jujuy` - America/Argentina/Jujuy * `America/Argentina/La_Rioja` - America/Argentina/La_Rioja * `America/Argentina/Mendoza` - America/Argentina/Mendoza * `America/Argentina/Rio_Gallegos` - America/Argentina/Rio_Gallegos * `America/Argentina/Salta` - America/Argentina/Salta * `America/Argentina/San_Juan` - America/Argentina/San_Juan * `America/Argentina/San_Luis` - America/Argentina/San_Luis * `America/Argentina/Tucuman` - America/Argentina/Tucuman * `America/Argentina/Ushuaia` - America/Argentina/Ushuaia * `America/Aruba` - America/Aruba * `America/Asuncion` - America/Asuncion * `America/Atikokan` - America/Atikokan * `America/Atka` - America/Atka * `America/Bahia` - America/Bahia * `America/Bahia_Banderas` - America/Bahia_Banderas * `America/Barbados` - America/Barbados * `America/Belem` - America/Belem * `America/Belize` - America/Belize * `America/Blanc-Sablon` - America/Blanc-Sablon * `America/Boa_Vista` - America/Boa_Vista * `America/Bogota` - America/Bogota * `America/Boise` - America/Boise * `America/Buenos_Aires` - America/Buenos_Aires * `America/Cambridge_Bay` - America/Cambridge_Bay * `America/Campo_Grande` - America/Campo_Grande * `America/Cancun` - America/Cancun * `America/Caracas` - America/Caracas * `America/Catamarca` - America/Catamarca * `America/Cayenne` - America/Cayenne * `America/Cayman` - America/Cayman * `America/Chicago` - America/Chicago * `America/Chihuahua` - America/Chihuahua * `America/Ciudad_Juarez` - America/Ciudad_Juarez * `America/Coral_Harbour` - America/Coral_Harbour * `America/Cordoba` - America/Cordoba * `America/Costa_Rica` - America/Costa_Rica * `America/Coyhaique` - America/Coyhaique * `America/Creston` - America/Creston * `America/Cuiaba` - America/Cuiaba * `America/Curacao` - America/Curacao * `America/Danmarkshavn` - America/Danmarkshavn * `America/Dawson` - America/Dawson * `America/Dawson_Creek` - America/Dawson_Creek * `America/Denver` - America/Denver * `America/Detroit` - America/Detroit * `America/Dominica` - America/Dominica * `America/Edmonton` - America/Edmonton * `America/Eirunepe` - America/Eirunepe * `America/El_Salvador` - America/El_Salvador * `America/Ensenada` - America/Ensenada * `America/Fort_Nelson` - America/Fort_Nelson * `America/Fort_Wayne` - America/Fort_Wayne * `America/Fortaleza` - America/Fortaleza * `America/Glace_Bay` - America/Glace_Bay * `America/Godthab` - America/Godthab * `America/Goose_Bay` - America/Goose_Bay * `America/Grand_Turk` - America/Grand_Turk * `America/Grenada` - America/Grenada * `America/Guadeloupe` - America/Guadeloupe * `America/Guatemala` - America/Guatemala * `America/Guayaquil` - America/Guayaquil * `America/Guyana` - America/Guyana * `America/Halifax` - America/Halifax * `America/Havana` - America/Havana * `America/Hermosillo` - America/Hermosillo * `America/Indiana/Indianapolis` - America/Indiana/Indianapolis * `America/Indiana/Knox` - America/Indiana/Knox * `America/Indiana/Marengo` - America/Indiana/Marengo * `America/Indiana/Petersburg` - America/Indiana/Petersburg * `America/Indiana/Tell_City` - America/Indiana/Tell_City * `America/Indiana/Vevay` - America/Indiana/Vevay * `America/Indiana/Vincennes` - America/Indiana/Vincennes * `America/Indiana/Winamac` - America/Indiana/Winamac * `America/Indianapolis` - America/Indianapolis * `America/Inuvik` - America/Inuvik * `America/Iqaluit` - America/Iqaluit * `America/Jamaica` - America/Jamaica * `America/Jujuy` - America/Jujuy * `America/Juneau` - America/Juneau * `America/Kentucky/Louisville` - America/Kentucky/Louisville * `America/Kentucky/Monticello` - America/Kentucky/Monticello * `America/Knox_IN` - America/Knox_IN * `America/Kralendijk` - America/Kralendijk * `America/La_Paz` - America/La_Paz * `America/Lima` - America/Lima * `America/Los_Angeles` - America/Los_Angeles * `America/Louisville` - America/Louisville * `America/Lower_Princes` - America/Lower_Princes * `America/Maceio` - America/Maceio * `America/Managua` - America/Managua * `America/Manaus` - America/Manaus * `America/Marigot` - America/Marigot * `America/Martinique` - America/Martinique * `America/Matamoros` - America/Matamoros * `America/Mazatlan` - America/Mazatlan * `America/Mendoza` - America/Mendoza * `America/Menominee` - America/Menominee * `America/Merida` - America/Merida * `America/Metlakatla` - America/Metlakatla * `America/Mexico_City` - America/Mexico_City * `America/Miquelon` - America/Miquelon * `America/Moncton` - America/Moncton * `America/Monterrey` - America/Monterrey * `America/Montevideo` - America/Montevideo * `America/Montreal` - America/Montreal * `America/Montserrat` - America/Montserrat * `America/Nassau` - America/Nassau * `America/New_York` - America/New_York * `America/Nipigon` - America/Nipigon * `America/Nome` - America/Nome * `America/Noronha` - America/Noronha * `America/North_Dakota/Beulah` - America/North_Dakota/Beulah * `America/North_Dakota/Center` - America/North_Dakota/Center * `America/North_Dakota/New_Salem` - America/North_Dakota/New_Salem * `America/Nuuk` - America/Nuuk * `America/Ojinaga` - America/Ojinaga * `America/Panama` - America/Panama * `America/Pangnirtung` - America/Pangnirtung * `America/Paramaribo` - America/Paramaribo * `America/Phoenix` - America/Phoenix * `America/Port-au-Prince` - America/Port-au-Prince * `America/Port_of_Spain` - America/Port_of_Spain * `America/Porto_Acre` - America/Porto_Acre * `America/Porto_Velho` - America/Porto_Velho * `America/Puerto_Rico` - America/Puerto_Rico * `America/Punta_Arenas` - America/Punta_Arenas * `America/Rainy_River` - America/Rainy_River * `America/Rankin_Inlet` - America/Rankin_Inlet * `America/Recife` - America/Recife * `America/Regina` - America/Regina * `America/Resolute` - America/Resolute * `America/Rio_Branco` - America/Rio_Branco * `America/Rosario` - America/Rosario * `America/Santa_Isabel` - America/Santa_Isabel * `America/Santarem` - America/Santarem * `America/Santiago` - America/Santiago * `America/Santo_Domingo` - America/Santo_Domingo * `America/Sao_Paulo` - America/Sao_Paulo * `America/Scoresbysund` - America/Scoresbysund * `America/Shiprock` - America/Shiprock * `America/Sitka` - America/Sitka * `America/St_Barthelemy` - America/St_Barthelemy * `America/St_Johns` - America/St_Johns * `America/St_Kitts` - America/St_Kitts * `America/St_Lucia` - America/St_Lucia * `America/St_Thomas` - America/St_Thomas * `America/St_Vincent` - America/St_Vincent * `America/Swift_Current` - America/Swift_Current * `America/Tegucigalpa` - America/Tegucigalpa * `America/Thule` - America/Thule * `America/Thunder_Bay` - America/Thunder_Bay * `America/Tijuana` - America/Tijuana * `America/Toronto` - America/Toronto * `America/Tortola` - America/Tortola * `America/Vancouver` - America/Vancouver * `America/Virgin` - America/Virgin * `America/Whitehorse` - America/Whitehorse * `America/Winnipeg` - America/Winnipeg * `America/Yakutat` - America/Yakutat * `America/Yellowknife` - America/Yellowknife * `Antarctica/Casey` - Antarctica/Casey * `Antarctica/Davis` - Antarctica/Davis * `Antarctica/DumontDUrville` - Antarctica/DumontDUrville * `Antarctica/Macquarie` - Antarctica/Macquarie * `Antarctica/Mawson` - Antarctica/Mawson * `Antarctica/McMurdo` - Antarctica/McMurdo * `Antarctica/Palmer` - Antarctica/Palmer * `Antarctica/Rothera` - Antarctica/Rothera * `Antarctica/South_Pole` - Antarctica/South_Pole * `Antarctica/Syowa` - Antarctica/Syowa * `Antarctica/Troll` - Antarctica/Troll * `Antarctica/Vostok` - Antarctica/Vostok * `Arctic/Longyearbyen` - Arctic/Longyearbyen * `Asia/Aden` - Asia/Aden * `Asia/Almaty` - Asia/Almaty * `Asia/Amman` - Asia/Amman * `Asia/Anadyr` - Asia/Anadyr * `Asia/Aqtau` - Asia/Aqtau * `Asia/Aqtobe` - Asia/Aqtobe * `Asia/Ashgabat` - Asia/Ashgabat * `Asia/Ashkhabad` - Asia/Ashkhabad * `Asia/Atyrau` - Asia/Atyrau * `Asia/Baghdad` - Asia/Baghdad * `Asia/Bahrain` - Asia/Bahrain * `Asia/Baku` - Asia/Baku * `Asia/Bangkok` - Asia/Bangkok * `Asia/Barnaul` - Asia/Barnaul * `Asia/Beirut` - Asia/Beirut * `Asia/Bishkek` - Asia/Bishkek * `Asia/Brunei` - Asia/Brunei * `Asia/Calcutta` - Asia/Calcutta * `Asia/Chita` - Asia/Chita * `Asia/Choibalsan` - Asia/Choibalsan * `Asia/Chongqing` - Asia/Chongqing * `Asia/Chungking` - Asia/Chungking * `Asia/Colombo` - Asia/Colombo * `Asia/Dacca` - Asia/Dacca * `Asia/Damascus` - Asia/Damascus * `Asia/Dhaka` - Asia/Dhaka * `Asia/Dili` - Asia/Dili * `Asia/Dubai` - Asia/Dubai * `Asia/Dushanbe` - Asia/Dushanbe * `Asia/Famagusta` - Asia/Famagusta * `Asia/Gaza` - Asia/Gaza * `Asia/Harbin` - Asia/Harbin * `Asia/Hebron` - Asia/Hebron * `Asia/Ho_Chi_Minh` - Asia/Ho_Chi_Minh * `Asia/Hong_Kong` - Asia/Hong_Kong * `Asia/Hovd` - Asia/Hovd * `Asia/Irkutsk` - Asia/Irkutsk * `Asia/Istanbul` - Asia/Istanbul * `Asia/Jakarta` - Asia/Jakarta * `Asia/Jayapura` - Asia/Jayapura * `Asia/Jerusalem` - Asia/Jerusalem * `Asia/Kabul` - Asia/Kabul * `Asia/Kamchatka` - Asia/Kamchatka * `Asia/Karachi` - Asia/Karachi * `Asia/Kashgar` - Asia/Kashgar * `Asia/Kathmandu` - Asia/Kathmandu * `Asia/Katmandu` - Asia/Katmandu * `Asia/Khandyga` - Asia/Khandyga * `Asia/Kolkata` - Asia/Kolkata * `Asia/Krasnoyarsk` - Asia/Krasnoyarsk * `Asia/Kuala_Lumpur` - Asia/Kuala_Lumpur * `Asia/Kuching` - Asia/Kuching * `Asia/Kuwait` - Asia/Kuwait * `Asia/Macao` - Asia/Macao * `Asia/Macau` - Asia/Macau * `Asia/Magadan` - Asia/Magadan * `Asia/Makassar` - Asia/Makassar * `Asia/Manila` - Asia/Manila * `Asia/Muscat` - Asia/Muscat * `Asia/Nicosia` - Asia/Nicosia * `Asia/Novokuznetsk` - Asia/Novokuznetsk * `Asia/Novosibirsk` - Asia/Novosibirsk * `Asia/Omsk` - Asia/Omsk * `Asia/Oral` - Asia/Oral * `Asia/Phnom_Penh` - Asia/Phnom_Penh * `Asia/Pontianak` - Asia/Pontianak * `Asia/Pyongyang` - Asia/Pyongyang * `Asia/Qatar` - Asia/Qatar * `Asia/Qostanay` - Asia/Qostanay * `Asia/Qyzylorda` - Asia/Qyzylorda * `Asia/Rangoon` - Asia/Rangoon * `Asia/Riyadh` - Asia/Riyadh * `Asia/Saigon` - Asia/Saigon * `Asia/Sakhalin` - Asia/Sakhalin * `Asia/Samarkand` - Asia/Samarkand * `Asia/Seoul` - Asia/Seoul * `Asia/Shanghai` - Asia/Shanghai * `Asia/Singapore` - Asia/Singapore * `Asia/Srednekolymsk` - Asia/Srednekolymsk * `Asia/Taipei` - Asia/Taipei * `Asia/Tashkent` - Asia/Tashkent * `Asia/Tbilisi` - Asia/Tbilisi * `Asia/Tehran` - Asia/Tehran * `Asia/Tel_Aviv` - Asia/Tel_Aviv * `Asia/Thimbu` - Asia/Thimbu * `Asia/Thimphu` - Asia/Thimphu * `Asia/Tokyo` - Asia/Tokyo * `Asia/Tomsk` - Asia/Tomsk * `Asia/Ujung_Pandang` - Asia/Ujung_Pandang * `Asia/Ulaanbaatar` - Asia/Ulaanbaatar * `Asia/Ulan_Bator` - Asia/Ulan_Bator * `Asia/Urumqi` - Asia/Urumqi * `Asia/Ust-Nera` - Asia/Ust-Nera * `Asia/Vientiane` - Asia/Vientiane * `Asia/Vladivostok` - Asia/Vladivostok * `Asia/Yakutsk` - Asia/Yakutsk * `Asia/Yangon` - Asia/Yangon * `Asia/Yekaterinburg` - Asia/Yekaterinburg * `Asia/Yerevan` - Asia/Yerevan * `Atlantic/Azores` - Atlantic/Azores * `Atlantic/Bermuda` - Atlantic/Bermuda * `Atlantic/Canary` - Atlantic/Canary * `Atlantic/Cape_Verde` - Atlantic/Cape_Verde * `Atlantic/Faeroe` - Atlantic/Faeroe * `Atlantic/Faroe` - Atlantic/Faroe * `Atlantic/Jan_Mayen` - Atlantic/Jan_Mayen * `Atlantic/Madeira` - Atlantic/Madeira * `Atlantic/Reykjavik` - Atlantic/Reykjavik * `Atlantic/South_Georgia` - Atlantic/South_Georgia * `Atlantic/St_Helena` - Atlantic/St_Helena * `Atlantic/Stanley` - Atlantic/Stanley * `Australia/ACT` - Australia/ACT * `Australia/Adelaide` - Australia/Adelaide * `Australia/Brisbane` - Australia/Brisbane * `Australia/Broken_Hill` - Australia/Broken_Hill * `Australia/Canberra` - Australia/Canberra * `Australia/Currie` - Australia/Currie * `Australia/Darwin` - Australia/Darwin * `Australia/Eucla` - Australia/Eucla * `Australia/Hobart` - Australia/Hobart * `Australia/LHI` - Australia/LHI * `Australia/Lindeman` - Australia/Lindeman * `Australia/Lord_Howe` - Australia/Lord_Howe * `Australia/Melbourne` - Australia/Melbourne * `Australia/NSW` - Australia/NSW * `Australia/North` - Australia/North * `Australia/Perth` - Australia/Perth * `Australia/Queensland` - Australia/Queensland * `Australia/South` - Australia/South * `Australia/Sydney` - Australia/Sydney * `Australia/Tasmania` - Australia/Tasmania * `Australia/Victoria` - Australia/Victoria * `Australia/West` - Australia/West * `Australia/Yancowinna` - Australia/Yancowinna * `Brazil/Acre` - Brazil/Acre * `Brazil/DeNoronha` - Brazil/DeNoronha * `Brazil/East` - Brazil/East * `Brazil/West` - Brazil/West * `CET` - CET * `CST6CDT` - CST6CDT * `Canada/Atlantic` - Canada/Atlantic * `Canada/Central` - Canada/Central * `Canada/Eastern` - Canada/Eastern * `Canada/Mountain` - Canada/Mountain * `Canada/Newfoundland` - Canada/Newfoundland * `Canada/Pacific` - Canada/Pacific * `Canada/Saskatchewan` - Canada/Saskatchewan * `Canada/Yukon` - Canada/Yukon * `Chile/Continental` - Chile/Continental * `Chile/EasterIsland` - Chile/EasterIsland * `Cuba` - Cuba * `EET` - EET * `EST` - EST * `EST5EDT` - EST5EDT * `Egypt` - Egypt * `Eire` - Eire * `Europe/Amsterdam` - Europe/Amsterdam * `Europe/Andorra` - Europe/Andorra * `Europe/Astrakhan` - Europe/Astrakhan * `Europe/Athens` - Europe/Athens * `Europe/Belfast` - Europe/Belfast * `Europe/Belgrade` - Europe/Belgrade * `Europe/Berlin` - Europe/Berlin * `Europe/Bratislava` - Europe/Bratislava * `Europe/Brussels` - Europe/Brussels * `Europe/Bucharest` - Europe/Bucharest * `Europe/Budapest` - Europe/Budapest * `Europe/Busingen` - Europe/Busingen * `Europe/Chisinau` - Europe/Chisinau * `Europe/Copenhagen` - Europe/Copenhagen * `Europe/Dublin` - Europe/Dublin * `Europe/Gibraltar` - Europe/Gibraltar * `Europe/Guernsey` - Europe/Guernsey * `Europe/Helsinki` - Europe/Helsinki * `Europe/Isle_of_Man` - Europe/Isle_of_Man * `Europe/Istanbul` - Europe/Istanbul * `Europe/Jersey` - Europe/Jersey * `Europe/Kaliningrad` - Europe/Kaliningrad * `Europe/Kiev` - Europe/Kiev * `Europe/Kirov` - Europe/Kirov * `Europe/Kyiv` - Europe/Kyiv * `Europe/Lisbon` - Europe/Lisbon * `Europe/Ljubljana` - Europe/Ljubljana * `Europe/London` - Europe/London * `Europe/Luxembourg` - Europe/Luxembourg * `Europe/Madrid` - Europe/Madrid * `Europe/Malta` - Europe/Malta * `Europe/Mariehamn` - Europe/Mariehamn * `Europe/Minsk` - Europe/Minsk * `Europe/Monaco` - Europe/Monaco * `Europe/Moscow` - Europe/Moscow * `Europe/Nicosia` - Europe/Nicosia * `Europe/Oslo` - Europe/Oslo * `Europe/Paris` - Europe/Paris * `Europe/Podgorica` - Europe/Podgorica * `Europe/Prague` - Europe/Prague * `Europe/Riga` - Europe/Riga * `Europe/Rome` - Europe/Rome * `Europe/Samara` - Europe/Samara * `Europe/San_Marino` - Europe/San_Marino * `Europe/Sarajevo` - Europe/Sarajevo * `Europe/Saratov` - Europe/Saratov * `Europe/Simferopol` - Europe/Simferopol * `Europe/Skopje` - Europe/Skopje * `Europe/Sofia` - Europe/Sofia * `Europe/Stockholm` - Europe/Stockholm * `Europe/Tallinn` - Europe/Tallinn * `Europe/Tirane` - Europe/Tirane * `Europe/Tiraspol` - Europe/Tiraspol * `Europe/Ulyanovsk` - Europe/Ulyanovsk * `Europe/Uzhgorod` - Europe/Uzhgorod * `Europe/Vaduz` - Europe/Vaduz * `Europe/Vatican` - Europe/Vatican * `Europe/Vienna` - Europe/Vienna * `Europe/Vilnius` - Europe/Vilnius * `Europe/Volgograd` - Europe/Volgograd * `Europe/Warsaw` - Europe/Warsaw * `Europe/Zagreb` - Europe/Zagreb * `Europe/Zaporozhye` - Europe/Zaporozhye * `Europe/Zurich` - Europe/Zurich * `GB` - GB * `GB-Eire` - GB-Eire * `Greenwich` - Greenwich * `HST` - HST * `Hongkong` - Hongkong * `Iceland` - Iceland * `Indian/Antananarivo` - Indian/Antananarivo * `Indian/Chagos` - Indian/Chagos * `Indian/Christmas` - Indian/Christmas * `Indian/Cocos` - Indian/Cocos * `Indian/Comoro` - Indian/Comoro * `Indian/Kerguelen` - Indian/Kerguelen * `Indian/Mahe` - Indian/Mahe * `Indian/Maldives` - Indian/Maldives * `Indian/Mauritius` - Indian/Mauritius * `Indian/Mayotte` - Indian/Mayotte * `Indian/Reunion` - Indian/Reunion * `Iran` - Iran * `Israel` - Israel * `Jamaica` - Jamaica * `Japan` - Japan * `Kwajalein` - Kwajalein * `Libya` - Libya * `MET` - MET * `MST` - MST * `MST7MDT` - MST7MDT * `Mexico/BajaNorte` - Mexico/BajaNorte * `Mexico/BajaSur` - Mexico/BajaSur * `Mexico/General` - Mexico/General * `NZ` - NZ * `NZ-CHAT` - NZ-CHAT * `Navajo` - Navajo * `PRC` - PRC * `PST8PDT` - PST8PDT * `Pacific/Apia` - Pacific/Apia * `Pacific/Auckland` - Pacific/Auckland * `Pacific/Bougainville` - Pacific/Bougainville * `Pacific/Chatham` - Pacific/Chatham * `Pacific/Chuuk` - Pacific/Chuuk * `Pacific/Easter` - Pacific/Easter * `Pacific/Efate` - Pacific/Efate * `Pacific/Enderbury` - Pacific/Enderbury * `Pacific/Fakaofo` - Pacific/Fakaofo * `Pacific/Fiji` - Pacific/Fiji * `Pacific/Funafuti` - Pacific/Funafuti * `Pacific/Galapagos` - Pacific/Galapagos * `Pacific/Gambier` - Pacific/Gambier * `Pacific/Guadalcanal` - Pacific/Guadalcanal * `Pacific/Guam` - Pacific/Guam * `Pacific/Honolulu` - Pacific/Honolulu * `Pacific/Johnston` - Pacific/Johnston * `Pacific/Kanton` - Pacific/Kanton * `Pacific/Kiritimati` - Pacific/Kiritimati * `Pacific/Kosrae` - Pacific/Kosrae * `Pacific/Kwajalein` - Pacific/Kwajalein * `Pacific/Majuro` - Pacific/Majuro * `Pacific/Marquesas` - Pacific/Marquesas * `Pacific/Midway` - Pacific/Midway * `Pacific/Nauru` - Pacific/Nauru * `Pacific/Niue` - Pacific/Niue * `Pacific/Norfolk` - Pacific/Norfolk * `Pacific/Noumea` - Pacific/Noumea * `Pacific/Pago_Pago` - Pacific/Pago_Pago * `Pacific/Palau` - Pacific/Palau * `Pacific/Pitcairn` - Pacific/Pitcairn * `Pacific/Pohnpei` - Pacific/Pohnpei * `Pacific/Ponape` - Pacific/Ponape * `Pacific/Port_Moresby` - Pacific/Port_Moresby * `Pacific/Rarotonga` - Pacific/Rarotonga * `Pacific/Saipan` - Pacific/Saipan * `Pacific/Samoa` - Pacific/Samoa * `Pacific/Tahiti` - Pacific/Tahiti * `Pacific/Tarawa` - Pacific/Tarawa * `Pacific/Tongatapu` - Pacific/Tongatapu * `Pacific/Truk` - Pacific/Truk * `Pacific/Wake` - Pacific/Wake * `Pacific/Wallis` - Pacific/Wallis * `Pacific/Yap` - Pacific/Yap * `Poland` - Poland * `Portugal` - Portugal * `ROC` - ROC * `ROK` - ROK * `Singapore` - Singapore * `Turkey` - Turkey * `UCT` - UCT * `US/Alaska` - US/Alaska * `US/Aleutian` - US/Aleutian * `US/Arizona` - US/Arizona * `US/Central` - US/Central * `US/East-Indiana` - US/East-Indiana * `US/Eastern` - US/Eastern * `US/Hawaii` - US/Hawaii * `US/Indiana-Starke` - US/Indiana-Starke * `US/Michigan` - US/Michigan * `US/Mountain` - US/Mountain * `US/Pacific` - US/Pacific * `US/Samoa` - US/Samoa * `UTC` - UTC * `Universal` - Universal * `W-SU` - W-SU * `WET` - WET * `Zulu` - Zulu
 * @export
 */
export type AlertTimezone = BlankEnum | NullEnum | TimezoneEnum;

/**
 * 
 * @export
 * @interface AlertUser
 */
export interface AlertUser {
    /**
     * 
     * @type {string}
     * @memberof AlertUser
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof AlertUser
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof AlertUser
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof AlertUser
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof AlertUser
     */
    'last_name': string;
}
/**
 * Serializer for user.
 * @export
 * @interface AlertUserRequest
 */
export interface AlertUserRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertUserRequest
     */
    'external_id': string;
}
/**
 * Custom Serializer for the Article Model.
 * @export
 * @interface Article
 */
export interface Article {
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'url': string;
    /**
     * Get Image URL if allowed for source.
     * @type {string}
     * @memberof Article
     */
    'image_url': string;
    /**
     * Determine publication date of article.
     * @type {string}
     * @memberof Article
     */
    'publication_date': string;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'publication_time': string | null;
    /**
     * Calculate publication datetime of article.
     * @type {string}
     * @memberof Article
     */
    'publication_datetime': string;
    /**
     * 
     * @type {Array<SubArticle>}
     * @memberof Article
     */
    'sub_articles': Array<SubArticle>;
    /**
     * 
     * @type {Array<CompanyArticle>}
     * @memberof Article
     */
    'company_articles': Array<CompanyArticle>;
    /**
     * 
     * @type {Array<IndustryArticle>}
     * @memberof Article
     */
    'industry_articles': Array<IndustryArticle>;
    /**
     * 
     * @type {Array<CategoryTree>}
     * @memberof Article
     */
    'categories': Array<CategoryTree>;
    /**
     * 
     * @type {ArticleSource}
     * @memberof Article
     */
    'source': ArticleSource;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'title_en': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof Article
     */
    'snippet': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof Article
     */
    'snippet_en': string;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'summary'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'summary_en'?: string | null;
    /**
     * 
     * @type {Language856Enum}
     * @memberof Article
     */
    'language': Language856Enum;
    /**
     * 
     * @type {number}
     * @memberof Article
     */
    'sentiment': number | null;
    /**
     * 
     * @type {ArticleCountry}
     * @memberof Article
     */
    'country': ArticleCountry | null;
    /**
     * 
     * @type {ArticleIsPaywalled}
     * @memberof Article
     */
    'is_paywalled': ArticleIsPaywalled | null;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Article
     */
    'updated_at': string;
}
/**
 * Article Category Aggregation Serializer.
 * @export
 * @interface ArticleCategoryAggregation
 */
export interface ArticleCategoryAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleCategoryAggregation
     */
    'count': number;
    /**
     * 
     * @type {Category}
     * @memberof ArticleCategoryAggregation
     */
    'category': Category;
}
/**
 * Article Category Aggregation Serializer.
 * @export
 * @interface ArticleCategorySentimentAggregation
 */
export interface ArticleCategorySentimentAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleCategorySentimentAggregation
     */
    'count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleCategorySentimentAggregation
     */
    'average_sentiment': number | null;
    /**
     * 
     * @type {Category}
     * @memberof ArticleCategorySentimentAggregation
     */
    'category': Category;
}
/**
 * @type ArticleCountry
 * @export
 */
export type ArticleCountry = CountryEnum | NullEnum;

/**
 * Article Country Aggregation Serializer.
 * @export
 * @interface ArticleCountryAggregation
 */
export interface ArticleCountryAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleCountryAggregation
     */
    'count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleCountryAggregation
     */
    'average_sentiment': number;
    /**
     * 
     * @type {string}
     * @memberof ArticleCountryAggregation
     */
    'country': string;
}
/**
 * Article Date Aggregation Serializer.
 * @export
 * @interface ArticleDateAggregation
 */
export interface ArticleDateAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleDateAggregation
     */
    'count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleDateAggregation
     */
    'average_sentiment': number;
    /**
     * 
     * @type {string}
     * @memberof ArticleDateAggregation
     */
    'date': string;
}
/**
 * Article Date Categories Aggregation Serializer.
 * @export
 * @interface ArticleDateCategoriesAggregation
 */
export interface ArticleDateCategoriesAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleDateCategoriesAggregation
     */
    'count': number;
    /**
     * 
     * @type {string}
     * @memberof ArticleDateCategoriesAggregation
     */
    'date': string;
    /**
     * 
     * @type {Array<ArticleCategoryAggregation>}
     * @memberof ArticleDateCategoriesAggregation
     */
    'categories': Array<ArticleCategoryAggregation>;
}
/**
 * Custom Serializer for the Article Model.
 * @export
 * @interface ArticleDetail
 */
export interface ArticleDetail {
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'url': string;
    /**
     * Get Image URL if allowed for source.
     * @type {string}
     * @memberof ArticleDetail
     */
    'image_url': string;
    /**
     * Determine publication date of article.
     * @type {string}
     * @memberof ArticleDetail
     */
    'publication_date': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'publication_time'?: string | null;
    /**
     * Calculate publication datetime of article.
     * @type {string}
     * @memberof ArticleDetail
     */
    'publication_datetime': string;
    /**
     * 
     * @type {Array<SubArticle>}
     * @memberof ArticleDetail
     */
    'sub_articles': Array<SubArticle>;
    /**
     * 
     * @type {Array<CompanyArticle>}
     * @memberof ArticleDetail
     */
    'company_articles': Array<CompanyArticle>;
    /**
     * 
     * @type {Array<IndustryArticle>}
     * @memberof ArticleDetail
     */
    'industry_articles': Array<IndustryArticle>;
    /**
     * 
     * @type {ArticleSource}
     * @memberof ArticleDetail
     */
    'source': ArticleSource;
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'title_en': string;
    /**
     * Get Title Translated.
     * @type {string}
     * @memberof ArticleDetail
     */
    'title_translated': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof ArticleDetail
     */
    'snippet': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof ArticleDetail
     */
    'snippet_en': string;
    /**
     * Get Snippet Translated.
     * @type {string}
     * @memberof ArticleDetail
     */
    'snippet_translated': string;
    /**
     * 
     * @type {number}
     * @memberof ArticleDetail
     */
    'sentiment'?: number | null;
    /**
     * 
     * @type {Language856Enum}
     * @memberof ArticleDetail
     */
    'language': Language856Enum;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof ArticleDetail
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleDetail
     */
    'updated_at': string;
}
/**
 * @type ArticleDetailCountry
 * @export
 */
export type ArticleDetailCountry = BlankEnum | CountryEnum | NullEnum;

/**
 * Data Export Serializer.
 * @export
 * @interface ArticleExport
 */
export interface ArticleExport {
    /**
     * 
     * @type {string}
     * @memberof ArticleExport
     */
    'external_id': string;
    /**
     * 
     * @type {ExportTypeEnum}
     * @memberof ArticleExport
     */
    'export_type': ExportTypeEnum;
    /**
     * 
     * @type {DataExportFileType}
     * @memberof ArticleExport
     */
    'file_type': DataExportFileType;
    /**
     * 
     * @type {ArticleFilters}
     * @memberof ArticleExport
     */
    'filters': ArticleFilters;
    /**
     * Location of exports
     * @type {string}
     * @memberof ArticleExport
     */
    'location': string | null;
    /**
     * 
     * @type {Status180Enum}
     * @memberof ArticleExport
     */
    'status': Status180Enum;
    /**
     * 
     * @type {number}
     * @memberof ArticleExport
     */
    'result_count': number | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleExport
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleExport
     */
    'updated_at': string;
}
/**
 * Data Export Serializer.
 * @export
 * @interface ArticleExportRequest
 */
export interface ArticleExportRequest {
    /**
     * 
     * @type {DataExportFileType}
     * @memberof ArticleExportRequest
     */
    'file_type': DataExportFileType;
    /**
     * 
     * @type {ArticleFiltersRequest}
     * @memberof ArticleExportRequest
     */
    'filters': ArticleFiltersRequest;
}
/**
 * Article Feedback Serializer.
 * @export
 * @interface ArticleFeedbackRead
 */
export interface ArticleFeedbackRead {
    /**
     * 
     * @type {Article}
     * @memberof ArticleFeedbackRead
     */
    'article': Article;
    /**
     * 
     * @type {ArticleFeedbackTypeEnum}
     * @memberof ArticleFeedbackRead
     */
    'feedback_type': ArticleFeedbackTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof ArticleFeedbackRead
     */
    'comment'?: string | null;
    /**
     * 
     * @type {Array<Category>}
     * @memberof ArticleFeedbackRead
     */
    'categories': Array<Category>;
    /**
     * 
     * @type {UserList}
     * @memberof ArticleFeedbackRead
     */
    'user': UserList;
}
/**
 * * `FALSE_POSITIVE` - FALSE_POSITIVE * `NO_RISK` - NO_RISK * `RISK_CONFIRMED` - RISK_CONFIRMED * `INCORRECT_COMPANY` - INCORRECT_COMPANY * `IRRELEVANT_ARTICLE` - IRRELEVANT_ARTICLE * `INCORRECTLY_CLASSIFIED` - INCORRECTLY_CLASSIFIED * `INCORRECT_INDUSTRY` - INCORRECT_INDUSTRY * `ARTICLE_NOT_ACCESSIBLE` - ARTICLE_NOT_ACCESSIBLE * `INCORRECT_SENTIMENT` - INCORRECT_SENTIMENT * `INCORRECT_DATE` - INCORRECT_DATE * `NOT_A_NEWS_ARTICLE` - NOT_A_NEWS_ARTICLE * `DUPLICATE_NEWS_ARTICLE` - DUPLICATE_NEWS_ARTICLE * `UNRELIABLE_SOURCE` - UNRELIABLE_SOURCE * `OTHER` - OTHER
 * @export
 * @enum {string}
 */

export const ArticleFeedbackTypeEnum = {
    FalsePositive: 'FALSE_POSITIVE',
    NoRisk: 'NO_RISK',
    RiskConfirmed: 'RISK_CONFIRMED',
    IncorrectCompany: 'INCORRECT_COMPANY',
    IrrelevantArticle: 'IRRELEVANT_ARTICLE',
    IncorrectlyClassified: 'INCORRECTLY_CLASSIFIED',
    IncorrectIndustry: 'INCORRECT_INDUSTRY',
    ArticleNotAccessible: 'ARTICLE_NOT_ACCESSIBLE',
    IncorrectSentiment: 'INCORRECT_SENTIMENT',
    IncorrectDate: 'INCORRECT_DATE',
    NotANewsArticle: 'NOT_A_NEWS_ARTICLE',
    DuplicateNewsArticle: 'DUPLICATE_NEWS_ARTICLE',
    UnreliableSource: 'UNRELIABLE_SOURCE',
    Other: 'OTHER'
} as const;

export type ArticleFeedbackTypeEnum = typeof ArticleFeedbackTypeEnum[keyof typeof ArticleFeedbackTypeEnum];


/**
 * Article Feedback Serializer.
 * @export
 * @interface ArticleFeedbackWrite
 */
export interface ArticleFeedbackWrite {
    /**
     * 
     * @type {string}
     * @memberof ArticleFeedbackWrite
     */
    'article': string;
    /**
     * 
     * @type {ArticleFeedbackTypeEnum}
     * @memberof ArticleFeedbackWrite
     */
    'feedback_type': ArticleFeedbackTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof ArticleFeedbackWrite
     */
    'comment'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFeedbackWrite
     */
    'categories': Array<string>;
}
/**
 * Article Feedback Serializer.
 * @export
 * @interface ArticleFeedbackWriteRequest
 */
export interface ArticleFeedbackWriteRequest {
    /**
     * 
     * @type {string}
     * @memberof ArticleFeedbackWriteRequest
     */
    'article': string;
    /**
     * 
     * @type {ArticleFeedbackTypeEnum}
     * @memberof ArticleFeedbackWriteRequest
     */
    'feedback_type': ArticleFeedbackTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof ArticleFeedbackWriteRequest
     */
    'comment'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFeedbackWriteRequest
     */
    'categories': Array<string>;
}
/**
 * Article Filter Serializer.
 * @export
 * @interface ArticleFilters
 */
export interface ArticleFilters {
    /**
     * 
     * @type {string}
     * @memberof ArticleFilters
     */
    'query'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'portfolios'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'countries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'languages'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'duns_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'excluded_companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'global_ultimates'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'registration_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'industries'?: Array<string> | null;
    /**
     * 
     * @type {ArticleFiltersMediaType}
     * @memberof ArticleFilters
     */
    'media_type'?: ArticleFiltersMediaType | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFilters
     */
    'category_set'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFilters
     */
    'excluded_categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFilters
     */
    'parent_category'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleFilters
     */
    'include_clustered_articles'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleFilters
     */
    'sentiment'?: boolean | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFilters
     */
    'min_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFilters
     */
    'max_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFilters
     */
    'min_created_at'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleFilters
     */
    'is_material'?: boolean | null;
}
/**
 * @type ArticleFiltersMediaType
 * @export
 */
export type ArticleFiltersMediaType = MediaTypeEnum | NullEnum;

/**
 * Article Filter Serializer.
 * @export
 * @interface ArticleFiltersRequest
 */
export interface ArticleFiltersRequest {
    /**
     * 
     * @type {string}
     * @memberof ArticleFiltersRequest
     */
    'query'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'portfolios'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'countries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'languages'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'duns_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'excluded_companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'global_ultimates'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'registration_numbers'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'industries'?: Array<string> | null;
    /**
     * 
     * @type {ArticleFiltersMediaType}
     * @memberof ArticleFiltersRequest
     */
    'media_type'?: ArticleFiltersMediaType | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFiltersRequest
     */
    'category_set'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof ArticleFiltersRequest
     */
    'excluded_categories'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFiltersRequest
     */
    'parent_category'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleFiltersRequest
     */
    'include_clustered_articles'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleFiltersRequest
     */
    'sentiment'?: boolean | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFiltersRequest
     */
    'min_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFiltersRequest
     */
    'max_publication_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleFiltersRequest
     */
    'min_created_at'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleFiltersRequest
     */
    'is_material'?: boolean | null;
}
/**
 * Article Industry Aggregation Serializer.
 * @export
 * @interface ArticleIndustryAggregation
 */
export interface ArticleIndustryAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleIndustryAggregation
     */
    'count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleIndustryAggregation
     */
    'average_sentiment': number;
    /**
     * 
     * @type {IndustryCode}
     * @memberof ArticleIndustryAggregation
     */
    'industry_code': IndustryCode;
}
/**
 * Article Industry Division Aggregation Serializer.
 * @export
 * @interface ArticleIndustryDivisionAggregation
 */
export interface ArticleIndustryDivisionAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleIndustryDivisionAggregation
     */
    'count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleIndustryDivisionAggregation
     */
    'average_sentiment': number;
    /**
     * 
     * @type {DivisionCode}
     * @memberof ArticleIndustryDivisionAggregation
     */
    'division': DivisionCode;
}
/**
 * @type ArticleIsPaywalled
 * @export
 */
export type ArticleIsPaywalled = IsPaywalledEnum | NullEnum;

/**
 * Serialize Article Keywords Aggregation.
 * @export
 * @interface ArticleKeywordAggregation
 */
export interface ArticleKeywordAggregation {
    /**
     * 
     * @type {number}
     * @memberof ArticleKeywordAggregation
     */
    'count': number;
    /**
     * 
     * @type {string}
     * @memberof ArticleKeywordAggregation
     */
    'color': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleKeywordAggregation
     */
    'keyword': string;
}
/**
 * Custom Serializer for the Article Model.
 * @export
 * @interface ArticleRequest
 */
export interface ArticleRequest {
    /**
     * 
     * @type {string}
     * @memberof ArticleRequest
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleRequest
     */
    'summary'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleRequest
     */
    'summary_en'?: string | null;
}
/**
 * 
 * @export
 * @interface ArticleSource
 */
export interface ArticleSource {
    /**
     * 
     * @type {string}
     * @memberof ArticleSource
     */
    'domain': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleSource
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleSource
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof ArticleSource
     */
    'social_references'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof ArticleSource
     */
    'paywall'?: boolean;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof ArticleSource
     */
    'country'?: ArticleDetailCountry | null;
}
/**
 * Article Statistics Serializer.
 * @export
 * @interface ArticleStatistics
 */
export interface ArticleStatistics {
    /**
     * 
     * @type {number}
     * @memberof ArticleStatistics
     */
    'country_count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleStatistics
     */
    'article_count': number;
    /**
     * 
     * @type {number}
     * @memberof ArticleStatistics
     */
    'source_count': number;
}
/**
 * Article summary Serializer.
 * @export
 * @interface ArticleSummary
 */
export interface ArticleSummary {
    /**
     * 
     * @type {string}
     * @memberof ArticleSummary
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ArticleSummary
     */
    'summary': string | null;
    /**
     * 
     * @type {string}
     * @memberof ArticleSummary
     */
    'finished_at': string | null;
    /**
     * 
     * @type {ArticleSummaryStatusEnum}
     * @memberof ArticleSummary
     */
    'status': ArticleSummaryStatusEnum;
    /**
     * 
     * @type {number}
     * @memberof ArticleSummary
     */
    'articles_count': number | null;
    /**
     * 
     * @type {number}
     * @memberof ArticleSummary
     */
    'summarized_articles_count': number | null;
}
/**
 * * `PENDING` - Pending * `COMPLETED` - Completed * `FAILED` - Failed
 * @export
 * @enum {string}
 */

export const ArticleSummaryStatusEnum = {
    Pending: 'PENDING',
    Completed: 'COMPLETED',
    Failed: 'FAILED'
} as const;

export type ArticleSummaryStatusEnum = typeof ArticleSummaryStatusEnum[keyof typeof ArticleSummaryStatusEnum];


/**
 * Article URL serializer.
 * @export
 * @interface ArticleURL
 */
export interface ArticleURL {
    /**
     * 
     * @type {string}
     * @memberof ArticleURL
     */
    'url': string;
}
/**
 * Article Annotations Serializer.
 * @export
 * @interface ArticlesAnnotations
 */
export interface ArticlesAnnotations {
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof ArticlesAnnotations
     */
    'countries'?: { [key: string]: number; };
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof ArticlesAnnotations
     */
    'languages'?: { [key: string]: number; };
    /**
     * 
     * @type {Array<CompanyAnnotation>}
     * @memberof ArticlesAnnotations
     */
    'companies'?: Array<CompanyAnnotation>;
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof ArticlesAnnotations
     */
    'parent_categories'?: { [key: string]: number; };
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof ArticlesAnnotations
     */
    'categories'?: { [key: string]: number; };
}
/**
 * * `CUSTOMER` - Customer * `SUPPORT` - Support
 * @export
 * @enum {string}
 */

export const AuthorTypeEnum = {
    Customer: 'CUSTOMER',
    Support: 'SUPPORT'
} as const;

export type AuthorTypeEnum = typeof AuthorTypeEnum[keyof typeof AuthorTypeEnum];


/**
 * * `risk_confirmed` - Risk confirmed * `no_risk` - No risk * `false_positive` - False positive * `discard` - Discard * `to_investigate` - To investigate
 * @export
 * @enum {string}
 */

export const AutomatedFalsePositiveRatingEnum = {
    RiskConfirmed: 'risk_confirmed',
    NoRisk: 'no_risk',
    FalsePositive: 'false_positive',
    Discard: 'discard',
    ToInvestigate: 'to_investigate'
} as const;

export type AutomatedFalsePositiveRatingEnum = typeof AutomatedFalsePositiveRatingEnum[keyof typeof AutomatedFalsePositiveRatingEnum];


/**
 * Custom Serializer for the Company Model.  :note: founding_date: DateField is not supported by elasticsearch
 * @export
 * @interface BasicCompany
 */
export interface BasicCompany {
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'duns_number'?: string | null;
    /**
     * Get LEI (Legal Entity Identifier). None for OpenSearch-sourced instances.
     * @type {string}
     * @memberof BasicCompany
     */
    'lei': string;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'clean_name': string;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'slug': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof BasicCompany
     */
    'country': CountryEnum;
    /**
     * Indicates whether the company is out of business.
     * @type {boolean}
     * @memberof BasicCompany
     */
    'is_out_of_business'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'ticker_symbol'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'address_postal'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof BasicCompany
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof BasicCompany
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'website_domain'?: string | null;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof BasicCompany
     */
    'social_logo': string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof BasicCompany
     */
    'review_count'?: number;
    /**
     * Average review score
     * @type {number}
     * @memberof BasicCompany
     */
    'review_average_score'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'dnb_website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'dnb_website_domain'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'global_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'global_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'domestic_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'domestic_ultimate_name'?: string | null;
    /**
     * Date when data was fetched from Dun and Bradstreet.
     * @type {string}
     * @memberof BasicCompany
     */
    'dnb_fetched_at'?: string | null;
    /**
     * Get Founding Date.
     * @type {string}
     * @memberof BasicCompany
     */
    'founding_date': string;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BasicCompany
     */
    'updated_at': string;
}
/**
 * Batch Portfolio Registration Serializer.  Serializer used for registering a new companies in batch.
 * @export
 * @interface BatchCompanyRegistration
 */
export interface BatchCompanyRegistration {
    /**
     * 
     * @type {BatchCompanyRegistrationCompany}
     * @memberof BatchCompanyRegistration
     */
    'company'?: BatchCompanyRegistrationCompany | null;
    /**
     * Get Registration Status text.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'status_text': string;
    /**
     * Get Status Name (full name translated).
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'status_name': string;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'duns_number'?: string | null;
    /**
     * DUNS number the system resolved to for this registration — either the user-supplied duns_number or the DUNS returned by the DNB Match API. Used as the canonical DUNS for company creation.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'resolved_duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof BatchCompanyRegistration
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * Street address used to disambiguate match candidates. Sent to D&B Match as streetAddressLine1.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'street_address'?: string | null;
    /**
     * Postal / ZIP code used to disambiguate match candidates.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'postal_code'?: string | null;
    /**
     * City / locality used to disambiguate match candidates.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'address_locality'?: string | null;
    /**
     * State / province / region used to disambiguate match candidates.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'address_region'?: string | null;
    /**
     * If enabled the registration will be processed with priority.
     * @type {boolean}
     * @memberof BatchCompanyRegistration
     */
    'is_priority'?: boolean;
    /**
     * 
     * @type {Status5e0Enum}
     * @memberof BatchCompanyRegistration
     */
    'status'?: Status5e0Enum;
    /**
     * 
     * @type {number}
     * @memberof BatchCompanyRegistration
     */
    'progress'?: number;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'api_key'?: string | null;
    /**
     * Email company report about this registration
     * @type {boolean}
     * @memberof BatchCompanyRegistration
     */
    'email_company_report'?: boolean;
    /**
     * Datestamp for when report was send to user.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'company_report_send_at'?: string | null;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'customer_reference'?: string | null;
    /**
     * With this option enabled the registration search will try to match more terms and shorter variants of the name. Only use this if you can\'t do an manual match because it can give a lot of false positive results.
     * @type {boolean}
     * @memberof BatchCompanyRegistration
     */
    'broad_fuzzy_search'?: boolean;
    /**
     * With this option enabled a missing company investigation is submitted automatically when the registration cannot be identified, instead of failing with company not found.
     * @type {boolean}
     * @memberof BatchCompanyRegistration
     */
    'submit_investigation_when_not_identified'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'updated_at': string;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'started_at'?: string | null;
    /**
     * Datestamp on when the registration was complete. If failed this is empty.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'finished_at': string | null;
    /**
     * Datestamp on when the registration was failed, if succeeded this is empty
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'failed_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'error'?: string | null;
    /**
     * Liveness heartbeat refreshed on every requeue by the profile\'s concurrency cap; cleared on admission.
     * @type {string}
     * @memberof BatchCompanyRegistration
     */
    'capacity_waiting_since'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof BatchCompanyRegistration
     */
    'missing_company_investigation'?: number | null;
}
/**
 * 
 * @export
 * @interface BatchCompanyRegistrationCompany
 */
export interface BatchCompanyRegistrationCompany {
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof BatchCompanyRegistrationCompany
     */
    'country': CountryEnum;
    /**
     * 
     * @type {PortfolioCompanyDetailGlobalUltimate}
     * @memberof BatchCompanyRegistrationCompany
     */
    'global_ultimate': PortfolioCompanyDetailGlobalUltimate | null;
    /**
     * Icon of the found website
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'website_icon_url': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'address_street': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'address_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'address_place': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'address_region': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'address_postal': string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof BatchCompanyRegistrationCompany
     */
    'review_count': number;
    /**
     * Average review score
     * @type {number}
     * @memberof BatchCompanyRegistrationCompany
     */
    'review_average_score': number | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof BatchCompanyRegistrationCompany
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof BatchCompanyRegistrationCompany
     */
    'social_logo': string | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof BatchCompanyRegistrationCompany
     */
    'article_count': number;
    /**
     * 
     * @type {Array<TradeNameSchema>}
     * @memberof BatchCompanyRegistrationCompany
     */
    'trade_names': Array<TradeNameSchema>;
}
/**
 * Batch Portfolio Registration Serializer.  Serializer used for registering a new companies in batch.
 * @export
 * @interface BatchCompanyRegistrationRequest
 */
export interface BatchCompanyRegistrationRequest {
    /**
     * 
     * @type {BatchCompanyRegistrationRequestCompany}
     * @memberof BatchCompanyRegistrationRequest
     */
    'company'?: BatchCompanyRegistrationRequestCompany | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'portfolio': string;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof BatchCompanyRegistrationRequest
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * Street address used to disambiguate match candidates. Sent to D&B Match as streetAddressLine1.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'street_address'?: string | null;
    /**
     * Postal / ZIP code used to disambiguate match candidates.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'postal_code'?: string | null;
    /**
     * City / locality used to disambiguate match candidates.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'address_locality'?: string | null;
    /**
     * State / province / region used to disambiguate match candidates.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'address_region'?: string | null;
    /**
     * If enabled the registration will be processed with priority.
     * @type {boolean}
     * @memberof BatchCompanyRegistrationRequest
     */
    'is_priority'?: boolean;
    /**
     * 
     * @type {Status5e0Enum}
     * @memberof BatchCompanyRegistrationRequest
     */
    'status'?: Status5e0Enum;
    /**
     * 
     * @type {number}
     * @memberof BatchCompanyRegistrationRequest
     */
    'progress'?: number;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'api_key'?: string | null;
    /**
     * Email company report about this registration
     * @type {boolean}
     * @memberof BatchCompanyRegistrationRequest
     */
    'email_company_report'?: boolean;
    /**
     * Datestamp for when report was send to user.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'company_report_send_at'?: string | null;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'customer_reference'?: string | null;
    /**
     * With this option enabled the registration search will try to match more terms and shorter variants of the name. Only use this if you can\'t do an manual match because it can give a lot of false positive results.
     * @type {boolean}
     * @memberof BatchCompanyRegistrationRequest
     */
    'broad_fuzzy_search'?: boolean;
    /**
     * With this option enabled a missing company investigation is submitted automatically when the registration cannot be identified, instead of failing with company not found.
     * @type {boolean}
     * @memberof BatchCompanyRegistrationRequest
     */
    'submit_investigation_when_not_identified'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'started_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'error'?: string | null;
    /**
     * Liveness heartbeat refreshed on every requeue by the profile\'s concurrency cap; cleared on admission.
     * @type {string}
     * @memberof BatchCompanyRegistrationRequest
     */
    'capacity_waiting_since'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof BatchCompanyRegistrationRequest
     */
    'missing_company_investigation'?: number | null;
}
/**
 * 
 * @export
 * @interface BatchCompanyRegistrationRequestCompany
 */
export interface BatchCompanyRegistrationRequestCompany {
    /**
     * 
     * @type {string}
     * @memberof BatchCompanyRegistrationRequestCompany
     */
    'external_id': string;
}
/**
 * Serializer for creating a batch compliance check.
 * @export
 * @interface BatchComplianceCheck
 */
export interface BatchComplianceCheck {
    /**
     * 
     * @type {string}
     * @memberof BatchComplianceCheck
     */
    'external_id': string;
    /**
     * If directors should be screened.
     * @type {boolean}
     * @memberof BatchComplianceCheck
     */
    'directors_screening_enabled'?: boolean;
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof BatchComplianceCheck
     */
    'director_role_groups'?: Array<DirectorRoleGroupsEnum> | null;
    /**
     * If ultimate owners should be screened.
     * @type {boolean}
     * @memberof BatchComplianceCheck
     */
    'ubo_screening_enabled'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof BatchComplianceCheck
     */
    'company_count': number;
    /**
     * 
     * @type {number}
     * @memberof BatchComplianceCheck
     */
    'director_count': number;
    /**
     * 
     * @type {number}
     * @memberof BatchComplianceCheck
     */
    'ubo_count': number;
    /**
     * 
     * @type {number}
     * @memberof BatchComplianceCheck
     */
    'total_entity_count': number;
    /**
     * 
     * @type {number}
     * @memberof BatchComplianceCheck
     */
    'compliance_check_credits_left': number | null;
    /**
     * 
     * @type {number}
     * @memberof BatchComplianceCheck
     */
    'compliance_check_credits_required': number;
    /**
     * 
     * @type {boolean}
     * @memberof BatchComplianceCheck
     */
    'sufficient_credits': boolean;
    /**
     * 
     * @type {string}
     * @memberof BatchComplianceCheck
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BatchComplianceCheck
     */
    'updated_at': string;
}
/**
 * Serializer for creating a batch compliance check.
 * @export
 * @interface BatchComplianceCheckRequest
 */
export interface BatchComplianceCheckRequest {
    /**
     * 
     * @type {string}
     * @memberof BatchComplianceCheckRequest
     */
    'portfolio_id'?: string;
    /**
     * 
     * @type {Array<string>}
     * @memberof BatchComplianceCheckRequest
     */
    'company_ids'?: Array<string>;
    /**
     * If directors should be screened.
     * @type {boolean}
     * @memberof BatchComplianceCheckRequest
     */
    'directors_screening_enabled'?: boolean;
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof BatchComplianceCheckRequest
     */
    'director_role_groups'?: Array<DirectorRoleGroupsEnum> | null;
    /**
     * If ultimate owners should be screened.
     * @type {boolean}
     * @memberof BatchComplianceCheckRequest
     */
    'ubo_screening_enabled'?: boolean;
}
/**
 * Portfolio User Update Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface BatchRegistration
 */
export interface BatchRegistration {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistration
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistration
     */
    'name'?: string | null;
    /**
     * 
     * @type {any}
     * @memberof BatchRegistration
     */
    'file_headers': any | null;
    /**
     * Get Upload URL.
     * @type {string}
     * @memberof BatchRegistration
     */
    'upload_url': string;
    /**
     * Send Registration Report after Registration
     * @type {boolean}
     * @memberof BatchRegistration
     */
    'send_registration_report'?: boolean;
    /**
     * Upload using S3 so that the file is parsed and processed on the server instead of on the client, this way the client can close their browser and does not notice the import CPU usage.
     * @type {boolean}
     * @memberof BatchRegistration
     */
    'upload_import'?: boolean;
    /**
     * 
     * @type {BatchRegistrationUploadFileType}
     * @memberof BatchRegistration
     */
    'upload_file_type'?: BatchRegistrationUploadFileType | null;
    /**
     * 
     * @type {BatchRegistrationStatus}
     * @memberof BatchRegistration
     */
    'status': BatchRegistrationStatus | null;
    /**
     * Get Localized status text.
     * @type {string}
     * @memberof BatchRegistration
     */
    'status_text': string;
    /**
     * 
     * @type {BatchRegistrationMappingData}
     * @memberof BatchRegistration
     */
    'mapping_data': BatchRegistrationMappingData | null;
    /**
     * Clear Portfolio after registration with old companies.
     * @type {boolean}
     * @memberof BatchRegistration
     */
    'clear_portfolio_not_registered'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof BatchRegistration
     */
    'progress': number | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistration
     */
    'created_at': string;
    /**
     * Datestamp on when headers of file where parsed.
     * @type {string}
     * @memberof BatchRegistration
     */
    'headers_parsed_at': string | null;
    /**
     * Datestamp on when the registration was complete.
     * @type {string}
     * @memberof BatchRegistration
     */
    'finished_at': string | null;
    /**
     * 
     * @type {BatchRegistrationCompaniesSummary}
     * @memberof BatchRegistration
     */
    'companies_summary': BatchRegistrationCompaniesSummary;
}
/**
 * 
 * @export
 * @interface BatchRegistrationCompaniesSummary
 */
export interface BatchRegistrationCompaniesSummary {
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationCompaniesSummary
     */
    'total'?: number;
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationCompaniesSummary
     */
    'finished'?: number;
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationCompaniesSummary
     */
    'failed'?: number;
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationCompaniesSummary
     */
    'pending'?: number;
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationCompaniesSummary
     */
    'skipped'?: number;
}
/**
 * Serializer for individual company status within a batch registration.
 * @export
 * @interface BatchRegistrationCompanyStatus
 */
export interface BatchRegistrationCompanyStatus {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'registration_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'primary_name': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'country': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'customer_reference': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'status': string;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'status_text': string | null;
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationCompanyStatus
     */
    'progress': number;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'error': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'started_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'finished_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'failed_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationCompanyStatus
     */
    'company_id': string | null;
}
/**
 * Serializer for rows skipped during batch registration due to invalid input.
 * @export
 * @interface BatchRegistrationError
 */
export interface BatchRegistrationError {
    /**
     * 
     * @type {number}
     * @memberof BatchRegistrationError
     */
    'id': number;
    /**
     * Zero-based index of the row in the uploaded file that failed.
     * @type {number}
     * @memberof BatchRegistrationError
     */
    'row_index': number;
    /**
     * Raw data of the row that caused the error.
     * @type {any}
     * @memberof BatchRegistrationError
     */
    'row_data': any;
    /**
     * Machine-readable error code.
     * @type {string}
     * @memberof BatchRegistrationError
     */
    'error_code': string;
    /**
     * Human-readable description of why this row was skipped.
     * @type {string}
     * @memberof BatchRegistrationError
     */
    'error_message': string;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationError
     */
    'created_at': string;
}
/**
 * Custom Serializer for the Batch Registration Field Mapping.
 * @export
 * @interface BatchRegistrationFieldMapping
 */
export interface BatchRegistrationFieldMapping {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'country'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'street_address'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'postal_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'const_portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'const_portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'const_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMapping
     */
    'const_country'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof BatchRegistrationFieldMapping
     */
    'const_clear_portfolio_not_registered'?: boolean | null;
}
/**
 * Custom Serializer for the Batch Registration Field Mapping.
 * @export
 * @interface BatchRegistrationFieldMappingRequest
 */
export interface BatchRegistrationFieldMappingRequest {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'country'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'street_address'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'postal_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'const_portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'const_portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'const_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'const_country'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof BatchRegistrationFieldMappingRequest
     */
    'const_clear_portfolio_not_registered'?: boolean | null;
}
/**
 * 
 * @export
 * @interface BatchRegistrationMappingData
 */
export interface BatchRegistrationMappingData {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'country'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'street_address'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'postal_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'const_portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'const_portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'const_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationMappingData
     */
    'const_country'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof BatchRegistrationMappingData
     */
    'const_clear_portfolio_not_registered'?: boolean | null;
}
/**
 * Portfolio User Update Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface BatchRegistrationRequest
 */
export interface BatchRegistrationRequest {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequest
     */
    'name'?: string | null;
    /**
     * Send Registration Report after Registration
     * @type {boolean}
     * @memberof BatchRegistrationRequest
     */
    'send_registration_report'?: boolean;
    /**
     * Upload using S3 so that the file is parsed and processed on the server instead of on the client, this way the client can close their browser and does not notice the import CPU usage.
     * @type {boolean}
     * @memberof BatchRegistrationRequest
     */
    'upload_import'?: boolean;
    /**
     * 
     * @type {BatchRegistrationUploadFileType}
     * @memberof BatchRegistrationRequest
     */
    'upload_file_type'?: BatchRegistrationUploadFileType | null;
    /**
     * 
     * @type {BatchRegistrationRequestMappingData}
     * @memberof BatchRegistrationRequest
     */
    'mapping_data': BatchRegistrationRequestMappingData | null;
    /**
     * Clear Portfolio after registration with old companies.
     * @type {boolean}
     * @memberof BatchRegistrationRequest
     */
    'clear_portfolio_not_registered'?: boolean;
}
/**
 * 
 * @export
 * @interface BatchRegistrationRequestMappingData
 */
export interface BatchRegistrationRequestMappingData {
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'country'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'street_address'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'postal_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'const_portfolio_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'const_portfolio_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'const_customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BatchRegistrationRequestMappingData
     */
    'const_country'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof BatchRegistrationRequestMappingData
     */
    'const_clear_portfolio_not_registered'?: boolean | null;
}
/**
 * @type BatchRegistrationStatus
 * @export
 */
export type BatchRegistrationStatus = BatchRegistrationStatusEnum | NullEnum;

/**
 * * `PENDING_PROCESSING` - Pending file for processing * `FILE_TYPE_MISSING` - File Type is Missing for batch registration. * `DOWNLOADING_FILE` - Downloading File for File server. * `DOWNLOADED_FILE` - Downloaded batch file to processing server. * `FAILED_DOWNLOADING_FILE` - Failed downloading batch file to processing server, contact support. * `PARSING_FILE` - Parsing uploaded file * `FAILED_PARSING_FILE` - Failed Parsing uploaded file, contact support * `WAITING_FIELD_MAPPING` - Waiting for field mappings * `PARSING_MAPPING` - Parsing Field Mapping. * `PARSED_MAPPING` - Finished Parsing Field Mapping. * `FAILED_PARSING_MAPPING` - Failed Parsing Field Mapping, contact support. * `PROCESSING_REGISTRATIONS` - Processing Registrations * `STARTING_SEARCH` - Started Searching for Companies * `FINISHED` - Finished * `MISSING_PORTFOLIO_REFERENCE` - Missing Portfolio Reference for registrations * `PORTFOLIO_ACCESS_NOT_ALLOWED` - No Access to this Portfolio * `CANCELLED` - Batch registration cancelled * `FAILED` - Batch registration failed
 * @export
 * @enum {string}
 */

export const BatchRegistrationStatusEnum = {
    PendingProcessing: 'PENDING_PROCESSING',
    FileTypeMissing: 'FILE_TYPE_MISSING',
    DownloadingFile: 'DOWNLOADING_FILE',
    DownloadedFile: 'DOWNLOADED_FILE',
    FailedDownloadingFile: 'FAILED_DOWNLOADING_FILE',
    ParsingFile: 'PARSING_FILE',
    FailedParsingFile: 'FAILED_PARSING_FILE',
    WaitingFieldMapping: 'WAITING_FIELD_MAPPING',
    ParsingMapping: 'PARSING_MAPPING',
    ParsedMapping: 'PARSED_MAPPING',
    FailedParsingMapping: 'FAILED_PARSING_MAPPING',
    ProcessingRegistrations: 'PROCESSING_REGISTRATIONS',
    StartingSearch: 'STARTING_SEARCH',
    Finished: 'FINISHED',
    MissingPortfolioReference: 'MISSING_PORTFOLIO_REFERENCE',
    PortfolioAccessNotAllowed: 'PORTFOLIO_ACCESS_NOT_ALLOWED',
    Cancelled: 'CANCELLED',
    Failed: 'FAILED'
} as const;

export type BatchRegistrationStatusEnum = typeof BatchRegistrationStatusEnum[keyof typeof BatchRegistrationStatusEnum];


/**
 * @type BatchRegistrationUploadFileType
 * File Type to upload  * `EXCEL` - Excel * `CSV` - Comma Delimited File * `JSON` - JSON
 * @export
 */
export type BatchRegistrationUploadFileType = BlankEnum | NullEnum | UploadFileTypeEnum;

/**
 * Data Export Serializer.
 * @export
 * @interface BillingDataExport
 */
export interface BillingDataExport {
    /**
     * 
     * @type {string}
     * @memberof BillingDataExport
     */
    'external_id': string;
    /**
     * 
     * @type {ExportTypeEnum}
     * @memberof BillingDataExport
     */
    'export_type': ExportTypeEnum;
    /**
     * 
     * @type {DataExportFileType}
     * @memberof BillingDataExport
     */
    'file_type'?: DataExportFileType;
    /**
     * 
     * @type {any}
     * @memberof BillingDataExport
     */
    'filters'?: any | null;
    /**
     * Location of exports
     * @type {string}
     * @memberof BillingDataExport
     */
    'location': string | null;
    /**
     * 
     * @type {Status180Enum}
     * @memberof BillingDataExport
     */
    'status': Status180Enum;
    /**
     * 
     * @type {number}
     * @memberof BillingDataExport
     */
    'result_count': number | null;
    /**
     * 
     * @type {string}
     * @memberof BillingDataExport
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BillingDataExport
     */
    'updated_at': string;
}
/**
 * Data Export Serializer.
 * @export
 * @interface BillingDataExportRequest
 */
export interface BillingDataExportRequest {
    /**
     * 
     * @type {DataExportFileType}
     * @memberof BillingDataExportRequest
     */
    'file_type'?: DataExportFileType;
    /**
     * 
     * @type {any}
     * @memberof BillingDataExportRequest
     */
    'filters'?: any | null;
}
/**
 * 
 * @export
 * @interface BillingNotification
 */
export interface BillingNotification {
    /**
     * 
     * @type {string}
     * @memberof BillingNotification
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BillingNotification
     */
    'name': string;
    /**
     * Company usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof BillingNotification
     */
    'company_usage_threshold'?: number | null;
    /**
     * UBO call usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof BillingNotification
     */
    'ubo_call_usage_threshold'?: number | null;
    /**
     * Compliance check usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof BillingNotification
     */
    'compliance_check_usage_threshold'?: number | null;
    /**
     * Breached at timestamp.
     * @type {string}
     * @memberof BillingNotification
     */
    'breached_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof BillingNotification
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BillingNotification
     */
    'updated_at': string;
}
/**
 * 
 * @export
 * @interface BillingNotificationRequest
 */
export interface BillingNotificationRequest {
    /**
     * 
     * @type {string}
     * @memberof BillingNotificationRequest
     */
    'name': string;
    /**
     * Company usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof BillingNotificationRequest
     */
    'company_usage_threshold'?: number | null;
    /**
     * UBO call usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof BillingNotificationRequest
     */
    'ubo_call_usage_threshold'?: number | null;
    /**
     * Compliance check usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof BillingNotificationRequest
     */
    'compliance_check_usage_threshold'?: number | null;
}
/**
 * Binder.
 * @export
 * @interface Binder
 */
export interface Binder {
    /**
     * 
     * @type {string}
     * @memberof Binder
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Binder
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof Binder
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Binder
     */
    'updated_at': string;
    /**
     * 
     * @type {boolean}
     * @memberof Binder
     */
    'contains_article': boolean | null;
    /**
     * 
     * @type {boolean}
     * @memberof Binder
     */
    'contains_compliance_entity_result': boolean | null;
}
/**
 * Binder Company Serializer.
 * @export
 * @interface BinderCompany
 */
export interface BinderCompany {
    /**
     * 
     * @type {string}
     * @memberof BinderCompany
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof BinderCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof BinderCompany
     */
    'country': CountryEnum;
    /**
     * Icon of the found website
     * @type {string}
     * @memberof BinderCompany
     */
    'website_icon_url'?: string | null;
}
/**
 * Binder Compliance Check Serializer.
 * @export
 * @interface BinderComplianceCheck
 */
export interface BinderComplianceCheck {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceCheck
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceCheckCompany}
     * @memberof BinderComplianceCheck
     */
    'company': BinderComplianceCheckCompany | null;
}
/**
 * 
 * @export
 * @interface BinderComplianceCheckCompany
 */
export interface BinderComplianceCheckCompany {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceCheckCompany
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceCheckCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof BinderComplianceCheckCompany
     */
    'country': CountryEnum;
    /**
     * Icon of the found website
     * @type {string}
     * @memberof BinderComplianceCheckCompany
     */
    'website_icon_url'?: string | null;
}
/**
 * Binder Compliance Entity Serializer.
 * @export
 * @interface BinderComplianceEntity
 */
export interface BinderComplianceEntity {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntity
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceCheck}
     * @memberof BinderComplianceEntity
     */
    'compliance_check': BinderComplianceCheck;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntity
     */
    'entity_role': string;
    /**
     * 
     * @type {ComplianceEntityTypeEnum}
     * @memberof BinderComplianceEntity
     */
    'entity_type'?: ComplianceEntityTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntity
     */
    'name': string;
}
/**
 * Binder Compliance Entity Serializer.
 * @export
 * @interface BinderComplianceEntityRequest
 */
export interface BinderComplianceEntityRequest {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityRequest
     */
    'entity_role': string;
    /**
     * 
     * @type {ComplianceEntityTypeEnum}
     * @memberof BinderComplianceEntityRequest
     */
    'entity_type'?: ComplianceEntityTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityRequest
     */
    'name': string;
}
/**
 * Binder compliance entity result serializer.  Served from an Elasticsearch document, not the annotated Django queryset, so binder_count is dropped here rather than shipped as an always-0 value.
 * @export
 * @interface BinderComplianceEntityResult
 */
export interface BinderComplianceEntityResult {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'external_id': string;
    /**
     * 
     * @type {ResultTypeEnum}
     * @memberof BinderComplianceEntityResult
     */
    'result_type': ResultTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof BinderComplianceEntityResult
     */
    'confidence': number | null;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof BinderComplianceEntityResult
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'false_positive_rating_datetime': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'false_positive_rating_user': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultAutomatedFalsePositiveRating}
     * @memberof BinderComplianceEntityResult
     */
    'automated_false_positive_rating': BinderComplianceEntityResultAutomatedFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'automated_false_positive_rating_datetime': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'automated_false_positive_rating_comments': string | null;
    /**
     * Return result type description.
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'result_type_description': string;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'image'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'title': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'title_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'text': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'text_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'formatted_title_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'formatted_text_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'formatted_title': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'formatted_text': string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultPepTier}
     * @memberof BinderComplianceEntityResult
     */
    'pep_tier': BinderComplianceEntityResultPepTier | null;
    /**
     * 
     * @type {BinderComplianceEntityResultPepRelationship}
     * @memberof BinderComplianceEntityResult
     */
    'pep_relationship': BinderComplianceEntityResultPepRelationship | null;
    /**
     * Name of the PEP this person is related to (family / associate only).
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'pep_related_to': string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultSoeRelationshipType}
     * @memberof BinderComplianceEntityResult
     */
    'soe_relationship_type': BinderComplianceEntityResultSoeRelationshipType | null;
    /**
     * Percentage of government ownership, when reported.
     * @type {number}
     * @memberof BinderComplianceEntityResult
     */
    'soe_ownership_percentage': number | null;
    /**
     * Immediate parent or controlling government entity.
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'soe_parent_entity': string | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultSource>}
     * @memberof BinderComplianceEntityResult
     */
    'sources': Array<ComplianceEntityResultSource> | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultAddress>}
     * @memberof BinderComplianceEntityResult
     */
    'addresses': Array<ComplianceEntityResultAddress> | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultTag>}
     * @memberof BinderComplianceEntityResult
     */
    'tags': Array<ComplianceEntityResultTag> | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'source_name'?: string | null;
    /**
     * Adverse media sub-source (news / icij / enforcement). Missing from the compliance API is treated as news.  * `news` - News * `icij` - ICIJ * `enforcement` - Enforcement
     * @type {SourceTypeEnum}
     * @memberof BinderComplianceEntityResult
     */
    'source_type': SourceTypeEnum;
    /**
     * Generic entity_document/v1 payload (enforcement/sanctions listings).
     * @type {any}
     * @memberof BinderComplianceEntityResult
     */
    'structured': any | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'source_date': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResult
     */
    'archived_at': string | null;
    /**
     * Return comment count.
     * @type {number}
     * @memberof BinderComplianceEntityResult
     */
    'comment_count': number;
    /**
     * 
     * @type {BinderComplianceEntityResultEntity}
     * @memberof BinderComplianceEntityResult
     */
    'entity': BinderComplianceEntityResultEntity;
}
/**
 * @type BinderComplianceEntityResultAutomatedFalsePositiveRating
 * @export
 */
export type BinderComplianceEntityResultAutomatedFalsePositiveRating = AutomatedFalsePositiveRatingEnum | NullEnum;

/**
 * 
 * @export
 * @interface BinderComplianceEntityResultEntity
 */
export interface BinderComplianceEntityResultEntity {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultEntity
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceCheck}
     * @memberof BinderComplianceEntityResultEntity
     */
    'compliance_check': BinderComplianceCheck;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultEntity
     */
    'entity_role': string;
    /**
     * 
     * @type {ComplianceEntityTypeEnum}
     * @memberof BinderComplianceEntityResultEntity
     */
    'entity_type'?: ComplianceEntityTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultEntity
     */
    'name': string;
}
/**
 * @type BinderComplianceEntityResultFalsePositiveRating
 * @export
 */
export type BinderComplianceEntityResultFalsePositiveRating = BlankEnum | FalsePositiveRatingEnum | NullEnum;

/**
 * @type BinderComplianceEntityResultPepRelationship
 * How this person relates to the PEP status: self, family or associate.  * `SELF` - Self * `FAMILY` - Family member * `ASSOCIATE` - Close associate
 * @export
 */
export type BinderComplianceEntityResultPepRelationship = NullEnum | PepRelationshipEnum;

/**
 * @type BinderComplianceEntityResultPepTier
 * PEP tier of this match (national / regional / local / international).  * `TIER_1` - Tier 1 (national) * `TIER_2` - Tier 2 (regional) * `TIER_3` - Tier 3 (local / SOE) * `INTERNATIONAL_ORG` - International organization
 * @export
 */
export type BinderComplianceEntityResultPepTier = NullEnum | PepTierEnum;

/**
 * Binder compliance entity result serializer.  Served from an Elasticsearch document, not the annotated Django queryset, so binder_count is dropped here rather than shipped as an always-0 value.
 * @export
 * @interface BinderComplianceEntityResultRequest
 */
export interface BinderComplianceEntityResultRequest {
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultRequest
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof BinderComplianceEntityResultRequest
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultRequest
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultRequest
     */
    'url'?: string | null;
    /**
     * 
     * @type {any}
     * @memberof BinderComplianceEntityResultRequest
     */
    'image'?: any | null;
    /**
     * 
     * @type {string}
     * @memberof BinderComplianceEntityResultRequest
     */
    'source_name'?: string | null;
}
/**
 * @type BinderComplianceEntityResultSoeRelationshipType
 * Nature of this match\'s government relationship (ownership level or control without ownership).  * `WHOLLY_OWNED` - Wholly state-owned * `MAJORITY_OWNED` - Majority state-owned * `MINORITY_OWNED` - Minority state-owned * `STATE_CONTROLLED` - State-controlled * `GOVERNMENT_LINKED` - Government-linked
 * @export
 */
export type BinderComplianceEntityResultSoeRelationshipType = NullEnum | SoeRelationshipTypeEnum;

/**
 * Binder Item Serializer.
 * @export
 * @interface BinderItem
 */
export interface BinderItem {
    /**
     * 
     * @type {string}
     * @memberof BinderItem
     */
    'external_id': string;
    /**
     * 
     * @type {BinderItemArticle}
     * @memberof BinderItem
     */
    'article': BinderItemArticle | null;
    /**
     * 
     * @type {BinderItemComplianceEntityResult}
     * @memberof BinderItem
     */
    'compliance_entity_result': BinderItemComplianceEntityResult | null;
    /**
     * 
     * @type {Array<BinderTag>}
     * @memberof BinderItem
     */
    'tags': Array<BinderTag>;
    /**
     * Get Binder Item Note count.
     * @type {number}
     * @memberof BinderItem
     */
    'comment_count': number;
}
/**
 * 
 * @export
 * @interface BinderItemArticle
 */
export interface BinderItemArticle {
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'url': string;
    /**
     * Get Image URL if allowed for source.
     * @type {string}
     * @memberof BinderItemArticle
     */
    'image_url': string;
    /**
     * Determine publication date of article.
     * @type {string}
     * @memberof BinderItemArticle
     */
    'publication_date': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'publication_time': string | null;
    /**
     * Calculate publication datetime of article.
     * @type {string}
     * @memberof BinderItemArticle
     */
    'publication_datetime': string;
    /**
     * 
     * @type {Array<SubArticle>}
     * @memberof BinderItemArticle
     */
    'sub_articles': Array<SubArticle>;
    /**
     * 
     * @type {Array<CompanyArticle>}
     * @memberof BinderItemArticle
     */
    'company_articles': Array<CompanyArticle>;
    /**
     * 
     * @type {Array<IndustryArticle>}
     * @memberof BinderItemArticle
     */
    'industry_articles': Array<IndustryArticle>;
    /**
     * 
     * @type {Array<CategoryTree>}
     * @memberof BinderItemArticle
     */
    'categories': Array<CategoryTree>;
    /**
     * 
     * @type {ArticleSource}
     * @memberof BinderItemArticle
     */
    'source': ArticleSource;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'title_en': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof BinderItemArticle
     */
    'snippet': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof BinderItemArticle
     */
    'snippet_en': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'summary'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'summary_en'?: string | null;
    /**
     * 
     * @type {Language856Enum}
     * @memberof BinderItemArticle
     */
    'language': Language856Enum;
    /**
     * 
     * @type {number}
     * @memberof BinderItemArticle
     */
    'sentiment': number | null;
    /**
     * 
     * @type {ArticleCountry}
     * @memberof BinderItemArticle
     */
    'country': ArticleCountry | null;
    /**
     * 
     * @type {ArticleIsPaywalled}
     * @memberof BinderItemArticle
     */
    'is_paywalled': ArticleIsPaywalled | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemArticle
     */
    'updated_at': string;
}
/**
 * 
 * @export
 * @interface BinderItemComplianceEntityResult
 */
export interface BinderItemComplianceEntityResult {
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'external_id': string;
    /**
     * 
     * @type {ResultTypeEnum}
     * @memberof BinderItemComplianceEntityResult
     */
    'result_type': ResultTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof BinderItemComplianceEntityResult
     */
    'confidence': number | null;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof BinderItemComplianceEntityResult
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'false_positive_rating_datetime': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'false_positive_rating_user': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultAutomatedFalsePositiveRating}
     * @memberof BinderItemComplianceEntityResult
     */
    'automated_false_positive_rating': BinderComplianceEntityResultAutomatedFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'automated_false_positive_rating_datetime': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'automated_false_positive_rating_comments': string | null;
    /**
     * Return result type description.
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'result_type_description': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'image'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'title': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'title_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'text': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'text_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'formatted_title_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'formatted_text_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'formatted_title': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'formatted_text': string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultPepTier}
     * @memberof BinderItemComplianceEntityResult
     */
    'pep_tier': BinderComplianceEntityResultPepTier | null;
    /**
     * 
     * @type {BinderComplianceEntityResultPepRelationship}
     * @memberof BinderItemComplianceEntityResult
     */
    'pep_relationship': BinderComplianceEntityResultPepRelationship | null;
    /**
     * Name of the PEP this person is related to (family / associate only).
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'pep_related_to': string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultSoeRelationshipType}
     * @memberof BinderItemComplianceEntityResult
     */
    'soe_relationship_type': BinderComplianceEntityResultSoeRelationshipType | null;
    /**
     * Percentage of government ownership, when reported.
     * @type {number}
     * @memberof BinderItemComplianceEntityResult
     */
    'soe_ownership_percentage': number | null;
    /**
     * Immediate parent or controlling government entity.
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'soe_parent_entity': string | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultSource>}
     * @memberof BinderItemComplianceEntityResult
     */
    'sources': Array<ComplianceEntityResultSource> | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultAddress>}
     * @memberof BinderItemComplianceEntityResult
     */
    'addresses': Array<ComplianceEntityResultAddress> | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultTag>}
     * @memberof BinderItemComplianceEntityResult
     */
    'tags': Array<ComplianceEntityResultTag> | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'source_name'?: string | null;
    /**
     * Adverse media sub-source (news / icij / enforcement). Missing from the compliance API is treated as news.  * `news` - News * `icij` - ICIJ * `enforcement` - Enforcement
     * @type {SourceTypeEnum}
     * @memberof BinderItemComplianceEntityResult
     */
    'source_type': SourceTypeEnum;
    /**
     * Generic entity_document/v1 payload (enforcement/sanctions listings).
     * @type {any}
     * @memberof BinderItemComplianceEntityResult
     */
    'structured': any | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'source_date': string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemComplianceEntityResult
     */
    'archived_at': string | null;
    /**
     * Return comment count.
     * @type {number}
     * @memberof BinderItemComplianceEntityResult
     */
    'comment_count': number;
    /**
     * 
     * @type {BinderComplianceEntityResultEntity}
     * @memberof BinderItemComplianceEntityResult
     */
    'entity': BinderComplianceEntityResultEntity;
}
/**
 * Binder Item Serializer.
 * @export
 * @interface BinderItemRequest
 */
export interface BinderItemRequest {
    /**
     * 
     * @type {BinderItemRequestArticle}
     * @memberof BinderItemRequest
     */
    'article': BinderItemRequestArticle | null;
    /**
     * 
     * @type {BinderItemRequestComplianceEntityResult}
     * @memberof BinderItemRequest
     */
    'compliance_entity_result': BinderItemRequestComplianceEntityResult | null;
    /**
     * 
     * @type {Array<BinderTagRequest>}
     * @memberof BinderItemRequest
     */
    'tags': Array<BinderTagRequest>;
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequest
     */
    'comment'?: string | null;
}
/**
 * 
 * @export
 * @interface BinderItemRequestArticle
 */
export interface BinderItemRequestArticle {
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestArticle
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestArticle
     */
    'summary'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestArticle
     */
    'summary_en'?: string | null;
}
/**
 * 
 * @export
 * @interface BinderItemRequestComplianceEntityResult
 */
export interface BinderItemRequestComplianceEntityResult {
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestComplianceEntityResult
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof BinderItemRequestComplianceEntityResult
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestComplianceEntityResult
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestComplianceEntityResult
     */
    'url'?: string | null;
    /**
     * 
     * @type {any}
     * @memberof BinderItemRequestComplianceEntityResult
     */
    'image'?: any | null;
    /**
     * 
     * @type {string}
     * @memberof BinderItemRequestComplianceEntityResult
     */
    'source_name'?: string | null;
}
/**
 * Binder Profile User Group Serializer.
 * @export
 * @interface BinderProfileUserGroup
 */
export interface BinderProfileUserGroup {
    /**
     * 
     * @type {string}
     * @memberof BinderProfileUserGroup
     */
    'external_id': string;
    /**
     * 
     * @type {ProfileUserGroupData}
     * @memberof BinderProfileUserGroup
     */
    'profile_user_group': ProfileUserGroupData;
    /**
     * 
     * @type {Permission655Enum}
     * @memberof BinderProfileUserGroup
     */
    'permission'?: Permission655Enum;
    /**
     * 
     * @type {string}
     * @memberof BinderProfileUserGroup
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderProfileUserGroup
     */
    'updated_at': string;
}
/**
 * Binder Profile User Group Serializer.
 * @export
 * @interface BinderProfileUserGroupRequest
 */
export interface BinderProfileUserGroupRequest {
    /**
     * 
     * @type {ProfileUserGroupDataRequest}
     * @memberof BinderProfileUserGroupRequest
     */
    'profile_user_group': ProfileUserGroupDataRequest;
    /**
     * 
     * @type {Permission655Enum}
     * @memberof BinderProfileUserGroupRequest
     */
    'permission'?: Permission655Enum;
}
/**
 * Binder.
 * @export
 * @interface BinderRequest
 */
export interface BinderRequest {
    /**
     * 
     * @type {string}
     * @memberof BinderRequest
     */
    'name': string;
}
/**
 * Binder Summary Serializer.
 * @export
 * @interface BinderSummary
 */
export interface BinderSummary {
    /**
     * 
     * @type {string}
     * @memberof BinderSummary
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof BinderSummary
     */
    'summary'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderSummary
     */
    'finished_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof BinderSummary
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderSummary
     */
    'updated_at': string;
}
/**
 * Binder Tag Serializer.
 * @export
 * @interface BinderTag
 */
export interface BinderTag {
    /**
     * 
     * @type {string}
     * @memberof BinderTag
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof BinderTag
     */
    'text': string;
    /**
     * 
     * @type {string}
     * @memberof BinderTag
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderTag
     */
    'updated_at': string;
    /**
     * Get Binder Item count.
     * @type {number}
     * @memberof BinderTag
     */
    'binder_item_count': number;
}
/**
 * Binder Tag Serializer.
 * @export
 * @interface BinderTagRequest
 */
export interface BinderTagRequest {
    /**
     * 
     * @type {string}
     * @memberof BinderTagRequest
     */
    'text': string;
}
/**
 * Binder User Serializer.
 * @export
 * @interface BinderUser
 */
export interface BinderUser {
    /**
     * 
     * @type {string}
     * @memberof BinderUser
     */
    'external_id': string;
    /**
     * 
     * @type {User}
     * @memberof BinderUser
     */
    'user': User;
    /**
     * 
     * @type {Permission655Enum}
     * @memberof BinderUser
     */
    'permission'?: Permission655Enum;
    /**
     * 
     * @type {string}
     * @memberof BinderUser
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof BinderUser
     */
    'updated_at': string;
}
/**
 * Binder User Serializer.
 * @export
 * @interface BinderUserRequest
 */
export interface BinderUserRequest {
    /**
     * 
     * @type {UserRequest}
     * @memberof BinderUserRequest
     */
    'user': UserRequest;
    /**
     * 
     * @type {Permission655Enum}
     * @memberof BinderUserRequest
     */
    'permission'?: Permission655Enum;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const BlankEnum = {
    Empty: ''
} as const;

export type BlankEnum = typeof BlankEnum[keyof typeof BlankEnum];


/**
 * Serialize Category instance.
 * @export
 * @interface Category
 */
export interface Category {
    /**
     * Return translated category name.
     * @type {string}
     * @memberof Category
     */
    'name': string;
    /**
     * Return translated category parent name.
     * @type {string}
     * @memberof Category
     */
    'parent_name': string;
    /**
     * 
     * @type {string}
     * @memberof Category
     */
    'external_id'?: string;
    /**
     * 
     * @type {number}
     * @memberof Category
     */
    'priority'?: number | null;
}
/**
 * * `sanction` - Sanction * `pep` - Politically Exposed Person * `adverse_media` - Adverse media * `enforcement` - Enforcement * `govt_owned` - Government owned
 * @export
 * @enum {string}
 */

export const CategoryEnum = {
    Sanction: 'sanction',
    Pep: 'pep',
    AdverseMedia: 'adverse_media',
    Enforcement: 'enforcement',
    GovtOwned: 'govt_owned'
} as const;

export type CategoryEnum = typeof CategoryEnum[keyof typeof CategoryEnum];


/**
 * 
 * @export
 * @interface CategorySet
 */
export interface CategorySet {
    /**
     * Return translated category name.
     * @type {string}
     * @memberof CategorySet
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof CategorySet
     */
    'icon_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CategorySet
     */
    'external_id'?: string;
}
/**
 * Category Tree Structure.
 * @export
 * @interface CategoryTree
 */
export interface CategoryTree {
    /**
     * 
     * @type {string}
     * @memberof CategoryTree
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof CategoryTree
     */
    'external_id'?: string;
    /**
     * 
     * @type {number}
     * @memberof CategoryTree
     */
    'priority'?: number | null;
    /**
     * Return is_material flag if present.
     * @type {boolean}
     * @memberof CategoryTree
     */
    'is_material': boolean;
    /**
     * 
     * @type {Array<CategoryTree>}
     * @memberof CategoryTree
     */
    'sub_categories': Array<CategoryTree>;
}
/**
 * * `INTERNAL_WEB` - Internal web app * `EXTERNAL_API` - External API
 * @export
 * @enum {string}
 */

export const ChannelEnum = {
    InternalWeb: 'INTERNAL_WEB',
    ExternalApi: 'EXTERNAL_API'
} as const;

export type ChannelEnum = typeof ChannelEnum[keyof typeof ChannelEnum];


/**
 * * `company_not_found` - company_not_found * `registration_not_found` - registration_not_found * `restricted_data_company_error` - restricted_data_company_error * `invalid_company_filters` - invalid_company_filters * `missing_company_reference_error` - missing_company_reference_error * `parser_not_found_error` - parser_not_found_error * `batch_registration_cancelled_error` - batch_registration_cancelled_error * `batch_registration_file_headers_error` - batch_registration_file_headers_error * `invalid_company_query` - invalid_company_query * `websocket_session_not_found` - websocket_session_not_found * `websocket_session_expired` - websocket_session_expired * `alert_not_found` - alert_not_found * `invalid_alert_data` - invalid_alert_data * `portfolio_used_in_alert` - portfolio_used_in_alert * `default_portfolio_restriction` - default_portfolio_restriction * `profile_access_restricted` - profile_access_restricted * `user_not_found` - user_not_found * `user_already_exists` - user_already_exists * `user_already_access` - user_already_access * `no_user_permissions` - no_user_permissions * `global_import_restricted` - global_import_restricted * `no_profile` - no_profile * `email_verification_required` - email_verification_required * `phone_verification_required` - phone_verification_required * `phone_verification_validation_failed` - phone_verification_validation_failed * `recaptcha_validation_failed` - recaptcha_validation_failed * `account_inactive` - account_inactive * `no_invitation_permissions` - no_invitation_permissions * `duplicate_invite` - duplicate_invite * `invitation_not_found` - invitation_not_found * `password_reset_not_found` - password_reset_not_found * `invitation_expired` - invitation_expired * `password_reset_expired` - password_reset_expired * `email_verification_not_found` - email_verification_not_found * `phone_verification_not_found` - phone_verification_not_found * `invitation_already_used` - invitation_already_used * `profile_credit_balance_empty` - profile_credit_balance_empty * `profile_user_group_not_found` - profile_user_group_not_found * `profile_user_group_user_not_found` - profile_user_group_user_not_found * `unsupported_export_file_type` - unsupported_export_file_type * `api_user_not_allowed` - api_user_not_allowed * `invalid_version` - invalid_version * `deprecated_version` - deprecated_version * `internal_server_error` - internal_server_error * `portfolio_access_denied` - portfolio_access_denied * `portfolio_not_found` - portfolio_not_found * `batch_registration_not_found` - batch_registration_not_found * `portfolio_user_not_found` - portfolio_user_not_found * `portfolio_company_not_found` - portfolio_company_not_found * `missing_company_registration_input` - missing_company_registration_input * `invalid_duns_number_input_error` - invalid_duns_number_input_error * `invalid_country_code_registration_input_error` - invalid_country_code_registration_input_error * `missing_portfolio_reference` - missing_portfolio_reference * `missing_batch_registration` - missing_batch_registration * `article_not_found` - article_not_found * `saved_article_filters_not_found` - saved_article_filters_not_found * `saved_article_filter_access_denied` - saved_article_filter_access_denied * `saved_article_filters_user_not_found` - saved_article_filters_user_not_found * `invalid_article_filters` - invalid_article_filters * `invalid_article_query` - invalid_article_query * `missing_country_registration_number_search` - missing_country_registration_number_search * `error` - error * `invalid` - invalid * `parse_error` - parse_error * `authentication_failed` - authentication_failed * `not_authenticated` - not_authenticated * `permission_denied` - permission_denied * `not_found` - not_found * `method_not_allowed` - method_not_allowed * `not_acceptable` - not_acceptable * `unsupported_media_type` - unsupported_media_type * `throttled` - throttled * `binder_access_denied` - binder_access_denied * `binder_already_exists` - binder_already_exists * `binder_not_found` - binder_not_found * `binder_item_note_not_owned` - binder_item_note_not_owned * `binder_item_already_existing` - binder_item_already_existing * `compliance_entity_result_not_found` - compliance_entity_result_not_found * `dnb_connection_failed` - dnb_connection_failed * `indued_connection_failed` - indued_connection_failed * `dnb_company_not_found` - dnb_company_not_found * `dnb_api_throttled` - dnb_api_throttled * `dnb_invalid_input` - dnb_invalid_input * `dnb_duns_transferred` - dnb_duns_transferred * `dnb_invalid_duns_number` - dnb_invalid_duns_number * `dnb_company_unavailable` - dnb_company_unavailable * `dnb_global_ultimate_not_found` - dnb_global_ultimate_not_found * `screening_not_found` - screening_not_found * `ubo_compliance_permission_denied` - ubo_compliance_permission_denied * `missing_company_investigation_not_found` - missing_company_investigation_not_found * `invalid_feedback_input` - invalid_feedback_input * `insufficient_articles` - insufficient_articles * `key_extraction_not_found` - key_extraction_not_found * `webhook_not_found` - webhook_not_found * `webhook_subscription_not_found` - webhook_subscription_not_found * `assistant_daily_profile_limit_exceeded` - assistant_daily_profile_limit_exceeded
 * @export
 * @enum {string}
 */

export const CodeEnum = {
    CompanyNotFound: 'company_not_found',
    RegistrationNotFound: 'registration_not_found',
    RestrictedDataCompanyError: 'restricted_data_company_error',
    InvalidCompanyFilters: 'invalid_company_filters',
    MissingCompanyReferenceError: 'missing_company_reference_error',
    ParserNotFoundError: 'parser_not_found_error',
    BatchRegistrationCancelledError: 'batch_registration_cancelled_error',
    BatchRegistrationFileHeadersError: 'batch_registration_file_headers_error',
    InvalidCompanyQuery: 'invalid_company_query',
    WebsocketSessionNotFound: 'websocket_session_not_found',
    WebsocketSessionExpired: 'websocket_session_expired',
    AlertNotFound: 'alert_not_found',
    InvalidAlertData: 'invalid_alert_data',
    PortfolioUsedInAlert: 'portfolio_used_in_alert',
    DefaultPortfolioRestriction: 'default_portfolio_restriction',
    ProfileAccessRestricted: 'profile_access_restricted',
    UserNotFound: 'user_not_found',
    UserAlreadyExists: 'user_already_exists',
    UserAlreadyAccess: 'user_already_access',
    NoUserPermissions: 'no_user_permissions',
    GlobalImportRestricted: 'global_import_restricted',
    NoProfile: 'no_profile',
    EmailVerificationRequired: 'email_verification_required',
    PhoneVerificationRequired: 'phone_verification_required',
    PhoneVerificationValidationFailed: 'phone_verification_validation_failed',
    RecaptchaValidationFailed: 'recaptcha_validation_failed',
    AccountInactive: 'account_inactive',
    NoInvitationPermissions: 'no_invitation_permissions',
    DuplicateInvite: 'duplicate_invite',
    InvitationNotFound: 'invitation_not_found',
    PasswordResetNotFound: 'password_reset_not_found',
    InvitationExpired: 'invitation_expired',
    PasswordResetExpired: 'password_reset_expired',
    EmailVerificationNotFound: 'email_verification_not_found',
    PhoneVerificationNotFound: 'phone_verification_not_found',
    InvitationAlreadyUsed: 'invitation_already_used',
    ProfileCreditBalanceEmpty: 'profile_credit_balance_empty',
    ProfileUserGroupNotFound: 'profile_user_group_not_found',
    ProfileUserGroupUserNotFound: 'profile_user_group_user_not_found',
    UnsupportedExportFileType: 'unsupported_export_file_type',
    ApiUserNotAllowed: 'api_user_not_allowed',
    InvalidVersion: 'invalid_version',
    DeprecatedVersion: 'deprecated_version',
    InternalServerError: 'internal_server_error',
    PortfolioAccessDenied: 'portfolio_access_denied',
    PortfolioNotFound: 'portfolio_not_found',
    BatchRegistrationNotFound: 'batch_registration_not_found',
    PortfolioUserNotFound: 'portfolio_user_not_found',
    PortfolioCompanyNotFound: 'portfolio_company_not_found',
    MissingCompanyRegistrationInput: 'missing_company_registration_input',
    InvalidDunsNumberInputError: 'invalid_duns_number_input_error',
    InvalidCountryCodeRegistrationInputError: 'invalid_country_code_registration_input_error',
    MissingPortfolioReference: 'missing_portfolio_reference',
    MissingBatchRegistration: 'missing_batch_registration',
    ArticleNotFound: 'article_not_found',
    SavedArticleFiltersNotFound: 'saved_article_filters_not_found',
    SavedArticleFilterAccessDenied: 'saved_article_filter_access_denied',
    SavedArticleFiltersUserNotFound: 'saved_article_filters_user_not_found',
    InvalidArticleFilters: 'invalid_article_filters',
    InvalidArticleQuery: 'invalid_article_query',
    MissingCountryRegistrationNumberSearch: 'missing_country_registration_number_search',
    Error: 'error',
    Invalid: 'invalid',
    ParseError: 'parse_error',
    AuthenticationFailed: 'authentication_failed',
    NotAuthenticated: 'not_authenticated',
    PermissionDenied: 'permission_denied',
    NotFound: 'not_found',
    MethodNotAllowed: 'method_not_allowed',
    NotAcceptable: 'not_acceptable',
    UnsupportedMediaType: 'unsupported_media_type',
    Throttled: 'throttled',
    BinderAccessDenied: 'binder_access_denied',
    BinderAlreadyExists: 'binder_already_exists',
    BinderNotFound: 'binder_not_found',
    BinderItemNoteNotOwned: 'binder_item_note_not_owned',
    BinderItemAlreadyExisting: 'binder_item_already_existing',
    ComplianceEntityResultNotFound: 'compliance_entity_result_not_found',
    DnbConnectionFailed: 'dnb_connection_failed',
    InduedConnectionFailed: 'indued_connection_failed',
    DnbCompanyNotFound: 'dnb_company_not_found',
    DnbApiThrottled: 'dnb_api_throttled',
    DnbInvalidInput: 'dnb_invalid_input',
    DnbDunsTransferred: 'dnb_duns_transferred',
    DnbInvalidDunsNumber: 'dnb_invalid_duns_number',
    DnbCompanyUnavailable: 'dnb_company_unavailable',
    DnbGlobalUltimateNotFound: 'dnb_global_ultimate_not_found',
    ScreeningNotFound: 'screening_not_found',
    UboCompliancePermissionDenied: 'ubo_compliance_permission_denied',
    MissingCompanyInvestigationNotFound: 'missing_company_investigation_not_found',
    InvalidFeedbackInput: 'invalid_feedback_input',
    InsufficientArticles: 'insufficient_articles',
    KeyExtractionNotFound: 'key_extraction_not_found',
    WebhookNotFound: 'webhook_not_found',
    WebhookSubscriptionNotFound: 'webhook_subscription_not_found',
    AssistantDailyProfileLimitExceeded: 'assistant_daily_profile_limit_exceeded'
} as const;

export type CodeEnum = typeof CodeEnum[keyof typeof CodeEnum];


/**
 * Serializer for comments.
 * @export
 * @interface Comment
 */
export interface Comment {
    /**
     * 
     * @type {string}
     * @memberof Comment
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Comment
     */
    'text': string;
    /**
     * 
     * @type {string}
     * @memberof Comment
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Comment
     */
    'updated_at': string;
    /**
     * 
     * @type {CommentCreatedBy}
     * @memberof Comment
     */
    'created_by': CommentCreatedBy;
}
/**
 * 
 * @export
 * @interface CommentCreatedBy
 */
export interface CommentCreatedBy {
    /**
     * 
     * @type {string}
     * @memberof CommentCreatedBy
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof CommentCreatedBy
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof CommentCreatedBy
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof CommentCreatedBy
     */
    'last_name': string;
}
/**
 * Permanent URL of a stored comment image, to embed in the note.
 * @export
 * @interface CommentImageUpload
 */
export interface CommentImageUpload {
    /**
     * 
     * @type {string}
     * @memberof CommentImageUpload
     */
    'image_url': string;
}
/**
 * Serializer for comments.
 * @export
 * @interface CommentRequest
 */
export interface CommentRequest {
    /**
     * 
     * @type {string}
     * @memberof CommentRequest
     */
    'text': string;
    /**
     * 
     * @type {string}
     * @memberof CommentRequest
     */
    'binder_item'?: string;
    /**
     * 
     * @type {string}
     * @memberof CommentRequest
     */
    'compliance_entity_result'?: string;
}
/**
 * Custom Serializer for the Company Model.  :note: founding_date: DateField is not supported by elasticsearch
 * @export
 * @interface Company
 */
export interface Company {
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'duns_number'?: string | null;
    /**
     * Get LEI (Legal Entity Identifier). None for OpenSearch-sourced instances.
     * @type {string}
     * @memberof Company
     */
    'lei': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'clean_name': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'slug': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof Company
     */
    'country': CountryEnum;
    /**
     * Indicates whether the company is out of business.
     * @type {boolean}
     * @memberof Company
     */
    'is_out_of_business'?: boolean;
    /**
     * 
     * @type {Array<RegistrationNumber>}
     * @memberof Company
     */
    'registration_numbers': Array<RegistrationNumber>;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'ticker_symbol'?: string | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof Company
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof Company
     */
    'primary_industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'address_postal'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof Company
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof Company
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'website_domain'?: string | null;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof Company
     */
    'social_logo': string | null;
    /**
     * Get Website Icon URL.
     * @type {string}
     * @memberof Company
     */
    'website_icon_url': string;
    /**
     * Description found by AI on the website
     * @type {string}
     * @memberof Company
     */
    'website_description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'website_description_source_url'?: string | null;
    /**
     * Get Social Description.
     * @type {string}
     * @memberof Company
     */
    'social_description': string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'linkedin_url'?: string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof Company
     */
    'review_count'?: number;
    /**
     * Average review score
     * @type {number}
     * @memberof Company
     */
    'review_average_score'?: number | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof Company
     */
    'article_count'?: number;
    /**
     * Amount of publications available
     * @type {number}
     * @memberof Company
     */
    'publication_count'?: number;
    /**
     * Amount of reports available
     * @type {number}
     * @memberof Company
     */
    'report_count'?: number;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'dnb_website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'dnb_website_domain'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'dnb_assessment'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'global_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {CompanyGlobalUltimateCountry}
     * @memberof Company
     */
    'global_ultimate_country'?: CompanyGlobalUltimateCountry | null;
    /**
     * Get Global Ultimate website icon url.
     * @type {string}
     * @memberof Company
     */
    'global_ultimate_website_icon_url': string;
    /**
     * Get Global Ultimate external id.
     * @type {string}
     * @memberof Company
     */
    'global_ultimate_external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'global_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'domestic_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'domestic_ultimate_name'?: string | null;
    /**
     * Get Global Ultimate website icon url.
     * @type {string}
     * @memberof Company
     */
    'domestic_ultimate_website_icon_url': string;
    /**
     * Get Domestic Ultimate external id.
     * @type {string}
     * @memberof Company
     */
    'domestic_ultimate_external_id': string;
    /**
     * Date when data was fetched from Dun and Bradstreet.
     * @type {string}
     * @memberof Company
     */
    'dnb_fetched_at'?: string | null;
    /**
     * Reprocessing company details, this is a temporary flag.
     * @type {boolean}
     * @memberof Company
     */
    'is_reprocessing_company_details'?: boolean;
    /**
     * Get Founding Date.
     * @type {string}
     * @memberof Company
     */
    'founding_date': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'updated_at': string;
    /**
     * 
     * @type {RegistrationStatusEnum}
     * @memberof Company
     */
    'registration_status'?: RegistrationStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'registration_status_text': string;
    /**
     * 
     * @type {number}
     * @memberof Company
     */
    'registration_progress'?: number;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'finished_registration_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'last_directors_update'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Company
     */
    'last_ubo_investigation'?: string | null;
    /**
     * Get if the company is pending DNB investigation.
     * @type {boolean}
     * @memberof Company
     */
    'is_pending_dnb_investigation': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Company
     */
    'is_pending_ubo_investigation': boolean;
}
/**
 * 
 * @export
 * @interface CompanyAnnotation
 */
export interface CompanyAnnotation {
    /**
     * 
     * @type {string}
     * @memberof CompanyAnnotation
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyAnnotation
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyAnnotation
     */
    'external_id': string;
    /**
     * 
     * @type {number}
     * @memberof CompanyAnnotation
     */
    'count': number;
}
/**
 * Company Annotations Serializer.
 * @export
 * @interface CompanyAnnotations
 */
export interface CompanyAnnotations {
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof CompanyAnnotations
     */
    'countries'?: { [key: string]: number; };
}
/**
 * Serialize Company Article.
 * @export
 * @interface CompanyArticle
 */
export interface CompanyArticle {
    /**
     * 
     * @type {NewsCompany}
     * @memberof CompanyArticle
     */
    'company': NewsCompany;
    /**
     * 
     * @type {TradeName}
     * @memberof CompanyArticle
     */
    'trade_name': TradeName;
    /**
     * Get Customer reference.
     * @type {string}
     * @memberof CompanyArticle
     */
    'customer_reference': string;
    /**
     * 
     * @type {Array<CompanyArticleCategory>}
     * @memberof CompanyArticle
     */
    'categories': Array<CompanyArticleCategory>;
    /**
     * Get Shortened snippet.
     * @type {string}
     * @memberof CompanyArticle
     */
    'snippet': string;
    /**
     * Get Shortened snippet.
     * @type {string}
     * @memberof CompanyArticle
     */
    'snippet_en': string;
    /**
     * Get Snippet Translated.
     * @type {string}
     * @memberof CompanyArticle
     */
    'snippet_translated': string;
    /**
     * 
     * @type {number}
     * @memberof CompanyArticle
     */
    'sentiment'?: number | null;
}
/**
 * Serializer for category in company article context with is_material.
 * @export
 * @interface CompanyArticleCategory
 */
export interface CompanyArticleCategory {
    /**
     * Return translated category name.
     * @type {string}
     * @memberof CompanyArticleCategory
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyArticleCategory
     */
    'external_id'?: string;
    /**
     * 
     * @type {number}
     * @memberof CompanyArticleCategory
     */
    'priority'?: number | null;
    /**
     * Return is_material flag if present.
     * @type {boolean}
     * @memberof CompanyArticleCategory
     */
    'is_material': boolean;
}
/**
 * Company Categories Aggregation Serializer.
 * @export
 * @interface CompanyCategoriesAggregation
 */
export interface CompanyCategoriesAggregation {
    /**
     * 
     * @type {BasicCompany}
     * @memberof CompanyCategoriesAggregation
     */
    'company': BasicCompany;
    /**
     * 
     * @type {Array<ArticleCategorySentimentAggregation>}
     * @memberof CompanyCategoriesAggregation
     */
    'categories': Array<ArticleCategorySentimentAggregation>;
}
/**
 * Company Country Aggregation Serializer.
 * @export
 * @interface CompanyCountryAggregation
 */
export interface CompanyCountryAggregation {
    /**
     * 
     * @type {number}
     * @memberof CompanyCountryAggregation
     */
    'count': number;
    /**
     * 
     * @type {string}
     * @memberof CompanyCountryAggregation
     */
    'country': string;
}
/**
 * Company Detail Serializer.
 * @export
 * @interface CompanyDetail
 */
export interface CompanyDetail {
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'duns_number'?: string | null;
    /**
     * Get LEI (Legal Entity Identifier). None for OpenSearch-sourced instances.
     * @type {string}
     * @memberof CompanyDetail
     */
    'lei': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'clean_name': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'slug': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof CompanyDetail
     */
    'country': CountryEnum;
    /**
     * Indicates whether the company is out of business.
     * @type {boolean}
     * @memberof CompanyDetail
     */
    'is_out_of_business'?: boolean;
    /**
     * 
     * @type {Array<RegistrationNumber>}
     * @memberof CompanyDetail
     */
    'registration_numbers': Array<RegistrationNumber>;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'ticker_symbol'?: string | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof CompanyDetail
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof CompanyDetail
     */
    'primary_industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'address_postal'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof CompanyDetail
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof CompanyDetail
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'website_domain'?: string | null;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof CompanyDetail
     */
    'social_logo': string | null;
    /**
     * Get Website Icon URL.
     * @type {string}
     * @memberof CompanyDetail
     */
    'website_icon_url': string;
    /**
     * Description found by AI on the website
     * @type {string}
     * @memberof CompanyDetail
     */
    'website_description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'website_description_source_url'?: string | null;
    /**
     * Get Social Description.
     * @type {string}
     * @memberof CompanyDetail
     */
    'social_description': string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'linkedin_url'?: string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof CompanyDetail
     */
    'review_count'?: number;
    /**
     * Average review score
     * @type {number}
     * @memberof CompanyDetail
     */
    'review_average_score'?: number | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof CompanyDetail
     */
    'article_count'?: number;
    /**
     * Amount of publications available
     * @type {number}
     * @memberof CompanyDetail
     */
    'publication_count'?: number;
    /**
     * Amount of reports available
     * @type {number}
     * @memberof CompanyDetail
     */
    'report_count'?: number;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'dnb_website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'dnb_website_domain'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'dnb_assessment'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'global_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {CompanyGlobalUltimateCountry}
     * @memberof CompanyDetail
     */
    'global_ultimate_country'?: CompanyGlobalUltimateCountry | null;
    /**
     * Get Global Ultimate website icon url.
     * @type {string}
     * @memberof CompanyDetail
     */
    'global_ultimate_website_icon_url': string;
    /**
     * Get Global Ultimate external id.
     * @type {string}
     * @memberof CompanyDetail
     */
    'global_ultimate_external_id': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'global_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'domestic_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'domestic_ultimate_name'?: string | null;
    /**
     * Get Global Ultimate website icon url.
     * @type {string}
     * @memberof CompanyDetail
     */
    'domestic_ultimate_website_icon_url': string;
    /**
     * Get Domestic Ultimate external id.
     * @type {string}
     * @memberof CompanyDetail
     */
    'domestic_ultimate_external_id': string;
    /**
     * Date when data was fetched from Dun and Bradstreet.
     * @type {string}
     * @memberof CompanyDetail
     */
    'dnb_fetched_at'?: string | null;
    /**
     * Reprocessing company details, this is a temporary flag.
     * @type {boolean}
     * @memberof CompanyDetail
     */
    'is_reprocessing_company_details'?: boolean;
    /**
     * Get Founding Date.
     * @type {string}
     * @memberof CompanyDetail
     */
    'founding_date': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'updated_at': string;
    /**
     * 
     * @type {RegistrationStatusEnum}
     * @memberof CompanyDetail
     */
    'registration_status'?: RegistrationStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'registration_status_text': string;
    /**
     * 
     * @type {number}
     * @memberof CompanyDetail
     */
    'registration_progress'?: number;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'finished_registration_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'last_directors_update'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetail
     */
    'last_ubo_investigation'?: string | null;
    /**
     * Get if the company is pending DNB investigation.
     * @type {boolean}
     * @memberof CompanyDetail
     */
    'is_pending_dnb_investigation': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof CompanyDetail
     */
    'is_pending_ubo_investigation': boolean;
    /**
     * 
     * @type {Array<CompanyFinancialUpdate>}
     * @memberof CompanyDetail
     */
    'financial_updates': Array<CompanyFinancialUpdate>;
    /**
     * 
     * @type {Array<CompanyThirdPartyAssessment>}
     * @memberof CompanyDetail
     */
    'third_party_assessments': Array<CompanyThirdPartyAssessment>;
    /**
     * 
     * @type {Array<CompanyEmployeeCount>}
     * @memberof CompanyDetail
     */
    'employee_count': Array<CompanyEmployeeCount>;
    /**
     * 
     * @type {Array<TradeName>}
     * @memberof CompanyDetail
     */
    'trade_names': Array<TradeName>;
    /**
     * 
     * @type {CompanyDetailScoring}
     * @memberof CompanyDetail
     */
    'scoring': CompanyDetailScoring | null;
}
/**
 * 
 * @export
 * @interface CompanyDetailScoring
 */
export interface CompanyDetailScoring {
    /**
     * 
     * @type {string}
     * @memberof CompanyDetailScoring
     */
    'date': string;
    /**
     * 
     * @type {number}
     * @memberof CompanyDetailScoring
     */
    'score': number;
    /**
     * 
     * @type {number}
     * @memberof CompanyDetailScoring
     */
    'trend'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof CompanyDetailScoring
     */
    'signal_count': number;
    /**
     * 
     * @type {string}
     * @memberof CompanyDetailScoring
     */
    'created_at': string;
}
/**
 * Company Employee Count.
 * @export
 * @interface CompanyEmployeeCount
 */
export interface CompanyEmployeeCount {
    /**
     * Get Reliability Description.
     * @type {string}
     * @memberof CompanyEmployeeCount
     */
    'reliability_description': string;
    /**
     * Get Informational Scope.
     * @type {string}
     * @memberof CompanyEmployeeCount
     */
    'informational_scope': string;
    /**
     * 
     * @type {number}
     * @memberof CompanyEmployeeCount
     */
    'value': number;
    /**
     * 
     * @type {string}
     * @memberof CompanyEmployeeCount
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyEmployeeCount
     */
    'updated_at': string;
}
/**
 * Data Export Serializer.
 * @export
 * @interface CompanyExport
 */
export interface CompanyExport {
    /**
     * 
     * @type {string}
     * @memberof CompanyExport
     */
    'external_id': string;
    /**
     * 
     * @type {ExportTypeEnum}
     * @memberof CompanyExport
     */
    'export_type': ExportTypeEnum;
    /**
     * 
     * @type {DataExportFileType}
     * @memberof CompanyExport
     */
    'file_type': DataExportFileType;
    /**
     * 
     * @type {CompanyFilters}
     * @memberof CompanyExport
     */
    'filters': CompanyFilters;
    /**
     * Location of exports
     * @type {string}
     * @memberof CompanyExport
     */
    'location': string | null;
    /**
     * 
     * @type {Status180Enum}
     * @memberof CompanyExport
     */
    'status': Status180Enum;
    /**
     * 
     * @type {number}
     * @memberof CompanyExport
     */
    'result_count': number | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyExport
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyExport
     */
    'updated_at': string;
}
/**
 * Data Export Serializer.
 * @export
 * @interface CompanyExportRequest
 */
export interface CompanyExportRequest {
    /**
     * 
     * @type {DataExportFileType}
     * @memberof CompanyExportRequest
     */
    'file_type': DataExportFileType;
    /**
     * 
     * @type {CompanyFiltersRequest}
     * @memberof CompanyExportRequest
     */
    'filters': CompanyFiltersRequest;
}
/**
 * Company Feedback Serializer.
 * @export
 * @interface CompanyFeedbackRead
 */
export interface CompanyFeedbackRead {
    /**
     * 
     * @type {Company}
     * @memberof CompanyFeedbackRead
     */
    'company': Company;
    /**
     * 
     * @type {FeedbackType43bEnum}
     * @memberof CompanyFeedbackRead
     */
    'feedback_type': FeedbackType43bEnum;
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackRead
     */
    'comment'?: string | null;
    /**
     * 
     * @type {UserList}
     * @memberof CompanyFeedbackRead
     */
    'user': UserList;
}
/**
 * * `NOT_ENOUGH_NEWS` - NOT_ENOUGH_NEWS * `COMPANY_NAME_OUTDATED` - COMPANY_NAME_OUTDATED * `INCORRECT_COMPANY_WEBSITE` - INCORRECT_COMPANY_WEBSITE * `MISSING_REGISTRATION_NUMBER` - MISSING_REGISTRATION_NUMBER * `MISSING_TRADE_NAME` - MISSING_TRADE_NAME * `INCORRECT_TRADE_NAME` - INCORRECT_TRADE_NAME * `NOT_ENOUGH_REVIEWS` - NOT_ENOUGH_REVIEWS * `OUTDATED_CORPORATE_LINKAGE` - OUTDATED_CORPORATE_LINKAGE * `INCORRECT_CORPORATE_LINKAGE` - INCORRECT_CORPORATE_LINKAGE * `OTHER` - OTHER * `COMPANY_EXCLUDED_ALERT` - COMPANY_EXCLUDED_ALERT * `DIRECTORS_NOT_IMPORTED` - DIRECTORS_NOT_IMPORTED * `NO_NEWS_ARTICLES_FOUND` - NO_NEWS_ARTICLES_FOUND
 * @export
 * @enum {string}
 */

export const CompanyFeedbackTypeEnum = {
    NotEnoughNews: 'NOT_ENOUGH_NEWS',
    CompanyNameOutdated: 'COMPANY_NAME_OUTDATED',
    IncorrectCompanyWebsite: 'INCORRECT_COMPANY_WEBSITE',
    MissingRegistrationNumber: 'MISSING_REGISTRATION_NUMBER',
    MissingTradeName: 'MISSING_TRADE_NAME',
    IncorrectTradeName: 'INCORRECT_TRADE_NAME',
    NotEnoughReviews: 'NOT_ENOUGH_REVIEWS',
    OutdatedCorporateLinkage: 'OUTDATED_CORPORATE_LINKAGE',
    IncorrectCorporateLinkage: 'INCORRECT_CORPORATE_LINKAGE',
    Other: 'OTHER',
    CompanyExcludedAlert: 'COMPANY_EXCLUDED_ALERT',
    DirectorsNotImported: 'DIRECTORS_NOT_IMPORTED',
    NoNewsArticlesFound: 'NO_NEWS_ARTICLES_FOUND'
} as const;

export type CompanyFeedbackTypeEnum = typeof CompanyFeedbackTypeEnum[keyof typeof CompanyFeedbackTypeEnum];


/**
 * Company Feedback Serializer.
 * @export
 * @interface CompanyFeedbackWrite
 */
export interface CompanyFeedbackWrite {
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackWrite
     */
    'company': string;
    /**
     * 
     * @type {FeedbackType43bEnum}
     * @memberof CompanyFeedbackWrite
     */
    'feedback_type': FeedbackType43bEnum;
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackWrite
     */
    'comment'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackWrite
     */
    'trade_name'?: string | null;
}
/**
 * Company Feedback Serializer.
 * @export
 * @interface CompanyFeedbackWriteRequest
 */
export interface CompanyFeedbackWriteRequest {
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackWriteRequest
     */
    'company': string;
    /**
     * 
     * @type {FeedbackType43bEnum}
     * @memberof CompanyFeedbackWriteRequest
     */
    'feedback_type': FeedbackType43bEnum;
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackWriteRequest
     */
    'comment'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFeedbackWriteRequest
     */
    'trade_name'?: string | null;
}
/**
 * Company Filter Serializer.
 * @export
 * @interface CompanyFilters
 */
export interface CompanyFilters {
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'query'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'slug'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'portfolios'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'countries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'duns_numbers'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'lei'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'excluded_companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'industries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'global_ultimates'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFilters
     */
    'registration_numbers'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'website_domain'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof CompanyFilters
     */
    'is_listed'?: boolean | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'min_created_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'max_created_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'min_updated_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFilters
     */
    'max_updated_at'?: string | null;
}
/**
 * Company Filter Serializer.
 * @export
 * @interface CompanyFiltersRequest
 */
export interface CompanyFiltersRequest {
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'query'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'slug'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'portfolios'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'countries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'duns_numbers'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'lei'?: string | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'excluded_companies'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'industries'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'global_ultimates'?: Array<string> | null;
    /**
     * 
     * @type {Array<string>}
     * @memberof CompanyFiltersRequest
     */
    'registration_numbers'?: Array<string> | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'website_domain'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof CompanyFiltersRequest
     */
    'is_listed'?: boolean | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'min_created_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'max_created_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'min_updated_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyFiltersRequest
     */
    'max_updated_at'?: string | null;
}
/**
 * Financial update Serializer.
 * @export
 * @interface CompanyFinancialUpdate
 */
export interface CompanyFinancialUpdate {
    /**
     * 
     * @type {string}
     * @memberof CompanyFinancialUpdate
     */
    'financial_statement_to_date': string;
    /**
     * Get Reliability Description.
     * @type {string}
     * @memberof CompanyFinancialUpdate
     */
    'reliability_description': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyFinancialUpdate
     */
    'accountant_name'?: string | null;
    /**
     * 
     * @type {Array<FinancialUpdateRevenue>}
     * @memberof CompanyFinancialUpdate
     */
    'revenue': Array<FinancialUpdateRevenue>;
    /**
     * 
     * @type {string}
     * @memberof CompanyFinancialUpdate
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyFinancialUpdate
     */
    'updated_at': string;
}
/**
 * @type CompanyGlobalUltimateCountry
 * @export
 */
export type CompanyGlobalUltimateCountry = BlankEnum | GlobalUltimateCountryEnum | NullEnum;

/**
 * Company Industry Aggregation Serializer.
 * @export
 * @interface CompanyIndustryAggregation
 */
export interface CompanyIndustryAggregation {
    /**
     * 
     * @type {number}
     * @memberof CompanyIndustryAggregation
     */
    'count': number;
    /**
     * 
     * @type {IndustryCode}
     * @memberof CompanyIndustryAggregation
     */
    'industry_code': IndustryCode;
}
/**
 * Company Industry Division Aggregation Serializer.
 * @export
 * @interface CompanyIndustryDivisionAggregation
 */
export interface CompanyIndustryDivisionAggregation {
    /**
     * 
     * @type {number}
     * @memberof CompanyIndustryDivisionAggregation
     */
    'count': number;
    /**
     * 
     * @type {DivisionCode}
     * @memberof CompanyIndustryDivisionAggregation
     */
    'division': DivisionCode;
}
/**
 * Company Investigation Serializer.
 * @export
 * @interface CompanyInvestigation
 */
export interface CompanyInvestigation {
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'external_id': string;
    /**
     * Official name of the company as registered in legal documents.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'legal_name': string;
    /**
     * Alternate name the company might use in its operations, distinct from the legal name.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'trade_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'address_postal'?: string | null;
    /**
     * Phone number should include international code prefix, e.g., +31.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {CountryEnum}
     * @memberof CompanyInvestigation
     */
    'country': CountryEnum;
    /**
     * Name of the primary officer or CEO of the company.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'officer_name'?: string | null;
    /**
     * Title or position of the named officer in the company.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'officer_title'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'industry_code'?: string | null;
    /**
     * Provide the official website of the company if available.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'website_url'?: string | null;
    /**
     * Any additional notes or details about the company.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'company': string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'registration_id': string;
    /**
     * MISSING: company is not yet known to D&B (submit as Mini). INCORRECT: existing D&B record is wrong and needs a full review (submit as FullRvw — requires Subject DUNS Number).  * `MISSING` - Missing * `INCORRECT` - Incorrect
     * @type {InvestigationTypeEnum}
     * @memberof CompanyInvestigation
     */
    'investigation_type': InvestigationTypeEnum;
    /**
     * DUNS number of the existing company being researched. Required when investigation_type is INCORRECT.
     * @type {string}
     * @memberof CompanyInvestigation
     */
    'subject_duns_number': string | null;
}
/**
 * Company Investigation Serializer.
 * @export
 * @interface CompanyInvestigationRequest
 */
export interface CompanyInvestigationRequest {
    /**
     * Official name of the company as registered in legal documents.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'legal_name': string;
    /**
     * Alternate name the company might use in its operations, distinct from the legal name.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'trade_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'address_postal'?: string | null;
    /**
     * Phone number should include international code prefix, e.g., +31.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {CountryEnum}
     * @memberof CompanyInvestigationRequest
     */
    'country': CountryEnum;
    /**
     * Name of the primary officer or CEO of the company.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'officer_name'?: string | null;
    /**
     * Title or position of the named officer in the company.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'officer_title'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'industry_code'?: string | null;
    /**
     * Provide the official website of the company if available.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'website_url'?: string | null;
    /**
     * Any additional notes or details about the company.
     * @type {string}
     * @memberof CompanyInvestigationRequest
     */
    'description'?: string | null;
}
/**
 * 
 * @export
 * @interface CompanyReportRequest
 */
export interface CompanyReportRequest {
    /**
     * 
     * @type {string}
     * @memberof CompanyReportRequest
     */
    'external_id': string;
}
/**
 * Custom Serializer for the Company Model.  :note: founding_date: DateField is not supported by elasticsearch
 * @export
 * @interface CompanyRequest
 */
export interface CompanyRequest {
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'clean_name': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'slug': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof CompanyRequest
     */
    'country': CountryEnum;
    /**
     * Indicates whether the company is out of business.
     * @type {boolean}
     * @memberof CompanyRequest
     */
    'is_out_of_business'?: boolean;
    /**
     * 
     * @type {Array<RegistrationNumberRequest>}
     * @memberof CompanyRequest
     */
    'registration_numbers': Array<RegistrationNumberRequest>;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'ticker_symbol'?: string | null;
    /**
     * 
     * @type {Array<IndustryCodeRequest>}
     * @memberof CompanyRequest
     */
    'industry_codes': Array<IndustryCodeRequest>;
    /**
     * 
     * @type {Array<IndustryCodeRequest>}
     * @memberof CompanyRequest
     */
    'primary_industry_codes': Array<IndustryCodeRequest>;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'address_postal'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof CompanyRequest
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof CompanyRequest
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'website_domain'?: string | null;
    /**
     * Description found by AI on the website
     * @type {string}
     * @memberof CompanyRequest
     */
    'website_description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'website_description_source_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'linkedin_url'?: string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof CompanyRequest
     */
    'review_count'?: number;
    /**
     * Average review score
     * @type {number}
     * @memberof CompanyRequest
     */
    'review_average_score'?: number | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof CompanyRequest
     */
    'article_count'?: number;
    /**
     * Amount of publications available
     * @type {number}
     * @memberof CompanyRequest
     */
    'publication_count'?: number;
    /**
     * Amount of reports available
     * @type {number}
     * @memberof CompanyRequest
     */
    'report_count'?: number;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'dnb_website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'dnb_website_domain'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'dnb_assessment'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'global_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {CompanyGlobalUltimateCountry}
     * @memberof CompanyRequest
     */
    'global_ultimate_country'?: CompanyGlobalUltimateCountry | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'global_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'domestic_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'domestic_ultimate_name'?: string | null;
    /**
     * Date when data was fetched from Dun and Bradstreet.
     * @type {string}
     * @memberof CompanyRequest
     */
    'dnb_fetched_at'?: string | null;
    /**
     * Reprocessing company details, this is a temporary flag.
     * @type {boolean}
     * @memberof CompanyRequest
     */
    'is_reprocessing_company_details'?: boolean;
    /**
     * 
     * @type {RegistrationStatusEnum}
     * @memberof CompanyRequest
     */
    'registration_status'?: RegistrationStatusEnum;
    /**
     * 
     * @type {number}
     * @memberof CompanyRequest
     */
    'registration_progress'?: number;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'finished_registration_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'last_directors_update'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyRequest
     */
    'last_ubo_investigation'?: string | null;
}
/**
 * 
 * @export
 * @interface CompanyScoring
 */
export interface CompanyScoring {
    /**
     * 
     * @type {string}
     * @memberof CompanyScoring
     */
    'date': string;
    /**
     * 
     * @type {number}
     * @memberof CompanyScoring
     */
    'score': number;
    /**
     * 
     * @type {number}
     * @memberof CompanyScoring
     */
    'trend'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof CompanyScoring
     */
    'signal_count': number;
    /**
     * 
     * @type {string}
     * @memberof CompanyScoring
     */
    'created_at': string;
}
/**
 * 
 * @export
 * @interface CompanyScoringSignal
 */
export interface CompanyScoringSignal {
    /**
     * 
     * @type {string}
     * @memberof CompanyScoringSignal
     */
    'date'?: string;
    /**
     * 
     * @type {number}
     * @memberof CompanyScoringSignal
     */
    'score'?: number;
    /**
     * 
     * @type {string}
     * @memberof CompanyScoringSignal
     */
    'notes'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof CompanyScoringSignal
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyScoringSignal
     */
    'url': string;
}
/**
 * Company Country Aggregation Serializer.
 * @export
 * @interface CompanyStats
 */
export interface CompanyStats {
    /**
     * 
     * @type {number}
     * @memberof CompanyStats
     */
    'company_count': number;
    /**
     * 
     * @type {number}
     * @memberof CompanyStats
     */
    'country_count': number;
    /**
     * 
     * @type {number}
     * @memberof CompanyStats
     */
    'industry_count': number;
    /**
     * 
     * @type {number}
     * @memberof CompanyStats
     */
    'division_group_code_count': number;
}
/**
 * Third Party Assessment.
 * @export
 * @interface CompanyThirdPartyAssessment
 */
export interface CompanyThirdPartyAssessment {
    /**
     * 
     * @type {string}
     * @memberof CompanyThirdPartyAssessment
     */
    'date': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyThirdPartyAssessment
     */
    'description': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyThirdPartyAssessment
     */
    'value': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyThirdPartyAssessment
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof CompanyThirdPartyAssessment
     */
    'updated_at': string;
}
/**
 * Compliance check serializer.
 * @export
 * @interface ComplianceCheck
 */
export interface ComplianceCheck {
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'external_id': string;
    /**
     * 
     * @type {ComplianceCheckCompany}
     * @memberof ComplianceCheck
     */
    'company': ComplianceCheckCompany | null;
    /**
     * Custom name for this compliance check.
     * @type {string}
     * @memberof ComplianceCheck
     */
    'name'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceCheck
     */
    'progress': number;
    /**
     * 
     * @type {ComplianceCheckStatusEnum}
     * @memberof ComplianceCheck
     */
    'status': ComplianceCheckStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'started_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'finished_at': string | null;
    /**
     * Warning if no ownership information is found.
     * @type {boolean}
     * @memberof ComplianceCheck
     */
    'warning_ownership_not_found': boolean;
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof ComplianceCheck
     */
    'director_role_groups': Array<DirectorRoleGroupsEnum> | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof ComplianceCheck
     */
    'compliance_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof ComplianceCheck
     */
    'activity_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof ComplianceCheck
     */
    'country_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof ComplianceCheck
     */
    'sanction_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof ComplianceCheck
     */
    'pep_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof ComplianceCheck
     */
    'adverse_media_score': ComplianceCheckComplianceScore | null;
    /**
     * Number of results across all entities that have been reviewed by a user.
     * @type {number}
     * @memberof ComplianceCheck
     */
    'reviewed_results_count': number;
    /**
     * Number of results across all entities that are open and not yet reviewed.
     * @type {number}
     * @memberof ComplianceCheck
     */
    'unreviewed_results_count': number;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'results_changed_at': string | null;
    /**
     * Whether the current user is subscribed to this check\'s monitoring alerts.
     * @type {boolean}
     * @memberof ComplianceCheck
     */
    'is_subscribed': boolean;
    /**
     * Whether monitoring was auto-paused on any entity (warning indicator).
     * @type {boolean}
     * @memberof ComplianceCheck
     */
    'monitoring_paused': boolean;
    /**
     * Master switch (opt-in): when enabled, screened entities of this check are automatically monitored (adverse media / sanctions).
     * @type {boolean}
     * @memberof ComplianceCheck
     */
    'monitoring_enabled'?: boolean;
    /**
     * Closed by a user once every result has been reviewed.
     * @type {boolean}
     * @memberof ComplianceCheck
     */
    'closed'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'closed_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheck
     */
    'closed_by': string | null;
}
/**
 * 
 * @export
 * @interface ComplianceCheckCompany
 */
export interface ComplianceCheckCompany {
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckCompany
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckCompany
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckCompany
     */
    'country': string;
}
/**
 * Basic company info embedded in a compliance check.
 * @export
 * @interface ComplianceCheckCompanyRequest
 */
export interface ComplianceCheckCompanyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckCompanyRequest
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckCompanyRequest
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckCompanyRequest
     */
    'country': string;
}
/**
 * @type ComplianceCheckComplianceScore
 * @export
 */
export type ComplianceCheckComplianceScore = ComplianceCheckScoreEnum | NullEnum;

/**
 * Optional filters and output format for a compliance check (audit trail) export.  All fields are optional; an empty payload exports every finished entity and all of their results as a PDF (the historic behaviour).
 * @export
 * @interface ComplianceCheckExport
 */
export interface ComplianceCheckExport {
    /**
     * 
     * @type {ComplianceCheckExportFileTypeEnum}
     * @memberof ComplianceCheckExport
     */
    'file_type'?: ComplianceCheckExportFileTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckExport
     */
    'only_selected_entities'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckExport
     */
    'exclude_false_positives'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckExport
     */
    'only_risk_confirmed_or_to_investigate'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof ComplianceCheckExport
     */
    'min_confidence'?: number | null;
    /**
     * 
     * @type {Array<ResultTypesEnum>}
     * @memberof ComplianceCheckExport
     */
    'result_types'?: Array<ResultTypesEnum>;
}
/**
 * * `PDF` - PDF * `EXCEL` - Excel
 * @export
 * @enum {string}
 */

export const ComplianceCheckExportFileTypeEnum = {
    Pdf: 'PDF',
    Excel: 'EXCEL'
} as const;

export type ComplianceCheckExportFileTypeEnum = typeof ComplianceCheckExportFileTypeEnum[keyof typeof ComplianceCheckExportFileTypeEnum];


/**
 * Optional filters and output format for a compliance check (audit trail) export.  All fields are optional; an empty payload exports every finished entity and all of their results as a PDF (the historic behaviour).
 * @export
 * @interface ComplianceCheckExportRequest
 */
export interface ComplianceCheckExportRequest {
    /**
     * 
     * @type {ComplianceCheckExportFileTypeEnum}
     * @memberof ComplianceCheckExportRequest
     */
    'file_type'?: ComplianceCheckExportFileTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckExportRequest
     */
    'only_selected_entities'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckExportRequest
     */
    'exclude_false_positives'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckExportRequest
     */
    'only_risk_confirmed_or_to_investigate'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof ComplianceCheckExportRequest
     */
    'min_confidence'?: number | null;
    /**
     * 
     * @type {Array<ResultTypesEnum>}
     * @memberof ComplianceCheckExportRequest
     */
    'result_types'?: Array<ResultTypesEnum>;
}
/**
 * Compliance check serializer.
 * @export
 * @interface ComplianceCheckRequest
 */
export interface ComplianceCheckRequest {
    /**
     * Custom name for this compliance check.
     * @type {string}
     * @memberof ComplianceCheckRequest
     */
    'name'?: string | null;
    /**
     * Master switch (opt-in): when enabled, screened entities of this check are automatically monitored (adverse media / sanctions).
     * @type {boolean}
     * @memberof ComplianceCheckRequest
     */
    'monitoring_enabled'?: boolean;
    /**
     * Closed by a user once every result has been reviewed.
     * @type {boolean}
     * @memberof ComplianceCheckRequest
     */
    'closed'?: boolean;
}
/**
 * Compliance check request serializer.
 * @export
 * @interface ComplianceCheckRequestRequest
 */
export interface ComplianceCheckRequestRequest {
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof ComplianceCheckRequestRequest
     */
    'director_role_groups'?: Array<DirectorRoleGroupsEnum> | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckRequestRequest
     */
    'company_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceCheckRequestRequest
     */
    'name'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckRequestRequest
     */
    'ubo_screening_enabled'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceCheckRequestRequest
     */
    'directors_screening_enabled'?: boolean;
    /**
     * 
     * @type {number}
     * @memberof ComplianceCheckRequestRequest
     */
    'ownership_screening_threshold'?: number | null;
}
/**
 * * `low` - Low * `medium` - Medium * `high` - High
 * @export
 * @enum {string}
 */

export const ComplianceCheckScoreEnum = {
    Low: 'low',
    Medium: 'medium',
    High: 'high'
} as const;

export type ComplianceCheckScoreEnum = typeof ComplianceCheckScoreEnum[keyof typeof ComplianceCheckScoreEnum];


/**
 * 
 * @export
 * @interface ComplianceCheckStartRequest
 */
export interface ComplianceCheckStartRequest {
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof ComplianceCheckStartRequest
     */
    'director_role_groups'?: Array<DirectorRoleGroupsEnum> | null;
}
/**
 * * `pending` - Pending * `queued` - Queued * `in_progress` - In Progress * `searching_directors` - Searching Directors * `completed` - Completed * `failed` - Failed
 * @export
 * @enum {string}
 */

export const ComplianceCheckStatusEnum = {
    Pending: 'pending',
    Queued: 'queued',
    InProgress: 'in_progress',
    SearchingDirectors: 'searching_directors',
    Completed: 'completed',
    Failed: 'failed'
} as const;

export type ComplianceCheckStatusEnum = typeof ComplianceCheckStatusEnum[keyof typeof ComplianceCheckStatusEnum];


/**
 * Compliance entity serializer.
 * @export
 * @interface ComplianceEntity
 */
export interface ComplianceEntity {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'external_id': string;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntity
     */
    'compliance_check_enabled'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntity
     */
    'adverse_media_check_enabled'?: boolean;
    /**
     * 
     * @type {SanctionMonitoringStatusEnum}
     * @memberof ComplianceEntity
     */
    'sanction_monitoring_status': SanctionMonitoringStatusEnum;
    /**
     * 
     * @type {AdverseMediaMonitoringStatusEnum}
     * @memberof ComplianceEntity
     */
    'adverse_media_monitoring_status': AdverseMediaMonitoringStatusEnum;
    /**
     * 
     * @type {ComplianceEntityTypeEnum}
     * @memberof ComplianceEntity
     */
    'entity_type'?: ComplianceEntityTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'entity_role': string;
    /**
     * Progress of the compliance check.
     * @type {number}
     * @memberof ComplianceEntity
     */
    'progress': number;
    /**
     * 
     * @type {any}
     * @memberof ComplianceEntity
     */
    'screening_status': any | null;
    /**
     * 
     * @type {ComplianceEntityStatusEnum}
     * @memberof ComplianceEntity
     */
    'status': ComplianceEntityStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'compliance_check_finished_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'adverse_media_check_finished_at': string | null;
    /**
     * 
     * @type {EnforcementResultsEnum}
     * @memberof ComplianceEntity
     */
    'sanction_results': EnforcementResultsEnum;
    /**
     * 
     * @type {EnforcementResultsEnum}
     * @memberof ComplianceEntity
     */
    'adverse_media_results': EnforcementResultsEnum;
    /**
     * 
     * @type {EnforcementResultsEnum}
     * @memberof ComplianceEntity
     */
    'pep_results': EnforcementResultsEnum;
    /**
     * 
     * @type {EnforcementResultsEnum}
     * @memberof ComplianceEntity
     */
    'govt_linked_owned_results': EnforcementResultsEnum;
    /**
     * 
     * @type {EnforcementResultsEnum}
     * @memberof ComplianceEntity
     */
    'enforcement_results': EnforcementResultsEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'name': string;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof ComplianceEntity
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'first_name': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'last_name': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'middle_name': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'occupation': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'place_of_birth'?: string | null;
    /**
     * 
     * @type {ComplianceEntityGender}
     * @memberof ComplianceEntity
     */
    'gender'?: ComplianceEntityGender | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'date_of_birth'?: string | null;
    /**
     * 
     * @type {ComplianceEntityCompany}
     * @memberof ComplianceEntity
     */
    'company'?: ComplianceEntityCompany | null;
    /**
     * 
     * @type {ComplianceEntityDirector}
     * @memberof ComplianceEntity
     */
    'director': ComplianceEntityDirector | null;
    /**
     * 
     * @type {ComplianceEntityUbo}
     * @memberof ComplianceEntity
     */
    'ubo': ComplianceEntityUbo | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntity
     */
    'aliases_count': number;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntity
     */
    'has_results': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntity
     */
    'has_open_results': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntity
     */
    'all_false_positive': boolean;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'updated_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'last_screened_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntity
     */
    'results_changed_at': string | null;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntity
     */
    'is_manually_added': boolean;
}
/**
 * Compliance entity alias serializer.
 * @export
 * @interface ComplianceEntityAlias
 */
export interface ComplianceEntityAlias {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityAlias
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityAlias
     */
    'entity': string;
    /**
     * Alternative name or alias for the compliance entity.
     * @type {string}
     * @memberof ComplianceEntityAlias
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityAlias
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityAlias
     */
    'updated_at': string;
}
/**
 * Compliance entity alias serializer.
 * @export
 * @interface ComplianceEntityAliasRequest
 */
export interface ComplianceEntityAliasRequest {
    /**
     * Alternative name or alias for the compliance entity.
     * @type {string}
     * @memberof ComplianceEntityAliasRequest
     */
    'name': string;
}
/**
 * Compliance entity batch update serializer.
 * @export
 * @interface ComplianceEntityBatchUpdateRequest
 */
export interface ComplianceEntityBatchUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityBatchUpdateRequest
     */
    'external_id': string;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntityBatchUpdateRequest
     */
    'compliance_check_enabled'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntityBatchUpdateRequest
     */
    'adverse_media_check_enabled'?: boolean;
}
/**
 * 
 * @export
 * @interface ComplianceEntityCompany
 */
export interface ComplianceEntityCompany {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompany
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof ComplianceEntityCompany
     */
    'country': CountryEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompany
     */
    'duns_number'?: string | null;
}
/**
 * Lightweight company details for a linked compliance entity.
 * @export
 * @interface ComplianceEntityCompanyDetails
 */
export interface ComplianceEntityCompanyDetails {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompanyDetails
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompanyDetails
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof ComplianceEntityCompanyDetails
     */
    'country': CountryEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompanyDetails
     */
    'duns_number'?: string | null;
}
/**
 * Lightweight company details for a linked compliance entity.
 * @export
 * @interface ComplianceEntityCompanyDetailsRequest
 */
export interface ComplianceEntityCompanyDetailsRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompanyDetailsRequest
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompanyDetailsRequest
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof ComplianceEntityCompanyDetailsRequest
     */
    'country': CountryEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityCompanyDetailsRequest
     */
    'duns_number'?: string | null;
}
/**
 * 
 * @export
 * @interface ComplianceEntityDirector
 */
export interface ComplianceEntityDirector {
    /**
     * Get Source Name.
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'source_name': string;
    /**
     * 
     * @type {Array<ManagementResponsibility>}
     * @memberof ComplianceEntityDirector
     */
    'management_responsibilities': Array<ManagementResponsibility>;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntityDirector
     */
    'is_most_senior'?: boolean;
    /**
     * 
     * @type {RoleGroupEnum}
     * @memberof ComplianceEntityDirector
     */
    'role_group'?: RoleGroupEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'first_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'middle_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'last_name'?: string | null;
    /**
     * Function title within this organization.
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'title'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'full_name': string;
    /**
     * 
     * @type {DirectorGender}
     * @memberof ComplianceEntityDirector
     */
    'gender'?: DirectorGender | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'url'?: string | null;
    /**
     * Indicate if the director is no longer active for this company.
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'inactive_since'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityDirector
     */
    'updated_at': string;
}
/**
 * @type ComplianceEntityGender
 * @export
 */
export type ComplianceEntityGender = BlankEnum | ComplianceEntityGenderEnum | NullEnum;

/**
 * * `male` - Male * `female` - Female
 * @export
 * @enum {string}
 */

export const ComplianceEntityGenderEnum = {
    Male: 'male',
    Female: 'female'
} as const;

export type ComplianceEntityGenderEnum = typeof ComplianceEntityGenderEnum[keyof typeof ComplianceEntityGenderEnum];


/**
 * Compliance entity identifier serializer.
 * @export
 * @interface ComplianceEntityIdentifier
 */
export interface ComplianceEntityIdentifier {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityIdentifier
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityIdentifier
     */
    'entity': string;
    /**
     * 
     * @type {SchemeEnum}
     * @memberof ComplianceEntityIdentifier
     */
    'scheme': SchemeEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityIdentifier
     */
    'value': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityIdentifier
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityIdentifier
     */
    'updated_at': string;
}
/**
 * Compliance entity identifier serializer.
 * @export
 * @interface ComplianceEntityIdentifierRequest
 */
export interface ComplianceEntityIdentifierRequest {
    /**
     * 
     * @type {SchemeEnum}
     * @memberof ComplianceEntityIdentifierRequest
     */
    'scheme': SchemeEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityIdentifierRequest
     */
    'value': string;
}
/**
 * Compliance entity serializer.
 * @export
 * @interface ComplianceEntityRequest
 */
export interface ComplianceEntityRequest {
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntityRequest
     */
    'compliance_check_enabled'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceEntityRequest
     */
    'adverse_media_check_enabled'?: boolean;
    /**
     * 
     * @type {ComplianceEntityTypeEnum}
     * @memberof ComplianceEntityRequest
     */
    'entity_type'?: ComplianceEntityTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequest
     */
    'entity_role': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequest
     */
    'name': string;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof ComplianceEntityRequest
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequest
     */
    'place_of_birth'?: string | null;
    /**
     * 
     * @type {ComplianceEntityGender}
     * @memberof ComplianceEntityRequest
     */
    'gender'?: ComplianceEntityGender | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequest
     */
    'date_of_birth'?: string | null;
    /**
     * 
     * @type {ComplianceEntityRequestCompany}
     * @memberof ComplianceEntityRequest
     */
    'company'?: ComplianceEntityRequestCompany | null;
}
/**
 * 
 * @export
 * @interface ComplianceEntityRequestCompany
 */
export interface ComplianceEntityRequestCompany {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequestCompany
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequestCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof ComplianceEntityRequestCompany
     */
    'country': CountryEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityRequestCompany
     */
    'duns_number'?: string | null;
}
/**
 * Compliance entity result serializer.
 * @export
 * @interface ComplianceEntityResult
 */
export interface ComplianceEntityResult {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'external_id': string;
    /**
     * 
     * @type {ResultTypeEnum}
     * @memberof ComplianceEntityResult
     */
    'result_type': ResultTypeEnum;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityResult
     */
    'confidence': number | null;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof ComplianceEntityResult
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'false_positive_rating_datetime': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'false_positive_rating_user': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultAutomatedFalsePositiveRating}
     * @memberof ComplianceEntityResult
     */
    'automated_false_positive_rating': BinderComplianceEntityResultAutomatedFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'automated_false_positive_rating_datetime': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'automated_false_positive_rating_comments': string | null;
    /**
     * Return result type description.
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'result_type_description': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'image'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'title': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'title_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'text': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'text_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'formatted_title_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'formatted_text_en': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'formatted_title': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'formatted_text': string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultPepTier}
     * @memberof ComplianceEntityResult
     */
    'pep_tier': BinderComplianceEntityResultPepTier | null;
    /**
     * 
     * @type {BinderComplianceEntityResultPepRelationship}
     * @memberof ComplianceEntityResult
     */
    'pep_relationship': BinderComplianceEntityResultPepRelationship | null;
    /**
     * Name of the PEP this person is related to (family / associate only).
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'pep_related_to': string | null;
    /**
     * 
     * @type {BinderComplianceEntityResultSoeRelationshipType}
     * @memberof ComplianceEntityResult
     */
    'soe_relationship_type': BinderComplianceEntityResultSoeRelationshipType | null;
    /**
     * Percentage of government ownership, when reported.
     * @type {number}
     * @memberof ComplianceEntityResult
     */
    'soe_ownership_percentage': number | null;
    /**
     * Immediate parent or controlling government entity.
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'soe_parent_entity': string | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultSource>}
     * @memberof ComplianceEntityResult
     */
    'sources': Array<ComplianceEntityResultSource> | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultAddress>}
     * @memberof ComplianceEntityResult
     */
    'addresses': Array<ComplianceEntityResultAddress> | null;
    /**
     * 
     * @type {Array<ComplianceEntityResultTag>}
     * @memberof ComplianceEntityResult
     */
    'tags': Array<ComplianceEntityResultTag> | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'source_name'?: string | null;
    /**
     * Adverse media sub-source (news / icij / enforcement). Missing from the compliance API is treated as news.  * `news` - News * `icij` - ICIJ * `enforcement` - Enforcement
     * @type {SourceTypeEnum}
     * @memberof ComplianceEntityResult
     */
    'source_type': SourceTypeEnum;
    /**
     * Generic entity_document/v1 payload (enforcement/sanctions listings).
     * @type {any}
     * @memberof ComplianceEntityResult
     */
    'structured': any | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'source_date': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResult
     */
    'archived_at': string | null;
    /**
     * Return comment count.
     * @type {number}
     * @memberof ComplianceEntityResult
     */
    'comment_count': number;
    /**
     * Return binder count.
     * @type {number}
     * @memberof ComplianceEntityResult
     */
    'binder_count': number;
}
/**
 * 
 * @export
 * @interface ComplianceEntityResultAddress
 */
export interface ComplianceEntityResultAddress {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultAddress
     */
    'street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultAddress
     */
    'city'?: string | null;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof ComplianceEntityResultAddress
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultAddress
     */
    'postal_code'?: string | null;
}
/**
 * 
 * @export
 * @interface ComplianceEntityResultAddressRequest
 */
export interface ComplianceEntityResultAddressRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultAddressRequest
     */
    'street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultAddressRequest
     */
    'city'?: string | null;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof ComplianceEntityResultAddressRequest
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultAddressRequest
     */
    'postal_code'?: string | null;
}
/**
 * Compliance entity result batch serializer.
 * @export
 * @interface ComplianceEntityResultBatch
 */
export interface ComplianceEntityResultBatch {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultBatch
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof ComplianceEntityResultBatch
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultBatch
     */
    'false_positive_rating_comments'?: string | null;
}
/**
 * Compliance entity result batch serializer.
 * @export
 * @interface ComplianceEntityResultBatchRequest
 */
export interface ComplianceEntityResultBatchRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultBatchRequest
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof ComplianceEntityResultBatchRequest
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultBatchRequest
     */
    'false_positive_rating_comments'?: string | null;
}
/**
 * Compliance entity result serializer.
 * @export
 * @interface ComplianceEntityResultRequest
 */
export interface ComplianceEntityResultRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultRequest
     */
    'external_id': string;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof ComplianceEntityResultRequest
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultRequest
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultRequest
     */
    'url'?: string | null;
    /**
     * 
     * @type {any}
     * @memberof ComplianceEntityResultRequest
     */
    'image'?: any | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultRequest
     */
    'source_name'?: string | null;
}
/**
 * 
 * @export
 * @interface ComplianceEntityResultSelectAllBinderRequest
 */
export interface ComplianceEntityResultSelectAllBinderRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSelectAllBinderRequest
     */
    'binder_id': string;
}
/**
 * 
 * @export
 * @interface ComplianceEntityResultSelectAllRatingResponse
 */
export interface ComplianceEntityResultSelectAllRatingResponse {
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityResultSelectAllRatingResponse
     */
    'updated': number;
}
/**
 * Compliance entity result source serializer.
 * @export
 * @interface ComplianceEntityResultSource
 */
export interface ComplianceEntityResultSource {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSource
     */
    'title'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSource
     */
    'description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSource
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSource
     */
    'domain'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSource
     */
    'document'?: string | null;
    /**
     * Return publication date.
     * @type {string}
     * @memberof ComplianceEntityResultSource
     */
    'publication_date': string;
}
/**
 * Compliance entity result source serializer.
 * @export
 * @interface ComplianceEntityResultSourceRequest
 */
export interface ComplianceEntityResultSourceRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSourceRequest
     */
    'title'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSourceRequest
     */
    'description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSourceRequest
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultSourceRequest
     */
    'domain'?: string | null;
    /**
     * 
     * @type {any}
     * @memberof ComplianceEntityResultSourceRequest
     */
    'document'?: any | null;
}
/**
 * 
 * @export
 * @interface ComplianceEntityResultTag
 */
export interface ComplianceEntityResultTag {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultTag
     */
    'tag': string;
}
/**
 * 
 * @export
 * @interface ComplianceEntityResultTagRequest
 */
export interface ComplianceEntityResultTagRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityResultTagRequest
     */
    'tag': string;
}
/**
 * * `on_hold` - On Hold * `queued` - Queued * `in_progress` - In Progress * `completed` - Completed * `skipped` - Skipped * `failed` - Failed
 * @export
 * @enum {string}
 */

export const ComplianceEntityStatusEnum = {
    OnHold: 'on_hold',
    Queued: 'queued',
    InProgress: 'in_progress',
    Completed: 'completed',
    Skipped: 'skipped',
    Failed: 'failed'
} as const;

export type ComplianceEntityStatusEnum = typeof ComplianceEntityStatusEnum[keyof typeof ComplianceEntityStatusEnum];


/**
 * * `individual` - Individual * `company` - Company * `vessel` - Vessel
 * @export
 * @enum {string}
 */

export const ComplianceEntityTypeEnum = {
    Individual: 'individual',
    Company: 'company',
    Vessel: 'vessel'
} as const;

export type ComplianceEntityTypeEnum = typeof ComplianceEntityTypeEnum[keyof typeof ComplianceEntityTypeEnum];


/**
 * Compliance entity UBO details serializer.
 * @export
 * @interface ComplianceEntityUBODetails
 */
export interface ComplianceEntityUBODetails {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'name': string;
    /**
     * 
     * @type {ComplianceEntityUBODetailsEntityTypeEnum}
     * @memberof ComplianceEntityUBODetails
     */
    'entity_type': ComplianceEntityUBODetailsEntityTypeEnum;
    /**
     * 
     * @type {ComplianceEntityUBODetailsNationality}
     * @memberof ComplianceEntityUBODetails
     */
    'nationality'?: ComplianceEntityUBODetailsNationality | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'birth_date'?: string | null;
    /**
     * 
     * @type {ComplianceEntityUBODetailsAddressCountry}
     * @memberof ComplianceEntityUBODetails
     */
    'address_country'?: ComplianceEntityUBODetailsAddressCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'address_street_address'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'degree_of_separation'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'direct_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'indirect_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'beneficial_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'implied_direct_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'implied_indirect_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUBODetails
     */
    'implied_beneficial_ownership_percentage'?: number | null;
    /**
     * 
     * @type {Array<UboNatureOfControlType>}
     * @memberof ComplianceEntityUBODetails
     */
    'ubo_nature_of_control_types': Array<UboNatureOfControlType>;
    /**
     * Indicate if the ubo is no longer active for this company.
     * @type {string}
     * @memberof ComplianceEntityUBODetails
     */
    'inactive_since'?: string | null;
}
/**
 * @type ComplianceEntityUBODetailsAddressCountry
 * @export
 */
export type ComplianceEntityUBODetailsAddressCountry = AddressCountryEnum | BlankEnum | NullEnum;

/**
 * * `PERSON` - Person * `COMPANY` - Company
 * @export
 * @enum {string}
 */

export const ComplianceEntityUBODetailsEntityTypeEnum = {
    Person: 'PERSON',
    Company: 'COMPANY'
} as const;

export type ComplianceEntityUBODetailsEntityTypeEnum = typeof ComplianceEntityUBODetailsEntityTypeEnum[keyof typeof ComplianceEntityUBODetailsEntityTypeEnum];


/**
 * @type ComplianceEntityUBODetailsNationality
 * @export
 */
export type ComplianceEntityUBODetailsNationality = BlankEnum | NationalityEnum | NullEnum;

/**
 * 
 * @export
 * @interface ComplianceEntityUbo
 */
export interface ComplianceEntityUbo {
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'name': string;
    /**
     * 
     * @type {ComplianceEntityUBODetailsEntityTypeEnum}
     * @memberof ComplianceEntityUbo
     */
    'entity_type': ComplianceEntityUBODetailsEntityTypeEnum;
    /**
     * 
     * @type {ComplianceEntityUBODetailsNationality}
     * @memberof ComplianceEntityUbo
     */
    'nationality'?: ComplianceEntityUBODetailsNationality | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'birth_date'?: string | null;
    /**
     * 
     * @type {ComplianceEntityUBODetailsAddressCountry}
     * @memberof ComplianceEntityUbo
     */
    'address_country'?: ComplianceEntityUBODetailsAddressCountry | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'address_street_address'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'degree_of_separation'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'direct_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'indirect_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'beneficial_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'implied_direct_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'implied_indirect_ownership_percentage'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceEntityUbo
     */
    'implied_beneficial_ownership_percentage'?: number | null;
    /**
     * 
     * @type {Array<UboNatureOfControlType>}
     * @memberof ComplianceEntityUbo
     */
    'ubo_nature_of_control_types': Array<UboNatureOfControlType>;
    /**
     * Indicate if the ubo is no longer active for this company.
     * @type {string}
     * @memberof ComplianceEntityUbo
     */
    'inactive_since'?: string | null;
}
/**
 * The whole Reports-tab payload.
 * @export
 * @interface ComplianceReport
 */
export interface ComplianceReport {
    /**
     * 
     * @type {ComplianceReportPeriod}
     * @memberof ComplianceReport
     */
    'period': ComplianceReportPeriod;
    /**
     * 
     * @type {ComplianceReportSummary}
     * @memberof ComplianceReport
     */
    'summary': ComplianceReportSummary;
    /**
     * 
     * @type {ComplianceReportByPortfolio}
     * @memberof ComplianceReport
     */
    'by_portfolio': ComplianceReportByPortfolio;
    /**
     * 
     * @type {Array<ComplianceReportLatestConfirmed>}
     * @memberof ComplianceReport
     */
    'latest_confirmed': Array<ComplianceReportLatestConfirmed>;
    /**
     * 
     * @type {Array<ComplianceReportRiskCategory>}
     * @memberof ComplianceReport
     */
    'by_risk_category': Array<ComplianceReportRiskCategory>;
    /**
     * 
     * @type {ComplianceReportByRiskSignal}
     * @memberof ComplianceReport
     */
    'by_risk_signal': ComplianceReportByRiskSignal;
    /**
     * 
     * @type {Array<ComplianceReportCountryRisk>}
     * @memberof ComplianceReport
     */
    'by_country_risk': Array<ComplianceReportCountryRisk> | null;
    /**
     * 
     * @type {Array<ComplianceReportIndustryRisk>}
     * @memberof ComplianceReport
     */
    'by_industry_risk': Array<ComplianceReportIndustryRisk> | null;
    /**
     * 
     * @type {ComplianceReportTrends}
     * @memberof ComplianceReport
     */
    'trends': ComplianceReportTrends;
}
/**
 * Per-portfolio rows plus a total counted without the portfolio join.
 * @export
 * @interface ComplianceReportByPortfolio
 */
export interface ComplianceReportByPortfolio {
    /**
     * 
     * @type {Array<ComplianceReportPortfolioRow>}
     * @memberof ComplianceReportByPortfolio
     */
    'rows': Array<ComplianceReportPortfolioRow>;
    /**
     * 
     * @type {ComplianceReportPortfolioTotal}
     * @memberof ComplianceReportByPortfolio
     */
    'total': ComplianceReportPortfolioTotal;
}
/**
 * Risk-signal flags against the screened population.
 * @export
 * @interface ComplianceReportByRiskSignal
 */
export interface ComplianceReportByRiskSignal {
    /**
     * 
     * @type {Array<ComplianceReportRiskSignal>}
     * @memberof ComplianceReportByRiskSignal
     */
    'signals': Array<ComplianceReportRiskSignal>;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportByRiskSignal
     */
    'screened_companies_total': number;
}
/**
 * Counterparties and alerts for one country of incorporation.
 * @export
 * @interface ComplianceReportCountryRisk
 */
export interface ComplianceReportCountryRisk {
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportCountryRisk
     */
    'country': string;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportCountryRisk
     */
    'companies': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportCountryRisk
     */
    'alerts': number;
    /**
     * 
     * @type {ComplianceReportCountryRiskScore}
     * @memberof ComplianceReportCountryRisk
     */
    'score': ComplianceReportCountryRiskScore | null;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceReportCountryRisk
     */
    'is_high_risk': boolean;
}
/**
 * @type ComplianceReportCountryRiskScore
 * @export
 */
export type ComplianceReportCountryRiskScore = ComplianceCheckScoreEnum | NullEnum;

/**
 * Counterparties and alerts for one primary industry code.
 * @export
 * @interface ComplianceReportIndustryRisk
 */
export interface ComplianceReportIndustryRisk {
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportIndustryRisk
     */
    'industry_code': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportIndustryRisk
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportIndustryRisk
     */
    'companies': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportIndustryRisk
     */
    'alerts': number;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof ComplianceReportIndustryRisk
     */
    'score': ComplianceCheckScoreEnum;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceReportIndustryRisk
     */
    'is_high_risk': boolean;
}
/**
 * A recently confirmed risk.
 * @export
 * @interface ComplianceReportLatestConfirmed
 */
export interface ComplianceReportLatestConfirmed {
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportLatestConfirmed
     */
    'company': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportLatestConfirmed
     */
    'title': string | null;
    /**
     * 
     * @type {RiskCategoryEnum}
     * @memberof ComplianceReportLatestConfirmed
     */
    'risk_category': RiskCategoryEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportLatestConfirmed
     */
    'portfolio': string | null;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportLatestConfirmed
     */
    'confirmed_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportLatestConfirmed
     */
    'compliance_check_id': string;
}
/**
 * Findings still awaiting or holding a risk verdict.
 * @export
 * @interface ComplianceReportOpenFindings
 */
export interface ComplianceReportOpenFindings {
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportOpenFindings
     */
    'to_investigate': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportOpenFindings
     */
    'confirmed_open': number;
}
/**
 * The window the report covers.
 * @export
 * @interface ComplianceReportPeriod
 */
export interface ComplianceReportPeriod {
    /**
     * 
     * @type {PeriodEnum}
     * @memberof ComplianceReportPeriod
     */
    'period': PeriodEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportPeriod
     */
    'start': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportPeriod
     */
    'end': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportPeriod
     */
    'previous_start': string;
}
/**
 * One portfolio\'s row.
 * @export
 * @interface ComplianceReportPortfolioRow
 */
export interface ComplianceReportPortfolioRow {
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioRow
     */
    'company_count': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioRow
     */
    'alerts': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioRow
     */
    'reviewed': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioRow
     */
    'confirmed': number;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportPortfolioRow
     */
    'portfolio_id': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportPortfolioRow
     */
    'name': string;
}
/**
 * Portfolio funnel counts.
 * @export
 * @interface ComplianceReportPortfolioTotal
 */
export interface ComplianceReportPortfolioTotal {
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioTotal
     */
    'company_count': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioTotal
     */
    'alerts': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioTotal
     */
    'reviewed': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPortfolioTotal
     */
    'confirmed': number;
}
/**
 * The previous equal-length period, for deltas. Null when incomplete.
 * @export
 * @interface ComplianceReportPreviousSummary
 */
export interface ComplianceReportPreviousSummary {
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPreviousSummary
     */
    'total_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPreviousSummary
     */
    'relevant_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPreviousSummary
     */
    'relevant_share': number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportPreviousSummary
     */
    'false_positive_rate': number | null;
}
/**
 * Alert count for one result type.
 * @export
 * @interface ComplianceReportRiskCategory
 */
export interface ComplianceReportRiskCategory {
    /**
     * 
     * @type {CategoryEnum}
     * @memberof ComplianceReportRiskCategory
     */
    'category': CategoryEnum;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportRiskCategory
     */
    'count': number;
    /**
     * 
     * @type {boolean}
     * @memberof ComplianceReportRiskCategory
     */
    'is_high_risk': boolean;
}
/**
 * Companies flagged high on one risk signal.
 * @export
 * @interface ComplianceReportRiskSignal
 */
export interface ComplianceReportRiskSignal {
    /**
     * 
     * @type {SignalEnum}
     * @memberof ComplianceReportRiskSignal
     */
    'signal': SignalEnum;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportRiskSignal
     */
    'companies_flagged_high': number;
}
/**
 * Executive summary. Current-state metrics carry no previous value.
 * @export
 * @interface ComplianceReportSummary
 */
export interface ComplianceReportSummary {
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'total_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'relevant_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'relevant_share': number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'false_positive_rate': number | null;
    /**
     * 
     * @type {ComplianceReportOpenFindings}
     * @memberof ComplianceReportSummary
     */
    'open_findings': ComplianceReportOpenFindings;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'average_review_days': number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'open_cases': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummary
     */
    'closed_cases': number;
    /**
     * 
     * @type {ComplianceReportSummaryPrevious}
     * @memberof ComplianceReportSummary
     */
    'previous': ComplianceReportSummaryPrevious | null;
}
/**
 * 
 * @export
 * @interface ComplianceReportSummaryPrevious
 */
export interface ComplianceReportSummaryPrevious {
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummaryPrevious
     */
    'total_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummaryPrevious
     */
    'relevant_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummaryPrevious
     */
    'relevant_share': number | null;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportSummaryPrevious
     */
    'false_positive_rate': number | null;
}
/**
 * One point on the trends chart.
 * @export
 * @interface ComplianceReportTrendBucket
 */
export interface ComplianceReportTrendBucket {
    /**
     * 
     * @type {string}
     * @memberof ComplianceReportTrendBucket
     */
    'bucket_start': string;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportTrendBucket
     */
    'relevant_findings': number;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportTrendBucket
     */
    'confirmed': number;
}
/**
 * Findings over time, bucketed to suit the period.
 * @export
 * @interface ComplianceReportTrends
 */
export interface ComplianceReportTrends {
    /**
     * 
     * @type {GranularityEnum}
     * @memberof ComplianceReportTrends
     */
    'granularity': GranularityEnum;
    /**
     * 
     * @type {Array<ComplianceReportTrendBucket>}
     * @memberof ComplianceReportTrends
     */
    'buckets': Array<ComplianceReportTrendBucket>;
    /**
     * 
     * @type {number}
     * @memberof ComplianceReportTrends
     */
    'median': number | null;
}
/**
 * Customer-managed compliance settings.  Entitlement flags and the caller\'s admin status are read-only extras for the module badge and role banner.
 * @export
 * @interface ComplianceSettings
 */
export interface ComplianceSettings {
    /**
     * 
     * @type {string}
     * @memberof ComplianceSettings
     */
    'external_id': string;
    /**
     * Include the industry risk indicator in compliance checks.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_industry_risk_enabled'?: boolean;
    /**
     * Include the world (country) risk indicator in compliance checks.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_world_risk_enabled'?: boolean;
    /**
     * Exclude results already rated as false positive (or no risk / discarded) from ongoing-monitoring alert emails.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'monitoring_exclude_false_positives'?: boolean;
    /**
     * Compliance API provider keys to exclude from sanction screening. Empty means every provider is screened.
     * @type {any}
     * @memberof ComplianceSettings
     */
    'sanction_excluded_providers'?: any;
    /**
     * Adverse media source keys to exclude from enforcement screening (\'news\', \'icij\', \'enforcement\', or an enforcement provider name). Empty means every source is included.
     * @type {any}
     * @memberof ComplianceSettings
     */
    'enforcement_excluded_sources'?: any;
    /**
     * Disable partial name matching for adverse media screening (exact name matching only).
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'disable_partial_name_search'?: boolean;
    /**
     * Minimum match probability (0.0-1.0) for a sanction hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof ComplianceSettings
     */
    'sanction_matching_threshold'?: number | null;
    /**
     * Minimum match probability (0.0-1.0) for a PEP hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof ComplianceSettings
     */
    'pep_matching_threshold'?: number | null;
    /**
     * PEP tiers to screen (TIER_1, TIER_2, TIER_3, INTERNATIONAL_ORG). Empty means all tiers.
     * @type {any}
     * @memberof ComplianceSettings
     */
    'pep_tiers'?: any;
    /**
     * Also screen relatives and close associates (RCA) of PEPs.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'pep_include_rca'?: boolean;
    /**
     * Minimum match probability (0.0-1.0) for an SOE hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof ComplianceSettings
     */
    'soe_matching_threshold'?: number | null;
    /**
     * SOE relationship types to screen (WHOLLY_OWNED, MAJORITY_OWNED, MINORITY_OWNED, STATE_CONTROLLED, GOVERNMENT_LINKED). Empty means all types.
     * @type {any}
     * @memberof ComplianceSettings
     */
    'soe_relationship_types'?: any;
    /**
     * Whether the caller may edit these settings.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_admin': boolean;
    /**
     * Whether sanctions screening is entitled for the account.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_compliance_sanctions_enabled': boolean;
    /**
     * Whether PEP screening is entitled for the account.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_compliance_pep_enabled': boolean;
    /**
     * Whether adverse media screening is entitled for the account.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_compliance_adverse_media_enabled': boolean;
    /**
     * Whether SOE screening is entitled for the account.
     * @type {boolean}
     * @memberof ComplianceSettings
     */
    'is_compliance_soe_enabled': boolean;
    /**
     * 
     * @type {string}
     * @memberof ComplianceSettings
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceSettings
     */
    'updated_at': string;
}
/**
 * A single compliance settings change (read-only audit entry).
 * @export
 * @interface ComplianceSettingsChangeLog
 */
export interface ComplianceSettingsChangeLog {
    /**
     * 
     * @type {string}
     * @memberof ComplianceSettingsChangeLog
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ComplianceSettingsChangeLog
     */
    'field': string;
    /**
     * 
     * @type {any}
     * @memberof ComplianceSettingsChangeLog
     */
    'old_value': any | null;
    /**
     * 
     * @type {any}
     * @memberof ComplianceSettingsChangeLog
     */
    'new_value': any | null;
    /**
     * Display name of who made the change (null for system changes).
     * @type {string}
     * @memberof ComplianceSettingsChangeLog
     */
    'changed_by': string;
    /**
     * 
     * @type {SourceEnum}
     * @memberof ComplianceSettingsChangeLog
     */
    'source': SourceEnum;
    /**
     * 
     * @type {string}
     * @memberof ComplianceSettingsChangeLog
     */
    'created_at': string;
}
/**
 * Customer-managed compliance settings.  Entitlement flags and the caller\'s admin status are read-only extras for the module badge and role banner.
 * @export
 * @interface ComplianceSettingsRequest
 */
export interface ComplianceSettingsRequest {
    /**
     * Include the industry risk indicator in compliance checks.
     * @type {boolean}
     * @memberof ComplianceSettingsRequest
     */
    'is_industry_risk_enabled'?: boolean;
    /**
     * Include the world (country) risk indicator in compliance checks.
     * @type {boolean}
     * @memberof ComplianceSettingsRequest
     */
    'is_world_risk_enabled'?: boolean;
    /**
     * Exclude results already rated as false positive (or no risk / discarded) from ongoing-monitoring alert emails.
     * @type {boolean}
     * @memberof ComplianceSettingsRequest
     */
    'monitoring_exclude_false_positives'?: boolean;
    /**
     * Compliance API provider keys to exclude from sanction screening. Empty means every provider is screened.
     * @type {any}
     * @memberof ComplianceSettingsRequest
     */
    'sanction_excluded_providers'?: any;
    /**
     * Adverse media source keys to exclude from enforcement screening (\'news\', \'icij\', \'enforcement\', or an enforcement provider name). Empty means every source is included.
     * @type {any}
     * @memberof ComplianceSettingsRequest
     */
    'enforcement_excluded_sources'?: any;
    /**
     * Disable partial name matching for adverse media screening (exact name matching only).
     * @type {boolean}
     * @memberof ComplianceSettingsRequest
     */
    'disable_partial_name_search'?: boolean;
    /**
     * Minimum match probability (0.0-1.0) for a sanction hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof ComplianceSettingsRequest
     */
    'sanction_matching_threshold'?: number | null;
    /**
     * Minimum match probability (0.0-1.0) for a PEP hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof ComplianceSettingsRequest
     */
    'pep_matching_threshold'?: number | null;
    /**
     * PEP tiers to screen (TIER_1, TIER_2, TIER_3, INTERNATIONAL_ORG). Empty means all tiers.
     * @type {any}
     * @memberof ComplianceSettingsRequest
     */
    'pep_tiers'?: any;
    /**
     * Also screen relatives and close associates (RCA) of PEPs.
     * @type {boolean}
     * @memberof ComplianceSettingsRequest
     */
    'pep_include_rca'?: boolean;
    /**
     * Minimum match probability (0.0-1.0) for an SOE hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof ComplianceSettingsRequest
     */
    'soe_matching_threshold'?: number | null;
    /**
     * SOE relationship types to screen (WHOLLY_OWNED, MAJORITY_OWNED, MINORITY_OWNED, STATE_CONTROLLED, GOVERNMENT_LINKED). Empty means all types.
     * @type {any}
     * @memberof ComplianceSettingsRequest
     */
    'soe_relationship_types'?: any;
}
/**
 * Read/create serializer for conversations.
 * @export
 * @interface Conversation
 */
export interface Conversation {
    /**
     * 
     * @type {string}
     * @memberof Conversation
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Conversation
     */
    'title'?: string;
    /**
     * 
     * @type {ChannelEnum}
     * @memberof Conversation
     */
    'channel': ChannelEnum;
    /**
     * 
     * @type {boolean}
     * @memberof Conversation
     */
    'is_archived'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Conversation
     */
    'has_unread': boolean;
    /**
     * 
     * @type {string}
     * @memberof Conversation
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Conversation
     */
    'updated_at': string;
}
/**
 * Read/create serializer for conversations.
 * @export
 * @interface ConversationRequest
 */
export interface ConversationRequest {
    /**
     * 
     * @type {string}
     * @memberof ConversationRequest
     */
    'title'?: string;
    /**
     * 
     * @type {boolean}
     * @memberof ConversationRequest
     */
    'is_archived'?: boolean;
}
/**
 * Serializer for CorporateLinkageRegistration model.  Handles the serialization of corporate linkage registration data, including: - Company and portfolio identification via external_ids - Scope of the corporate linkage import - Registration status tracking
 * @export
 * @interface CorporateLinkageRegistration
 */
export interface CorporateLinkageRegistration {
    /**
     * 
     * @type {string}
     * @memberof CorporateLinkageRegistration
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof CorporateLinkageRegistration
     */
    'company': string;
    /**
     * 
     * @type {ScopeEnum}
     * @memberof CorporateLinkageRegistration
     */
    'scope'?: ScopeEnum;
    /**
     * 
     * @type {string}
     * @memberof CorporateLinkageRegistration
     */
    'portfolio': string;
    /**
     * 
     * @type {string}
     * @memberof CorporateLinkageRegistration
     */
    'started_at': string | null;
    /**
     * Datestamp on when the registration was complete. If failed this is empty.
     * @type {string}
     * @memberof CorporateLinkageRegistration
     */
    'finished_at': string | null;
}
/**
 * Serializer for CorporateLinkageRegistration model.  Handles the serialization of corporate linkage registration data, including: - Company and portfolio identification via external_ids - Scope of the corporate linkage import - Registration status tracking
 * @export
 * @interface CorporateLinkageRegistrationRequest
 */
export interface CorporateLinkageRegistrationRequest {
    /**
     * 
     * @type {ScopeEnum}
     * @memberof CorporateLinkageRegistrationRequest
     */
    'scope'?: ScopeEnum;
    /**
     * 
     * @type {string}
     * @memberof CorporateLinkageRegistrationRequest
     */
    'portfolio': string;
}
/**
 * * `AF` - Afghanistan * `AX` - Aland Islands * `AL` - Albania * `DZ` - Algeria * `AS` - American Samoa * `AD` - Andorra * `AO` - Angola * `AI` - Anguilla * `AQ` - Antarctica * `AG` - Antigua and Barbuda * `AR` - Argentina * `AM` - Armenia * `AW` - Aruba * `AU` - Australia * `AT` - Austria * `AZ` - Azerbaijan * `BS` - Bahamas * `BH` - Bahrain * `BD` - Bangladesh * `BB` - Barbados * `BY` - Belarus * `BE` - Belgium * `BZ` - Belize * `BJ` - Benin * `BM` - Bermuda * `BT` - Bhutan * `BO` - Bolivia * `BQ` - Bonaire * `BA` - Bosnia and Herzegovina * `BW` - Botswana * `BV` - Bouvet Island * `BR` - Brazil * `IO` - British Indian Ocean Territory * `BN` - Brunei Darussalam * `BG` - Bulgaria * `BF` - Burkina Faso * `BI` - Burundi * `CV` - Cabo Verde * `KH` - Cambodia * `CM` - Cameroon * `CA` - Canada * `KY` - Cayman Islands * `CF` - Central African Republic * `TD` - Chad * `CL` - Chile * `CN` - China * `CX` - Christmas Island * `CC` - Cocos Keeling Islands * `CO` - Colombia * `KM` - Comoros * `CG` - Congo * `CD` - Congo Democratic Republic * `CK` - Cook Islands * `CR` - Costa Rica * `CI` - Cote d\'Ivoire * `HR` - Croatia * `CU` - Cuba * `CW` - Curacao * `CY` - Cyprus * `CZ` - Czechia * `DK` - Denmark * `DJ` - Djibouti * `DM` - Dominica * `DO` - Dominican Republic * `EC` - Ecuador * `EG` - Egypt * `SV` - El Salvador * `GQ` - Equatorial Guinea * `ER` - Eritrea * `EE` - Estonia * `SZ` - Eswatini * `ET` - Ethiopia * `FK` - Falkland Islands * `FO` - Faroe Islands * `FJ` - Fiji * `FI` - Finland * `FR` - France * `GF` - French Guiana * `PF` - French Polynesia * `TF` - French Southern Territories * `GA` - Gabon * `GM` - Gambia * `GE` - Georgia * `DE` - Germany * `GH` - Ghana * `GI` - Gibraltar * `GR` - Greece * `GL` - Greenland * `GD` - Grenada * `GP` - Guadeloupe * `GU` - Guam * `GT` - Guatemala * `GG` - Guernsey * `GN` - Guinea * `GW` - Guinea-Bissau * `GY` - Guyana * `HT` - Haiti * `HM` - Heard Island and McDonald Islands * `VA` - Holy See * `HN` - Honduras * `HK` - Hong Kong * `HU` - Hungary * `IS` - Iceland * `IN` - India * `ID` - Indonesia * `IR` - Iran (Islamic Republic of) * `IQ` - Iraq * `IE` - Ireland * `IM` - Isle of Man * `IL` - Israel * `IT` - Italy * `JM` - Jamaica * `JP` - Japan * `JE` - Jersey * `JO` - Jordan * `KZ` - Kazakhstan * `KE` - Kenya * `KI` - Kiribati * `KP` - Korea (the Democratic People\'s Republic of) * `KR` - Korea (the Republic of) * `XK` - Kosovo * `KW` - Kuwait * `KG` - Kyrgyzstan * `LA` - Lao People\'s Democratic Republic * `LV` - Latvia * `LB` - Lebanon * `LS` - Lesotho * `LR` - Liberia * `LY` - Libya * `LI` - Liechtenstein * `LT` - Lithuania * `LU` - Luxembourg * `MO` - Macao * `MG` - Madagascar * `MW` - Malawi * `MY` - Malaysia * `MV` - Maldives * `ML` - Mali * `MT` - Malta * `MH` - Marshall Islands * `MQ` - Martinique * `MR` - Mauritania * `MU` - Mauritius * `YT` - Mayotte * `MX` - Mexico * `FM` - Micronesia * `MD` - Moldova * `MC` - Monaco * `MN` - Mongolia * `ME` - Montenegro * `MS` - Montserrat * `MA` - Morocco * `MZ` - Mozambique * `MM` - Myanmar * `NA` - Namibia * `NR` - Nauru * `NP` - Nepal * `NL` - Netherlands * `NC` - New Caledonia * `NZ` - New Zealand * `NI` - Nicaragua * `NE` - Niger * `NG` - Nigeria * `NU` - Niue * `NF` - Norfolk Island * `MK` - North Macedonia * `MP` - Northern Mariana Islands * `NO` - Norway * `OM` - Oman * `PK` - Pakistan * `PW` - Palau * `PS` - Palestine, State of * `PA` - Panama * `PG` - Papua New Guinea * `PY` - Paraguay * `PE` - Peru * `PH` - Philippines * `PN` - Pitcairn * `PL` - Poland * `PT` - Portugal * `PR` - Puerto Rico * `QA` - Qatar * `RE` - Réunion * `RO` - Romania * `RU` - Russian Federation * `RW` - Rwanda * `BL` - Saint Barthélemy * `SH` - Saint Helena * `KN` - Saint Kitts and Nevis * `LC` - Saint Lucia * `MF` - Saint Martin * `PM` - Saint Pierre and Miquelon * `VC` - Saint Vincent and the Grenadines * `WS` - Samoa * `SM` - San Marino * `ST` - Sao Tome and Principe * `SA` - Saudi Arabia * `SN` - Senegal * `RS` - Serbia * `SC` - Seychelles * `SL` - Sierra Leone * `SG` - Singapore * `SX` - Sint Maarten * `SK` - Slovakia * `SI` - Slovenia * `SB` - Solomon Islands * `SO` - Somalia * `ZA` - South Africa * `GS` - South Georgia and the South Sandwich Islands * `SS` - South Sudan * `ES` - Spain * `LK` - Sri Lanka * `SD` - Sudan * `SR` - Suriname * `SJ` - Svalbard and Jan Mayen * `SE` - Sweden * `CH` - Switzerland * `SY` - Syrian Arab Republic * `TW` - Taiwan * `TJ` - Tajikistan * `TZ` - Tanzania * `TH` - Thailand * `TL` - Timor-Leste * `TG` - Togo * `TK` - Tokelau * `TO` - Tonga * `TT` - Trinidad and Tobago * `TN` - Tunisia * `TR` - Turkey * `TM` - Turkmenistan * `TC` - Turks and Caicos Islands * `TV` - Tuvalu * `UG` - Uganda * `UA` - Ukraine * `AE` - United Arab Emirates * `GB` - United Kingdom * `UM` - United States Minor Outlying Islands * `US` - United States of America * `UY` - Uruguay * `UZ` - Uzbekistan * `VU` - Vanuatu * `VE` - Venezuela * `VN` - Viet Nam * `VG` - Virgin Islands * `VI` - Virgin Islands * `WF` - Wallis and Futuna * `EH` - Western Sahara * `YE` - Yemen * `ZM` - Zambia * `ZW` - Zimbabwe
 * @export
 * @enum {string}
 */

export const CountryEnum = {
    Af: 'AF',
    Ax: 'AX',
    Al: 'AL',
    Dz: 'DZ',
    As: 'AS',
    Ad: 'AD',
    Ao: 'AO',
    Ai: 'AI',
    Aq: 'AQ',
    Ag: 'AG',
    Ar: 'AR',
    Am: 'AM',
    Aw: 'AW',
    Au: 'AU',
    At: 'AT',
    Az: 'AZ',
    Bs: 'BS',
    Bh: 'BH',
    Bd: 'BD',
    Bb: 'BB',
    By: 'BY',
    Be: 'BE',
    Bz: 'BZ',
    Bj: 'BJ',
    Bm: 'BM',
    Bt: 'BT',
    Bo: 'BO',
    Bq: 'BQ',
    Ba: 'BA',
    Bw: 'BW',
    Bv: 'BV',
    Br: 'BR',
    Io: 'IO',
    Bn: 'BN',
    Bg: 'BG',
    Bf: 'BF',
    Bi: 'BI',
    Cv: 'CV',
    Kh: 'KH',
    Cm: 'CM',
    Ca: 'CA',
    Ky: 'KY',
    Cf: 'CF',
    Td: 'TD',
    Cl: 'CL',
    Cn: 'CN',
    Cx: 'CX',
    Cc: 'CC',
    Co: 'CO',
    Km: 'KM',
    Cg: 'CG',
    Cd: 'CD',
    Ck: 'CK',
    Cr: 'CR',
    Ci: 'CI',
    Hr: 'HR',
    Cu: 'CU',
    Cw: 'CW',
    Cy: 'CY',
    Cz: 'CZ',
    Dk: 'DK',
    Dj: 'DJ',
    Dm: 'DM',
    Do: 'DO',
    Ec: 'EC',
    Eg: 'EG',
    Sv: 'SV',
    Gq: 'GQ',
    Er: 'ER',
    Ee: 'EE',
    Sz: 'SZ',
    Et: 'ET',
    Fk: 'FK',
    Fo: 'FO',
    Fj: 'FJ',
    Fi: 'FI',
    Fr: 'FR',
    Gf: 'GF',
    Pf: 'PF',
    Tf: 'TF',
    Ga: 'GA',
    Gm: 'GM',
    Ge: 'GE',
    De: 'DE',
    Gh: 'GH',
    Gi: 'GI',
    Gr: 'GR',
    Gl: 'GL',
    Gd: 'GD',
    Gp: 'GP',
    Gu: 'GU',
    Gt: 'GT',
    Gg: 'GG',
    Gn: 'GN',
    Gw: 'GW',
    Gy: 'GY',
    Ht: 'HT',
    Hm: 'HM',
    Va: 'VA',
    Hn: 'HN',
    Hk: 'HK',
    Hu: 'HU',
    Is: 'IS',
    In: 'IN',
    Id: 'ID',
    Ir: 'IR',
    Iq: 'IQ',
    Ie: 'IE',
    Im: 'IM',
    Il: 'IL',
    It: 'IT',
    Jm: 'JM',
    Jp: 'JP',
    Je: 'JE',
    Jo: 'JO',
    Kz: 'KZ',
    Ke: 'KE',
    Ki: 'KI',
    Kp: 'KP',
    Kr: 'KR',
    Xk: 'XK',
    Kw: 'KW',
    Kg: 'KG',
    La: 'LA',
    Lv: 'LV',
    Lb: 'LB',
    Ls: 'LS',
    Lr: 'LR',
    Ly: 'LY',
    Li: 'LI',
    Lt: 'LT',
    Lu: 'LU',
    Mo: 'MO',
    Mg: 'MG',
    Mw: 'MW',
    My: 'MY',
    Mv: 'MV',
    Ml: 'ML',
    Mt: 'MT',
    Mh: 'MH',
    Mq: 'MQ',
    Mr: 'MR',
    Mu: 'MU',
    Yt: 'YT',
    Mx: 'MX',
    Fm: 'FM',
    Md: 'MD',
    Mc: 'MC',
    Mn: 'MN',
    Me: 'ME',
    Ms: 'MS',
    Ma: 'MA',
    Mz: 'MZ',
    Mm: 'MM',
    Na: 'NA',
    Nr: 'NR',
    Np: 'NP',
    Nl: 'NL',
    Nc: 'NC',
    Nz: 'NZ',
    Ni: 'NI',
    Ne: 'NE',
    Ng: 'NG',
    Nu: 'NU',
    Nf: 'NF',
    Mk: 'MK',
    Mp: 'MP',
    No: 'NO',
    Om: 'OM',
    Pk: 'PK',
    Pw: 'PW',
    Ps: 'PS',
    Pa: 'PA',
    Pg: 'PG',
    Py: 'PY',
    Pe: 'PE',
    Ph: 'PH',
    Pn: 'PN',
    Pl: 'PL',
    Pt: 'PT',
    Pr: 'PR',
    Qa: 'QA',
    Re: 'RE',
    Ro: 'RO',
    Ru: 'RU',
    Rw: 'RW',
    Bl: 'BL',
    Sh: 'SH',
    Kn: 'KN',
    Lc: 'LC',
    Mf: 'MF',
    Pm: 'PM',
    Vc: 'VC',
    Ws: 'WS',
    Sm: 'SM',
    St: 'ST',
    Sa: 'SA',
    Sn: 'SN',
    Rs: 'RS',
    Sc: 'SC',
    Sl: 'SL',
    Sg: 'SG',
    Sx: 'SX',
    Sk: 'SK',
    Si: 'SI',
    Sb: 'SB',
    So: 'SO',
    Za: 'ZA',
    Gs: 'GS',
    Ss: 'SS',
    Es: 'ES',
    Lk: 'LK',
    Sd: 'SD',
    Sr: 'SR',
    Sj: 'SJ',
    Se: 'SE',
    Ch: 'CH',
    Sy: 'SY',
    Tw: 'TW',
    Tj: 'TJ',
    Tz: 'TZ',
    Th: 'TH',
    Tl: 'TL',
    Tg: 'TG',
    Tk: 'TK',
    To: 'TO',
    Tt: 'TT',
    Tn: 'TN',
    Tr: 'TR',
    Tm: 'TM',
    Tc: 'TC',
    Tv: 'TV',
    Ug: 'UG',
    Ua: 'UA',
    Ae: 'AE',
    Gb: 'GB',
    Um: 'UM',
    Us: 'US',
    Uy: 'UY',
    Uz: 'UZ',
    Vu: 'VU',
    Ve: 'VE',
    Vn: 'VN',
    Vg: 'VG',
    Vi: 'VI',
    Wf: 'WF',
    Eh: 'EH',
    Ye: 'YE',
    Zm: 'ZM',
    Zw: 'ZW'
} as const;

export type CountryEnum = typeof CountryEnum[keyof typeof CountryEnum];


/**
 * 
 * @export
 * @interface CurrentUsage
 */
export interface CurrentUsage {
    /**
     * 
     * @type {number}
     * @memberof CurrentUsage
     */
    'company_usage': number;
    /**
     * 
     * @type {number}
     * @memberof CurrentUsage
     */
    'company_usage_budget': number | null;
    /**
     * 
     * @type {number}
     * @memberof CurrentUsage
     */
    'ubo_call_usage': number;
    /**
     * 
     * @type {number}
     * @memberof CurrentUsage
     */
    'ubo_call_usage_budget': number | null;
    /**
     * 
     * @type {number}
     * @memberof CurrentUsage
     */
    'compliance_check_usage': number;
    /**
     * 
     * @type {number}
     * @memberof CurrentUsage
     */
    'compliance_check_usage_budget': number | null;
    /**
     * 
     * @type {boolean}
     * @memberof CurrentUsage
     */
    'is_overusage_hard_restricted': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof CurrentUsage
     */
    'automatical_renewal': boolean;
    /**
     * 
     * @type {string}
     * @memberof CurrentUsage
     */
    'end_date': string;
}
/**
 * 
 * @export
 * @interface DailyUsage
 */
export interface DailyUsage {
    /**
     * 
     * @type {string}
     * @memberof DailyUsage
     */
    'date': string;
    /**
     * 
     * @type {number}
     * @memberof DailyUsage
     */
    'company_usage': number;
    /**
     * 
     * @type {number}
     * @memberof DailyUsage
     */
    'ubo_call_usage': number;
    /**
     * 
     * @type {number}
     * @memberof DailyUsage
     */
    'compliance_check_usage': number;
}
/**
 * Data Export Serializer.
 * @export
 * @interface DataExport
 */
export interface DataExport {
    /**
     * 
     * @type {string}
     * @memberof DataExport
     */
    'external_id': string;
    /**
     * 
     * @type {ExportTypeEnum}
     * @memberof DataExport
     */
    'export_type': ExportTypeEnum;
    /**
     * 
     * @type {DataExportFileType}
     * @memberof DataExport
     */
    'file_type': DataExportFileType;
    /**
     * 
     * @type {any}
     * @memberof DataExport
     */
    'filters'?: any | null;
    /**
     * Location of exports
     * @type {string}
     * @memberof DataExport
     */
    'location': string | null;
    /**
     * 
     * @type {Status180Enum}
     * @memberof DataExport
     */
    'status': Status180Enum;
    /**
     * 
     * @type {number}
     * @memberof DataExport
     */
    'result_count': number | null;
    /**
     * 
     * @type {string}
     * @memberof DataExport
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof DataExport
     */
    'updated_at': string;
}
/**
 * * `PDF` - PDF * `EXCEL` - Excel * `JSONL` - JSONL
 * @export
 * @enum {string}
 */

export const DataExportFileType = {
    Pdf: 'PDF',
    Excel: 'EXCEL',
    Jsonl: 'JSONL'
} as const;

export type DataExportFileType = typeof DataExportFileType[keyof typeof DataExportFileType];


/**
 * Data Export Serializer.
 * @export
 * @interface DataExportRequest
 */
export interface DataExportRequest {
    /**
     * 
     * @type {DataExportFileType}
     * @memberof DataExportRequest
     */
    'file_type': DataExportFileType;
    /**
     * 
     * @type {any}
     * @memberof DataExportRequest
     */
    'filters'?: any | null;
}
/**
 * * `view_only` - Only Viewing Access * `write` - View and Write Access * `admin` - View, Write and Admin Access
 * @export
 * @enum {string}
 */

export const DefaultPermission506Enum = {
    ViewOnly: 'view_only',
    Write: 'write',
    Admin: 'admin'
} as const;

export type DefaultPermission506Enum = typeof DefaultPermission506Enum[keyof typeof DefaultPermission506Enum];


/**
 * Custom Serializer for the Director Model.
 * @export
 * @interface Director
 */
export interface Director {
    /**
     * Get Source Name.
     * @type {string}
     * @memberof Director
     */
    'source_name': string;
    /**
     * 
     * @type {Array<ManagementResponsibility>}
     * @memberof Director
     */
    'management_responsibilities': Array<ManagementResponsibility>;
    /**
     * 
     * @type {boolean}
     * @memberof Director
     */
    'is_most_senior'?: boolean;
    /**
     * 
     * @type {RoleGroupEnum}
     * @memberof Director
     */
    'role_group'?: RoleGroupEnum;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'first_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'middle_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'last_name'?: string | null;
    /**
     * Function title within this organization.
     * @type {string}
     * @memberof Director
     */
    'title'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'full_name': string;
    /**
     * 
     * @type {DirectorGender}
     * @memberof Director
     */
    'gender'?: DirectorGender | null;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'url'?: string | null;
    /**
     * Indicate if the director is no longer active for this company.
     * @type {string}
     * @memberof Director
     */
    'inactive_since'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Director
     */
    'updated_at': string;
}
/**
 * @type DirectorGender
 * @export
 */
export type DirectorGender = BlankEnum | DirectorGenderEnum | NullEnum;

/**
 * * `M` - Male * `F` - Female
 * @export
 * @enum {string}
 */

export const DirectorGenderEnum = {
    M: 'M',
    F: 'F'
} as const;

export type DirectorGenderEnum = typeof DirectorGenderEnum[keyof typeof DirectorGenderEnum];


/**
 * * `BOARD` - Board * `MANAGEMENT` - Management * `OFFICER` - Officer / Signatory * `OTHER` - Other
 * @export
 * @enum {string}
 */

export const DirectorRoleGroupsEnum = {
    Board: 'BOARD',
    Management: 'MANAGEMENT',
    Officer: 'OFFICER',
    Other: 'OTHER'
} as const;

export type DirectorRoleGroupsEnum = typeof DirectorRoleGroupsEnum[keyof typeof DirectorRoleGroupsEnum];


/**
 * Industry Categories Aggregation Serializer.
 * @export
 * @interface DivisionCategoriesAggregation
 */
export interface DivisionCategoriesAggregation {
    /**
     * 
     * @type {DivisionCode}
     * @memberof DivisionCategoriesAggregation
     */
    'division': DivisionCode;
    /**
     * 
     * @type {Array<ArticleCategoryAggregation>}
     * @memberof DivisionCategoriesAggregation
     */
    'categories': Array<ArticleCategoryAggregation>;
}
/**
 * Industry Code Division Group (highest level).
 * @export
 * @interface DivisionCode
 */
export interface DivisionCode {
    /**
     * 
     * @type {string}
     * @memberof DivisionCode
     */
    'code': string;
    /**
     * Get translated description of division.
     * @type {string}
     * @memberof DivisionCode
     */
    'description': string;
}
/**
 * Active Country Serializer.
 * @export
 * @interface DnbActiveCountry
 */
export interface DnbActiveCountry {
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountry
     */
    'country': string;
    /**
     * 
     * @type {number}
     * @memberof DnbActiveCountry
     */
    'count': number;
    /**
     * 
     * @type {DnbActiveCountryDomesticUltimate}
     * @memberof DnbActiveCountry
     */
    'domestic_ultimate'?: DnbActiveCountryDomesticUltimate | null;
}
/**
 * 
 * @export
 * @interface DnbActiveCountryDomesticUltimate
 */
export interface DnbActiveCountryDomesticUltimate {
    /**
     * 
     * @type {DnbLinkageCompanyScoring}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'scoring': DnbLinkageCompanyScoring;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'duns_number': string;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'primary_name': string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_country': string;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_postal_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_street_address'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'external_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'website_icon_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'social_logo'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'global_ultimate'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'domestic_ultimate'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'association_type'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'parent_duns_number'?: string | null;
    /**
     * 
     * @type {Array<DnbLinkageCompany>}
     * @memberof DnbActiveCountryDomesticUltimate
     */
    'children'?: Array<DnbLinkageCompany>;
}
/**
 * Dun And Bradstreet Company.
 * @export
 * @interface DnbCompany
 */
export interface DnbCompany {
    /**
     * 
     * @type {string}
     * @memberof DnbCompany
     */
    'duns_number': string;
    /**
     * 
     * @type {string}
     * @memberof DnbCompany
     */
    'name': string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbCompany
     */
    'country': string;
    /**
     * 
     * @type {string}
     * @memberof DnbCompany
     */
    'external_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbCompany
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbCompany
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {Array<DnbIndustryCode>}
     * @memberof DnbCompany
     */
    'primary_industry_codes'?: Array<DnbIndustryCode>;
    /**
     * 
     * @type {DnbCompanyCorporateLinkage}
     * @memberof DnbCompany
     */
    'corporate_linkage'?: DnbCompanyCorporateLinkage | null;
}
/**
 * 
 * @export
 * @interface DnbCompanyCorporateLinkage
 */
export interface DnbCompanyCorporateLinkage {
    /**
     * 
     * @type {boolean}
     * @memberof DnbCompanyCorporateLinkage
     */
    'is_branch': boolean;
    /**
     * 
     * @type {DnbCorporateLinkageGlobalUltimate}
     * @memberof DnbCompanyCorporateLinkage
     */
    'global_ultimate'?: DnbCorporateLinkageGlobalUltimate | null;
    /**
     * 
     * @type {DnbCorporateLinkageGlobalUltimate}
     * @memberof DnbCompanyCorporateLinkage
     */
    'domestic_ultimate'?: DnbCorporateLinkageGlobalUltimate | null;
    /**
     * 
     * @type {DnbCorporateLinkageGlobalUltimate}
     * @memberof DnbCompanyCorporateLinkage
     */
    'parent'?: DnbCorporateLinkageGlobalUltimate | null;
    /**
     * 
     * @type {Array<DnbFamilyTreeRole>}
     * @memberof DnbCompanyCorporateLinkage
     */
    'family_tree_roles'?: Array<DnbFamilyTreeRole>;
    /**
     * 
     * @type {number}
     * @memberof DnbCompanyCorporateLinkage
     */
    'global_ultimate_family_count'?: number | null;
}
/**
 * 
 * @export
 * @interface DnbCorporateLinkage
 */
export interface DnbCorporateLinkage {
    /**
     * 
     * @type {boolean}
     * @memberof DnbCorporateLinkage
     */
    'is_branch': boolean;
    /**
     * 
     * @type {DnbCorporateLinkageGlobalUltimate}
     * @memberof DnbCorporateLinkage
     */
    'global_ultimate'?: DnbCorporateLinkageGlobalUltimate | null;
    /**
     * 
     * @type {DnbCorporateLinkageGlobalUltimate}
     * @memberof DnbCorporateLinkage
     */
    'domestic_ultimate'?: DnbCorporateLinkageGlobalUltimate | null;
    /**
     * 
     * @type {DnbCorporateLinkageGlobalUltimate}
     * @memberof DnbCorporateLinkage
     */
    'parent'?: DnbCorporateLinkageGlobalUltimate | null;
    /**
     * 
     * @type {Array<DnbFamilyTreeRole>}
     * @memberof DnbCorporateLinkage
     */
    'family_tree_roles'?: Array<DnbFamilyTreeRole>;
    /**
     * 
     * @type {number}
     * @memberof DnbCorporateLinkage
     */
    'global_ultimate_family_count'?: number | null;
}
/**
 * 
 * @export
 * @interface DnbCorporateLinkageGlobalUltimate
 */
export interface DnbCorporateLinkageGlobalUltimate {
    /**
     * 
     * @type {string}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'duns_number': string;
    /**
     * 
     * @type {string}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'name': string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'country': string;
    /**
     * 
     * @type {string}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'external_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {Array<DnbIndustryCode>}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'primary_industry_codes'?: Array<DnbIndustryCode>;
    /**
     * 
     * @type {DnbCompanyCorporateLinkage}
     * @memberof DnbCorporateLinkageGlobalUltimate
     */
    'corporate_linkage'?: DnbCompanyCorporateLinkage | null;
}
/**
 * Dun and Bradstreet Directors.
 * @export
 * @interface DnbDirector
 */
export interface DnbDirector {
    /**
     * 
     * @type {string}
     * @memberof DnbDirector
     */
    'full_name': string;
    /**
     * 
     * @type {string}
     * @memberof DnbDirector
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof DnbDirector
     */
    'last_name': string;
    /**
     * 
     * @type {Array<string>}
     * @memberof DnbDirector
     */
    'job_titles'?: Array<string>;
    /**
     * 
     * @type {Array<string>}
     * @memberof DnbDirector
     */
    'responsibilities'?: Array<string>;
    /**
     * 
     * @type {Array<{ [key: string]: string; }>}
     * @memberof DnbDirector
     */
    'management_responsibilities'?: Array<{ [key: string]: string; }>;
    /**
     * 
     * @type {boolean}
     * @memberof DnbDirector
     */
    'is_most_senior'?: boolean;
    /**
     * 
     * @type {DnbDirectorGender}
     * @memberof DnbDirector
     */
    'gender'?: DnbDirectorGender | null;
}
/**
 * @type DnbDirectorGender
 * @export
 */
export type DnbDirectorGender = DnbDirectorGenderEnum | NullEnum;

/**
 * * `MALE` - MALE * `FEMALE` - FEMALE
 * @export
 * @enum {string}
 */

export const DnbDirectorGenderEnum = {
    Male: 'MALE',
    Female: 'FEMALE'
} as const;

export type DnbDirectorGenderEnum = typeof DnbDirectorGenderEnum[keyof typeof DnbDirectorGenderEnum];


/**
 * 
 * @export
 * @interface DnbFamilyTreeRole
 */
export interface DnbFamilyTreeRole {
    /**
     * 
     * @type {number}
     * @memberof DnbFamilyTreeRole
     */
    'code': number;
    /**
     * 
     * @type {string}
     * @memberof DnbFamilyTreeRole
     */
    'description': string;
}
/**
 * 
 * @export
 * @interface DnbIndustryCode
 */
export interface DnbIndustryCode {
    /**
     * 
     * @type {number}
     * @memberof DnbIndustryCode
     */
    'code': number;
    /**
     * 
     * @type {string}
     * @memberof DnbIndustryCode
     */
    'description': string;
}
/**
 * Dun and Bradstreet linkage organization.
 * @export
 * @interface DnbLinkageCompany
 */
export interface DnbLinkageCompany {
    /**
     * 
     * @type {DnbLinkageCompanyScoring}
     * @memberof DnbLinkageCompany
     */
    'scoring': DnbLinkageCompanyScoring;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'duns_number': string;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'primary_name': string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'address_country': string;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'address_locality'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'address_postal_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'address_street_address'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof DnbLinkageCompany
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof DnbLinkageCompany
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'external_id'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'website_icon_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'social_logo'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof DnbLinkageCompany
     */
    'global_ultimate'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof DnbLinkageCompany
     */
    'domestic_ultimate'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'association_type'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompany
     */
    'parent_duns_number'?: string | null;
    /**
     * 
     * @type {Array<DnbLinkageCompany>}
     * @memberof DnbLinkageCompany
     */
    'children'?: Array<DnbLinkageCompany>;
}
/**
 * 
 * @export
 * @interface DnbLinkageCompanyScoring
 */
export interface DnbLinkageCompanyScoring {
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompanyScoring
     */
    'date': string;
    /**
     * 
     * @type {number}
     * @memberof DnbLinkageCompanyScoring
     */
    'score': number;
    /**
     * 
     * @type {number}
     * @memberof DnbLinkageCompanyScoring
     */
    'trend'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof DnbLinkageCompanyScoring
     */
    'signal_count': number;
    /**
     * 
     * @type {string}
     * @memberof DnbLinkageCompanyScoring
     */
    'created_at': string;
}
/**
 * A resolved effective keyword (default or custom) for a set (read-only).
 * @export
 * @interface EffectiveKeyword
 */
export interface EffectiveKeyword {
    /**
     * 
     * @type {string}
     * @memberof EffectiveKeyword
     */
    'term': string;
    /**
     * 
     * @type {string}
     * @memberof EffectiveKeyword
     */
    'language': string | null;
    /**
     * 
     * @type {string}
     * @memberof EffectiveKeyword
     */
    'entity_type': string | null;
    /**
     * 
     * @type {string}
     * @memberof EffectiveKeyword
     */
    'source': string;
    /**
     * 
     * @type {boolean}
     * @memberof EffectiveKeyword
     */
    'excluded': boolean;
    /**
     * 
     * @type {string}
     * @memberof EffectiveKeyword
     */
    'entry_id': string | null;
}
/**
 * Effective keyword list for one language plus whole-set per-language counts.
 * @export
 * @interface EffectiveKeywordList
 */
export interface EffectiveKeywordList {
    /**
     * 
     * @type {Array<EffectiveKeyword>}
     * @memberof EffectiveKeywordList
     */
    'results': Array<EffectiveKeyword>;
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof EffectiveKeywordList
     */
    'language_counts': { [key: string]: number; };
}
/**
 * An enforcement provider available for exclusion (read-only).
 * @export
 * @interface EnforcementProvider
 */
export interface EnforcementProvider {
    /**
     * 
     * @type {string}
     * @memberof EnforcementProvider
     */
    'key': string;
    /**
     * 
     * @type {string}
     * @memberof EnforcementProvider
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof EnforcementProvider
     */
    'authority': string;
    /**
     * 
     * @type {string}
     * @memberof EnforcementProvider
     */
    'jurisdiction_country': string | null;
    /**
     * 
     * @type {number}
     * @memberof EnforcementProvider
     */
    'active_listing_count': number;
    /**
     * 
     * @type {string}
     * @memberof EnforcementProvider
     */
    'last_ingestion': string | null;
}
/**
 * * `no_results` - No results * `results_found` - Results found * `all_results_marked_as_false_positive` - All results marked as false positive * `risk_confirmed` - Risk confirmed
 * @export
 * @enum {string}
 */

export const EnforcementResultsEnum = {
    NoResults: 'no_results',
    ResultsFound: 'results_found',
    AllResultsMarkedAsFalsePositive: 'all_results_marked_as_false_positive',
    RiskConfirmed: 'risk_confirmed'
} as const;

export type EnforcementResultsEnum = typeof EnforcementResultsEnum[keyof typeof EnforcementResultsEnum];


/**
 * 
 * @export
 * @interface ErrorDescription
 */
export interface ErrorDescription {
    /**
     * 
     * @type {number}
     * @memberof ErrorDescription
     */
    'status': number;
    /**
     * 
     * @type {CodeEnum}
     * @memberof ErrorDescription
     */
    'code': CodeEnum;
    /**
     * 
     * @type {string}
     * @memberof ErrorDescription
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof ErrorDescription
     */
    'description': string;
    /**
     * 
     * @type {any}
     * @memberof ErrorDescription
     */
    'field_errors': any | null;
}
/**
 * * `NEWS` - News * `BINDER` - Binder * `COMPANIES` - Companies * `REGISTRATIONS` - Registrations * `COMPLIANCE` - Compliance * `COMPLIANCE_CHANGELOG` - Compliance changelog * `BILLING` - Billing * `KEY_EVENTS` - Key Events
 * @export
 * @enum {string}
 */

export const ExportTypeEnum = {
    News: 'NEWS',
    Binder: 'BINDER',
    Companies: 'COMPANIES',
    Registrations: 'REGISTRATIONS',
    Compliance: 'COMPLIANCE',
    ComplianceChangelog: 'COMPLIANCE_CHANGELOG',
    Billing: 'BILLING',
    KeyEvents: 'KEY_EVENTS'
} as const;

export type ExportTypeEnum = typeof ExportTypeEnum[keyof typeof ExportTypeEnum];


/**
 * * `risk_confirmed` - Risk confirmed * `no_risk` - No risk * `false_positive` - False positive * `discard` - Discard * `to_investigate` - To investigate
 * @export
 * @enum {string}
 */

export const FalsePositiveRatingEnum = {
    RiskConfirmed: 'risk_confirmed',
    NoRisk: 'no_risk',
    FalsePositive: 'false_positive',
    Discard: 'discard',
    ToInvestigate: 'to_investigate'
} as const;

export type FalsePositiveRatingEnum = typeof FalsePositiveRatingEnum[keyof typeof FalsePositiveRatingEnum];


/**
 * * `NOT_ENOUGH_NEWS` - Not Enough News * `COMPANY_NAME_OUTDATED` - Company Name Outdated * `INCORRECT_COMPANY_WEBSITE` - Incorrect Company Website * `MISSING_REGISTRATION_NUMBER` - Missing Registration Number * `MISSING_TRADE_NAME` - Missing Trade Name * `INCORRECT_TRADE_NAME` - Incorrect Trade Name * `NOT_ENOUGH_REVIEWS` - Not Enough Reviews * `OUTDATED_CORPORATE_LINKAGE` - Outdated Corporate Linkage * `INCORRECT_CORPORATE_LINKAGE` - Incorrect Corporate Linkage * `OTHER` - Other * `COMPANY_EXCLUDED_ALERT` - Company has been excluded from alert by user. * `DIRECTORS_NOT_IMPORTED` - Directors are not being imported from company website * `NO_NEWS_ARTICLES_FOUND` - No News Articles Found and visited by user
 * @export
 * @enum {string}
 */

export const FeedbackType43bEnum = {
    NotEnoughNews: 'NOT_ENOUGH_NEWS',
    CompanyNameOutdated: 'COMPANY_NAME_OUTDATED',
    IncorrectCompanyWebsite: 'INCORRECT_COMPANY_WEBSITE',
    MissingRegistrationNumber: 'MISSING_REGISTRATION_NUMBER',
    MissingTradeName: 'MISSING_TRADE_NAME',
    IncorrectTradeName: 'INCORRECT_TRADE_NAME',
    NotEnoughReviews: 'NOT_ENOUGH_REVIEWS',
    OutdatedCorporateLinkage: 'OUTDATED_CORPORATE_LINKAGE',
    IncorrectCorporateLinkage: 'INCORRECT_CORPORATE_LINKAGE',
    Other: 'OTHER',
    CompanyExcludedAlert: 'COMPANY_EXCLUDED_ALERT',
    DirectorsNotImported: 'DIRECTORS_NOT_IMPORTED',
    NoNewsArticlesFound: 'NO_NEWS_ARTICLES_FOUND'
} as const;

export type FeedbackType43bEnum = typeof FeedbackType43bEnum[keyof typeof FeedbackType43bEnum];


/**
 * Financial update Serializer.
 * @export
 * @interface FinancialUpdateRevenue
 */
export interface FinancialUpdateRevenue {
    /**
     * 
     * @type {number}
     * @memberof FinancialUpdateRevenue
     */
    'value': number;
    /**
     * 
     * @type {string}
     * @memberof FinancialUpdateRevenue
     */
    'currency'?: string | null;
}
/**
 * Serializer for forbidden error.
 * @export
 * @interface ForbiddenError
 */
export interface ForbiddenError {
    /**
     * 
     * @type {string}
     * @memberof ForbiddenError
     */
    'detail': string;
}
/**
 * Global Ultimate Company Serializer.
 * @export
 * @interface GlobalUltimate
 */
export interface GlobalUltimate {
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimate
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimate
     */
    'name': string | null;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimate
     */
    'country': string | null;
}
/**
 * GlobalUltimateCompany Serializer.
 * @export
 * @interface GlobalUltimateCompany
 */
export interface GlobalUltimateCompany {
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimateCompany
     */
    'duns_number': string;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimateCompany
     */
    'external_id': string | null;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimateCompany
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimateCompany
     */
    'country': string | null;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimateCompany
     */
    'social_logo': string | null;
    /**
     * 
     * @type {string}
     * @memberof GlobalUltimateCompany
     */
    'website_icon_url': string | null;
}
/**
 * * `AF` - Afghanistan * `AX` - Aland Islands * `AL` - Albania * `DZ` - Algeria * `AS` - American Samoa * `AD` - Andorra * `AO` - Angola * `AI` - Anguilla * `AQ` - Antarctica * `AG` - Antigua and Barbuda * `AR` - Argentina * `AM` - Armenia * `AW` - Aruba * `AU` - Australia * `AT` - Austria * `AZ` - Azerbaijan * `BS` - Bahamas * `BH` - Bahrain * `BD` - Bangladesh * `BB` - Barbados * `BY` - Belarus * `BE` - Belgium * `BZ` - Belize * `BJ` - Benin * `BM` - Bermuda * `BT` - Bhutan * `BO` - Bolivia * `BQ` - Bonaire * `BA` - Bosnia and Herzegovina * `BW` - Botswana * `BV` - Bouvet Island * `BR` - Brazil * `IO` - British Indian Ocean Territory * `BN` - Brunei Darussalam * `BG` - Bulgaria * `BF` - Burkina Faso * `BI` - Burundi * `CV` - Cabo Verde * `KH` - Cambodia * `CM` - Cameroon * `CA` - Canada * `KY` - Cayman Islands * `CF` - Central African Republic * `TD` - Chad * `CL` - Chile * `CN` - China * `CX` - Christmas Island * `CC` - Cocos Keeling Islands * `CO` - Colombia * `KM` - Comoros * `CG` - Congo * `CD` - Congo Democratic Republic * `CK` - Cook Islands * `CR` - Costa Rica * `CI` - Cote d\'Ivoire * `HR` - Croatia * `CU` - Cuba * `CW` - Curacao * `CY` - Cyprus * `CZ` - Czechia * `DK` - Denmark * `DJ` - Djibouti * `DM` - Dominica * `DO` - Dominican Republic * `EC` - Ecuador * `EG` - Egypt * `SV` - El Salvador * `GQ` - Equatorial Guinea * `ER` - Eritrea * `EE` - Estonia * `SZ` - Eswatini * `ET` - Ethiopia * `FK` - Falkland Islands * `FO` - Faroe Islands * `FJ` - Fiji * `FI` - Finland * `FR` - France * `GF` - French Guiana * `PF` - French Polynesia * `TF` - French Southern Territories * `GA` - Gabon * `GM` - Gambia * `GE` - Georgia * `DE` - Germany * `GH` - Ghana * `GI` - Gibraltar * `GR` - Greece * `GL` - Greenland * `GD` - Grenada * `GP` - Guadeloupe * `GU` - Guam * `GT` - Guatemala * `GG` - Guernsey * `GN` - Guinea * `GW` - Guinea-Bissau * `GY` - Guyana * `HT` - Haiti * `HM` - Heard Island and McDonald Islands * `VA` - Holy See * `HN` - Honduras * `HK` - Hong Kong * `HU` - Hungary * `IS` - Iceland * `IN` - India * `ID` - Indonesia * `IR` - Iran (Islamic Republic of) * `IQ` - Iraq * `IE` - Ireland * `IM` - Isle of Man * `IL` - Israel * `IT` - Italy * `JM` - Jamaica * `JP` - Japan * `JE` - Jersey * `JO` - Jordan * `KZ` - Kazakhstan * `KE` - Kenya * `KI` - Kiribati * `KP` - Korea (the Democratic People\'s Republic of) * `KR` - Korea (the Republic of) * `XK` - Kosovo * `KW` - Kuwait * `KG` - Kyrgyzstan * `LA` - Lao People\'s Democratic Republic * `LV` - Latvia * `LB` - Lebanon * `LS` - Lesotho * `LR` - Liberia * `LY` - Libya * `LI` - Liechtenstein * `LT` - Lithuania * `LU` - Luxembourg * `MO` - Macao * `MG` - Madagascar * `MW` - Malawi * `MY` - Malaysia * `MV` - Maldives * `ML` - Mali * `MT` - Malta * `MH` - Marshall Islands * `MQ` - Martinique * `MR` - Mauritania * `MU` - Mauritius * `YT` - Mayotte * `MX` - Mexico * `FM` - Micronesia * `MD` - Moldova * `MC` - Monaco * `MN` - Mongolia * `ME` - Montenegro * `MS` - Montserrat * `MA` - Morocco * `MZ` - Mozambique * `MM` - Myanmar * `NA` - Namibia * `NR` - Nauru * `NP` - Nepal * `NL` - Netherlands * `NC` - New Caledonia * `NZ` - New Zealand * `NI` - Nicaragua * `NE` - Niger * `NG` - Nigeria * `NU` - Niue * `NF` - Norfolk Island * `MK` - North Macedonia * `MP` - Northern Mariana Islands * `NO` - Norway * `OM` - Oman * `PK` - Pakistan * `PW` - Palau * `PS` - Palestine, State of * `PA` - Panama * `PG` - Papua New Guinea * `PY` - Paraguay * `PE` - Peru * `PH` - Philippines * `PN` - Pitcairn * `PL` - Poland * `PT` - Portugal * `PR` - Puerto Rico * `QA` - Qatar * `RE` - Réunion * `RO` - Romania * `RU` - Russian Federation * `RW` - Rwanda * `BL` - Saint Barthélemy * `SH` - Saint Helena * `KN` - Saint Kitts and Nevis * `LC` - Saint Lucia * `MF` - Saint Martin * `PM` - Saint Pierre and Miquelon * `VC` - Saint Vincent and the Grenadines * `WS` - Samoa * `SM` - San Marino * `ST` - Sao Tome and Principe * `SA` - Saudi Arabia * `SN` - Senegal * `RS` - Serbia * `SC` - Seychelles * `SL` - Sierra Leone * `SG` - Singapore * `SX` - Sint Maarten * `SK` - Slovakia * `SI` - Slovenia * `SB` - Solomon Islands * `SO` - Somalia * `ZA` - South Africa * `GS` - South Georgia and the South Sandwich Islands * `SS` - South Sudan * `ES` - Spain * `LK` - Sri Lanka * `SD` - Sudan * `SR` - Suriname * `SJ` - Svalbard and Jan Mayen * `SE` - Sweden * `CH` - Switzerland * `SY` - Syrian Arab Republic * `TW` - Taiwan * `TJ` - Tajikistan * `TZ` - Tanzania * `TH` - Thailand * `TL` - Timor-Leste * `TG` - Togo * `TK` - Tokelau * `TO` - Tonga * `TT` - Trinidad and Tobago * `TN` - Tunisia * `TR` - Turkey * `TM` - Turkmenistan * `TC` - Turks and Caicos Islands * `TV` - Tuvalu * `UG` - Uganda * `UA` - Ukraine * `AE` - United Arab Emirates * `GB` - United Kingdom * `UM` - United States Minor Outlying Islands * `US` - United States of America * `UY` - Uruguay * `UZ` - Uzbekistan * `VU` - Vanuatu * `VE` - Venezuela * `VN` - Viet Nam * `VG` - Virgin Islands * `VI` - Virgin Islands * `WF` - Wallis and Futuna * `EH` - Western Sahara * `YE` - Yemen * `ZM` - Zambia * `ZW` - Zimbabwe
 * @export
 * @enum {string}
 */

export const GlobalUltimateCountryEnum = {
    Af: 'AF',
    Ax: 'AX',
    Al: 'AL',
    Dz: 'DZ',
    As: 'AS',
    Ad: 'AD',
    Ao: 'AO',
    Ai: 'AI',
    Aq: 'AQ',
    Ag: 'AG',
    Ar: 'AR',
    Am: 'AM',
    Aw: 'AW',
    Au: 'AU',
    At: 'AT',
    Az: 'AZ',
    Bs: 'BS',
    Bh: 'BH',
    Bd: 'BD',
    Bb: 'BB',
    By: 'BY',
    Be: 'BE',
    Bz: 'BZ',
    Bj: 'BJ',
    Bm: 'BM',
    Bt: 'BT',
    Bo: 'BO',
    Bq: 'BQ',
    Ba: 'BA',
    Bw: 'BW',
    Bv: 'BV',
    Br: 'BR',
    Io: 'IO',
    Bn: 'BN',
    Bg: 'BG',
    Bf: 'BF',
    Bi: 'BI',
    Cv: 'CV',
    Kh: 'KH',
    Cm: 'CM',
    Ca: 'CA',
    Ky: 'KY',
    Cf: 'CF',
    Td: 'TD',
    Cl: 'CL',
    Cn: 'CN',
    Cx: 'CX',
    Cc: 'CC',
    Co: 'CO',
    Km: 'KM',
    Cg: 'CG',
    Cd: 'CD',
    Ck: 'CK',
    Cr: 'CR',
    Ci: 'CI',
    Hr: 'HR',
    Cu: 'CU',
    Cw: 'CW',
    Cy: 'CY',
    Cz: 'CZ',
    Dk: 'DK',
    Dj: 'DJ',
    Dm: 'DM',
    Do: 'DO',
    Ec: 'EC',
    Eg: 'EG',
    Sv: 'SV',
    Gq: 'GQ',
    Er: 'ER',
    Ee: 'EE',
    Sz: 'SZ',
    Et: 'ET',
    Fk: 'FK',
    Fo: 'FO',
    Fj: 'FJ',
    Fi: 'FI',
    Fr: 'FR',
    Gf: 'GF',
    Pf: 'PF',
    Tf: 'TF',
    Ga: 'GA',
    Gm: 'GM',
    Ge: 'GE',
    De: 'DE',
    Gh: 'GH',
    Gi: 'GI',
    Gr: 'GR',
    Gl: 'GL',
    Gd: 'GD',
    Gp: 'GP',
    Gu: 'GU',
    Gt: 'GT',
    Gg: 'GG',
    Gn: 'GN',
    Gw: 'GW',
    Gy: 'GY',
    Ht: 'HT',
    Hm: 'HM',
    Va: 'VA',
    Hn: 'HN',
    Hk: 'HK',
    Hu: 'HU',
    Is: 'IS',
    In: 'IN',
    Id: 'ID',
    Ir: 'IR',
    Iq: 'IQ',
    Ie: 'IE',
    Im: 'IM',
    Il: 'IL',
    It: 'IT',
    Jm: 'JM',
    Jp: 'JP',
    Je: 'JE',
    Jo: 'JO',
    Kz: 'KZ',
    Ke: 'KE',
    Ki: 'KI',
    Kp: 'KP',
    Kr: 'KR',
    Xk: 'XK',
    Kw: 'KW',
    Kg: 'KG',
    La: 'LA',
    Lv: 'LV',
    Lb: 'LB',
    Ls: 'LS',
    Lr: 'LR',
    Ly: 'LY',
    Li: 'LI',
    Lt: 'LT',
    Lu: 'LU',
    Mo: 'MO',
    Mg: 'MG',
    Mw: 'MW',
    My: 'MY',
    Mv: 'MV',
    Ml: 'ML',
    Mt: 'MT',
    Mh: 'MH',
    Mq: 'MQ',
    Mr: 'MR',
    Mu: 'MU',
    Yt: 'YT',
    Mx: 'MX',
    Fm: 'FM',
    Md: 'MD',
    Mc: 'MC',
    Mn: 'MN',
    Me: 'ME',
    Ms: 'MS',
    Ma: 'MA',
    Mz: 'MZ',
    Mm: 'MM',
    Na: 'NA',
    Nr: 'NR',
    Np: 'NP',
    Nl: 'NL',
    Nc: 'NC',
    Nz: 'NZ',
    Ni: 'NI',
    Ne: 'NE',
    Ng: 'NG',
    Nu: 'NU',
    Nf: 'NF',
    Mk: 'MK',
    Mp: 'MP',
    No: 'NO',
    Om: 'OM',
    Pk: 'PK',
    Pw: 'PW',
    Ps: 'PS',
    Pa: 'PA',
    Pg: 'PG',
    Py: 'PY',
    Pe: 'PE',
    Ph: 'PH',
    Pn: 'PN',
    Pl: 'PL',
    Pt: 'PT',
    Pr: 'PR',
    Qa: 'QA',
    Re: 'RE',
    Ro: 'RO',
    Ru: 'RU',
    Rw: 'RW',
    Bl: 'BL',
    Sh: 'SH',
    Kn: 'KN',
    Lc: 'LC',
    Mf: 'MF',
    Pm: 'PM',
    Vc: 'VC',
    Ws: 'WS',
    Sm: 'SM',
    St: 'ST',
    Sa: 'SA',
    Sn: 'SN',
    Rs: 'RS',
    Sc: 'SC',
    Sl: 'SL',
    Sg: 'SG',
    Sx: 'SX',
    Sk: 'SK',
    Si: 'SI',
    Sb: 'SB',
    So: 'SO',
    Za: 'ZA',
    Gs: 'GS',
    Ss: 'SS',
    Es: 'ES',
    Lk: 'LK',
    Sd: 'SD',
    Sr: 'SR',
    Sj: 'SJ',
    Se: 'SE',
    Ch: 'CH',
    Sy: 'SY',
    Tw: 'TW',
    Tj: 'TJ',
    Tz: 'TZ',
    Th: 'TH',
    Tl: 'TL',
    Tg: 'TG',
    Tk: 'TK',
    To: 'TO',
    Tt: 'TT',
    Tn: 'TN',
    Tr: 'TR',
    Tm: 'TM',
    Tc: 'TC',
    Tv: 'TV',
    Ug: 'UG',
    Ua: 'UA',
    Ae: 'AE',
    Gb: 'GB',
    Um: 'UM',
    Us: 'US',
    Uy: 'UY',
    Uz: 'UZ',
    Vu: 'VU',
    Ve: 'VE',
    Vn: 'VN',
    Vg: 'VG',
    Vi: 'VI',
    Wf: 'WF',
    Eh: 'EH',
    Ye: 'YE',
    Zm: 'ZM',
    Zw: 'ZW'
} as const;

export type GlobalUltimateCountryEnum = typeof GlobalUltimateCountryEnum[keyof typeof GlobalUltimateCountryEnum];


/**
 * * `day` - day * `week` - week * `month` - month
 * @export
 * @enum {string}
 */

export const GranularityEnum = {
    Day: 'day',
    Week: 'week',
    Month: 'month'
} as const;

export type GranularityEnum = typeof GranularityEnum[keyof typeof GranularityEnum];


/**
 * Serialize Company Article.
 * @export
 * @interface IndustryArticle
 */
export interface IndustryArticle {
    /**
     * 
     * @type {IndustryArticleIndustryCode}
     * @memberof IndustryArticle
     */
    'industry_code': IndustryArticleIndustryCode;
    /**
     * 
     * @type {Array<CategoryTree>}
     * @memberof IndustryArticle
     */
    'categories': Array<CategoryTree>;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof IndustryArticle
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * Get Shortened snippet.
     * @type {string}
     * @memberof IndustryArticle
     */
    'snippet': string;
    /**
     * Get Shortened snippet.
     * @type {string}
     * @memberof IndustryArticle
     */
    'snippet_en': string;
    /**
     * Get Snippet Translated.
     * @type {string}
     * @memberof IndustryArticle
     */
    'snippet_translated': string;
    /**
     * 
     * @type {number}
     * @memberof IndustryArticle
     */
    'sentiment'?: number | null;
}
/**
 * 
 * @export
 * @interface IndustryArticleIndustryCode
 */
export interface IndustryArticleIndustryCode {
    /**
     * 
     * @type {string}
     * @memberof IndustryArticleIndustryCode
     */
    'code': string;
    /**
     * Get translated description of industry code.
     * @type {string}
     * @memberof IndustryArticleIndustryCode
     */
    'description': string;
    /**
     * 
     * @type {string}
     * @memberof IndustryArticleIndustryCode
     */
    'division_group_code'?: string | null;
    /**
     * Highest level of industry code grouping
     * @type {string}
     * @memberof IndustryArticleIndustryCode
     */
    'division_group_name'?: string | null;
}
/**
 * Industry Categories Aggregation Serializer.
 * @export
 * @interface IndustryCategoriesAggregation
 */
export interface IndustryCategoriesAggregation {
    /**
     * 
     * @type {IndustryCode}
     * @memberof IndustryCategoriesAggregation
     */
    'industry_code': IndustryCode;
    /**
     * 
     * @type {Array<ArticleCategoryAggregation>}
     * @memberof IndustryCategoriesAggregation
     */
    'categories': Array<ArticleCategoryAggregation>;
}
/**
 * Custom Serializer for the IndustryCode Model.
 * @export
 * @interface IndustryCode
 */
export interface IndustryCode {
    /**
     * 
     * @type {string}
     * @memberof IndustryCode
     */
    'code': string;
    /**
     * Get translated description of industry code.
     * @type {string}
     * @memberof IndustryCode
     */
    'description': string;
    /**
     * 
     * @type {string}
     * @memberof IndustryCode
     */
    'division_group_code'?: string | null;
    /**
     * Highest level of industry code grouping
     * @type {string}
     * @memberof IndustryCode
     */
    'division_group_name'?: string | null;
}
/**
 * Industry Code Serializer.
 * @export
 * @interface IndustryCodeRequest
 */
export interface IndustryCodeRequest {
    /**
     * 
     * @type {string}
     * @memberof IndustryCodeRequest
     */
    'code': string;
    /**
     * 
     * @type {string}
     * @memberof IndustryCodeRequest
     */
    'description'?: string | null;
}
/**
 * Serializer for Integration model.
 * @export
 * @interface Integration
 */
export interface Integration {
    /**
     * 
     * @type {number}
     * @memberof Integration
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof Integration
     */
    'external_id': string;
    /**
     * Display name for this integration
     * @type {string}
     * @memberof Integration
     */
    'name': string;
    /**
     * The external system type  * `SALESFORCE` - Salesforce * `HUBSPOT` - HubSpot * `SAP` - SAP * `DYNAMICS` - Microsoft Dynamics * `NETSUITE` - NetSuite * `CUSTOM` - Custom System
     * @type {SystemTypeEnum}
     * @memberof Integration
     */
    'system_type': SystemTypeEnum;
    /**
     * Human-readable system type
     * @type {string}
     * @memberof Integration
     */
    'system_type_display': string;
    /**
     * 
     * @type {IntegrationStatusEnum}
     * @memberof Integration
     */
    'status'?: IntegrationStatusEnum;
    /**
     * Human-readable status
     * @type {string}
     * @memberof Integration
     */
    'status_display': string;
    /**
     * Whether automatic sync is enabled
     * @type {boolean}
     * @memberof Integration
     */
    'sync_enabled'?: boolean;
    /**
     * How often to sync in minutes
     * @type {number}
     * @memberof Integration
     */
    'sync_interval_minutes'?: number;
    /**
     * Last successful sync timestamp
     * @type {string}
     * @memberof Integration
     */
    'last_sync_at': string | null;
    /**
     * Last sync error message
     * @type {string}
     * @memberof Integration
     */
    'last_sync_error': string | null;
    /**
     * Default portfolio for imported companies
     * @type {number}
     * @memberof Integration
     */
    'default_portfolio'?: number | null;
    /**
     * Name of the default portfolio for imported companies
     * @type {string}
     * @memberof Integration
     */
    'default_portfolio_name': string;
    /**
     * 
     * @type {Array<IntegrationFieldMapping>}
     * @memberof Integration
     */
    'field_mappings': Array<IntegrationFieldMapping>;
    /**
     * 
     * @type {string}
     * @memberof Integration
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Integration
     */
    'updated_at': string;
    /**
     * System-specific configuration. Required fields vary by system_type: Salesforce (client_id, client_secret, instance_url), HubSpot (access_token OR api_key), SAP (base_url, username, password, company_db if api_type is B1)
     * @type {any}
     * @memberof Integration
     */
    'config'?: any;
}
/**
 * Serializer for company mappings.
 * @export
 * @interface IntegrationCompanyMapping
 */
export interface IntegrationCompanyMapping {
    /**
     * 
     * @type {number}
     * @memberof IntegrationCompanyMapping
     */
    'id': number;
    /**
     * 
     * @type {number}
     * @memberof IntegrationCompanyMapping
     */
    'integration': number;
    /**
     * Name of the integration
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'integration_name': string;
    /**
     * 
     * @type {number}
     * @memberof IntegrationCompanyMapping
     */
    'company': number;
    /**
     * Name of the mapped company
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'company_name': string;
    /**
     * External ID of the mapped company
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'company_external_id': string;
    /**
     * Company ID in the external system
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'external_id': string;
    /**
     * Whether to sync this company
     * @type {boolean}
     * @memberof IntegrationCompanyMapping
     */
    'sync_enabled'?: boolean;
    /**
     * Last time this company was synced
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'last_synced_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof IntegrationCompanyMapping
     */
    'updated_at': string;
}
/**
 * Serializer for creating integrations.
 * @export
 * @interface IntegrationCreate
 */
export interface IntegrationCreate {
    /**
     * Display name for this integration
     * @type {string}
     * @memberof IntegrationCreate
     */
    'name': string;
    /**
     * The external system type  * `SALESFORCE` - Salesforce * `HUBSPOT` - HubSpot * `SAP` - SAP * `DYNAMICS` - Microsoft Dynamics * `NETSUITE` - NetSuite * `CUSTOM` - Custom System
     * @type {SystemTypeEnum}
     * @memberof IntegrationCreate
     */
    'system_type': SystemTypeEnum;
    /**
     * System-specific configuration (API keys, endpoints, etc.)
     * @type {any}
     * @memberof IntegrationCreate
     */
    'config'?: any;
    /**
     * Whether automatic sync is enabled
     * @type {boolean}
     * @memberof IntegrationCreate
     */
    'sync_enabled'?: boolean;
    /**
     * How often to sync in minutes
     * @type {number}
     * @memberof IntegrationCreate
     */
    'sync_interval_minutes'?: number;
    /**
     * Default portfolio for imported companies
     * @type {number}
     * @memberof IntegrationCreate
     */
    'default_portfolio'?: number | null;
}
/**
 * Serializer for creating integrations.
 * @export
 * @interface IntegrationCreateRequest
 */
export interface IntegrationCreateRequest {
    /**
     * Display name for this integration
     * @type {string}
     * @memberof IntegrationCreateRequest
     */
    'name': string;
    /**
     * The external system type  * `SALESFORCE` - Salesforce * `HUBSPOT` - HubSpot * `SAP` - SAP * `DYNAMICS` - Microsoft Dynamics * `NETSUITE` - NetSuite * `CUSTOM` - Custom System
     * @type {SystemTypeEnum}
     * @memberof IntegrationCreateRequest
     */
    'system_type': SystemTypeEnum;
    /**
     * System-specific configuration (API keys, endpoints, etc.)
     * @type {any}
     * @memberof IntegrationCreateRequest
     */
    'config'?: any;
    /**
     * Whether automatic sync is enabled
     * @type {boolean}
     * @memberof IntegrationCreateRequest
     */
    'sync_enabled'?: boolean;
    /**
     * How often to sync in minutes
     * @type {number}
     * @memberof IntegrationCreateRequest
     */
    'sync_interval_minutes'?: number;
    /**
     * Default portfolio for imported companies
     * @type {number}
     * @memberof IntegrationCreateRequest
     */
    'default_portfolio'?: number | null;
}
/**
 * Serializer for field mappings.
 * @export
 * @interface IntegrationFieldMapping
 */
export interface IntegrationFieldMapping {
    /**
     * 
     * @type {number}
     * @memberof IntegrationFieldMapping
     */
    'id': number;
    /**
     * Field name in the external system
     * @type {string}
     * @memberof IntegrationFieldMapping
     */
    'external_field': string;
    /**
     * Field name in our Company model
     * @type {string}
     * @memberof IntegrationFieldMapping
     */
    'internal_field': string;
    /**
     * Optional transformation to apply
     * @type {string}
     * @memberof IntegrationFieldMapping
     */
    'transformation'?: string | null;
    /**
     * Whether this field is required for sync
     * @type {boolean}
     * @memberof IntegrationFieldMapping
     */
    'is_required'?: boolean;
    /**
     * Whether this field is used to identify companies
     * @type {boolean}
     * @memberof IntegrationFieldMapping
     */
    'is_identifier'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof IntegrationFieldMapping
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof IntegrationFieldMapping
     */
    'updated_at': string;
}
/**
 * Serializer for field mappings.
 * @export
 * @interface IntegrationFieldMappingRequest
 */
export interface IntegrationFieldMappingRequest {
    /**
     * Field name in the external system
     * @type {string}
     * @memberof IntegrationFieldMappingRequest
     */
    'external_field': string;
    /**
     * Field name in our Company model
     * @type {string}
     * @memberof IntegrationFieldMappingRequest
     */
    'internal_field': string;
    /**
     * Optional transformation to apply
     * @type {string}
     * @memberof IntegrationFieldMappingRequest
     */
    'transformation'?: string | null;
    /**
     * Whether this field is required for sync
     * @type {boolean}
     * @memberof IntegrationFieldMappingRequest
     */
    'is_required'?: boolean;
    /**
     * Whether this field is used to identify companies
     * @type {boolean}
     * @memberof IntegrationFieldMappingRequest
     */
    'is_identifier'?: boolean;
}
/**
 * Serializer for Integration model.
 * @export
 * @interface IntegrationRequest
 */
export interface IntegrationRequest {
    /**
     * Display name for this integration
     * @type {string}
     * @memberof IntegrationRequest
     */
    'name': string;
    /**
     * The external system type  * `SALESFORCE` - Salesforce * `HUBSPOT` - HubSpot * `SAP` - SAP * `DYNAMICS` - Microsoft Dynamics * `NETSUITE` - NetSuite * `CUSTOM` - Custom System
     * @type {SystemTypeEnum}
     * @memberof IntegrationRequest
     */
    'system_type': SystemTypeEnum;
    /**
     * 
     * @type {IntegrationStatusEnum}
     * @memberof IntegrationRequest
     */
    'status'?: IntegrationStatusEnum;
    /**
     * Whether automatic sync is enabled
     * @type {boolean}
     * @memberof IntegrationRequest
     */
    'sync_enabled'?: boolean;
    /**
     * How often to sync in minutes
     * @type {number}
     * @memberof IntegrationRequest
     */
    'sync_interval_minutes'?: number;
    /**
     * Default portfolio for imported companies
     * @type {number}
     * @memberof IntegrationRequest
     */
    'default_portfolio'?: number | null;
    /**
     * System-specific configuration. Required fields vary by system_type: Salesforce (client_id, client_secret, instance_url), HubSpot (access_token OR api_key), SAP (base_url, username, password, company_db if api_type is B1)
     * @type {any}
     * @memberof IntegrationRequest
     */
    'config'?: any;
}
/**
 * * `ACTIVE` - Active * `INACTIVE` - Inactive * `ERROR` - Error * `CONFIGURING` - Configuring
 * @export
 * @enum {string}
 */

export const IntegrationStatusEnum = {
    Active: 'ACTIVE',
    Inactive: 'INACTIVE',
    Error: 'ERROR',
    Configuring: 'CONFIGURING'
} as const;

export type IntegrationStatusEnum = typeof IntegrationStatusEnum[keyof typeof IntegrationStatusEnum];


/**
 * Serializer for sync records.
 * @export
 * @interface IntegrationSync
 */
export interface IntegrationSync {
    /**
     * 
     * @type {number}
     * @memberof IntegrationSync
     */
    'id': number;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'external_id': string;
    /**
     * 
     * @type {number}
     * @memberof IntegrationSync
     */
    'integration': number;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'integration_name': string;
    /**
     * 
     * @type {SyncTypeEnum}
     * @memberof IntegrationSync
     */
    'sync_type': SyncTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'sync_type_display': string;
    /**
     * 
     * @type {IntegrationSyncStatusEnum}
     * @memberof IntegrationSync
     */
    'status': IntegrationSyncStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'status_display': string;
    /**
     * Number of companies fetched from external system
     * @type {number}
     * @memberof IntegrationSync
     */
    'companies_fetched': number;
    /**
     * Number of new companies created
     * @type {number}
     * @memberof IntegrationSync
     */
    'companies_created': number;
    /**
     * Number of existing companies updated
     * @type {number}
     * @memberof IntegrationSync
     */
    'companies_updated': number;
    /**
     * Number of companies that failed to sync
     * @type {number}
     * @memberof IntegrationSync
     */
    'companies_failed': number;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'started_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'completed_at': string | null;
    /**
     * Get duration in seconds.
     * @type {number}
     * @memberof IntegrationSync
     */
    'duration': number;
    /**
     * 
     * @type {number}
     * @memberof IntegrationSync
     */
    'triggered_by': number | null;
    /**
     * 
     * @type {string}
     * @memberof IntegrationSync
     */
    'created_at': string;
}
/**
 * * `PENDING` - Pending * `IN_PROGRESS` - In Progress * `COMPLETED` - Completed * `FAILED` - Failed * `PARTIAL` - Partial Success
 * @export
 * @enum {string}
 */

export const IntegrationSyncStatusEnum = {
    Pending: 'PENDING',
    InProgress: 'IN_PROGRESS',
    Completed: 'COMPLETED',
    Failed: 'FAILED',
    Partial: 'PARTIAL'
} as const;

export type IntegrationSyncStatusEnum = typeof IntegrationSyncStatusEnum[keyof typeof IntegrationSyncStatusEnum];


/**
 * 
 * @export
 * @interface IntegrationsIntegrationsSyncCreate200Response
 */
export interface IntegrationsIntegrationsSyncCreate200Response {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsIntegrationsSyncCreate200Response
     */
    'task_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof IntegrationsIntegrationsSyncCreate200Response
     */
    'message'?: string;
}
/**
 * Serializer for handling server errors.
 * @export
 * @interface InternalServerError
 */
export interface InternalServerError {
    /**
     * 
     * @type {string}
     * @memberof InternalServerError
     */
    'detail': string;
}
/**
 * * `DAILY` - Daily * `WEEKLY` - Weekly * `MONTHLY` - Monthly
 * @export
 * @enum {string}
 */

export const IntervalEnum = {
    Daily: 'DAILY',
    Weekly: 'WEEKLY',
    Monthly: 'MONTHLY'
} as const;

export type IntervalEnum = typeof IntervalEnum[keyof typeof IntervalEnum];


/**
 * * `MISSING` - Missing * `INCORRECT` - Incorrect
 * @export
 * @enum {string}
 */

export const InvestigationTypeEnum = {
    Missing: 'MISSING',
    Incorrect: 'INCORRECT'
} as const;

export type InvestigationTypeEnum = typeof InvestigationTypeEnum[keyof typeof InvestigationTypeEnum];


/**
 * List invitations.
 * @export
 * @interface Invitation
 */
export interface Invitation {
    /**
     * 
     * @type {InvitationInvitedBy}
     * @memberof Invitation
     */
    'invited_by': InvitationInvitedBy | null;
    /**
     * Check whether the invitation is no longer valid.
     * @type {boolean}
     * @memberof Invitation
     */
    'is_expired': boolean;
    /**
     * 
     * @type {string}
     * @memberof Invitation
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Invitation
     */
    'first_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Invitation
     */
    'last_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Invitation
     */
    'email': string;
    /**
     * 
     * @type {InvitationLanguage}
     * @memberof Invitation
     */
    'language'?: InvitationLanguage | null;
    /**
     * Flag indicating if the invited user becomes a profile admin.
     * @type {boolean}
     * @memberof Invitation
     */
    'is_admin'?: boolean;
    /**
     * Date indicating when the invitation expires.
     * @type {string}
     * @memberof Invitation
     */
    'expired_at': string;
    /**
     * 
     * @type {string}
     * @memberof Invitation
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Invitation
     */
    'updated_at': string;
}
/**
 * 
 * @export
 * @interface InvitationInvitedBy
 */
export interface InvitationInvitedBy {
    /**
     * 
     * @type {string}
     * @memberof InvitationInvitedBy
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof InvitationInvitedBy
     */
    'last_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof InvitationInvitedBy
     */
    'email'?: string;
}
/**
 * User that created an invitation.
 * @export
 * @interface InvitationInvitedByRequest
 */
export interface InvitationInvitedByRequest {
    /**
     * 
     * @type {string}
     * @memberof InvitationInvitedByRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof InvitationInvitedByRequest
     */
    'last_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof InvitationInvitedByRequest
     */
    'email'?: string;
}
/**
 * @type InvitationLanguage
 * Default language for this invitation.  * `af` - Afrikaans * `ar` - Arabic * `az` - Azerbaijani * `bg` - Bulgarian * `be` - Belarusian * `bn` - Bengali * `br` - Breton * `bs` - Bosnian * `ca` - Catalan * `cs` - Czech * `cy` - Welsh * `da` - Danish * `de` - German * `el` - Greek * `en` - English * `eo` - Esperanto * `es` - Spanish * `et` - Estonian * `eu` - Basque * `fa` - Persian * `fi` - Finnish * `fr` - French * `fy` - Frisian * `ga` - Irish * `gd` - Scottish Gaelic * `gl` - Galician * `he` - Hebrew * `hi` - Hindi * `hr` - Croatian * `hu` - Hungarian * `hy` - Armenian * `ia` - Interlingua * `id` - Indonesian * `ig` - Igbo * `io` - Ido * `is` - Icelandic * `it` - Italian * `ja` - Japanese * `ka` - Georgian * `kk` - Kazakh * `km` - Khmer * `kn` - Kannada * `ko` - Korean * `ky` - Kyrgyz * `lb` - Luxembourgish * `lt` - Lithuanian * `lv` - Latvian * `mk` - Macedonian * `ml` - Malayalam * `mn` - Mongolian * `mr` - Marathi * `my` - Burmese * `ne` - Nepali * `nl` - Dutch * `no` - Norwegian * `os` - Ossetic * `pa` - Punjabi * `pl` - Polish * `pt` - Portuguese * `ro` - Romanian * `ru` - Russian * `sk` - Slovak * `sl` - Slovenian * `sq` - Albanian * `sr` - Serbian * `sv` - Swedish * `sw` - Swahili * `ta` - Tamil * `te` - Telugu * `tg` - Tajik * `th` - Thai * `tk` - Turkmen * `tr` - Turkish * `tt` - Tatar * `uk` - Ukrainian * `ur` - Urdu * `uz` - Uzbek * `vi` - Vietnamese * `zh` - Chinese
 * @export
 */
export type InvitationLanguage = BlankEnum | Language856Enum | NullEnum;

/**
 * List invitations.
 * @export
 * @interface InvitationRequest
 */
export interface InvitationRequest {
    /**
     * 
     * @type {string}
     * @memberof InvitationRequest
     */
    'first_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof InvitationRequest
     */
    'last_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof InvitationRequest
     */
    'email': string;
    /**
     * 
     * @type {InvitationLanguage}
     * @memberof InvitationRequest
     */
    'language'?: InvitationLanguage | null;
    /**
     * Flag indicating if the invited user becomes a profile admin.
     * @type {boolean}
     * @memberof InvitationRequest
     */
    'is_admin'?: boolean;
}
/**
 * * `full` - Full * `partial` - Partial
 * @export
 * @enum {string}
 */

export const IsPaywalledEnum = {
    Full: 'full',
    Partial: 'partial'
} as const;

export type IsPaywalledEnum = typeof IsPaywalledEnum[keyof typeof IsPaywalledEnum];


/**
 * Serializer for KeyEvent model.
 * @export
 * @interface KeyEvent
 */
export interface KeyEvent {
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'article_url': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'article_external_id': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'event_name': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'article_snippet': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'article_title': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'company_id': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEvent
     */
    'publication_date': string;
}
/**
 * Serializer for KeyEventExtraction Model.
 * @export
 * @interface KeyEventExtraction
 */
export interface KeyEventExtraction {
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtraction
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtraction
     */
    'company_id': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtraction
     */
    'start_date': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtraction
     */
    'end_date': string;
    /**
     * 
     * @type {KeyEventExtractionStatusEnum}
     * @memberof KeyEventExtraction
     */
    'status': KeyEventExtractionStatusEnum;
    /**
     * 
     * @type {number}
     * @memberof KeyEventExtraction
     */
    'article_count': number | null;
    /**
     * 
     * @type {number}
     * @memberof KeyEventExtraction
     */
    'processed_articles_count': number;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtraction
     */
    'finished_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtraction
     */
    'failed_at': string | null;
}
/**
 * Serializer for KeyEventExtraction Model.
 * @export
 * @interface KeyEventExtractionRequest
 */
export interface KeyEventExtractionRequest {
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtractionRequest
     */
    'company_id': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtractionRequest
     */
    'start_date': string;
    /**
     * 
     * @type {string}
     * @memberof KeyEventExtractionRequest
     */
    'end_date': string;
}
/**
 * * `PENDING` - Pending * `IN_PROGRESS` - In Progress * `COMPLETED` - Completed * `FAILED` - Failed
 * @export
 * @enum {string}
 */

export const KeyEventExtractionStatusEnum = {
    Pending: 'PENDING',
    InProgress: 'IN_PROGRESS',
    Completed: 'COMPLETED',
    Failed: 'FAILED'
} as const;

export type KeyEventExtractionStatusEnum = typeof KeyEventExtractionStatusEnum[keyof typeof KeyEventExtractionStatusEnum];


/**
 * A raw adverse-media keyword-set entry (read-only).
 * @export
 * @interface KeywordSetEntry
 */
export interface KeywordSetEntry {
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntry
     */
    'id': string;
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntry
     */
    'term': string;
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntry
     */
    'language': string | null;
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntry
     */
    'entity_type': string | null;
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntry
     */
    'action': string;
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntry
     */
    'created_at': string | null;
}
/**
 * * `INDIVIDUAL` - INDIVIDUAL * `ORGANIZATION` - ORGANIZATION
 * @export
 * @enum {string}
 */

export const KeywordSetEntryCreateEntityTypeEnum = {
    Individual: 'INDIVIDUAL',
    Organization: 'ORGANIZATION'
} as const;

export type KeywordSetEntryCreateEntityTypeEnum = typeof KeywordSetEntryCreateEntityTypeEnum[keyof typeof KeywordSetEntryCreateEntityTypeEnum];


/**
 * * `af` - af * `ar` - ar * `az` - az * `bg` - bg * `be` - be * `bn` - bn * `br` - br * `bs` - bs * `ca` - ca * `cs` - cs * `cy` - cy * `da` - da * `de` - de * `el` - el * `en` - en * `eo` - eo * `es` - es * `et` - et * `eu` - eu * `fa` - fa * `fi` - fi * `fr` - fr * `fy` - fy * `ga` - ga * `gd` - gd * `gl` - gl * `he` - he * `hi` - hi * `hr` - hr * `hu` - hu * `hy` - hy * `ia` - ia * `id` - id * `ig` - ig * `io` - io * `is` - is * `it` - it * `ja` - ja * `ka` - ka * `kk` - kk * `km` - km * `no` - no * `kn` - kn * `ko` - ko * `ky` - ky * `lb` - lb * `lt` - lt * `lv` - lv * `mk` - mk * `ml` - ml * `mn` - mn * `mr` - mr * `my` - my * `ne` - ne * `nl` - nl * `os` - os * `pa` - pa * `pl` - pl * `pt` - pt * `ro` - ro * `ru` - ru * `sk` - sk * `sl` - sl * `sq` - sq * `sr` - sr * `sv` - sv * `sw` - sw * `ta` - ta * `te` - te * `tg` - tg * `th` - th * `tk` - tk * `tr` - tr * `tt` - tt * `uk` - uk * `ur` - ur * `uz` - uz * `vi` - vi * `zh` - zh
 * @export
 * @enum {string}
 */

export const KeywordSetEntryCreateLanguageEnum = {
    Af: 'af',
    Ar: 'ar',
    Az: 'az',
    Bg: 'bg',
    Be: 'be',
    Bn: 'bn',
    Br: 'br',
    Bs: 'bs',
    Ca: 'ca',
    Cs: 'cs',
    Cy: 'cy',
    Da: 'da',
    De: 'de',
    El: 'el',
    En: 'en',
    Eo: 'eo',
    Es: 'es',
    Et: 'et',
    Eu: 'eu',
    Fa: 'fa',
    Fi: 'fi',
    Fr: 'fr',
    Fy: 'fy',
    Ga: 'ga',
    Gd: 'gd',
    Gl: 'gl',
    He: 'he',
    Hi: 'hi',
    Hr: 'hr',
    Hu: 'hu',
    Hy: 'hy',
    Ia: 'ia',
    Id: 'id',
    Ig: 'ig',
    Io: 'io',
    Is: 'is',
    It: 'it',
    Ja: 'ja',
    Ka: 'ka',
    Kk: 'kk',
    Km: 'km',
    No: 'no',
    Kn: 'kn',
    Ko: 'ko',
    Ky: 'ky',
    Lb: 'lb',
    Lt: 'lt',
    Lv: 'lv',
    Mk: 'mk',
    Ml: 'ml',
    Mn: 'mn',
    Mr: 'mr',
    My: 'my',
    Ne: 'ne',
    Nl: 'nl',
    Os: 'os',
    Pa: 'pa',
    Pl: 'pl',
    Pt: 'pt',
    Ro: 'ro',
    Ru: 'ru',
    Sk: 'sk',
    Sl: 'sl',
    Sq: 'sq',
    Sr: 'sr',
    Sv: 'sv',
    Sw: 'sw',
    Ta: 'ta',
    Te: 'te',
    Tg: 'tg',
    Th: 'th',
    Tk: 'tk',
    Tr: 'tr',
    Tt: 'tt',
    Uk: 'uk',
    Ur: 'ur',
    Uz: 'uz',
    Vi: 'vi',
    Zh: 'zh'
} as const;

export type KeywordSetEntryCreateLanguageEnum = typeof KeywordSetEntryCreateLanguageEnum[keyof typeof KeywordSetEntryCreateLanguageEnum];


/**
 * Add an add/exclude entry to the active profile\'s keyword set.
 * @export
 * @interface KeywordSetEntryCreateRequest
 */
export interface KeywordSetEntryCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof KeywordSetEntryCreateRequest
     */
    'term': string;
    /**
     * 
     * @type {KeywordSetEntryCreateLanguageEnum}
     * @memberof KeywordSetEntryCreateRequest
     */
    'language': KeywordSetEntryCreateLanguageEnum;
    /**
     * 
     * @type {KeywordSetEntryCreateRequestEntityType}
     * @memberof KeywordSetEntryCreateRequest
     */
    'entity_type'?: KeywordSetEntryCreateRequestEntityType | null;
    /**
     * 
     * @type {ActionEnum}
     * @memberof KeywordSetEntryCreateRequest
     */
    'action'?: ActionEnum;
}
/**
 * @type KeywordSetEntryCreateRequestEntityType
 * @export
 */
export type KeywordSetEntryCreateRequestEntityType = KeywordSetEntryCreateEntityTypeEnum | NullEnum;

/**
 * * `af` - Afrikaans * `ar` - Arabic * `az` - Azerbaijani * `bg` - Bulgarian * `be` - Belarusian * `bn` - Bengali * `br` - Breton * `bs` - Bosnian * `ca` - Catalan * `cs` - Czech * `cy` - Welsh * `da` - Danish * `de` - German * `el` - Greek * `en` - English * `eo` - Esperanto * `es` - Spanish * `et` - Estonian * `eu` - Basque * `fa` - Persian * `fi` - Finnish * `fr` - French * `fy` - Frisian * `ga` - Irish * `gd` - Scottish Gaelic * `gl` - Galician * `he` - Hebrew * `hi` - Hindi * `hr` - Croatian * `hu` - Hungarian * `hy` - Armenian * `ia` - Interlingua * `id` - Indonesian * `ig` - Igbo * `io` - Ido * `is` - Icelandic * `it` - Italian * `ja` - Japanese * `ka` - Georgian * `kk` - Kazakh * `km` - Khmer * `kn` - Kannada * `ko` - Korean * `ky` - Kyrgyz * `lb` - Luxembourgish * `lt` - Lithuanian * `lv` - Latvian * `mk` - Macedonian * `ml` - Malayalam * `mn` - Mongolian * `mr` - Marathi * `my` - Burmese * `ne` - Nepali * `nl` - Dutch * `no` - Norwegian * `os` - Ossetic * `pa` - Punjabi * `pl` - Polish * `pt` - Portuguese * `ro` - Romanian * `ru` - Russian * `sk` - Slovak * `sl` - Slovenian * `sq` - Albanian * `sr` - Serbian * `sv` - Swedish * `sw` - Swahili * `ta` - Tamil * `te` - Telugu * `tg` - Tajik * `th` - Thai * `tk` - Turkmen * `tr` - Turkish * `tt` - Tatar * `uk` - Ukrainian * `ur` - Urdu * `uz` - Uzbek * `vi` - Vietnamese * `zh` - Chinese
 * @export
 * @enum {string}
 */

export const Language856Enum = {
    Af: 'af',
    Ar: 'ar',
    Az: 'az',
    Bg: 'bg',
    Be: 'be',
    Bn: 'bn',
    Br: 'br',
    Bs: 'bs',
    Ca: 'ca',
    Cs: 'cs',
    Cy: 'cy',
    Da: 'da',
    De: 'de',
    El: 'el',
    En: 'en',
    Eo: 'eo',
    Es: 'es',
    Et: 'et',
    Eu: 'eu',
    Fa: 'fa',
    Fi: 'fi',
    Fr: 'fr',
    Fy: 'fy',
    Ga: 'ga',
    Gd: 'gd',
    Gl: 'gl',
    He: 'he',
    Hi: 'hi',
    Hr: 'hr',
    Hu: 'hu',
    Hy: 'hy',
    Ia: 'ia',
    Id: 'id',
    Ig: 'ig',
    Io: 'io',
    Is: 'is',
    It: 'it',
    Ja: 'ja',
    Ka: 'ka',
    Kk: 'kk',
    Km: 'km',
    Kn: 'kn',
    Ko: 'ko',
    Ky: 'ky',
    Lb: 'lb',
    Lt: 'lt',
    Lv: 'lv',
    Mk: 'mk',
    Ml: 'ml',
    Mn: 'mn',
    Mr: 'mr',
    My: 'my',
    Ne: 'ne',
    Nl: 'nl',
    No: 'no',
    Os: 'os',
    Pa: 'pa',
    Pl: 'pl',
    Pt: 'pt',
    Ro: 'ro',
    Ru: 'ru',
    Sk: 'sk',
    Sl: 'sl',
    Sq: 'sq',
    Sr: 'sr',
    Sv: 'sv',
    Sw: 'sw',
    Ta: 'ta',
    Te: 'te',
    Tg: 'tg',
    Th: 'th',
    Tk: 'tk',
    Tr: 'tr',
    Tt: 'tt',
    Uk: 'uk',
    Ur: 'ur',
    Uz: 'uz',
    Vi: 'vi',
    Zh: 'zh'
} as const;

export type Language856Enum = typeof Language856Enum[keyof typeof Language856Enum];


/**
 * 
 * @export
 * @interface ManagementResponsibility
 */
export interface ManagementResponsibility {
    /**
     * 
     * @type {string}
     * @memberof ManagementResponsibility
     */
    'mrcCode': string;
    /**
     * 
     * @type {string}
     * @memberof ManagementResponsibility
     */
    'description': string;
}
/**
 * * `GAZETTE` - GAZETTE * `MAINSTREAM` - MAINSTREAM
 * @export
 * @enum {string}
 */

export const MediaTypeEnum = {
    Gazette: 'GAZETTE',
    Mainstream: 'MAINSTREAM'
} as const;

export type MediaTypeEnum = typeof MediaTypeEnum[keyof typeof MediaTypeEnum];


/**
 * * `AF` - Afghanistan * `AX` - Aland Islands * `AL` - Albania * `DZ` - Algeria * `AS` - American Samoa * `AD` - Andorra * `AO` - Angola * `AI` - Anguilla * `AQ` - Antarctica * `AG` - Antigua and Barbuda * `AR` - Argentina * `AM` - Armenia * `AW` - Aruba * `AU` - Australia * `AT` - Austria * `AZ` - Azerbaijan * `BS` - Bahamas * `BH` - Bahrain * `BD` - Bangladesh * `BB` - Barbados * `BY` - Belarus * `BE` - Belgium * `BZ` - Belize * `BJ` - Benin * `BM` - Bermuda * `BT` - Bhutan * `BO` - Bolivia * `BQ` - Bonaire * `BA` - Bosnia and Herzegovina * `BW` - Botswana * `BV` - Bouvet Island * `BR` - Brazil * `IO` - British Indian Ocean Territory * `BN` - Brunei Darussalam * `BG` - Bulgaria * `BF` - Burkina Faso * `BI` - Burundi * `CV` - Cabo Verde * `KH` - Cambodia * `CM` - Cameroon * `CA` - Canada * `KY` - Cayman Islands * `CF` - Central African Republic * `TD` - Chad * `CL` - Chile * `CN` - China * `CX` - Christmas Island * `CC` - Cocos Keeling Islands * `CO` - Colombia * `KM` - Comoros * `CG` - Congo * `CD` - Congo Democratic Republic * `CK` - Cook Islands * `CR` - Costa Rica * `CI` - Cote d\'Ivoire * `HR` - Croatia * `CU` - Cuba * `CW` - Curacao * `CY` - Cyprus * `CZ` - Czechia * `DK` - Denmark * `DJ` - Djibouti * `DM` - Dominica * `DO` - Dominican Republic * `EC` - Ecuador * `EG` - Egypt * `SV` - El Salvador * `GQ` - Equatorial Guinea * `ER` - Eritrea * `EE` - Estonia * `SZ` - Eswatini * `ET` - Ethiopia * `FK` - Falkland Islands * `FO` - Faroe Islands * `FJ` - Fiji * `FI` - Finland * `FR` - France * `GF` - French Guiana * `PF` - French Polynesia * `TF` - French Southern Territories * `GA` - Gabon * `GM` - Gambia * `GE` - Georgia * `DE` - Germany * `GH` - Ghana * `GI` - Gibraltar * `GR` - Greece * `GL` - Greenland * `GD` - Grenada * `GP` - Guadeloupe * `GU` - Guam * `GT` - Guatemala * `GG` - Guernsey * `GN` - Guinea * `GW` - Guinea-Bissau * `GY` - Guyana * `HT` - Haiti * `HM` - Heard Island and McDonald Islands * `VA` - Holy See * `HN` - Honduras * `HK` - Hong Kong * `HU` - Hungary * `IS` - Iceland * `IN` - India * `ID` - Indonesia * `IR` - Iran (Islamic Republic of) * `IQ` - Iraq * `IE` - Ireland * `IM` - Isle of Man * `IL` - Israel * `IT` - Italy * `JM` - Jamaica * `JP` - Japan * `JE` - Jersey * `JO` - Jordan * `KZ` - Kazakhstan * `KE` - Kenya * `KI` - Kiribati * `KP` - Korea (the Democratic People\'s Republic of) * `KR` - Korea (the Republic of) * `XK` - Kosovo * `KW` - Kuwait * `KG` - Kyrgyzstan * `LA` - Lao People\'s Democratic Republic * `LV` - Latvia * `LB` - Lebanon * `LS` - Lesotho * `LR` - Liberia * `LY` - Libya * `LI` - Liechtenstein * `LT` - Lithuania * `LU` - Luxembourg * `MO` - Macao * `MG` - Madagascar * `MW` - Malawi * `MY` - Malaysia * `MV` - Maldives * `ML` - Mali * `MT` - Malta * `MH` - Marshall Islands * `MQ` - Martinique * `MR` - Mauritania * `MU` - Mauritius * `YT` - Mayotte * `MX` - Mexico * `FM` - Micronesia * `MD` - Moldova * `MC` - Monaco * `MN` - Mongolia * `ME` - Montenegro * `MS` - Montserrat * `MA` - Morocco * `MZ` - Mozambique * `MM` - Myanmar * `NA` - Namibia * `NR` - Nauru * `NP` - Nepal * `NL` - Netherlands * `NC` - New Caledonia * `NZ` - New Zealand * `NI` - Nicaragua * `NE` - Niger * `NG` - Nigeria * `NU` - Niue * `NF` - Norfolk Island * `MK` - North Macedonia * `MP` - Northern Mariana Islands * `NO` - Norway * `OM` - Oman * `PK` - Pakistan * `PW` - Palau * `PS` - Palestine, State of * `PA` - Panama * `PG` - Papua New Guinea * `PY` - Paraguay * `PE` - Peru * `PH` - Philippines * `PN` - Pitcairn * `PL` - Poland * `PT` - Portugal * `PR` - Puerto Rico * `QA` - Qatar * `RE` - Réunion * `RO` - Romania * `RU` - Russian Federation * `RW` - Rwanda * `BL` - Saint Barthélemy * `SH` - Saint Helena * `KN` - Saint Kitts and Nevis * `LC` - Saint Lucia * `MF` - Saint Martin * `PM` - Saint Pierre and Miquelon * `VC` - Saint Vincent and the Grenadines * `WS` - Samoa * `SM` - San Marino * `ST` - Sao Tome and Principe * `SA` - Saudi Arabia * `SN` - Senegal * `RS` - Serbia * `SC` - Seychelles * `SL` - Sierra Leone * `SG` - Singapore * `SX` - Sint Maarten * `SK` - Slovakia * `SI` - Slovenia * `SB` - Solomon Islands * `SO` - Somalia * `ZA` - South Africa * `GS` - South Georgia and the South Sandwich Islands * `SS` - South Sudan * `ES` - Spain * `LK` - Sri Lanka * `SD` - Sudan * `SR` - Suriname * `SJ` - Svalbard and Jan Mayen * `SE` - Sweden * `CH` - Switzerland * `SY` - Syrian Arab Republic * `TW` - Taiwan * `TJ` - Tajikistan * `TZ` - Tanzania * `TH` - Thailand * `TL` - Timor-Leste * `TG` - Togo * `TK` - Tokelau * `TO` - Tonga * `TT` - Trinidad and Tobago * `TN` - Tunisia * `TR` - Turkey * `TM` - Turkmenistan * `TC` - Turks and Caicos Islands * `TV` - Tuvalu * `UG` - Uganda * `UA` - Ukraine * `AE` - United Arab Emirates * `GB` - United Kingdom * `UM` - United States Minor Outlying Islands * `US` - United States of America * `UY` - Uruguay * `UZ` - Uzbekistan * `VU` - Vanuatu * `VE` - Venezuela * `VN` - Viet Nam * `VG` - Virgin Islands * `VI` - Virgin Islands * `WF` - Wallis and Futuna * `EH` - Western Sahara * `YE` - Yemen * `ZM` - Zambia * `ZW` - Zimbabwe
 * @export
 * @enum {string}
 */

export const NationalityEnum = {
    Af: 'AF',
    Ax: 'AX',
    Al: 'AL',
    Dz: 'DZ',
    As: 'AS',
    Ad: 'AD',
    Ao: 'AO',
    Ai: 'AI',
    Aq: 'AQ',
    Ag: 'AG',
    Ar: 'AR',
    Am: 'AM',
    Aw: 'AW',
    Au: 'AU',
    At: 'AT',
    Az: 'AZ',
    Bs: 'BS',
    Bh: 'BH',
    Bd: 'BD',
    Bb: 'BB',
    By: 'BY',
    Be: 'BE',
    Bz: 'BZ',
    Bj: 'BJ',
    Bm: 'BM',
    Bt: 'BT',
    Bo: 'BO',
    Bq: 'BQ',
    Ba: 'BA',
    Bw: 'BW',
    Bv: 'BV',
    Br: 'BR',
    Io: 'IO',
    Bn: 'BN',
    Bg: 'BG',
    Bf: 'BF',
    Bi: 'BI',
    Cv: 'CV',
    Kh: 'KH',
    Cm: 'CM',
    Ca: 'CA',
    Ky: 'KY',
    Cf: 'CF',
    Td: 'TD',
    Cl: 'CL',
    Cn: 'CN',
    Cx: 'CX',
    Cc: 'CC',
    Co: 'CO',
    Km: 'KM',
    Cg: 'CG',
    Cd: 'CD',
    Ck: 'CK',
    Cr: 'CR',
    Ci: 'CI',
    Hr: 'HR',
    Cu: 'CU',
    Cw: 'CW',
    Cy: 'CY',
    Cz: 'CZ',
    Dk: 'DK',
    Dj: 'DJ',
    Dm: 'DM',
    Do: 'DO',
    Ec: 'EC',
    Eg: 'EG',
    Sv: 'SV',
    Gq: 'GQ',
    Er: 'ER',
    Ee: 'EE',
    Sz: 'SZ',
    Et: 'ET',
    Fk: 'FK',
    Fo: 'FO',
    Fj: 'FJ',
    Fi: 'FI',
    Fr: 'FR',
    Gf: 'GF',
    Pf: 'PF',
    Tf: 'TF',
    Ga: 'GA',
    Gm: 'GM',
    Ge: 'GE',
    De: 'DE',
    Gh: 'GH',
    Gi: 'GI',
    Gr: 'GR',
    Gl: 'GL',
    Gd: 'GD',
    Gp: 'GP',
    Gu: 'GU',
    Gt: 'GT',
    Gg: 'GG',
    Gn: 'GN',
    Gw: 'GW',
    Gy: 'GY',
    Ht: 'HT',
    Hm: 'HM',
    Va: 'VA',
    Hn: 'HN',
    Hk: 'HK',
    Hu: 'HU',
    Is: 'IS',
    In: 'IN',
    Id: 'ID',
    Ir: 'IR',
    Iq: 'IQ',
    Ie: 'IE',
    Im: 'IM',
    Il: 'IL',
    It: 'IT',
    Jm: 'JM',
    Jp: 'JP',
    Je: 'JE',
    Jo: 'JO',
    Kz: 'KZ',
    Ke: 'KE',
    Ki: 'KI',
    Kp: 'KP',
    Kr: 'KR',
    Xk: 'XK',
    Kw: 'KW',
    Kg: 'KG',
    La: 'LA',
    Lv: 'LV',
    Lb: 'LB',
    Ls: 'LS',
    Lr: 'LR',
    Ly: 'LY',
    Li: 'LI',
    Lt: 'LT',
    Lu: 'LU',
    Mo: 'MO',
    Mg: 'MG',
    Mw: 'MW',
    My: 'MY',
    Mv: 'MV',
    Ml: 'ML',
    Mt: 'MT',
    Mh: 'MH',
    Mq: 'MQ',
    Mr: 'MR',
    Mu: 'MU',
    Yt: 'YT',
    Mx: 'MX',
    Fm: 'FM',
    Md: 'MD',
    Mc: 'MC',
    Mn: 'MN',
    Me: 'ME',
    Ms: 'MS',
    Ma: 'MA',
    Mz: 'MZ',
    Mm: 'MM',
    Na: 'NA',
    Nr: 'NR',
    Np: 'NP',
    Nl: 'NL',
    Nc: 'NC',
    Nz: 'NZ',
    Ni: 'NI',
    Ne: 'NE',
    Ng: 'NG',
    Nu: 'NU',
    Nf: 'NF',
    Mk: 'MK',
    Mp: 'MP',
    No: 'NO',
    Om: 'OM',
    Pk: 'PK',
    Pw: 'PW',
    Ps: 'PS',
    Pa: 'PA',
    Pg: 'PG',
    Py: 'PY',
    Pe: 'PE',
    Ph: 'PH',
    Pn: 'PN',
    Pl: 'PL',
    Pt: 'PT',
    Pr: 'PR',
    Qa: 'QA',
    Re: 'RE',
    Ro: 'RO',
    Ru: 'RU',
    Rw: 'RW',
    Bl: 'BL',
    Sh: 'SH',
    Kn: 'KN',
    Lc: 'LC',
    Mf: 'MF',
    Pm: 'PM',
    Vc: 'VC',
    Ws: 'WS',
    Sm: 'SM',
    St: 'ST',
    Sa: 'SA',
    Sn: 'SN',
    Rs: 'RS',
    Sc: 'SC',
    Sl: 'SL',
    Sg: 'SG',
    Sx: 'SX',
    Sk: 'SK',
    Si: 'SI',
    Sb: 'SB',
    So: 'SO',
    Za: 'ZA',
    Gs: 'GS',
    Ss: 'SS',
    Es: 'ES',
    Lk: 'LK',
    Sd: 'SD',
    Sr: 'SR',
    Sj: 'SJ',
    Se: 'SE',
    Ch: 'CH',
    Sy: 'SY',
    Tw: 'TW',
    Tj: 'TJ',
    Tz: 'TZ',
    Th: 'TH',
    Tl: 'TL',
    Tg: 'TG',
    Tk: 'TK',
    To: 'TO',
    Tt: 'TT',
    Tn: 'TN',
    Tr: 'TR',
    Tm: 'TM',
    Tc: 'TC',
    Tv: 'TV',
    Ug: 'UG',
    Ua: 'UA',
    Ae: 'AE',
    Gb: 'GB',
    Um: 'UM',
    Us: 'US',
    Uy: 'UY',
    Uz: 'UZ',
    Vu: 'VU',
    Ve: 'VE',
    Vn: 'VN',
    Vg: 'VG',
    Vi: 'VI',
    Wf: 'WF',
    Eh: 'EH',
    Ye: 'YE',
    Zm: 'ZM',
    Zw: 'ZW'
} as const;

export type NationalityEnum = typeof NationalityEnum[keyof typeof NationalityEnum];


/**
 * Custom Company Serializer for News Articles.
 * @export
 * @interface NewsCompany
 */
export interface NewsCompany {
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof NewsCompany
     */
    'country': CountryEnum;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof NewsCompany
     */
    'social_logo': string | null;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'website_icon_url': string;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'global_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'global_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'domestic_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof NewsCompany
     */
    'domestic_ultimate_duns_number'?: string | null;
}
/**
 * Serializer for not found error.
 * @export
 * @interface NotFoundError
 */
export interface NotFoundError {
    /**
     * 
     * @type {string}
     * @memberof NotFoundError
     */
    'detail': string;
}
/**
 * Serializer for notifications.
 * @export
 * @interface Notification
 */
export interface Notification {
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'external_id': string;
    /**
     * Type of notification  * `COMMENT` - Compliance Comment * `COMMENT_MENTION` - Compliance Comment Mention * `BINDER_COMMENT` - Binder Comment * `BINDER_COMMENT_MENTION` - Binder Comment Mention * `COMPANY_REGISTRATION_UPDATE` - Company Registration Update * `COMPLIANCE_CHECK_UPDATE` - Compliance Check Update * `NEWS_ARTICLE_FEEDBACK_RECEIVED` - News Article Feedback Received * `COMPANY_FEEDBACK_RECEIVED` - Company Feedback Received * `MISSING_COMPANY_INVESTIGATION_UPDATE` - Missing Company Investigation Update * `BATCH_REGISTRATION_UPDATE` - Batch Registration Update * `BATCH_COMPLIANCE_CHECK_UPDATE` - Batch Compliance Check Update * `EXPORT_UPDATE` - Export Update * `SUPPORT_REQUEST_UPDATE` - Support Request Update
     * @type {NotificationTypeEnum}
     * @memberof Notification
     */
    'notification_type': NotificationTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'message': string;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'target_url': string | null;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'status': string | null;
    /**
     * 
     * @type {number}
     * @memberof Notification
     */
    'progress': number | null;
    /**
     * 
     * @type {PriorityEnum}
     * @memberof Notification
     */
    'priority': PriorityEnum;
    /**
     * 
     * @type {any}
     * @memberof Notification
     */
    'data': any;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'confirmed_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Notification
     */
    'updated_at': string;
}
/**
 * Serializer for batch updating notifications.
 * @export
 * @interface NotificationBatchUpdateRequest
 */
export interface NotificationBatchUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof NotificationBatchUpdateRequest
     */
    'external_id'?: string;
}
/**
 * Serializer for a single per-type notification setting.  Used both to validate PUT input (``notification_type`` is validated against the model choices, so unknown types return 400) and to render the current setting for each type. ``display_name`` is the translated, human-readable name of the notification type for the settings page.
 * @export
 * @interface NotificationSetting
 */
export interface NotificationSetting {
    /**
     * Notification type this setting applies to  * `COMMENT` - Compliance Comment * `COMMENT_MENTION` - Compliance Comment Mention * `BINDER_COMMENT` - Binder Comment * `BINDER_COMMENT_MENTION` - Binder Comment Mention * `COMPANY_REGISTRATION_UPDATE` - Company Registration Update * `COMPLIANCE_CHECK_UPDATE` - Compliance Check Update * `NEWS_ARTICLE_FEEDBACK_RECEIVED` - News Article Feedback Received * `COMPANY_FEEDBACK_RECEIVED` - Company Feedback Received * `MISSING_COMPANY_INVESTIGATION_UPDATE` - Missing Company Investigation Update * `BATCH_REGISTRATION_UPDATE` - Batch Registration Update * `BATCH_COMPLIANCE_CHECK_UPDATE` - Batch Compliance Check Update * `EXPORT_UPDATE` - Export Update * `SUPPORT_REQUEST_UPDATE` - Support Request Update
     * @type {NotificationTypeEnum}
     * @memberof NotificationSetting
     */
    'notification_type': NotificationTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof NotificationSetting
     */
    'display_name': string;
    /**
     * 
     * @type {boolean}
     * @memberof NotificationSetting
     */
    'in_app_enabled': boolean;
}
/**
 * Serializer for a single per-type notification setting.  Used both to validate PUT input (``notification_type`` is validated against the model choices, so unknown types return 400) and to render the current setting for each type. ``display_name`` is the translated, human-readable name of the notification type for the settings page.
 * @export
 * @interface NotificationSettingRequest
 */
export interface NotificationSettingRequest {
    /**
     * Notification type this setting applies to  * `COMMENT` - Compliance Comment * `COMMENT_MENTION` - Compliance Comment Mention * `BINDER_COMMENT` - Binder Comment * `BINDER_COMMENT_MENTION` - Binder Comment Mention * `COMPANY_REGISTRATION_UPDATE` - Company Registration Update * `COMPLIANCE_CHECK_UPDATE` - Compliance Check Update * `NEWS_ARTICLE_FEEDBACK_RECEIVED` - News Article Feedback Received * `COMPANY_FEEDBACK_RECEIVED` - Company Feedback Received * `MISSING_COMPANY_INVESTIGATION_UPDATE` - Missing Company Investigation Update * `BATCH_REGISTRATION_UPDATE` - Batch Registration Update * `BATCH_COMPLIANCE_CHECK_UPDATE` - Batch Compliance Check Update * `EXPORT_UPDATE` - Export Update * `SUPPORT_REQUEST_UPDATE` - Support Request Update
     * @type {NotificationTypeEnum}
     * @memberof NotificationSettingRequest
     */
    'notification_type': NotificationTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof NotificationSettingRequest
     */
    'in_app_enabled': boolean;
}
/**
 * Unread notification counts grouped by notification type.
 * @export
 * @interface NotificationStatistics
 */
export interface NotificationStatistics {
    /**
     * 
     * @type {number}
     * @memberof NotificationStatistics
     */
    'total': number;
    /**
     * 
     * @type {{ [key: string]: number; }}
     * @memberof NotificationStatistics
     */
    'counts': { [key: string]: number; };
}
/**
 * * `COMMENT` - Compliance Comment * `COMMENT_MENTION` - Compliance Comment Mention * `BINDER_COMMENT` - Binder Comment * `BINDER_COMMENT_MENTION` - Binder Comment Mention * `COMPANY_REGISTRATION_UPDATE` - Company Registration Update * `COMPLIANCE_CHECK_UPDATE` - Compliance Check Update * `NEWS_ARTICLE_FEEDBACK_RECEIVED` - News Article Feedback Received * `COMPANY_FEEDBACK_RECEIVED` - Company Feedback Received * `MISSING_COMPANY_INVESTIGATION_UPDATE` - Missing Company Investigation Update * `BATCH_REGISTRATION_UPDATE` - Batch Registration Update * `BATCH_COMPLIANCE_CHECK_UPDATE` - Batch Compliance Check Update * `EXPORT_UPDATE` - Export Update * `SUPPORT_REQUEST_UPDATE` - Support Request Update
 * @export
 * @enum {string}
 */

export const NotificationTypeEnum = {
    Comment: 'COMMENT',
    CommentMention: 'COMMENT_MENTION',
    BinderComment: 'BINDER_COMMENT',
    BinderCommentMention: 'BINDER_COMMENT_MENTION',
    CompanyRegistrationUpdate: 'COMPANY_REGISTRATION_UPDATE',
    ComplianceCheckUpdate: 'COMPLIANCE_CHECK_UPDATE',
    NewsArticleFeedbackReceived: 'NEWS_ARTICLE_FEEDBACK_RECEIVED',
    CompanyFeedbackReceived: 'COMPANY_FEEDBACK_RECEIVED',
    MissingCompanyInvestigationUpdate: 'MISSING_COMPANY_INVESTIGATION_UPDATE',
    BatchRegistrationUpdate: 'BATCH_REGISTRATION_UPDATE',
    BatchComplianceCheckUpdate: 'BATCH_COMPLIANCE_CHECK_UPDATE',
    ExportUpdate: 'EXPORT_UPDATE',
    SupportRequestUpdate: 'SUPPORT_REQUEST_UPDATE'
} as const;

export type NotificationTypeEnum = typeof NotificationTypeEnum[keyof typeof NotificationTypeEnum];


/**
 * 
 * @export
 * @enum {string}
 */

export const NullEnum = {
    Null: 'null'
} as const;

export type NullEnum = typeof NullEnum[keyof typeof NullEnum];


/**
 * 
 * @export
 * @interface PageVisit
 */
export interface PageVisit {
    /**
     * 
     * @type {string}
     * @memberof PageVisit
     */
    'current_page_name': string;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof PageVisit
     */
    'parameters'?: { [key: string]: any; };
}
/**
 * 
 * @export
 * @interface PageVisitRequest
 */
export interface PageVisitRequest {
    /**
     * 
     * @type {string}
     * @memberof PageVisitRequest
     */
    'current_page_name': string;
    /**
     * 
     * @type {{ [key: string]: any; }}
     * @memberof PageVisitRequest
     */
    'parameters'?: { [key: string]: any; };
}
/**
 * 
 * @export
 * @interface PaginatedAlertList
 */
export interface PaginatedAlertList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedAlertList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedAlertList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Alert>}
     * @memberof PaginatedAlertList
     */
    'results'?: Array<Alert>;
}
/**
 * 
 * @export
 * @interface PaginatedAlertUserList
 */
export interface PaginatedAlertUserList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedAlertUserList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedAlertUserList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<AlertUser>}
     * @memberof PaginatedAlertUserList
     */
    'results'?: Array<AlertUser>;
}
/**
 * 
 * @export
 * @interface PaginatedArticleFeedbackReadList
 */
export interface PaginatedArticleFeedbackReadList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedArticleFeedbackReadList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedArticleFeedbackReadList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ArticleFeedbackRead>}
     * @memberof PaginatedArticleFeedbackReadList
     */
    'results'?: Array<ArticleFeedbackRead>;
}
/**
 * 
 * @export
 * @interface PaginatedArticleList
 */
export interface PaginatedArticleList {
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedArticleList
     */
    'total_results'?: number | null;
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedArticleList
     */
    'next_key'?: string | null;
    /**
     * 
     * @type {Array<Article>}
     * @memberof PaginatedArticleList
     */
    'results'?: Array<Article>;
}
/**
 * 
 * @export
 * @interface PaginatedBatchRegistrationCompanyStatusList
 */
export interface PaginatedBatchRegistrationCompanyStatusList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBatchRegistrationCompanyStatusList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBatchRegistrationCompanyStatusList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BatchRegistrationCompanyStatus>}
     * @memberof PaginatedBatchRegistrationCompanyStatusList
     */
    'results'?: Array<BatchRegistrationCompanyStatus>;
}
/**
 * 
 * @export
 * @interface PaginatedBatchRegistrationErrorList
 */
export interface PaginatedBatchRegistrationErrorList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBatchRegistrationErrorList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBatchRegistrationErrorList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BatchRegistrationError>}
     * @memberof PaginatedBatchRegistrationErrorList
     */
    'results'?: Array<BatchRegistrationError>;
}
/**
 * 
 * @export
 * @interface PaginatedBatchRegistrationList
 */
export interface PaginatedBatchRegistrationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBatchRegistrationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBatchRegistrationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BatchRegistration>}
     * @memberof PaginatedBatchRegistrationList
     */
    'results'?: Array<BatchRegistration>;
}
/**
 * 
 * @export
 * @interface PaginatedBillingNotificationList
 */
export interface PaginatedBillingNotificationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBillingNotificationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBillingNotificationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BillingNotification>}
     * @memberof PaginatedBillingNotificationList
     */
    'results'?: Array<BillingNotification>;
}
/**
 * 
 * @export
 * @interface PaginatedBinderItemList
 */
export interface PaginatedBinderItemList {
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBinderItemList
     */
    'total_results'?: number | null;
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBinderItemList
     */
    'next_key'?: string | null;
    /**
     * 
     * @type {Array<BinderItem>}
     * @memberof PaginatedBinderItemList
     */
    'results'?: Array<BinderItem>;
}
/**
 * 
 * @export
 * @interface PaginatedBinderList
 */
export interface PaginatedBinderList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBinderList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBinderList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Binder>}
     * @memberof PaginatedBinderList
     */
    'results'?: Array<Binder>;
}
/**
 * 
 * @export
 * @interface PaginatedBinderProfileUserGroupList
 */
export interface PaginatedBinderProfileUserGroupList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBinderProfileUserGroupList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBinderProfileUserGroupList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BinderProfileUserGroup>}
     * @memberof PaginatedBinderProfileUserGroupList
     */
    'results'?: Array<BinderProfileUserGroup>;
}
/**
 * 
 * @export
 * @interface PaginatedBinderTagList
 */
export interface PaginatedBinderTagList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBinderTagList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBinderTagList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BinderTag>}
     * @memberof PaginatedBinderTagList
     */
    'results'?: Array<BinderTag>;
}
/**
 * 
 * @export
 * @interface PaginatedBinderUserList
 */
export interface PaginatedBinderUserList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedBinderUserList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedBinderUserList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<BinderUser>}
     * @memberof PaginatedBinderUserList
     */
    'results'?: Array<BinderUser>;
}
/**
 * 
 * @export
 * @interface PaginatedCommentList
 */
export interface PaginatedCommentList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedCommentList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedCommentList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Comment>}
     * @memberof PaginatedCommentList
     */
    'results'?: Array<Comment>;
}
/**
 * 
 * @export
 * @interface PaginatedCompanyCategoriesAggregationList
 */
export interface PaginatedCompanyCategoriesAggregationList {
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedCompanyCategoriesAggregationList
     */
    'total_results'?: number | null;
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedCompanyCategoriesAggregationList
     */
    'next_key'?: string | null;
    /**
     * 
     * @type {Array<CompanyCategoriesAggregation>}
     * @memberof PaginatedCompanyCategoriesAggregationList
     */
    'results'?: Array<CompanyCategoriesAggregation>;
}
/**
 * 
 * @export
 * @interface PaginatedCompanyFeedbackReadList
 */
export interface PaginatedCompanyFeedbackReadList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedCompanyFeedbackReadList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedCompanyFeedbackReadList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<CompanyFeedbackRead>}
     * @memberof PaginatedCompanyFeedbackReadList
     */
    'results'?: Array<CompanyFeedbackRead>;
}
/**
 * 
 * @export
 * @interface PaginatedCompanyList
 */
export interface PaginatedCompanyList {
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedCompanyList
     */
    'total_results'?: number | null;
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedCompanyList
     */
    'next_key'?: string | null;
    /**
     * 
     * @type {Array<Company>}
     * @memberof PaginatedCompanyList
     */
    'results'?: Array<Company>;
    /**
     * 
     * @type {CompanyAnnotations}
     * @memberof PaginatedCompanyList
     */
    'annotations'?: CompanyAnnotations;
}
/**
 * 
 * @export
 * @interface PaginatedCompanyScoringList
 */
export interface PaginatedCompanyScoringList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedCompanyScoringList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedCompanyScoringList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<CompanyScoring>}
     * @memberof PaginatedCompanyScoringList
     */
    'results'?: Array<CompanyScoring>;
}
/**
 * 
 * @export
 * @interface PaginatedCompanyScoringSignalList
 */
export interface PaginatedCompanyScoringSignalList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedCompanyScoringSignalList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedCompanyScoringSignalList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<CompanyScoringSignal>}
     * @memberof PaginatedCompanyScoringSignalList
     */
    'results'?: Array<CompanyScoringSignal>;
}
/**
 * 
 * @export
 * @interface PaginatedComplianceCheckList
 */
export interface PaginatedComplianceCheckList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedComplianceCheckList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedComplianceCheckList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ComplianceCheck>}
     * @memberof PaginatedComplianceCheckList
     */
    'results'?: Array<ComplianceCheck>;
}
/**
 * 
 * @export
 * @interface PaginatedComplianceEntityAliasList
 */
export interface PaginatedComplianceEntityAliasList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedComplianceEntityAliasList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedComplianceEntityAliasList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ComplianceEntityAlias>}
     * @memberof PaginatedComplianceEntityAliasList
     */
    'results'?: Array<ComplianceEntityAlias>;
}
/**
 * 
 * @export
 * @interface PaginatedComplianceEntityIdentifierList
 */
export interface PaginatedComplianceEntityIdentifierList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedComplianceEntityIdentifierList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedComplianceEntityIdentifierList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ComplianceEntityIdentifier>}
     * @memberof PaginatedComplianceEntityIdentifierList
     */
    'results'?: Array<ComplianceEntityIdentifier>;
}
/**
 * 
 * @export
 * @interface PaginatedComplianceEntityResultList
 */
export interface PaginatedComplianceEntityResultList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedComplianceEntityResultList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedComplianceEntityResultList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ComplianceEntityResult>}
     * @memberof PaginatedComplianceEntityResultList
     */
    'results'?: Array<ComplianceEntityResult>;
}
/**
 * 
 * @export
 * @interface PaginatedComplianceSettingsChangeLogList
 */
export interface PaginatedComplianceSettingsChangeLogList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedComplianceSettingsChangeLogList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedComplianceSettingsChangeLogList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ComplianceSettingsChangeLog>}
     * @memberof PaginatedComplianceSettingsChangeLogList
     */
    'results'?: Array<ComplianceSettingsChangeLog>;
}
/**
 * 
 * @export
 * @interface PaginatedConversationList
 */
export interface PaginatedConversationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedConversationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedConversationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Conversation>}
     * @memberof PaginatedConversationList
     */
    'results'?: Array<Conversation>;
}
/**
 * 
 * @export
 * @interface PaginatedDirectorList
 */
export interface PaginatedDirectorList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedDirectorList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedDirectorList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Director>}
     * @memberof PaginatedDirectorList
     */
    'results'?: Array<Director>;
}
/**
 * 
 * @export
 * @interface PaginatedIndustryCategoriesAggregationList
 */
export interface PaginatedIndustryCategoriesAggregationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedIndustryCategoriesAggregationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedIndustryCategoriesAggregationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<IndustryCategoriesAggregation>}
     * @memberof PaginatedIndustryCategoriesAggregationList
     */
    'results'?: Array<IndustryCategoriesAggregation>;
}
/**
 * 
 * @export
 * @interface PaginatedIndustryCodeList
 */
export interface PaginatedIndustryCodeList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedIndustryCodeList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedIndustryCodeList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof PaginatedIndustryCodeList
     */
    'results'?: Array<IndustryCode>;
}
/**
 * 
 * @export
 * @interface PaginatedIntegrationCompanyMappingList
 */
export interface PaginatedIntegrationCompanyMappingList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedIntegrationCompanyMappingList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedIntegrationCompanyMappingList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<IntegrationCompanyMapping>}
     * @memberof PaginatedIntegrationCompanyMappingList
     */
    'results'?: Array<IntegrationCompanyMapping>;
}
/**
 * 
 * @export
 * @interface PaginatedIntegrationFieldMappingList
 */
export interface PaginatedIntegrationFieldMappingList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedIntegrationFieldMappingList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedIntegrationFieldMappingList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<IntegrationFieldMapping>}
     * @memberof PaginatedIntegrationFieldMappingList
     */
    'results'?: Array<IntegrationFieldMapping>;
}
/**
 * 
 * @export
 * @interface PaginatedIntegrationList
 */
export interface PaginatedIntegrationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedIntegrationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedIntegrationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Integration>}
     * @memberof PaginatedIntegrationList
     */
    'results'?: Array<Integration>;
}
/**
 * 
 * @export
 * @interface PaginatedIntegrationSyncList
 */
export interface PaginatedIntegrationSyncList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedIntegrationSyncList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedIntegrationSyncList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<IntegrationSync>}
     * @memberof PaginatedIntegrationSyncList
     */
    'results'?: Array<IntegrationSync>;
}
/**
 * 
 * @export
 * @interface PaginatedInvitationList
 */
export interface PaginatedInvitationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedInvitationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedInvitationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Invitation>}
     * @memberof PaginatedInvitationList
     */
    'results'?: Array<Invitation>;
}
/**
 * 
 * @export
 * @interface PaginatedKeyEventList
 */
export interface PaginatedKeyEventList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedKeyEventList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedKeyEventList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<KeyEvent>}
     * @memberof PaginatedKeyEventList
     */
    'results'?: Array<KeyEvent>;
}
/**
 * 
 * @export
 * @interface PaginatedNotificationList
 */
export interface PaginatedNotificationList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedNotificationList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedNotificationList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Notification>}
     * @memberof PaginatedNotificationList
     */
    'results'?: Array<Notification>;
}
/**
 * 
 * @export
 * @interface PaginatedPortfolioBatchResetList
 */
export interface PaginatedPortfolioBatchResetList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedPortfolioBatchResetList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedPortfolioBatchResetList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<PortfolioBatchReset>}
     * @memberof PaginatedPortfolioBatchResetList
     */
    'results'?: Array<PortfolioBatchReset>;
}
/**
 * 
 * @export
 * @interface PaginatedPortfolioCompanyList
 */
export interface PaginatedPortfolioCompanyList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedPortfolioCompanyList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedPortfolioCompanyList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<PortfolioCompany>}
     * @memberof PaginatedPortfolioCompanyList
     */
    'results'?: Array<PortfolioCompany>;
}
/**
 * 
 * @export
 * @interface PaginatedPortfolioList
 */
export interface PaginatedPortfolioList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedPortfolioList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedPortfolioList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Portfolio>}
     * @memberof PaginatedPortfolioList
     */
    'results'?: Array<Portfolio>;
}
/**
 * 
 * @export
 * @interface PaginatedPortfolioProfileUserGroupList
 */
export interface PaginatedPortfolioProfileUserGroupList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedPortfolioProfileUserGroupList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedPortfolioProfileUserGroupList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<PortfolioProfileUserGroup>}
     * @memberof PaginatedPortfolioProfileUserGroupList
     */
    'results'?: Array<PortfolioProfileUserGroup>;
}
/**
 * 
 * @export
 * @interface PaginatedPortfolioUserList
 */
export interface PaginatedPortfolioUserList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedPortfolioUserList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedPortfolioUserList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<PortfolioUser>}
     * @memberof PaginatedPortfolioUserList
     */
    'results'?: Array<PortfolioUser>;
}
/**
 * 
 * @export
 * @interface PaginatedProfileCountryRiskOverrideList
 */
export interface PaginatedProfileCountryRiskOverrideList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedProfileCountryRiskOverrideList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedProfileCountryRiskOverrideList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ProfileCountryRiskOverride>}
     * @memberof PaginatedProfileCountryRiskOverrideList
     */
    'results'?: Array<ProfileCountryRiskOverride>;
}
/**
 * 
 * @export
 * @interface PaginatedProfileIndustryRiskOverrideList
 */
export interface PaginatedProfileIndustryRiskOverrideList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedProfileIndustryRiskOverrideList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedProfileIndustryRiskOverrideList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ProfileIndustryRiskOverride>}
     * @memberof PaginatedProfileIndustryRiskOverrideList
     */
    'results'?: Array<ProfileIndustryRiskOverride>;
}
/**
 * 
 * @export
 * @interface PaginatedProfileList
 */
export interface PaginatedProfileList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedProfileList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedProfileList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<Profile>}
     * @memberof PaginatedProfileList
     */
    'results'?: Array<Profile>;
}
/**
 * 
 * @export
 * @interface PaginatedProfileUserGroupList
 */
export interface PaginatedProfileUserGroupList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedProfileUserGroupList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedProfileUserGroupList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ProfileUserGroup>}
     * @memberof PaginatedProfileUserGroupList
     */
    'results'?: Array<ProfileUserGroup>;
}
/**
 * 
 * @export
 * @interface PaginatedProfileUserGroupUserList
 */
export interface PaginatedProfileUserGroupUserList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedProfileUserGroupUserList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedProfileUserGroupUserList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ProfileUserGroupUser>}
     * @memberof PaginatedProfileUserGroupUserList
     */
    'results'?: Array<ProfileUserGroupUser>;
}
/**
 * 
 * @export
 * @interface PaginatedProfileUserListList
 */
export interface PaginatedProfileUserListList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedProfileUserListList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedProfileUserListList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<ProfileUserList>}
     * @memberof PaginatedProfileUserListList
     */
    'results'?: Array<ProfileUserList>;
}
/**
 * 
 * @export
 * @interface PaginatedSavedArticleFilterList
 */
export interface PaginatedSavedArticleFilterList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedSavedArticleFilterList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedSavedArticleFilterList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<SavedArticleFilter>}
     * @memberof PaginatedSavedArticleFilterList
     */
    'results'?: Array<SavedArticleFilter>;
}
/**
 * 
 * @export
 * @interface PaginatedSavedArticleFilterUserList
 */
export interface PaginatedSavedArticleFilterUserList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedSavedArticleFilterUserList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedSavedArticleFilterUserList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<SavedArticleFilterUser>}
     * @memberof PaginatedSavedArticleFilterUserList
     */
    'results'?: Array<SavedArticleFilterUser>;
}
/**
 * 
 * @export
 * @interface PaginatedSupportRequestCommentList
 */
export interface PaginatedSupportRequestCommentList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedSupportRequestCommentList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedSupportRequestCommentList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<SupportRequestComment>}
     * @memberof PaginatedSupportRequestCommentList
     */
    'results'?: Array<SupportRequestComment>;
}
/**
 * 
 * @export
 * @interface PaginatedSupportRequestList
 */
export interface PaginatedSupportRequestList {
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedSupportRequestList
     */
    'next_key'?: string | null;
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedSupportRequestList
     */
    'total_results'?: number | null;
    /**
     * 
     * @type {Array<SupportRequest>}
     * @memberof PaginatedSupportRequestList
     */
    'results'?: Array<SupportRequest>;
}
/**
 * 
 * @export
 * @interface PaginatedUniversalCompanyList
 */
export interface PaginatedUniversalCompanyList {
    /**
     * The total number of results matching the search criteria, regardless of the current page.
     * @type {number}
     * @memberof PaginatedUniversalCompanyList
     */
    'total_results'?: number | null;
    /**
     * A cursor value used for pagination. Include this value in the `next_key` query parameter of your next request to retrieve the subsequent page of results. If this value is `null`, there are no more results to fetch.
     * @type {string}
     * @memberof PaginatedUniversalCompanyList
     */
    'next_key'?: string | null;
    /**
     * 
     * @type {Array<UniversalCompany>}
     * @memberof PaginatedUniversalCompanyList
     */
    'results'?: Array<UniversalCompany>;
    /**
     * 
     * @type {CompanyAnnotations}
     * @memberof PaginatedUniversalCompanyList
     */
    'annotations'?: CompanyAnnotations;
}
/**
 * Password Change Serializer.
 * @export
 * @interface PasswordChangeRequest
 */
export interface PasswordChangeRequest {
    /**
     * 
     * @type {string}
     * @memberof PasswordChangeRequest
     */
    'password1': string;
    /**
     * 
     * @type {string}
     * @memberof PasswordChangeRequest
     */
    'password2': string;
}
/**
 * PasswordReset Serializer.
 * @export
 * @interface PasswordReset
 */
export interface PasswordReset {
    /**
     * 
     * @type {string}
     * @memberof PasswordReset
     */
    'email': string;
    /**
     * Date indicating when the password reset expires.
     * @type {string}
     * @memberof PasswordReset
     */
    'expired_at': string;
}
/**
 * PasswordReset Serializer.
 * @export
 * @interface PasswordResetRequest
 */
export interface PasswordResetRequest {
    /**
     * 
     * @type {string}
     * @memberof PasswordResetRequest
     */
    'email': string;
}
/**
 * Alert Serializer.
 * @export
 * @interface PatchedAlertRequest
 */
export interface PatchedAlertRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedAlertRequest
     */
    'name'?: string;
    /**
     * Action when processing this alert, sending email of sms  * `EMAIL` - E-mail * `SMS` - Sms * `PUSH_NOTIFICATION` - Push Notification
     * @type {ActionTypeEnum}
     * @memberof PatchedAlertRequest
     */
    'action_type'?: ActionTypeEnum;
    /**
     * 
     * @type {AlertRequestArticleFilters}
     * @memberof PatchedAlertRequest
     */
    'article_filters'?: AlertRequestArticleFilters | null;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof PatchedAlertRequest
     */
    'customer_reference'?: string | null;
    /**
     * Only send alerts on business days, not weekends.
     * @type {boolean}
     * @memberof PatchedAlertRequest
     */
    'business_days'?: boolean;
    /**
     * Send Push notifications about this alert to the user.
     * @type {boolean}
     * @memberof PatchedAlertRequest
     */
    'push_notifications'?: boolean;
    /**
     * Generate summary of alert.
     * @type {boolean}
     * @memberof PatchedAlertRequest
     */
    'generate_summary'?: boolean;
    /**
     * Free-text instruction that steers both which articles are included and how the summary is written (e.g. \'Focus on M&A and executive changes; ignore product launches\'). When set, an LLM curates the candidate articles against this instruction and produces a summary in one combined call.
     * @type {string}
     * @memberof PatchedAlertRequest
     */
    'ai_instruction'?: string | null;
    /**
     * 
     * @type {IntervalEnum}
     * @memberof PatchedAlertRequest
     */
    'interval'?: IntervalEnum;
    /**
     * 
     * @type {string}
     * @memberof PatchedAlertRequest
     */
    'start_date'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedAlertRequest
     */
    'time'?: string;
    /**
     * 
     * @type {AlertTimezone}
     * @memberof PatchedAlertRequest
     */
    'timezone'?: AlertTimezone | null;
}
/**
 * Serializer for creating a batch compliance check.
 * @export
 * @interface PatchedBatchComplianceCheckRequest
 */
export interface PatchedBatchComplianceCheckRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedBatchComplianceCheckRequest
     */
    'portfolio_id'?: string;
    /**
     * 
     * @type {Array<string>}
     * @memberof PatchedBatchComplianceCheckRequest
     */
    'company_ids'?: Array<string>;
    /**
     * If directors should be screened.
     * @type {boolean}
     * @memberof PatchedBatchComplianceCheckRequest
     */
    'directors_screening_enabled'?: boolean;
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof PatchedBatchComplianceCheckRequest
     */
    'director_role_groups'?: Array<DirectorRoleGroupsEnum> | null;
    /**
     * If ultimate owners should be screened.
     * @type {boolean}
     * @memberof PatchedBatchComplianceCheckRequest
     */
    'ubo_screening_enabled'?: boolean;
}
/**
 * 
 * @export
 * @interface PatchedBillingNotificationRequest
 */
export interface PatchedBillingNotificationRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedBillingNotificationRequest
     */
    'name'?: string;
    /**
     * Company usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof PatchedBillingNotificationRequest
     */
    'company_usage_threshold'?: number | null;
    /**
     * UBO call usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof PatchedBillingNotificationRequest
     */
    'ubo_call_usage_threshold'?: number | null;
    /**
     * Compliance check usage threshold as a fraction (0-1, e.g. 0.8 = 80%).
     * @type {number}
     * @memberof PatchedBillingNotificationRequest
     */
    'compliance_check_usage_threshold'?: number | null;
}
/**
 * Binder Item Serializer.
 * @export
 * @interface PatchedBinderItemRequest
 */
export interface PatchedBinderItemRequest {
    /**
     * 
     * @type {BinderItemRequestArticle}
     * @memberof PatchedBinderItemRequest
     */
    'article'?: BinderItemRequestArticle | null;
    /**
     * 
     * @type {BinderItemRequestComplianceEntityResult}
     * @memberof PatchedBinderItemRequest
     */
    'compliance_entity_result'?: BinderItemRequestComplianceEntityResult | null;
    /**
     * 
     * @type {Array<BinderTagRequest>}
     * @memberof PatchedBinderItemRequest
     */
    'tags'?: Array<BinderTagRequest>;
    /**
     * 
     * @type {string}
     * @memberof PatchedBinderItemRequest
     */
    'comment'?: string | null;
}
/**
 * Binder Profile User Group Serializer.
 * @export
 * @interface PatchedBinderProfileUserGroupRequest
 */
export interface PatchedBinderProfileUserGroupRequest {
    /**
     * 
     * @type {ProfileUserGroupDataRequest}
     * @memberof PatchedBinderProfileUserGroupRequest
     */
    'profile_user_group'?: ProfileUserGroupDataRequest;
    /**
     * 
     * @type {Permission655Enum}
     * @memberof PatchedBinderProfileUserGroupRequest
     */
    'permission'?: Permission655Enum;
}
/**
 * Binder.
 * @export
 * @interface PatchedBinderRequest
 */
export interface PatchedBinderRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedBinderRequest
     */
    'name'?: string;
}
/**
 * Binder User Serializer.
 * @export
 * @interface PatchedBinderUserRequest
 */
export interface PatchedBinderUserRequest {
    /**
     * 
     * @type {UserRequest}
     * @memberof PatchedBinderUserRequest
     */
    'user'?: UserRequest;
    /**
     * 
     * @type {Permission655Enum}
     * @memberof PatchedBinderUserRequest
     */
    'permission'?: Permission655Enum;
}
/**
 * Serializer for comments.
 * @export
 * @interface PatchedCommentRequest
 */
export interface PatchedCommentRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedCommentRequest
     */
    'text'?: string;
    /**
     * 
     * @type {string}
     * @memberof PatchedCommentRequest
     */
    'binder_item'?: string;
    /**
     * 
     * @type {string}
     * @memberof PatchedCommentRequest
     */
    'compliance_entity_result'?: string;
}
/**
 * Bulk-set the monitoring master switch across compliance checks.
 * @export
 * @interface PatchedComplianceCheckMonitoringBulkRequest
 */
export interface PatchedComplianceCheckMonitoringBulkRequest {
    /**
     * 
     * @type {Array<string>}
     * @memberof PatchedComplianceCheckMonitoringBulkRequest
     */
    'external_ids'?: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof PatchedComplianceCheckMonitoringBulkRequest
     */
    'monitoring_enabled'?: boolean;
}
/**
 * Compliance check serializer.
 * @export
 * @interface PatchedComplianceCheckRequest
 */
export interface PatchedComplianceCheckRequest {
    /**
     * Custom name for this compliance check.
     * @type {string}
     * @memberof PatchedComplianceCheckRequest
     */
    'name'?: string | null;
    /**
     * Master switch (opt-in): when enabled, screened entities of this check are automatically monitored (adverse media / sanctions).
     * @type {boolean}
     * @memberof PatchedComplianceCheckRequest
     */
    'monitoring_enabled'?: boolean;
    /**
     * Closed by a user once every result has been reviewed.
     * @type {boolean}
     * @memberof PatchedComplianceCheckRequest
     */
    'closed'?: boolean;
}
/**
 * Bulk subscribe/unsubscribe the current user across compliance checks.
 * @export
 * @interface PatchedComplianceCheckSubscriptionBulkRequest
 */
export interface PatchedComplianceCheckSubscriptionBulkRequest {
    /**
     * 
     * @type {Array<string>}
     * @memberof PatchedComplianceCheckSubscriptionBulkRequest
     */
    'external_ids'?: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof PatchedComplianceCheckSubscriptionBulkRequest
     */
    'subscribed'?: boolean;
}
/**
 * Compliance entity alias serializer.
 * @export
 * @interface PatchedComplianceEntityAliasRequest
 */
export interface PatchedComplianceEntityAliasRequest {
    /**
     * Alternative name or alias for the compliance entity.
     * @type {string}
     * @memberof PatchedComplianceEntityAliasRequest
     */
    'name'?: string;
}
/**
 * Compliance entity identifier serializer.
 * @export
 * @interface PatchedComplianceEntityIdentifierRequest
 */
export interface PatchedComplianceEntityIdentifierRequest {
    /**
     * 
     * @type {SchemeEnum}
     * @memberof PatchedComplianceEntityIdentifierRequest
     */
    'scheme'?: SchemeEnum;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityIdentifierRequest
     */
    'value'?: string;
}
/**
 * Compliance entity serializer.
 * @export
 * @interface PatchedComplianceEntityRequest
 */
export interface PatchedComplianceEntityRequest {
    /**
     * 
     * @type {boolean}
     * @memberof PatchedComplianceEntityRequest
     */
    'compliance_check_enabled'?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof PatchedComplianceEntityRequest
     */
    'adverse_media_check_enabled'?: boolean;
    /**
     * 
     * @type {ComplianceEntityTypeEnum}
     * @memberof PatchedComplianceEntityRequest
     */
    'entity_type'?: ComplianceEntityTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityRequest
     */
    'entity_role'?: string;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityRequest
     */
    'name'?: string;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof PatchedComplianceEntityRequest
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityRequest
     */
    'place_of_birth'?: string | null;
    /**
     * 
     * @type {ComplianceEntityGender}
     * @memberof PatchedComplianceEntityRequest
     */
    'gender'?: ComplianceEntityGender | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityRequest
     */
    'date_of_birth'?: string | null;
    /**
     * 
     * @type {ComplianceEntityRequestCompany}
     * @memberof PatchedComplianceEntityRequest
     */
    'company'?: ComplianceEntityRequestCompany | null;
}
/**
 * Compliance entity result serializer.
 * @export
 * @interface PatchedComplianceEntityResultRequest
 */
export interface PatchedComplianceEntityResultRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityResultRequest
     */
    'external_id'?: string;
    /**
     * 
     * @type {BinderComplianceEntityResultFalsePositiveRating}
     * @memberof PatchedComplianceEntityResultRequest
     */
    'false_positive_rating'?: BinderComplianceEntityResultFalsePositiveRating | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityResultRequest
     */
    'false_positive_rating_comments'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityResultRequest
     */
    'url'?: string | null;
    /**
     * 
     * @type {any}
     * @memberof PatchedComplianceEntityResultRequest
     */
    'image'?: any | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedComplianceEntityResultRequest
     */
    'source_name'?: string | null;
}
/**
 * 
 * @export
 * @interface PatchedComplianceEntityResultSelectAllRatingRequest
 */
export interface PatchedComplianceEntityResultSelectAllRatingRequest {
    /**
     * 
     * @type {PatchedComplianceEntityResultSelectAllRatingRequestFalsePositiveRating}
     * @memberof PatchedComplianceEntityResultSelectAllRatingRequest
     */
    'false_positive_rating'?: PatchedComplianceEntityResultSelectAllRatingRequestFalsePositiveRating | null;
}
/**
 * @type PatchedComplianceEntityResultSelectAllRatingRequestFalsePositiveRating
 * @export
 */
export type PatchedComplianceEntityResultSelectAllRatingRequestFalsePositiveRating = FalsePositiveRatingEnum | NullEnum;

/**
 * Customer-managed compliance settings.  Entitlement flags and the caller\'s admin status are read-only extras for the module badge and role banner.
 * @export
 * @interface PatchedComplianceSettingsRequest
 */
export interface PatchedComplianceSettingsRequest {
    /**
     * Include the industry risk indicator in compliance checks.
     * @type {boolean}
     * @memberof PatchedComplianceSettingsRequest
     */
    'is_industry_risk_enabled'?: boolean;
    /**
     * Include the world (country) risk indicator in compliance checks.
     * @type {boolean}
     * @memberof PatchedComplianceSettingsRequest
     */
    'is_world_risk_enabled'?: boolean;
    /**
     * Exclude results already rated as false positive (or no risk / discarded) from ongoing-monitoring alert emails.
     * @type {boolean}
     * @memberof PatchedComplianceSettingsRequest
     */
    'monitoring_exclude_false_positives'?: boolean;
    /**
     * Compliance API provider keys to exclude from sanction screening. Empty means every provider is screened.
     * @type {any}
     * @memberof PatchedComplianceSettingsRequest
     */
    'sanction_excluded_providers'?: any;
    /**
     * Adverse media source keys to exclude from enforcement screening (\'news\', \'icij\', \'enforcement\', or an enforcement provider name). Empty means every source is included.
     * @type {any}
     * @memberof PatchedComplianceSettingsRequest
     */
    'enforcement_excluded_sources'?: any;
    /**
     * Disable partial name matching for adverse media screening (exact name matching only).
     * @type {boolean}
     * @memberof PatchedComplianceSettingsRequest
     */
    'disable_partial_name_search'?: boolean;
    /**
     * Minimum match probability (0.0-1.0) for a sanction hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof PatchedComplianceSettingsRequest
     */
    'sanction_matching_threshold'?: number | null;
    /**
     * Minimum match probability (0.0-1.0) for a PEP hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof PatchedComplianceSettingsRequest
     */
    'pep_matching_threshold'?: number | null;
    /**
     * PEP tiers to screen (TIER_1, TIER_2, TIER_3, INTERNATIONAL_ORG). Empty means all tiers.
     * @type {any}
     * @memberof PatchedComplianceSettingsRequest
     */
    'pep_tiers'?: any;
    /**
     * Also screen relatives and close associates (RCA) of PEPs.
     * @type {boolean}
     * @memberof PatchedComplianceSettingsRequest
     */
    'pep_include_rca'?: boolean;
    /**
     * Minimum match probability (0.0-1.0) for an SOE hit to be returned. Empty means the Compliance API default is used.
     * @type {number}
     * @memberof PatchedComplianceSettingsRequest
     */
    'soe_matching_threshold'?: number | null;
    /**
     * SOE relationship types to screen (WHOLLY_OWNED, MAJORITY_OWNED, MINORITY_OWNED, STATE_CONTROLLED, GOVERNMENT_LINKED). Empty means all types.
     * @type {any}
     * @memberof PatchedComplianceSettingsRequest
     */
    'soe_relationship_types'?: any;
}
/**
 * Serializer for field mappings.
 * @export
 * @interface PatchedIntegrationFieldMappingRequest
 */
export interface PatchedIntegrationFieldMappingRequest {
    /**
     * Field name in the external system
     * @type {string}
     * @memberof PatchedIntegrationFieldMappingRequest
     */
    'external_field'?: string;
    /**
     * Field name in our Company model
     * @type {string}
     * @memberof PatchedIntegrationFieldMappingRequest
     */
    'internal_field'?: string;
    /**
     * Optional transformation to apply
     * @type {string}
     * @memberof PatchedIntegrationFieldMappingRequest
     */
    'transformation'?: string | null;
    /**
     * Whether this field is required for sync
     * @type {boolean}
     * @memberof PatchedIntegrationFieldMappingRequest
     */
    'is_required'?: boolean;
    /**
     * Whether this field is used to identify companies
     * @type {boolean}
     * @memberof PatchedIntegrationFieldMappingRequest
     */
    'is_identifier'?: boolean;
}
/**
 * Serializer for Integration model.
 * @export
 * @interface PatchedIntegrationRequest
 */
export interface PatchedIntegrationRequest {
    /**
     * Display name for this integration
     * @type {string}
     * @memberof PatchedIntegrationRequest
     */
    'name'?: string;
    /**
     * The external system type  * `SALESFORCE` - Salesforce * `HUBSPOT` - HubSpot * `SAP` - SAP * `DYNAMICS` - Microsoft Dynamics * `NETSUITE` - NetSuite * `CUSTOM` - Custom System
     * @type {SystemTypeEnum}
     * @memberof PatchedIntegrationRequest
     */
    'system_type'?: SystemTypeEnum;
    /**
     * 
     * @type {IntegrationStatusEnum}
     * @memberof PatchedIntegrationRequest
     */
    'status'?: IntegrationStatusEnum;
    /**
     * Whether automatic sync is enabled
     * @type {boolean}
     * @memberof PatchedIntegrationRequest
     */
    'sync_enabled'?: boolean;
    /**
     * How often to sync in minutes
     * @type {number}
     * @memberof PatchedIntegrationRequest
     */
    'sync_interval_minutes'?: number;
    /**
     * Default portfolio for imported companies
     * @type {number}
     * @memberof PatchedIntegrationRequest
     */
    'default_portfolio'?: number | null;
    /**
     * System-specific configuration. Required fields vary by system_type: Salesforce (client_id, client_secret, instance_url), HubSpot (access_token OR api_key), SAP (base_url, username, password, company_db if api_type is B1)
     * @type {any}
     * @memberof PatchedIntegrationRequest
     */
    'config'?: any;
}
/**
 * Serializer for notifications.
 * @export
 * @interface PatchedNotificationRequest
 */
export interface PatchedNotificationRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedNotificationRequest
     */
    'confirmed_at'?: string | null;
}
/**
 * Portfolio Profile User Group Serializer.
 * @export
 * @interface PatchedPortfolioProfileUserGroupRequest
 */
export interface PatchedPortfolioProfileUserGroupRequest {
    /**
     * 
     * @type {ProfileUserGroupDataRequest}
     * @memberof PatchedPortfolioProfileUserGroupRequest
     */
    'profile_user_group'?: ProfileUserGroupDataRequest;
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PatchedPortfolioProfileUserGroupRequest
     */
    'permission'?: PermissionEnum;
}
/**
 * Serialize Portfolio instance.
 * @export
 * @interface PatchedPortfolioRequest
 */
export interface PatchedPortfolioRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedPortfolioRequest
     */
    'name'?: string;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof PatchedPortfolioRequest
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {PatchedPortfolioRequestDefaultPermission}
     * @memberof PatchedPortfolioRequest
     */
    'default_permission'?: PatchedPortfolioRequestDefaultPermission | null;
}
/**
 * @type PatchedPortfolioRequestDefaultPermission
 * Default permission for all users in organization.  * `view_only` - Only Viewing Access * `write` - View and Write Access * `admin` - View, Write and Admin Access * `owner` - Portfolio Owner
 * @export
 */
export type PatchedPortfolioRequestDefaultPermission = BlankEnum | NullEnum | PermissionEnum;

/**
 * Portfolio User Update Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface PatchedPortfolioUserUpdateRequest
 */
export interface PatchedPortfolioUserUpdateRequest {
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PatchedPortfolioUserUpdateRequest
     */
    'permission'?: PermissionEnum;
}
/**
 * Per-profile world (country) risk override.
 * @export
 * @interface PatchedProfileCountryRiskOverrideRequest
 */
export interface PatchedProfileCountryRiskOverrideRequest {
    /**
     * 
     * @type {CountryEnum}
     * @memberof PatchedProfileCountryRiskOverrideRequest
     */
    'country'?: CountryEnum;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof PatchedProfileCountryRiskOverrideRequest
     */
    'score'?: ComplianceCheckScoreEnum;
}
/**
 * Per-profile industry (activity) risk override.  ``industry_code`` is written by SIC code; ``industry_code_detail`` is the read-only expansion for display.
 * @export
 * @interface PatchedProfileIndustryRiskOverrideRequest
 */
export interface PatchedProfileIndustryRiskOverrideRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedProfileIndustryRiskOverrideRequest
     */
    'industry_code'?: string;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof PatchedProfileIndustryRiskOverrideRequest
     */
    'score'?: ComplianceCheckScoreEnum;
}
/**
 * Get Profiles where a user is an part of.
 * @export
 * @interface PatchedProfileRequest
 */
export interface PatchedProfileRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedProfileRequest
     */
    'name'?: string;
    /**
     * 
     * @type {PatchedProfileRequestLanguage}
     * @memberof PatchedProfileRequest
     */
    'language'?: PatchedProfileRequestLanguage | null;
}
/**
 * @type PatchedProfileRequestLanguage
 * Default language for this entire profile, only relevant if this entire profile is this specific language.  * `af` - Afrikaans * `ar` - Arabic * `az` - Azerbaijani * `bg` - Bulgarian * `be` - Belarusian * `bn` - Bengali * `br` - Breton * `bs` - Bosnian * `ca` - Catalan * `cs` - Czech * `cy` - Welsh * `da` - Danish * `de` - German * `el` - Greek * `en` - English * `eo` - Esperanto * `es` - Spanish * `et` - Estonian * `eu` - Basque * `fa` - Persian * `fi` - Finnish * `fr` - French * `fy` - Frisian * `ga` - Irish * `gd` - Scottish Gaelic * `gl` - Galician * `he` - Hebrew * `hi` - Hindi * `hr` - Croatian * `hu` - Hungarian * `hy` - Armenian * `ia` - Interlingua * `id` - Indonesian * `ig` - Igbo * `io` - Ido * `is` - Icelandic * `it` - Italian * `ja` - Japanese * `ka` - Georgian * `kk` - Kazakh * `km` - Khmer * `kn` - Kannada * `ko` - Korean * `ky` - Kyrgyz * `lb` - Luxembourgish * `lt` - Lithuanian * `lv` - Latvian * `mk` - Macedonian * `ml` - Malayalam * `mn` - Mongolian * `mr` - Marathi * `my` - Burmese * `ne` - Nepali * `nl` - Dutch * `no` - Norwegian * `os` - Ossetic * `pa` - Punjabi * `pl` - Polish * `pt` - Portuguese * `ro` - Romanian * `ru` - Russian * `sk` - Slovak * `sl` - Slovenian * `sq` - Albanian * `sr` - Serbian * `sv` - Swedish * `sw` - Swahili * `ta` - Tamil * `te` - Telugu * `tg` - Tajik * `th` - Thai * `tk` - Turkmen * `tr` - Turkish * `tt` - Tatar * `uk` - Ukrainian * `ur` - Urdu * `uz` - Uzbek * `vi` - Vietnamese * `zh` - Chinese
 * @export
 */
export type PatchedProfileRequestLanguage = BlankEnum | Language856Enum | NullEnum;

/**
 * Profile User Group.
 * @export
 * @interface PatchedProfileUserGroupRequest
 */
export interface PatchedProfileUserGroupRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedProfileUserGroupRequest
     */
    'name'?: string;
    /**
     * Description of the purpose of this group.
     * @type {string}
     * @memberof PatchedProfileUserGroupRequest
     */
    'description'?: string | null;
}
/**
 * Saved Article Filter Serializer.
 * @export
 * @interface PatchedSavedArticleFilterRequest
 */
export interface PatchedSavedArticleFilterRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedSavedArticleFilterRequest
     */
    'name'?: string;
    /**
     * 
     * @type {ArticleFiltersRequest}
     * @memberof PatchedSavedArticleFilterRequest
     */
    'filters'?: ArticleFiltersRequest;
    /**
     * 
     * @type {PatchedSavedArticleFilterRequestDefaultPermission}
     * @memberof PatchedSavedArticleFilterRequest
     */
    'default_permission'?: PatchedSavedArticleFilterRequestDefaultPermission | null;
}
/**
 * @type PatchedSavedArticleFilterRequestDefaultPermission
 * @export
 */
export type PatchedSavedArticleFilterRequestDefaultPermission = BlankEnum | DefaultPermission506Enum | NullEnum;

/**
 * Serializer for Article Filters Update Users.
 * @export
 * @interface PatchedSavedArticleFilterUserUpdateRequest
 */
export interface PatchedSavedArticleFilterUserUpdateRequest {
    /**
     * 
     * @type {Permission506Enum}
     * @memberof PatchedSavedArticleFilterUserUpdateRequest
     */
    'permission'?: Permission506Enum;
}
/**
 * User Profile Detail Serializer.
 * @export
 * @interface PatchedUserProfileDetailRequest
 */
export interface PatchedUserProfileDetailRequest {
    /**
     * Flag indicating if the user has admin permissions.
     * @type {boolean}
     * @memberof PatchedUserProfileDetailRequest
     */
    'is_admin'?: boolean;
    /**
     * Receive monthly report about user analytics via mail.
     * @type {boolean}
     * @memberof PatchedUserProfileDetailRequest
     */
    'monthly_user_statistics'?: boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof PatchedUserProfileDetailRequest
     */
    'customer_reference'?: string | null;
}
/**
 * Update User Information.
 * @export
 * @interface PatchedUserUpdateRequest
 */
export interface PatchedUserUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedUserUpdateRequest
     */
    'phone_number'?: string | null;
    /**
     * Customize news alerts. Ex: Senior Independent Auditor
     * @type {string}
     * @memberof PatchedUserUpdateRequest
     */
    'function_role_description'?: string | null;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof PatchedUserUpdateRequest
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedUserUpdateRequest
     */
    'active_profile'?: string;
    /**
     * 
     * @type {string}
     * @memberof PatchedUserUpdateRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof PatchedUserUpdateRequest
     */
    'last_name'?: string;
}
/**
 * @type PatchedUserUpdateRequestLanguage
 * Default language for this user.  * `af` - Afrikaans * `ar` - Arabic * `az` - Azerbaijani * `bg` - Bulgarian * `be` - Belarusian * `bn` - Bengali * `br` - Breton * `bs` - Bosnian * `ca` - Catalan * `cs` - Czech * `cy` - Welsh * `da` - Danish * `de` - German * `el` - Greek * `en` - English * `eo` - Esperanto * `es` - Spanish * `et` - Estonian * `eu` - Basque * `fa` - Persian * `fi` - Finnish * `fr` - French * `fy` - Frisian * `ga` - Irish * `gd` - Scottish Gaelic * `gl` - Galician * `he` - Hebrew * `hi` - Hindi * `hr` - Croatian * `hu` - Hungarian * `hy` - Armenian * `ia` - Interlingua * `id` - Indonesian * `ig` - Igbo * `io` - Ido * `is` - Icelandic * `it` - Italian * `ja` - Japanese * `ka` - Georgian * `kk` - Kazakh * `km` - Khmer * `kn` - Kannada * `ko` - Korean * `ky` - Kyrgyz * `lb` - Luxembourgish * `lt` - Lithuanian * `lv` - Latvian * `mk` - Macedonian * `ml` - Malayalam * `mn` - Mongolian * `mr` - Marathi * `my` - Burmese * `ne` - Nepali * `nl` - Dutch * `no` - Norwegian * `os` - Ossetic * `pa` - Punjabi * `pl` - Polish * `pt` - Portuguese * `ro` - Romanian * `ru` - Russian * `sk` - Slovak * `sl` - Slovenian * `sq` - Albanian * `sr` - Serbian * `sv` - Swedish * `sw` - Swahili * `ta` - Tamil * `te` - Telugu * `tg` - Tajik * `th` - Thai * `tk` - Turkmen * `tr` - Turkish * `tt` - Tatar * `uk` - Ukrainian * `ur` - Urdu * `uz` - Uzbek * `vi` - Vietnamese * `zh` - Chinese
 * @export
 */
export type PatchedUserUpdateRequestLanguage = BlankEnum | Language856Enum | NullEnum;

/**
 * * `SELF` - Self * `FAMILY` - Family member * `ASSOCIATE` - Close associate
 * @export
 * @enum {string}
 */

export const PepRelationshipEnum = {
    Self: 'SELF',
    Family: 'FAMILY',
    Associate: 'ASSOCIATE'
} as const;

export type PepRelationshipEnum = typeof PepRelationshipEnum[keyof typeof PepRelationshipEnum];


/**
 * * `TIER_1` - Tier 1 (national) * `TIER_2` - Tier 2 (regional) * `TIER_3` - Tier 3 (local / SOE) * `INTERNATIONAL_ORG` - International organization
 * @export
 * @enum {string}
 */

export const PepTierEnum = {
    Tier1: 'TIER_1',
    Tier2: 'TIER_2',
    Tier3: 'TIER_3',
    InternationalOrg: 'INTERNATIONAL_ORG'
} as const;

export type PepTierEnum = typeof PepTierEnum[keyof typeof PepTierEnum];


/**
 * * `month` - Month * `quarter` - Quarter * `ytd` - Ytd * `year` - Year
 * @export
 * @enum {string}
 */

export const PeriodEnum = {
    Month: 'month',
    Quarter: 'quarter',
    Ytd: 'ytd',
    Year: 'year'
} as const;

export type PeriodEnum = typeof PeriodEnum[keyof typeof PeriodEnum];


/**
 * * `view_only` - Only Viewing Access * `write` - View and Write Access * `admin` - View, Write and Admin Access
 * @export
 * @enum {string}
 */

export const Permission506Enum = {
    ViewOnly: 'view_only',
    Write: 'write',
    Admin: 'admin'
} as const;

export type Permission506Enum = typeof Permission506Enum[keyof typeof Permission506Enum];


/**
 * * `view_only` - View Only Access * `write` - View and Modify Access * `admin` - Complete Administrator Access * `owner` - Owner of this Binder
 * @export
 * @enum {string}
 */

export const Permission655Enum = {
    ViewOnly: 'view_only',
    Write: 'write',
    Admin: 'admin',
    Owner: 'owner'
} as const;

export type Permission655Enum = typeof Permission655Enum[keyof typeof Permission655Enum];


/**
 * * `view_only` - Only Viewing Access * `write` - View and Write Access * `admin` - View, Write and Admin Access * `owner` - Portfolio Owner
 * @export
 * @enum {string}
 */

export const PermissionEnum = {
    ViewOnly: 'view_only',
    Write: 'write',
    Admin: 'admin',
    Owner: 'owner'
} as const;

export type PermissionEnum = typeof PermissionEnum[keyof typeof PermissionEnum];


/**
 * Phone Verification Check Serializer.
 * @export
 * @interface PhoneVerificationCheck
 */
export interface PhoneVerificationCheck {
    /**
     * 
     * @type {number}
     * @memberof PhoneVerificationCheck
     */
    'verification_code': number;
}
/**
 * Phone Verification Check Serializer.
 * @export
 * @interface PhoneVerificationCheckRequest
 */
export interface PhoneVerificationCheckRequest {
    /**
     * 
     * @type {number}
     * @memberof PhoneVerificationCheckRequest
     */
    'verification_code': number;
}
/**
 * Serialize Portfolio instance.
 * @export
 * @interface Portfolio
 */
export interface Portfolio {
    /**
     * Get Amount of Companies registered to this Portfolio.
     * @type {number}
     * @memberof Portfolio
     */
    'companies_count': number;
    /**
     * Get User Permission.
     * @type {PortfolioPermissionEnum}
     * @memberof Portfolio
     */
    'permission': PortfolioPermissionEnum;
    /**
     * 
     * @type {boolean}
     * @memberof Portfolio
     */
    'contains_company': boolean | null;
    /**
     * 
     * @type {string}
     * @memberof Portfolio
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Portfolio
     */
    'name': string;
    /**
     * Is automatically created by the System.
     * @type {boolean}
     * @memberof Portfolio
     */
    'is_system_created': boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof Portfolio
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {PatchedPortfolioRequestDefaultPermission}
     * @memberof Portfolio
     */
    'default_permission'?: PatchedPortfolioRequestDefaultPermission | null;
    /**
     * 
     * @type {string}
     * @memberof Portfolio
     */
    'updated_at': string;
    /**
     * 
     * @type {string}
     * @memberof Portfolio
     */
    'created_at': string;
}
/**
 * Portfolio Batch Reset Serializer.
 * @export
 * @interface PortfolioBatchReset
 */
export interface PortfolioBatchReset {
    /**
     * 
     * @type {string}
     * @memberof PortfolioBatchReset
     */
    'reset_last_synchronised_at': string;
}
/**
 * Portfolio Batch Reset Serializer.
 * @export
 * @interface PortfolioBatchResetRequest
 */
export interface PortfolioBatchResetRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfolioBatchResetRequest
     */
    'reset_last_synchronised_at': string;
}
/**
 * Portfolio Company serializer.
 * @export
 * @interface PortfolioCompany
 */
export interface PortfolioCompany {
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof PortfolioCompany
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {PortfolioCompanyCompany}
     * @memberof PortfolioCompany
     */
    'company': PortfolioCompanyCompany;
    /**
     * 
     * @type {PortfolioCompanyComplianceCheck}
     * @memberof PortfolioCompany
     */
    'compliance_check': PortfolioCompanyComplianceCheck;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompany
     */
    'last_synchronised_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompany
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompany
     */
    'updated_at': string;
}
/**
 * 
 * @export
 * @interface PortfolioCompanyCompany
 */
export interface PortfolioCompanyCompany {
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof PortfolioCompanyCompany
     */
    'country': CountryEnum;
    /**
     * 
     * @type {PortfolioCompanyDetailGlobalUltimate}
     * @memberof PortfolioCompanyCompany
     */
    'global_ultimate': PortfolioCompanyDetailGlobalUltimate | null;
    /**
     * Icon of the found website
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'website_icon_url': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'address_street': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'address_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'address_place': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'address_region': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'address_postal': string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof PortfolioCompanyCompany
     */
    'review_count': number;
    /**
     * Average review score
     * @type {number}
     * @memberof PortfolioCompanyCompany
     */
    'review_average_score': number | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof PortfolioCompanyCompany
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof PortfolioCompanyCompany
     */
    'social_logo': string | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof PortfolioCompanyCompany
     */
    'article_count': number;
    /**
     * 
     * @type {Array<TradeNameSchema>}
     * @memberof PortfolioCompanyCompany
     */
    'trade_names': Array<TradeNameSchema>;
}
/**
 * 
 * @export
 * @interface PortfolioCompanyComplianceCheck
 */
export interface PortfolioCompanyComplianceCheck {
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'external_id': string;
    /**
     * 
     * @type {ComplianceCheckCompany}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'company': ComplianceCheckCompany | null;
    /**
     * Custom name for this compliance check.
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'name'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'progress': number;
    /**
     * 
     * @type {ComplianceCheckStatusEnum}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'status': ComplianceCheckStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'started_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'finished_at': string | null;
    /**
     * Warning if no ownership information is found.
     * @type {boolean}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'warning_ownership_not_found': boolean;
    /**
     * Providing director role groups enables director screening. Null selects all roles; an empty list selects none. Cannot be combined with directors_screening_enabled=false.
     * @type {Array<DirectorRoleGroupsEnum>}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'director_role_groups': Array<DirectorRoleGroupsEnum> | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'compliance_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'activity_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'country_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'sanction_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'pep_score': ComplianceCheckComplianceScore | null;
    /**
     * 
     * @type {ComplianceCheckComplianceScore}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'adverse_media_score': ComplianceCheckComplianceScore | null;
    /**
     * Number of results across all entities that have been reviewed by a user.
     * @type {number}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'reviewed_results_count': number;
    /**
     * Number of results across all entities that are open and not yet reviewed.
     * @type {number}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'unreviewed_results_count': number;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'results_changed_at': string | null;
    /**
     * Whether the current user is subscribed to this check\'s monitoring alerts.
     * @type {boolean}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'is_subscribed': boolean;
    /**
     * Whether monitoring was auto-paused on any entity (warning indicator).
     * @type {boolean}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'monitoring_paused': boolean;
    /**
     * Master switch (opt-in): when enabled, screened entities of this check are automatically monitored (adverse media / sanctions).
     * @type {boolean}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'monitoring_enabled'?: boolean;
    /**
     * Closed by a user once every result has been reviewed.
     * @type {boolean}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'closed'?: boolean;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'closed_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyComplianceCheck
     */
    'closed_by': string | null;
}
/**
 * Portfolio Company Detail Serializer.  Alternative serializer for the Company model which is limited.
 * @export
 * @interface PortfolioCompanyDetail
 */
export interface PortfolioCompanyDetail {
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'name': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof PortfolioCompanyDetail
     */
    'country': CountryEnum;
    /**
     * 
     * @type {PortfolioCompanyDetailGlobalUltimate}
     * @memberof PortfolioCompanyDetail
     */
    'global_ultimate': PortfolioCompanyDetailGlobalUltimate | null;
    /**
     * Icon of the found website
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'website_icon_url': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'address_street': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'address_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'address_place': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'address_region': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'address_postal': string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof PortfolioCompanyDetail
     */
    'review_count': number;
    /**
     * Average review score
     * @type {number}
     * @memberof PortfolioCompanyDetail
     */
    'review_average_score': number | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof PortfolioCompanyDetail
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof PortfolioCompanyDetail
     */
    'social_logo': string | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof PortfolioCompanyDetail
     */
    'article_count': number;
    /**
     * 
     * @type {Array<TradeNameSchema>}
     * @memberof PortfolioCompanyDetail
     */
    'trade_names': Array<TradeNameSchema>;
}
/**
 * 
 * @export
 * @interface PortfolioCompanyDetailGlobalUltimate
 */
export interface PortfolioCompanyDetailGlobalUltimate {
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetailGlobalUltimate
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetailGlobalUltimate
     */
    'name': string | null;
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetailGlobalUltimate
     */
    'country': string | null;
}
/**
 * Portfolio Company Detail Serializer.  Alternative serializer for the Company model which is limited.
 * @export
 * @interface PortfolioCompanyDetailRequest
 */
export interface PortfolioCompanyDetailRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfolioCompanyDetailRequest
     */
    'external_id': string;
}
/**
 * 
 * @export
 * @enum {string}
 */

export const PortfolioPermissionEnum = {
    ViewOnly: 'view_only',
    Write: 'write',
    Admin: 'admin',
    Owner: 'owner'
} as const;

export type PortfolioPermissionEnum = typeof PortfolioPermissionEnum[keyof typeof PortfolioPermissionEnum];


/**
 * Portfolio Profile User Group Serializer.
 * @export
 * @interface PortfolioProfileUserGroup
 */
export interface PortfolioProfileUserGroup {
    /**
     * 
     * @type {string}
     * @memberof PortfolioProfileUserGroup
     */
    'external_id': string;
    /**
     * 
     * @type {ProfileUserGroupData}
     * @memberof PortfolioProfileUserGroup
     */
    'profile_user_group': ProfileUserGroupData;
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PortfolioProfileUserGroup
     */
    'permission'?: PermissionEnum;
    /**
     * 
     * @type {string}
     * @memberof PortfolioProfileUserGroup
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioProfileUserGroup
     */
    'updated_at': string;
}
/**
 * Portfolio Profile User Group Serializer.
 * @export
 * @interface PortfolioProfileUserGroupRequest
 */
export interface PortfolioProfileUserGroupRequest {
    /**
     * 
     * @type {ProfileUserGroupDataRequest}
     * @memberof PortfolioProfileUserGroupRequest
     */
    'profile_user_group': ProfileUserGroupDataRequest;
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PortfolioProfileUserGroupRequest
     */
    'permission'?: PermissionEnum;
}
/**
 * Serialize Portfolio instance.
 * @export
 * @interface PortfolioRequest
 */
export interface PortfolioRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfolioRequest
     */
    'name': string;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof PortfolioRequest
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {PatchedPortfolioRequestDefaultPermission}
     * @memberof PortfolioRequest
     */
    'default_permission'?: PatchedPortfolioRequestDefaultPermission | null;
}
/**
 * Portfolio User Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface PortfolioUser
 */
export interface PortfolioUser {
    /**
     * 
     * @type {User}
     * @memberof PortfolioUser
     */
    'user': User;
    /**
     * 
     * @type {string}
     * @memberof PortfolioUser
     */
    'external_id': string;
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PortfolioUser
     */
    'permission'?: PermissionEnum;
    /**
     * 
     * @type {string}
     * @memberof PortfolioUser
     */
    'updated_at': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioUser
     */
    'created_at': string;
}
/**
 * Portfolio User Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface PortfolioUserRequest
 */
export interface PortfolioUserRequest {
    /**
     * 
     * @type {UserRequest}
     * @memberof PortfolioUserRequest
     */
    'user': UserRequest;
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PortfolioUserRequest
     */
    'permission'?: PermissionEnum;
}
/**
 * Portfolio User Update Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface PortfolioUserUpdate
 */
export interface PortfolioUserUpdate {
    /**
     * 
     * @type {string}
     * @memberof PortfolioUserUpdate
     */
    'external_id': string;
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PortfolioUserUpdate
     */
    'permission'?: PermissionEnum;
    /**
     * 
     * @type {string}
     * @memberof PortfolioUserUpdate
     */
    'updated_at': string;
    /**
     * 
     * @type {string}
     * @memberof PortfolioUserUpdate
     */
    'created_at': string;
}
/**
 * Portfolio User Update Serializer.  Users that have access to this Portfolio.
 * @export
 * @interface PortfolioUserUpdateRequest
 */
export interface PortfolioUserUpdateRequest {
    /**
     * 
     * @type {PermissionEnum}
     * @memberof PortfolioUserUpdateRequest
     */
    'permission'?: PermissionEnum;
}
/**
 * * `HIGH` - High * `MEDIUM` - Medium * `LOW` - Low
 * @export
 * @enum {string}
 */

export const PriorityEnum = {
    High: 'HIGH',
    Medium: 'MEDIUM',
    Low: 'LOW'
} as const;

export type PriorityEnum = typeof PriorityEnum[keyof typeof PriorityEnum];


/**
 * Get Profiles where a user is an part of.
 * @export
 * @interface Profile
 */
export interface Profile {
    /**
     * 
     * @type {string}
     * @memberof Profile
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Profile
     */
    'name': string;
    /**
     * Get logo URL.
     * @type {string}
     * @memberof Profile
     */
    'logo': string;
    /**
     * Get color.
     * @type {string}
     * @memberof Profile
     */
    'primary_color': string;
    /**
     * Get color.
     * @type {string}
     * @memberof Profile
     */
    'color1': string;
    /**
     * Get color.
     * @type {string}
     * @memberof Profile
     */
    'color2': string;
    /**
     * Get color.
     * @type {string}
     * @memberof Profile
     */
    'color3': string;
    /**
     * Get color.
     * @type {string}
     * @memberof Profile
     */
    'color4': string;
    /**
     * 
     * @type {PatchedProfileRequestLanguage}
     * @memberof Profile
     */
    'language'?: PatchedProfileRequestLanguage | null;
    /**
     * Administrator flag for users with Admin permissions.  This is a User specific field, so this is not in the correct context, this custom methodfield gets the context and checks if the user is an administrator.
     * @type {boolean}
     * @memberof Profile
     */
    'is_admin': boolean;
    /**
     * Check if global import is enabled.
     * @type {boolean}
     * @memberof Profile
     */
    'is_global_import_enabled': boolean;
    /**
     * Enabled users in this profile can see UBO data.
     * @type {boolean}
     * @memberof Profile
     */
    'is_ubo_enabled': boolean;
    /**
     * Gateway derived from the narrow per-check-type compliance flags.  Compliance is enabled for the profile when at least one screening type (sanctions, PEP, adverse media or SOE) is enabled.
     * @type {boolean}
     * @memberof Profile
     */
    'is_compliance_enabled': boolean;
    /**
     * Enabled users in this profile get sanctions screening.
     * @type {boolean}
     * @memberof Profile
     */
    'is_compliance_sanctions_enabled': boolean;
    /**
     * Enabled users in this profile get politically exposed person (PEP) screening.
     * @type {boolean}
     * @memberof Profile
     */
    'is_compliance_pep_enabled': boolean;
    /**
     * Enabled users in this profile get adverse media screening.
     * @type {boolean}
     * @memberof Profile
     */
    'is_compliance_adverse_media_enabled': boolean;
    /**
     * Enabled users in this profile get state linked / owned entity (SOE) screening.
     * @type {boolean}
     * @memberof Profile
     */
    'is_compliance_soe_enabled': boolean;
    /**
     * Check if summirzation is enabled.
     * @type {boolean}
     * @memberof Profile
     */
    'is_summarization_enabled': boolean;
    /**
     * 
     * @type {string}
     * @memberof Profile
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof Profile
     */
    'updated_at': string;
}
/**
 * Per-profile world (country) risk override.
 * @export
 * @interface ProfileCountryRiskOverride
 */
export interface ProfileCountryRiskOverride {
    /**
     * 
     * @type {string}
     * @memberof ProfileCountryRiskOverride
     */
    'external_id': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof ProfileCountryRiskOverride
     */
    'country': CountryEnum;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof ProfileCountryRiskOverride
     */
    'score': ComplianceCheckScoreEnum;
    /**
     * 
     * @type {string}
     * @memberof ProfileCountryRiskOverride
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileCountryRiskOverride
     */
    'updated_at': string;
}
/**
 * Per-profile world (country) risk override.
 * @export
 * @interface ProfileCountryRiskOverrideRequest
 */
export interface ProfileCountryRiskOverrideRequest {
    /**
     * 
     * @type {CountryEnum}
     * @memberof ProfileCountryRiskOverrideRequest
     */
    'country': CountryEnum;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof ProfileCountryRiskOverrideRequest
     */
    'score': ComplianceCheckScoreEnum;
}
/**
 * Create Profile Serializer.
 * @export
 * @interface ProfileCreateRequest
 */
export interface ProfileCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfileCreateRequest
     */
    'duns_number': string;
}
/**
 * Per-profile industry (activity) risk override.  ``industry_code`` is written by SIC code; ``industry_code_detail`` is the read-only expansion for display.
 * @export
 * @interface ProfileIndustryRiskOverride
 */
export interface ProfileIndustryRiskOverride {
    /**
     * 
     * @type {string}
     * @memberof ProfileIndustryRiskOverride
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileIndustryRiskOverride
     */
    'industry_code': string;
    /**
     * 
     * @type {IndustryArticleIndustryCode}
     * @memberof ProfileIndustryRiskOverride
     */
    'industry_code_detail': IndustryArticleIndustryCode;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof ProfileIndustryRiskOverride
     */
    'score': ComplianceCheckScoreEnum;
    /**
     * 
     * @type {string}
     * @memberof ProfileIndustryRiskOverride
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileIndustryRiskOverride
     */
    'updated_at': string;
}
/**
 * Per-profile industry (activity) risk override.  ``industry_code`` is written by SIC code; ``industry_code_detail`` is the read-only expansion for display.
 * @export
 * @interface ProfileIndustryRiskOverrideRequest
 */
export interface ProfileIndustryRiskOverrideRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfileIndustryRiskOverrideRequest
     */
    'industry_code': string;
    /**
     * 
     * @type {ComplianceCheckScoreEnum}
     * @memberof ProfileIndustryRiskOverrideRequest
     */
    'score': ComplianceCheckScoreEnum;
}
/**
 * Get Profiles where a user is an part of.
 * @export
 * @interface ProfileRequest
 */
export interface ProfileRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfileRequest
     */
    'name': string;
    /**
     * 
     * @type {PatchedProfileRequestLanguage}
     * @memberof ProfileRequest
     */
    'language'?: PatchedProfileRequestLanguage | null;
}
/**
 * Profile User Group.
 * @export
 * @interface ProfileUserGroup
 */
export interface ProfileUserGroup {
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroup
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroup
     */
    'name': string;
    /**
     * Description of the purpose of this group.
     * @type {string}
     * @memberof ProfileUserGroup
     */
    'description'?: string | null;
    /**
     * Flag indicating if the group is complete profile group.
     * @type {boolean}
     * @memberof ProfileUserGroup
     */
    'is_complete_profile_group': boolean | null;
    /**
     * 
     * @type {number}
     * @memberof ProfileUserGroup
     */
    'user_count': number;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroup
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroup
     */
    'updated_at': string;
}
/**
 * Profile User Group Data Serializer.
 * @export
 * @interface ProfileUserGroupData
 */
export interface ProfileUserGroupData {
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupData
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupData
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupData
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupData
     */
    'updated_at': string;
}
/**
 * Profile User Group Data Serializer.
 * @export
 * @interface ProfileUserGroupDataRequest
 */
export interface ProfileUserGroupDataRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupDataRequest
     */
    'external_id': string;
}
/**
 * Profile User Group.
 * @export
 * @interface ProfileUserGroupRequest
 */
export interface ProfileUserGroupRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupRequest
     */
    'name': string;
    /**
     * Description of the purpose of this group.
     * @type {string}
     * @memberof ProfileUserGroupRequest
     */
    'description'?: string | null;
}
/**
 * Profile User Group User.
 * @export
 * @interface ProfileUserGroupUser
 */
export interface ProfileUserGroupUser {
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupUser
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupUser
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupUser
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupUser
     */
    'last_name': string;
}
/**
 * Profile User Group User.
 * @export
 * @interface ProfileUserGroupUserRequest
 */
export interface ProfileUserGroupUserRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfileUserGroupUserRequest
     */
    'external_id'?: string;
}
/**
 * Get Profiles of a specific User.
 * @export
 * @interface ProfileUserList
 */
export interface ProfileUserList {
    /**
     * 
     * @type {UserList}
     * @memberof ProfileUserList
     */
    'user': UserList;
    /**
     * Flag indicating if the user has admin permissions.
     * @type {boolean}
     * @memberof ProfileUserList
     */
    'is_admin'?: boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof ProfileUserList
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserList
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof ProfileUserList
     */
    'updated_at': string;
}
/**
 * Portfolio Registration Serializer.  Serializer used for registering a new company.
 * @export
 * @interface Registration
 */
export interface Registration {
    /**
     * 
     * @type {string}
     * @memberof Registration
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof Registration
     */
    'duns_number'?: string | null;
    /**
     * DUNS number the system resolved to for this registration — either the user-supplied duns_number or the DUNS returned by the DNB Match API. Used as the canonical DUNS for company creation.
     * @type {string}
     * @memberof Registration
     */
    'resolved_duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof Registration
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof Registration
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof Registration
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * Street address used to disambiguate match candidates. Sent to D&B Match as streetAddressLine1.
     * @type {string}
     * @memberof Registration
     */
    'street_address'?: string | null;
    /**
     * Postal / ZIP code used to disambiguate match candidates.
     * @type {string}
     * @memberof Registration
     */
    'postal_code'?: string | null;
    /**
     * City / locality used to disambiguate match candidates.
     * @type {string}
     * @memberof Registration
     */
    'address_locality'?: string | null;
    /**
     * State / province / region used to disambiguate match candidates.
     * @type {string}
     * @memberof Registration
     */
    'address_region'?: string | null;
    /**
     * Email company report about this registration
     * @type {boolean}
     * @memberof Registration
     */
    'email_company_report'?: boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof Registration
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {BatchCompanyRegistrationCompany}
     * @memberof Registration
     */
    'company'?: BatchCompanyRegistrationCompany | null;
    /**
     * Datestamp on when the registration was complete. If failed this is empty.
     * @type {string}
     * @memberof Registration
     */
    'finished_at': string | null;
    /**
     * 
     * @type {Status5e0Enum}
     * @memberof Registration
     */
    'status': Status5e0Enum;
    /**
     * Get Registration Status text.
     * @type {string}
     * @memberof Registration
     */
    'status_text': string;
    /**
     * 
     * @type {number}
     * @memberof Registration
     */
    'progress': number;
    /**
     * Datestamp on when the registration was failed, if succeeded this is empty
     * @type {string}
     * @memberof Registration
     */
    'failed_at': string | null;
    /**
     * If enabled the registration will be processed with priority.
     * @type {boolean}
     * @memberof Registration
     */
    'is_priority': boolean;
}
/**
 * Custom Serializer for the RegistrationNumber Model.
 * @export
 * @interface RegistrationNumber
 */
export interface RegistrationNumber {
    /**
     * 
     * @type {number}
     * @memberof RegistrationNumber
     */
    'id': number;
    /**
     * Get Translated Description of Registration Number.
     * @type {string}
     * @memberof RegistrationNumber
     */
    'description': string;
    /**
     * 
     * @type {string}
     * @memberof RegistrationNumber
     */
    'number': string;
    /**
     * Dun and Bradstreet type code for the source of thisRegistration number.
     * @type {number}
     * @memberof RegistrationNumber
     */
    'type': number;
}
/**
 * Custom Serializer for the RegistrationNumber Model.
 * @export
 * @interface RegistrationNumberRequest
 */
export interface RegistrationNumberRequest {
    /**
     * 
     * @type {string}
     * @memberof RegistrationNumberRequest
     */
    'number': string;
    /**
     * Dun and Bradstreet type code for the source of thisRegistration number.
     * @type {number}
     * @memberof RegistrationNumberRequest
     */
    'type': number;
}
/**
 * Portfolio Registration Serializer.  Serializer used for registering a new company.
 * @export
 * @interface RegistrationRequest
 */
export interface RegistrationRequest {
    /**
     * 
     * @type {string}
     * @memberof RegistrationRequest
     */
    'duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof RegistrationRequest
     */
    'registration_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof RegistrationRequest
     */
    'primary_name'?: string | null;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof RegistrationRequest
     */
    'country'?: ArticleDetailCountry | null;
    /**
     * Street address used to disambiguate match candidates. Sent to D&B Match as streetAddressLine1.
     * @type {string}
     * @memberof RegistrationRequest
     */
    'street_address'?: string | null;
    /**
     * Postal / ZIP code used to disambiguate match candidates.
     * @type {string}
     * @memberof RegistrationRequest
     */
    'postal_code'?: string | null;
    /**
     * City / locality used to disambiguate match candidates.
     * @type {string}
     * @memberof RegistrationRequest
     */
    'address_locality'?: string | null;
    /**
     * State / province / region used to disambiguate match candidates.
     * @type {string}
     * @memberof RegistrationRequest
     */
    'address_region'?: string | null;
    /**
     * Email company report about this registration
     * @type {boolean}
     * @memberof RegistrationRequest
     */
    'email_company_report'?: boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof RegistrationRequest
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {BatchCompanyRegistrationRequestCompany}
     * @memberof RegistrationRequest
     */
    'company'?: BatchCompanyRegistrationRequestCompany | null;
}
/**
 * * `queued_search` - Queued for search * `searching` - Searching for company in registry * `queued_registration` - Queued for registration * `registering` - Registering company * `queued_website_search` - Queued for website search * `searching_website` - Searching for company website * `searching_activity_description` - Generating company activity description * `searching_website_icon` - Searching for company website icon * `searching_directors` - Searching for directors online * `social_search` - Searching for social media websites * `generating_company_description` - Generating company description * `determine_trade_names` - Determining trade names * `searching_news` - Searching for news articles * `processing_news` - Processing news articles * `registered` - Registered * `invalid_input` - Invalid input, please check your input * `permission_denied` - Permission denied, please contact support * `company_not_found` - Company has not been found in Dun and Bradstreet registry * `expired` - Registration has been pending for too long, expired. * `cancelled` - Registration has been cancelled. * `failed` - Registration has failed, please check the error message
 * @export
 * @enum {string}
 */

export const RegistrationStatusEnum = {
    QueuedSearch: 'queued_search',
    Searching: 'searching',
    QueuedRegistration: 'queued_registration',
    Registering: 'registering',
    QueuedWebsiteSearch: 'queued_website_search',
    SearchingWebsite: 'searching_website',
    SearchingActivityDescription: 'searching_activity_description',
    SearchingWebsiteIcon: 'searching_website_icon',
    SearchingDirectors: 'searching_directors',
    SocialSearch: 'social_search',
    GeneratingCompanyDescription: 'generating_company_description',
    DetermineTradeNames: 'determine_trade_names',
    SearchingNews: 'searching_news',
    ProcessingNews: 'processing_news',
    Registered: 'registered',
    InvalidInput: 'invalid_input',
    PermissionDenied: 'permission_denied',
    CompanyNotFound: 'company_not_found',
    Expired: 'expired',
    Cancelled: 'cancelled',
    Failed: 'failed'
} as const;

export type RegistrationStatusEnum = typeof RegistrationStatusEnum[keyof typeof RegistrationStatusEnum];


/**
 * * `GENERAL` - General * `SANCTION_LIST_REQUEST` - Sanction List Request * `FEATURE_REQUEST` - Feature Request * `DATA_REQUEST` - Data Request * `BUG` - Bug
 * @export
 * @enum {string}
 */

export const RequestTypeEnum = {
    General: 'GENERAL',
    SanctionListRequest: 'SANCTION_LIST_REQUEST',
    FeatureRequest: 'FEATURE_REQUEST',
    DataRequest: 'DATA_REQUEST',
    Bug: 'BUG'
} as const;

export type RequestTypeEnum = typeof RequestTypeEnum[keyof typeof RequestTypeEnum];


/**
 * * `sanction` - Sanction * `pep` - Politically Exposed Person * `adverse_media` - Adverse media * `enforcement` - Enforcement * `govt_owned` - Government owned
 * @export
 * @enum {string}
 */

export const ResultTypeEnum = {
    Sanction: 'sanction',
    Pep: 'pep',
    AdverseMedia: 'adverse_media',
    Enforcement: 'enforcement',
    GovtOwned: 'govt_owned'
} as const;

export type ResultTypeEnum = typeof ResultTypeEnum[keyof typeof ResultTypeEnum];


/**
 * * `sanction` - Sanction * `pep` - Politically Exposed Person * `adverse_media` - Adverse media * `enforcement` - Enforcement * `govt_owned` - Government owned
 * @export
 * @enum {string}
 */

export const ResultTypesEnum = {
    Sanction: 'sanction',
    Pep: 'pep',
    AdverseMedia: 'adverse_media',
    Enforcement: 'enforcement',
    GovtOwned: 'govt_owned'
} as const;

export type ResultTypesEnum = typeof ResultTypesEnum[keyof typeof ResultTypesEnum];


/**
 * * `sanction` - Sanction * `pep` - Politically Exposed Person * `adverse_media` - Adverse media * `enforcement` - Enforcement * `govt_owned` - Government owned
 * @export
 * @enum {string}
 */

export const RiskCategoryEnum = {
    Sanction: 'sanction',
    Pep: 'pep',
    AdverseMedia: 'adverse_media',
    Enforcement: 'enforcement',
    GovtOwned: 'govt_owned'
} as const;

export type RiskCategoryEnum = typeof RiskCategoryEnum[keyof typeof RiskCategoryEnum];


/**
 * * `BOARD` - Board * `MANAGEMENT` - Management * `OFFICER` - Officer / Signatory * `OTHER` - Other
 * @export
 * @enum {string}
 */

export const RoleGroupEnum = {
    Board: 'BOARD',
    Management: 'MANAGEMENT',
    Officer: 'OFFICER',
    Other: 'OTHER'
} as const;

export type RoleGroupEnum = typeof RoleGroupEnum[keyof typeof RoleGroupEnum];


/**
 * * `disabled` - Disabled * `pending` - Pending * `active` - Active * `paused` - Paused * `deactivating` - Deactivating
 * @export
 * @enum {string}
 */

export const SanctionMonitoringStatusEnum = {
    Disabled: 'disabled',
    Pending: 'pending',
    Active: 'active',
    Paused: 'paused',
    Deactivating: 'deactivating'
} as const;

export type SanctionMonitoringStatusEnum = typeof SanctionMonitoringStatusEnum[keyof typeof SanctionMonitoringStatusEnum];


/**
 * A sanctions provider available for exclusion (read-only).
 * @export
 * @interface SanctionProvider
 */
export interface SanctionProvider {
    /**
     * 
     * @type {string}
     * @memberof SanctionProvider
     */
    'key': string;
    /**
     * 
     * @type {string}
     * @memberof SanctionProvider
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof SanctionProvider
     */
    'authority': string;
    /**
     * 
     * @type {string}
     * @memberof SanctionProvider
     */
    'jurisdiction_country': string | null;
    /**
     * 
     * @type {number}
     * @memberof SanctionProvider
     */
    'active_listing_count': number;
    /**
     * 
     * @type {string}
     * @memberof SanctionProvider
     */
    'last_ingestion': string | null;
}
/**
 * Saved Article Filter Serializer.
 * @export
 * @interface SavedArticleFilter
 */
export interface SavedArticleFilter {
    /**
     * 
     * @type {string}
     * @memberof SavedArticleFilter
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof SavedArticleFilter
     */
    'name': string;
    /**
     * 
     * @type {ArticleFilters}
     * @memberof SavedArticleFilter
     */
    'filters': ArticleFilters;
    /**
     * 
     * @type {PatchedSavedArticleFilterRequestDefaultPermission}
     * @memberof SavedArticleFilter
     */
    'default_permission'?: PatchedSavedArticleFilterRequestDefaultPermission | null;
    /**
     * 
     * @type {string}
     * @memberof SavedArticleFilter
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof SavedArticleFilter
     */
    'updated_at': string;
}
/**
 * Saved Article Filter Serializer.
 * @export
 * @interface SavedArticleFilterRequest
 */
export interface SavedArticleFilterRequest {
    /**
     * 
     * @type {string}
     * @memberof SavedArticleFilterRequest
     */
    'name': string;
    /**
     * 
     * @type {ArticleFiltersRequest}
     * @memberof SavedArticleFilterRequest
     */
    'filters': ArticleFiltersRequest;
    /**
     * 
     * @type {PatchedSavedArticleFilterRequestDefaultPermission}
     * @memberof SavedArticleFilterRequest
     */
    'default_permission'?: PatchedSavedArticleFilterRequestDefaultPermission | null;
}
/**
 * Serializer for Article Filters Users.
 * @export
 * @interface SavedArticleFilterUser
 */
export interface SavedArticleFilterUser {
    /**
     * 
     * @type {UserWrite}
     * @memberof SavedArticleFilterUser
     */
    'user': UserWrite;
    /**
     * 
     * @type {Permission506Enum}
     * @memberof SavedArticleFilterUser
     */
    'permission': Permission506Enum;
}
/**
 * Serializer for Article Filters Users.
 * @export
 * @interface SavedArticleFilterUserRequest
 */
export interface SavedArticleFilterUserRequest {
    /**
     * 
     * @type {UserWriteRequest}
     * @memberof SavedArticleFilterUserRequest
     */
    'user': UserWriteRequest;
    /**
     * 
     * @type {Permission506Enum}
     * @memberof SavedArticleFilterUserRequest
     */
    'permission': Permission506Enum;
}
/**
 * Serializer for Article Filters Update Users.
 * @export
 * @interface SavedArticleFilterUserUpdate
 */
export interface SavedArticleFilterUserUpdate {
    /**
     * 
     * @type {Permission506Enum}
     * @memberof SavedArticleFilterUserUpdate
     */
    'permission': Permission506Enum;
}
/**
 * Serializer for Article Filters Update Users.
 * @export
 * @interface SavedArticleFilterUserUpdateRequest
 */
export interface SavedArticleFilterUserUpdateRequest {
    /**
     * 
     * @type {Permission506Enum}
     * @memberof SavedArticleFilterUserUpdateRequest
     */
    'permission': Permission506Enum;
}
/**
 * * `IMO` - Imo
 * @export
 * @enum {string}
 */

export const SchemeEnum = {
    Imo: 'IMO'
} as const;

export type SchemeEnum = typeof SchemeEnum[keyof typeof SchemeEnum];


/**
 * * `LOCAL` - Local Corporate Linkage * `COUNTRY` - Country Corporate Linkage * `GLOBAL` - Global Corporate Linkage
 * @export
 * @enum {string}
 */

export const ScopeEnum = {
    Local: 'LOCAL',
    Country: 'COUNTRY',
    Global: 'GLOBAL'
} as const;

export type ScopeEnum = typeof ScopeEnum[keyof typeof ScopeEnum];


/**
 * * `activity` - activity * `adverse_media` - adverse_media * `pep` - pep * `sanctions` - sanctions
 * @export
 * @enum {string}
 */

export const SignalEnum = {
    Activity: 'activity',
    AdverseMedia: 'adverse_media',
    Pep: 'pep',
    Sanctions: 'sanctions'
} as const;

export type SignalEnum = typeof SignalEnum[keyof typeof SignalEnum];


/**
 * * `WHOLLY_OWNED` - Wholly state-owned * `MAJORITY_OWNED` - Majority state-owned * `MINORITY_OWNED` - Minority state-owned * `STATE_CONTROLLED` - State-controlled * `GOVERNMENT_LINKED` - Government-linked
 * @export
 * @enum {string}
 */

export const SoeRelationshipTypeEnum = {
    WhollyOwned: 'WHOLLY_OWNED',
    MajorityOwned: 'MAJORITY_OWNED',
    MinorityOwned: 'MINORITY_OWNED',
    StateControlled: 'STATE_CONTROLLED',
    GovernmentLinked: 'GOVERNMENT_LINKED'
} as const;

export type SoeRelationshipTypeEnum = typeof SoeRelationshipTypeEnum[keyof typeof SoeRelationshipTypeEnum];


/**
 * Serializer for Source Information.
 * @export
 * @interface Source
 */
export interface Source {
    /**
     * 
     * @type {string}
     * @memberof Source
     */
    'domain': string;
    /**
     * 
     * @type {string}
     * @memberof Source
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof Source
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof Source
     */
    'social_references'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof Source
     */
    'paywall'?: boolean;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof Source
     */
    'country'?: ArticleDetailCountry | null;
}
/**
 * * `settings` - Settings * `keyword_set` - Adverse media keywords * `risk_override` - Risk override
 * @export
 * @enum {string}
 */

export const SourceEnum = {
    Settings: 'settings',
    KeywordSet: 'keyword_set',
    RiskOverride: 'risk_override'
} as const;

export type SourceEnum = typeof SourceEnum[keyof typeof SourceEnum];


/**
 * Serializer for Source Information.
 * @export
 * @interface SourceRequest
 */
export interface SourceRequest {
    /**
     * 
     * @type {string}
     * @memberof SourceRequest
     */
    'domain': string;
    /**
     * 
     * @type {string}
     * @memberof SourceRequest
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof SourceRequest
     */
    'name': string;
    /**
     * 
     * @type {number}
     * @memberof SourceRequest
     */
    'social_references'?: number;
    /**
     * 
     * @type {boolean}
     * @memberof SourceRequest
     */
    'paywall'?: boolean;
    /**
     * 
     * @type {ArticleDetailCountry}
     * @memberof SourceRequest
     */
    'country'?: ArticleDetailCountry | null;
}
/**
 * * `news` - News * `icij` - ICIJ * `enforcement` - Enforcement
 * @export
 * @enum {string}
 */

export const SourceTypeEnum = {
    News: 'news',
    Icij: 'icij',
    Enforcement: 'enforcement'
} as const;

export type SourceTypeEnum = typeof SourceTypeEnum[keyof typeof SourceTypeEnum];


/**
 * * `pending` - Pending * `in_progress` - In Progress * `failed` - Failed * `finished` - Finished
 * @export
 * @enum {string}
 */

export const Status180Enum = {
    Pending: 'pending',
    InProgress: 'in_progress',
    Failed: 'failed',
    Finished: 'finished'
} as const;

export type Status180Enum = typeof Status180Enum[keyof typeof Status180Enum];


/**
 * * `queued_search` - Queued for search * `searching` - Searching for company in registry * `queued_registration` - Queued for registration * `registering` - Registering company * `queued_website_search` - Queued for website search * `searching_website` - Searching for company website * `searching_activity_description` - Generating company activity description * `searching_website_icon` - Searching for company website icon * `searching_directors` - Searching for directors online * `social_search` - Searching for social media websites * `generating_company_description` - Generating company description * `determine_trade_names` - Determining trade names * `searching_news` - Searching for news articles * `processing_news` - Processing news articles * `registered` - Registered * `invalid_input` - Invalid input, please check your input * `permission_denied` - Permission denied, please contact support * `company_not_found` - Company has not been found in Dun and Bradstreet registry * `expired` - Registration has been pending for too long, expired. * `cancelled` - Registration has been cancelled. * `failed` - Registration has failed, please check the error message
 * @export
 * @enum {string}
 */

export const Status5e0Enum = {
    QueuedSearch: 'queued_search',
    Searching: 'searching',
    QueuedRegistration: 'queued_registration',
    Registering: 'registering',
    QueuedWebsiteSearch: 'queued_website_search',
    SearchingWebsite: 'searching_website',
    SearchingActivityDescription: 'searching_activity_description',
    SearchingWebsiteIcon: 'searching_website_icon',
    SearchingDirectors: 'searching_directors',
    SocialSearch: 'social_search',
    GeneratingCompanyDescription: 'generating_company_description',
    DetermineTradeNames: 'determine_trade_names',
    SearchingNews: 'searching_news',
    ProcessingNews: 'processing_news',
    Registered: 'registered',
    InvalidInput: 'invalid_input',
    PermissionDenied: 'permission_denied',
    CompanyNotFound: 'company_not_found',
    Expired: 'expired',
    Cancelled: 'cancelled',
    Failed: 'failed'
} as const;

export type Status5e0Enum = typeof Status5e0Enum[keyof typeof Status5e0Enum];


/**
 * Serializer for snippet of Sub Article.
 * @export
 * @interface SubArticle
 */
export interface SubArticle {
    /**
     * 
     * @type {string}
     * @memberof SubArticle
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof SubArticle
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof SubArticle
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof SubArticle
     */
    'title_en': string;
    /**
     * Get Title Translated.
     * @type {string}
     * @memberof SubArticle
     */
    'title_translated': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof SubArticle
     */
    'snippet': string;
    /**
     * Get snippet if allowed for source.
     * @type {string}
     * @memberof SubArticle
     */
    'snippet_en': string;
    /**
     * Get Snippet Translated.
     * @type {string}
     * @memberof SubArticle
     */
    'snippet_translated': string;
    /**
     * Get Image URL if allowed for source.
     * @type {string}
     * @memberof SubArticle
     */
    'image_url': string;
}
/**
 * Serializer for snippet of Sub Article.
 * @export
 * @interface SubArticleRequest
 */
export interface SubArticleRequest {
    /**
     * 
     * @type {string}
     * @memberof SubArticleRequest
     */
    'external_id'?: string;
    /**
     * 
     * @type {string}
     * @memberof SubArticleRequest
     */
    'url': string;
    /**
     * 
     * @type {string}
     * @memberof SubArticleRequest
     */
    'title': string;
    /**
     * 
     * @type {string}
     * @memberof SubArticleRequest
     */
    'title_en': string;
}
/**
 * Read serializer for Support Requests.
 * @export
 * @interface SupportRequest
 */
export interface SupportRequest {
    /**
     * 
     * @type {string}
     * @memberof SupportRequest
     */
    'external_id': string;
    /**
     * 
     * @type {RequestTypeEnum}
     * @memberof SupportRequest
     */
    'request_type': RequestTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof SupportRequest
     */
    'subject': string;
    /**
     * 
     * @type {SupportStatusEnum}
     * @memberof SupportRequest
     */
    'status': SupportStatusEnum;
    /**
     * 
     * @type {SupportRequestUser}
     * @memberof SupportRequest
     */
    'user': SupportRequestUser | null;
    /**
     * 
     * @type {string}
     * @memberof SupportRequest
     */
    'profile': string | null;
    /**
     * 
     * @type {string}
     * @memberof SupportRequest
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof SupportRequest
     */
    'updated_at': string;
}
/**
 * Serializer for a single Support Request comment.
 * @export
 * @interface SupportRequestComment
 */
export interface SupportRequestComment {
    /**
     * 
     * @type {string}
     * @memberof SupportRequestComment
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof SupportRequestComment
     */
    'comment': string;
    /**
     * 
     * @type {SupportRequestUser}
     * @memberof SupportRequestComment
     */
    'author': SupportRequestUser | null;
    /**
     * 
     * @type {AuthorTypeEnum}
     * @memberof SupportRequestComment
     */
    'author_type': AuthorTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof SupportRequestComment
     */
    'is_initial': boolean;
    /**
     * 
     * @type {string}
     * @memberof SupportRequestComment
     */
    'created_at': string;
}
/**
 * Serializer for a single Support Request comment.
 * @export
 * @interface SupportRequestCommentRequest
 */
export interface SupportRequestCommentRequest {
    /**
     * 
     * @type {string}
     * @memberof SupportRequestCommentRequest
     */
    'comment': string;
}
/**
 * Write serializer for creating a Support Request and opening comment.
 * @export
 * @interface SupportRequestCreateRequest
 */
export interface SupportRequestCreateRequest {
    /**
     * 
     * @type {RequestTypeEnum}
     * @memberof SupportRequestCreateRequest
     */
    'request_type'?: RequestTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof SupportRequestCreateRequest
     */
    'subject': string;
    /**
     * 
     * @type {string}
     * @memberof SupportRequestCreateRequest
     */
    'message': string;
}
/**
 * 
 * @export
 * @interface SupportRequestUser
 */
export interface SupportRequestUser {
    /**
     * 
     * @type {string}
     * @memberof SupportRequestUser
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof SupportRequestUser
     */
    'last_name'?: string;
}
/**
 * Minimal user identity exposed inside private support threads.
 * @export
 * @interface SupportRequestUserRequest
 */
export interface SupportRequestUserRequest {
    /**
     * 
     * @type {string}
     * @memberof SupportRequestUserRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof SupportRequestUserRequest
     */
    'last_name'?: string;
}
/**
 * * `OPEN` - Open * `IN_PROGRESS` - In progress * `RESOLVED` - Resolved
 * @export
 * @enum {string}
 */

export const SupportStatusEnum = {
    Open: 'OPEN',
    InProgress: 'IN_PROGRESS',
    Resolved: 'RESOLVED'
} as const;

export type SupportStatusEnum = typeof SupportStatusEnum[keyof typeof SupportStatusEnum];


/**
 * Serializer for sync single company request.
 * @export
 * @interface SyncCompanyRequestRequest
 */
export interface SyncCompanyRequestRequest {
    /**
     * Company ID in external system
     * @type {string}
     * @memberof SyncCompanyRequestRequest
     */
    'external_id': string;
}
/**
 * Serializer for triggering sync.
 * @export
 * @interface SyncTriggerRequest
 */
export interface SyncTriggerRequest {
    /**
     * 
     * @type {SyncTypeEnum}
     * @memberof SyncTriggerRequest
     */
    'sync_type'?: SyncTypeEnum;
    /**
     * Maximum number of companies to sync
     * @type {number}
     * @memberof SyncTriggerRequest
     */
    'limit'?: number;
}
/**
 * * `FULL` - Full Sync * `INCREMENTAL` - Incremental Sync * `MANUAL` - Manual Sync * `SCHEDULED` - Scheduled Sync
 * @export
 * @enum {string}
 */

export const SyncTypeEnum = {
    Full: 'FULL',
    Incremental: 'INCREMENTAL',
    Manual: 'MANUAL',
    Scheduled: 'SCHEDULED'
} as const;

export type SyncTypeEnum = typeof SyncTypeEnum[keyof typeof SyncTypeEnum];


/**
 * * `SALESFORCE` - Salesforce * `HUBSPOT` - HubSpot * `SAP` - SAP * `DYNAMICS` - Microsoft Dynamics * `NETSUITE` - NetSuite * `CUSTOM` - Custom System
 * @export
 * @enum {string}
 */

export const SystemTypeEnum = {
    Salesforce: 'SALESFORCE',
    Hubspot: 'HUBSPOT',
    Sap: 'SAP',
    Dynamics: 'DYNAMICS',
    Netsuite: 'NETSUITE',
    Custom: 'CUSTOM'
} as const;

export type SystemTypeEnum = typeof SystemTypeEnum[keyof typeof SystemTypeEnum];


/**
 * Serializer for throttling error.
 * @export
 * @interface ThrottlingError
 */
export interface ThrottlingError {
    /**
     * 
     * @type {string}
     * @memberof ThrottlingError
     */
    'detail': string;
}
/**
 * * `Africa/Abidjan` - Africa/Abidjan * `Africa/Accra` - Africa/Accra * `Africa/Addis_Ababa` - Africa/Addis_Ababa * `Africa/Algiers` - Africa/Algiers * `Africa/Asmara` - Africa/Asmara * `Africa/Asmera` - Africa/Asmera * `Africa/Bamako` - Africa/Bamako * `Africa/Bangui` - Africa/Bangui * `Africa/Banjul` - Africa/Banjul * `Africa/Bissau` - Africa/Bissau * `Africa/Blantyre` - Africa/Blantyre * `Africa/Brazzaville` - Africa/Brazzaville * `Africa/Bujumbura` - Africa/Bujumbura * `Africa/Cairo` - Africa/Cairo * `Africa/Casablanca` - Africa/Casablanca * `Africa/Ceuta` - Africa/Ceuta * `Africa/Conakry` - Africa/Conakry * `Africa/Dakar` - Africa/Dakar * `Africa/Dar_es_Salaam` - Africa/Dar_es_Salaam * `Africa/Djibouti` - Africa/Djibouti * `Africa/Douala` - Africa/Douala * `Africa/El_Aaiun` - Africa/El_Aaiun * `Africa/Freetown` - Africa/Freetown * `Africa/Gaborone` - Africa/Gaborone * `Africa/Harare` - Africa/Harare * `Africa/Johannesburg` - Africa/Johannesburg * `Africa/Juba` - Africa/Juba * `Africa/Kampala` - Africa/Kampala * `Africa/Khartoum` - Africa/Khartoum * `Africa/Kigali` - Africa/Kigali * `Africa/Kinshasa` - Africa/Kinshasa * `Africa/Lagos` - Africa/Lagos * `Africa/Libreville` - Africa/Libreville * `Africa/Lome` - Africa/Lome * `Africa/Luanda` - Africa/Luanda * `Africa/Lubumbashi` - Africa/Lubumbashi * `Africa/Lusaka` - Africa/Lusaka * `Africa/Malabo` - Africa/Malabo * `Africa/Maputo` - Africa/Maputo * `Africa/Maseru` - Africa/Maseru * `Africa/Mbabane` - Africa/Mbabane * `Africa/Mogadishu` - Africa/Mogadishu * `Africa/Monrovia` - Africa/Monrovia * `Africa/Nairobi` - Africa/Nairobi * `Africa/Ndjamena` - Africa/Ndjamena * `Africa/Niamey` - Africa/Niamey * `Africa/Nouakchott` - Africa/Nouakchott * `Africa/Ouagadougou` - Africa/Ouagadougou * `Africa/Porto-Novo` - Africa/Porto-Novo * `Africa/Sao_Tome` - Africa/Sao_Tome * `Africa/Timbuktu` - Africa/Timbuktu * `Africa/Tripoli` - Africa/Tripoli * `Africa/Tunis` - Africa/Tunis * `Africa/Windhoek` - Africa/Windhoek * `America/Adak` - America/Adak * `America/Anchorage` - America/Anchorage * `America/Anguilla` - America/Anguilla * `America/Antigua` - America/Antigua * `America/Araguaina` - America/Araguaina * `America/Argentina/Buenos_Aires` - America/Argentina/Buenos_Aires * `America/Argentina/Catamarca` - America/Argentina/Catamarca * `America/Argentina/ComodRivadavia` - America/Argentina/ComodRivadavia * `America/Argentina/Cordoba` - America/Argentina/Cordoba * `America/Argentina/Jujuy` - America/Argentina/Jujuy * `America/Argentina/La_Rioja` - America/Argentina/La_Rioja * `America/Argentina/Mendoza` - America/Argentina/Mendoza * `America/Argentina/Rio_Gallegos` - America/Argentina/Rio_Gallegos * `America/Argentina/Salta` - America/Argentina/Salta * `America/Argentina/San_Juan` - America/Argentina/San_Juan * `America/Argentina/San_Luis` - America/Argentina/San_Luis * `America/Argentina/Tucuman` - America/Argentina/Tucuman * `America/Argentina/Ushuaia` - America/Argentina/Ushuaia * `America/Aruba` - America/Aruba * `America/Asuncion` - America/Asuncion * `America/Atikokan` - America/Atikokan * `America/Atka` - America/Atka * `America/Bahia` - America/Bahia * `America/Bahia_Banderas` - America/Bahia_Banderas * `America/Barbados` - America/Barbados * `America/Belem` - America/Belem * `America/Belize` - America/Belize * `America/Blanc-Sablon` - America/Blanc-Sablon * `America/Boa_Vista` - America/Boa_Vista * `America/Bogota` - America/Bogota * `America/Boise` - America/Boise * `America/Buenos_Aires` - America/Buenos_Aires * `America/Cambridge_Bay` - America/Cambridge_Bay * `America/Campo_Grande` - America/Campo_Grande * `America/Cancun` - America/Cancun * `America/Caracas` - America/Caracas * `America/Catamarca` - America/Catamarca * `America/Cayenne` - America/Cayenne * `America/Cayman` - America/Cayman * `America/Chicago` - America/Chicago * `America/Chihuahua` - America/Chihuahua * `America/Ciudad_Juarez` - America/Ciudad_Juarez * `America/Coral_Harbour` - America/Coral_Harbour * `America/Cordoba` - America/Cordoba * `America/Costa_Rica` - America/Costa_Rica * `America/Coyhaique` - America/Coyhaique * `America/Creston` - America/Creston * `America/Cuiaba` - America/Cuiaba * `America/Curacao` - America/Curacao * `America/Danmarkshavn` - America/Danmarkshavn * `America/Dawson` - America/Dawson * `America/Dawson_Creek` - America/Dawson_Creek * `America/Denver` - America/Denver * `America/Detroit` - America/Detroit * `America/Dominica` - America/Dominica * `America/Edmonton` - America/Edmonton * `America/Eirunepe` - America/Eirunepe * `America/El_Salvador` - America/El_Salvador * `America/Ensenada` - America/Ensenada * `America/Fort_Nelson` - America/Fort_Nelson * `America/Fort_Wayne` - America/Fort_Wayne * `America/Fortaleza` - America/Fortaleza * `America/Glace_Bay` - America/Glace_Bay * `America/Godthab` - America/Godthab * `America/Goose_Bay` - America/Goose_Bay * `America/Grand_Turk` - America/Grand_Turk * `America/Grenada` - America/Grenada * `America/Guadeloupe` - America/Guadeloupe * `America/Guatemala` - America/Guatemala * `America/Guayaquil` - America/Guayaquil * `America/Guyana` - America/Guyana * `America/Halifax` - America/Halifax * `America/Havana` - America/Havana * `America/Hermosillo` - America/Hermosillo * `America/Indiana/Indianapolis` - America/Indiana/Indianapolis * `America/Indiana/Knox` - America/Indiana/Knox * `America/Indiana/Marengo` - America/Indiana/Marengo * `America/Indiana/Petersburg` - America/Indiana/Petersburg * `America/Indiana/Tell_City` - America/Indiana/Tell_City * `America/Indiana/Vevay` - America/Indiana/Vevay * `America/Indiana/Vincennes` - America/Indiana/Vincennes * `America/Indiana/Winamac` - America/Indiana/Winamac * `America/Indianapolis` - America/Indianapolis * `America/Inuvik` - America/Inuvik * `America/Iqaluit` - America/Iqaluit * `America/Jamaica` - America/Jamaica * `America/Jujuy` - America/Jujuy * `America/Juneau` - America/Juneau * `America/Kentucky/Louisville` - America/Kentucky/Louisville * `America/Kentucky/Monticello` - America/Kentucky/Monticello * `America/Knox_IN` - America/Knox_IN * `America/Kralendijk` - America/Kralendijk * `America/La_Paz` - America/La_Paz * `America/Lima` - America/Lima * `America/Los_Angeles` - America/Los_Angeles * `America/Louisville` - America/Louisville * `America/Lower_Princes` - America/Lower_Princes * `America/Maceio` - America/Maceio * `America/Managua` - America/Managua * `America/Manaus` - America/Manaus * `America/Marigot` - America/Marigot * `America/Martinique` - America/Martinique * `America/Matamoros` - America/Matamoros * `America/Mazatlan` - America/Mazatlan * `America/Mendoza` - America/Mendoza * `America/Menominee` - America/Menominee * `America/Merida` - America/Merida * `America/Metlakatla` - America/Metlakatla * `America/Mexico_City` - America/Mexico_City * `America/Miquelon` - America/Miquelon * `America/Moncton` - America/Moncton * `America/Monterrey` - America/Monterrey * `America/Montevideo` - America/Montevideo * `America/Montreal` - America/Montreal * `America/Montserrat` - America/Montserrat * `America/Nassau` - America/Nassau * `America/New_York` - America/New_York * `America/Nipigon` - America/Nipigon * `America/Nome` - America/Nome * `America/Noronha` - America/Noronha * `America/North_Dakota/Beulah` - America/North_Dakota/Beulah * `America/North_Dakota/Center` - America/North_Dakota/Center * `America/North_Dakota/New_Salem` - America/North_Dakota/New_Salem * `America/Nuuk` - America/Nuuk * `America/Ojinaga` - America/Ojinaga * `America/Panama` - America/Panama * `America/Pangnirtung` - America/Pangnirtung * `America/Paramaribo` - America/Paramaribo * `America/Phoenix` - America/Phoenix * `America/Port-au-Prince` - America/Port-au-Prince * `America/Port_of_Spain` - America/Port_of_Spain * `America/Porto_Acre` - America/Porto_Acre * `America/Porto_Velho` - America/Porto_Velho * `America/Puerto_Rico` - America/Puerto_Rico * `America/Punta_Arenas` - America/Punta_Arenas * `America/Rainy_River` - America/Rainy_River * `America/Rankin_Inlet` - America/Rankin_Inlet * `America/Recife` - America/Recife * `America/Regina` - America/Regina * `America/Resolute` - America/Resolute * `America/Rio_Branco` - America/Rio_Branco * `America/Rosario` - America/Rosario * `America/Santa_Isabel` - America/Santa_Isabel * `America/Santarem` - America/Santarem * `America/Santiago` - America/Santiago * `America/Santo_Domingo` - America/Santo_Domingo * `America/Sao_Paulo` - America/Sao_Paulo * `America/Scoresbysund` - America/Scoresbysund * `America/Shiprock` - America/Shiprock * `America/Sitka` - America/Sitka * `America/St_Barthelemy` - America/St_Barthelemy * `America/St_Johns` - America/St_Johns * `America/St_Kitts` - America/St_Kitts * `America/St_Lucia` - America/St_Lucia * `America/St_Thomas` - America/St_Thomas * `America/St_Vincent` - America/St_Vincent * `America/Swift_Current` - America/Swift_Current * `America/Tegucigalpa` - America/Tegucigalpa * `America/Thule` - America/Thule * `America/Thunder_Bay` - America/Thunder_Bay * `America/Tijuana` - America/Tijuana * `America/Toronto` - America/Toronto * `America/Tortola` - America/Tortola * `America/Vancouver` - America/Vancouver * `America/Virgin` - America/Virgin * `America/Whitehorse` - America/Whitehorse * `America/Winnipeg` - America/Winnipeg * `America/Yakutat` - America/Yakutat * `America/Yellowknife` - America/Yellowknife * `Antarctica/Casey` - Antarctica/Casey * `Antarctica/Davis` - Antarctica/Davis * `Antarctica/DumontDUrville` - Antarctica/DumontDUrville * `Antarctica/Macquarie` - Antarctica/Macquarie * `Antarctica/Mawson` - Antarctica/Mawson * `Antarctica/McMurdo` - Antarctica/McMurdo * `Antarctica/Palmer` - Antarctica/Palmer * `Antarctica/Rothera` - Antarctica/Rothera * `Antarctica/South_Pole` - Antarctica/South_Pole * `Antarctica/Syowa` - Antarctica/Syowa * `Antarctica/Troll` - Antarctica/Troll * `Antarctica/Vostok` - Antarctica/Vostok * `Arctic/Longyearbyen` - Arctic/Longyearbyen * `Asia/Aden` - Asia/Aden * `Asia/Almaty` - Asia/Almaty * `Asia/Amman` - Asia/Amman * `Asia/Anadyr` - Asia/Anadyr * `Asia/Aqtau` - Asia/Aqtau * `Asia/Aqtobe` - Asia/Aqtobe * `Asia/Ashgabat` - Asia/Ashgabat * `Asia/Ashkhabad` - Asia/Ashkhabad * `Asia/Atyrau` - Asia/Atyrau * `Asia/Baghdad` - Asia/Baghdad * `Asia/Bahrain` - Asia/Bahrain * `Asia/Baku` - Asia/Baku * `Asia/Bangkok` - Asia/Bangkok * `Asia/Barnaul` - Asia/Barnaul * `Asia/Beirut` - Asia/Beirut * `Asia/Bishkek` - Asia/Bishkek * `Asia/Brunei` - Asia/Brunei * `Asia/Calcutta` - Asia/Calcutta * `Asia/Chita` - Asia/Chita * `Asia/Choibalsan` - Asia/Choibalsan * `Asia/Chongqing` - Asia/Chongqing * `Asia/Chungking` - Asia/Chungking * `Asia/Colombo` - Asia/Colombo * `Asia/Dacca` - Asia/Dacca * `Asia/Damascus` - Asia/Damascus * `Asia/Dhaka` - Asia/Dhaka * `Asia/Dili` - Asia/Dili * `Asia/Dubai` - Asia/Dubai * `Asia/Dushanbe` - Asia/Dushanbe * `Asia/Famagusta` - Asia/Famagusta * `Asia/Gaza` - Asia/Gaza * `Asia/Harbin` - Asia/Harbin * `Asia/Hebron` - Asia/Hebron * `Asia/Ho_Chi_Minh` - Asia/Ho_Chi_Minh * `Asia/Hong_Kong` - Asia/Hong_Kong * `Asia/Hovd` - Asia/Hovd * `Asia/Irkutsk` - Asia/Irkutsk * `Asia/Istanbul` - Asia/Istanbul * `Asia/Jakarta` - Asia/Jakarta * `Asia/Jayapura` - Asia/Jayapura * `Asia/Jerusalem` - Asia/Jerusalem * `Asia/Kabul` - Asia/Kabul * `Asia/Kamchatka` - Asia/Kamchatka * `Asia/Karachi` - Asia/Karachi * `Asia/Kashgar` - Asia/Kashgar * `Asia/Kathmandu` - Asia/Kathmandu * `Asia/Katmandu` - Asia/Katmandu * `Asia/Khandyga` - Asia/Khandyga * `Asia/Kolkata` - Asia/Kolkata * `Asia/Krasnoyarsk` - Asia/Krasnoyarsk * `Asia/Kuala_Lumpur` - Asia/Kuala_Lumpur * `Asia/Kuching` - Asia/Kuching * `Asia/Kuwait` - Asia/Kuwait * `Asia/Macao` - Asia/Macao * `Asia/Macau` - Asia/Macau * `Asia/Magadan` - Asia/Magadan * `Asia/Makassar` - Asia/Makassar * `Asia/Manila` - Asia/Manila * `Asia/Muscat` - Asia/Muscat * `Asia/Nicosia` - Asia/Nicosia * `Asia/Novokuznetsk` - Asia/Novokuznetsk * `Asia/Novosibirsk` - Asia/Novosibirsk * `Asia/Omsk` - Asia/Omsk * `Asia/Oral` - Asia/Oral * `Asia/Phnom_Penh` - Asia/Phnom_Penh * `Asia/Pontianak` - Asia/Pontianak * `Asia/Pyongyang` - Asia/Pyongyang * `Asia/Qatar` - Asia/Qatar * `Asia/Qostanay` - Asia/Qostanay * `Asia/Qyzylorda` - Asia/Qyzylorda * `Asia/Rangoon` - Asia/Rangoon * `Asia/Riyadh` - Asia/Riyadh * `Asia/Saigon` - Asia/Saigon * `Asia/Sakhalin` - Asia/Sakhalin * `Asia/Samarkand` - Asia/Samarkand * `Asia/Seoul` - Asia/Seoul * `Asia/Shanghai` - Asia/Shanghai * `Asia/Singapore` - Asia/Singapore * `Asia/Srednekolymsk` - Asia/Srednekolymsk * `Asia/Taipei` - Asia/Taipei * `Asia/Tashkent` - Asia/Tashkent * `Asia/Tbilisi` - Asia/Tbilisi * `Asia/Tehran` - Asia/Tehran * `Asia/Tel_Aviv` - Asia/Tel_Aviv * `Asia/Thimbu` - Asia/Thimbu * `Asia/Thimphu` - Asia/Thimphu * `Asia/Tokyo` - Asia/Tokyo * `Asia/Tomsk` - Asia/Tomsk * `Asia/Ujung_Pandang` - Asia/Ujung_Pandang * `Asia/Ulaanbaatar` - Asia/Ulaanbaatar * `Asia/Ulan_Bator` - Asia/Ulan_Bator * `Asia/Urumqi` - Asia/Urumqi * `Asia/Ust-Nera` - Asia/Ust-Nera * `Asia/Vientiane` - Asia/Vientiane * `Asia/Vladivostok` - Asia/Vladivostok * `Asia/Yakutsk` - Asia/Yakutsk * `Asia/Yangon` - Asia/Yangon * `Asia/Yekaterinburg` - Asia/Yekaterinburg * `Asia/Yerevan` - Asia/Yerevan * `Atlantic/Azores` - Atlantic/Azores * `Atlantic/Bermuda` - Atlantic/Bermuda * `Atlantic/Canary` - Atlantic/Canary * `Atlantic/Cape_Verde` - Atlantic/Cape_Verde * `Atlantic/Faeroe` - Atlantic/Faeroe * `Atlantic/Faroe` - Atlantic/Faroe * `Atlantic/Jan_Mayen` - Atlantic/Jan_Mayen * `Atlantic/Madeira` - Atlantic/Madeira * `Atlantic/Reykjavik` - Atlantic/Reykjavik * `Atlantic/South_Georgia` - Atlantic/South_Georgia * `Atlantic/St_Helena` - Atlantic/St_Helena * `Atlantic/Stanley` - Atlantic/Stanley * `Australia/ACT` - Australia/ACT * `Australia/Adelaide` - Australia/Adelaide * `Australia/Brisbane` - Australia/Brisbane * `Australia/Broken_Hill` - Australia/Broken_Hill * `Australia/Canberra` - Australia/Canberra * `Australia/Currie` - Australia/Currie * `Australia/Darwin` - Australia/Darwin * `Australia/Eucla` - Australia/Eucla * `Australia/Hobart` - Australia/Hobart * `Australia/LHI` - Australia/LHI * `Australia/Lindeman` - Australia/Lindeman * `Australia/Lord_Howe` - Australia/Lord_Howe * `Australia/Melbourne` - Australia/Melbourne * `Australia/NSW` - Australia/NSW * `Australia/North` - Australia/North * `Australia/Perth` - Australia/Perth * `Australia/Queensland` - Australia/Queensland * `Australia/South` - Australia/South * `Australia/Sydney` - Australia/Sydney * `Australia/Tasmania` - Australia/Tasmania * `Australia/Victoria` - Australia/Victoria * `Australia/West` - Australia/West * `Australia/Yancowinna` - Australia/Yancowinna * `Brazil/Acre` - Brazil/Acre * `Brazil/DeNoronha` - Brazil/DeNoronha * `Brazil/East` - Brazil/East * `Brazil/West` - Brazil/West * `CET` - CET * `CST6CDT` - CST6CDT * `Canada/Atlantic` - Canada/Atlantic * `Canada/Central` - Canada/Central * `Canada/Eastern` - Canada/Eastern * `Canada/Mountain` - Canada/Mountain * `Canada/Newfoundland` - Canada/Newfoundland * `Canada/Pacific` - Canada/Pacific * `Canada/Saskatchewan` - Canada/Saskatchewan * `Canada/Yukon` - Canada/Yukon * `Chile/Continental` - Chile/Continental * `Chile/EasterIsland` - Chile/EasterIsland * `Cuba` - Cuba * `EET` - EET * `EST` - EST * `EST5EDT` - EST5EDT * `Egypt` - Egypt * `Eire` - Eire * `Europe/Amsterdam` - Europe/Amsterdam * `Europe/Andorra` - Europe/Andorra * `Europe/Astrakhan` - Europe/Astrakhan * `Europe/Athens` - Europe/Athens * `Europe/Belfast` - Europe/Belfast * `Europe/Belgrade` - Europe/Belgrade * `Europe/Berlin` - Europe/Berlin * `Europe/Bratislava` - Europe/Bratislava * `Europe/Brussels` - Europe/Brussels * `Europe/Bucharest` - Europe/Bucharest * `Europe/Budapest` - Europe/Budapest * `Europe/Busingen` - Europe/Busingen * `Europe/Chisinau` - Europe/Chisinau * `Europe/Copenhagen` - Europe/Copenhagen * `Europe/Dublin` - Europe/Dublin * `Europe/Gibraltar` - Europe/Gibraltar * `Europe/Guernsey` - Europe/Guernsey * `Europe/Helsinki` - Europe/Helsinki * `Europe/Isle_of_Man` - Europe/Isle_of_Man * `Europe/Istanbul` - Europe/Istanbul * `Europe/Jersey` - Europe/Jersey * `Europe/Kaliningrad` - Europe/Kaliningrad * `Europe/Kiev` - Europe/Kiev * `Europe/Kirov` - Europe/Kirov * `Europe/Kyiv` - Europe/Kyiv * `Europe/Lisbon` - Europe/Lisbon * `Europe/Ljubljana` - Europe/Ljubljana * `Europe/London` - Europe/London * `Europe/Luxembourg` - Europe/Luxembourg * `Europe/Madrid` - Europe/Madrid * `Europe/Malta` - Europe/Malta * `Europe/Mariehamn` - Europe/Mariehamn * `Europe/Minsk` - Europe/Minsk * `Europe/Monaco` - Europe/Monaco * `Europe/Moscow` - Europe/Moscow * `Europe/Nicosia` - Europe/Nicosia * `Europe/Oslo` - Europe/Oslo * `Europe/Paris` - Europe/Paris * `Europe/Podgorica` - Europe/Podgorica * `Europe/Prague` - Europe/Prague * `Europe/Riga` - Europe/Riga * `Europe/Rome` - Europe/Rome * `Europe/Samara` - Europe/Samara * `Europe/San_Marino` - Europe/San_Marino * `Europe/Sarajevo` - Europe/Sarajevo * `Europe/Saratov` - Europe/Saratov * `Europe/Simferopol` - Europe/Simferopol * `Europe/Skopje` - Europe/Skopje * `Europe/Sofia` - Europe/Sofia * `Europe/Stockholm` - Europe/Stockholm * `Europe/Tallinn` - Europe/Tallinn * `Europe/Tirane` - Europe/Tirane * `Europe/Tiraspol` - Europe/Tiraspol * `Europe/Ulyanovsk` - Europe/Ulyanovsk * `Europe/Uzhgorod` - Europe/Uzhgorod * `Europe/Vaduz` - Europe/Vaduz * `Europe/Vatican` - Europe/Vatican * `Europe/Vienna` - Europe/Vienna * `Europe/Vilnius` - Europe/Vilnius * `Europe/Volgograd` - Europe/Volgograd * `Europe/Warsaw` - Europe/Warsaw * `Europe/Zagreb` - Europe/Zagreb * `Europe/Zaporozhye` - Europe/Zaporozhye * `Europe/Zurich` - Europe/Zurich * `GB` - GB * `GB-Eire` - GB-Eire * `Greenwich` - Greenwich * `HST` - HST * `Hongkong` - Hongkong * `Iceland` - Iceland * `Indian/Antananarivo` - Indian/Antananarivo * `Indian/Chagos` - Indian/Chagos * `Indian/Christmas` - Indian/Christmas * `Indian/Cocos` - Indian/Cocos * `Indian/Comoro` - Indian/Comoro * `Indian/Kerguelen` - Indian/Kerguelen * `Indian/Mahe` - Indian/Mahe * `Indian/Maldives` - Indian/Maldives * `Indian/Mauritius` - Indian/Mauritius * `Indian/Mayotte` - Indian/Mayotte * `Indian/Reunion` - Indian/Reunion * `Iran` - Iran * `Israel` - Israel * `Jamaica` - Jamaica * `Japan` - Japan * `Kwajalein` - Kwajalein * `Libya` - Libya * `MET` - MET * `MST` - MST * `MST7MDT` - MST7MDT * `Mexico/BajaNorte` - Mexico/BajaNorte * `Mexico/BajaSur` - Mexico/BajaSur * `Mexico/General` - Mexico/General * `NZ` - NZ * `NZ-CHAT` - NZ-CHAT * `Navajo` - Navajo * `PRC` - PRC * `PST8PDT` - PST8PDT * `Pacific/Apia` - Pacific/Apia * `Pacific/Auckland` - Pacific/Auckland * `Pacific/Bougainville` - Pacific/Bougainville * `Pacific/Chatham` - Pacific/Chatham * `Pacific/Chuuk` - Pacific/Chuuk * `Pacific/Easter` - Pacific/Easter * `Pacific/Efate` - Pacific/Efate * `Pacific/Enderbury` - Pacific/Enderbury * `Pacific/Fakaofo` - Pacific/Fakaofo * `Pacific/Fiji` - Pacific/Fiji * `Pacific/Funafuti` - Pacific/Funafuti * `Pacific/Galapagos` - Pacific/Galapagos * `Pacific/Gambier` - Pacific/Gambier * `Pacific/Guadalcanal` - Pacific/Guadalcanal * `Pacific/Guam` - Pacific/Guam * `Pacific/Honolulu` - Pacific/Honolulu * `Pacific/Johnston` - Pacific/Johnston * `Pacific/Kanton` - Pacific/Kanton * `Pacific/Kiritimati` - Pacific/Kiritimati * `Pacific/Kosrae` - Pacific/Kosrae * `Pacific/Kwajalein` - Pacific/Kwajalein * `Pacific/Majuro` - Pacific/Majuro * `Pacific/Marquesas` - Pacific/Marquesas * `Pacific/Midway` - Pacific/Midway * `Pacific/Nauru` - Pacific/Nauru * `Pacific/Niue` - Pacific/Niue * `Pacific/Norfolk` - Pacific/Norfolk * `Pacific/Noumea` - Pacific/Noumea * `Pacific/Pago_Pago` - Pacific/Pago_Pago * `Pacific/Palau` - Pacific/Palau * `Pacific/Pitcairn` - Pacific/Pitcairn * `Pacific/Pohnpei` - Pacific/Pohnpei * `Pacific/Ponape` - Pacific/Ponape * `Pacific/Port_Moresby` - Pacific/Port_Moresby * `Pacific/Rarotonga` - Pacific/Rarotonga * `Pacific/Saipan` - Pacific/Saipan * `Pacific/Samoa` - Pacific/Samoa * `Pacific/Tahiti` - Pacific/Tahiti * `Pacific/Tarawa` - Pacific/Tarawa * `Pacific/Tongatapu` - Pacific/Tongatapu * `Pacific/Truk` - Pacific/Truk * `Pacific/Wake` - Pacific/Wake * `Pacific/Wallis` - Pacific/Wallis * `Pacific/Yap` - Pacific/Yap * `Poland` - Poland * `Portugal` - Portugal * `ROC` - ROC * `ROK` - ROK * `Singapore` - Singapore * `Turkey` - Turkey * `UCT` - UCT * `US/Alaska` - US/Alaska * `US/Aleutian` - US/Aleutian * `US/Arizona` - US/Arizona * `US/Central` - US/Central * `US/East-Indiana` - US/East-Indiana * `US/Eastern` - US/Eastern * `US/Hawaii` - US/Hawaii * `US/Indiana-Starke` - US/Indiana-Starke * `US/Michigan` - US/Michigan * `US/Mountain` - US/Mountain * `US/Pacific` - US/Pacific * `US/Samoa` - US/Samoa * `UTC` - UTC * `Universal` - Universal * `W-SU` - W-SU * `WET` - WET * `Zulu` - Zulu
 * @export
 * @enum {string}
 */

export const TimezoneEnum = {
    AfricaAbidjan: 'Africa/Abidjan',
    AfricaAccra: 'Africa/Accra',
    AfricaAddisAbaba: 'Africa/Addis_Ababa',
    AfricaAlgiers: 'Africa/Algiers',
    AfricaAsmara: 'Africa/Asmara',
    AfricaAsmera: 'Africa/Asmera',
    AfricaBamako: 'Africa/Bamako',
    AfricaBangui: 'Africa/Bangui',
    AfricaBanjul: 'Africa/Banjul',
    AfricaBissau: 'Africa/Bissau',
    AfricaBlantyre: 'Africa/Blantyre',
    AfricaBrazzaville: 'Africa/Brazzaville',
    AfricaBujumbura: 'Africa/Bujumbura',
    AfricaCairo: 'Africa/Cairo',
    AfricaCasablanca: 'Africa/Casablanca',
    AfricaCeuta: 'Africa/Ceuta',
    AfricaConakry: 'Africa/Conakry',
    AfricaDakar: 'Africa/Dakar',
    AfricaDarEsSalaam: 'Africa/Dar_es_Salaam',
    AfricaDjibouti: 'Africa/Djibouti',
    AfricaDouala: 'Africa/Douala',
    AfricaElAaiun: 'Africa/El_Aaiun',
    AfricaFreetown: 'Africa/Freetown',
    AfricaGaborone: 'Africa/Gaborone',
    AfricaHarare: 'Africa/Harare',
    AfricaJohannesburg: 'Africa/Johannesburg',
    AfricaJuba: 'Africa/Juba',
    AfricaKampala: 'Africa/Kampala',
    AfricaKhartoum: 'Africa/Khartoum',
    AfricaKigali: 'Africa/Kigali',
    AfricaKinshasa: 'Africa/Kinshasa',
    AfricaLagos: 'Africa/Lagos',
    AfricaLibreville: 'Africa/Libreville',
    AfricaLome: 'Africa/Lome',
    AfricaLuanda: 'Africa/Luanda',
    AfricaLubumbashi: 'Africa/Lubumbashi',
    AfricaLusaka: 'Africa/Lusaka',
    AfricaMalabo: 'Africa/Malabo',
    AfricaMaputo: 'Africa/Maputo',
    AfricaMaseru: 'Africa/Maseru',
    AfricaMbabane: 'Africa/Mbabane',
    AfricaMogadishu: 'Africa/Mogadishu',
    AfricaMonrovia: 'Africa/Monrovia',
    AfricaNairobi: 'Africa/Nairobi',
    AfricaNdjamena: 'Africa/Ndjamena',
    AfricaNiamey: 'Africa/Niamey',
    AfricaNouakchott: 'Africa/Nouakchott',
    AfricaOuagadougou: 'Africa/Ouagadougou',
    AfricaPortoNovo: 'Africa/Porto-Novo',
    AfricaSaoTome: 'Africa/Sao_Tome',
    AfricaTimbuktu: 'Africa/Timbuktu',
    AfricaTripoli: 'Africa/Tripoli',
    AfricaTunis: 'Africa/Tunis',
    AfricaWindhoek: 'Africa/Windhoek',
    AmericaAdak: 'America/Adak',
    AmericaAnchorage: 'America/Anchorage',
    AmericaAnguilla: 'America/Anguilla',
    AmericaAntigua: 'America/Antigua',
    AmericaAraguaina: 'America/Araguaina',
    AmericaArgentinaBuenosAires: 'America/Argentina/Buenos_Aires',
    AmericaArgentinaCatamarca: 'America/Argentina/Catamarca',
    AmericaArgentinaComodRivadavia: 'America/Argentina/ComodRivadavia',
    AmericaArgentinaCordoba: 'America/Argentina/Cordoba',
    AmericaArgentinaJujuy: 'America/Argentina/Jujuy',
    AmericaArgentinaLaRioja: 'America/Argentina/La_Rioja',
    AmericaArgentinaMendoza: 'America/Argentina/Mendoza',
    AmericaArgentinaRioGallegos: 'America/Argentina/Rio_Gallegos',
    AmericaArgentinaSalta: 'America/Argentina/Salta',
    AmericaArgentinaSanJuan: 'America/Argentina/San_Juan',
    AmericaArgentinaSanLuis: 'America/Argentina/San_Luis',
    AmericaArgentinaTucuman: 'America/Argentina/Tucuman',
    AmericaArgentinaUshuaia: 'America/Argentina/Ushuaia',
    AmericaAruba: 'America/Aruba',
    AmericaAsuncion: 'America/Asuncion',
    AmericaAtikokan: 'America/Atikokan',
    AmericaAtka: 'America/Atka',
    AmericaBahia: 'America/Bahia',
    AmericaBahiaBanderas: 'America/Bahia_Banderas',
    AmericaBarbados: 'America/Barbados',
    AmericaBelem: 'America/Belem',
    AmericaBelize: 'America/Belize',
    AmericaBlancSablon: 'America/Blanc-Sablon',
    AmericaBoaVista: 'America/Boa_Vista',
    AmericaBogota: 'America/Bogota',
    AmericaBoise: 'America/Boise',
    AmericaBuenosAires: 'America/Buenos_Aires',
    AmericaCambridgeBay: 'America/Cambridge_Bay',
    AmericaCampoGrande: 'America/Campo_Grande',
    AmericaCancun: 'America/Cancun',
    AmericaCaracas: 'America/Caracas',
    AmericaCatamarca: 'America/Catamarca',
    AmericaCayenne: 'America/Cayenne',
    AmericaCayman: 'America/Cayman',
    AmericaChicago: 'America/Chicago',
    AmericaChihuahua: 'America/Chihuahua',
    AmericaCiudadJuarez: 'America/Ciudad_Juarez',
    AmericaCoralHarbour: 'America/Coral_Harbour',
    AmericaCordoba: 'America/Cordoba',
    AmericaCostaRica: 'America/Costa_Rica',
    AmericaCoyhaique: 'America/Coyhaique',
    AmericaCreston: 'America/Creston',
    AmericaCuiaba: 'America/Cuiaba',
    AmericaCuracao: 'America/Curacao',
    AmericaDanmarkshavn: 'America/Danmarkshavn',
    AmericaDawson: 'America/Dawson',
    AmericaDawsonCreek: 'America/Dawson_Creek',
    AmericaDenver: 'America/Denver',
    AmericaDetroit: 'America/Detroit',
    AmericaDominica: 'America/Dominica',
    AmericaEdmonton: 'America/Edmonton',
    AmericaEirunepe: 'America/Eirunepe',
    AmericaElSalvador: 'America/El_Salvador',
    AmericaEnsenada: 'America/Ensenada',
    AmericaFortNelson: 'America/Fort_Nelson',
    AmericaFortWayne: 'America/Fort_Wayne',
    AmericaFortaleza: 'America/Fortaleza',
    AmericaGlaceBay: 'America/Glace_Bay',
    AmericaGodthab: 'America/Godthab',
    AmericaGooseBay: 'America/Goose_Bay',
    AmericaGrandTurk: 'America/Grand_Turk',
    AmericaGrenada: 'America/Grenada',
    AmericaGuadeloupe: 'America/Guadeloupe',
    AmericaGuatemala: 'America/Guatemala',
    AmericaGuayaquil: 'America/Guayaquil',
    AmericaGuyana: 'America/Guyana',
    AmericaHalifax: 'America/Halifax',
    AmericaHavana: 'America/Havana',
    AmericaHermosillo: 'America/Hermosillo',
    AmericaIndianaIndianapolis: 'America/Indiana/Indianapolis',
    AmericaIndianaKnox: 'America/Indiana/Knox',
    AmericaIndianaMarengo: 'America/Indiana/Marengo',
    AmericaIndianaPetersburg: 'America/Indiana/Petersburg',
    AmericaIndianaTellCity: 'America/Indiana/Tell_City',
    AmericaIndianaVevay: 'America/Indiana/Vevay',
    AmericaIndianaVincennes: 'America/Indiana/Vincennes',
    AmericaIndianaWinamac: 'America/Indiana/Winamac',
    AmericaIndianapolis: 'America/Indianapolis',
    AmericaInuvik: 'America/Inuvik',
    AmericaIqaluit: 'America/Iqaluit',
    AmericaJamaica: 'America/Jamaica',
    AmericaJujuy: 'America/Jujuy',
    AmericaJuneau: 'America/Juneau',
    AmericaKentuckyLouisville: 'America/Kentucky/Louisville',
    AmericaKentuckyMonticello: 'America/Kentucky/Monticello',
    AmericaKnoxIn: 'America/Knox_IN',
    AmericaKralendijk: 'America/Kralendijk',
    AmericaLaPaz: 'America/La_Paz',
    AmericaLima: 'America/Lima',
    AmericaLosAngeles: 'America/Los_Angeles',
    AmericaLouisville: 'America/Louisville',
    AmericaLowerPrinces: 'America/Lower_Princes',
    AmericaMaceio: 'America/Maceio',
    AmericaManagua: 'America/Managua',
    AmericaManaus: 'America/Manaus',
    AmericaMarigot: 'America/Marigot',
    AmericaMartinique: 'America/Martinique',
    AmericaMatamoros: 'America/Matamoros',
    AmericaMazatlan: 'America/Mazatlan',
    AmericaMendoza: 'America/Mendoza',
    AmericaMenominee: 'America/Menominee',
    AmericaMerida: 'America/Merida',
    AmericaMetlakatla: 'America/Metlakatla',
    AmericaMexicoCity: 'America/Mexico_City',
    AmericaMiquelon: 'America/Miquelon',
    AmericaMoncton: 'America/Moncton',
    AmericaMonterrey: 'America/Monterrey',
    AmericaMontevideo: 'America/Montevideo',
    AmericaMontreal: 'America/Montreal',
    AmericaMontserrat: 'America/Montserrat',
    AmericaNassau: 'America/Nassau',
    AmericaNewYork: 'America/New_York',
    AmericaNipigon: 'America/Nipigon',
    AmericaNome: 'America/Nome',
    AmericaNoronha: 'America/Noronha',
    AmericaNorthDakotaBeulah: 'America/North_Dakota/Beulah',
    AmericaNorthDakotaCenter: 'America/North_Dakota/Center',
    AmericaNorthDakotaNewSalem: 'America/North_Dakota/New_Salem',
    AmericaNuuk: 'America/Nuuk',
    AmericaOjinaga: 'America/Ojinaga',
    AmericaPanama: 'America/Panama',
    AmericaPangnirtung: 'America/Pangnirtung',
    AmericaParamaribo: 'America/Paramaribo',
    AmericaPhoenix: 'America/Phoenix',
    AmericaPortAuPrince: 'America/Port-au-Prince',
    AmericaPortOfSpain: 'America/Port_of_Spain',
    AmericaPortoAcre: 'America/Porto_Acre',
    AmericaPortoVelho: 'America/Porto_Velho',
    AmericaPuertoRico: 'America/Puerto_Rico',
    AmericaPuntaArenas: 'America/Punta_Arenas',
    AmericaRainyRiver: 'America/Rainy_River',
    AmericaRankinInlet: 'America/Rankin_Inlet',
    AmericaRecife: 'America/Recife',
    AmericaRegina: 'America/Regina',
    AmericaResolute: 'America/Resolute',
    AmericaRioBranco: 'America/Rio_Branco',
    AmericaRosario: 'America/Rosario',
    AmericaSantaIsabel: 'America/Santa_Isabel',
    AmericaSantarem: 'America/Santarem',
    AmericaSantiago: 'America/Santiago',
    AmericaSantoDomingo: 'America/Santo_Domingo',
    AmericaSaoPaulo: 'America/Sao_Paulo',
    AmericaScoresbysund: 'America/Scoresbysund',
    AmericaShiprock: 'America/Shiprock',
    AmericaSitka: 'America/Sitka',
    AmericaStBarthelemy: 'America/St_Barthelemy',
    AmericaStJohns: 'America/St_Johns',
    AmericaStKitts: 'America/St_Kitts',
    AmericaStLucia: 'America/St_Lucia',
    AmericaStThomas: 'America/St_Thomas',
    AmericaStVincent: 'America/St_Vincent',
    AmericaSwiftCurrent: 'America/Swift_Current',
    AmericaTegucigalpa: 'America/Tegucigalpa',
    AmericaThule: 'America/Thule',
    AmericaThunderBay: 'America/Thunder_Bay',
    AmericaTijuana: 'America/Tijuana',
    AmericaToronto: 'America/Toronto',
    AmericaTortola: 'America/Tortola',
    AmericaVancouver: 'America/Vancouver',
    AmericaVirgin: 'America/Virgin',
    AmericaWhitehorse: 'America/Whitehorse',
    AmericaWinnipeg: 'America/Winnipeg',
    AmericaYakutat: 'America/Yakutat',
    AmericaYellowknife: 'America/Yellowknife',
    AntarcticaCasey: 'Antarctica/Casey',
    AntarcticaDavis: 'Antarctica/Davis',
    AntarcticaDumontDUrville: 'Antarctica/DumontDUrville',
    AntarcticaMacquarie: 'Antarctica/Macquarie',
    AntarcticaMawson: 'Antarctica/Mawson',
    AntarcticaMcMurdo: 'Antarctica/McMurdo',
    AntarcticaPalmer: 'Antarctica/Palmer',
    AntarcticaRothera: 'Antarctica/Rothera',
    AntarcticaSouthPole: 'Antarctica/South_Pole',
    AntarcticaSyowa: 'Antarctica/Syowa',
    AntarcticaTroll: 'Antarctica/Troll',
    AntarcticaVostok: 'Antarctica/Vostok',
    ArcticLongyearbyen: 'Arctic/Longyearbyen',
    AsiaAden: 'Asia/Aden',
    AsiaAlmaty: 'Asia/Almaty',
    AsiaAmman: 'Asia/Amman',
    AsiaAnadyr: 'Asia/Anadyr',
    AsiaAqtau: 'Asia/Aqtau',
    AsiaAqtobe: 'Asia/Aqtobe',
    AsiaAshgabat: 'Asia/Ashgabat',
    AsiaAshkhabad: 'Asia/Ashkhabad',
    AsiaAtyrau: 'Asia/Atyrau',
    AsiaBaghdad: 'Asia/Baghdad',
    AsiaBahrain: 'Asia/Bahrain',
    AsiaBaku: 'Asia/Baku',
    AsiaBangkok: 'Asia/Bangkok',
    AsiaBarnaul: 'Asia/Barnaul',
    AsiaBeirut: 'Asia/Beirut',
    AsiaBishkek: 'Asia/Bishkek',
    AsiaBrunei: 'Asia/Brunei',
    AsiaCalcutta: 'Asia/Calcutta',
    AsiaChita: 'Asia/Chita',
    AsiaChoibalsan: 'Asia/Choibalsan',
    AsiaChongqing: 'Asia/Chongqing',
    AsiaChungking: 'Asia/Chungking',
    AsiaColombo: 'Asia/Colombo',
    AsiaDacca: 'Asia/Dacca',
    AsiaDamascus: 'Asia/Damascus',
    AsiaDhaka: 'Asia/Dhaka',
    AsiaDili: 'Asia/Dili',
    AsiaDubai: 'Asia/Dubai',
    AsiaDushanbe: 'Asia/Dushanbe',
    AsiaFamagusta: 'Asia/Famagusta',
    AsiaGaza: 'Asia/Gaza',
    AsiaHarbin: 'Asia/Harbin',
    AsiaHebron: 'Asia/Hebron',
    AsiaHoChiMinh: 'Asia/Ho_Chi_Minh',
    AsiaHongKong: 'Asia/Hong_Kong',
    AsiaHovd: 'Asia/Hovd',
    AsiaIrkutsk: 'Asia/Irkutsk',
    AsiaIstanbul: 'Asia/Istanbul',
    AsiaJakarta: 'Asia/Jakarta',
    AsiaJayapura: 'Asia/Jayapura',
    AsiaJerusalem: 'Asia/Jerusalem',
    AsiaKabul: 'Asia/Kabul',
    AsiaKamchatka: 'Asia/Kamchatka',
    AsiaKarachi: 'Asia/Karachi',
    AsiaKashgar: 'Asia/Kashgar',
    AsiaKathmandu: 'Asia/Kathmandu',
    AsiaKatmandu: 'Asia/Katmandu',
    AsiaKhandyga: 'Asia/Khandyga',
    AsiaKolkata: 'Asia/Kolkata',
    AsiaKrasnoyarsk: 'Asia/Krasnoyarsk',
    AsiaKualaLumpur: 'Asia/Kuala_Lumpur',
    AsiaKuching: 'Asia/Kuching',
    AsiaKuwait: 'Asia/Kuwait',
    AsiaMacao: 'Asia/Macao',
    AsiaMacau: 'Asia/Macau',
    AsiaMagadan: 'Asia/Magadan',
    AsiaMakassar: 'Asia/Makassar',
    AsiaManila: 'Asia/Manila',
    AsiaMuscat: 'Asia/Muscat',
    AsiaNicosia: 'Asia/Nicosia',
    AsiaNovokuznetsk: 'Asia/Novokuznetsk',
    AsiaNovosibirsk: 'Asia/Novosibirsk',
    AsiaOmsk: 'Asia/Omsk',
    AsiaOral: 'Asia/Oral',
    AsiaPhnomPenh: 'Asia/Phnom_Penh',
    AsiaPontianak: 'Asia/Pontianak',
    AsiaPyongyang: 'Asia/Pyongyang',
    AsiaQatar: 'Asia/Qatar',
    AsiaQostanay: 'Asia/Qostanay',
    AsiaQyzylorda: 'Asia/Qyzylorda',
    AsiaRangoon: 'Asia/Rangoon',
    AsiaRiyadh: 'Asia/Riyadh',
    AsiaSaigon: 'Asia/Saigon',
    AsiaSakhalin: 'Asia/Sakhalin',
    AsiaSamarkand: 'Asia/Samarkand',
    AsiaSeoul: 'Asia/Seoul',
    AsiaShanghai: 'Asia/Shanghai',
    AsiaSingapore: 'Asia/Singapore',
    AsiaSrednekolymsk: 'Asia/Srednekolymsk',
    AsiaTaipei: 'Asia/Taipei',
    AsiaTashkent: 'Asia/Tashkent',
    AsiaTbilisi: 'Asia/Tbilisi',
    AsiaTehran: 'Asia/Tehran',
    AsiaTelAviv: 'Asia/Tel_Aviv',
    AsiaThimbu: 'Asia/Thimbu',
    AsiaThimphu: 'Asia/Thimphu',
    AsiaTokyo: 'Asia/Tokyo',
    AsiaTomsk: 'Asia/Tomsk',
    AsiaUjungPandang: 'Asia/Ujung_Pandang',
    AsiaUlaanbaatar: 'Asia/Ulaanbaatar',
    AsiaUlanBator: 'Asia/Ulan_Bator',
    AsiaUrumqi: 'Asia/Urumqi',
    AsiaUstNera: 'Asia/Ust-Nera',
    AsiaVientiane: 'Asia/Vientiane',
    AsiaVladivostok: 'Asia/Vladivostok',
    AsiaYakutsk: 'Asia/Yakutsk',
    AsiaYangon: 'Asia/Yangon',
    AsiaYekaterinburg: 'Asia/Yekaterinburg',
    AsiaYerevan: 'Asia/Yerevan',
    AtlanticAzores: 'Atlantic/Azores',
    AtlanticBermuda: 'Atlantic/Bermuda',
    AtlanticCanary: 'Atlantic/Canary',
    AtlanticCapeVerde: 'Atlantic/Cape_Verde',
    AtlanticFaeroe: 'Atlantic/Faeroe',
    AtlanticFaroe: 'Atlantic/Faroe',
    AtlanticJanMayen: 'Atlantic/Jan_Mayen',
    AtlanticMadeira: 'Atlantic/Madeira',
    AtlanticReykjavik: 'Atlantic/Reykjavik',
    AtlanticSouthGeorgia: 'Atlantic/South_Georgia',
    AtlanticStHelena: 'Atlantic/St_Helena',
    AtlanticStanley: 'Atlantic/Stanley',
    AustraliaAct: 'Australia/ACT',
    AustraliaAdelaide: 'Australia/Adelaide',
    AustraliaBrisbane: 'Australia/Brisbane',
    AustraliaBrokenHill: 'Australia/Broken_Hill',
    AustraliaCanberra: 'Australia/Canberra',
    AustraliaCurrie: 'Australia/Currie',
    AustraliaDarwin: 'Australia/Darwin',
    AustraliaEucla: 'Australia/Eucla',
    AustraliaHobart: 'Australia/Hobart',
    AustraliaLhi: 'Australia/LHI',
    AustraliaLindeman: 'Australia/Lindeman',
    AustraliaLordHowe: 'Australia/Lord_Howe',
    AustraliaMelbourne: 'Australia/Melbourne',
    AustraliaNsw: 'Australia/NSW',
    AustraliaNorth: 'Australia/North',
    AustraliaPerth: 'Australia/Perth',
    AustraliaQueensland: 'Australia/Queensland',
    AustraliaSouth: 'Australia/South',
    AustraliaSydney: 'Australia/Sydney',
    AustraliaTasmania: 'Australia/Tasmania',
    AustraliaVictoria: 'Australia/Victoria',
    AustraliaWest: 'Australia/West',
    AustraliaYancowinna: 'Australia/Yancowinna',
    BrazilAcre: 'Brazil/Acre',
    BrazilDeNoronha: 'Brazil/DeNoronha',
    BrazilEast: 'Brazil/East',
    BrazilWest: 'Brazil/West',
    Cet: 'CET',
    Cst6Cdt: 'CST6CDT',
    CanadaAtlantic: 'Canada/Atlantic',
    CanadaCentral: 'Canada/Central',
    CanadaEastern: 'Canada/Eastern',
    CanadaMountain: 'Canada/Mountain',
    CanadaNewfoundland: 'Canada/Newfoundland',
    CanadaPacific: 'Canada/Pacific',
    CanadaSaskatchewan: 'Canada/Saskatchewan',
    CanadaYukon: 'Canada/Yukon',
    ChileContinental: 'Chile/Continental',
    ChileEasterIsland: 'Chile/EasterIsland',
    Cuba: 'Cuba',
    Eet: 'EET',
    Est: 'EST',
    Est5Edt: 'EST5EDT',
    Egypt: 'Egypt',
    Eire: 'Eire',
    EuropeAmsterdam: 'Europe/Amsterdam',
    EuropeAndorra: 'Europe/Andorra',
    EuropeAstrakhan: 'Europe/Astrakhan',
    EuropeAthens: 'Europe/Athens',
    EuropeBelfast: 'Europe/Belfast',
    EuropeBelgrade: 'Europe/Belgrade',
    EuropeBerlin: 'Europe/Berlin',
    EuropeBratislava: 'Europe/Bratislava',
    EuropeBrussels: 'Europe/Brussels',
    EuropeBucharest: 'Europe/Bucharest',
    EuropeBudapest: 'Europe/Budapest',
    EuropeBusingen: 'Europe/Busingen',
    EuropeChisinau: 'Europe/Chisinau',
    EuropeCopenhagen: 'Europe/Copenhagen',
    EuropeDublin: 'Europe/Dublin',
    EuropeGibraltar: 'Europe/Gibraltar',
    EuropeGuernsey: 'Europe/Guernsey',
    EuropeHelsinki: 'Europe/Helsinki',
    EuropeIsleOfMan: 'Europe/Isle_of_Man',
    EuropeIstanbul: 'Europe/Istanbul',
    EuropeJersey: 'Europe/Jersey',
    EuropeKaliningrad: 'Europe/Kaliningrad',
    EuropeKiev: 'Europe/Kiev',
    EuropeKirov: 'Europe/Kirov',
    EuropeKyiv: 'Europe/Kyiv',
    EuropeLisbon: 'Europe/Lisbon',
    EuropeLjubljana: 'Europe/Ljubljana',
    EuropeLondon: 'Europe/London',
    EuropeLuxembourg: 'Europe/Luxembourg',
    EuropeMadrid: 'Europe/Madrid',
    EuropeMalta: 'Europe/Malta',
    EuropeMariehamn: 'Europe/Mariehamn',
    EuropeMinsk: 'Europe/Minsk',
    EuropeMonaco: 'Europe/Monaco',
    EuropeMoscow: 'Europe/Moscow',
    EuropeNicosia: 'Europe/Nicosia',
    EuropeOslo: 'Europe/Oslo',
    EuropeParis: 'Europe/Paris',
    EuropePodgorica: 'Europe/Podgorica',
    EuropePrague: 'Europe/Prague',
    EuropeRiga: 'Europe/Riga',
    EuropeRome: 'Europe/Rome',
    EuropeSamara: 'Europe/Samara',
    EuropeSanMarino: 'Europe/San_Marino',
    EuropeSarajevo: 'Europe/Sarajevo',
    EuropeSaratov: 'Europe/Saratov',
    EuropeSimferopol: 'Europe/Simferopol',
    EuropeSkopje: 'Europe/Skopje',
    EuropeSofia: 'Europe/Sofia',
    EuropeStockholm: 'Europe/Stockholm',
    EuropeTallinn: 'Europe/Tallinn',
    EuropeTirane: 'Europe/Tirane',
    EuropeTiraspol: 'Europe/Tiraspol',
    EuropeUlyanovsk: 'Europe/Ulyanovsk',
    EuropeUzhgorod: 'Europe/Uzhgorod',
    EuropeVaduz: 'Europe/Vaduz',
    EuropeVatican: 'Europe/Vatican',
    EuropeVienna: 'Europe/Vienna',
    EuropeVilnius: 'Europe/Vilnius',
    EuropeVolgograd: 'Europe/Volgograd',
    EuropeWarsaw: 'Europe/Warsaw',
    EuropeZagreb: 'Europe/Zagreb',
    EuropeZaporozhye: 'Europe/Zaporozhye',
    EuropeZurich: 'Europe/Zurich',
    Gb: 'GB',
    GbEire: 'GB-Eire',
    Greenwich: 'Greenwich',
    Hst: 'HST',
    Hongkong: 'Hongkong',
    Iceland: 'Iceland',
    IndianAntananarivo: 'Indian/Antananarivo',
    IndianChagos: 'Indian/Chagos',
    IndianChristmas: 'Indian/Christmas',
    IndianCocos: 'Indian/Cocos',
    IndianComoro: 'Indian/Comoro',
    IndianKerguelen: 'Indian/Kerguelen',
    IndianMahe: 'Indian/Mahe',
    IndianMaldives: 'Indian/Maldives',
    IndianMauritius: 'Indian/Mauritius',
    IndianMayotte: 'Indian/Mayotte',
    IndianReunion: 'Indian/Reunion',
    Iran: 'Iran',
    Israel: 'Israel',
    Jamaica: 'Jamaica',
    Japan: 'Japan',
    Kwajalein: 'Kwajalein',
    Libya: 'Libya',
    Met: 'MET',
    Mst: 'MST',
    Mst7Mdt: 'MST7MDT',
    MexicoBajaNorte: 'Mexico/BajaNorte',
    MexicoBajaSur: 'Mexico/BajaSur',
    MexicoGeneral: 'Mexico/General',
    Nz: 'NZ',
    NzChat: 'NZ-CHAT',
    Navajo: 'Navajo',
    Prc: 'PRC',
    Pst8Pdt: 'PST8PDT',
    PacificApia: 'Pacific/Apia',
    PacificAuckland: 'Pacific/Auckland',
    PacificBougainville: 'Pacific/Bougainville',
    PacificChatham: 'Pacific/Chatham',
    PacificChuuk: 'Pacific/Chuuk',
    PacificEaster: 'Pacific/Easter',
    PacificEfate: 'Pacific/Efate',
    PacificEnderbury: 'Pacific/Enderbury',
    PacificFakaofo: 'Pacific/Fakaofo',
    PacificFiji: 'Pacific/Fiji',
    PacificFunafuti: 'Pacific/Funafuti',
    PacificGalapagos: 'Pacific/Galapagos',
    PacificGambier: 'Pacific/Gambier',
    PacificGuadalcanal: 'Pacific/Guadalcanal',
    PacificGuam: 'Pacific/Guam',
    PacificHonolulu: 'Pacific/Honolulu',
    PacificJohnston: 'Pacific/Johnston',
    PacificKanton: 'Pacific/Kanton',
    PacificKiritimati: 'Pacific/Kiritimati',
    PacificKosrae: 'Pacific/Kosrae',
    PacificKwajalein: 'Pacific/Kwajalein',
    PacificMajuro: 'Pacific/Majuro',
    PacificMarquesas: 'Pacific/Marquesas',
    PacificMidway: 'Pacific/Midway',
    PacificNauru: 'Pacific/Nauru',
    PacificNiue: 'Pacific/Niue',
    PacificNorfolk: 'Pacific/Norfolk',
    PacificNoumea: 'Pacific/Noumea',
    PacificPagoPago: 'Pacific/Pago_Pago',
    PacificPalau: 'Pacific/Palau',
    PacificPitcairn: 'Pacific/Pitcairn',
    PacificPohnpei: 'Pacific/Pohnpei',
    PacificPonape: 'Pacific/Ponape',
    PacificPortMoresby: 'Pacific/Port_Moresby',
    PacificRarotonga: 'Pacific/Rarotonga',
    PacificSaipan: 'Pacific/Saipan',
    PacificSamoa: 'Pacific/Samoa',
    PacificTahiti: 'Pacific/Tahiti',
    PacificTarawa: 'Pacific/Tarawa',
    PacificTongatapu: 'Pacific/Tongatapu',
    PacificTruk: 'Pacific/Truk',
    PacificWake: 'Pacific/Wake',
    PacificWallis: 'Pacific/Wallis',
    PacificYap: 'Pacific/Yap',
    Poland: 'Poland',
    Portugal: 'Portugal',
    Roc: 'ROC',
    Rok: 'ROK',
    Singapore: 'Singapore',
    Turkey: 'Turkey',
    Uct: 'UCT',
    UsAlaska: 'US/Alaska',
    UsAleutian: 'US/Aleutian',
    UsArizona: 'US/Arizona',
    UsCentral: 'US/Central',
    UsEastIndiana: 'US/East-Indiana',
    UsEastern: 'US/Eastern',
    UsHawaii: 'US/Hawaii',
    UsIndianaStarke: 'US/Indiana-Starke',
    UsMichigan: 'US/Michigan',
    UsMountain: 'US/Mountain',
    UsPacific: 'US/Pacific',
    UsSamoa: 'US/Samoa',
    Utc: 'UTC',
    Universal: 'Universal',
    WSu: 'W-SU',
    Wet: 'WET',
    Zulu: 'Zulu'
} as const;

export type TimezoneEnum = typeof TimezoneEnum[keyof typeof TimezoneEnum];


/**
 * 
 * @export
 * @interface TokenObtainPair
 */
export interface TokenObtainPair {
    /**
     * 
     * @type {string}
     * @memberof TokenObtainPair
     */
    'access': string;
    /**
     * 
     * @type {string}
     * @memberof TokenObtainPair
     */
    'refresh': string;
}
/**
 * 
 * @export
 * @interface TokenObtainPairRequest
 */
export interface TokenObtainPairRequest {
    /**
     * 
     * @type {string}
     * @memberof TokenObtainPairRequest
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof TokenObtainPairRequest
     */
    'password': string;
}
/**
 * 
 * @export
 * @interface TokenRefresh
 */
export interface TokenRefresh {
    /**
     * 
     * @type {string}
     * @memberof TokenRefresh
     */
    'access': string;
    /**
     * 
     * @type {string}
     * @memberof TokenRefresh
     */
    'refresh': string;
}
/**
 * 
 * @export
 * @interface TokenRefreshRequest
 */
export interface TokenRefreshRequest {
    /**
     * 
     * @type {string}
     * @memberof TokenRefreshRequest
     */
    'refresh': string;
}
/**
 * 
 * @export
 * @interface TokenVerifyRequest
 */
export interface TokenVerifyRequest {
    /**
     * 
     * @type {string}
     * @memberof TokenVerifyRequest
     */
    'token': string;
}
/**
 * Serialize Trade Name.
 * @export
 * @interface TradeName
 */
export interface TradeName {
    /**
     * 
     * @type {string}
     * @memberof TradeName
     */
    'text': string;
}
/**
 * Schema-only serializer for trade names (avoids circular import).
 * @export
 * @interface TradeNameSchema
 */
export interface TradeNameSchema {
    /**
     * 
     * @type {string}
     * @memberof TradeNameSchema
     */
    'text': string;
}
/**
 * 
 * @export
 * @interface TradeNameSuggestion
 */
export interface TradeNameSuggestion {
    /**
     * 
     * @type {string}
     * @memberof TradeNameSuggestion
     */
    'text': string;
    /**
     * 
     * @type {string}
     * @memberof TradeNameSuggestion
     */
    'company': string;
}
/**
 * 
 * @export
 * @interface TradeNameSuggestionRequest
 */
export interface TradeNameSuggestionRequest {
    /**
     * 
     * @type {string}
     * @memberof TradeNameSuggestionRequest
     */
    'text': string;
    /**
     * 
     * @type {string}
     * @memberof TradeNameSuggestionRequest
     */
    'company': string;
}
/**
 * Serialize Trending Companies.
 * @export
 * @interface TrendingCompanyAggregation
 */
export interface TrendingCompanyAggregation {
    /**
     * 
     * @type {number}
     * @memberof TrendingCompanyAggregation
     */
    'count': number;
    /**
     * 
     * @type {number}
     * @memberof TrendingCompanyAggregation
     */
    'score': number;
    /**
     * 
     * @type {BasicCompany}
     * @memberof TrendingCompanyAggregation
     */
    'company': BasicCompany;
}
/**
 * UBO Investigation Serializer.
 * @export
 * @interface UBOInvestigation
 */
export interface UBOInvestigation {
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigation
     */
    'external_id': string;
    /**
     * 
     * @type {UBOInvestigationCompany}
     * @memberof UBOInvestigation
     */
    'company': UBOInvestigationCompany;
}
/**
 * 
 * @export
 * @interface UBOInvestigationCompany
 */
export interface UBOInvestigationCompany {
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'duns_number'?: string | null;
    /**
     * Get LEI (Legal Entity Identifier). None for OpenSearch-sourced instances.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'lei': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'clean_name': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'slug': string;
    /**
     * 
     * @type {CountryEnum}
     * @memberof UBOInvestigationCompany
     */
    'country': CountryEnum;
    /**
     * Indicates whether the company is out of business.
     * @type {boolean}
     * @memberof UBOInvestigationCompany
     */
    'is_out_of_business'?: boolean;
    /**
     * 
     * @type {Array<RegistrationNumber>}
     * @memberof UBOInvestigationCompany
     */
    'registration_numbers': Array<RegistrationNumber>;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'ticker_symbol'?: string | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof UBOInvestigationCompany
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof UBOInvestigationCompany
     */
    'primary_industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'address_street'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'address_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'address_place'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'address_region'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'address_postal'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'address_phone'?: string | null;
    /**
     * 
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'address_latitude'?: number | null;
    /**
     * 
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'address_longitude'?: number | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'website_domain'?: string | null;
    /**
     * Get Social Logo.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'social_logo': string | null;
    /**
     * Get Website Icon URL.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'website_icon_url': string;
    /**
     * Description found by AI on the website
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'website_description'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'website_description_source_url'?: string | null;
    /**
     * Get Social Description.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'social_description': string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'linkedin_url'?: string | null;
    /**
     * Amount of reviews available
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'review_count'?: number;
    /**
     * Average review score
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'review_average_score'?: number | null;
    /**
     * Amount of articles available
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'article_count'?: number;
    /**
     * Amount of publications available
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'publication_count'?: number;
    /**
     * Amount of reports available
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'report_count'?: number;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'dnb_website_url'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'dnb_website_domain'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'dnb_assessment'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'global_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {CompanyGlobalUltimateCountry}
     * @memberof UBOInvestigationCompany
     */
    'global_ultimate_country'?: CompanyGlobalUltimateCountry | null;
    /**
     * Get Global Ultimate website icon url.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'global_ultimate_website_icon_url': string;
    /**
     * Get Global Ultimate external id.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'global_ultimate_external_id': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'global_ultimate_name'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'domestic_ultimate_duns_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'domestic_ultimate_name'?: string | null;
    /**
     * Get Global Ultimate website icon url.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'domestic_ultimate_website_icon_url': string;
    /**
     * Get Domestic Ultimate external id.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'domestic_ultimate_external_id': string;
    /**
     * Date when data was fetched from Dun and Bradstreet.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'dnb_fetched_at'?: string | null;
    /**
     * Reprocessing company details, this is a temporary flag.
     * @type {boolean}
     * @memberof UBOInvestigationCompany
     */
    'is_reprocessing_company_details'?: boolean;
    /**
     * Get Founding Date.
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'founding_date': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'updated_at': string;
    /**
     * 
     * @type {RegistrationStatusEnum}
     * @memberof UBOInvestigationCompany
     */
    'registration_status'?: RegistrationStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'registration_status_text': string;
    /**
     * 
     * @type {number}
     * @memberof UBOInvestigationCompany
     */
    'registration_progress'?: number;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'finished_registration_at'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'last_directors_update'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UBOInvestigationCompany
     */
    'last_ubo_investigation'?: string | null;
    /**
     * Get if the company is pending DNB investigation.
     * @type {boolean}
     * @memberof UBOInvestigationCompany
     */
    'is_pending_dnb_investigation': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof UBOInvestigationCompany
     */
    'is_pending_ubo_investigation': boolean;
}
/**
 * Ubo control ownership type serializer.
 * @export
 * @interface UboControlOwnershipType
 */
export interface UboControlOwnershipType {
    /**
     * 
     * @type {string}
     * @memberof UboControlOwnershipType
     */
    'dnb_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UboControlOwnershipType
     */
    'description': string;
}
/**
 * Ubo nature of control type serializer.
 * @export
 * @interface UboNatureOfControlType
 */
export interface UboNatureOfControlType {
    /**
     * 
     * @type {string}
     * @memberof UboNatureOfControlType
     */
    'dnb_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UboNatureOfControlType
     */
    'description': string;
    /**
     * 
     * @type {UboNatureOfControlTypeControlClass}
     * @memberof UboNatureOfControlType
     */
    'control_class': UboNatureOfControlTypeControlClass;
    /**
     * 
     * @type {any}
     * @memberof UboNatureOfControlType
     */
    'control_reasons'?: any | null;
    /**
     * 
     * @type {boolean}
     * @memberof UboNatureOfControlType
     */
    'is_direct_control': boolean;
    /**
     * 
     * @type {number}
     * @memberof UboNatureOfControlType
     */
    'control_percentage'?: number | null;
}
/**
 * 
 * @export
 * @interface UboNatureOfControlTypeControlClass
 */
export interface UboNatureOfControlTypeControlClass {
    /**
     * 
     * @type {string}
     * @memberof UboNatureOfControlTypeControlClass
     */
    'dnb_code'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UboNatureOfControlTypeControlClass
     */
    'description': string;
}
/**
 * Serializer for unauthenticated error.
 * @export
 * @interface UnauthenticatedError
 */
export interface UnauthenticatedError {
    /**
     * 
     * @type {string}
     * @memberof UnauthenticatedError
     */
    'detail': string;
}
/**
 * Universal Company Serializer.
 * @export
 * @interface UniversalCompany
 */
export interface UniversalCompany {
    /**
     * 
     * @type {UniversalCompanyGlobalUltimate}
     * @memberof UniversalCompany
     */
    'global_ultimate': UniversalCompanyGlobalUltimate;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'website_icon_url': string;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'address_region': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'address_street': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'address_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'address_place': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'address_postal': string | null;
    /**
     * 
     * @type {DnbLinkageCompanyScoring}
     * @memberof UniversalCompany
     */
    'scoring': DnbLinkageCompanyScoring;
    /**
     * 
     * @type {Array<TradeName>}
     * @memberof UniversalCompany
     */
    'trade_names': Array<TradeName>;
    /**
     * 
     * @type {boolean}
     * @memberof UniversalCompany
     */
    'in_portfolio': boolean | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'duns_number': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'external_id': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'country': string | null;
    /**
     * 
     * @type {Array<IndustryCode>}
     * @memberof UniversalCompany
     */
    'industry_codes': Array<IndustryCode>;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompany
     */
    'social_logo'?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof UniversalCompany
     */
    'is_out_of_business'?: boolean | null;
    /**
     * 
     * @type {number}
     * @memberof UniversalCompany
     */
    'article_count'?: number | null;
}
/**
 * 
 * @export
 * @interface UniversalCompanyGlobalUltimate
 */
export interface UniversalCompanyGlobalUltimate {
    /**
     * 
     * @type {string}
     * @memberof UniversalCompanyGlobalUltimate
     */
    'duns_number': string;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompanyGlobalUltimate
     */
    'external_id': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompanyGlobalUltimate
     */
    'name': string;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompanyGlobalUltimate
     */
    'country': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompanyGlobalUltimate
     */
    'social_logo': string | null;
    /**
     * 
     * @type {string}
     * @memberof UniversalCompanyGlobalUltimate
     */
    'website_icon_url': string | null;
}
/**
 * * `EXCEL` - Excel * `CSV` - Comma Delimited File * `JSON` - JSON
 * @export
 * @enum {string}
 */

export const UploadFileTypeEnum = {
    Excel: 'EXCEL',
    Csv: 'CSV',
    Json: 'JSON'
} as const;

export type UploadFileTypeEnum = typeof UploadFileTypeEnum[keyof typeof UploadFileTypeEnum];


/**
 * User Data Serializer.
 * @export
 * @interface User
 */
export interface User {
    /**
     * 
     * @type {string}
     * @memberof User
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof User
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof User
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof User
     */
    'last_name': string;
}
/**
 * Get User Information.
 * @export
 * @interface UserDetail
 */
export interface UserDetail {
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'external_id': string;
    /**
     * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
     * @type {boolean}
     * @memberof UserDetail
     */
    'is_active': boolean;
    /**
     * Designates whether the user can log into this admin site.
     * @type {boolean}
     * @memberof UserDetail
     */
    'is_staff': boolean;
    /**
     * Designates that this user has all permissions without explicitly assigning them.
     * @type {boolean}
     * @memberof UserDetail
     */
    'is_superuser': boolean;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'last_login': string | null;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'date_joined': string;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserDetail
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'phone_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'first_name'?: string;
    /**
     * Get language name by code.
     * @type {string}
     * @memberof UserDetail
     */
    'language_name': string;
    /**
     * 
     * @type {UserDetailActiveProfile}
     * @memberof UserDetail
     */
    'active_profile': UserDetailActiveProfile;
    /**
     * 
     * @type {boolean}
     * @memberof UserDetail
     */
    'email_verified': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof UserDetail
     */
    'phone_number_verified': boolean;
    /**
     * 
     * @type {string}
     * @memberof UserDetail
     */
    'last_name'?: string;
    /**
     * Customize news alerts. Ex: Senior Independent Auditor
     * @type {string}
     * @memberof UserDetail
     */
    'function_role_description'?: string | null;
    /**
     * Get If complete translation is enabled.
     * @type {boolean}
     * @memberof UserDetail
     */
    'is_complete_translation_enabled': boolean;
    /**
     * Get If UBO compliance is enabled.
     * @type {boolean}
     * @memberof UserDetail
     */
    'is_compliance_enabled': boolean;
    /**
     * Get If UBO compliance is enabled.
     * @type {boolean}
     * @memberof UserDetail
     */
    'is_ubo_enabled': boolean;
    /**
     * Get if the user has multiple profiles.
     * @type {boolean}
     * @memberof UserDetail
     */
    'multiple_profiles': boolean;
    /**
     * Get the intercom user hash.
     * @type {string}
     * @memberof UserDetail
     */
    'intercom_user_hash': string;
    /**
     * Get the count of unconfirmed notifications for the user.  Excludes types the user disabled so the badge matches the filtered bell.
     * @type {number}
     * @memberof UserDetail
     */
    'unconfirmed_notification_count': number;
    /**
     * 
     * @type {Array<string>}
     * @memberof UserDetail
     */
    'tags': Array<string>;
}
/**
 * 
 * @export
 * @interface UserDetailActiveProfile
 */
export interface UserDetailActiveProfile {
    /**
     * 
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'name': string;
    /**
     * Get logo URL.
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'logo': string;
    /**
     * Get color.
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'primary_color': string;
    /**
     * Get color.
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'color1': string;
    /**
     * Get color.
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'color2': string;
    /**
     * Get color.
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'color3': string;
    /**
     * Get color.
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'color4': string;
    /**
     * 
     * @type {PatchedProfileRequestLanguage}
     * @memberof UserDetailActiveProfile
     */
    'language'?: PatchedProfileRequestLanguage | null;
    /**
     * Administrator flag for users with Admin permissions.  This is a User specific field, so this is not in the correct context, this custom methodfield gets the context and checks if the user is an administrator.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_admin': boolean;
    /**
     * Check if global import is enabled.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_global_import_enabled': boolean;
    /**
     * Enabled users in this profile can see UBO data.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_ubo_enabled': boolean;
    /**
     * Gateway derived from the narrow per-check-type compliance flags.  Compliance is enabled for the profile when at least one screening type (sanctions, PEP, adverse media or SOE) is enabled.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_compliance_enabled': boolean;
    /**
     * Enabled users in this profile get sanctions screening.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_compliance_sanctions_enabled': boolean;
    /**
     * Enabled users in this profile get politically exposed person (PEP) screening.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_compliance_pep_enabled': boolean;
    /**
     * Enabled users in this profile get adverse media screening.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_compliance_adverse_media_enabled': boolean;
    /**
     * Enabled users in this profile get state linked / owned entity (SOE) screening.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_compliance_soe_enabled': boolean;
    /**
     * Check if summirzation is enabled.
     * @type {boolean}
     * @memberof UserDetailActiveProfile
     */
    'is_summarization_enabled': boolean;
    /**
     * 
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof UserDetailActiveProfile
     */
    'updated_at': string;
}
/**
 * Get User Information.
 * @export
 * @interface UserDetailRequest
 */
export interface UserDetailRequest {
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserDetailRequest
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof UserDetailRequest
     */
    'phone_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UserDetailRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserDetailRequest
     */
    'last_name'?: string;
    /**
     * Customize news alerts. Ex: Senior Independent Auditor
     * @type {string}
     * @memberof UserDetailRequest
     */
    'function_role_description'?: string | null;
}
/**
 * User External Registration Serializer.
 * @export
 * @interface UserExternalRegistration
 */
export interface UserExternalRegistration {
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistration
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistration
     */
    'last_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistration
     */
    'email'?: string;
    /**
     * Get Refresh access token so that the user can sign in directly.
     * @type {string}
     * @memberof UserExternalRegistration
     */
    'refresh': string;
    /**
     * Get Access token so that the user can sign in directly.
     * @type {string}
     * @memberof UserExternalRegistration
     */
    'access': string;
}
/**
 * User External Registration Serializer.
 * @export
 * @interface UserExternalRegistrationRequest
 */
export interface UserExternalRegistrationRequest {
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistrationRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistrationRequest
     */
    'last_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistrationRequest
     */
    'email'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistrationRequest
     */
    'password1': string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistrationRequest
     */
    'password2': string;
    /**
     * 
     * @type {string}
     * @memberof UserExternalRegistrationRequest
     */
    'recaptcha_key': string;
}
/**
 * List Users.
 * @export
 * @interface UserList
 */
export interface UserList {
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'last_login': string | null;
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'date_joined': string;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserList
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * Get language name by code.
     * @type {string}
     * @memberof UserList
     */
    'language_name': string;
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserList
     */
    'last_name'?: string;
}
/**
 * Updating User Password.
 * @export
 * @interface UserPasswordChangeRequest
 */
export interface UserPasswordChangeRequest {
    /**
     * 
     * @type {string}
     * @memberof UserPasswordChangeRequest
     */
    'old_password': string;
    /**
     * 
     * @type {string}
     * @memberof UserPasswordChangeRequest
     */
    'new_password1': string;
    /**
     * 
     * @type {string}
     * @memberof UserPasswordChangeRequest
     */
    'new_password2': string;
}
/**
 * User Profile Detail Serializer.
 * @export
 * @interface UserProfileDetail
 */
export interface UserProfileDetail {
    /**
     * 
     * @type {UserProfileDetailUser}
     * @memberof UserProfileDetail
     */
    'user': UserProfileDetailUser;
    /**
     * 
     * @type {UserDetailActiveProfile}
     * @memberof UserProfileDetail
     */
    'profile': UserDetailActiveProfile;
    /**
     * Flag indicating if the user has admin permissions.
     * @type {boolean}
     * @memberof UserProfileDetail
     */
    'is_admin'?: boolean;
    /**
     * Receive monthly report about user analytics via mail.
     * @type {boolean}
     * @memberof UserProfileDetail
     */
    'monthly_user_statistics'?: boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof UserProfileDetail
     */
    'customer_reference'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetail
     */
    'created_at': string;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetail
     */
    'updated_at': string;
}
/**
 * User Profile Detail Serializer.
 * @export
 * @interface UserProfileDetailRequest
 */
export interface UserProfileDetailRequest {
    /**
     * Flag indicating if the user has admin permissions.
     * @type {boolean}
     * @memberof UserProfileDetailRequest
     */
    'is_admin'?: boolean;
    /**
     * Receive monthly report about user analytics via mail.
     * @type {boolean}
     * @memberof UserProfileDetailRequest
     */
    'monthly_user_statistics'?: boolean;
    /**
     * Customer reference for the client to understand relationship.
     * @type {string}
     * @memberof UserProfileDetailRequest
     */
    'customer_reference'?: string | null;
}
/**
 * 
 * @export
 * @interface UserProfileDetailUser
 */
export interface UserProfileDetailUser {
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'external_id': string;
    /**
     * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'is_active': boolean;
    /**
     * Designates whether the user can log into this admin site.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'is_staff': boolean;
    /**
     * Designates that this user has all permissions without explicitly assigning them.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'is_superuser': boolean;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'last_login': string | null;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'date_joined': string;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserProfileDetailUser
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'phone_number'?: string | null;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'first_name'?: string;
    /**
     * Get language name by code.
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'language_name': string;
    /**
     * 
     * @type {UserDetailActiveProfile}
     * @memberof UserProfileDetailUser
     */
    'active_profile': UserDetailActiveProfile;
    /**
     * 
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'email_verified': boolean;
    /**
     * 
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'phone_number_verified': boolean;
    /**
     * 
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'last_name'?: string;
    /**
     * Customize news alerts. Ex: Senior Independent Auditor
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'function_role_description'?: string | null;
    /**
     * Get If complete translation is enabled.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'is_complete_translation_enabled': boolean;
    /**
     * Get If UBO compliance is enabled.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'is_compliance_enabled': boolean;
    /**
     * Get If UBO compliance is enabled.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'is_ubo_enabled': boolean;
    /**
     * Get if the user has multiple profiles.
     * @type {boolean}
     * @memberof UserProfileDetailUser
     */
    'multiple_profiles': boolean;
    /**
     * Get the intercom user hash.
     * @type {string}
     * @memberof UserProfileDetailUser
     */
    'intercom_user_hash': string;
    /**
     * Get the count of unconfirmed notifications for the user.  Excludes types the user disabled so the badge matches the filtered bell.
     * @type {number}
     * @memberof UserProfileDetailUser
     */
    'unconfirmed_notification_count': number;
    /**
     * 
     * @type {Array<string>}
     * @memberof UserProfileDetailUser
     */
    'tags': Array<string>;
}
/**
 * Register an user.
 * @export
 * @interface UserRegistration
 */
export interface UserRegistration {
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'last_name'?: string;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserRegistration
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'date_joined': string;
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'last_login': string | null;
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof UserRegistration
     */
    'username': string;
    /**
     * Get Refresh access token so that the user can sign in directly.
     * @type {string}
     * @memberof UserRegistration
     */
    'refresh': string;
    /**
     * Get Access token so that the user can sign in directly.
     * @type {string}
     * @memberof UserRegistration
     */
    'access': string;
    /**
     * Get language name by code.
     * @type {string}
     * @memberof UserRegistration
     */
    'language_name': string;
}
/**
 * Register an user.
 * @export
 * @interface UserRegistrationRequest
 */
export interface UserRegistrationRequest {
    /**
     * 
     * @type {string}
     * @memberof UserRegistrationRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserRegistrationRequest
     */
    'last_name'?: string;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserRegistrationRequest
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof UserRegistrationRequest
     */
    'password1': string;
    /**
     * 
     * @type {string}
     * @memberof UserRegistrationRequest
     */
    'password2': string;
}
/**
 * User Data Serializer.
 * @export
 * @interface UserRequest
 */
export interface UserRequest {
    /**
     * 
     * @type {string}
     * @memberof UserRequest
     */
    'external_id': string;
}
/**
 * Update User Information.
 * @export
 * @interface UserUpdateRequest
 */
export interface UserUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof UserUpdateRequest
     */
    'phone_number'?: string | null;
    /**
     * Customize news alerts. Ex: Senior Independent Auditor
     * @type {string}
     * @memberof UserUpdateRequest
     */
    'function_role_description'?: string | null;
    /**
     * 
     * @type {PatchedUserUpdateRequestLanguage}
     * @memberof UserUpdateRequest
     */
    'language'?: PatchedUserUpdateRequestLanguage | null;
    /**
     * 
     * @type {string}
     * @memberof UserUpdateRequest
     */
    'active_profile': string;
    /**
     * 
     * @type {string}
     * @memberof UserUpdateRequest
     */
    'first_name'?: string;
    /**
     * 
     * @type {string}
     * @memberof UserUpdateRequest
     */
    'last_name'?: string;
}
/**
 * Get User Serializer.
 * @export
 * @interface UserWrite
 */
export interface UserWrite {
    /**
     * 
     * @type {string}
     * @memberof UserWrite
     */
    'external_id': string;
    /**
     * 
     * @type {string}
     * @memberof UserWrite
     */
    'username': string;
    /**
     * 
     * @type {string}
     * @memberof UserWrite
     */
    'email': string;
    /**
     * 
     * @type {string}
     * @memberof UserWrite
     */
    'first_name': string;
    /**
     * 
     * @type {string}
     * @memberof UserWrite
     */
    'last_name': string;
}
/**
 * Get User Serializer.
 * @export
 * @interface UserWriteRequest
 */
export interface UserWriteRequest {
    /**
     * 
     * @type {string}
     * @memberof UserWriteRequest
     */
    'external_id': string;
}
/**
 * Serializer for validation error.
 * @export
 * @interface ValidationError
 */
export interface ValidationError {
    /**
     * 
     * @type {{ [key: string]: Array<string>; }}
     * @memberof ValidationError
     */
    'errors': { [key: string]: Array<string>; };
    /**
     * 
     * @type {Array<string>}
     * @memberof ValidationError
     */
    'non_field_errors': Array<string>;
}
/**
 * Serializer for Websocket Sessions.
 * @export
 * @interface WebsocketSession
 */
export interface WebsocketSession {
    /**
     * 
     * @type {string}
     * @memberof WebsocketSession
     */
    'external_id': string;
    /**
     * Session Authentication token
     * @type {string}
     * @memberof WebsocketSession
     */
    'access_token': string;
    /**
     * Datetime of when last message was send
     * @type {string}
     * @memberof WebsocketSession
     */
    'last_message_send_at': string | null;
    /**
     * 
     * @type {string}
     * @memberof WebsocketSession
     */
    'expired_at': string;
    /**
     * 
     * @type {string}
     * @memberof WebsocketSession
     */
    'updated_at': string;
    /**
     * 
     * @type {string}
     * @memberof WebsocketSession
     */
    'created_at': string;
}

/**
 * ActivitiesApi - axios parameter creator
 * @export
 */
export const ActivitiesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * List or create comments.
         * @param {CommentRequest} commentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsCreate: async (commentRequest: CommentRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentRequest' is not null or undefined
            assertParamExists('activitiesCommentsCreate', 'commentRequest', commentRequest)
            const localVarPath = `/activities/comments/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(commentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsDestroy: async (commentId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('activitiesCommentsDestroy', 'commentId', commentId)
            const localVarPath = `/activities/comments/{comment_id}/`
                .replace(`{${"comment_id"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Validate the uploaded image, store it and return the URL to embed.  The image is embedded in the comment HTML by URL, so no base64 payload ends up in ``Comment.text``, and only this CDN host is accepted as an ``<img>`` source by ``sanitize_comment_html``.  Bytes travel through the API rather than a pre-signed S3 PUT for two reasons: the image bucket\'s CORS policy only allows GET from a browser, and the objects are served from our own CDN domain, so they are decoded and size-capped here instead of trusting whatever the client uploads.
         * @param {any} image 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsImagesCreate: async (image: any, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'image' is not null or undefined
            assertParamExists('activitiesCommentsImagesCreate', 'image', image)
            const localVarPath = `/activities/comments/images/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (image !== undefined) { 
                localVarFormParams.append('image', image as any);
            }
    
    
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List or create comments.
         * @param {string} [binderId] Filter comments for a specific binder (external_id).
         * @param {string} [binderItemId] Filter comments for a specific binder item (external_id).
         * @param {string} [complianceEntityResultId] Filter comments for a specific compliance entity result.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter comments by text or creator username.
         * @param {'created_at' | 'updated_at'} [sorting] Sort comments
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsList: async (binderId?: string, binderItemId?: string, complianceEntityResultId?: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/activities/comments/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (binderId !== undefined) {
                localVarQueryParameter['binder_id'] = binderId;
            }

            if (binderItemId !== undefined) {
                localVarQueryParameter['binder_item_id'] = binderItemId;
            }

            if (complianceEntityResultId !== undefined) {
                localVarQueryParameter['compliance_entity_result_id'] = complianceEntityResultId;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {PatchedCommentRequest} [patchedCommentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsPartialUpdate: async (commentId: string, patchedCommentRequest?: PatchedCommentRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('activitiesCommentsPartialUpdate', 'commentId', commentId)
            const localVarPath = `/activities/comments/{comment_id}/`
                .replace(`{${"comment_id"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedCommentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsRetrieve: async (commentId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('activitiesCommentsRetrieve', 'commentId', commentId)
            const localVarPath = `/activities/comments/{comment_id}/`
                .replace(`{${"comment_id"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {CommentRequest} commentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsUpdate: async (commentId: string, commentRequest: CommentRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('activitiesCommentsUpdate', 'commentId', commentId)
            // verify required parameter 'commentRequest' is not null or undefined
            assertParamExists('activitiesCommentsUpdate', 'commentRequest', commentRequest)
            const localVarPath = `/activities/comments/{comment_id}/`
                .replace(`{${"comment_id"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(commentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationSettingsList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/activities/notification_settings/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
         * @param {Array<NotificationSettingRequest>} notificationSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationSettingsUpdate: async (notificationSettingRequest: Array<NotificationSettingRequest>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationSettingRequest' is not null or undefined
            assertParamExists('activitiesNotificationSettingsUpdate', 'notificationSettingRequest', notificationSettingRequest)
            const localVarPath = `/activities/notification_settings/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(notificationSettingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Batch confirm notifications.
         * @param {Array<NotificationBatchUpdateRequest>} notificationBatchUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsBatchUpdatePartialUpdate: async (notificationBatchUpdateRequest: Array<NotificationBatchUpdateRequest>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationBatchUpdateRequest' is not null or undefined
            assertParamExists('activitiesNotificationsBatchUpdatePartialUpdate', 'notificationBatchUpdateRequest', notificationBatchUpdateRequest)
            const localVarPath = `/activities/notifications/batch_update/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(notificationBatchUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List notifications for the current user and active profile.
         * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
         * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
         * @param {string} [minCreatedAt] Filter notifications created on or after this date.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
         * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
         * @param {string} [query] Free-text search across the notification payload.
         * @param {'confirmed_at' | 'created_at' | 'priority' | 'updated_at'} [sorting] Sort notifications
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order
         * @param {string} [status] Filter by status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsList: async (confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, nextKey?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, sorting?: 'confirmed_at' | 'created_at' | 'priority' | 'updated_at', sortingOrder?: 'ascending' | 'descending', status?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/activities/notifications/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (confirmed !== undefined) {
                localVarQueryParameter['confirmed'] = confirmed;
            }

            if (maxCreatedAt !== undefined) {
                localVarQueryParameter['max_created_at'] = (maxCreatedAt as any instanceof Date) ?
                    (maxCreatedAt as any).toISOString() :
                    maxCreatedAt;
            }

            if (minCreatedAt !== undefined) {
                localVarQueryParameter['min_created_at'] = (minCreatedAt as any instanceof Date) ?
                    (minCreatedAt as any).toISOString() :
                    minCreatedAt;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (notificationType) {
                localVarQueryParameter['notification_type'] = notificationType;
            }

            if (priority !== undefined) {
                localVarQueryParameter['priority'] = priority;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update (confirm) a notification.
         * @param {string} notificationId 
         * @param {PatchedNotificationRequest} [patchedNotificationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsPartialUpdate: async (notificationId: string, patchedNotificationRequest?: PatchedNotificationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('activitiesNotificationsPartialUpdate', 'notificationId', notificationId)
            const localVarPath = `/activities/notifications/{notification_id}/`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedNotificationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update (confirm) a notification.
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsRetrieve: async (notificationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('activitiesNotificationsRetrieve', 'notificationId', notificationId)
            const localVarPath = `/activities/notifications/{notification_id}/`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Notification counts grouped by notification type.  Unlike the paginated list endpoint, these counts cover all matching notifications for the current user and active profile, independent of pagination, so the inbox can show stable per-tab badges. The same filters as the list endpoint are accepted; when ``confirmed`` is not supplied the counts default to unread notifications only.
         * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
         * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
         * @param {string} [minCreatedAt] Filter notifications created on or after this date.
         * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
         * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
         * @param {string} [query] Free-text search across the notification payload.
         * @param {string} [status] Filter by status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsStatisticsRetrieve: async (confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, status?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/activities/notifications/statistics/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (confirmed !== undefined) {
                localVarQueryParameter['confirmed'] = confirmed;
            }

            if (maxCreatedAt !== undefined) {
                localVarQueryParameter['max_created_at'] = (maxCreatedAt as any instanceof Date) ?
                    (maxCreatedAt as any).toISOString() :
                    maxCreatedAt;
            }

            if (minCreatedAt !== undefined) {
                localVarQueryParameter['min_created_at'] = (minCreatedAt as any instanceof Date) ?
                    (minCreatedAt as any).toISOString() :
                    minCreatedAt;
            }

            if (notificationType) {
                localVarQueryParameter['notification_type'] = notificationType;
            }

            if (priority !== undefined) {
                localVarQueryParameter['priority'] = priority;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ActivitiesApi - functional programming interface
 * @export
 */
export const ActivitiesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ActivitiesApiAxiosParamCreator(configuration)
    return {
        /**
         * List or create comments.
         * @param {CommentRequest} commentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsCreate(commentRequest: CommentRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Comment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsCreate(commentRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsDestroy(commentId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsDestroy(commentId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Validate the uploaded image, store it and return the URL to embed.  The image is embedded in the comment HTML by URL, so no base64 payload ends up in ``Comment.text``, and only this CDN host is accepted as an ``<img>`` source by ``sanitize_comment_html``.  Bytes travel through the API rather than a pre-signed S3 PUT for two reasons: the image bucket\'s CORS policy only allows GET from a browser, and the objects are served from our own CDN domain, so they are decoded and size-capped here instead of trusting whatever the client uploads.
         * @param {any} image 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsImagesCreate(image: any, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CommentImageUpload>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsImagesCreate(image, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List or create comments.
         * @param {string} [binderId] Filter comments for a specific binder (external_id).
         * @param {string} [binderItemId] Filter comments for a specific binder item (external_id).
         * @param {string} [complianceEntityResultId] Filter comments for a specific compliance entity result.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter comments by text or creator username.
         * @param {'created_at' | 'updated_at'} [sorting] Sort comments
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsList(binderId?: string, binderItemId?: string, complianceEntityResultId?: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedCommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsList(binderId, binderItemId, complianceEntityResultId, nextKey, query, sorting, sortingOrder, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {PatchedCommentRequest} [patchedCommentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsPartialUpdate(commentId: string, patchedCommentRequest?: PatchedCommentRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Comment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsPartialUpdate(commentId, patchedCommentRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsRetrieve(commentId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Comment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsRetrieve(commentId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {CommentRequest} commentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesCommentsUpdate(commentId: string, commentRequest: CommentRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Comment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesCommentsUpdate(commentId, commentRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationSettingsList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<NotificationSetting>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationSettingsList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
         * @param {Array<NotificationSettingRequest>} notificationSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationSettingsUpdate(notificationSettingRequest: Array<NotificationSettingRequest>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<NotificationSetting>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationSettingsUpdate(notificationSettingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Batch confirm notifications.
         * @param {Array<NotificationBatchUpdateRequest>} notificationBatchUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationsBatchUpdatePartialUpdate(notificationBatchUpdateRequest: Array<NotificationBatchUpdateRequest>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<Notification>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationsBatchUpdatePartialUpdate(notificationBatchUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List notifications for the current user and active profile.
         * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
         * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
         * @param {string} [minCreatedAt] Filter notifications created on or after this date.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
         * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
         * @param {string} [query] Free-text search across the notification payload.
         * @param {'confirmed_at' | 'created_at' | 'priority' | 'updated_at'} [sorting] Sort notifications
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order
         * @param {string} [status] Filter by status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationsList(confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, nextKey?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, sorting?: 'confirmed_at' | 'created_at' | 'priority' | 'updated_at', sortingOrder?: 'ascending' | 'descending', status?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedNotificationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationsList(confirmed, maxCreatedAt, minCreatedAt, nextKey, notificationType, priority, query, sorting, sortingOrder, status, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update (confirm) a notification.
         * @param {string} notificationId 
         * @param {PatchedNotificationRequest} [patchedNotificationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationsPartialUpdate(notificationId: string, patchedNotificationRequest?: PatchedNotificationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Notification>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationsPartialUpdate(notificationId, patchedNotificationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update (confirm) a notification.
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationsRetrieve(notificationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Notification>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationsRetrieve(notificationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Notification counts grouped by notification type.  Unlike the paginated list endpoint, these counts cover all matching notifications for the current user and active profile, independent of pagination, so the inbox can show stable per-tab badges. The same filters as the list endpoint are accepted; when ``confirmed`` is not supplied the counts default to unread notifications only.
         * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
         * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
         * @param {string} [minCreatedAt] Filter notifications created on or after this date.
         * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
         * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
         * @param {string} [query] Free-text search across the notification payload.
         * @param {string} [status] Filter by status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activitiesNotificationsStatisticsRetrieve(confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, status?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationStatistics>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activitiesNotificationsStatisticsRetrieve(confirmed, maxCreatedAt, minCreatedAt, notificationType, priority, query, status, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * ActivitiesApi - factory interface
 * @export
 */
export const ActivitiesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ActivitiesApiFp(configuration)
    return {
        /**
         * List or create comments.
         * @param {CommentRequest} commentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsCreate(commentRequest: CommentRequest, options?: any): AxiosPromise<Comment> {
            return localVarFp.activitiesCommentsCreate(commentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsDestroy(commentId: string, options?: any): AxiosPromise<void> {
            return localVarFp.activitiesCommentsDestroy(commentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Validate the uploaded image, store it and return the URL to embed.  The image is embedded in the comment HTML by URL, so no base64 payload ends up in ``Comment.text``, and only this CDN host is accepted as an ``<img>`` source by ``sanitize_comment_html``.  Bytes travel through the API rather than a pre-signed S3 PUT for two reasons: the image bucket\'s CORS policy only allows GET from a browser, and the objects are served from our own CDN domain, so they are decoded and size-capped here instead of trusting whatever the client uploads.
         * @param {any} image 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsImagesCreate(image: any, options?: any): AxiosPromise<CommentImageUpload> {
            return localVarFp.activitiesCommentsImagesCreate(image, options).then((request) => request(axios, basePath));
        },
        /**
         * List or create comments.
         * @param {string} [binderId] Filter comments for a specific binder (external_id).
         * @param {string} [binderItemId] Filter comments for a specific binder item (external_id).
         * @param {string} [complianceEntityResultId] Filter comments for a specific compliance entity result.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter comments by text or creator username.
         * @param {'created_at' | 'updated_at'} [sorting] Sort comments
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsList(binderId?: string, binderItemId?: string, complianceEntityResultId?: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options?: any): AxiosPromise<PaginatedCommentList> {
            return localVarFp.activitiesCommentsList(binderId, binderItemId, complianceEntityResultId, nextKey, query, sorting, sortingOrder, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {PatchedCommentRequest} [patchedCommentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsPartialUpdate(commentId: string, patchedCommentRequest?: PatchedCommentRequest, options?: any): AxiosPromise<Comment> {
            return localVarFp.activitiesCommentsPartialUpdate(commentId, patchedCommentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsRetrieve(commentId: string, options?: any): AxiosPromise<Comment> {
            return localVarFp.activitiesCommentsRetrieve(commentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or delete a comment.
         * @param {string} commentId 
         * @param {CommentRequest} commentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesCommentsUpdate(commentId: string, commentRequest: CommentRequest, options?: any): AxiosPromise<Comment> {
            return localVarFp.activitiesCommentsUpdate(commentId, commentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationSettingsList(options?: any): AxiosPromise<Array<NotificationSetting>> {
            return localVarFp.activitiesNotificationSettingsList(options).then((request) => request(axios, basePath));
        },
        /**
         * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
         * @param {Array<NotificationSettingRequest>} notificationSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationSettingsUpdate(notificationSettingRequest: Array<NotificationSettingRequest>, options?: any): AxiosPromise<Array<NotificationSetting>> {
            return localVarFp.activitiesNotificationSettingsUpdate(notificationSettingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Batch confirm notifications.
         * @param {Array<NotificationBatchUpdateRequest>} notificationBatchUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsBatchUpdatePartialUpdate(notificationBatchUpdateRequest: Array<NotificationBatchUpdateRequest>, options?: any): AxiosPromise<Array<Notification>> {
            return localVarFp.activitiesNotificationsBatchUpdatePartialUpdate(notificationBatchUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List notifications for the current user and active profile.
         * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
         * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
         * @param {string} [minCreatedAt] Filter notifications created on or after this date.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
         * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
         * @param {string} [query] Free-text search across the notification payload.
         * @param {'confirmed_at' | 'created_at' | 'priority' | 'updated_at'} [sorting] Sort notifications
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order
         * @param {string} [status] Filter by status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsList(confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, nextKey?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, sorting?: 'confirmed_at' | 'created_at' | 'priority' | 'updated_at', sortingOrder?: 'ascending' | 'descending', status?: string, options?: any): AxiosPromise<PaginatedNotificationList> {
            return localVarFp.activitiesNotificationsList(confirmed, maxCreatedAt, minCreatedAt, nextKey, notificationType, priority, query, sorting, sortingOrder, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update (confirm) a notification.
         * @param {string} notificationId 
         * @param {PatchedNotificationRequest} [patchedNotificationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsPartialUpdate(notificationId: string, patchedNotificationRequest?: PatchedNotificationRequest, options?: any): AxiosPromise<Notification> {
            return localVarFp.activitiesNotificationsPartialUpdate(notificationId, patchedNotificationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update (confirm) a notification.
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsRetrieve(notificationId: string, options?: any): AxiosPromise<Notification> {
            return localVarFp.activitiesNotificationsRetrieve(notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Notification counts grouped by notification type.  Unlike the paginated list endpoint, these counts cover all matching notifications for the current user and active profile, independent of pagination, so the inbox can show stable per-tab badges. The same filters as the list endpoint are accepted; when ``confirmed`` is not supplied the counts default to unread notifications only.
         * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
         * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
         * @param {string} [minCreatedAt] Filter notifications created on or after this date.
         * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
         * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
         * @param {string} [query] Free-text search across the notification payload.
         * @param {string} [status] Filter by status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activitiesNotificationsStatisticsRetrieve(confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, status?: string, options?: any): AxiosPromise<NotificationStatistics> {
            return localVarFp.activitiesNotificationsStatisticsRetrieve(confirmed, maxCreatedAt, minCreatedAt, notificationType, priority, query, status, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ActivitiesApi - interface
 * @export
 * @interface ActivitiesApi
 */
export interface ActivitiesApiInterface {
    /**
     * List or create comments.
     * @param {CommentRequest} commentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsCreate(commentRequest: CommentRequest, options?: AxiosRequestConfig): AxiosPromise<Comment>;

    /**
     * Retrieve, update or delete a comment.
     * @param {string} commentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsDestroy(commentId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Validate the uploaded image, store it and return the URL to embed.  The image is embedded in the comment HTML by URL, so no base64 payload ends up in ``Comment.text``, and only this CDN host is accepted as an ``<img>`` source by ``sanitize_comment_html``.  Bytes travel through the API rather than a pre-signed S3 PUT for two reasons: the image bucket\'s CORS policy only allows GET from a browser, and the objects are served from our own CDN domain, so they are decoded and size-capped here instead of trusting whatever the client uploads.
     * @param {any} image 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsImagesCreate(image: any, options?: AxiosRequestConfig): AxiosPromise<CommentImageUpload>;

    /**
     * List or create comments.
     * @param {string} [binderId] Filter comments for a specific binder (external_id).
     * @param {string} [binderItemId] Filter comments for a specific binder item (external_id).
     * @param {string} [complianceEntityResultId] Filter comments for a specific compliance entity result.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter comments by text or creator username.
     * @param {'created_at' | 'updated_at'} [sorting] Sort comments
     * @param {'ascending' | 'descending'} [sortingOrder] Sort order
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsList(binderId?: string, binderItemId?: string, complianceEntityResultId?: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): AxiosPromise<PaginatedCommentList>;

    /**
     * Retrieve, update or delete a comment.
     * @param {string} commentId 
     * @param {PatchedCommentRequest} [patchedCommentRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsPartialUpdate(commentId: string, patchedCommentRequest?: PatchedCommentRequest, options?: AxiosRequestConfig): AxiosPromise<Comment>;

    /**
     * Retrieve, update or delete a comment.
     * @param {string} commentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsRetrieve(commentId: string, options?: AxiosRequestConfig): AxiosPromise<Comment>;

    /**
     * Retrieve, update or delete a comment.
     * @param {string} commentId 
     * @param {CommentRequest} commentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesCommentsUpdate(commentId: string, commentRequest: CommentRequest, options?: AxiosRequestConfig): AxiosPromise<Comment>;

    /**
     * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationSettingsList(options?: AxiosRequestConfig): AxiosPromise<Array<NotificationSetting>>;

    /**
     * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
     * @param {Array<NotificationSettingRequest>} notificationSettingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationSettingsUpdate(notificationSettingRequest: Array<NotificationSettingRequest>, options?: AxiosRequestConfig): AxiosPromise<Array<NotificationSetting>>;

    /**
     * Batch confirm notifications.
     * @param {Array<NotificationBatchUpdateRequest>} notificationBatchUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationsBatchUpdatePartialUpdate(notificationBatchUpdateRequest: Array<NotificationBatchUpdateRequest>, options?: AxiosRequestConfig): AxiosPromise<Array<Notification>>;

    /**
     * List notifications for the current user and active profile.
     * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
     * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
     * @param {string} [minCreatedAt] Filter notifications created on or after this date.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
     * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
     * @param {string} [query] Free-text search across the notification payload.
     * @param {'confirmed_at' | 'created_at' | 'priority' | 'updated_at'} [sorting] Sort notifications
     * @param {'ascending' | 'descending'} [sortingOrder] Sort order
     * @param {string} [status] Filter by status.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationsList(confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, nextKey?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, sorting?: 'confirmed_at' | 'created_at' | 'priority' | 'updated_at', sortingOrder?: 'ascending' | 'descending', status?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedNotificationList>;

    /**
     * Retrieve or update (confirm) a notification.
     * @param {string} notificationId 
     * @param {PatchedNotificationRequest} [patchedNotificationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationsPartialUpdate(notificationId: string, patchedNotificationRequest?: PatchedNotificationRequest, options?: AxiosRequestConfig): AxiosPromise<Notification>;

    /**
     * Retrieve or update (confirm) a notification.
     * @param {string} notificationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationsRetrieve(notificationId: string, options?: AxiosRequestConfig): AxiosPromise<Notification>;

    /**
     * Notification counts grouped by notification type.  Unlike the paginated list endpoint, these counts cover all matching notifications for the current user and active profile, independent of pagination, so the inbox can show stable per-tab badges. The same filters as the list endpoint are accepted; when ``confirmed`` is not supplied the counts default to unread notifications only.
     * @param {'false' | 'true'} [confirmed] Filter by confirmation status.
     * @param {string} [maxCreatedAt] Filter notifications created on or before this date.
     * @param {string} [minCreatedAt] Filter notifications created on or after this date.
     * @param {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>} [notificationType] Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
     * @param {'HIGH' | 'LOW' | 'MEDIUM'} [priority] Filter by priority.
     * @param {string} [query] Free-text search across the notification payload.
     * @param {string} [status] Filter by status.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApiInterface
     */
    activitiesNotificationsStatisticsRetrieve(confirmed?: 'false' | 'true', maxCreatedAt?: string, minCreatedAt?: string, notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>, priority?: 'HIGH' | 'LOW' | 'MEDIUM', query?: string, status?: string, options?: AxiosRequestConfig): AxiosPromise<NotificationStatistics>;

}

/**
 * Request parameters for activitiesCommentsCreate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsCreateRequest
 */
export interface ActivitiesApiActivitiesCommentsCreateRequest {
    /**
     * 
     * @type {CommentRequest}
     * @memberof ActivitiesApiActivitiesCommentsCreate
     */
    readonly commentRequest: CommentRequest
}

/**
 * Request parameters for activitiesCommentsDestroy operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsDestroyRequest
 */
export interface ActivitiesApiActivitiesCommentsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsDestroy
     */
    readonly commentId: string
}

/**
 * Request parameters for activitiesCommentsImagesCreate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsImagesCreateRequest
 */
export interface ActivitiesApiActivitiesCommentsImagesCreateRequest {
    /**
     * 
     * @type {any}
     * @memberof ActivitiesApiActivitiesCommentsImagesCreate
     */
    readonly image: any
}

/**
 * Request parameters for activitiesCommentsList operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsListRequest
 */
export interface ActivitiesApiActivitiesCommentsListRequest {
    /**
     * Filter comments for a specific binder (external_id).
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly binderId?: string

    /**
     * Filter comments for a specific binder item (external_id).
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly binderItemId?: string

    /**
     * Filter comments for a specific compliance entity result.
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly complianceEntityResultId?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly nextKey?: string

    /**
     * Filter comments by text or creator username.
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly query?: string

    /**
     * Sort comments
     * @type {'created_at' | 'updated_at'}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly sorting?: 'created_at' | 'updated_at'

    /**
     * Sort order
     * @type {'ascending' | 'descending'}
     * @memberof ActivitiesApiActivitiesCommentsList
     */
    readonly sortingOrder?: 'ascending' | 'descending'
}

/**
 * Request parameters for activitiesCommentsPartialUpdate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsPartialUpdateRequest
 */
export interface ActivitiesApiActivitiesCommentsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsPartialUpdate
     */
    readonly commentId: string

    /**
     * 
     * @type {PatchedCommentRequest}
     * @memberof ActivitiesApiActivitiesCommentsPartialUpdate
     */
    readonly patchedCommentRequest?: PatchedCommentRequest
}

/**
 * Request parameters for activitiesCommentsRetrieve operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsRetrieveRequest
 */
export interface ActivitiesApiActivitiesCommentsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsRetrieve
     */
    readonly commentId: string
}

/**
 * Request parameters for activitiesCommentsUpdate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesCommentsUpdateRequest
 */
export interface ActivitiesApiActivitiesCommentsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ActivitiesApiActivitiesCommentsUpdate
     */
    readonly commentId: string

    /**
     * 
     * @type {CommentRequest}
     * @memberof ActivitiesApiActivitiesCommentsUpdate
     */
    readonly commentRequest: CommentRequest
}

/**
 * Request parameters for activitiesNotificationSettingsUpdate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesNotificationSettingsUpdateRequest
 */
export interface ActivitiesApiActivitiesNotificationSettingsUpdateRequest {
    /**
     * 
     * @type {Array<NotificationSettingRequest>}
     * @memberof ActivitiesApiActivitiesNotificationSettingsUpdate
     */
    readonly notificationSettingRequest: Array<NotificationSettingRequest>
}

/**
 * Request parameters for activitiesNotificationsBatchUpdatePartialUpdate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesNotificationsBatchUpdatePartialUpdateRequest
 */
export interface ActivitiesApiActivitiesNotificationsBatchUpdatePartialUpdateRequest {
    /**
     * 
     * @type {Array<NotificationBatchUpdateRequest>}
     * @memberof ActivitiesApiActivitiesNotificationsBatchUpdatePartialUpdate
     */
    readonly notificationBatchUpdateRequest: Array<NotificationBatchUpdateRequest>
}

/**
 * Request parameters for activitiesNotificationsList operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesNotificationsListRequest
 */
export interface ActivitiesApiActivitiesNotificationsListRequest {
    /**
     * Filter by confirmation status.
     * @type {'false' | 'true'}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly confirmed?: 'false' | 'true'

    /**
     * Filter notifications created on or before this date.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly maxCreatedAt?: string

    /**
     * Filter notifications created on or after this date.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly minCreatedAt?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly nextKey?: string

    /**
     * Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
     * @type {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>

    /**
     * Filter by priority.
     * @type {'HIGH' | 'LOW' | 'MEDIUM'}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly priority?: 'HIGH' | 'LOW' | 'MEDIUM'

    /**
     * Free-text search across the notification payload.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly query?: string

    /**
     * Sort notifications
     * @type {'confirmed_at' | 'created_at' | 'priority' | 'updated_at'}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly sorting?: 'confirmed_at' | 'created_at' | 'priority' | 'updated_at'

    /**
     * Sort order
     * @type {'ascending' | 'descending'}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly sortingOrder?: 'ascending' | 'descending'

    /**
     * Filter by status.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsList
     */
    readonly status?: string
}

/**
 * Request parameters for activitiesNotificationsPartialUpdate operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesNotificationsPartialUpdateRequest
 */
export interface ActivitiesApiActivitiesNotificationsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsPartialUpdate
     */
    readonly notificationId: string

    /**
     * 
     * @type {PatchedNotificationRequest}
     * @memberof ActivitiesApiActivitiesNotificationsPartialUpdate
     */
    readonly patchedNotificationRequest?: PatchedNotificationRequest
}

/**
 * Request parameters for activitiesNotificationsRetrieve operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesNotificationsRetrieveRequest
 */
export interface ActivitiesApiActivitiesNotificationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsRetrieve
     */
    readonly notificationId: string
}

/**
 * Request parameters for activitiesNotificationsStatisticsRetrieve operation in ActivitiesApi.
 * @export
 * @interface ActivitiesApiActivitiesNotificationsStatisticsRetrieveRequest
 */
export interface ActivitiesApiActivitiesNotificationsStatisticsRetrieveRequest {
    /**
     * Filter by confirmation status.
     * @type {'false' | 'true'}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly confirmed?: 'false' | 'true'

    /**
     * Filter notifications created on or before this date.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly maxCreatedAt?: string

    /**
     * Filter notifications created on or after this date.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly minCreatedAt?: string

    /**
     * Filter by notification type. Repeat the parameter to match multiple types, e.g. ?notification_type&#x3D;COMMENT&amp;notification_type&#x3D;BINDER_COMMENT.
     * @type {Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly notificationType?: Array<'BATCH_COMPLIANCE_CHECK_UPDATE' | 'BATCH_REGISTRATION_UPDATE' | 'BINDER_COMMENT' | 'BINDER_COMMENT_MENTION' | 'COMMENT' | 'COMMENT_MENTION' | 'COMPANY_FEEDBACK_RECEIVED' | 'COMPANY_REGISTRATION_UPDATE' | 'COMPLIANCE_CHECK_UPDATE' | 'EXPORT_UPDATE' | 'MISSING_COMPANY_INVESTIGATION_UPDATE' | 'NEWS_ARTICLE_FEEDBACK_RECEIVED' | 'SUPPORT_REQUEST_UPDATE'>

    /**
     * Filter by priority.
     * @type {'HIGH' | 'LOW' | 'MEDIUM'}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly priority?: 'HIGH' | 'LOW' | 'MEDIUM'

    /**
     * Free-text search across the notification payload.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly query?: string

    /**
     * Filter by status.
     * @type {string}
     * @memberof ActivitiesApiActivitiesNotificationsStatisticsRetrieve
     */
    readonly status?: string
}

/**
 * ActivitiesApi - object-oriented interface
 * @export
 * @class ActivitiesApi
 * @extends {BaseAPI}
 */
export class ActivitiesApi extends BaseAPI implements ActivitiesApiInterface {
    /**
     * List or create comments.
     * @param {ActivitiesApiActivitiesCommentsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsCreate(requestParameters: ActivitiesApiActivitiesCommentsCreateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsCreate(requestParameters.commentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or delete a comment.
     * @param {ActivitiesApiActivitiesCommentsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsDestroy(requestParameters: ActivitiesApiActivitiesCommentsDestroyRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsDestroy(requestParameters.commentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Validate the uploaded image, store it and return the URL to embed.  The image is embedded in the comment HTML by URL, so no base64 payload ends up in ``Comment.text``, and only this CDN host is accepted as an ``<img>`` source by ``sanitize_comment_html``.  Bytes travel through the API rather than a pre-signed S3 PUT for two reasons: the image bucket\'s CORS policy only allows GET from a browser, and the objects are served from our own CDN domain, so they are decoded and size-capped here instead of trusting whatever the client uploads.
     * @param {ActivitiesApiActivitiesCommentsImagesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsImagesCreate(requestParameters: ActivitiesApiActivitiesCommentsImagesCreateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsImagesCreate(requestParameters.image, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List or create comments.
     * @param {ActivitiesApiActivitiesCommentsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsList(requestParameters: ActivitiesApiActivitiesCommentsListRequest = {}, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsList(requestParameters.binderId, requestParameters.binderItemId, requestParameters.complianceEntityResultId, requestParameters.nextKey, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or delete a comment.
     * @param {ActivitiesApiActivitiesCommentsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsPartialUpdate(requestParameters: ActivitiesApiActivitiesCommentsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsPartialUpdate(requestParameters.commentId, requestParameters.patchedCommentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or delete a comment.
     * @param {ActivitiesApiActivitiesCommentsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsRetrieve(requestParameters: ActivitiesApiActivitiesCommentsRetrieveRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsRetrieve(requestParameters.commentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or delete a comment.
     * @param {ActivitiesApiActivitiesCommentsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesCommentsUpdate(requestParameters: ActivitiesApiActivitiesCommentsUpdateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesCommentsUpdate(requestParameters.commentId, requestParameters.commentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationSettingsList(options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationSettingsList(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and update the current user\'s per-type notification settings.  Settings are global to the user (not scoped to a profile). The response always contains every notification type; a type with no stored row is reported as enabled (default-on).
     * @param {ActivitiesApiActivitiesNotificationSettingsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationSettingsUpdate(requestParameters: ActivitiesApiActivitiesNotificationSettingsUpdateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationSettingsUpdate(requestParameters.notificationSettingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Batch confirm notifications.
     * @param {ActivitiesApiActivitiesNotificationsBatchUpdatePartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationsBatchUpdatePartialUpdate(requestParameters: ActivitiesApiActivitiesNotificationsBatchUpdatePartialUpdateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationsBatchUpdatePartialUpdate(requestParameters.notificationBatchUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List notifications for the current user and active profile.
     * @param {ActivitiesApiActivitiesNotificationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationsList(requestParameters: ActivitiesApiActivitiesNotificationsListRequest = {}, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationsList(requestParameters.confirmed, requestParameters.maxCreatedAt, requestParameters.minCreatedAt, requestParameters.nextKey, requestParameters.notificationType, requestParameters.priority, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update (confirm) a notification.
     * @param {ActivitiesApiActivitiesNotificationsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationsPartialUpdate(requestParameters: ActivitiesApiActivitiesNotificationsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationsPartialUpdate(requestParameters.notificationId, requestParameters.patchedNotificationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update (confirm) a notification.
     * @param {ActivitiesApiActivitiesNotificationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationsRetrieve(requestParameters: ActivitiesApiActivitiesNotificationsRetrieveRequest, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationsRetrieve(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Notification counts grouped by notification type.  Unlike the paginated list endpoint, these counts cover all matching notifications for the current user and active profile, independent of pagination, so the inbox can show stable per-tab badges. The same filters as the list endpoint are accepted; when ``confirmed`` is not supplied the counts default to unread notifications only.
     * @param {ActivitiesApiActivitiesNotificationsStatisticsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ActivitiesApi
     */
    public activitiesNotificationsStatisticsRetrieve(requestParameters: ActivitiesApiActivitiesNotificationsStatisticsRetrieveRequest = {}, options?: AxiosRequestConfig) {
        return ActivitiesApiFp(this.configuration).activitiesNotificationsStatisticsRetrieve(requestParameters.confirmed, requestParameters.maxCreatedAt, requestParameters.minCreatedAt, requestParameters.notificationType, requestParameters.priority, requestParameters.query, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * AlertsApi - axios parameter creator
 * @export
 */
export const AlertsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Request Company Report.
         * @param {CompanyReportRequest} companyReportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsCompanyReportsCreate: async (companyReportRequest: CompanyReportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyReportRequest' is not null or undefined
            assertParamExists('alertsCompanyReportsCreate', 'companyReportRequest', companyReportRequest)
            const localVarPath = `/alerts/company_reports/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(companyReportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create new Alert.
         * @param {AlertRequest} alertRequest 
         * @param {string} [userId] Custom User ID to list alerts for
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsCreate: async (alertRequest: AlertRequest, userId?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertRequest' is not null or undefined
            assertParamExists('alertsCreate', 'alertRequest', alertRequest)
            const localVarPath = `/alerts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (userId !== undefined) {
                localVarQueryParameter['user_id'] = userId;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(alertRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsDestroy: async (alertId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsDestroy', 'alertId', alertId)
            const localVarPath = `/alerts/{alert_id}/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create Alert Feedback.
         * @param {string} alertId 
         * @param {string} secret 
         * @param {AlertFeedbackRequest} alertFeedbackRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsFeedbackCreate: async (alertId: string, secret: string, alertFeedbackRequest: AlertFeedbackRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsFeedbackCreate', 'alertId', alertId)
            // verify required parameter 'secret' is not null or undefined
            assertParamExists('alertsFeedbackCreate', 'secret', secret)
            // verify required parameter 'alertFeedbackRequest' is not null or undefined
            assertParamExists('alertsFeedbackCreate', 'alertFeedbackRequest', alertFeedbackRequest)
            const localVarPath = `/alerts/{alert_id}/{secret}/feedback/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)))
                .replace(`{${"secret"}}`, encodeURIComponent(String(secret)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(alertFeedbackRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List all alerts available.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter alerts based on (part of) name.
         * @param {'alphabetical' | 'created_at' | 'updated_at'} [sorting] Sort alerts
         * @param {'ascending' | 'descending'} [sortingOrder] Sort alerts order, descending is from high to low and ascending from high to low.
         * @param {string} [userId] Custom User ID to list alerts for
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsList: async (nextKey?: string, query?: string, sorting?: 'alphabetical' | 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', userId?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/alerts/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (userId !== undefined) {
                localVarQueryParameter['user_id'] = userId;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {PatchedAlertRequest} [patchedAlertRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsPartialUpdate: async (alertId: string, patchedAlertRequest?: PatchedAlertRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsPartialUpdate', 'alertId', alertId)
            const localVarPath = `/alerts/{alert_id}/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedAlertRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsRetrieve: async (alertId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsRetrieve', 'alertId', alertId)
            const localVarPath = `/alerts/{alert_id}/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Subscribers of alert.
         * @param {string} alertId 
         * @param {AlertUserRequest} alertUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersCreate: async (alertId: string, alertUserRequest: AlertUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsSubscribersCreate', 'alertId', alertId)
            // verify required parameter 'alertUserRequest' is not null or undefined
            assertParamExists('alertsSubscribersCreate', 'alertUserRequest', alertUserRequest)
            const localVarPath = `/alerts/{alert_id}/subscribers/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(alertUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve and/or destroy subscriber.
         * @param {string} alertId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersDestroy: async (alertId: string, userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsSubscribersDestroy', 'alertId', alertId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('alertsSubscribersDestroy', 'userId', userId)
            const localVarPath = `/alerts/{alert_id}/subscribers/{user_id}`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Subscribers of alert.
         * @param {string} alertId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersList: async (alertId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsSubscribersList', 'alertId', alertId)
            const localVarPath = `/alerts/{alert_id}/subscribers/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve and/or destroy subscriber.
         * @param {string} alertId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersRetrieve: async (alertId: string, userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsSubscribersRetrieve', 'alertId', alertId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('alertsSubscribersRetrieve', 'userId', userId)
            const localVarPath = `/alerts/{alert_id}/subscribers/{user_id}`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {AlertRequest} alertRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsUpdate: async (alertId: string, alertRequest: AlertRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'alertId' is not null or undefined
            assertParamExists('alertsUpdate', 'alertId', alertId)
            // verify required parameter 'alertRequest' is not null or undefined
            assertParamExists('alertsUpdate', 'alertRequest', alertRequest)
            const localVarPath = `/alerts/{alert_id}/`
                .replace(`{${"alert_id"}}`, encodeURIComponent(String(alertId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(alertRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create new Websocket Session.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsWebsocketsSessionsCreate: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/alerts/websockets/sessions/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AlertsApi - functional programming interface
 * @export
 */
export const AlertsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AlertsApiAxiosParamCreator(configuration)
    return {
        /**
         * Request Company Report.
         * @param {CompanyReportRequest} companyReportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsCompanyReportsCreate(companyReportRequest: CompanyReportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsCompanyReportsCreate(companyReportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create new Alert.
         * @param {AlertRequest} alertRequest 
         * @param {string} [userId] Custom User ID to list alerts for
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsCreate(alertRequest: AlertRequest, userId?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Alert>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsCreate(alertRequest, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsDestroy(alertId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsDestroy(alertId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create Alert Feedback.
         * @param {string} alertId 
         * @param {string} secret 
         * @param {AlertFeedbackRequest} alertFeedbackRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsFeedbackCreate(alertId: string, secret: string, alertFeedbackRequest: AlertFeedbackRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AlertFeedback>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsFeedbackCreate(alertId, secret, alertFeedbackRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List all alerts available.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter alerts based on (part of) name.
         * @param {'alphabetical' | 'created_at' | 'updated_at'} [sorting] Sort alerts
         * @param {'ascending' | 'descending'} [sortingOrder] Sort alerts order, descending is from high to low and ascending from high to low.
         * @param {string} [userId] Custom User ID to list alerts for
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsList(nextKey?: string, query?: string, sorting?: 'alphabetical' | 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', userId?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedAlertList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsList(nextKey, query, sorting, sortingOrder, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {PatchedAlertRequest} [patchedAlertRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsPartialUpdate(alertId: string, patchedAlertRequest?: PatchedAlertRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Alert>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsPartialUpdate(alertId, patchedAlertRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsRetrieve(alertId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Alert>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsRetrieve(alertId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Subscribers of alert.
         * @param {string} alertId 
         * @param {AlertUserRequest} alertUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsSubscribersCreate(alertId: string, alertUserRequest: AlertUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AlertUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsSubscribersCreate(alertId, alertUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve and/or destroy subscriber.
         * @param {string} alertId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsSubscribersDestroy(alertId: string, userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsSubscribersDestroy(alertId, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Subscribers of alert.
         * @param {string} alertId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsSubscribersList(alertId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedAlertUserList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsSubscribersList(alertId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve and/or destroy subscriber.
         * @param {string} alertId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsSubscribersRetrieve(alertId: string, userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AlertUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsSubscribersRetrieve(alertId, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {AlertRequest} alertRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsUpdate(alertId: string, alertRequest: AlertRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Alert>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsUpdate(alertId, alertRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create new Websocket Session.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async alertsWebsocketsSessionsCreate(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WebsocketSession>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.alertsWebsocketsSessionsCreate(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * AlertsApi - factory interface
 * @export
 */
export const AlertsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AlertsApiFp(configuration)
    return {
        /**
         * Request Company Report.
         * @param {CompanyReportRequest} companyReportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsCompanyReportsCreate(companyReportRequest: CompanyReportRequest, options?: any): AxiosPromise<void> {
            return localVarFp.alertsCompanyReportsCreate(companyReportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create new Alert.
         * @param {AlertRequest} alertRequest 
         * @param {string} [userId] Custom User ID to list alerts for
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsCreate(alertRequest: AlertRequest, userId?: string, options?: any): AxiosPromise<Alert> {
            return localVarFp.alertsCreate(alertRequest, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsDestroy(alertId: string, options?: any): AxiosPromise<void> {
            return localVarFp.alertsDestroy(alertId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create Alert Feedback.
         * @param {string} alertId 
         * @param {string} secret 
         * @param {AlertFeedbackRequest} alertFeedbackRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsFeedbackCreate(alertId: string, secret: string, alertFeedbackRequest: AlertFeedbackRequest, options?: any): AxiosPromise<AlertFeedback> {
            return localVarFp.alertsFeedbackCreate(alertId, secret, alertFeedbackRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List all alerts available.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter alerts based on (part of) name.
         * @param {'alphabetical' | 'created_at' | 'updated_at'} [sorting] Sort alerts
         * @param {'ascending' | 'descending'} [sortingOrder] Sort alerts order, descending is from high to low and ascending from high to low.
         * @param {string} [userId] Custom User ID to list alerts for
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsList(nextKey?: string, query?: string, sorting?: 'alphabetical' | 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', userId?: string, options?: any): AxiosPromise<PaginatedAlertList> {
            return localVarFp.alertsList(nextKey, query, sorting, sortingOrder, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {PatchedAlertRequest} [patchedAlertRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsPartialUpdate(alertId: string, patchedAlertRequest?: PatchedAlertRequest, options?: any): AxiosPromise<Alert> {
            return localVarFp.alertsPartialUpdate(alertId, patchedAlertRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsRetrieve(alertId: string, options?: any): AxiosPromise<Alert> {
            return localVarFp.alertsRetrieve(alertId, options).then((request) => request(axios, basePath));
        },
        /**
         * List Subscribers of alert.
         * @param {string} alertId 
         * @param {AlertUserRequest} alertUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersCreate(alertId: string, alertUserRequest: AlertUserRequest, options?: any): AxiosPromise<AlertUser> {
            return localVarFp.alertsSubscribersCreate(alertId, alertUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve and/or destroy subscriber.
         * @param {string} alertId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersDestroy(alertId: string, userId: string, options?: any): AxiosPromise<void> {
            return localVarFp.alertsSubscribersDestroy(alertId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * List Subscribers of alert.
         * @param {string} alertId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersList(alertId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedAlertUserList> {
            return localVarFp.alertsSubscribersList(alertId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve and/or destroy subscriber.
         * @param {string} alertId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsSubscribersRetrieve(alertId: string, userId: string, options?: any): AxiosPromise<AlertUser> {
            return localVarFp.alertsSubscribersRetrieve(alertId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, Update and Delete an Alert.
         * @param {string} alertId 
         * @param {AlertRequest} alertRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsUpdate(alertId: string, alertRequest: AlertRequest, options?: any): AxiosPromise<Alert> {
            return localVarFp.alertsUpdate(alertId, alertRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create new Websocket Session.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        alertsWebsocketsSessionsCreate(options?: any): AxiosPromise<WebsocketSession> {
            return localVarFp.alertsWebsocketsSessionsCreate(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AlertsApi - interface
 * @export
 * @interface AlertsApi
 */
export interface AlertsApiInterface {
    /**
     * Request Company Report.
     * @param {CompanyReportRequest} companyReportRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsCompanyReportsCreate(companyReportRequest: CompanyReportRequest, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Create new Alert.
     * @param {AlertRequest} alertRequest 
     * @param {string} [userId] Custom User ID to list alerts for
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsCreate(alertRequest: AlertRequest, userId?: string, options?: AxiosRequestConfig): AxiosPromise<Alert>;

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {string} alertId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsDestroy(alertId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Create Alert Feedback.
     * @param {string} alertId 
     * @param {string} secret 
     * @param {AlertFeedbackRequest} alertFeedbackRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsFeedbackCreate(alertId: string, secret: string, alertFeedbackRequest: AlertFeedbackRequest, options?: AxiosRequestConfig): AxiosPromise<AlertFeedback>;

    /**
     * List all alerts available.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter alerts based on (part of) name.
     * @param {'alphabetical' | 'created_at' | 'updated_at'} [sorting] Sort alerts
     * @param {'ascending' | 'descending'} [sortingOrder] Sort alerts order, descending is from high to low and ascending from high to low.
     * @param {string} [userId] Custom User ID to list alerts for
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsList(nextKey?: string, query?: string, sorting?: 'alphabetical' | 'created_at' | 'updated_at', sortingOrder?: 'ascending' | 'descending', userId?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedAlertList>;

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {string} alertId 
     * @param {PatchedAlertRequest} [patchedAlertRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsPartialUpdate(alertId: string, patchedAlertRequest?: PatchedAlertRequest, options?: AxiosRequestConfig): AxiosPromise<Alert>;

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {string} alertId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsRetrieve(alertId: string, options?: AxiosRequestConfig): AxiosPromise<Alert>;

    /**
     * List Subscribers of alert.
     * @param {string} alertId 
     * @param {AlertUserRequest} alertUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsSubscribersCreate(alertId: string, alertUserRequest: AlertUserRequest, options?: AxiosRequestConfig): AxiosPromise<AlertUser>;

    /**
     * Retrieve and/or destroy subscriber.
     * @param {string} alertId 
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsSubscribersDestroy(alertId: string, userId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List Subscribers of alert.
     * @param {string} alertId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsSubscribersList(alertId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedAlertUserList>;

    /**
     * Retrieve and/or destroy subscriber.
     * @param {string} alertId 
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsSubscribersRetrieve(alertId: string, userId: string, options?: AxiosRequestConfig): AxiosPromise<AlertUser>;

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {string} alertId 
     * @param {AlertRequest} alertRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsUpdate(alertId: string, alertRequest: AlertRequest, options?: AxiosRequestConfig): AxiosPromise<Alert>;

    /**
     * Create new Websocket Session.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApiInterface
     */
    alertsWebsocketsSessionsCreate(options?: AxiosRequestConfig): AxiosPromise<WebsocketSession>;

}

/**
 * Request parameters for alertsCompanyReportsCreate operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsCompanyReportsCreateRequest
 */
export interface AlertsApiAlertsCompanyReportsCreateRequest {
    /**
     * 
     * @type {CompanyReportRequest}
     * @memberof AlertsApiAlertsCompanyReportsCreate
     */
    readonly companyReportRequest: CompanyReportRequest
}

/**
 * Request parameters for alertsCreate operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsCreateRequest
 */
export interface AlertsApiAlertsCreateRequest {
    /**
     * 
     * @type {AlertRequest}
     * @memberof AlertsApiAlertsCreate
     */
    readonly alertRequest: AlertRequest

    /**
     * Custom User ID to list alerts for
     * @type {string}
     * @memberof AlertsApiAlertsCreate
     */
    readonly userId?: string
}

/**
 * Request parameters for alertsDestroy operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsDestroyRequest
 */
export interface AlertsApiAlertsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsDestroy
     */
    readonly alertId: string
}

/**
 * Request parameters for alertsFeedbackCreate operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsFeedbackCreateRequest
 */
export interface AlertsApiAlertsFeedbackCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsFeedbackCreate
     */
    readonly alertId: string

    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsFeedbackCreate
     */
    readonly secret: string

    /**
     * 
     * @type {AlertFeedbackRequest}
     * @memberof AlertsApiAlertsFeedbackCreate
     */
    readonly alertFeedbackRequest: AlertFeedbackRequest
}

/**
 * Request parameters for alertsList operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsListRequest
 */
export interface AlertsApiAlertsListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof AlertsApiAlertsList
     */
    readonly nextKey?: string

    /**
     * Filter alerts based on (part of) name.
     * @type {string}
     * @memberof AlertsApiAlertsList
     */
    readonly query?: string

    /**
     * Sort alerts
     * @type {'alphabetical' | 'created_at' | 'updated_at'}
     * @memberof AlertsApiAlertsList
     */
    readonly sorting?: 'alphabetical' | 'created_at' | 'updated_at'

    /**
     * Sort alerts order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof AlertsApiAlertsList
     */
    readonly sortingOrder?: 'ascending' | 'descending'

    /**
     * Custom User ID to list alerts for
     * @type {string}
     * @memberof AlertsApiAlertsList
     */
    readonly userId?: string
}

/**
 * Request parameters for alertsPartialUpdate operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsPartialUpdateRequest
 */
export interface AlertsApiAlertsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsPartialUpdate
     */
    readonly alertId: string

    /**
     * 
     * @type {PatchedAlertRequest}
     * @memberof AlertsApiAlertsPartialUpdate
     */
    readonly patchedAlertRequest?: PatchedAlertRequest
}

/**
 * Request parameters for alertsRetrieve operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsRetrieveRequest
 */
export interface AlertsApiAlertsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsRetrieve
     */
    readonly alertId: string
}

/**
 * Request parameters for alertsSubscribersCreate operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsSubscribersCreateRequest
 */
export interface AlertsApiAlertsSubscribersCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersCreate
     */
    readonly alertId: string

    /**
     * 
     * @type {AlertUserRequest}
     * @memberof AlertsApiAlertsSubscribersCreate
     */
    readonly alertUserRequest: AlertUserRequest
}

/**
 * Request parameters for alertsSubscribersDestroy operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsSubscribersDestroyRequest
 */
export interface AlertsApiAlertsSubscribersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersDestroy
     */
    readonly alertId: string

    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersDestroy
     */
    readonly userId: string
}

/**
 * Request parameters for alertsSubscribersList operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsSubscribersListRequest
 */
export interface AlertsApiAlertsSubscribersListRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersList
     */
    readonly alertId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for alertsSubscribersRetrieve operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsSubscribersRetrieveRequest
 */
export interface AlertsApiAlertsSubscribersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersRetrieve
     */
    readonly alertId: string

    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsSubscribersRetrieve
     */
    readonly userId: string
}

/**
 * Request parameters for alertsUpdate operation in AlertsApi.
 * @export
 * @interface AlertsApiAlertsUpdateRequest
 */
export interface AlertsApiAlertsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof AlertsApiAlertsUpdate
     */
    readonly alertId: string

    /**
     * 
     * @type {AlertRequest}
     * @memberof AlertsApiAlertsUpdate
     */
    readonly alertRequest: AlertRequest
}

/**
 * AlertsApi - object-oriented interface
 * @export
 * @class AlertsApi
 * @extends {BaseAPI}
 */
export class AlertsApi extends BaseAPI implements AlertsApiInterface {
    /**
     * Request Company Report.
     * @param {AlertsApiAlertsCompanyReportsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsCompanyReportsCreate(requestParameters: AlertsApiAlertsCompanyReportsCreateRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsCompanyReportsCreate(requestParameters.companyReportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create new Alert.
     * @param {AlertsApiAlertsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsCreate(requestParameters: AlertsApiAlertsCreateRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsCreate(requestParameters.alertRequest, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {AlertsApiAlertsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsDestroy(requestParameters: AlertsApiAlertsDestroyRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsDestroy(requestParameters.alertId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create Alert Feedback.
     * @param {AlertsApiAlertsFeedbackCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsFeedbackCreate(requestParameters: AlertsApiAlertsFeedbackCreateRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsFeedbackCreate(requestParameters.alertId, requestParameters.secret, requestParameters.alertFeedbackRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List all alerts available.
     * @param {AlertsApiAlertsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsList(requestParameters: AlertsApiAlertsListRequest = {}, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsList(requestParameters.nextKey, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {AlertsApiAlertsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsPartialUpdate(requestParameters: AlertsApiAlertsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsPartialUpdate(requestParameters.alertId, requestParameters.patchedAlertRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {AlertsApiAlertsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsRetrieve(requestParameters: AlertsApiAlertsRetrieveRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsRetrieve(requestParameters.alertId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Subscribers of alert.
     * @param {AlertsApiAlertsSubscribersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsSubscribersCreate(requestParameters: AlertsApiAlertsSubscribersCreateRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsSubscribersCreate(requestParameters.alertId, requestParameters.alertUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve and/or destroy subscriber.
     * @param {AlertsApiAlertsSubscribersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsSubscribersDestroy(requestParameters: AlertsApiAlertsSubscribersDestroyRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsSubscribersDestroy(requestParameters.alertId, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Subscribers of alert.
     * @param {AlertsApiAlertsSubscribersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsSubscribersList(requestParameters: AlertsApiAlertsSubscribersListRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsSubscribersList(requestParameters.alertId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve and/or destroy subscriber.
     * @param {AlertsApiAlertsSubscribersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsSubscribersRetrieve(requestParameters: AlertsApiAlertsSubscribersRetrieveRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsSubscribersRetrieve(requestParameters.alertId, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, Update and Delete an Alert.
     * @param {AlertsApiAlertsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsUpdate(requestParameters: AlertsApiAlertsUpdateRequest, options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsUpdate(requestParameters.alertId, requestParameters.alertRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create new Websocket Session.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AlertsApi
     */
    public alertsWebsocketsSessionsCreate(options?: AxiosRequestConfig) {
        return AlertsApiFp(this.configuration).alertsWebsocketsSessionsCreate(options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * AnalyticsApi - axios parameter creator
 * @export
 */
export const AnalyticsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Get Count of Articles published by Category.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesCategoriesList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/categories/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }

            if (useParentCategory !== undefined) {
                localVarQueryParameter['use_parent_category'] = useParentCategory;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Articles published by Country.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesCountriesList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/countries/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Articles published by Date.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {'day' | 'month' | 'week' | 'year'} [interval] 
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesDateCategoriesList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/date/categories/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (interval !== undefined) {
                localVarQueryParameter['interval'] = interval;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }

            if (useParentCategory !== undefined) {
                localVarQueryParameter['use_parent_category'] = useParentCategory;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Articles published by Date.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {'day' | 'hour' | 'month' | 'week' | 'year'} [interval] 
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesDateList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'hour' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/date/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (interval !== undefined) {
                localVarQueryParameter['interval'] = interval;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Articles published by Industry.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesIndustryCodesList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/industry_codes/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Articles published by Industry.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesIndustryDivisionsList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/industry_divisions/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get List of Article Keywords.
         * @param {number} count Amount of keywords to return
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesKeywordsList: async (count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'count' is not null or undefined
            assertParamExists('analyticsArticlesKeywordsList', 'count', count)
            const localVarPath = `/analytics/articles/keywords/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (count !== undefined) {
                localVarQueryParameter['count'] = count;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Article Statistics.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesStatisticsList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/articles/statistics/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get for each company all categories mentioned in the news.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesCategoriesList: async (company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/companies/categories/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get count of Companies by Country.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesCountriesList: async (company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/companies/countries/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Companies by Industry.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesIndustryCodesList: async (company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/companies/industry_codes/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Count of Companies by Industry.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesIndustryDivisionsList: async (company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/companies/industry_divisions/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {KeyEventExtractionRequest} keyEventExtractionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsCreate: async (keyEventExtractionRequest: KeyEventExtractionRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'keyEventExtractionRequest' is not null or undefined
            assertParamExists('analyticsCompaniesKeyEventsCreate', 'keyEventExtractionRequest', keyEventExtractionRequest)
            const localVarPath = `/analytics/companies/key_events/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(keyEventExtractionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Key Events Export View.
         * @param {string} keyEventExtractionId 
         * @param {DataExportRequest} dataExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsExportCreate: async (keyEventExtractionId: string, dataExportRequest: DataExportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'keyEventExtractionId' is not null or undefined
            assertParamExists('analyticsCompaniesKeyEventsExportCreate', 'keyEventExtractionId', keyEventExtractionId)
            // verify required parameter 'dataExportRequest' is not null or undefined
            assertParamExists('analyticsCompaniesKeyEventsExportCreate', 'dataExportRequest', dataExportRequest)
            const localVarPath = `/analytics/companies/key_events/{key_event_extraction_id}/export/`
                .replace(`{${"key_event_extraction_id"}}`, encodeURIComponent(String(keyEventExtractionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(dataExportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} keyEventExtractionId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsKeyEventsList: async (keyEventExtractionId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'keyEventExtractionId' is not null or undefined
            assertParamExists('analyticsCompaniesKeyEventsKeyEventsList', 'keyEventExtractionId', keyEventExtractionId)
            const localVarPath = `/analytics/companies/key_events/{key_event_extraction_id}/key_events`
                .replace(`{${"key_event_extraction_id"}}`, encodeURIComponent(String(keyEventExtractionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} keyEventExtractionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsRetrieve: async (keyEventExtractionId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'keyEventExtractionId' is not null or undefined
            assertParamExists('analyticsCompaniesKeyEventsRetrieve', 'keyEventExtractionId', keyEventExtractionId)
            const localVarPath = `/analytics/companies/key_events/{key_event_extraction_id}`
                .replace(`{${"key_event_extraction_id"}}`, encodeURIComponent(String(keyEventExtractionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Company Statistics.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesStatisticsRetrieve: async (company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/companies/statistics/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Trending Companies based on Articles.
         * @param {number} count Amount of keywords to return
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesTrendingList: async (count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'count' is not null or undefined
            assertParamExists('analyticsCompaniesTrendingList', 'count', count)
            const localVarPath = `/analytics/companies/trending/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (count !== undefined) {
                localVarQueryParameter['count'] = count;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get for each industry all categories mentioned in the news.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsIndustryCodesCategoriesList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/industry_codes/categories/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get for each division all categories mentioned in the news.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsIndustryCodesDivisionsCategoriesList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/analytics/industry_codes/divisions/categories/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AnalyticsApi - functional programming interface
 * @export
 */
export const AnalyticsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AnalyticsApiAxiosParamCreator(configuration)
    return {
        /**
         * Get Count of Articles published by Category.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesCategoriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleCategoryAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesCategoriesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, useParentCategory, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Articles published by Country.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesCountriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleCountryAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesCountriesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Articles published by Date.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {'day' | 'month' | 'week' | 'year'} [interval] 
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesDateCategoriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleDateCategoriesAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesDateCategoriesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, interval, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, useParentCategory, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Articles published by Date.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {'day' | 'hour' | 'month' | 'week' | 'year'} [interval] 
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesDateList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'hour' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleDateAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesDateList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, interval, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Articles published by Industry.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesIndustryCodesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleIndustryAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesIndustryCodesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Articles published by Industry.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesIndustryDivisionsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleIndustryDivisionAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesIndustryDivisionsList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get List of Article Keywords.
         * @param {number} count Amount of keywords to return
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesKeywordsList(count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleKeywordAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesKeywordsList(count, category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Article Statistics.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsArticlesStatisticsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticleStatistics>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsArticlesStatisticsList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get for each company all categories mentioned in the news.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesCategoriesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedCompanyCategoriesAggregationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesCategoriesList(company, country, dunsNumber, excludeCompany, industry, lei, nextKey, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get count of Companies by Country.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesCountriesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CompanyCountryAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesCountriesList(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Companies by Industry.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesIndustryCodesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CompanyIndustryAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesIndustryCodesList(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Count of Companies by Industry.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesIndustryDivisionsList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CompanyIndustryDivisionAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesIndustryDivisionsList(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {KeyEventExtractionRequest} keyEventExtractionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesKeyEventsCreate(keyEventExtractionRequest: KeyEventExtractionRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<KeyEventExtraction>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesKeyEventsCreate(keyEventExtractionRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Key Events Export View.
         * @param {string} keyEventExtractionId 
         * @param {DataExportRequest} dataExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesKeyEventsExportCreate(keyEventExtractionId: string, dataExportRequest: DataExportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DataExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesKeyEventsExportCreate(keyEventExtractionId, dataExportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} keyEventExtractionId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesKeyEventsKeyEventsList(keyEventExtractionId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedKeyEventList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesKeyEventsKeyEventsList(keyEventExtractionId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} keyEventExtractionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesKeyEventsRetrieve(keyEventExtractionId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<KeyEventExtraction>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesKeyEventsRetrieve(keyEventExtractionId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Company Statistics.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesStatisticsRetrieve(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyStats>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesStatisticsRetrieve(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Trending Companies based on Articles.
         * @param {number} count Amount of keywords to return
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsCompaniesTrendingList(count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<TrendingCompanyAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsCompaniesTrendingList(count, category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get for each industry all categories mentioned in the news.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsIndustryCodesCategoriesList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIndustryCategoriesAggregationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsIndustryCodesCategoriesList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get for each division all categories mentioned in the news.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async analyticsIndustryCodesDivisionsCategoriesList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DivisionCategoriesAggregation>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.analyticsIndustryCodesDivisionsCategoriesList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * AnalyticsApi - factory interface
 * @export
 */
export const AnalyticsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AnalyticsApiFp(configuration)
    return {
        /**
         * Get Count of Articles published by Category.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesCategoriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options?: any): AxiosPromise<Array<ArticleCategoryAggregation>> {
            return localVarFp.analyticsArticlesCategoriesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, useParentCategory, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Articles published by Country.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesCountriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticleCountryAggregation>> {
            return localVarFp.analyticsArticlesCountriesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Articles published by Date.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {'day' | 'month' | 'week' | 'year'} [interval] 
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesDateCategoriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options?: any): AxiosPromise<Array<ArticleDateCategoriesAggregation>> {
            return localVarFp.analyticsArticlesDateCategoriesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, interval, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, useParentCategory, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Articles published by Date.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {'day' | 'hour' | 'month' | 'week' | 'year'} [interval] 
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesDateList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'hour' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticleDateAggregation>> {
            return localVarFp.analyticsArticlesDateList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, interval, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Articles published by Industry.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesIndustryCodesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticleIndustryAggregation>> {
            return localVarFp.analyticsArticlesIndustryCodesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Articles published by Industry.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesIndustryDivisionsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticleIndustryDivisionAggregation>> {
            return localVarFp.analyticsArticlesIndustryDivisionsList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get List of Article Keywords.
         * @param {number} count Amount of keywords to return
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesKeywordsList(count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticleKeywordAggregation>> {
            return localVarFp.analyticsArticlesKeywordsList(count, category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Article Statistics.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsArticlesStatisticsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticleStatistics>> {
            return localVarFp.analyticsArticlesStatisticsList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get for each company all categories mentioned in the news.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesCategoriesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<PaginatedCompanyCategoriesAggregationList> {
            return localVarFp.analyticsCompaniesCategoriesList(company, country, dunsNumber, excludeCompany, industry, lei, nextKey, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
        /**
         * Get count of Companies by Country.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesCountriesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<Array<CompanyCountryAggregation>> {
            return localVarFp.analyticsCompaniesCountriesList(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Companies by Industry.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesIndustryCodesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<Array<CompanyIndustryAggregation>> {
            return localVarFp.analyticsCompaniesIndustryCodesList(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Count of Companies by Industry.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesIndustryDivisionsList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<Array<CompanyIndustryDivisionAggregation>> {
            return localVarFp.analyticsCompaniesIndustryDivisionsList(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {KeyEventExtractionRequest} keyEventExtractionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsCreate(keyEventExtractionRequest: KeyEventExtractionRequest, options?: any): AxiosPromise<KeyEventExtraction> {
            return localVarFp.analyticsCompaniesKeyEventsCreate(keyEventExtractionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Key Events Export View.
         * @param {string} keyEventExtractionId 
         * @param {DataExportRequest} dataExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsExportCreate(keyEventExtractionId: string, dataExportRequest: DataExportRequest, options?: any): AxiosPromise<DataExport> {
            return localVarFp.analyticsCompaniesKeyEventsExportCreate(keyEventExtractionId, dataExportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} keyEventExtractionId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsKeyEventsList(keyEventExtractionId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedKeyEventList> {
            return localVarFp.analyticsCompaniesKeyEventsKeyEventsList(keyEventExtractionId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} keyEventExtractionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesKeyEventsRetrieve(keyEventExtractionId: string, options?: any): AxiosPromise<KeyEventExtraction> {
            return localVarFp.analyticsCompaniesKeyEventsRetrieve(keyEventExtractionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Company Statistics.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesStatisticsRetrieve(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<CompanyStats> {
            return localVarFp.analyticsCompaniesStatisticsRetrieve(company, country, dunsNumber, excludeCompany, industry, lei, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
        /**
         * List Trending Companies based on Articles.
         * @param {number} count Amount of keywords to return
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsCompaniesTrendingList(count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<TrendingCompanyAggregation>> {
            return localVarFp.analyticsCompaniesTrendingList(count, category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * Get for each industry all categories mentioned in the news.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsIndustryCodesCategoriesList(nextKey?: string, options?: any): AxiosPromise<PaginatedIndustryCategoriesAggregationList> {
            return localVarFp.analyticsIndustryCodesCategoriesList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Get for each division all categories mentioned in the news.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        analyticsIndustryCodesDivisionsCategoriesList(options?: any): AxiosPromise<Array<DivisionCategoriesAggregation>> {
            return localVarFp.analyticsIndustryCodesDivisionsCategoriesList(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AnalyticsApi - interface
 * @export
 * @interface AnalyticsApi
 */
export interface AnalyticsApiInterface {
    /**
     * Get Count of Articles published by Category.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesCategoriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleCategoryAggregation>>;

    /**
     * Get Count of Articles published by Country.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesCountriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleCountryAggregation>>;

    /**
     * Get Count of Articles published by Date.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {'day' | 'month' | 'week' | 'year'} [interval] 
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {boolean} [useParentCategory] If enabled, group by parent categories instead of lowest level of categories.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesDateCategoriesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, useParentCategory?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleDateCategoriesAggregation>>;

    /**
     * Get Count of Articles published by Date.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {'day' | 'hour' | 'month' | 'week' | 'year'} [interval] 
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesDateList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, interval?: 'day' | 'hour' | 'month' | 'week' | 'year', isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleDateAggregation>>;

    /**
     * Get Count of Articles published by Industry.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesIndustryCodesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleIndustryAggregation>>;

    /**
     * Get Count of Articles published by Industry.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesIndustryDivisionsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleIndustryDivisionAggregation>>;

    /**
     * Get List of Article Keywords.
     * @param {number} count Amount of keywords to return
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesKeywordsList(count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleKeywordAggregation>>;

    /**
     * Get Article Statistics.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsArticlesStatisticsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticleStatistics>>;

    /**
     * Get for each company all categories mentioned in the news.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesCategoriesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedCompanyCategoriesAggregationList>;

    /**
     * Get count of Companies by Country.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesCountriesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<Array<CompanyCountryAggregation>>;

    /**
     * Get Count of Companies by Industry.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesIndustryCodesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<Array<CompanyIndustryAggregation>>;

    /**
     * Get Count of Companies by Industry.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesIndustryDivisionsList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<Array<CompanyIndustryDivisionAggregation>>;

    /**
     * 
     * @param {KeyEventExtractionRequest} keyEventExtractionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesKeyEventsCreate(keyEventExtractionRequest: KeyEventExtractionRequest, options?: AxiosRequestConfig): AxiosPromise<KeyEventExtraction>;

    /**
     * Key Events Export View.
     * @param {string} keyEventExtractionId 
     * @param {DataExportRequest} dataExportRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesKeyEventsExportCreate(keyEventExtractionId: string, dataExportRequest: DataExportRequest, options?: AxiosRequestConfig): AxiosPromise<DataExport>;

    /**
     * 
     * @param {string} keyEventExtractionId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesKeyEventsKeyEventsList(keyEventExtractionId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedKeyEventList>;

    /**
     * 
     * @param {string} keyEventExtractionId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesKeyEventsRetrieve(keyEventExtractionId: string, options?: AxiosRequestConfig): AxiosPromise<KeyEventExtraction>;

    /**
     * Get Company Statistics.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesStatisticsRetrieve(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, industry?: Array<string>, lei?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<CompanyStats>;

    /**
     * List Trending Companies based on Articles.
     * @param {number} count Amount of keywords to return
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsCompaniesTrendingList(count: number, category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<TrendingCompanyAggregation>>;

    /**
     * Get for each industry all categories mentioned in the news.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsIndustryCodesCategoriesList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedIndustryCategoriesAggregationList>;

    /**
     * Get for each division all categories mentioned in the news.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApiInterface
     */
    analyticsIndustryCodesDivisionsCategoriesList(options?: AxiosRequestConfig): AxiosPromise<Array<DivisionCategoriesAggregation>>;

}

/**
 * Request parameters for analyticsArticlesCategoriesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesCategoriesListRequest
 */
export interface AnalyticsApiAnalyticsArticlesCategoriesListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly sentiment?: boolean

    /**
     * If enabled, group by parent categories instead of lowest level of categories.
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCategoriesList
     */
    readonly useParentCategory?: boolean
}

/**
 * Request parameters for analyticsArticlesCountriesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesCountriesListRequest
 */
export interface AnalyticsApiAnalyticsArticlesCountriesListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesCountriesList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsArticlesDateCategoriesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesDateCategoriesListRequest
 */
export interface AnalyticsApiAnalyticsArticlesDateCategoriesListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly industry?: Array<string>

    /**
     * 
     * @type {'day' | 'month' | 'week' | 'year'}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly interval?: 'day' | 'month' | 'week' | 'year'

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly sentiment?: boolean

    /**
     * If enabled, group by parent categories instead of lowest level of categories.
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateCategoriesList
     */
    readonly useParentCategory?: boolean
}

/**
 * Request parameters for analyticsArticlesDateList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesDateListRequest
 */
export interface AnalyticsApiAnalyticsArticlesDateListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly industry?: Array<string>

    /**
     * 
     * @type {'day' | 'hour' | 'month' | 'week' | 'year'}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly interval?: 'day' | 'hour' | 'month' | 'week' | 'year'

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesDateList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsArticlesIndustryCodesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesIndustryCodesListRequest
 */
export interface AnalyticsApiAnalyticsArticlesIndustryCodesListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryCodesList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsArticlesIndustryDivisionsList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesIndustryDivisionsListRequest
 */
export interface AnalyticsApiAnalyticsArticlesIndustryDivisionsListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesIndustryDivisionsList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsArticlesKeywordsList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesKeywordsListRequest
 */
export interface AnalyticsApiAnalyticsArticlesKeywordsListRequest {
    /**
     * Amount of keywords to return
     * @type {number}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly count: number

    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesKeywordsList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsArticlesStatisticsList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsArticlesStatisticsListRequest
 */
export interface AnalyticsApiAnalyticsArticlesStatisticsListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsArticlesStatisticsList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsCompaniesCategoriesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesCategoriesListRequest
 */
export interface AnalyticsApiAnalyticsCompaniesCategoriesListRequest {
    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly lei?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly nextKey?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCategoriesList
     */
    readonly slug?: string
}

/**
 * Request parameters for analyticsCompaniesCountriesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesCountriesListRequest
 */
export interface AnalyticsApiAnalyticsCompaniesCountriesListRequest {
    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly lei?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesCountriesList
     */
    readonly slug?: string
}

/**
 * Request parameters for analyticsCompaniesIndustryCodesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesIndustryCodesListRequest
 */
export interface AnalyticsApiAnalyticsCompaniesIndustryCodesListRequest {
    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly lei?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryCodesList
     */
    readonly slug?: string
}

/**
 * Request parameters for analyticsCompaniesIndustryDivisionsList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesIndustryDivisionsListRequest
 */
export interface AnalyticsApiAnalyticsCompaniesIndustryDivisionsListRequest {
    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly excludeCompany?: Array<string>

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly lei?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesIndustryDivisionsList
     */
    readonly slug?: string
}

/**
 * Request parameters for analyticsCompaniesKeyEventsCreate operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesKeyEventsCreateRequest
 */
export interface AnalyticsApiAnalyticsCompaniesKeyEventsCreateRequest {
    /**
     * 
     * @type {KeyEventExtractionRequest}
     * @memberof AnalyticsApiAnalyticsCompaniesKeyEventsCreate
     */
    readonly keyEventExtractionRequest: KeyEventExtractionRequest
}

/**
 * Request parameters for analyticsCompaniesKeyEventsExportCreate operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesKeyEventsExportCreateRequest
 */
export interface AnalyticsApiAnalyticsCompaniesKeyEventsExportCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesKeyEventsExportCreate
     */
    readonly keyEventExtractionId: string

    /**
     * 
     * @type {DataExportRequest}
     * @memberof AnalyticsApiAnalyticsCompaniesKeyEventsExportCreate
     */
    readonly dataExportRequest: DataExportRequest
}

/**
 * Request parameters for analyticsCompaniesKeyEventsKeyEventsList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesKeyEventsKeyEventsListRequest
 */
export interface AnalyticsApiAnalyticsCompaniesKeyEventsKeyEventsListRequest {
    /**
     * 
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesKeyEventsKeyEventsList
     */
    readonly keyEventExtractionId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesKeyEventsKeyEventsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for analyticsCompaniesKeyEventsRetrieve operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesKeyEventsRetrieveRequest
 */
export interface AnalyticsApiAnalyticsCompaniesKeyEventsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesKeyEventsRetrieve
     */
    readonly keyEventExtractionId: string
}

/**
 * Request parameters for analyticsCompaniesStatisticsRetrieve operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesStatisticsRetrieveRequest
 */
export interface AnalyticsApiAnalyticsCompaniesStatisticsRetrieveRequest {
    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly excludeCompany?: Array<string>

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly lei?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesStatisticsRetrieve
     */
    readonly slug?: string
}

/**
 * Request parameters for analyticsCompaniesTrendingList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsCompaniesTrendingListRequest
 */
export interface AnalyticsApiAnalyticsCompaniesTrendingListRequest {
    /**
     * Amount of keywords to return
     * @type {number}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly count: number

    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof AnalyticsApiAnalyticsCompaniesTrendingList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for analyticsIndustryCodesCategoriesList operation in AnalyticsApi.
 * @export
 * @interface AnalyticsApiAnalyticsIndustryCodesCategoriesListRequest
 */
export interface AnalyticsApiAnalyticsIndustryCodesCategoriesListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof AnalyticsApiAnalyticsIndustryCodesCategoriesList
     */
    readonly nextKey?: string
}

/**
 * AnalyticsApi - object-oriented interface
 * @export
 * @class AnalyticsApi
 * @extends {BaseAPI}
 */
export class AnalyticsApi extends BaseAPI implements AnalyticsApiInterface {
    /**
     * Get Count of Articles published by Category.
     * @param {AnalyticsApiAnalyticsArticlesCategoriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesCategoriesList(requestParameters: AnalyticsApiAnalyticsArticlesCategoriesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesCategoriesList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, requestParameters.useParentCategory, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Articles published by Country.
     * @param {AnalyticsApiAnalyticsArticlesCountriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesCountriesList(requestParameters: AnalyticsApiAnalyticsArticlesCountriesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesCountriesList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Articles published by Date.
     * @param {AnalyticsApiAnalyticsArticlesDateCategoriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesDateCategoriesList(requestParameters: AnalyticsApiAnalyticsArticlesDateCategoriesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesDateCategoriesList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.interval, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, requestParameters.useParentCategory, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Articles published by Date.
     * @param {AnalyticsApiAnalyticsArticlesDateListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesDateList(requestParameters: AnalyticsApiAnalyticsArticlesDateListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesDateList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.interval, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Articles published by Industry.
     * @param {AnalyticsApiAnalyticsArticlesIndustryCodesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesIndustryCodesList(requestParameters: AnalyticsApiAnalyticsArticlesIndustryCodesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesIndustryCodesList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Articles published by Industry.
     * @param {AnalyticsApiAnalyticsArticlesIndustryDivisionsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesIndustryDivisionsList(requestParameters: AnalyticsApiAnalyticsArticlesIndustryDivisionsListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesIndustryDivisionsList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get List of Article Keywords.
     * @param {AnalyticsApiAnalyticsArticlesKeywordsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesKeywordsList(requestParameters: AnalyticsApiAnalyticsArticlesKeywordsListRequest, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesKeywordsList(requestParameters.count, requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Article Statistics.
     * @param {AnalyticsApiAnalyticsArticlesStatisticsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsArticlesStatisticsList(requestParameters: AnalyticsApiAnalyticsArticlesStatisticsListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsArticlesStatisticsList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get for each company all categories mentioned in the news.
     * @param {AnalyticsApiAnalyticsCompaniesCategoriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesCategoriesList(requestParameters: AnalyticsApiAnalyticsCompaniesCategoriesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesCategoriesList(requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.industry, requestParameters.lei, requestParameters.nextKey, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get count of Companies by Country.
     * @param {AnalyticsApiAnalyticsCompaniesCountriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesCountriesList(requestParameters: AnalyticsApiAnalyticsCompaniesCountriesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesCountriesList(requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.industry, requestParameters.lei, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Companies by Industry.
     * @param {AnalyticsApiAnalyticsCompaniesIndustryCodesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesIndustryCodesList(requestParameters: AnalyticsApiAnalyticsCompaniesIndustryCodesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesIndustryCodesList(requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.industry, requestParameters.lei, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Count of Companies by Industry.
     * @param {AnalyticsApiAnalyticsCompaniesIndustryDivisionsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesIndustryDivisionsList(requestParameters: AnalyticsApiAnalyticsCompaniesIndustryDivisionsListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesIndustryDivisionsList(requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.industry, requestParameters.lei, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AnalyticsApiAnalyticsCompaniesKeyEventsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesKeyEventsCreate(requestParameters: AnalyticsApiAnalyticsCompaniesKeyEventsCreateRequest, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesKeyEventsCreate(requestParameters.keyEventExtractionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Key Events Export View.
     * @param {AnalyticsApiAnalyticsCompaniesKeyEventsExportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesKeyEventsExportCreate(requestParameters: AnalyticsApiAnalyticsCompaniesKeyEventsExportCreateRequest, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesKeyEventsExportCreate(requestParameters.keyEventExtractionId, requestParameters.dataExportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AnalyticsApiAnalyticsCompaniesKeyEventsKeyEventsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesKeyEventsKeyEventsList(requestParameters: AnalyticsApiAnalyticsCompaniesKeyEventsKeyEventsListRequest, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesKeyEventsKeyEventsList(requestParameters.keyEventExtractionId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AnalyticsApiAnalyticsCompaniesKeyEventsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesKeyEventsRetrieve(requestParameters: AnalyticsApiAnalyticsCompaniesKeyEventsRetrieveRequest, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesKeyEventsRetrieve(requestParameters.keyEventExtractionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Company Statistics.
     * @param {AnalyticsApiAnalyticsCompaniesStatisticsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesStatisticsRetrieve(requestParameters: AnalyticsApiAnalyticsCompaniesStatisticsRetrieveRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesStatisticsRetrieve(requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.industry, requestParameters.lei, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Trending Companies based on Articles.
     * @param {AnalyticsApiAnalyticsCompaniesTrendingListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsCompaniesTrendingList(requestParameters: AnalyticsApiAnalyticsCompaniesTrendingListRequest, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsCompaniesTrendingList(requestParameters.count, requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get for each industry all categories mentioned in the news.
     * @param {AnalyticsApiAnalyticsIndustryCodesCategoriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsIndustryCodesCategoriesList(requestParameters: AnalyticsApiAnalyticsIndustryCodesCategoriesListRequest = {}, options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsIndustryCodesCategoriesList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get for each division all categories mentioned in the news.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AnalyticsApi
     */
    public analyticsIndustryCodesDivisionsCategoriesList(options?: AxiosRequestConfig) {
        return AnalyticsApiFp(this.configuration).analyticsIndustryCodesDivisionsCategoriesList(options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * AssistantApi - axios parameter creator
 * @export
 */
export const AssistantApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Apply the requested decision to the action.
         * @param {string} actionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantActionsCreate: async (actionId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'actionId' is not null or undefined
            assertParamExists('assistantActionsCreate', 'actionId', actionId)
            const localVarPath = `/assistant/actions/{action_id}/`
                .replace(`{${"action_id"}}`, encodeURIComponent(String(actionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create conversations for the current user/profile.
         * @param {ConversationRequest} [conversationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsCreate: async (conversationRequest?: ConversationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/assistant/conversations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(conversationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create conversations for the current user/profile.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/assistant/conversations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Post a user message; dispatch or queue the assistant turn.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsMessagesCreate: async (conversationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'conversationId' is not null or undefined
            assertParamExists('assistantConversationsMessagesCreate', 'conversationId', conversationId)
            const localVarPath = `/assistant/conversations/{conversation_id}/messages/`
                .replace(`{${"conversation_id"}}`, encodeURIComponent(String(conversationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List messages, oldest first. Reading a thread clears its unread flag.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsMessagesRetrieve: async (conversationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'conversationId' is not null or undefined
            assertParamExists('assistantConversationsMessagesRetrieve', 'conversationId', conversationId)
            const localVarPath = `/assistant/conversations/{conversation_id}/messages/`
                .replace(`{${"conversation_id"}}`, encodeURIComponent(String(conversationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve a single conversation.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsRetrieve: async (conversationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'conversationId' is not null or undefined
            assertParamExists('assistantConversationsRetrieve', 'conversationId', conversationId)
            const localVarPath = `/assistant/conversations/{conversation_id}/`
                .replace(`{${"conversation_id"}}`, encodeURIComponent(String(conversationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AssistantApi - functional programming interface
 * @export
 */
export const AssistantApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AssistantApiAxiosParamCreator(configuration)
    return {
        /**
         * Apply the requested decision to the action.
         * @param {string} actionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assistantActionsCreate(actionId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assistantActionsCreate(actionId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create conversations for the current user/profile.
         * @param {ConversationRequest} [conversationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assistantConversationsCreate(conversationRequest?: ConversationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Conversation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assistantConversationsCreate(conversationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create conversations for the current user/profile.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assistantConversationsList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedConversationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assistantConversationsList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Post a user message; dispatch or queue the assistant turn.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assistantConversationsMessagesCreate(conversationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assistantConversationsMessagesCreate(conversationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List messages, oldest first. Reading a thread clears its unread flag.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assistantConversationsMessagesRetrieve(conversationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assistantConversationsMessagesRetrieve(conversationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve a single conversation.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assistantConversationsRetrieve(conversationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Conversation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assistantConversationsRetrieve(conversationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * AssistantApi - factory interface
 * @export
 */
export const AssistantApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AssistantApiFp(configuration)
    return {
        /**
         * Apply the requested decision to the action.
         * @param {string} actionId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantActionsCreate(actionId: string, options?: any): AxiosPromise<void> {
            return localVarFp.assistantActionsCreate(actionId, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create conversations for the current user/profile.
         * @param {ConversationRequest} [conversationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsCreate(conversationRequest?: ConversationRequest, options?: any): AxiosPromise<Conversation> {
            return localVarFp.assistantConversationsCreate(conversationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create conversations for the current user/profile.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsList(nextKey?: string, options?: any): AxiosPromise<PaginatedConversationList> {
            return localVarFp.assistantConversationsList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Post a user message; dispatch or queue the assistant turn.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsMessagesCreate(conversationId: string, options?: any): AxiosPromise<void> {
            return localVarFp.assistantConversationsMessagesCreate(conversationId, options).then((request) => request(axios, basePath));
        },
        /**
         * List messages, oldest first. Reading a thread clears its unread flag.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsMessagesRetrieve(conversationId: string, options?: any): AxiosPromise<void> {
            return localVarFp.assistantConversationsMessagesRetrieve(conversationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve a single conversation.
         * @param {string} conversationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assistantConversationsRetrieve(conversationId: string, options?: any): AxiosPromise<Conversation> {
            return localVarFp.assistantConversationsRetrieve(conversationId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AssistantApi - interface
 * @export
 * @interface AssistantApi
 */
export interface AssistantApiInterface {
    /**
     * Apply the requested decision to the action.
     * @param {string} actionId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApiInterface
     */
    assistantActionsCreate(actionId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List and create conversations for the current user/profile.
     * @param {ConversationRequest} [conversationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApiInterface
     */
    assistantConversationsCreate(conversationRequest?: ConversationRequest, options?: AxiosRequestConfig): AxiosPromise<Conversation>;

    /**
     * List and create conversations for the current user/profile.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApiInterface
     */
    assistantConversationsList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedConversationList>;

    /**
     * Post a user message; dispatch or queue the assistant turn.
     * @param {string} conversationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApiInterface
     */
    assistantConversationsMessagesCreate(conversationId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List messages, oldest first. Reading a thread clears its unread flag.
     * @param {string} conversationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApiInterface
     */
    assistantConversationsMessagesRetrieve(conversationId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Retrieve a single conversation.
     * @param {string} conversationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApiInterface
     */
    assistantConversationsRetrieve(conversationId: string, options?: AxiosRequestConfig): AxiosPromise<Conversation>;

}

/**
 * Request parameters for assistantActionsCreate operation in AssistantApi.
 * @export
 * @interface AssistantApiAssistantActionsCreateRequest
 */
export interface AssistantApiAssistantActionsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof AssistantApiAssistantActionsCreate
     */
    readonly actionId: string
}

/**
 * Request parameters for assistantConversationsCreate operation in AssistantApi.
 * @export
 * @interface AssistantApiAssistantConversationsCreateRequest
 */
export interface AssistantApiAssistantConversationsCreateRequest {
    /**
     * 
     * @type {ConversationRequest}
     * @memberof AssistantApiAssistantConversationsCreate
     */
    readonly conversationRequest?: ConversationRequest
}

/**
 * Request parameters for assistantConversationsList operation in AssistantApi.
 * @export
 * @interface AssistantApiAssistantConversationsListRequest
 */
export interface AssistantApiAssistantConversationsListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof AssistantApiAssistantConversationsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for assistantConversationsMessagesCreate operation in AssistantApi.
 * @export
 * @interface AssistantApiAssistantConversationsMessagesCreateRequest
 */
export interface AssistantApiAssistantConversationsMessagesCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof AssistantApiAssistantConversationsMessagesCreate
     */
    readonly conversationId: string
}

/**
 * Request parameters for assistantConversationsMessagesRetrieve operation in AssistantApi.
 * @export
 * @interface AssistantApiAssistantConversationsMessagesRetrieveRequest
 */
export interface AssistantApiAssistantConversationsMessagesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof AssistantApiAssistantConversationsMessagesRetrieve
     */
    readonly conversationId: string
}

/**
 * Request parameters for assistantConversationsRetrieve operation in AssistantApi.
 * @export
 * @interface AssistantApiAssistantConversationsRetrieveRequest
 */
export interface AssistantApiAssistantConversationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof AssistantApiAssistantConversationsRetrieve
     */
    readonly conversationId: string
}

/**
 * AssistantApi - object-oriented interface
 * @export
 * @class AssistantApi
 * @extends {BaseAPI}
 */
export class AssistantApi extends BaseAPI implements AssistantApiInterface {
    /**
     * Apply the requested decision to the action.
     * @param {AssistantApiAssistantActionsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApi
     */
    public assistantActionsCreate(requestParameters: AssistantApiAssistantActionsCreateRequest, options?: AxiosRequestConfig) {
        return AssistantApiFp(this.configuration).assistantActionsCreate(requestParameters.actionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create conversations for the current user/profile.
     * @param {AssistantApiAssistantConversationsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApi
     */
    public assistantConversationsCreate(requestParameters: AssistantApiAssistantConversationsCreateRequest = {}, options?: AxiosRequestConfig) {
        return AssistantApiFp(this.configuration).assistantConversationsCreate(requestParameters.conversationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create conversations for the current user/profile.
     * @param {AssistantApiAssistantConversationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApi
     */
    public assistantConversationsList(requestParameters: AssistantApiAssistantConversationsListRequest = {}, options?: AxiosRequestConfig) {
        return AssistantApiFp(this.configuration).assistantConversationsList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Post a user message; dispatch or queue the assistant turn.
     * @param {AssistantApiAssistantConversationsMessagesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApi
     */
    public assistantConversationsMessagesCreate(requestParameters: AssistantApiAssistantConversationsMessagesCreateRequest, options?: AxiosRequestConfig) {
        return AssistantApiFp(this.configuration).assistantConversationsMessagesCreate(requestParameters.conversationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List messages, oldest first. Reading a thread clears its unread flag.
     * @param {AssistantApiAssistantConversationsMessagesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApi
     */
    public assistantConversationsMessagesRetrieve(requestParameters: AssistantApiAssistantConversationsMessagesRetrieveRequest, options?: AxiosRequestConfig) {
        return AssistantApiFp(this.configuration).assistantConversationsMessagesRetrieve(requestParameters.conversationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve a single conversation.
     * @param {AssistantApiAssistantConversationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof AssistantApi
     */
    public assistantConversationsRetrieve(requestParameters: AssistantApiAssistantConversationsRetrieveRequest, options?: AxiosRequestConfig) {
        return AssistantApiFp(this.configuration).assistantConversationsRetrieve(requestParameters.conversationId, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * BillingApi - axios parameter creator
 * @export
 */
export const BillingApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * API view to provide an aggregation of usage per type per day of the current billing period, combined per day.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingAnalyticsDailyList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/billing/analytics/daily`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * API view to provide the current usage and budget for the current billing period.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingCurrentUsageRetrieve: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/billing/current_usage`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {BillingDataExportRequest} [billingDataExportRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingExportCreate: async (billingDataExportRequest?: BillingDataExportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/billing/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(billingDataExportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {BillingNotificationRequest} billingNotificationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsCreate: async (billingNotificationRequest: BillingNotificationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'billingNotificationRequest' is not null or undefined
            assertParamExists('billingNotificationsCreate', 'billingNotificationRequest', billingNotificationRequest)
            const localVarPath = `/billing/notifications`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(billingNotificationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsDestroy: async (notificationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('billingNotificationsDestroy', 'notificationId', notificationId)
            const localVarPath = `/billing/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/billing/notifications`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {PatchedBillingNotificationRequest} [patchedBillingNotificationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsPartialUpdate: async (notificationId: string, patchedBillingNotificationRequest?: PatchedBillingNotificationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('billingNotificationsPartialUpdate', 'notificationId', notificationId)
            const localVarPath = `/billing/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedBillingNotificationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsRetrieve: async (notificationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('billingNotificationsRetrieve', 'notificationId', notificationId)
            const localVarPath = `/billing/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {BillingNotificationRequest} billingNotificationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsUpdate: async (notificationId: string, billingNotificationRequest: BillingNotificationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'notificationId' is not null or undefined
            assertParamExists('billingNotificationsUpdate', 'notificationId', notificationId)
            // verify required parameter 'billingNotificationRequest' is not null or undefined
            assertParamExists('billingNotificationsUpdate', 'billingNotificationRequest', billingNotificationRequest)
            const localVarPath = `/billing/notifications/{notification_id}`
                .replace(`{${"notification_id"}}`, encodeURIComponent(String(notificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(billingNotificationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BillingApi - functional programming interface
 * @export
 */
export const BillingApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BillingApiAxiosParamCreator(configuration)
    return {
        /**
         * API view to provide an aggregation of usage per type per day of the current billing period, combined per day.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingAnalyticsDailyList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DailyUsage>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingAnalyticsDailyList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * API view to provide the current usage and budget for the current billing period.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingCurrentUsageRetrieve(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CurrentUsage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingCurrentUsageRetrieve(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {BillingDataExportRequest} [billingDataExportRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingExportCreate(billingDataExportRequest?: BillingDataExportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingDataExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingExportCreate(billingDataExportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {BillingNotificationRequest} billingNotificationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingNotificationsCreate(billingNotificationRequest: BillingNotificationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingNotification>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingNotificationsCreate(billingNotificationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingNotificationsDestroy(notificationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingNotificationsDestroy(notificationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingNotificationsList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBillingNotificationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingNotificationsList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {PatchedBillingNotificationRequest} [patchedBillingNotificationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingNotificationsPartialUpdate(notificationId: string, patchedBillingNotificationRequest?: PatchedBillingNotificationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingNotification>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingNotificationsPartialUpdate(notificationId, patchedBillingNotificationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingNotificationsRetrieve(notificationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingNotification>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingNotificationsRetrieve(notificationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {BillingNotificationRequest} billingNotificationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async billingNotificationsUpdate(notificationId: string, billingNotificationRequest: BillingNotificationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BillingNotification>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.billingNotificationsUpdate(notificationId, billingNotificationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * BillingApi - factory interface
 * @export
 */
export const BillingApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BillingApiFp(configuration)
    return {
        /**
         * API view to provide an aggregation of usage per type per day of the current billing period, combined per day.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingAnalyticsDailyList(options?: any): AxiosPromise<Array<DailyUsage>> {
            return localVarFp.billingAnalyticsDailyList(options).then((request) => request(axios, basePath));
        },
        /**
         * API view to provide the current usage and budget for the current billing period.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingCurrentUsageRetrieve(options?: any): AxiosPromise<CurrentUsage> {
            return localVarFp.billingCurrentUsageRetrieve(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingDataExportRequest} [billingDataExportRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingExportCreate(billingDataExportRequest?: BillingDataExportRequest, options?: any): AxiosPromise<BillingDataExport> {
            return localVarFp.billingExportCreate(billingDataExportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {BillingNotificationRequest} billingNotificationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsCreate(billingNotificationRequest: BillingNotificationRequest, options?: any): AxiosPromise<BillingNotification> {
            return localVarFp.billingNotificationsCreate(billingNotificationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsDestroy(notificationId: string, options?: any): AxiosPromise<void> {
            return localVarFp.billingNotificationsDestroy(notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsList(nextKey?: string, options?: any): AxiosPromise<PaginatedBillingNotificationList> {
            return localVarFp.billingNotificationsList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {PatchedBillingNotificationRequest} [patchedBillingNotificationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsPartialUpdate(notificationId: string, patchedBillingNotificationRequest?: PatchedBillingNotificationRequest, options?: any): AxiosPromise<BillingNotification> {
            return localVarFp.billingNotificationsPartialUpdate(notificationId, patchedBillingNotificationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsRetrieve(notificationId: string, options?: any): AxiosPromise<BillingNotification> {
            return localVarFp.billingNotificationsRetrieve(notificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} notificationId 
         * @param {BillingNotificationRequest} billingNotificationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        billingNotificationsUpdate(notificationId: string, billingNotificationRequest: BillingNotificationRequest, options?: any): AxiosPromise<BillingNotification> {
            return localVarFp.billingNotificationsUpdate(notificationId, billingNotificationRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BillingApi - interface
 * @export
 * @interface BillingApi
 */
export interface BillingApiInterface {
    /**
     * API view to provide an aggregation of usage per type per day of the current billing period, combined per day.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingAnalyticsDailyList(options?: AxiosRequestConfig): AxiosPromise<Array<DailyUsage>>;

    /**
     * API view to provide the current usage and budget for the current billing period.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingCurrentUsageRetrieve(options?: AxiosRequestConfig): AxiosPromise<CurrentUsage>;

    /**
     * 
     * @param {BillingDataExportRequest} [billingDataExportRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingExportCreate(billingDataExportRequest?: BillingDataExportRequest, options?: AxiosRequestConfig): AxiosPromise<BillingDataExport>;

    /**
     * 
     * @param {BillingNotificationRequest} billingNotificationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingNotificationsCreate(billingNotificationRequest: BillingNotificationRequest, options?: AxiosRequestConfig): AxiosPromise<BillingNotification>;

    /**
     * 
     * @param {string} notificationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingNotificationsDestroy(notificationId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingNotificationsList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedBillingNotificationList>;

    /**
     * 
     * @param {string} notificationId 
     * @param {PatchedBillingNotificationRequest} [patchedBillingNotificationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingNotificationsPartialUpdate(notificationId: string, patchedBillingNotificationRequest?: PatchedBillingNotificationRequest, options?: AxiosRequestConfig): AxiosPromise<BillingNotification>;

    /**
     * 
     * @param {string} notificationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingNotificationsRetrieve(notificationId: string, options?: AxiosRequestConfig): AxiosPromise<BillingNotification>;

    /**
     * 
     * @param {string} notificationId 
     * @param {BillingNotificationRequest} billingNotificationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApiInterface
     */
    billingNotificationsUpdate(notificationId: string, billingNotificationRequest: BillingNotificationRequest, options?: AxiosRequestConfig): AxiosPromise<BillingNotification>;

}

/**
 * Request parameters for billingExportCreate operation in BillingApi.
 * @export
 * @interface BillingApiBillingExportCreateRequest
 */
export interface BillingApiBillingExportCreateRequest {
    /**
     * 
     * @type {BillingDataExportRequest}
     * @memberof BillingApiBillingExportCreate
     */
    readonly billingDataExportRequest?: BillingDataExportRequest
}

/**
 * Request parameters for billingNotificationsCreate operation in BillingApi.
 * @export
 * @interface BillingApiBillingNotificationsCreateRequest
 */
export interface BillingApiBillingNotificationsCreateRequest {
    /**
     * 
     * @type {BillingNotificationRequest}
     * @memberof BillingApiBillingNotificationsCreate
     */
    readonly billingNotificationRequest: BillingNotificationRequest
}

/**
 * Request parameters for billingNotificationsDestroy operation in BillingApi.
 * @export
 * @interface BillingApiBillingNotificationsDestroyRequest
 */
export interface BillingApiBillingNotificationsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof BillingApiBillingNotificationsDestroy
     */
    readonly notificationId: string
}

/**
 * Request parameters for billingNotificationsList operation in BillingApi.
 * @export
 * @interface BillingApiBillingNotificationsListRequest
 */
export interface BillingApiBillingNotificationsListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof BillingApiBillingNotificationsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for billingNotificationsPartialUpdate operation in BillingApi.
 * @export
 * @interface BillingApiBillingNotificationsPartialUpdateRequest
 */
export interface BillingApiBillingNotificationsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BillingApiBillingNotificationsPartialUpdate
     */
    readonly notificationId: string

    /**
     * 
     * @type {PatchedBillingNotificationRequest}
     * @memberof BillingApiBillingNotificationsPartialUpdate
     */
    readonly patchedBillingNotificationRequest?: PatchedBillingNotificationRequest
}

/**
 * Request parameters for billingNotificationsRetrieve operation in BillingApi.
 * @export
 * @interface BillingApiBillingNotificationsRetrieveRequest
 */
export interface BillingApiBillingNotificationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BillingApiBillingNotificationsRetrieve
     */
    readonly notificationId: string
}

/**
 * Request parameters for billingNotificationsUpdate operation in BillingApi.
 * @export
 * @interface BillingApiBillingNotificationsUpdateRequest
 */
export interface BillingApiBillingNotificationsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BillingApiBillingNotificationsUpdate
     */
    readonly notificationId: string

    /**
     * 
     * @type {BillingNotificationRequest}
     * @memberof BillingApiBillingNotificationsUpdate
     */
    readonly billingNotificationRequest: BillingNotificationRequest
}

/**
 * BillingApi - object-oriented interface
 * @export
 * @class BillingApi
 * @extends {BaseAPI}
 */
export class BillingApi extends BaseAPI implements BillingApiInterface {
    /**
     * API view to provide an aggregation of usage per type per day of the current billing period, combined per day.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingAnalyticsDailyList(options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingAnalyticsDailyList(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * API view to provide the current usage and budget for the current billing period.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingCurrentUsageRetrieve(options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingCurrentUsageRetrieve(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingExportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingExportCreate(requestParameters: BillingApiBillingExportCreateRequest = {}, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingExportCreate(requestParameters.billingDataExportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingNotificationsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingNotificationsCreate(requestParameters: BillingApiBillingNotificationsCreateRequest, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingNotificationsCreate(requestParameters.billingNotificationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingNotificationsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingNotificationsDestroy(requestParameters: BillingApiBillingNotificationsDestroyRequest, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingNotificationsDestroy(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingNotificationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingNotificationsList(requestParameters: BillingApiBillingNotificationsListRequest = {}, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingNotificationsList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingNotificationsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingNotificationsPartialUpdate(requestParameters: BillingApiBillingNotificationsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingNotificationsPartialUpdate(requestParameters.notificationId, requestParameters.patchedBillingNotificationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingNotificationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingNotificationsRetrieve(requestParameters: BillingApiBillingNotificationsRetrieveRequest, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingNotificationsRetrieve(requestParameters.notificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {BillingApiBillingNotificationsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BillingApi
     */
    public billingNotificationsUpdate(requestParameters: BillingApiBillingNotificationsUpdateRequest, options?: AxiosRequestConfig) {
        return BillingApiFp(this.configuration).billingNotificationsUpdate(requestParameters.notificationId, requestParameters.billingNotificationRequest, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * BindersApi - axios parameter creator
 * @export
 */
export const BindersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * List Or Create Binders.
         * @param {BinderRequest} binderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersCreate: async (binderRequest: BinderRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderRequest' is not null or undefined
            assertParamExists('bindersCreate', 'binderRequest', binderRequest)
            const localVarPath = `/binders/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersDestroy: async (binderId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersDestroy', 'binderId', binderId)
            const localVarPath = `/binders/{binder_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Articles Export View.
         * @param {string} binderId 
         * @param {DataExportRequest} dataExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersExportCreate: async (binderId: string, dataExportRequest: DataExportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersExportCreate', 'binderId', binderId)
            // verify required parameter 'dataExportRequest' is not null or undefined
            assertParamExists('bindersExportCreate', 'dataExportRequest', dataExportRequest)
            const localVarPath = `/binders/{binder_id}/export/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(dataExportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Binder Item By Article.
         * @param {string} articleId 
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsArticlesRetrieve: async (articleId: string, binderId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'articleId' is not null or undefined
            assertParamExists('bindersItemsArticlesRetrieve', 'articleId', articleId)
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsArticlesRetrieve', 'binderId', binderId)
            const localVarPath = `/binders/{binder_id}/items/articles/{article_id}`
                .replace(`{${"article_id"}}`, encodeURIComponent(String(articleId)))
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create multiple Binder Items in a single request.
         * @param {string} binderId 
         * @param {Array<BinderItemRequest>} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsBatchCreateCreate: async (binderId: string, binderItemRequest: Array<BinderItemRequest>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsBatchCreateCreate', 'binderId', binderId)
            // verify required parameter 'binderItemRequest' is not null or undefined
            assertParamExists('bindersItemsBatchCreateCreate', 'binderItemRequest', binderItemRequest)
            const localVarPath = `/binders/{binder_id}/items/batch_create`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderItemRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Or Create Binder Items.
         * @param {string} binderId 
         * @param {BinderItemRequest} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsCreate: async (binderId: string, binderItemRequest: BinderItemRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsCreate', 'binderId', binderId)
            // verify required parameter 'binderItemRequest' is not null or undefined
            assertParamExists('bindersItemsCreate', 'binderItemRequest', binderItemRequest)
            const localVarPath = `/binders/{binder_id}/items/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderItemRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsDestroy: async (binderId: string, binderItemId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsDestroy', 'binderId', binderId)
            // verify required parameter 'binderItemId' is not null or undefined
            assertParamExists('bindersItemsDestroy', 'binderItemId', binderItemId)
            const localVarPath = `/binders/{binder_id}/items/{binder_item_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_item_id"}}`, encodeURIComponent(String(binderItemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List binder Items.
         * @param {string} binderId 
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} sorting Sort binder items.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder Items using Query parameter.
         * @param {Array<string>} [tags] Filter items on tags.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsList: async (binderId: string, sorting: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', nextKey?: string, query?: string, tags?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsList', 'binderId', binderId)
            // verify required parameter 'sorting' is not null or undefined
            assertParamExists('bindersItemsList', 'sorting', sorting)
            const localVarPath = `/binders/{binder_id}/items/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (tags) {
                localVarQueryParameter['tags'] = tags;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {PatchedBinderItemRequest} [patchedBinderItemRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsPartialUpdate: async (binderId: string, binderItemId: string, patchedBinderItemRequest?: PatchedBinderItemRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsPartialUpdate', 'binderId', binderId)
            // verify required parameter 'binderItemId' is not null or undefined
            assertParamExists('bindersItemsPartialUpdate', 'binderItemId', binderItemId)
            const localVarPath = `/binders/{binder_id}/items/{binder_item_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_item_id"}}`, encodeURIComponent(String(binderItemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedBinderItemRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsRetrieve: async (binderId: string, binderItemId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsRetrieve', 'binderId', binderId)
            // verify required parameter 'binderItemId' is not null or undefined
            assertParamExists('bindersItemsRetrieve', 'binderItemId', binderItemId)
            const localVarPath = `/binders/{binder_id}/items/{binder_item_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_item_id"}}`, encodeURIComponent(String(binderItemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {BinderItemRequest} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsUpdate: async (binderId: string, binderItemId: string, binderItemRequest: BinderItemRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersItemsUpdate', 'binderId', binderId)
            // verify required parameter 'binderItemId' is not null or undefined
            assertParamExists('bindersItemsUpdate', 'binderItemId', binderItemId)
            // verify required parameter 'binderItemRequest' is not null or undefined
            assertParamExists('bindersItemsUpdate', 'binderItemRequest', binderItemRequest)
            const localVarPath = `/binders/{binder_id}/items/{binder_item_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_item_id"}}`, encodeURIComponent(String(binderItemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderItemRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List binders.
         * @param {'alphabetical' | 'created_at' | 'recently_used' | 'updated_at'} sorting Sort binders. \&#39;recently_used\&#39; orders by the requesting user\&#39;s most recent add to the binder, falling back to the binder\&#39;s last-edited time for binders the user hasn\&#39;t added to, name as tie-break.
         * @param {'ascending' | 'descending'} sortingOrder Sort binders order, descending is from high to low and ascending from high to low.
         * @param {string} [annotateArticleId] Article external_id. When given, each result carries contains_article (bool) indicating whether the binder already contains that article. Omitted -&gt; contains_article is null.
         * @param {string} [annotateComplianceEntityResultId] ComplianceEntityResult external_id. When given, each result carries contains_compliance_entity_result (bool) indicating whether the binder already contains that compliance entity result. Omitted -&gt; contains_compliance_entity_result is null. Can be combined with annotate_article_id.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [ownedBinders] Only included binders owned by yourself.
         * @param {string} [query] Filter Binders using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersList: async (sorting: 'alphabetical' | 'created_at' | 'recently_used' | 'updated_at', sortingOrder: 'ascending' | 'descending', annotateArticleId?: string, annotateComplianceEntityResultId?: string, nextKey?: string, ownedBinders?: boolean, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'sorting' is not null or undefined
            assertParamExists('bindersList', 'sorting', sorting)
            // verify required parameter 'sortingOrder' is not null or undefined
            assertParamExists('bindersList', 'sortingOrder', sortingOrder)
            const localVarPath = `/binders/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (annotateArticleId !== undefined) {
                localVarQueryParameter['annotate_article_id'] = annotateArticleId;
            }

            if (annotateComplianceEntityResultId !== undefined) {
                localVarQueryParameter['annotate_compliance_entity_result_id'] = annotateComplianceEntityResultId;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (ownedBinders !== undefined) {
                localVarQueryParameter['owned_binders'] = ownedBinders;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {PatchedBinderRequest} [patchedBinderRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersPartialUpdate: async (binderId: string, patchedBinderRequest?: PatchedBinderRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersPartialUpdate', 'binderId', binderId)
            const localVarPath = `/binders/{binder_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedBinderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Or Create Binder Profile User Groups.
         * @param {string} binderId 
         * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsCreate: async (binderId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsCreate', 'binderId', binderId)
            // verify required parameter 'binderProfileUserGroupRequest' is not null or undefined
            assertParamExists('bindersProfileUserGroupsCreate', 'binderProfileUserGroupRequest', binderProfileUserGroupRequest)
            const localVarPath = `/binders/{binder_id}/profile_user_groups/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsDestroy: async (binderId: string, profileUserGroupId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsDestroy', 'binderId', binderId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsDestroy', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/binders/{binder_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List binders.
         * @param {string} binderId 
         * @param {'created_at' | 'updated_at'} sorting Sort binder user groups
         * @param {'ascending' | 'descending'} sortingOrder Sort binder user groups order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder profile user groups using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsList: async (binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsList', 'binderId', binderId)
            // verify required parameter 'sorting' is not null or undefined
            assertParamExists('bindersProfileUserGroupsList', 'sorting', sorting)
            // verify required parameter 'sortingOrder' is not null or undefined
            assertParamExists('bindersProfileUserGroupsList', 'sortingOrder', sortingOrder)
            const localVarPath = `/binders/{binder_id}/profile_user_groups/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {PatchedBinderProfileUserGroupRequest} [patchedBinderProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsPartialUpdate: async (binderId: string, profileUserGroupId: string, patchedBinderProfileUserGroupRequest?: PatchedBinderProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsPartialUpdate', 'binderId', binderId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsPartialUpdate', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/binders/{binder_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedBinderProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsRetrieve: async (binderId: string, profileUserGroupId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsRetrieve', 'binderId', binderId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsRetrieve', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/binders/{binder_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsUpdate: async (binderId: string, profileUserGroupId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsUpdate', 'binderId', binderId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('bindersProfileUserGroupsUpdate', 'profileUserGroupId', profileUserGroupId)
            // verify required parameter 'binderProfileUserGroupRequest' is not null or undefined
            assertParamExists('bindersProfileUserGroupsUpdate', 'binderProfileUserGroupRequest', binderProfileUserGroupRequest)
            const localVarPath = `/binders/{binder_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersRetrieve: async (binderId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersRetrieve', 'binderId', binderId)
            const localVarPath = `/binders/{binder_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve Binder Summary View.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersSummaryRetrieve: async (binderId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersSummaryRetrieve', 'binderId', binderId)
            const localVarPath = `/binders/{binder_id}/summary/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List binder tags.
         * @param {string} binderId 
         * @param {'binder_item_count' | 'created_at' | 'updated_at'} sorting Sort binder tags
         * @param {'ascending' | 'descending'} sortingOrder Sort binder tags order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder tags using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersTagsList: async (binderId: string, sorting: 'binder_item_count' | 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersTagsList', 'binderId', binderId)
            // verify required parameter 'sorting' is not null or undefined
            assertParamExists('bindersTagsList', 'sorting', sorting)
            // verify required parameter 'sortingOrder' is not null or undefined
            assertParamExists('bindersTagsList', 'sortingOrder', sortingOrder)
            const localVarPath = `/binders/{binder_id}/tags/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {BinderRequest} binderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUpdate: async (binderId: string, binderRequest: BinderRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUpdate', 'binderId', binderId)
            // verify required parameter 'binderRequest' is not null or undefined
            assertParamExists('bindersUpdate', 'binderRequest', binderRequest)
            const localVarPath = `/binders/{binder_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Or Create Binder Users.
         * @param {string} binderId 
         * @param {BinderUserRequest} binderUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersCreate: async (binderId: string, binderUserRequest: BinderUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUsersCreate', 'binderId', binderId)
            // verify required parameter 'binderUserRequest' is not null or undefined
            assertParamExists('bindersUsersCreate', 'binderUserRequest', binderUserRequest)
            const localVarPath = `/binders/{binder_id}/users/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersDestroy: async (binderId: string, binderUserId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUsersDestroy', 'binderId', binderId)
            // verify required parameter 'binderUserId' is not null or undefined
            assertParamExists('bindersUsersDestroy', 'binderUserId', binderUserId)
            const localVarPath = `/binders/{binder_id}/users/{binder_user_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_user_id"}}`, encodeURIComponent(String(binderUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List binders.
         * @param {string} binderId 
         * @param {'created_at' | 'updated_at'} sorting Sort binder users
         * @param {'ascending' | 'descending'} sortingOrder Sort binder users order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder users using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersList: async (binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUsersList', 'binderId', binderId)
            // verify required parameter 'sorting' is not null or undefined
            assertParamExists('bindersUsersList', 'sorting', sorting)
            // verify required parameter 'sortingOrder' is not null or undefined
            assertParamExists('bindersUsersList', 'sortingOrder', sortingOrder)
            const localVarPath = `/binders/{binder_id}/users/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {PatchedBinderUserRequest} [patchedBinderUserRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersPartialUpdate: async (binderId: string, binderUserId: string, patchedBinderUserRequest?: PatchedBinderUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUsersPartialUpdate', 'binderId', binderId)
            // verify required parameter 'binderUserId' is not null or undefined
            assertParamExists('bindersUsersPartialUpdate', 'binderUserId', binderUserId)
            const localVarPath = `/binders/{binder_id}/users/{binder_user_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_user_id"}}`, encodeURIComponent(String(binderUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedBinderUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersRetrieve: async (binderId: string, binderUserId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUsersRetrieve', 'binderId', binderId)
            // verify required parameter 'binderUserId' is not null or undefined
            assertParamExists('bindersUsersRetrieve', 'binderUserId', binderUserId)
            const localVarPath = `/binders/{binder_id}/users/{binder_user_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_user_id"}}`, encodeURIComponent(String(binderUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {BinderUserRequest} binderUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersUpdate: async (binderId: string, binderUserId: string, binderUserRequest: BinderUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'binderId' is not null or undefined
            assertParamExists('bindersUsersUpdate', 'binderId', binderId)
            // verify required parameter 'binderUserId' is not null or undefined
            assertParamExists('bindersUsersUpdate', 'binderUserId', binderUserId)
            // verify required parameter 'binderUserRequest' is not null or undefined
            assertParamExists('bindersUsersUpdate', 'binderUserRequest', binderUserRequest)
            const localVarPath = `/binders/{binder_id}/users/{binder_user_id}/`
                .replace(`{${"binder_id"}}`, encodeURIComponent(String(binderId)))
                .replace(`{${"binder_user_id"}}`, encodeURIComponent(String(binderUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(binderUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BindersApi - functional programming interface
 * @export
 */
export const BindersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BindersApiAxiosParamCreator(configuration)
    return {
        /**
         * List Or Create Binders.
         * @param {BinderRequest} binderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersCreate(binderRequest: BinderRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Binder>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersCreate(binderRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersDestroy(binderId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersDestroy(binderId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Articles Export View.
         * @param {string} binderId 
         * @param {DataExportRequest} dataExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersExportCreate(binderId: string, dataExportRequest: DataExportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DataExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersExportCreate(binderId, dataExportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Binder Item By Article.
         * @param {string} articleId 
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsArticlesRetrieve(articleId: string, binderId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderItem>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsArticlesRetrieve(articleId, binderId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create multiple Binder Items in a single request.
         * @param {string} binderId 
         * @param {Array<BinderItemRequest>} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsBatchCreateCreate(binderId: string, binderItemRequest: Array<BinderItemRequest>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsBatchCreateCreate(binderId, binderItemRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Or Create Binder Items.
         * @param {string} binderId 
         * @param {BinderItemRequest} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsCreate(binderId: string, binderItemRequest: BinderItemRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderItem>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsCreate(binderId, binderItemRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsDestroy(binderId: string, binderItemId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsDestroy(binderId, binderItemId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List binder Items.
         * @param {string} binderId 
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} sorting Sort binder items.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder Items using Query parameter.
         * @param {Array<string>} [tags] Filter items on tags.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsList(binderId: string, sorting: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', nextKey?: string, query?: string, tags?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBinderItemList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsList(binderId, sorting, nextKey, query, tags, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {PatchedBinderItemRequest} [patchedBinderItemRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsPartialUpdate(binderId: string, binderItemId: string, patchedBinderItemRequest?: PatchedBinderItemRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderItem>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsPartialUpdate(binderId, binderItemId, patchedBinderItemRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsRetrieve(binderId: string, binderItemId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderItem>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsRetrieve(binderId, binderItemId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {BinderItemRequest} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersItemsUpdate(binderId: string, binderItemId: string, binderItemRequest: BinderItemRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderItem>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersItemsUpdate(binderId, binderItemId, binderItemRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List binders.
         * @param {'alphabetical' | 'created_at' | 'recently_used' | 'updated_at'} sorting Sort binders. \&#39;recently_used\&#39; orders by the requesting user\&#39;s most recent add to the binder, falling back to the binder\&#39;s last-edited time for binders the user hasn\&#39;t added to, name as tie-break.
         * @param {'ascending' | 'descending'} sortingOrder Sort binders order, descending is from high to low and ascending from high to low.
         * @param {string} [annotateArticleId] Article external_id. When given, each result carries contains_article (bool) indicating whether the binder already contains that article. Omitted -&gt; contains_article is null.
         * @param {string} [annotateComplianceEntityResultId] ComplianceEntityResult external_id. When given, each result carries contains_compliance_entity_result (bool) indicating whether the binder already contains that compliance entity result. Omitted -&gt; contains_compliance_entity_result is null. Can be combined with annotate_article_id.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [ownedBinders] Only included binders owned by yourself.
         * @param {string} [query] Filter Binders using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersList(sorting: 'alphabetical' | 'created_at' | 'recently_used' | 'updated_at', sortingOrder: 'ascending' | 'descending', annotateArticleId?: string, annotateComplianceEntityResultId?: string, nextKey?: string, ownedBinders?: boolean, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBinderList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersList(sorting, sortingOrder, annotateArticleId, annotateComplianceEntityResultId, nextKey, ownedBinders, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {PatchedBinderRequest} [patchedBinderRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersPartialUpdate(binderId: string, patchedBinderRequest?: PatchedBinderRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Binder>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersPartialUpdate(binderId, patchedBinderRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Or Create Binder Profile User Groups.
         * @param {string} binderId 
         * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersProfileUserGroupsCreate(binderId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersProfileUserGroupsCreate(binderId, binderProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersProfileUserGroupsDestroy(binderId: string, profileUserGroupId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersProfileUserGroupsDestroy(binderId, profileUserGroupId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List binders.
         * @param {string} binderId 
         * @param {'created_at' | 'updated_at'} sorting Sort binder user groups
         * @param {'ascending' | 'descending'} sortingOrder Sort binder user groups order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder profile user groups using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersProfileUserGroupsList(binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBinderProfileUserGroupList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersProfileUserGroupsList(binderId, sorting, sortingOrder, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {PatchedBinderProfileUserGroupRequest} [patchedBinderProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersProfileUserGroupsPartialUpdate(binderId: string, profileUserGroupId: string, patchedBinderProfileUserGroupRequest?: PatchedBinderProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersProfileUserGroupsPartialUpdate(binderId, profileUserGroupId, patchedBinderProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersProfileUserGroupsRetrieve(binderId: string, profileUserGroupId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersProfileUserGroupsRetrieve(binderId, profileUserGroupId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersProfileUserGroupsUpdate(binderId: string, profileUserGroupId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersProfileUserGroupsUpdate(binderId, profileUserGroupId, binderProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersRetrieve(binderId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Binder>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersRetrieve(binderId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve Binder Summary View.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersSummaryRetrieve(binderId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderSummary>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersSummaryRetrieve(binderId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List binder tags.
         * @param {string} binderId 
         * @param {'binder_item_count' | 'created_at' | 'updated_at'} sorting Sort binder tags
         * @param {'ascending' | 'descending'} sortingOrder Sort binder tags order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder tags using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersTagsList(binderId: string, sorting: 'binder_item_count' | 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBinderTagList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersTagsList(binderId, sorting, sortingOrder, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {BinderRequest} binderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUpdate(binderId: string, binderRequest: BinderRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Binder>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUpdate(binderId, binderRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Or Create Binder Users.
         * @param {string} binderId 
         * @param {BinderUserRequest} binderUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUsersCreate(binderId: string, binderUserRequest: BinderUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUsersCreate(binderId, binderUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUsersDestroy(binderId: string, binderUserId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUsersDestroy(binderId, binderUserId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List binders.
         * @param {string} binderId 
         * @param {'created_at' | 'updated_at'} sorting Sort binder users
         * @param {'ascending' | 'descending'} sortingOrder Sort binder users order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder users using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUsersList(binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBinderUserList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUsersList(binderId, sorting, sortingOrder, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {PatchedBinderUserRequest} [patchedBinderUserRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUsersPartialUpdate(binderId: string, binderUserId: string, patchedBinderUserRequest?: PatchedBinderUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUsersPartialUpdate(binderId, binderUserId, patchedBinderUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUsersRetrieve(binderId: string, binderUserId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUsersRetrieve(binderId, binderUserId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {BinderUserRequest} binderUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bindersUsersUpdate(binderId: string, binderUserId: string, binderUserRequest: BinderUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BinderUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bindersUsersUpdate(binderId, binderUserId, binderUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * BindersApi - factory interface
 * @export
 */
export const BindersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BindersApiFp(configuration)
    return {
        /**
         * List Or Create Binders.
         * @param {BinderRequest} binderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersCreate(binderRequest: BinderRequest, options?: any): AxiosPromise<Binder> {
            return localVarFp.bindersCreate(binderRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersDestroy(binderId: string, options?: any): AxiosPromise<void> {
            return localVarFp.bindersDestroy(binderId, options).then((request) => request(axios, basePath));
        },
        /**
         * Articles Export View.
         * @param {string} binderId 
         * @param {DataExportRequest} dataExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersExportCreate(binderId: string, dataExportRequest: DataExportRequest, options?: any): AxiosPromise<DataExport> {
            return localVarFp.bindersExportCreate(binderId, dataExportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Binder Item By Article.
         * @param {string} articleId 
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsArticlesRetrieve(articleId: string, binderId: string, options?: any): AxiosPromise<BinderItem> {
            return localVarFp.bindersItemsArticlesRetrieve(articleId, binderId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create multiple Binder Items in a single request.
         * @param {string} binderId 
         * @param {Array<BinderItemRequest>} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsBatchCreateCreate(binderId: string, binderItemRequest: Array<BinderItemRequest>, options?: any): AxiosPromise<void> {
            return localVarFp.bindersItemsBatchCreateCreate(binderId, binderItemRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List Or Create Binder Items.
         * @param {string} binderId 
         * @param {BinderItemRequest} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsCreate(binderId: string, binderItemRequest: BinderItemRequest, options?: any): AxiosPromise<BinderItem> {
            return localVarFp.bindersItemsCreate(binderId, binderItemRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsDestroy(binderId: string, binderItemId: string, options?: any): AxiosPromise<void> {
            return localVarFp.bindersItemsDestroy(binderId, binderItemId, options).then((request) => request(axios, basePath));
        },
        /**
         * List binder Items.
         * @param {string} binderId 
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} sorting Sort binder items.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder Items using Query parameter.
         * @param {Array<string>} [tags] Filter items on tags.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsList(binderId: string, sorting: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', nextKey?: string, query?: string, tags?: Array<string>, options?: any): AxiosPromise<PaginatedBinderItemList> {
            return localVarFp.bindersItemsList(binderId, sorting, nextKey, query, tags, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {PatchedBinderItemRequest} [patchedBinderItemRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsPartialUpdate(binderId: string, binderItemId: string, patchedBinderItemRequest?: PatchedBinderItemRequest, options?: any): AxiosPromise<BinderItem> {
            return localVarFp.bindersItemsPartialUpdate(binderId, binderItemId, patchedBinderItemRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsRetrieve(binderId: string, binderItemId: string, options?: any): AxiosPromise<BinderItem> {
            return localVarFp.bindersItemsRetrieve(binderId, binderItemId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder Item.
         * @param {string} binderId 
         * @param {string} binderItemId 
         * @param {BinderItemRequest} binderItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersItemsUpdate(binderId: string, binderItemId: string, binderItemRequest: BinderItemRequest, options?: any): AxiosPromise<BinderItem> {
            return localVarFp.bindersItemsUpdate(binderId, binderItemId, binderItemRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List binders.
         * @param {'alphabetical' | 'created_at' | 'recently_used' | 'updated_at'} sorting Sort binders. \&#39;recently_used\&#39; orders by the requesting user\&#39;s most recent add to the binder, falling back to the binder\&#39;s last-edited time for binders the user hasn\&#39;t added to, name as tie-break.
         * @param {'ascending' | 'descending'} sortingOrder Sort binders order, descending is from high to low and ascending from high to low.
         * @param {string} [annotateArticleId] Article external_id. When given, each result carries contains_article (bool) indicating whether the binder already contains that article. Omitted -&gt; contains_article is null.
         * @param {string} [annotateComplianceEntityResultId] ComplianceEntityResult external_id. When given, each result carries contains_compliance_entity_result (bool) indicating whether the binder already contains that compliance entity result. Omitted -&gt; contains_compliance_entity_result is null. Can be combined with annotate_article_id.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [ownedBinders] Only included binders owned by yourself.
         * @param {string} [query] Filter Binders using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersList(sorting: 'alphabetical' | 'created_at' | 'recently_used' | 'updated_at', sortingOrder: 'ascending' | 'descending', annotateArticleId?: string, annotateComplianceEntityResultId?: string, nextKey?: string, ownedBinders?: boolean, query?: string, options?: any): AxiosPromise<PaginatedBinderList> {
            return localVarFp.bindersList(sorting, sortingOrder, annotateArticleId, annotateComplianceEntityResultId, nextKey, ownedBinders, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {PatchedBinderRequest} [patchedBinderRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersPartialUpdate(binderId: string, patchedBinderRequest?: PatchedBinderRequest, options?: any): AxiosPromise<Binder> {
            return localVarFp.bindersPartialUpdate(binderId, patchedBinderRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List Or Create Binder Profile User Groups.
         * @param {string} binderId 
         * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsCreate(binderId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options?: any): AxiosPromise<BinderProfileUserGroup> {
            return localVarFp.bindersProfileUserGroupsCreate(binderId, binderProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsDestroy(binderId: string, profileUserGroupId: string, options?: any): AxiosPromise<void> {
            return localVarFp.bindersProfileUserGroupsDestroy(binderId, profileUserGroupId, options).then((request) => request(axios, basePath));
        },
        /**
         * List binders.
         * @param {string} binderId 
         * @param {'created_at' | 'updated_at'} sorting Sort binder user groups
         * @param {'ascending' | 'descending'} sortingOrder Sort binder user groups order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder profile user groups using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsList(binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedBinderProfileUserGroupList> {
            return localVarFp.bindersProfileUserGroupsList(binderId, sorting, sortingOrder, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {PatchedBinderProfileUserGroupRequest} [patchedBinderProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsPartialUpdate(binderId: string, profileUserGroupId: string, patchedBinderProfileUserGroupRequest?: PatchedBinderProfileUserGroupRequest, options?: any): AxiosPromise<BinderProfileUserGroup> {
            return localVarFp.bindersProfileUserGroupsPartialUpdate(binderId, profileUserGroupId, patchedBinderProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsRetrieve(binderId: string, profileUserGroupId: string, options?: any): AxiosPromise<BinderProfileUserGroup> {
            return localVarFp.bindersProfileUserGroupsRetrieve(binderId, profileUserGroupId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User Group.
         * @param {string} binderId 
         * @param {string} profileUserGroupId 
         * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersProfileUserGroupsUpdate(binderId: string, profileUserGroupId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options?: any): AxiosPromise<BinderProfileUserGroup> {
            return localVarFp.bindersProfileUserGroupsUpdate(binderId, profileUserGroupId, binderProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersRetrieve(binderId: string, options?: any): AxiosPromise<Binder> {
            return localVarFp.bindersRetrieve(binderId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve Binder Summary View.
         * @param {string} binderId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersSummaryRetrieve(binderId: string, options?: any): AxiosPromise<BinderSummary> {
            return localVarFp.bindersSummaryRetrieve(binderId, options).then((request) => request(axios, basePath));
        },
        /**
         * List binder tags.
         * @param {string} binderId 
         * @param {'binder_item_count' | 'created_at' | 'updated_at'} sorting Sort binder tags
         * @param {'ascending' | 'descending'} sortingOrder Sort binder tags order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder tags using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersTagsList(binderId: string, sorting: 'binder_item_count' | 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedBinderTagList> {
            return localVarFp.bindersTagsList(binderId, sorting, sortingOrder, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder.
         * @param {string} binderId 
         * @param {BinderRequest} binderRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUpdate(binderId: string, binderRequest: BinderRequest, options?: any): AxiosPromise<Binder> {
            return localVarFp.bindersUpdate(binderId, binderRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List Or Create Binder Users.
         * @param {string} binderId 
         * @param {BinderUserRequest} binderUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersCreate(binderId: string, binderUserRequest: BinderUserRequest, options?: any): AxiosPromise<BinderUser> {
            return localVarFp.bindersUsersCreate(binderId, binderUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersDestroy(binderId: string, binderUserId: string, options?: any): AxiosPromise<void> {
            return localVarFp.bindersUsersDestroy(binderId, binderUserId, options).then((request) => request(axios, basePath));
        },
        /**
         * List binders.
         * @param {string} binderId 
         * @param {'created_at' | 'updated_at'} sorting Sort binder users
         * @param {'ascending' | 'descending'} sortingOrder Sort binder users order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Binder users using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersList(binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedBinderUserList> {
            return localVarFp.bindersUsersList(binderId, sorting, sortingOrder, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {PatchedBinderUserRequest} [patchedBinderUserRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersPartialUpdate(binderId: string, binderUserId: string, patchedBinderUserRequest?: PatchedBinderUserRequest, options?: any): AxiosPromise<BinderUser> {
            return localVarFp.bindersUsersPartialUpdate(binderId, binderUserId, patchedBinderUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersRetrieve(binderId: string, binderUserId: string, options?: any): AxiosPromise<BinderUser> {
            return localVarFp.bindersUsersRetrieve(binderId, binderUserId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Binder User.
         * @param {string} binderId 
         * @param {string} binderUserId 
         * @param {BinderUserRequest} binderUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bindersUsersUpdate(binderId: string, binderUserId: string, binderUserRequest: BinderUserRequest, options?: any): AxiosPromise<BinderUser> {
            return localVarFp.bindersUsersUpdate(binderId, binderUserId, binderUserRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * BindersApi - interface
 * @export
 * @interface BindersApi
 */
export interface BindersApiInterface {
    /**
     * List Or Create Binders.
     * @param {BinderRequest} binderRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersCreate(binderRequest: BinderRequest, options?: AxiosRequestConfig): AxiosPromise<Binder>;

    /**
     * Retrieve, update or destroy Binder.
     * @param {string} binderId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersDestroy(binderId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Articles Export View.
     * @param {string} binderId 
     * @param {DataExportRequest} dataExportRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersExportCreate(binderId: string, dataExportRequest: DataExportRequest, options?: AxiosRequestConfig): AxiosPromise<DataExport>;

    /**
     * Get Binder Item By Article.
     * @param {string} articleId 
     * @param {string} binderId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsArticlesRetrieve(articleId: string, binderId: string, options?: AxiosRequestConfig): AxiosPromise<BinderItem>;

    /**
     * Create multiple Binder Items in a single request.
     * @param {string} binderId 
     * @param {Array<BinderItemRequest>} binderItemRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsBatchCreateCreate(binderId: string, binderItemRequest: Array<BinderItemRequest>, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List Or Create Binder Items.
     * @param {string} binderId 
     * @param {BinderItemRequest} binderItemRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsCreate(binderId: string, binderItemRequest: BinderItemRequest, options?: AxiosRequestConfig): AxiosPromise<BinderItem>;

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {string} binderId 
     * @param {string} binderItemId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsDestroy(binderId: string, binderItemId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List binder Items.
     * @param {string} binderId 
     * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} sorting Sort binder items.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter Binder Items using Query parameter.
     * @param {Array<string>} [tags] Filter items on tags.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsList(binderId: string, sorting: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', nextKey?: string, query?: string, tags?: Array<string>, options?: AxiosRequestConfig): AxiosPromise<PaginatedBinderItemList>;

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {string} binderId 
     * @param {string} binderItemId 
     * @param {PatchedBinderItemRequest} [patchedBinderItemRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsPartialUpdate(binderId: string, binderItemId: string, patchedBinderItemRequest?: PatchedBinderItemRequest, options?: AxiosRequestConfig): AxiosPromise<BinderItem>;

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {string} binderId 
     * @param {string} binderItemId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsRetrieve(binderId: string, binderItemId: string, options?: AxiosRequestConfig): AxiosPromise<BinderItem>;

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {string} binderId 
     * @param {string} binderItemId 
     * @param {BinderItemRequest} binderItemRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersItemsUpdate(binderId: string, binderItemId: string, binderItemRequest: BinderItemRequest, options?: AxiosRequestConfig): AxiosPromise<BinderItem>;

    /**
     * List binders.
     * @param {'alphabetical' | 'created_at' | 'recently_used' | 'updated_at'} sorting Sort binders. \&#39;recently_used\&#39; orders by the requesting user\&#39;s most recent add to the binder, falling back to the binder\&#39;s last-edited time for binders the user hasn\&#39;t added to, name as tie-break.
     * @param {'ascending' | 'descending'} sortingOrder Sort binders order, descending is from high to low and ascending from high to low.
     * @param {string} [annotateArticleId] Article external_id. When given, each result carries contains_article (bool) indicating whether the binder already contains that article. Omitted -&gt; contains_article is null.
     * @param {string} [annotateComplianceEntityResultId] ComplianceEntityResult external_id. When given, each result carries contains_compliance_entity_result (bool) indicating whether the binder already contains that compliance entity result. Omitted -&gt; contains_compliance_entity_result is null. Can be combined with annotate_article_id.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {boolean} [ownedBinders] Only included binders owned by yourself.
     * @param {string} [query] Filter Binders using Query parameter, name search.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersList(sorting: 'alphabetical' | 'created_at' | 'recently_used' | 'updated_at', sortingOrder: 'ascending' | 'descending', annotateArticleId?: string, annotateComplianceEntityResultId?: string, nextKey?: string, ownedBinders?: boolean, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedBinderList>;

    /**
     * Retrieve, update or destroy Binder.
     * @param {string} binderId 
     * @param {PatchedBinderRequest} [patchedBinderRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersPartialUpdate(binderId: string, patchedBinderRequest?: PatchedBinderRequest, options?: AxiosRequestConfig): AxiosPromise<Binder>;

    /**
     * List Or Create Binder Profile User Groups.
     * @param {string} binderId 
     * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersProfileUserGroupsCreate(binderId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<BinderProfileUserGroup>;

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {string} binderId 
     * @param {string} profileUserGroupId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersProfileUserGroupsDestroy(binderId: string, profileUserGroupId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List binders.
     * @param {string} binderId 
     * @param {'created_at' | 'updated_at'} sorting Sort binder user groups
     * @param {'ascending' | 'descending'} sortingOrder Sort binder user groups order, descending is from high to low and ascending from high to low.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter Binder profile user groups using Query parameter, name search.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersProfileUserGroupsList(binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedBinderProfileUserGroupList>;

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {string} binderId 
     * @param {string} profileUserGroupId 
     * @param {PatchedBinderProfileUserGroupRequest} [patchedBinderProfileUserGroupRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersProfileUserGroupsPartialUpdate(binderId: string, profileUserGroupId: string, patchedBinderProfileUserGroupRequest?: PatchedBinderProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<BinderProfileUserGroup>;

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {string} binderId 
     * @param {string} profileUserGroupId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersProfileUserGroupsRetrieve(binderId: string, profileUserGroupId: string, options?: AxiosRequestConfig): AxiosPromise<BinderProfileUserGroup>;

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {string} binderId 
     * @param {string} profileUserGroupId 
     * @param {BinderProfileUserGroupRequest} binderProfileUserGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersProfileUserGroupsUpdate(binderId: string, profileUserGroupId: string, binderProfileUserGroupRequest: BinderProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<BinderProfileUserGroup>;

    /**
     * Retrieve, update or destroy Binder.
     * @param {string} binderId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersRetrieve(binderId: string, options?: AxiosRequestConfig): AxiosPromise<Binder>;

    /**
     * Retrieve Binder Summary View.
     * @param {string} binderId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersSummaryRetrieve(binderId: string, options?: AxiosRequestConfig): AxiosPromise<BinderSummary>;

    /**
     * List binder tags.
     * @param {string} binderId 
     * @param {'binder_item_count' | 'created_at' | 'updated_at'} sorting Sort binder tags
     * @param {'ascending' | 'descending'} sortingOrder Sort binder tags order, descending is from high to low and ascending from high to low.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter Binder tags using Query parameter, name search.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersTagsList(binderId: string, sorting: 'binder_item_count' | 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedBinderTagList>;

    /**
     * Retrieve, update or destroy Binder.
     * @param {string} binderId 
     * @param {BinderRequest} binderRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUpdate(binderId: string, binderRequest: BinderRequest, options?: AxiosRequestConfig): AxiosPromise<Binder>;

    /**
     * List Or Create Binder Users.
     * @param {string} binderId 
     * @param {BinderUserRequest} binderUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUsersCreate(binderId: string, binderUserRequest: BinderUserRequest, options?: AxiosRequestConfig): AxiosPromise<BinderUser>;

    /**
     * Retrieve, update or destroy Binder User.
     * @param {string} binderId 
     * @param {string} binderUserId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUsersDestroy(binderId: string, binderUserId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List binders.
     * @param {string} binderId 
     * @param {'created_at' | 'updated_at'} sorting Sort binder users
     * @param {'ascending' | 'descending'} sortingOrder Sort binder users order, descending is from high to low and ascending from high to low.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter Binder users using Query parameter, name search.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUsersList(binderId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedBinderUserList>;

    /**
     * Retrieve, update or destroy Binder User.
     * @param {string} binderId 
     * @param {string} binderUserId 
     * @param {PatchedBinderUserRequest} [patchedBinderUserRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUsersPartialUpdate(binderId: string, binderUserId: string, patchedBinderUserRequest?: PatchedBinderUserRequest, options?: AxiosRequestConfig): AxiosPromise<BinderUser>;

    /**
     * Retrieve, update or destroy Binder User.
     * @param {string} binderId 
     * @param {string} binderUserId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUsersRetrieve(binderId: string, binderUserId: string, options?: AxiosRequestConfig): AxiosPromise<BinderUser>;

    /**
     * Retrieve, update or destroy Binder User.
     * @param {string} binderId 
     * @param {string} binderUserId 
     * @param {BinderUserRequest} binderUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApiInterface
     */
    bindersUsersUpdate(binderId: string, binderUserId: string, binderUserRequest: BinderUserRequest, options?: AxiosRequestConfig): AxiosPromise<BinderUser>;

}

/**
 * Request parameters for bindersCreate operation in BindersApi.
 * @export
 * @interface BindersApiBindersCreateRequest
 */
export interface BindersApiBindersCreateRequest {
    /**
     * 
     * @type {BinderRequest}
     * @memberof BindersApiBindersCreate
     */
    readonly binderRequest: BinderRequest
}

/**
 * Request parameters for bindersDestroy operation in BindersApi.
 * @export
 * @interface BindersApiBindersDestroyRequest
 */
export interface BindersApiBindersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersDestroy
     */
    readonly binderId: string
}

/**
 * Request parameters for bindersExportCreate operation in BindersApi.
 * @export
 * @interface BindersApiBindersExportCreateRequest
 */
export interface BindersApiBindersExportCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersExportCreate
     */
    readonly binderId: string

    /**
     * 
     * @type {DataExportRequest}
     * @memberof BindersApiBindersExportCreate
     */
    readonly dataExportRequest: DataExportRequest
}

/**
 * Request parameters for bindersItemsArticlesRetrieve operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsArticlesRetrieveRequest
 */
export interface BindersApiBindersItemsArticlesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsArticlesRetrieve
     */
    readonly articleId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsArticlesRetrieve
     */
    readonly binderId: string
}

/**
 * Request parameters for bindersItemsBatchCreateCreate operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsBatchCreateCreateRequest
 */
export interface BindersApiBindersItemsBatchCreateCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsBatchCreateCreate
     */
    readonly binderId: string

    /**
     * 
     * @type {Array<BinderItemRequest>}
     * @memberof BindersApiBindersItemsBatchCreateCreate
     */
    readonly binderItemRequest: Array<BinderItemRequest>
}

/**
 * Request parameters for bindersItemsCreate operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsCreateRequest
 */
export interface BindersApiBindersItemsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsCreate
     */
    readonly binderId: string

    /**
     * 
     * @type {BinderItemRequest}
     * @memberof BindersApiBindersItemsCreate
     */
    readonly binderItemRequest: BinderItemRequest
}

/**
 * Request parameters for bindersItemsDestroy operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsDestroyRequest
 */
export interface BindersApiBindersItemsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsDestroy
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsDestroy
     */
    readonly binderItemId: string
}

/**
 * Request parameters for bindersItemsList operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsListRequest
 */
export interface BindersApiBindersItemsListRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsList
     */
    readonly binderId: string

    /**
     * Sort binder items.
     * @type {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'}
     * @memberof BindersApiBindersItemsList
     */
    readonly sorting: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof BindersApiBindersItemsList
     */
    readonly nextKey?: string

    /**
     * Filter Binder Items using Query parameter.
     * @type {string}
     * @memberof BindersApiBindersItemsList
     */
    readonly query?: string

    /**
     * Filter items on tags.
     * @type {Array<string>}
     * @memberof BindersApiBindersItemsList
     */
    readonly tags?: Array<string>
}

/**
 * Request parameters for bindersItemsPartialUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsPartialUpdateRequest
 */
export interface BindersApiBindersItemsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsPartialUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsPartialUpdate
     */
    readonly binderItemId: string

    /**
     * 
     * @type {PatchedBinderItemRequest}
     * @memberof BindersApiBindersItemsPartialUpdate
     */
    readonly patchedBinderItemRequest?: PatchedBinderItemRequest
}

/**
 * Request parameters for bindersItemsRetrieve operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsRetrieveRequest
 */
export interface BindersApiBindersItemsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsRetrieve
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsRetrieve
     */
    readonly binderItemId: string
}

/**
 * Request parameters for bindersItemsUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersItemsUpdateRequest
 */
export interface BindersApiBindersItemsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersItemsUpdate
     */
    readonly binderItemId: string

    /**
     * 
     * @type {BinderItemRequest}
     * @memberof BindersApiBindersItemsUpdate
     */
    readonly binderItemRequest: BinderItemRequest
}

/**
 * Request parameters for bindersList operation in BindersApi.
 * @export
 * @interface BindersApiBindersListRequest
 */
export interface BindersApiBindersListRequest {
    /**
     * Sort binders. \&#39;recently_used\&#39; orders by the requesting user\&#39;s most recent add to the binder, falling back to the binder\&#39;s last-edited time for binders the user hasn\&#39;t added to, name as tie-break.
     * @type {'alphabetical' | 'created_at' | 'recently_used' | 'updated_at'}
     * @memberof BindersApiBindersList
     */
    readonly sorting: 'alphabetical' | 'created_at' | 'recently_used' | 'updated_at'

    /**
     * Sort binders order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof BindersApiBindersList
     */
    readonly sortingOrder: 'ascending' | 'descending'

    /**
     * Article external_id. When given, each result carries contains_article (bool) indicating whether the binder already contains that article. Omitted -&gt; contains_article is null.
     * @type {string}
     * @memberof BindersApiBindersList
     */
    readonly annotateArticleId?: string

    /**
     * ComplianceEntityResult external_id. When given, each result carries contains_compliance_entity_result (bool) indicating whether the binder already contains that compliance entity result. Omitted -&gt; contains_compliance_entity_result is null. Can be combined with annotate_article_id.
     * @type {string}
     * @memberof BindersApiBindersList
     */
    readonly annotateComplianceEntityResultId?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof BindersApiBindersList
     */
    readonly nextKey?: string

    /**
     * Only included binders owned by yourself.
     * @type {boolean}
     * @memberof BindersApiBindersList
     */
    readonly ownedBinders?: boolean

    /**
     * Filter Binders using Query parameter, name search.
     * @type {string}
     * @memberof BindersApiBindersList
     */
    readonly query?: string
}

/**
 * Request parameters for bindersPartialUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersPartialUpdateRequest
 */
export interface BindersApiBindersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersPartialUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {PatchedBinderRequest}
     * @memberof BindersApiBindersPartialUpdate
     */
    readonly patchedBinderRequest?: PatchedBinderRequest
}

/**
 * Request parameters for bindersProfileUserGroupsCreate operation in BindersApi.
 * @export
 * @interface BindersApiBindersProfileUserGroupsCreateRequest
 */
export interface BindersApiBindersProfileUserGroupsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsCreate
     */
    readonly binderId: string

    /**
     * 
     * @type {BinderProfileUserGroupRequest}
     * @memberof BindersApiBindersProfileUserGroupsCreate
     */
    readonly binderProfileUserGroupRequest: BinderProfileUserGroupRequest
}

/**
 * Request parameters for bindersProfileUserGroupsDestroy operation in BindersApi.
 * @export
 * @interface BindersApiBindersProfileUserGroupsDestroyRequest
 */
export interface BindersApiBindersProfileUserGroupsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsDestroy
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsDestroy
     */
    readonly profileUserGroupId: string
}

/**
 * Request parameters for bindersProfileUserGroupsList operation in BindersApi.
 * @export
 * @interface BindersApiBindersProfileUserGroupsListRequest
 */
export interface BindersApiBindersProfileUserGroupsListRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsList
     */
    readonly binderId: string

    /**
     * Sort binder user groups
     * @type {'created_at' | 'updated_at'}
     * @memberof BindersApiBindersProfileUserGroupsList
     */
    readonly sorting: 'created_at' | 'updated_at'

    /**
     * Sort binder user groups order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof BindersApiBindersProfileUserGroupsList
     */
    readonly sortingOrder: 'ascending' | 'descending'

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsList
     */
    readonly nextKey?: string

    /**
     * Filter Binder profile user groups using Query parameter, name search.
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsList
     */
    readonly query?: string
}

/**
 * Request parameters for bindersProfileUserGroupsPartialUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersProfileUserGroupsPartialUpdateRequest
 */
export interface BindersApiBindersProfileUserGroupsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsPartialUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsPartialUpdate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {PatchedBinderProfileUserGroupRequest}
     * @memberof BindersApiBindersProfileUserGroupsPartialUpdate
     */
    readonly patchedBinderProfileUserGroupRequest?: PatchedBinderProfileUserGroupRequest
}

/**
 * Request parameters for bindersProfileUserGroupsRetrieve operation in BindersApi.
 * @export
 * @interface BindersApiBindersProfileUserGroupsRetrieveRequest
 */
export interface BindersApiBindersProfileUserGroupsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsRetrieve
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsRetrieve
     */
    readonly profileUserGroupId: string
}

/**
 * Request parameters for bindersProfileUserGroupsUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersProfileUserGroupsUpdateRequest
 */
export interface BindersApiBindersProfileUserGroupsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersProfileUserGroupsUpdate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {BinderProfileUserGroupRequest}
     * @memberof BindersApiBindersProfileUserGroupsUpdate
     */
    readonly binderProfileUserGroupRequest: BinderProfileUserGroupRequest
}

/**
 * Request parameters for bindersRetrieve operation in BindersApi.
 * @export
 * @interface BindersApiBindersRetrieveRequest
 */
export interface BindersApiBindersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersRetrieve
     */
    readonly binderId: string
}

/**
 * Request parameters for bindersSummaryRetrieve operation in BindersApi.
 * @export
 * @interface BindersApiBindersSummaryRetrieveRequest
 */
export interface BindersApiBindersSummaryRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersSummaryRetrieve
     */
    readonly binderId: string
}

/**
 * Request parameters for bindersTagsList operation in BindersApi.
 * @export
 * @interface BindersApiBindersTagsListRequest
 */
export interface BindersApiBindersTagsListRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersTagsList
     */
    readonly binderId: string

    /**
     * Sort binder tags
     * @type {'binder_item_count' | 'created_at' | 'updated_at'}
     * @memberof BindersApiBindersTagsList
     */
    readonly sorting: 'binder_item_count' | 'created_at' | 'updated_at'

    /**
     * Sort binder tags order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof BindersApiBindersTagsList
     */
    readonly sortingOrder: 'ascending' | 'descending'

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof BindersApiBindersTagsList
     */
    readonly nextKey?: string

    /**
     * Filter Binder tags using Query parameter, name search.
     * @type {string}
     * @memberof BindersApiBindersTagsList
     */
    readonly query?: string
}

/**
 * Request parameters for bindersUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersUpdateRequest
 */
export interface BindersApiBindersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {BinderRequest}
     * @memberof BindersApiBindersUpdate
     */
    readonly binderRequest: BinderRequest
}

/**
 * Request parameters for bindersUsersCreate operation in BindersApi.
 * @export
 * @interface BindersApiBindersUsersCreateRequest
 */
export interface BindersApiBindersUsersCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersCreate
     */
    readonly binderId: string

    /**
     * 
     * @type {BinderUserRequest}
     * @memberof BindersApiBindersUsersCreate
     */
    readonly binderUserRequest: BinderUserRequest
}

/**
 * Request parameters for bindersUsersDestroy operation in BindersApi.
 * @export
 * @interface BindersApiBindersUsersDestroyRequest
 */
export interface BindersApiBindersUsersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersDestroy
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersDestroy
     */
    readonly binderUserId: string
}

/**
 * Request parameters for bindersUsersList operation in BindersApi.
 * @export
 * @interface BindersApiBindersUsersListRequest
 */
export interface BindersApiBindersUsersListRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersList
     */
    readonly binderId: string

    /**
     * Sort binder users
     * @type {'created_at' | 'updated_at'}
     * @memberof BindersApiBindersUsersList
     */
    readonly sorting: 'created_at' | 'updated_at'

    /**
     * Sort binder users order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof BindersApiBindersUsersList
     */
    readonly sortingOrder: 'ascending' | 'descending'

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof BindersApiBindersUsersList
     */
    readonly nextKey?: string

    /**
     * Filter Binder users using Query parameter, name search.
     * @type {string}
     * @memberof BindersApiBindersUsersList
     */
    readonly query?: string
}

/**
 * Request parameters for bindersUsersPartialUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersUsersPartialUpdateRequest
 */
export interface BindersApiBindersUsersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersPartialUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersPartialUpdate
     */
    readonly binderUserId: string

    /**
     * 
     * @type {PatchedBinderUserRequest}
     * @memberof BindersApiBindersUsersPartialUpdate
     */
    readonly patchedBinderUserRequest?: PatchedBinderUserRequest
}

/**
 * Request parameters for bindersUsersRetrieve operation in BindersApi.
 * @export
 * @interface BindersApiBindersUsersRetrieveRequest
 */
export interface BindersApiBindersUsersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersRetrieve
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersRetrieve
     */
    readonly binderUserId: string
}

/**
 * Request parameters for bindersUsersUpdate operation in BindersApi.
 * @export
 * @interface BindersApiBindersUsersUpdateRequest
 */
export interface BindersApiBindersUsersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersUpdate
     */
    readonly binderId: string

    /**
     * 
     * @type {string}
     * @memberof BindersApiBindersUsersUpdate
     */
    readonly binderUserId: string

    /**
     * 
     * @type {BinderUserRequest}
     * @memberof BindersApiBindersUsersUpdate
     */
    readonly binderUserRequest: BinderUserRequest
}

/**
 * BindersApi - object-oriented interface
 * @export
 * @class BindersApi
 * @extends {BaseAPI}
 */
export class BindersApi extends BaseAPI implements BindersApiInterface {
    /**
     * List Or Create Binders.
     * @param {BindersApiBindersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersCreate(requestParameters: BindersApiBindersCreateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersCreate(requestParameters.binderRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder.
     * @param {BindersApiBindersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersDestroy(requestParameters: BindersApiBindersDestroyRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersDestroy(requestParameters.binderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Articles Export View.
     * @param {BindersApiBindersExportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersExportCreate(requestParameters: BindersApiBindersExportCreateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersExportCreate(requestParameters.binderId, requestParameters.dataExportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Binder Item By Article.
     * @param {BindersApiBindersItemsArticlesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsArticlesRetrieve(requestParameters: BindersApiBindersItemsArticlesRetrieveRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsArticlesRetrieve(requestParameters.articleId, requestParameters.binderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create multiple Binder Items in a single request.
     * @param {BindersApiBindersItemsBatchCreateCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsBatchCreateCreate(requestParameters: BindersApiBindersItemsBatchCreateCreateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsBatchCreateCreate(requestParameters.binderId, requestParameters.binderItemRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Or Create Binder Items.
     * @param {BindersApiBindersItemsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsCreate(requestParameters: BindersApiBindersItemsCreateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsCreate(requestParameters.binderId, requestParameters.binderItemRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {BindersApiBindersItemsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsDestroy(requestParameters: BindersApiBindersItemsDestroyRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsDestroy(requestParameters.binderId, requestParameters.binderItemId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List binder Items.
     * @param {BindersApiBindersItemsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsList(requestParameters: BindersApiBindersItemsListRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsList(requestParameters.binderId, requestParameters.sorting, requestParameters.nextKey, requestParameters.query, requestParameters.tags, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {BindersApiBindersItemsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsPartialUpdate(requestParameters: BindersApiBindersItemsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsPartialUpdate(requestParameters.binderId, requestParameters.binderItemId, requestParameters.patchedBinderItemRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {BindersApiBindersItemsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsRetrieve(requestParameters: BindersApiBindersItemsRetrieveRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsRetrieve(requestParameters.binderId, requestParameters.binderItemId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder Item.
     * @param {BindersApiBindersItemsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersItemsUpdate(requestParameters: BindersApiBindersItemsUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersItemsUpdate(requestParameters.binderId, requestParameters.binderItemId, requestParameters.binderItemRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List binders.
     * @param {BindersApiBindersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersList(requestParameters: BindersApiBindersListRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersList(requestParameters.sorting, requestParameters.sortingOrder, requestParameters.annotateArticleId, requestParameters.annotateComplianceEntityResultId, requestParameters.nextKey, requestParameters.ownedBinders, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder.
     * @param {BindersApiBindersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersPartialUpdate(requestParameters: BindersApiBindersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersPartialUpdate(requestParameters.binderId, requestParameters.patchedBinderRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Or Create Binder Profile User Groups.
     * @param {BindersApiBindersProfileUserGroupsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersProfileUserGroupsCreate(requestParameters: BindersApiBindersProfileUserGroupsCreateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersProfileUserGroupsCreate(requestParameters.binderId, requestParameters.binderProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {BindersApiBindersProfileUserGroupsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersProfileUserGroupsDestroy(requestParameters: BindersApiBindersProfileUserGroupsDestroyRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersProfileUserGroupsDestroy(requestParameters.binderId, requestParameters.profileUserGroupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List binders.
     * @param {BindersApiBindersProfileUserGroupsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersProfileUserGroupsList(requestParameters: BindersApiBindersProfileUserGroupsListRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersProfileUserGroupsList(requestParameters.binderId, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {BindersApiBindersProfileUserGroupsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersProfileUserGroupsPartialUpdate(requestParameters: BindersApiBindersProfileUserGroupsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersProfileUserGroupsPartialUpdate(requestParameters.binderId, requestParameters.profileUserGroupId, requestParameters.patchedBinderProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {BindersApiBindersProfileUserGroupsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersProfileUserGroupsRetrieve(requestParameters: BindersApiBindersProfileUserGroupsRetrieveRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersProfileUserGroupsRetrieve(requestParameters.binderId, requestParameters.profileUserGroupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User Group.
     * @param {BindersApiBindersProfileUserGroupsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersProfileUserGroupsUpdate(requestParameters: BindersApiBindersProfileUserGroupsUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersProfileUserGroupsUpdate(requestParameters.binderId, requestParameters.profileUserGroupId, requestParameters.binderProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder.
     * @param {BindersApiBindersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersRetrieve(requestParameters: BindersApiBindersRetrieveRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersRetrieve(requestParameters.binderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve Binder Summary View.
     * @param {BindersApiBindersSummaryRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersSummaryRetrieve(requestParameters: BindersApiBindersSummaryRetrieveRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersSummaryRetrieve(requestParameters.binderId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List binder tags.
     * @param {BindersApiBindersTagsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersTagsList(requestParameters: BindersApiBindersTagsListRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersTagsList(requestParameters.binderId, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder.
     * @param {BindersApiBindersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUpdate(requestParameters: BindersApiBindersUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUpdate(requestParameters.binderId, requestParameters.binderRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Or Create Binder Users.
     * @param {BindersApiBindersUsersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUsersCreate(requestParameters: BindersApiBindersUsersCreateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUsersCreate(requestParameters.binderId, requestParameters.binderUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User.
     * @param {BindersApiBindersUsersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUsersDestroy(requestParameters: BindersApiBindersUsersDestroyRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUsersDestroy(requestParameters.binderId, requestParameters.binderUserId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List binders.
     * @param {BindersApiBindersUsersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUsersList(requestParameters: BindersApiBindersUsersListRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUsersList(requestParameters.binderId, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User.
     * @param {BindersApiBindersUsersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUsersPartialUpdate(requestParameters: BindersApiBindersUsersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUsersPartialUpdate(requestParameters.binderId, requestParameters.binderUserId, requestParameters.patchedBinderUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User.
     * @param {BindersApiBindersUsersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUsersRetrieve(requestParameters: BindersApiBindersUsersRetrieveRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUsersRetrieve(requestParameters.binderId, requestParameters.binderUserId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Binder User.
     * @param {BindersApiBindersUsersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof BindersApi
     */
    public bindersUsersUpdate(requestParameters: BindersApiBindersUsersUpdateRequest, options?: AxiosRequestConfig) {
        return BindersApiFp(this.configuration).bindersUsersUpdate(requestParameters.binderId, requestParameters.binderUserId, requestParameters.binderUserRequest, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * CompaniesApi - axios parameter creator
 * @export
 */
export const CompaniesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new UBO investigation.
         * @param {string} companyId 
         * @param {CorporateLinkageRegistrationRequest} corporateLinkageRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesCorporateLinkageRegistrationCreate: async (companyId: string, corporateLinkageRegistrationRequest: CorporateLinkageRegistrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('companiesCorporateLinkageRegistrationCreate', 'companyId', companyId)
            // verify required parameter 'corporateLinkageRegistrationRequest' is not null or undefined
            assertParamExists('companiesCorporateLinkageRegistrationCreate', 'corporateLinkageRegistrationRequest', corporateLinkageRegistrationRequest)
            const localVarPath = `/companies/{company_id}/corporate_linkage_registration/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(corporateLinkageRegistrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Search Company Directors using Postgresql.
         * @param {string} companyId Unique UUID number identifying this company.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Filter directors based on name or part of full name.
         * @param {'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER'} [roleGroup] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesDirectorsList: async (companyId: string, nextKey?: string, pageSize?: number, query?: string, roleGroup?: 'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('companiesDirectorsList', 'companyId', companyId)
            const localVarPath = `/companies/{company_id}/directors/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (roleGroup !== undefined) {
                localVarQueryParameter['role_group'] = roleGroup;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Advanced Director Search.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesDirectorsSearchCreate: async (companyId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('companiesDirectorsSearchCreate', 'companyId', companyId)
            const localVarPath = `/companies/{company_id}/directors/search/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Companies Export View.
         * @param {CompanyExportRequest} companyExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesExportCreate: async (companyExportRequest: CompanyExportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyExportRequest' is not null or undefined
            assertParamExists('companiesExportCreate', 'companyExportRequest', companyExportRequest)
            const localVarPath = `/companies/export/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(companyExportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List industry codes, optionally filtered by ``query``.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter on code, description, or division group name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesIndustryCodesList: async (nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/companies/industry_codes/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Search all companies in Business Radar using Elasticsearch.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {boolean} [includeAnnotations] Include annotations for response
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesList: async (company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/companies/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (includeAnnotations !== undefined) {
                localVarQueryParameter['include_annotations'] = includeAnnotations;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Register new Company, this is not related to an portfolio. Used for screening companies. Costs Credits.
         * @param {RegistrationRequest} [registrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesRegister: async (registrationRequest?: RegistrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/companies/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(registrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Registrations.
         * @param {string} registrationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesRegistrationsRetrieve: async (registrationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'registrationId' is not null or undefined
            assertParamExists('companiesRegistrationsRetrieve', 'registrationId', registrationId)
            const localVarPath = `/companies/registrations/{registration_id}/`
                .replace(`{${"registration_id"}}`, encodeURIComponent(String(registrationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Send Company Report.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesReportCreate: async (companyId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('companiesReportCreate', 'companyId', companyId)
            const localVarPath = `/companies/{company_id}/report/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Company Details.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesRetrieve: async (companyId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('companiesRetrieve', 'companyId', companyId)
            const localVarPath = `/companies/{company_id}/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new UBO investigation.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesUboInvestigationCreate: async (companyId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('companiesUboInvestigationCreate', 'companyId', companyId)
            const localVarPath = `/companies/{company_id}/ubo_investigation/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Search all companies using Dun and Bradstreet.  Companies will contain an optional external_id, which is null if company is not in Business Radar.  When you pass query and optional country it will search using dun and bradstreet, otherwise using internal search.  Annotations are not available (empty) when searching using d&b.
         * @param {string} [annotatePortfolioId] Portfolio external_id. When given, each result carries in_portfolio (bool) indicating whether the company is already in that portfolio. Requires view access to the portfolio. Omitted -&gt; in_portfolio is null.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {boolean} [includeAnnotations] Include annotations for response
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesUniversalSearchList: async (annotatePortfolioId?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/companies/universal_search/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (annotatePortfolioId !== undefined) {
                localVarQueryParameter['annotate_portfolio_id'] = annotatePortfolioId;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (includeAnnotations !== undefined) {
                localVarQueryParameter['include_annotations'] = includeAnnotations;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (lei !== undefined) {
                localVarQueryParameter['lei'] = lei;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (slug !== undefined) {
                localVarQueryParameter['slug'] = slug;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CompaniesApi - functional programming interface
 * @export
 */
export const CompaniesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CompaniesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new UBO investigation.
         * @param {string} companyId 
         * @param {CorporateLinkageRegistrationRequest} corporateLinkageRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesCorporateLinkageRegistrationCreate(companyId: string, corporateLinkageRegistrationRequest: CorporateLinkageRegistrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CorporateLinkageRegistration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesCorporateLinkageRegistrationCreate(companyId, corporateLinkageRegistrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Search Company Directors using Postgresql.
         * @param {string} companyId Unique UUID number identifying this company.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Filter directors based on name or part of full name.
         * @param {'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER'} [roleGroup] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesDirectorsList(companyId: string, nextKey?: string, pageSize?: number, query?: string, roleGroup?: 'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedDirectorList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesDirectorsList(companyId, nextKey, pageSize, query, roleGroup, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Advanced Director Search.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesDirectorsSearchCreate(companyId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesDirectorsSearchCreate(companyId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Companies Export View.
         * @param {CompanyExportRequest} companyExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesExportCreate(companyExportRequest: CompanyExportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesExportCreate(companyExportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List industry codes, optionally filtered by ``query``.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter on code, description, or division group name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesIndustryCodesList(nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIndustryCodeList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesIndustryCodesList(nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Search all companies in Business Radar using Elasticsearch.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {boolean} [includeAnnotations] Include annotations for response
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedCompanyList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesList(company, country, dunsNumber, excludeCompany, includeAnnotations, industry, lei, nextKey, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Register new Company, this is not related to an portfolio. Used for screening companies. Costs Credits.
         * @param {RegistrationRequest} [registrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesRegister(registrationRequest?: RegistrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Registration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesRegister(registrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Registrations.
         * @param {string} registrationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesRegistrationsRetrieve(registrationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Registration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesRegistrationsRetrieve(registrationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Send Company Report.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesReportCreate(companyId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesReportCreate(companyId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Company Details.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesRetrieve(companyId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesRetrieve(companyId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a new UBO investigation.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesUboInvestigationCreate(companyId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UBOInvestigation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesUboInvestigationCreate(companyId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Search all companies using Dun and Bradstreet.  Companies will contain an optional external_id, which is null if company is not in Business Radar.  When you pass query and optional country it will search using dun and bradstreet, otherwise using internal search.  Annotations are not available (empty) when searching using d&b.
         * @param {string} [annotatePortfolioId] Portfolio external_id. When given, each result carries in_portfolio (bool) indicating whether the company is already in that portfolio. Requires view access to the portfolio. Omitted -&gt; in_portfolio is null.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {boolean} [includeAnnotations] Include annotations for response
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async companiesUniversalSearchList(annotatePortfolioId?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedUniversalCompanyList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.companiesUniversalSearchList(annotatePortfolioId, company, country, dunsNumber, excludeCompany, includeAnnotations, industry, lei, nextKey, portfolioId, query, registrationNumber, slug, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * CompaniesApi - factory interface
 * @export
 */
export const CompaniesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CompaniesApiFp(configuration)
    return {
        /**
         * Create a new UBO investigation.
         * @param {string} companyId 
         * @param {CorporateLinkageRegistrationRequest} corporateLinkageRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesCorporateLinkageRegistrationCreate(companyId: string, corporateLinkageRegistrationRequest: CorporateLinkageRegistrationRequest, options?: any): AxiosPromise<CorporateLinkageRegistration> {
            return localVarFp.companiesCorporateLinkageRegistrationCreate(companyId, corporateLinkageRegistrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Search Company Directors using Postgresql.
         * @param {string} companyId Unique UUID number identifying this company.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Filter directors based on name or part of full name.
         * @param {'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER'} [roleGroup] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesDirectorsList(companyId: string, nextKey?: string, pageSize?: number, query?: string, roleGroup?: 'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER', options?: any): AxiosPromise<PaginatedDirectorList> {
            return localVarFp.companiesDirectorsList(companyId, nextKey, pageSize, query, roleGroup, options).then((request) => request(axios, basePath));
        },
        /**
         * Advanced Director Search.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesDirectorsSearchCreate(companyId: string, options?: any): AxiosPromise<void> {
            return localVarFp.companiesDirectorsSearchCreate(companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * Companies Export View.
         * @param {CompanyExportRequest} companyExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesExportCreate(companyExportRequest: CompanyExportRequest, options?: any): AxiosPromise<CompanyExport> {
            return localVarFp.companiesExportCreate(companyExportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List industry codes, optionally filtered by ``query``.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter on code, description, or division group name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesIndustryCodesList(nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedIndustryCodeList> {
            return localVarFp.companiesIndustryCodesList(nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Search all companies in Business Radar using Elasticsearch.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {boolean} [includeAnnotations] Include annotations for response
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<PaginatedCompanyList> {
            return localVarFp.companiesList(company, country, dunsNumber, excludeCompany, includeAnnotations, industry, lei, nextKey, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
        /**
         * Register new Company, this is not related to an portfolio. Used for screening companies. Costs Credits.
         * @param {RegistrationRequest} [registrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesRegister(registrationRequest?: RegistrationRequest, options?: any): AxiosPromise<Registration> {
            return localVarFp.companiesRegister(registrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List Registrations.
         * @param {string} registrationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesRegistrationsRetrieve(registrationId: string, options?: any): AxiosPromise<Registration> {
            return localVarFp.companiesRegistrationsRetrieve(registrationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Send Company Report.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesReportCreate(companyId: string, options?: any): AxiosPromise<void> {
            return localVarFp.companiesReportCreate(companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Company Details.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesRetrieve(companyId: string, options?: any): AxiosPromise<CompanyDetail> {
            return localVarFp.companiesRetrieve(companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new UBO investigation.
         * @param {string} companyId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesUboInvestigationCreate(companyId: string, options?: any): AxiosPromise<UBOInvestigation> {
            return localVarFp.companiesUboInvestigationCreate(companyId, options).then((request) => request(axios, basePath));
        },
        /**
         * Search all companies using Dun and Bradstreet.  Companies will contain an optional external_id, which is null if company is not in Business Radar.  When you pass query and optional country it will search using dun and bradstreet, otherwise using internal search.  Annotations are not available (empty) when searching using d&b.
         * @param {string} [annotatePortfolioId] Portfolio external_id. When given, each result carries in_portfolio (bool) indicating whether the company is already in that portfolio. Requires view access to the portfolio. Omitted -&gt; in_portfolio is null.
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {boolean} [includeAnnotations] Include annotations for response
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
         * @param {string} [query] Custom search query to text search all companies.
         * @param {Array<string>} [registrationNumber] Local Registration Number
         * @param {string} [slug] Search Companies By Slug.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        companiesUniversalSearchList(annotatePortfolioId?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: any): AxiosPromise<PaginatedUniversalCompanyList> {
            return localVarFp.companiesUniversalSearchList(annotatePortfolioId, company, country, dunsNumber, excludeCompany, includeAnnotations, industry, lei, nextKey, portfolioId, query, registrationNumber, slug, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CompaniesApi - interface
 * @export
 * @interface CompaniesApi
 */
export interface CompaniesApiInterface {
    /**
     * Create a new UBO investigation.
     * @param {string} companyId 
     * @param {CorporateLinkageRegistrationRequest} corporateLinkageRegistrationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesCorporateLinkageRegistrationCreate(companyId: string, corporateLinkageRegistrationRequest: CorporateLinkageRegistrationRequest, options?: AxiosRequestConfig): AxiosPromise<CorporateLinkageRegistration>;

    /**
     * Search Company Directors using Postgresql.
     * @param {string} companyId Unique UUID number identifying this company.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
     * @param {string} [query] Filter directors based on name or part of full name.
     * @param {'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER'} [roleGroup] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesDirectorsList(companyId: string, nextKey?: string, pageSize?: number, query?: string, roleGroup?: 'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER', options?: AxiosRequestConfig): AxiosPromise<PaginatedDirectorList>;

    /**
     * Advanced Director Search.
     * @param {string} companyId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesDirectorsSearchCreate(companyId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Companies Export View.
     * @param {CompanyExportRequest} companyExportRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesExportCreate(companyExportRequest: CompanyExportRequest, options?: AxiosRequestConfig): AxiosPromise<CompanyExport>;

    /**
     * List industry codes, optionally filtered by ``query``.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter on code, description, or division group name.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesIndustryCodesList(nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedIndustryCodeList>;

    /**
     * Search all companies in Business Radar using Elasticsearch.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {boolean} [includeAnnotations] Include annotations for response
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesList(company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedCompanyList>;

    /**
     * Register new Company, this is not related to an portfolio. Used for screening companies. Costs Credits.
     * @param {RegistrationRequest} [registrationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesRegister(registrationRequest?: RegistrationRequest, options?: AxiosRequestConfig): AxiosPromise<Registration>;

    /**
     * List Registrations.
     * @param {string} registrationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesRegistrationsRetrieve(registrationId: string, options?: AxiosRequestConfig): AxiosPromise<Registration>;

    /**
     * Send Company Report.
     * @param {string} companyId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesReportCreate(companyId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get Company Details.
     * @param {string} companyId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesRetrieve(companyId: string, options?: AxiosRequestConfig): AxiosPromise<CompanyDetail>;

    /**
     * Create a new UBO investigation.
     * @param {string} companyId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesUboInvestigationCreate(companyId: string, options?: AxiosRequestConfig): AxiosPromise<UBOInvestigation>;

    /**
     * Search all companies using Dun and Bradstreet.  Companies will contain an optional external_id, which is null if company is not in Business Radar.  When you pass query and optional country it will search using dun and bradstreet, otherwise using internal search.  Annotations are not available (empty) when searching using d&b.
     * @param {string} [annotatePortfolioId] Portfolio external_id. When given, each result carries in_portfolio (bool) indicating whether the company is already in that portfolio. Requires view access to the portfolio. Omitted -&gt; in_portfolio is null.
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {boolean} [includeAnnotations] Include annotations for response
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {string} [lei] 20-character Legal Entity Identifier (ISO 17442)
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {Array<string>} [portfolioId] Portfolio ID to filter companies
     * @param {string} [query] Custom search query to text search all companies.
     * @param {Array<string>} [registrationNumber] Local Registration Number
     * @param {string} [slug] Search Companies By Slug.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApiInterface
     */
    companiesUniversalSearchList(annotatePortfolioId?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCompany?: Array<string>, includeAnnotations?: boolean, industry?: Array<string>, lei?: string, nextKey?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, slug?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedUniversalCompanyList>;

}

/**
 * Request parameters for companiesCorporateLinkageRegistrationCreate operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesCorporateLinkageRegistrationCreateRequest
 */
export interface CompaniesApiCompaniesCorporateLinkageRegistrationCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof CompaniesApiCompaniesCorporateLinkageRegistrationCreate
     */
    readonly companyId: string

    /**
     * 
     * @type {CorporateLinkageRegistrationRequest}
     * @memberof CompaniesApiCompaniesCorporateLinkageRegistrationCreate
     */
    readonly corporateLinkageRegistrationRequest: CorporateLinkageRegistrationRequest
}

/**
 * Request parameters for companiesDirectorsList operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesDirectorsListRequest
 */
export interface CompaniesApiCompaniesDirectorsListRequest {
    /**
     * Unique UUID number identifying this company.
     * @type {string}
     * @memberof CompaniesApiCompaniesDirectorsList
     */
    readonly companyId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof CompaniesApiCompaniesDirectorsList
     */
    readonly nextKey?: string

    /**
     * Number of results per page. Defaults to 50, capped at 200.
     * @type {number}
     * @memberof CompaniesApiCompaniesDirectorsList
     */
    readonly pageSize?: number

    /**
     * Filter directors based on name or part of full name.
     * @type {string}
     * @memberof CompaniesApiCompaniesDirectorsList
     */
    readonly query?: string

    /**
     * 
     * @type {'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER'}
     * @memberof CompaniesApiCompaniesDirectorsList
     */
    readonly roleGroup?: 'BOARD' | 'MANAGEMENT' | 'OFFICER' | 'OTHER'
}

/**
 * Request parameters for companiesDirectorsSearchCreate operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesDirectorsSearchCreateRequest
 */
export interface CompaniesApiCompaniesDirectorsSearchCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof CompaniesApiCompaniesDirectorsSearchCreate
     */
    readonly companyId: string
}

/**
 * Request parameters for companiesExportCreate operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesExportCreateRequest
 */
export interface CompaniesApiCompaniesExportCreateRequest {
    /**
     * 
     * @type {CompanyExportRequest}
     * @memberof CompaniesApiCompaniesExportCreate
     */
    readonly companyExportRequest: CompanyExportRequest
}

/**
 * Request parameters for companiesIndustryCodesList operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesIndustryCodesListRequest
 */
export interface CompaniesApiCompaniesIndustryCodesListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof CompaniesApiCompaniesIndustryCodesList
     */
    readonly nextKey?: string

    /**
     * Filter on code, description, or division group name.
     * @type {string}
     * @memberof CompaniesApiCompaniesIndustryCodesList
     */
    readonly query?: string
}

/**
 * Request parameters for companiesList operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesListRequest
 */
export interface CompaniesApiCompaniesListRequest {
    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * Include annotations for response
     * @type {boolean}
     * @memberof CompaniesApiCompaniesList
     */
    readonly includeAnnotations?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof CompaniesApiCompaniesList
     */
    readonly lei?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof CompaniesApiCompaniesList
     */
    readonly nextKey?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof CompaniesApiCompaniesList
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof CompaniesApiCompaniesList
     */
    readonly slug?: string
}

/**
 * Request parameters for companiesRegister operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesRegisterRequest
 */
export interface CompaniesApiCompaniesRegisterRequest {
    /**
     * 
     * @type {RegistrationRequest}
     * @memberof CompaniesApiCompaniesRegister
     */
    readonly registrationRequest?: RegistrationRequest
}

/**
 * Request parameters for companiesRegistrationsRetrieve operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesRegistrationsRetrieveRequest
 */
export interface CompaniesApiCompaniesRegistrationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof CompaniesApiCompaniesRegistrationsRetrieve
     */
    readonly registrationId: string
}

/**
 * Request parameters for companiesReportCreate operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesReportCreateRequest
 */
export interface CompaniesApiCompaniesReportCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof CompaniesApiCompaniesReportCreate
     */
    readonly companyId: string
}

/**
 * Request parameters for companiesRetrieve operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesRetrieveRequest
 */
export interface CompaniesApiCompaniesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof CompaniesApiCompaniesRetrieve
     */
    readonly companyId: string
}

/**
 * Request parameters for companiesUboInvestigationCreate operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesUboInvestigationCreateRequest
 */
export interface CompaniesApiCompaniesUboInvestigationCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof CompaniesApiCompaniesUboInvestigationCreate
     */
    readonly companyId: string
}

/**
 * Request parameters for companiesUniversalSearchList operation in CompaniesApi.
 * @export
 * @interface CompaniesApiCompaniesUniversalSearchListRequest
 */
export interface CompaniesApiCompaniesUniversalSearchListRequest {
    /**
     * Portfolio external_id. When given, each result carries in_portfolio (bool) indicating whether the company is already in that portfolio. Requires view access to the portfolio. Omitted -&gt; in_portfolio is null.
     * @type {string}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly annotatePortfolioId?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly excludeCompany?: Array<string>

    /**
     * Include annotations for response
     * @type {boolean}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly includeAnnotations?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly industry?: Array<string>

    /**
     * 20-character Legal Entity Identifier (ISO 17442)
     * @type {string}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly lei?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly nextKey?: string

    /**
     * Portfolio ID to filter companies
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search query to text search all companies.
     * @type {string}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly query?: string

    /**
     * Local Registration Number
     * @type {Array<string>}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Search Companies By Slug.
     * @type {string}
     * @memberof CompaniesApiCompaniesUniversalSearchList
     */
    readonly slug?: string
}

/**
 * CompaniesApi - object-oriented interface
 * @export
 * @class CompaniesApi
 * @extends {BaseAPI}
 */
export class CompaniesApi extends BaseAPI implements CompaniesApiInterface {
    /**
     * Create a new UBO investigation.
     * @param {CompaniesApiCompaniesCorporateLinkageRegistrationCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesCorporateLinkageRegistrationCreate(requestParameters: CompaniesApiCompaniesCorporateLinkageRegistrationCreateRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesCorporateLinkageRegistrationCreate(requestParameters.companyId, requestParameters.corporateLinkageRegistrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Search Company Directors using Postgresql.
     * @param {CompaniesApiCompaniesDirectorsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesDirectorsList(requestParameters: CompaniesApiCompaniesDirectorsListRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesDirectorsList(requestParameters.companyId, requestParameters.nextKey, requestParameters.pageSize, requestParameters.query, requestParameters.roleGroup, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Advanced Director Search.
     * @param {CompaniesApiCompaniesDirectorsSearchCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesDirectorsSearchCreate(requestParameters: CompaniesApiCompaniesDirectorsSearchCreateRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesDirectorsSearchCreate(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Companies Export View.
     * @param {CompaniesApiCompaniesExportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesExportCreate(requestParameters: CompaniesApiCompaniesExportCreateRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesExportCreate(requestParameters.companyExportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List industry codes, optionally filtered by ``query``.
     * @param {CompaniesApiCompaniesIndustryCodesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesIndustryCodesList(requestParameters: CompaniesApiCompaniesIndustryCodesListRequest = {}, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesIndustryCodesList(requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Search all companies in Business Radar using Elasticsearch.
     * @param {CompaniesApiCompaniesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesList(requestParameters: CompaniesApiCompaniesListRequest = {}, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesList(requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.includeAnnotations, requestParameters.industry, requestParameters.lei, requestParameters.nextKey, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Register new Company, this is not related to an portfolio. Used for screening companies. Costs Credits.
     * @param {CompaniesApiCompaniesRegisterRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesRegister(requestParameters: CompaniesApiCompaniesRegisterRequest = {}, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesRegister(requestParameters.registrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Registrations.
     * @param {CompaniesApiCompaniesRegistrationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesRegistrationsRetrieve(requestParameters: CompaniesApiCompaniesRegistrationsRetrieveRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesRegistrationsRetrieve(requestParameters.registrationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Send Company Report.
     * @param {CompaniesApiCompaniesReportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesReportCreate(requestParameters: CompaniesApiCompaniesReportCreateRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesReportCreate(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Company Details.
     * @param {CompaniesApiCompaniesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesRetrieve(requestParameters: CompaniesApiCompaniesRetrieveRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesRetrieve(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new UBO investigation.
     * @param {CompaniesApiCompaniesUboInvestigationCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesUboInvestigationCreate(requestParameters: CompaniesApiCompaniesUboInvestigationCreateRequest, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesUboInvestigationCreate(requestParameters.companyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Search all companies using Dun and Bradstreet.  Companies will contain an optional external_id, which is null if company is not in Business Radar.  When you pass query and optional country it will search using dun and bradstreet, otherwise using internal search.  Annotations are not available (empty) when searching using d&b.
     * @param {CompaniesApiCompaniesUniversalSearchListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CompaniesApi
     */
    public companiesUniversalSearchList(requestParameters: CompaniesApiCompaniesUniversalSearchListRequest = {}, options?: AxiosRequestConfig) {
        return CompaniesApiFp(this.configuration).companiesUniversalSearchList(requestParameters.annotatePortfolioId, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCompany, requestParameters.includeAnnotations, requestParameters.industry, requestParameters.lei, requestParameters.nextKey, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.slug, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * ComplianceApi - axios parameter creator
 * @export
 */
export const ComplianceApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Add an add/exclude keyword entry to the active profile\'s set.
         * @param {KeywordSetEntryCreateRequest} keywordSetEntryCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsCreate: async (keywordSetEntryCreateRequest: KeywordSetEntryCreateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'keywordSetEntryCreateRequest' is not null or undefined
            assertParamExists('complianceAdverseMediaKeywordsCreate', 'keywordSetEntryCreateRequest', keywordSetEntryCreateRequest)
            const localVarPath = `/compliance/adverse_media/keywords/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(keywordSetEntryCreateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete one add/exclude entry from the active profile\'s keyword set.
         * @param {string} entryId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsDestroy: async (entryId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'entryId' is not null or undefined
            assertParamExists('complianceAdverseMediaKeywordsDestroy', 'entryId', entryId)
            const localVarPath = `/compliance/adverse_media/keywords/{entry_id}/`
                .replace(`{${"entry_id"}}`, encodeURIComponent(String(entryId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete every entry in the active profile\'s keyword set.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsResetCreate: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/adverse_media/keywords/reset/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Return the effective keyword list for a language plus per-language counts.  ``language`` is required: the full list is too large to return unfiltered, so the frontend fetches one language and uses ``language_counts`` for the rest.
         * @param {string} language 
         * @param {string} [entityType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsRetrieve: async (language: string, entityType?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'language' is not null or undefined
            assertParamExists('complianceAdverseMediaKeywordsRetrieve', 'language', language)
            const localVarPath = `/compliance/adverse_media/keywords/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (entityType !== undefined) {
                localVarQueryParameter['entity_type'] = entityType;
            }

            if (language !== undefined) {
                localVarQueryParameter['language'] = language;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new batch compliance check.  Supports either a list of company external_ids or a portfolio_id to derive companies. No processing/sending logic is executed here; this only creates the batch record.
         * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesCreate: async (batchComplianceCheckRequest?: BatchComplianceCheckRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/batches/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(batchComplianceCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {PatchedBatchComplianceCheckRequest} [patchedBatchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesPartialUpdate: async (batchComplianceCheckId: string, patchedBatchComplianceCheckRequest?: PatchedBatchComplianceCheckRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchComplianceCheckId' is not null or undefined
            assertParamExists('complianceBatchesPartialUpdate', 'batchComplianceCheckId', batchComplianceCheckId)
            const localVarPath = `/compliance/batches/{batch_compliance_check_id}/`
                .replace(`{${"batch_compliance_check_id"}}`, encodeURIComponent(String(batchComplianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedBatchComplianceCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesRetrieve: async (batchComplianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchComplianceCheckId' is not null or undefined
            assertParamExists('complianceBatchesRetrieve', 'batchComplianceCheckId', batchComplianceCheckId)
            const localVarPath = `/compliance/batches/{batch_compliance_check_id}/`
                .replace(`{${"batch_compliance_check_id"}}`, encodeURIComponent(String(batchComplianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Start a compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesStartCreate: async (batchComplianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchComplianceCheckId' is not null or undefined
            assertParamExists('complianceBatchesStartCreate', 'batchComplianceCheckId', batchComplianceCheckId)
            const localVarPath = `/compliance/batches/{batch_compliance_check_id}/start/`
                .replace(`{${"batch_compliance_check_id"}}`, encodeURIComponent(String(batchComplianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesUpdate: async (batchComplianceCheckId: string, batchComplianceCheckRequest?: BatchComplianceCheckRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchComplianceCheckId' is not null or undefined
            assertParamExists('complianceBatchesUpdate', 'batchComplianceCheckId', batchComplianceCheckId)
            const localVarPath = `/compliance/batches/{batch_compliance_check_id}/`
                .replace(`{${"batch_compliance_check_id"}}`, encodeURIComponent(String(batchComplianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(batchComplianceCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new compliance check.
         * @param {ComplianceCheckRequestRequest} [complianceCheckRequestRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceCreate: async (complianceCheckRequestRequest?: ComplianceCheckRequestRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceCheckRequestRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Return enforcement providers with their live listing stats.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEnforcementProvidersList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/enforcement/providers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesCreate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesCreate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesCreate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityAliasRequest' is not null or undefined
            assertParamExists('complianceEntitiesAliasesCreate', 'complianceEntityAliasRequest', complianceEntityAliasRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/aliases/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityAliasRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesDestroy: async (complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesDestroy', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityAliasId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesDestroy', 'complianceEntityAliasId', complianceEntityAliasId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesDestroy', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/aliases/{compliance_entity_alias_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_alias_id"}}`, encodeURIComponent(String(complianceEntityAliasId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesList: async (complianceCheckId: string, complianceEntityId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesList', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesList', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/aliases/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {PatchedComplianceEntityAliasRequest} [patchedComplianceEntityAliasRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesPartialUpdate: async (complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, patchedComplianceEntityAliasRequest?: PatchedComplianceEntityAliasRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesPartialUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityAliasId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesPartialUpdate', 'complianceEntityAliasId', complianceEntityAliasId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesPartialUpdate', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/aliases/{compliance_entity_alias_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_alias_id"}}`, encodeURIComponent(String(complianceEntityAliasId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityAliasRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesRetrieve: async (complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesRetrieve', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityAliasId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesRetrieve', 'complianceEntityAliasId', complianceEntityAliasId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesRetrieve', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/aliases/{compliance_entity_alias_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_alias_id"}}`, encodeURIComponent(String(complianceEntityAliasId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesUpdate: async (complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityAliasId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesUpdate', 'complianceEntityAliasId', complianceEntityAliasId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesAliasesUpdate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityAliasRequest' is not null or undefined
            assertParamExists('complianceEntitiesAliasesUpdate', 'complianceEntityAliasRequest', complianceEntityAliasRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/aliases/{compliance_entity_alias_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_alias_id"}}`, encodeURIComponent(String(complianceEntityAliasId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityAliasRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Batch update compliance entities.
         * @param {string} complianceCheckId 
         * @param {Array<ComplianceEntityBatchUpdateRequest>} complianceEntityBatchUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesBatchUpdatePartialUpdate: async (complianceCheckId: string, complianceEntityBatchUpdateRequest: Array<ComplianceEntityBatchUpdateRequest>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesBatchUpdatePartialUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityBatchUpdateRequest' is not null or undefined
            assertParamExists('complianceEntitiesBatchUpdatePartialUpdate', 'complianceEntityBatchUpdateRequest', complianceEntityBatchUpdateRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/batch_update/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityBatchUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve all compliance entities associated with a compliance check.
         * @param {string} complianceCheckId 
         * @param {ComplianceEntityRequest} complianceEntityRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesCreate: async (complianceCheckId: string, complianceEntityRequest: ComplianceEntityRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesCreate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityRequest' is not null or undefined
            assertParamExists('complianceEntitiesCreate', 'complianceEntityRequest', complianceEntityRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesDestroy: async (complianceCheckId: string, complianceEntityId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesDestroy', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesDestroy', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersCreate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersCreate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersCreate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityIdentifierRequest' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersCreate', 'complianceEntityIdentifierRequest', complianceEntityIdentifierRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/identifiers/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityIdentifierRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersDestroy: async (complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersDestroy', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersDestroy', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityIdentifierId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersDestroy', 'complianceEntityIdentifierId', complianceEntityIdentifierId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/identifiers/{compliance_entity_identifier_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_identifier_id"}}`, encodeURIComponent(String(complianceEntityIdentifierId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersList: async (complianceCheckId: string, complianceEntityId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersList', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersList', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/identifiers/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {PatchedComplianceEntityIdentifierRequest} [patchedComplianceEntityIdentifierRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersPartialUpdate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, patchedComplianceEntityIdentifierRequest?: PatchedComplianceEntityIdentifierRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersPartialUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersPartialUpdate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityIdentifierId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersPartialUpdate', 'complianceEntityIdentifierId', complianceEntityIdentifierId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/identifiers/{compliance_entity_identifier_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_identifier_id"}}`, encodeURIComponent(String(complianceEntityIdentifierId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityIdentifierRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersRetrieve: async (complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersRetrieve', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersRetrieve', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityIdentifierId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersRetrieve', 'complianceEntityIdentifierId', complianceEntityIdentifierId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/identifiers/{compliance_entity_identifier_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_identifier_id"}}`, encodeURIComponent(String(complianceEntityIdentifierId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersUpdate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersUpdate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityIdentifierId' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersUpdate', 'complianceEntityIdentifierId', complianceEntityIdentifierId)
            // verify required parameter 'complianceEntityIdentifierRequest' is not null or undefined
            assertParamExists('complianceEntitiesIdentifiersUpdate', 'complianceEntityIdentifierRequest', complianceEntityIdentifierRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/identifiers/{compliance_entity_identifier_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_identifier_id"}}`, encodeURIComponent(String(complianceEntityIdentifierId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityIdentifierRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve all compliance entities associated with a compliance check.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesList: async (complianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesList', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {PatchedComplianceEntityRequest} [patchedComplianceEntityRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesPartialUpdate: async (complianceCheckId: string, complianceEntityId: string, patchedComplianceEntityRequest?: PatchedComplianceEntityRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesPartialUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesPartialUpdate', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsCreate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesResultsCreate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesResultsCreate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityResultSelectAllBinderRequest' is not null or undefined
            assertParamExists('complianceEntitiesResultsCreate', 'complianceEntityResultSelectAllBinderRequest', complianceEntityResultSelectAllBinderRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/results/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (hasBinder !== undefined) {
                localVarQueryParameter['has_binder'] = hasBinder;
            }

            if (hasComment !== undefined) {
                localVarQueryParameter['has_comment'] = hasComment;
            }

            if (includeAutomatedFalsePositives !== undefined) {
                localVarQueryParameter['include_automated_false_positives'] = includeAutomatedFalsePositives;
            }

            if (maxConfidence !== undefined) {
                localVarQueryParameter['max_confidence'] = maxConfidence;
            }

            if (minConfidence !== undefined) {
                localVarQueryParameter['min_confidence'] = minConfidence;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (rating) {
                localVarQueryParameter['rating'] = rating;
            }

            if (resultType) {
                localVarQueryParameter['result_type'] = resultType;
            }

            if (sourceType) {
                localVarQueryParameter['source_type'] = sourceType;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityResultSelectAllBinderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Paginated list of compliance entity results (`{next_key,total_results,results}`).
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {'confidence' | 'found_at'} [sorting] 
         * @param {'ascending' | 'descending'} [sortingOrder] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsList: async (complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesResultsList', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesResultsList', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/results/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (hasBinder !== undefined) {
                localVarQueryParameter['has_binder'] = hasBinder;
            }

            if (hasComment !== undefined) {
                localVarQueryParameter['has_comment'] = hasComment;
            }

            if (includeAutomatedFalsePositives !== undefined) {
                localVarQueryParameter['include_automated_false_positives'] = includeAutomatedFalsePositives;
            }

            if (maxConfidence !== undefined) {
                localVarQueryParameter['max_confidence'] = maxConfidence;
            }

            if (minConfidence !== undefined) {
                localVarQueryParameter['min_confidence'] = minConfidence;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (rating) {
                localVarQueryParameter['rating'] = rating;
            }

            if (resultType) {
                localVarQueryParameter['result_type'] = resultType;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (sourceType) {
                localVarQueryParameter['source_type'] = sourceType;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsPartialUpdate: async (complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesResultsPartialUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesResultsPartialUpdate', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/results/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (hasBinder !== undefined) {
                localVarQueryParameter['has_binder'] = hasBinder;
            }

            if (hasComment !== undefined) {
                localVarQueryParameter['has_comment'] = hasComment;
            }

            if (includeAutomatedFalsePositives !== undefined) {
                localVarQueryParameter['include_automated_false_positives'] = includeAutomatedFalsePositives;
            }

            if (maxConfidence !== undefined) {
                localVarQueryParameter['max_confidence'] = maxConfidence;
            }

            if (minConfidence !== undefined) {
                localVarQueryParameter['min_confidence'] = minConfidence;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (rating) {
                localVarQueryParameter['rating'] = rating;
            }

            if (resultType) {
                localVarQueryParameter['result_type'] = resultType;
            }

            if (sourceType) {
                localVarQueryParameter['source_type'] = sourceType;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityResultSelectAllRatingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsPartialUpdate2: async (complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesResultsPartialUpdate2', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesResultsPartialUpdate2', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityResultId' is not null or undefined
            assertParamExists('complianceEntitiesResultsPartialUpdate2', 'complianceEntityResultId', complianceEntityResultId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/results/{compliance_entity_result_id}`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_result_id"}}`, encodeURIComponent(String(complianceEntityResultId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityResultRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsRetrieve: async (complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesResultsRetrieve', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesResultsRetrieve', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityResultId' is not null or undefined
            assertParamExists('complianceEntitiesResultsRetrieve', 'complianceEntityResultId', complianceEntityResultId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/results/{compliance_entity_result_id}`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_result_id"}}`, encodeURIComponent(String(complianceEntityResultId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsUpdate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesResultsUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesResultsUpdate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityResultId' is not null or undefined
            assertParamExists('complianceEntitiesResultsUpdate', 'complianceEntityResultId', complianceEntityResultId)
            // verify required parameter 'complianceEntityResultRequest' is not null or undefined
            assertParamExists('complianceEntitiesResultsUpdate', 'complianceEntityResultRequest', complianceEntityResultRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/results/{compliance_entity_result_id}`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)))
                .replace(`{${"compliance_entity_result_id"}}`, encodeURIComponent(String(complianceEntityResultId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityResultRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesRetrieve: async (complianceCheckId: string, complianceEntityId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesRetrieve', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesRetrieve', 'complianceEntityId', complianceEntityId)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityRequest} complianceEntityRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesUpdate: async (complianceCheckId: string, complianceEntityId: string, complianceEntityRequest: ComplianceEntityRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceEntitiesUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityId' is not null or undefined
            assertParamExists('complianceEntitiesUpdate', 'complianceEntityId', complianceEntityId)
            // verify required parameter 'complianceEntityRequest' is not null or undefined
            assertParamExists('complianceEntitiesUpdate', 'complianceEntityRequest', complianceEntityRequest)
            const localVarPath = `/compliance/{compliance_check_id}/entities/{compliance_entity_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_id"}}`, encodeURIComponent(String(complianceEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Start a compliance check export with optional audit-trail filters.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckExportRequest} [complianceCheckExportRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceExportCreate: async (complianceCheckId: string, complianceCheckExportRequest?: ComplianceCheckExportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceExportCreate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/export/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceCheckExportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List compliance checks for the current user\'s profile.
         * @param {boolean} [closed] Filter by whether the check was closed by a user.
         * @param {Array<'low' | 'medium' | 'high'>} [complianceScore] Filter by compliance score. Accepts multiple values.
         * @param {string} [maxCreatedAt] Return compliance checks created on or before this datetime (ISO 8601).
         * @param {string} [maxFinishedAt] Return compliance checks finished on or before this datetime (ISO 8601).
         * @param {string} [minCreatedAt] Return compliance checks created on or after this datetime (ISO 8601).
         * @param {string} [minFinishedAt] Return compliance checks finished on or after this datetime (ISO 8601).
         * @param {boolean} [monitoringEnabled] Filter by whether ongoing monitoring is enabled.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search compliance checks by company name.
         * @param {'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count'} [sorting] Field to sort results by.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order. descending is newest/highest first, ascending is oldest/lowest first.
         * @param {Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>} [status] Filter by compliance check status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceList: async (closed?: boolean, complianceScore?: Array<'low' | 'medium' | 'high'>, maxCreatedAt?: string, maxFinishedAt?: string, minCreatedAt?: string, minFinishedAt?: string, monitoringEnabled?: boolean, nextKey?: string, query?: string, sorting?: 'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count', sortingOrder?: 'ascending' | 'descending', status?: Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (closed !== undefined) {
                localVarQueryParameter['closed'] = closed;
            }

            if (complianceScore) {
                localVarQueryParameter['compliance_score'] = complianceScore;
            }

            if (maxCreatedAt !== undefined) {
                localVarQueryParameter['max_created_at'] = (maxCreatedAt as any instanceof Date) ?
                    (maxCreatedAt as any).toISOString() :
                    maxCreatedAt;
            }

            if (maxFinishedAt !== undefined) {
                localVarQueryParameter['max_finished_at'] = (maxFinishedAt as any instanceof Date) ?
                    (maxFinishedAt as any).toISOString() :
                    maxFinishedAt;
            }

            if (minCreatedAt !== undefined) {
                localVarQueryParameter['min_created_at'] = (minCreatedAt as any instanceof Date) ?
                    (minCreatedAt as any).toISOString() :
                    minCreatedAt;
            }

            if (minFinishedAt !== undefined) {
                localVarQueryParameter['min_finished_at'] = (minFinishedAt as any instanceof Date) ?
                    (minFinishedAt as any).toISOString() :
                    minFinishedAt;
            }

            if (monitoringEnabled !== undefined) {
                localVarQueryParameter['monitoring_enabled'] = monitoringEnabled;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Set `monitoring_enabled` on many checks; reconcile their entities.
         * @param {PatchedComplianceCheckMonitoringBulkRequest} [patchedComplianceCheckMonitoringBulkRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringBatchUpdatePartialUpdate: async (patchedComplianceCheckMonitoringBulkRequest?: PatchedComplianceCheckMonitoringBulkRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/monitoring/batch_update/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceCheckMonitoringBulkRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add/remove the current user as a monitoring subscriber on many checks.
         * @param {PatchedComplianceCheckSubscriptionBulkRequest} [patchedComplianceCheckSubscriptionBulkRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringSubscriptionBatchUpdatePartialUpdate: async (patchedComplianceCheckSubscriptionBulkRequest?: PatchedComplianceCheckSubscriptionBulkRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/monitoring-subscription/batch_update/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceCheckSubscriptionBulkRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Subscribe the current user (allowed even without monitoring enabled).
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringSubscriptionCreate: async (complianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceMonitoringSubscriptionCreate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/monitoring-subscription/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Unsubscribe the current user.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringSubscriptionDestroy: async (complianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceMonitoringSubscriptionDestroy', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/monitoring-subscription/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {PatchedComplianceCheckRequest} [patchedComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        compliancePartialUpdate: async (complianceCheckId: string, patchedComplianceCheckRequest?: PatchedComplianceCheckRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('compliancePartialUpdate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Return the report for the requested scope and period.
         * @param {string} [anchorDate] Day the period is anchored on. Defaults to today.
         * @param {'month' | 'quarter' | 'ytd' | 'year'} [period] * &#x60;month&#x60; - Month * &#x60;quarter&#x60; - Quarter * &#x60;ytd&#x60; - Ytd * &#x60;year&#x60; - Year
         * @param {Array<string>} [portfolioId] 
         * @param {'me' | 'organization'} [scope] * &#x60;me&#x60; - Me * &#x60;organization&#x60; - Organization
         * @param {Array<string>} [userId] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceReportsRetrieve: async (anchorDate?: string, period?: 'month' | 'quarter' | 'ytd' | 'year', portfolioId?: Array<string>, scope?: 'me' | 'organization', userId?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/reports/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (anchorDate !== undefined) {
                localVarQueryParameter['anchor_date'] = (anchorDate as any instanceof Date) ?
                    (anchorDate as any).toISOString().substr(0,10) :
                    anchorDate;
            }

            if (period !== undefined) {
                localVarQueryParameter['period'] = period;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (scope !== undefined) {
                localVarQueryParameter['scope'] = scope;
            }

            if (userId) {
                localVarQueryParameter['user_id'] = userId;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update entity results in batch.
         * @param {string} complianceCheckId 
         * @param {Array<ComplianceEntityResultBatchRequest>} complianceEntityResultBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsBatchUpdatePartialUpdate: async (complianceCheckId: string, complianceEntityResultBatchRequest: Array<ComplianceEntityResultBatchRequest>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsBatchUpdatePartialUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityResultBatchRequest' is not null or undefined
            assertParamExists('complianceResultsBatchUpdatePartialUpdate', 'complianceEntityResultBatchRequest', complianceEntityResultBatchRequest)
            const localVarPath = `/compliance/{compliance_check_id}/results/batch_update/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityResultBatchRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
         * @param {string} complianceCheckId 
         * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsCreate: async (complianceCheckId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsCreate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityResultSelectAllBinderRequest' is not null or undefined
            assertParamExists('complianceResultsCreate', 'complianceEntityResultSelectAllBinderRequest', complianceEntityResultSelectAllBinderRequest)
            const localVarPath = `/compliance/{compliance_check_id}/results/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (hasBinder !== undefined) {
                localVarQueryParameter['has_binder'] = hasBinder;
            }

            if (hasComment !== undefined) {
                localVarQueryParameter['has_comment'] = hasComment;
            }

            if (includeAutomatedFalsePositives !== undefined) {
                localVarQueryParameter['include_automated_false_positives'] = includeAutomatedFalsePositives;
            }

            if (maxConfidence !== undefined) {
                localVarQueryParameter['max_confidence'] = maxConfidence;
            }

            if (minConfidence !== undefined) {
                localVarQueryParameter['min_confidence'] = minConfidence;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (rating) {
                localVarQueryParameter['rating'] = rating;
            }

            if (resultType) {
                localVarQueryParameter['result_type'] = resultType;
            }

            if (sourceType) {
                localVarQueryParameter['source_type'] = sourceType;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityResultSelectAllBinderRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Paginated list of compliance entity results (`{next_key,total_results,results}`).
         * @param {string} complianceCheckId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {'confidence' | 'found_at'} [sorting] 
         * @param {'ascending' | 'descending'} [sortingOrder] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsList: async (complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsList', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/results/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (hasBinder !== undefined) {
                localVarQueryParameter['has_binder'] = hasBinder;
            }

            if (hasComment !== undefined) {
                localVarQueryParameter['has_comment'] = hasComment;
            }

            if (includeAutomatedFalsePositives !== undefined) {
                localVarQueryParameter['include_automated_false_positives'] = includeAutomatedFalsePositives;
            }

            if (maxConfidence !== undefined) {
                localVarQueryParameter['max_confidence'] = maxConfidence;
            }

            if (minConfidence !== undefined) {
                localVarQueryParameter['min_confidence'] = minConfidence;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['page_size'] = pageSize;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (rating) {
                localVarQueryParameter['rating'] = rating;
            }

            if (resultType) {
                localVarQueryParameter['result_type'] = resultType;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (sourceType) {
                localVarQueryParameter['source_type'] = sourceType;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
         * @param {string} complianceCheckId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsPartialUpdate: async (complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsPartialUpdate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/results/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (hasBinder !== undefined) {
                localVarQueryParameter['has_binder'] = hasBinder;
            }

            if (hasComment !== undefined) {
                localVarQueryParameter['has_comment'] = hasComment;
            }

            if (includeAutomatedFalsePositives !== undefined) {
                localVarQueryParameter['include_automated_false_positives'] = includeAutomatedFalsePositives;
            }

            if (maxConfidence !== undefined) {
                localVarQueryParameter['max_confidence'] = maxConfidence;
            }

            if (minConfidence !== undefined) {
                localVarQueryParameter['min_confidence'] = minConfidence;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (rating) {
                localVarQueryParameter['rating'] = rating;
            }

            if (resultType) {
                localVarQueryParameter['result_type'] = resultType;
            }

            if (sourceType) {
                localVarQueryParameter['source_type'] = sourceType;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityResultSelectAllRatingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsPartialUpdate2: async (complianceCheckId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsPartialUpdate2', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityResultId' is not null or undefined
            assertParamExists('complianceResultsPartialUpdate2', 'complianceEntityResultId', complianceEntityResultId)
            const localVarPath = `/compliance/{compliance_check_id}/results/{compliance_entity_result_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_result_id"}}`, encodeURIComponent(String(complianceEntityResultId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceEntityResultRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsRetrieve: async (complianceCheckId: string, complianceEntityResultId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsRetrieve', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityResultId' is not null or undefined
            assertParamExists('complianceResultsRetrieve', 'complianceEntityResultId', complianceEntityResultId)
            const localVarPath = `/compliance/{compliance_check_id}/results/{compliance_entity_result_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_result_id"}}`, encodeURIComponent(String(complianceEntityResultId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsUpdate: async (complianceCheckId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceResultsUpdate', 'complianceCheckId', complianceCheckId)
            // verify required parameter 'complianceEntityResultId' is not null or undefined
            assertParamExists('complianceResultsUpdate', 'complianceEntityResultId', complianceEntityResultId)
            // verify required parameter 'complianceEntityResultRequest' is not null or undefined
            assertParamExists('complianceResultsUpdate', 'complianceEntityResultRequest', complianceEntityResultRequest)
            const localVarPath = `/compliance/{compliance_check_id}/results/{compliance_entity_result_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)))
                .replace(`{${"compliance_entity_result_id"}}`, encodeURIComponent(String(complianceEntityResultId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceEntityResultRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceRetrieve: async (complianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceRetrieve', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Return sanctions providers with their live listing stats.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSanctionsProvidersList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/sanctions/providers/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Queue an Excel export of the changelog for the active profile.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsChangelogExportCreate: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/changelog/export/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List the active profile\'s compliance settings change history (read-only).  Optional ``source`` / ``field`` query params filter the list; the response carries ``source_counts`` for the filter chips. Ordered latest change first.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsChangelogList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/changelog/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create one or many industry risk overrides.
         * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskCreate: async (profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileIndustryRiskOverrideRequest' is not null or undefined
            assertParamExists('complianceSettingsIndustryRiskCreate', 'profileIndustryRiskOverrideRequest', profileIndustryRiskOverrideRequest)
            const localVarPath = `/compliance/settings/industry_risk/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileIndustryRiskOverrideRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskDestroy: async (overrideId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsIndustryRiskDestroy', 'overrideId', overrideId)
            const localVarPath = `/compliance/settings/industry_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List industry risk overrides, optionally filtered by ``query``.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskList: async (nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/industry_risk/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {PatchedProfileIndustryRiskOverrideRequest} [patchedProfileIndustryRiskOverrideRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskPartialUpdate: async (overrideId: string, patchedProfileIndustryRiskOverrideRequest?: PatchedProfileIndustryRiskOverrideRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsIndustryRiskPartialUpdate', 'overrideId', overrideId)
            const localVarPath = `/compliance/settings/industry_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedProfileIndustryRiskOverrideRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskRetrieve: async (overrideId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsIndustryRiskRetrieve', 'overrideId', overrideId)
            const localVarPath = `/compliance/settings/industry_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskUpdate: async (overrideId: string, profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsIndustryRiskUpdate', 'overrideId', overrideId)
            // verify required parameter 'profileIndustryRiskOverrideRequest' is not null or undefined
            assertParamExists('complianceSettingsIndustryRiskUpdate', 'profileIndustryRiskOverrideRequest', profileIndustryRiskOverrideRequest)
            const localVarPath = `/compliance/settings/industry_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileIndustryRiskOverrideRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {PatchedComplianceSettingsRequest} [patchedComplianceSettingsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsPartialUpdate: async (patchedComplianceSettingsRequest?: PatchedComplianceSettingsRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedComplianceSettingsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsRetrieve: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {ComplianceSettingsRequest} [complianceSettingsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsUpdate: async (complianceSettingsRequest?: ComplianceSettingsRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceSettingsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create world (country) risk overrides for the active profile.
         * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskCreate: async (profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileCountryRiskOverrideRequest' is not null or undefined
            assertParamExists('complianceSettingsWorldRiskCreate', 'profileCountryRiskOverrideRequest', profileCountryRiskOverrideRequest)
            const localVarPath = `/compliance/settings/world_risk/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileCountryRiskOverrideRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskDestroy: async (overrideId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsWorldRiskDestroy', 'overrideId', overrideId)
            const localVarPath = `/compliance/settings/world_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create world (country) risk overrides for the active profile.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/compliance/settings/world_risk/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {PatchedProfileCountryRiskOverrideRequest} [patchedProfileCountryRiskOverrideRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskPartialUpdate: async (overrideId: string, patchedProfileCountryRiskOverrideRequest?: PatchedProfileCountryRiskOverrideRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsWorldRiskPartialUpdate', 'overrideId', overrideId)
            const localVarPath = `/compliance/settings/world_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedProfileCountryRiskOverrideRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskRetrieve: async (overrideId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsWorldRiskRetrieve', 'overrideId', overrideId)
            const localVarPath = `/compliance/settings/world_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskUpdate: async (overrideId: string, profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'overrideId' is not null or undefined
            assertParamExists('complianceSettingsWorldRiskUpdate', 'overrideId', overrideId)
            // verify required parameter 'profileCountryRiskOverrideRequest' is not null or undefined
            assertParamExists('complianceSettingsWorldRiskUpdate', 'profileCountryRiskOverrideRequest', profileCountryRiskOverrideRequest)
            const localVarPath = `/compliance/settings/world_risk/{override_id}/`
                .replace(`{${"override_id"}}`, encodeURIComponent(String(overrideId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileCountryRiskOverrideRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Start a compliance check.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckStartRequest} [complianceCheckStartRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceStartCreate: async (complianceCheckId: string, complianceCheckStartRequest?: ComplianceCheckStartRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceStartCreate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/start/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceCheckStartRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Start a compliance check.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceStartDirectorsSearchCreate: async (complianceCheckId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceStartDirectorsSearchCreate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/start_directors_search/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckRequest} [complianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceUpdate: async (complianceCheckId: string, complianceCheckRequest?: ComplianceCheckRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'complianceCheckId' is not null or undefined
            assertParamExists('complianceUpdate', 'complianceCheckId', complianceCheckId)
            const localVarPath = `/compliance/{compliance_check_id}/`
                .replace(`{${"compliance_check_id"}}`, encodeURIComponent(String(complianceCheckId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(complianceCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ComplianceApi - functional programming interface
 * @export
 */
export const ComplianceApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ComplianceApiAxiosParamCreator(configuration)
    return {
        /**
         * Add an add/exclude keyword entry to the active profile\'s set.
         * @param {KeywordSetEntryCreateRequest} keywordSetEntryCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceAdverseMediaKeywordsCreate(keywordSetEntryCreateRequest: KeywordSetEntryCreateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<KeywordSetEntry>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceAdverseMediaKeywordsCreate(keywordSetEntryCreateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete one add/exclude entry from the active profile\'s keyword set.
         * @param {string} entryId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceAdverseMediaKeywordsDestroy(entryId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceAdverseMediaKeywordsDestroy(entryId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete every entry in the active profile\'s keyword set.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceAdverseMediaKeywordsResetCreate(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceAdverseMediaKeywordsResetCreate(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Return the effective keyword list for a language plus per-language counts.  ``language`` is required: the full list is too large to return unfiltered, so the frontend fetches one language and uses ``language_counts`` for the rest.
         * @param {string} language 
         * @param {string} [entityType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceAdverseMediaKeywordsRetrieve(language: string, entityType?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EffectiveKeywordList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceAdverseMediaKeywordsRetrieve(language, entityType, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a new batch compliance check.  Supports either a list of company external_ids or a portfolio_id to derive companies. No processing/sending logic is executed here; this only creates the batch record.
         * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceBatchesCreate(batchComplianceCheckRequest?: BatchComplianceCheckRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceBatchesCreate(batchComplianceCheckRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {PatchedBatchComplianceCheckRequest} [patchedBatchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceBatchesPartialUpdate(batchComplianceCheckId: string, patchedBatchComplianceCheckRequest?: PatchedBatchComplianceCheckRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceBatchesPartialUpdate(batchComplianceCheckId, patchedBatchComplianceCheckRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceBatchesRetrieve(batchComplianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceBatchesRetrieve(batchComplianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Start a compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceBatchesStartCreate(batchComplianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceBatchesStartCreate(batchComplianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceBatchesUpdate(batchComplianceCheckId: string, batchComplianceCheckRequest?: BatchComplianceCheckRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceBatchesUpdate(batchComplianceCheckId, batchComplianceCheckRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a new compliance check.
         * @param {ComplianceCheckRequestRequest} [complianceCheckRequestRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceCreate(complianceCheckRequestRequest?: ComplianceCheckRequestRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceCreate(complianceCheckRequestRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Return enforcement providers with their live listing stats.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEnforcementProvidersList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<EnforcementProvider>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEnforcementProvidersList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesAliasesCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityAlias>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesAliasesCreate(complianceCheckId, complianceEntityId, complianceEntityAliasRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesAliasesDestroy(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesAliasesDestroy(complianceCheckId, complianceEntityAliasId, complianceEntityId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesAliasesList(complianceCheckId: string, complianceEntityId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedComplianceEntityAliasList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesAliasesList(complianceCheckId, complianceEntityId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {PatchedComplianceEntityAliasRequest} [patchedComplianceEntityAliasRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesAliasesPartialUpdate(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, patchedComplianceEntityAliasRequest?: PatchedComplianceEntityAliasRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityAlias>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesAliasesPartialUpdate(complianceCheckId, complianceEntityAliasId, complianceEntityId, patchedComplianceEntityAliasRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesAliasesRetrieve(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityAlias>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesAliasesRetrieve(complianceCheckId, complianceEntityAliasId, complianceEntityId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesAliasesUpdate(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityAlias>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesAliasesUpdate(complianceCheckId, complianceEntityAliasId, complianceEntityId, complianceEntityAliasRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Batch update compliance entities.
         * @param {string} complianceCheckId 
         * @param {Array<ComplianceEntityBatchUpdateRequest>} complianceEntityBatchUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesBatchUpdatePartialUpdate(complianceCheckId: string, complianceEntityBatchUpdateRequest: Array<ComplianceEntityBatchUpdateRequest>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<{ [key: string]: any; }>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesBatchUpdatePartialUpdate(complianceCheckId, complianceEntityBatchUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve all compliance entities associated with a compliance check.
         * @param {string} complianceCheckId 
         * @param {ComplianceEntityRequest} complianceEntityRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesCreate(complianceCheckId: string, complianceEntityRequest: ComplianceEntityRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntity>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesCreate(complianceCheckId, complianceEntityRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesDestroy(complianceCheckId: string, complianceEntityId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesDestroy(complianceCheckId, complianceEntityId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesIdentifiersCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityIdentifier>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesIdentifiersCreate(complianceCheckId, complianceEntityId, complianceEntityIdentifierRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesIdentifiersDestroy(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesIdentifiersDestroy(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesIdentifiersList(complianceCheckId: string, complianceEntityId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedComplianceEntityIdentifierList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesIdentifiersList(complianceCheckId, complianceEntityId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {PatchedComplianceEntityIdentifierRequest} [patchedComplianceEntityIdentifierRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesIdentifiersPartialUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, patchedComplianceEntityIdentifierRequest?: PatchedComplianceEntityIdentifierRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityIdentifier>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesIdentifiersPartialUpdate(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, patchedComplianceEntityIdentifierRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesIdentifiersRetrieve(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityIdentifier>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesIdentifiersRetrieve(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesIdentifiersUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityIdentifier>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesIdentifiersUpdate(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, complianceEntityIdentifierRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve all compliance entities associated with a compliance check.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesList(complianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ComplianceEntity>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesList(complianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {PatchedComplianceEntityRequest} [patchedComplianceEntityRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesPartialUpdate(complianceCheckId: string, complianceEntityId: string, patchedComplianceEntityRequest?: PatchedComplianceEntityRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntity>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesPartialUpdate(complianceCheckId, complianceEntityId, patchedComplianceEntityRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesResultsCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesResultsCreate(complianceCheckId, complianceEntityId, complianceEntityResultSelectAllBinderRequest, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Paginated list of compliance entity results (`{next_key,total_results,results}`).
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {'confidence' | 'found_at'} [sorting] 
         * @param {'ascending' | 'descending'} [sortingOrder] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesResultsList(complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedComplianceEntityResultList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesResultsList(complianceCheckId, complianceEntityId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, nextKey, pageSize, query, rating, resultType, sorting, sortingOrder, sourceType, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesResultsPartialUpdate(complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResultSelectAllRatingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesResultsPartialUpdate(complianceCheckId, complianceEntityId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, patchedComplianceEntityResultSelectAllRatingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesResultsPartialUpdate2(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesResultsPartialUpdate2(complianceCheckId, complianceEntityId, complianceEntityResultId, patchedComplianceEntityResultRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesResultsRetrieve(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesResultsRetrieve(complianceCheckId, complianceEntityId, complianceEntityResultId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesResultsUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesResultsUpdate(complianceCheckId, complianceEntityId, complianceEntityResultId, complianceEntityResultRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesRetrieve(complianceCheckId: string, complianceEntityId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntity>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesRetrieve(complianceCheckId, complianceEntityId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityRequest} complianceEntityRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceEntitiesUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityRequest: ComplianceEntityRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntity>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceEntitiesUpdate(complianceCheckId, complianceEntityId, complianceEntityRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Start a compliance check export with optional audit-trail filters.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckExportRequest} [complianceCheckExportRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceExportCreate(complianceCheckId: string, complianceCheckExportRequest?: ComplianceCheckExportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceCheckExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceExportCreate(complianceCheckId, complianceCheckExportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List compliance checks for the current user\'s profile.
         * @param {boolean} [closed] Filter by whether the check was closed by a user.
         * @param {Array<'low' | 'medium' | 'high'>} [complianceScore] Filter by compliance score. Accepts multiple values.
         * @param {string} [maxCreatedAt] Return compliance checks created on or before this datetime (ISO 8601).
         * @param {string} [maxFinishedAt] Return compliance checks finished on or before this datetime (ISO 8601).
         * @param {string} [minCreatedAt] Return compliance checks created on or after this datetime (ISO 8601).
         * @param {string} [minFinishedAt] Return compliance checks finished on or after this datetime (ISO 8601).
         * @param {boolean} [monitoringEnabled] Filter by whether ongoing monitoring is enabled.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search compliance checks by company name.
         * @param {'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count'} [sorting] Field to sort results by.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order. descending is newest/highest first, ascending is oldest/lowest first.
         * @param {Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>} [status] Filter by compliance check status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceList(closed?: boolean, complianceScore?: Array<'low' | 'medium' | 'high'>, maxCreatedAt?: string, maxFinishedAt?: string, minCreatedAt?: string, minFinishedAt?: string, monitoringEnabled?: boolean, nextKey?: string, query?: string, sorting?: 'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count', sortingOrder?: 'ascending' | 'descending', status?: Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedComplianceCheckList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceList(closed, complianceScore, maxCreatedAt, maxFinishedAt, minCreatedAt, minFinishedAt, monitoringEnabled, nextKey, query, sorting, sortingOrder, status, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Set `monitoring_enabled` on many checks; reconcile their entities.
         * @param {PatchedComplianceCheckMonitoringBulkRequest} [patchedComplianceCheckMonitoringBulkRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceMonitoringBatchUpdatePartialUpdate(patchedComplianceCheckMonitoringBulkRequest?: PatchedComplianceCheckMonitoringBulkRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceMonitoringBatchUpdatePartialUpdate(patchedComplianceCheckMonitoringBulkRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Add/remove the current user as a monitoring subscriber on many checks.
         * @param {PatchedComplianceCheckSubscriptionBulkRequest} [patchedComplianceCheckSubscriptionBulkRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceMonitoringSubscriptionBatchUpdatePartialUpdate(patchedComplianceCheckSubscriptionBulkRequest?: PatchedComplianceCheckSubscriptionBulkRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceMonitoringSubscriptionBatchUpdatePartialUpdate(patchedComplianceCheckSubscriptionBulkRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Subscribe the current user (allowed even without monitoring enabled).
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceMonitoringSubscriptionCreate(complianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceMonitoringSubscriptionCreate(complianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Unsubscribe the current user.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceMonitoringSubscriptionDestroy(complianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceMonitoringSubscriptionDestroy(complianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {PatchedComplianceCheckRequest} [patchedComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async compliancePartialUpdate(complianceCheckId: string, patchedComplianceCheckRequest?: PatchedComplianceCheckRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.compliancePartialUpdate(complianceCheckId, patchedComplianceCheckRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Return the report for the requested scope and period.
         * @param {string} [anchorDate] Day the period is anchored on. Defaults to today.
         * @param {'month' | 'quarter' | 'ytd' | 'year'} [period] * &#x60;month&#x60; - Month * &#x60;quarter&#x60; - Quarter * &#x60;ytd&#x60; - Ytd * &#x60;year&#x60; - Year
         * @param {Array<string>} [portfolioId] 
         * @param {'me' | 'organization'} [scope] * &#x60;me&#x60; - Me * &#x60;organization&#x60; - Organization
         * @param {Array<string>} [userId] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceReportsRetrieve(anchorDate?: string, period?: 'month' | 'quarter' | 'ytd' | 'year', portfolioId?: Array<string>, scope?: 'me' | 'organization', userId?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceReport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceReportsRetrieve(anchorDate, period, portfolioId, scope, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update entity results in batch.
         * @param {string} complianceCheckId 
         * @param {Array<ComplianceEntityResultBatchRequest>} complianceEntityResultBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsBatchUpdatePartialUpdate(complianceCheckId: string, complianceEntityResultBatchRequest: Array<ComplianceEntityResultBatchRequest>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ComplianceEntityResultBatch>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsBatchUpdatePartialUpdate(complianceCheckId, complianceEntityResultBatchRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
         * @param {string} complianceCheckId 
         * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsCreate(complianceCheckId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsCreate(complianceCheckId, complianceEntityResultSelectAllBinderRequest, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Paginated list of compliance entity results (`{next_key,total_results,results}`).
         * @param {string} complianceCheckId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {'confidence' | 'found_at'} [sorting] 
         * @param {'ascending' | 'descending'} [sortingOrder] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsList(complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedComplianceEntityResultList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsList(complianceCheckId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, nextKey, pageSize, query, rating, resultType, sorting, sortingOrder, sourceType, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
         * @param {string} complianceCheckId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsPartialUpdate(complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResultSelectAllRatingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsPartialUpdate(complianceCheckId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, patchedComplianceEntityResultSelectAllRatingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsPartialUpdate2(complianceCheckId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsPartialUpdate2(complianceCheckId, complianceEntityResultId, patchedComplianceEntityResultRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsRetrieve(complianceCheckId: string, complianceEntityResultId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsRetrieve(complianceCheckId, complianceEntityResultId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceResultsUpdate(complianceCheckId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceEntityResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceResultsUpdate(complianceCheckId, complianceEntityResultId, complianceEntityResultRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceRetrieve(complianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceRetrieve(complianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Return sanctions providers with their live listing stats.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSanctionsProvidersList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SanctionProvider>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSanctionsProvidersList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Queue an Excel export of the changelog for the active profile.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsChangelogExportCreate(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsChangelogExportCreate(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List the active profile\'s compliance settings change history (read-only).  Optional ``source`` / ``field`` query params filter the list; the response carries ``source_counts`` for the filter chips. Ordered latest change first.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsChangelogList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedComplianceSettingsChangeLogList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsChangelogList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create one or many industry risk overrides.
         * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsIndustryRiskCreate(profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileIndustryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsIndustryRiskCreate(profileIndustryRiskOverrideRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsIndustryRiskDestroy(overrideId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsIndustryRiskDestroy(overrideId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List industry risk overrides, optionally filtered by ``query``.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsIndustryRiskList(nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedProfileIndustryRiskOverrideList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsIndustryRiskList(nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {PatchedProfileIndustryRiskOverrideRequest} [patchedProfileIndustryRiskOverrideRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsIndustryRiskPartialUpdate(overrideId: string, patchedProfileIndustryRiskOverrideRequest?: PatchedProfileIndustryRiskOverrideRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileIndustryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsIndustryRiskPartialUpdate(overrideId, patchedProfileIndustryRiskOverrideRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsIndustryRiskRetrieve(overrideId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileIndustryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsIndustryRiskRetrieve(overrideId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsIndustryRiskUpdate(overrideId: string, profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileIndustryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsIndustryRiskUpdate(overrideId, profileIndustryRiskOverrideRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {PatchedComplianceSettingsRequest} [patchedComplianceSettingsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsPartialUpdate(patchedComplianceSettingsRequest?: PatchedComplianceSettingsRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceSettings>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsPartialUpdate(patchedComplianceSettingsRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsRetrieve(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceSettings>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsRetrieve(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {ComplianceSettingsRequest} [complianceSettingsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsUpdate(complianceSettingsRequest?: ComplianceSettingsRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceSettings>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsUpdate(complianceSettingsRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create world (country) risk overrides for the active profile.
         * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsWorldRiskCreate(profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileCountryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsWorldRiskCreate(profileCountryRiskOverrideRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsWorldRiskDestroy(overrideId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsWorldRiskDestroy(overrideId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create world (country) risk overrides for the active profile.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsWorldRiskList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedProfileCountryRiskOverrideList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsWorldRiskList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {PatchedProfileCountryRiskOverrideRequest} [patchedProfileCountryRiskOverrideRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsWorldRiskPartialUpdate(overrideId: string, patchedProfileCountryRiskOverrideRequest?: PatchedProfileCountryRiskOverrideRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileCountryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsWorldRiskPartialUpdate(overrideId, patchedProfileCountryRiskOverrideRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsWorldRiskRetrieve(overrideId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileCountryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsWorldRiskRetrieve(overrideId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceSettingsWorldRiskUpdate(overrideId: string, profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileCountryRiskOverride>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceSettingsWorldRiskUpdate(overrideId, profileCountryRiskOverrideRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Start a compliance check.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckStartRequest} [complianceCheckStartRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceStartCreate(complianceCheckId: string, complianceCheckStartRequest?: ComplianceCheckStartRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceStartCreate(complianceCheckId, complianceCheckStartRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Start a compliance check.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceStartDirectorsSearchCreate(complianceCheckId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceStartDirectorsSearchCreate(complianceCheckId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckRequest} [complianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async complianceUpdate(complianceCheckId: string, complianceCheckRequest?: ComplianceCheckRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ComplianceCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.complianceUpdate(complianceCheckId, complianceCheckRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * ComplianceApi - factory interface
 * @export
 */
export const ComplianceApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ComplianceApiFp(configuration)
    return {
        /**
         * Add an add/exclude keyword entry to the active profile\'s set.
         * @param {KeywordSetEntryCreateRequest} keywordSetEntryCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsCreate(keywordSetEntryCreateRequest: KeywordSetEntryCreateRequest, options?: any): AxiosPromise<KeywordSetEntry> {
            return localVarFp.complianceAdverseMediaKeywordsCreate(keywordSetEntryCreateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete one add/exclude entry from the active profile\'s keyword set.
         * @param {string} entryId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsDestroy(entryId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceAdverseMediaKeywordsDestroy(entryId, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete every entry in the active profile\'s keyword set.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsResetCreate(options?: any): AxiosPromise<void> {
            return localVarFp.complianceAdverseMediaKeywordsResetCreate(options).then((request) => request(axios, basePath));
        },
        /**
         * Return the effective keyword list for a language plus per-language counts.  ``language`` is required: the full list is too large to return unfiltered, so the frontend fetches one language and uses ``language_counts`` for the rest.
         * @param {string} language 
         * @param {string} [entityType] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceAdverseMediaKeywordsRetrieve(language: string, entityType?: string, options?: any): AxiosPromise<EffectiveKeywordList> {
            return localVarFp.complianceAdverseMediaKeywordsRetrieve(language, entityType, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new batch compliance check.  Supports either a list of company external_ids or a portfolio_id to derive companies. No processing/sending logic is executed here; this only creates the batch record.
         * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesCreate(batchComplianceCheckRequest?: BatchComplianceCheckRequest, options?: any): AxiosPromise<BatchComplianceCheck> {
            return localVarFp.complianceBatchesCreate(batchComplianceCheckRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {PatchedBatchComplianceCheckRequest} [patchedBatchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesPartialUpdate(batchComplianceCheckId: string, patchedBatchComplianceCheckRequest?: PatchedBatchComplianceCheckRequest, options?: any): AxiosPromise<BatchComplianceCheck> {
            return localVarFp.complianceBatchesPartialUpdate(batchComplianceCheckId, patchedBatchComplianceCheckRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesRetrieve(batchComplianceCheckId: string, options?: any): AxiosPromise<BatchComplianceCheck> {
            return localVarFp.complianceBatchesRetrieve(batchComplianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Start a compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesStartCreate(batchComplianceCheckId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceBatchesStartCreate(batchComplianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a batch compliance check.
         * @param {string} batchComplianceCheckId 
         * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceBatchesUpdate(batchComplianceCheckId: string, batchComplianceCheckRequest?: BatchComplianceCheckRequest, options?: any): AxiosPromise<BatchComplianceCheck> {
            return localVarFp.complianceBatchesUpdate(batchComplianceCheckId, batchComplianceCheckRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new compliance check.
         * @param {ComplianceCheckRequestRequest} [complianceCheckRequestRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceCreate(complianceCheckRequestRequest?: ComplianceCheckRequestRequest, options?: any): AxiosPromise<ComplianceCheck> {
            return localVarFp.complianceCreate(complianceCheckRequestRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Return enforcement providers with their live listing stats.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEnforcementProvidersList(options?: any): AxiosPromise<Array<EnforcementProvider>> {
            return localVarFp.complianceEnforcementProvidersList(options).then((request) => request(axios, basePath));
        },
        /**
         * List and create compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options?: any): AxiosPromise<ComplianceEntityAlias> {
            return localVarFp.complianceEntitiesAliasesCreate(complianceCheckId, complianceEntityId, complianceEntityAliasRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesDestroy(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceEntitiesAliasesDestroy(complianceCheckId, complianceEntityAliasId, complianceEntityId, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesList(complianceCheckId: string, complianceEntityId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedComplianceEntityAliasList> {
            return localVarFp.complianceEntitiesAliasesList(complianceCheckId, complianceEntityId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {PatchedComplianceEntityAliasRequest} [patchedComplianceEntityAliasRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesPartialUpdate(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, patchedComplianceEntityAliasRequest?: PatchedComplianceEntityAliasRequest, options?: any): AxiosPromise<ComplianceEntityAlias> {
            return localVarFp.complianceEntitiesAliasesPartialUpdate(complianceCheckId, complianceEntityAliasId, complianceEntityId, patchedComplianceEntityAliasRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesRetrieve(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options?: any): AxiosPromise<ComplianceEntityAlias> {
            return localVarFp.complianceEntitiesAliasesRetrieve(complianceCheckId, complianceEntityAliasId, complianceEntityId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity aliases.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityAliasId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesAliasesUpdate(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options?: any): AxiosPromise<ComplianceEntityAlias> {
            return localVarFp.complianceEntitiesAliasesUpdate(complianceCheckId, complianceEntityAliasId, complianceEntityId, complianceEntityAliasRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Batch update compliance entities.
         * @param {string} complianceCheckId 
         * @param {Array<ComplianceEntityBatchUpdateRequest>} complianceEntityBatchUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesBatchUpdatePartialUpdate(complianceCheckId: string, complianceEntityBatchUpdateRequest: Array<ComplianceEntityBatchUpdateRequest>, options?: any): AxiosPromise<{ [key: string]: any; }> {
            return localVarFp.complianceEntitiesBatchUpdatePartialUpdate(complianceCheckId, complianceEntityBatchUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve all compliance entities associated with a compliance check.
         * @param {string} complianceCheckId 
         * @param {ComplianceEntityRequest} complianceEntityRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesCreate(complianceCheckId: string, complianceEntityRequest: ComplianceEntityRequest, options?: any): AxiosPromise<ComplianceEntity> {
            return localVarFp.complianceEntitiesCreate(complianceCheckId, complianceEntityRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesDestroy(complianceCheckId: string, complianceEntityId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceEntitiesDestroy(complianceCheckId, complianceEntityId, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options?: any): AxiosPromise<ComplianceEntityIdentifier> {
            return localVarFp.complianceEntitiesIdentifiersCreate(complianceCheckId, complianceEntityId, complianceEntityIdentifierRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersDestroy(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceEntitiesIdentifiersDestroy(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersList(complianceCheckId: string, complianceEntityId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedComplianceEntityIdentifierList> {
            return localVarFp.complianceEntitiesIdentifiersList(complianceCheckId, complianceEntityId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {PatchedComplianceEntityIdentifierRequest} [patchedComplianceEntityIdentifierRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersPartialUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, patchedComplianceEntityIdentifierRequest?: PatchedComplianceEntityIdentifierRequest, options?: any): AxiosPromise<ComplianceEntityIdentifier> {
            return localVarFp.complianceEntitiesIdentifiersPartialUpdate(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, patchedComplianceEntityIdentifierRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersRetrieve(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options?: any): AxiosPromise<ComplianceEntityIdentifier> {
            return localVarFp.complianceEntitiesIdentifiersRetrieve(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy compliance entity identifiers.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityIdentifierId 
         * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesIdentifiersUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options?: any): AxiosPromise<ComplianceEntityIdentifier> {
            return localVarFp.complianceEntitiesIdentifiersUpdate(complianceCheckId, complianceEntityId, complianceEntityIdentifierId, complianceEntityIdentifierRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve all compliance entities associated with a compliance check.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesList(complianceCheckId: string, options?: any): AxiosPromise<Array<ComplianceEntity>> {
            return localVarFp.complianceEntitiesList(complianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {PatchedComplianceEntityRequest} [patchedComplianceEntityRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesPartialUpdate(complianceCheckId: string, complianceEntityId: string, patchedComplianceEntityRequest?: PatchedComplianceEntityRequest, options?: any): AxiosPromise<ComplianceEntity> {
            return localVarFp.complianceEntitiesPartialUpdate(complianceCheckId, complianceEntityId, patchedComplianceEntityRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: any): AxiosPromise<void> {
            return localVarFp.complianceEntitiesResultsCreate(complianceCheckId, complianceEntityId, complianceEntityResultSelectAllBinderRequest, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, options).then((request) => request(axios, basePath));
        },
        /**
         * Paginated list of compliance entity results (`{next_key,total_results,results}`).
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {'confidence' | 'found_at'} [sorting] 
         * @param {'ascending' | 'descending'} [sortingOrder] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsList(complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: any): AxiosPromise<PaginatedComplianceEntityResultList> {
            return localVarFp.complianceEntitiesResultsList(complianceCheckId, complianceEntityId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, nextKey, pageSize, query, rating, resultType, sorting, sortingOrder, sourceType, options).then((request) => request(axios, basePath));
        },
        /**
         * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsPartialUpdate(complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options?: any): AxiosPromise<ComplianceEntityResultSelectAllRatingResponse> {
            return localVarFp.complianceEntitiesResultsPartialUpdate(complianceCheckId, complianceEntityId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, patchedComplianceEntityResultSelectAllRatingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsPartialUpdate2(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options?: any): AxiosPromise<ComplianceEntityResult> {
            return localVarFp.complianceEntitiesResultsPartialUpdate2(complianceCheckId, complianceEntityId, complianceEntityResultId, patchedComplianceEntityResultRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsRetrieve(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, options?: any): AxiosPromise<ComplianceEntityResult> {
            return localVarFp.complianceEntitiesResultsRetrieve(complianceCheckId, complianceEntityId, complianceEntityResultId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {string} complianceEntityResultId 
         * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesResultsUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options?: any): AxiosPromise<ComplianceEntityResult> {
            return localVarFp.complianceEntitiesResultsUpdate(complianceCheckId, complianceEntityId, complianceEntityResultId, complianceEntityResultRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesRetrieve(complianceCheckId: string, complianceEntityId: string, options?: any): AxiosPromise<ComplianceEntity> {
            return localVarFp.complianceEntitiesRetrieve(complianceCheckId, complianceEntityId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityId 
         * @param {ComplianceEntityRequest} complianceEntityRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceEntitiesUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityRequest: ComplianceEntityRequest, options?: any): AxiosPromise<ComplianceEntity> {
            return localVarFp.complianceEntitiesUpdate(complianceCheckId, complianceEntityId, complianceEntityRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Start a compliance check export with optional audit-trail filters.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckExportRequest} [complianceCheckExportRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceExportCreate(complianceCheckId: string, complianceCheckExportRequest?: ComplianceCheckExportRequest, options?: any): AxiosPromise<ComplianceCheckExport> {
            return localVarFp.complianceExportCreate(complianceCheckId, complianceCheckExportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List compliance checks for the current user\'s profile.
         * @param {boolean} [closed] Filter by whether the check was closed by a user.
         * @param {Array<'low' | 'medium' | 'high'>} [complianceScore] Filter by compliance score. Accepts multiple values.
         * @param {string} [maxCreatedAt] Return compliance checks created on or before this datetime (ISO 8601).
         * @param {string} [maxFinishedAt] Return compliance checks finished on or before this datetime (ISO 8601).
         * @param {string} [minCreatedAt] Return compliance checks created on or after this datetime (ISO 8601).
         * @param {string} [minFinishedAt] Return compliance checks finished on or after this datetime (ISO 8601).
         * @param {boolean} [monitoringEnabled] Filter by whether ongoing monitoring is enabled.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search compliance checks by company name.
         * @param {'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count'} [sorting] Field to sort results by.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort order. descending is newest/highest first, ascending is oldest/lowest first.
         * @param {Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>} [status] Filter by compliance check status.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceList(closed?: boolean, complianceScore?: Array<'low' | 'medium' | 'high'>, maxCreatedAt?: string, maxFinishedAt?: string, minCreatedAt?: string, minFinishedAt?: string, monitoringEnabled?: boolean, nextKey?: string, query?: string, sorting?: 'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count', sortingOrder?: 'ascending' | 'descending', status?: Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>, options?: any): AxiosPromise<PaginatedComplianceCheckList> {
            return localVarFp.complianceList(closed, complianceScore, maxCreatedAt, maxFinishedAt, minCreatedAt, minFinishedAt, monitoringEnabled, nextKey, query, sorting, sortingOrder, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Set `monitoring_enabled` on many checks; reconcile their entities.
         * @param {PatchedComplianceCheckMonitoringBulkRequest} [patchedComplianceCheckMonitoringBulkRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringBatchUpdatePartialUpdate(patchedComplianceCheckMonitoringBulkRequest?: PatchedComplianceCheckMonitoringBulkRequest, options?: any): AxiosPromise<void> {
            return localVarFp.complianceMonitoringBatchUpdatePartialUpdate(patchedComplianceCheckMonitoringBulkRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Add/remove the current user as a monitoring subscriber on many checks.
         * @param {PatchedComplianceCheckSubscriptionBulkRequest} [patchedComplianceCheckSubscriptionBulkRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringSubscriptionBatchUpdatePartialUpdate(patchedComplianceCheckSubscriptionBulkRequest?: PatchedComplianceCheckSubscriptionBulkRequest, options?: any): AxiosPromise<void> {
            return localVarFp.complianceMonitoringSubscriptionBatchUpdatePartialUpdate(patchedComplianceCheckSubscriptionBulkRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Subscribe the current user (allowed even without monitoring enabled).
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringSubscriptionCreate(complianceCheckId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceMonitoringSubscriptionCreate(complianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Unsubscribe the current user.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceMonitoringSubscriptionDestroy(complianceCheckId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceMonitoringSubscriptionDestroy(complianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {PatchedComplianceCheckRequest} [patchedComplianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        compliancePartialUpdate(complianceCheckId: string, patchedComplianceCheckRequest?: PatchedComplianceCheckRequest, options?: any): AxiosPromise<ComplianceCheck> {
            return localVarFp.compliancePartialUpdate(complianceCheckId, patchedComplianceCheckRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Return the report for the requested scope and period.
         * @param {string} [anchorDate] Day the period is anchored on. Defaults to today.
         * @param {'month' | 'quarter' | 'ytd' | 'year'} [period] * &#x60;month&#x60; - Month * &#x60;quarter&#x60; - Quarter * &#x60;ytd&#x60; - Ytd * &#x60;year&#x60; - Year
         * @param {Array<string>} [portfolioId] 
         * @param {'me' | 'organization'} [scope] * &#x60;me&#x60; - Me * &#x60;organization&#x60; - Organization
         * @param {Array<string>} [userId] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceReportsRetrieve(anchorDate?: string, period?: 'month' | 'quarter' | 'ytd' | 'year', portfolioId?: Array<string>, scope?: 'me' | 'organization', userId?: Array<string>, options?: any): AxiosPromise<ComplianceReport> {
            return localVarFp.complianceReportsRetrieve(anchorDate, period, portfolioId, scope, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Update entity results in batch.
         * @param {string} complianceCheckId 
         * @param {Array<ComplianceEntityResultBatchRequest>} complianceEntityResultBatchRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsBatchUpdatePartialUpdate(complianceCheckId: string, complianceEntityResultBatchRequest: Array<ComplianceEntityResultBatchRequest>, options?: any): AxiosPromise<Array<ComplianceEntityResultBatch>> {
            return localVarFp.complianceResultsBatchUpdatePartialUpdate(complianceCheckId, complianceEntityResultBatchRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
         * @param {string} complianceCheckId 
         * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsCreate(complianceCheckId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: any): AxiosPromise<void> {
            return localVarFp.complianceResultsCreate(complianceCheckId, complianceEntityResultSelectAllBinderRequest, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, options).then((request) => request(axios, basePath));
        },
        /**
         * Paginated list of compliance entity results (`{next_key,total_results,results}`).
         * @param {string} complianceCheckId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {'confidence' | 'found_at'} [sorting] 
         * @param {'ascending' | 'descending'} [sortingOrder] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsList(complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: any): AxiosPromise<PaginatedComplianceEntityResultList> {
            return localVarFp.complianceResultsList(complianceCheckId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, nextKey, pageSize, query, rating, resultType, sorting, sortingOrder, sourceType, options).then((request) => request(axios, basePath));
        },
        /**
         * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
         * @param {string} complianceCheckId 
         * @param {boolean} [hasBinder] 
         * @param {boolean} [hasComment] 
         * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
         * @param {number} [maxConfidence] 
         * @param {number} [minConfidence] 
         * @param {string} [query] Search by result name or body text.
         * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
         * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
         * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
         * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsPartialUpdate(complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options?: any): AxiosPromise<ComplianceEntityResultSelectAllRatingResponse> {
            return localVarFp.complianceResultsPartialUpdate(complianceCheckId, hasBinder, hasComment, includeAutomatedFalsePositives, maxConfidence, minConfidence, query, rating, resultType, sourceType, patchedComplianceEntityResultSelectAllRatingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsPartialUpdate2(complianceCheckId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options?: any): AxiosPromise<ComplianceEntityResult> {
            return localVarFp.complianceResultsPartialUpdate2(complianceCheckId, complianceEntityResultId, patchedComplianceEntityResultRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsRetrieve(complianceCheckId: string, complianceEntityResultId: string, options?: any): AxiosPromise<ComplianceEntityResult> {
            return localVarFp.complianceResultsRetrieve(complianceCheckId, complianceEntityResultId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve or update a compliance entity result.
         * @param {string} complianceCheckId 
         * @param {string} complianceEntityResultId 
         * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceResultsUpdate(complianceCheckId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options?: any): AxiosPromise<ComplianceEntityResult> {
            return localVarFp.complianceResultsUpdate(complianceCheckId, complianceEntityResultId, complianceEntityResultRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceRetrieve(complianceCheckId: string, options?: any): AxiosPromise<ComplianceCheck> {
            return localVarFp.complianceRetrieve(complianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Return sanctions providers with their live listing stats.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSanctionsProvidersList(options?: any): AxiosPromise<Array<SanctionProvider>> {
            return localVarFp.complianceSanctionsProvidersList(options).then((request) => request(axios, basePath));
        },
        /**
         * Queue an Excel export of the changelog for the active profile.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsChangelogExportCreate(options?: any): AxiosPromise<void> {
            return localVarFp.complianceSettingsChangelogExportCreate(options).then((request) => request(axios, basePath));
        },
        /**
         * List the active profile\'s compliance settings change history (read-only).  Optional ``source`` / ``field`` query params filter the list; the response carries ``source_counts`` for the filter chips. Ordered latest change first.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsChangelogList(nextKey?: string, options?: any): AxiosPromise<PaginatedComplianceSettingsChangeLogList> {
            return localVarFp.complianceSettingsChangelogList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Create one or many industry risk overrides.
         * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskCreate(profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options?: any): AxiosPromise<ProfileIndustryRiskOverride> {
            return localVarFp.complianceSettingsIndustryRiskCreate(profileIndustryRiskOverrideRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskDestroy(overrideId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceSettingsIndustryRiskDestroy(overrideId, options).then((request) => request(axios, basePath));
        },
        /**
         * List industry risk overrides, optionally filtered by ``query``.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskList(nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedProfileIndustryRiskOverrideList> {
            return localVarFp.complianceSettingsIndustryRiskList(nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {PatchedProfileIndustryRiskOverrideRequest} [patchedProfileIndustryRiskOverrideRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskPartialUpdate(overrideId: string, patchedProfileIndustryRiskOverrideRequest?: PatchedProfileIndustryRiskOverrideRequest, options?: any): AxiosPromise<ProfileIndustryRiskOverride> {
            return localVarFp.complianceSettingsIndustryRiskPartialUpdate(overrideId, patchedProfileIndustryRiskOverrideRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskRetrieve(overrideId: string, options?: any): AxiosPromise<ProfileIndustryRiskOverride> {
            return localVarFp.complianceSettingsIndustryRiskRetrieve(overrideId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy an industry risk override.
         * @param {string} overrideId 
         * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsIndustryRiskUpdate(overrideId: string, profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options?: any): AxiosPromise<ProfileIndustryRiskOverride> {
            return localVarFp.complianceSettingsIndustryRiskUpdate(overrideId, profileIndustryRiskOverrideRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {PatchedComplianceSettingsRequest} [patchedComplianceSettingsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsPartialUpdate(patchedComplianceSettingsRequest?: PatchedComplianceSettingsRequest, options?: any): AxiosPromise<ComplianceSettings> {
            return localVarFp.complianceSettingsPartialUpdate(patchedComplianceSettingsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsRetrieve(options?: any): AxiosPromise<ComplianceSettings> {
            return localVarFp.complianceSettingsRetrieve(options).then((request) => request(axios, basePath));
        },
        /**
         * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
         * @param {ComplianceSettingsRequest} [complianceSettingsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsUpdate(complianceSettingsRequest?: ComplianceSettingsRequest, options?: any): AxiosPromise<ComplianceSettings> {
            return localVarFp.complianceSettingsUpdate(complianceSettingsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create world (country) risk overrides for the active profile.
         * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskCreate(profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options?: any): AxiosPromise<ProfileCountryRiskOverride> {
            return localVarFp.complianceSettingsWorldRiskCreate(profileCountryRiskOverrideRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskDestroy(overrideId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceSettingsWorldRiskDestroy(overrideId, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create world (country) risk overrides for the active profile.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskList(nextKey?: string, options?: any): AxiosPromise<PaginatedProfileCountryRiskOverrideList> {
            return localVarFp.complianceSettingsWorldRiskList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {PatchedProfileCountryRiskOverrideRequest} [patchedProfileCountryRiskOverrideRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskPartialUpdate(overrideId: string, patchedProfileCountryRiskOverrideRequest?: PatchedProfileCountryRiskOverrideRequest, options?: any): AxiosPromise<ProfileCountryRiskOverride> {
            return localVarFp.complianceSettingsWorldRiskPartialUpdate(overrideId, patchedProfileCountryRiskOverrideRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskRetrieve(overrideId: string, options?: any): AxiosPromise<ProfileCountryRiskOverride> {
            return localVarFp.complianceSettingsWorldRiskRetrieve(overrideId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update, and destroy a world (country) risk override.
         * @param {string} overrideId 
         * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceSettingsWorldRiskUpdate(overrideId: string, profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options?: any): AxiosPromise<ProfileCountryRiskOverride> {
            return localVarFp.complianceSettingsWorldRiskUpdate(overrideId, profileCountryRiskOverrideRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Start a compliance check.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckStartRequest} [complianceCheckStartRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceStartCreate(complianceCheckId: string, complianceCheckStartRequest?: ComplianceCheckStartRequest, options?: any): AxiosPromise<void> {
            return localVarFp.complianceStartCreate(complianceCheckId, complianceCheckStartRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Start a compliance check.
         * @param {string} complianceCheckId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceStartDirectorsSearchCreate(complianceCheckId: string, options?: any): AxiosPromise<void> {
            return localVarFp.complianceStartDirectorsSearchCreate(complianceCheckId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve the results of a compliance check based on its id.
         * @param {string} complianceCheckId 
         * @param {ComplianceCheckRequest} [complianceCheckRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        complianceUpdate(complianceCheckId: string, complianceCheckRequest?: ComplianceCheckRequest, options?: any): AxiosPromise<ComplianceCheck> {
            return localVarFp.complianceUpdate(complianceCheckId, complianceCheckRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ComplianceApi - interface
 * @export
 * @interface ComplianceApi
 */
export interface ComplianceApiInterface {
    /**
     * Add an add/exclude keyword entry to the active profile\'s set.
     * @param {KeywordSetEntryCreateRequest} keywordSetEntryCreateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceAdverseMediaKeywordsCreate(keywordSetEntryCreateRequest: KeywordSetEntryCreateRequest, options?: AxiosRequestConfig): AxiosPromise<KeywordSetEntry>;

    /**
     * Delete one add/exclude entry from the active profile\'s keyword set.
     * @param {string} entryId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceAdverseMediaKeywordsDestroy(entryId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Delete every entry in the active profile\'s keyword set.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceAdverseMediaKeywordsResetCreate(options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Return the effective keyword list for a language plus per-language counts.  ``language`` is required: the full list is too large to return unfiltered, so the frontend fetches one language and uses ``language_counts`` for the rest.
     * @param {string} language 
     * @param {string} [entityType] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceAdverseMediaKeywordsRetrieve(language: string, entityType?: string, options?: AxiosRequestConfig): AxiosPromise<EffectiveKeywordList>;

    /**
     * Create a new batch compliance check.  Supports either a list of company external_ids or a portfolio_id to derive companies. No processing/sending logic is executed here; this only creates the batch record.
     * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceBatchesCreate(batchComplianceCheckRequest?: BatchComplianceCheckRequest, options?: AxiosRequestConfig): AxiosPromise<BatchComplianceCheck>;

    /**
     * Retrieve, update, and destroy a batch compliance check.
     * @param {string} batchComplianceCheckId 
     * @param {PatchedBatchComplianceCheckRequest} [patchedBatchComplianceCheckRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceBatchesPartialUpdate(batchComplianceCheckId: string, patchedBatchComplianceCheckRequest?: PatchedBatchComplianceCheckRequest, options?: AxiosRequestConfig): AxiosPromise<BatchComplianceCheck>;

    /**
     * Retrieve, update, and destroy a batch compliance check.
     * @param {string} batchComplianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceBatchesRetrieve(batchComplianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<BatchComplianceCheck>;

    /**
     * Start a compliance check.
     * @param {string} batchComplianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceBatchesStartCreate(batchComplianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Retrieve, update, and destroy a batch compliance check.
     * @param {string} batchComplianceCheckId 
     * @param {BatchComplianceCheckRequest} [batchComplianceCheckRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceBatchesUpdate(batchComplianceCheckId: string, batchComplianceCheckRequest?: BatchComplianceCheckRequest, options?: AxiosRequestConfig): AxiosPromise<BatchComplianceCheck>;

    /**
     * Create a new compliance check.
     * @param {ComplianceCheckRequestRequest} [complianceCheckRequestRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceCreate(complianceCheckRequestRequest?: ComplianceCheckRequestRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceCheck>;

    /**
     * Return enforcement providers with their live listing stats.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEnforcementProvidersList(options?: AxiosRequestConfig): AxiosPromise<Array<EnforcementProvider>>;

    /**
     * List and create compliance entity aliases.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesAliasesCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityAlias>;

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityAliasId 
     * @param {string} complianceEntityId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesAliasesDestroy(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List and create compliance entity aliases.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesAliasesList(complianceCheckId: string, complianceEntityId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedComplianceEntityAliasList>;

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityAliasId 
     * @param {string} complianceEntityId 
     * @param {PatchedComplianceEntityAliasRequest} [patchedComplianceEntityAliasRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesAliasesPartialUpdate(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, patchedComplianceEntityAliasRequest?: PatchedComplianceEntityAliasRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityAlias>;

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityAliasId 
     * @param {string} complianceEntityId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesAliasesRetrieve(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityAlias>;

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityAliasId 
     * @param {string} complianceEntityId 
     * @param {ComplianceEntityAliasRequest} complianceEntityAliasRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesAliasesUpdate(complianceCheckId: string, complianceEntityAliasId: string, complianceEntityId: string, complianceEntityAliasRequest: ComplianceEntityAliasRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityAlias>;

    /**
     * Batch update compliance entities.
     * @param {string} complianceCheckId 
     * @param {Array<ComplianceEntityBatchUpdateRequest>} complianceEntityBatchUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesBatchUpdatePartialUpdate(complianceCheckId: string, complianceEntityBatchUpdateRequest: Array<ComplianceEntityBatchUpdateRequest>, options?: AxiosRequestConfig): AxiosPromise<{ [key: string]: any; }>;

    /**
     * Retrieve all compliance entities associated with a compliance check.
     * @param {string} complianceCheckId 
     * @param {ComplianceEntityRequest} complianceEntityRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesCreate(complianceCheckId: string, complianceEntityRequest: ComplianceEntityRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntity>;

    /**
     * Retrieve or update a compliance entity.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesDestroy(complianceCheckId: string, complianceEntityId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List and create compliance entity identifiers.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesIdentifiersCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityIdentifier>;

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityIdentifierId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesIdentifiersDestroy(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List and create compliance entity identifiers.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesIdentifiersList(complianceCheckId: string, complianceEntityId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedComplianceEntityIdentifierList>;

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityIdentifierId 
     * @param {PatchedComplianceEntityIdentifierRequest} [patchedComplianceEntityIdentifierRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesIdentifiersPartialUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, patchedComplianceEntityIdentifierRequest?: PatchedComplianceEntityIdentifierRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityIdentifier>;

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityIdentifierId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesIdentifiersRetrieve(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityIdentifier>;

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityIdentifierId 
     * @param {ComplianceEntityIdentifierRequest} complianceEntityIdentifierRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesIdentifiersUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityIdentifierId: string, complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityIdentifier>;

    /**
     * Retrieve all compliance entities associated with a compliance check.
     * @param {string} complianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesList(complianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<Array<ComplianceEntity>>;

    /**
     * Retrieve or update a compliance entity.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {PatchedComplianceEntityRequest} [patchedComplianceEntityRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesPartialUpdate(complianceCheckId: string, complianceEntityId: string, patchedComplianceEntityRequest?: PatchedComplianceEntityRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntity>;

    /**
     * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
     * @param {boolean} [hasBinder] 
     * @param {boolean} [hasComment] 
     * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
     * @param {number} [maxConfidence] 
     * @param {number} [minConfidence] 
     * @param {string} [query] Search by result name or body text.
     * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
     * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
     * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesResultsCreate(complianceCheckId: string, complianceEntityId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Paginated list of compliance entity results (`{next_key,total_results,results}`).
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {boolean} [hasBinder] 
     * @param {boolean} [hasComment] 
     * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
     * @param {number} [maxConfidence] 
     * @param {number} [minConfidence] 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
     * @param {string} [query] Search by result name or body text.
     * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
     * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
     * @param {'confidence' | 'found_at'} [sorting] 
     * @param {'ascending' | 'descending'} [sortingOrder] 
     * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesResultsList(complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): AxiosPromise<PaginatedComplianceEntityResultList>;

    /**
     * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {boolean} [hasBinder] 
     * @param {boolean} [hasComment] 
     * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
     * @param {number} [maxConfidence] 
     * @param {number} [minConfidence] 
     * @param {string} [query] Search by result name or body text.
     * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
     * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
     * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
     * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesResultsPartialUpdate(complianceCheckId: string, complianceEntityId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResultSelectAllRatingResponse>;

    /**
     * Retrieve or update a compliance entity result.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityResultId 
     * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesResultsPartialUpdate2(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResult>;

    /**
     * Retrieve or update a compliance entity result.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityResultId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesResultsRetrieve(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResult>;

    /**
     * Retrieve or update a compliance entity result.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {string} complianceEntityResultId 
     * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesResultsUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResult>;

    /**
     * Retrieve or update a compliance entity.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesRetrieve(complianceCheckId: string, complianceEntityId: string, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntity>;

    /**
     * Retrieve or update a compliance entity.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityId 
     * @param {ComplianceEntityRequest} complianceEntityRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceEntitiesUpdate(complianceCheckId: string, complianceEntityId: string, complianceEntityRequest: ComplianceEntityRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntity>;

    /**
     * Start a compliance check export with optional audit-trail filters.
     * @param {string} complianceCheckId 
     * @param {ComplianceCheckExportRequest} [complianceCheckExportRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceExportCreate(complianceCheckId: string, complianceCheckExportRequest?: ComplianceCheckExportRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceCheckExport>;

    /**
     * List compliance checks for the current user\'s profile.
     * @param {boolean} [closed] Filter by whether the check was closed by a user.
     * @param {Array<'low' | 'medium' | 'high'>} [complianceScore] Filter by compliance score. Accepts multiple values.
     * @param {string} [maxCreatedAt] Return compliance checks created on or before this datetime (ISO 8601).
     * @param {string} [maxFinishedAt] Return compliance checks finished on or before this datetime (ISO 8601).
     * @param {string} [minCreatedAt] Return compliance checks created on or after this datetime (ISO 8601).
     * @param {string} [minFinishedAt] Return compliance checks finished on or after this datetime (ISO 8601).
     * @param {boolean} [monitoringEnabled] Filter by whether ongoing monitoring is enabled.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Search compliance checks by company name.
     * @param {'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count'} [sorting] Field to sort results by.
     * @param {'ascending' | 'descending'} [sortingOrder] Sort order. descending is newest/highest first, ascending is oldest/lowest first.
     * @param {Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>} [status] Filter by compliance check status.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceList(closed?: boolean, complianceScore?: Array<'low' | 'medium' | 'high'>, maxCreatedAt?: string, maxFinishedAt?: string, minCreatedAt?: string, minFinishedAt?: string, monitoringEnabled?: boolean, nextKey?: string, query?: string, sorting?: 'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count', sortingOrder?: 'ascending' | 'descending', status?: Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>, options?: AxiosRequestConfig): AxiosPromise<PaginatedComplianceCheckList>;

    /**
     * Set `monitoring_enabled` on many checks; reconcile their entities.
     * @param {PatchedComplianceCheckMonitoringBulkRequest} [patchedComplianceCheckMonitoringBulkRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceMonitoringBatchUpdatePartialUpdate(patchedComplianceCheckMonitoringBulkRequest?: PatchedComplianceCheckMonitoringBulkRequest, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Add/remove the current user as a monitoring subscriber on many checks.
     * @param {PatchedComplianceCheckSubscriptionBulkRequest} [patchedComplianceCheckSubscriptionBulkRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceMonitoringSubscriptionBatchUpdatePartialUpdate(patchedComplianceCheckSubscriptionBulkRequest?: PatchedComplianceCheckSubscriptionBulkRequest, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Subscribe the current user (allowed even without monitoring enabled).
     * @param {string} complianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceMonitoringSubscriptionCreate(complianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Unsubscribe the current user.
     * @param {string} complianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceMonitoringSubscriptionDestroy(complianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Retrieve the results of a compliance check based on its id.
     * @param {string} complianceCheckId 
     * @param {PatchedComplianceCheckRequest} [patchedComplianceCheckRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    compliancePartialUpdate(complianceCheckId: string, patchedComplianceCheckRequest?: PatchedComplianceCheckRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceCheck>;

    /**
     * Return the report for the requested scope and period.
     * @param {string} [anchorDate] Day the period is anchored on. Defaults to today.
     * @param {'month' | 'quarter' | 'ytd' | 'year'} [period] * &#x60;month&#x60; - Month * &#x60;quarter&#x60; - Quarter * &#x60;ytd&#x60; - Ytd * &#x60;year&#x60; - Year
     * @param {Array<string>} [portfolioId] 
     * @param {'me' | 'organization'} [scope] * &#x60;me&#x60; - Me * &#x60;organization&#x60; - Organization
     * @param {Array<string>} [userId] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceReportsRetrieve(anchorDate?: string, period?: 'month' | 'quarter' | 'ytd' | 'year', portfolioId?: Array<string>, scope?: 'me' | 'organization', userId?: Array<string>, options?: AxiosRequestConfig): AxiosPromise<ComplianceReport>;

    /**
     * Update entity results in batch.
     * @param {string} complianceCheckId 
     * @param {Array<ComplianceEntityResultBatchRequest>} complianceEntityResultBatchRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsBatchUpdatePartialUpdate(complianceCheckId: string, complianceEntityResultBatchRequest: Array<ComplianceEntityResultBatchRequest>, options?: AxiosRequestConfig): AxiosPromise<Array<ComplianceEntityResultBatch>>;

    /**
     * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
     * @param {string} complianceCheckId 
     * @param {ComplianceEntityResultSelectAllBinderRequest} complianceEntityResultSelectAllBinderRequest 
     * @param {boolean} [hasBinder] 
     * @param {boolean} [hasComment] 
     * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
     * @param {number} [maxConfidence] 
     * @param {number} [minConfidence] 
     * @param {string} [query] Search by result name or body text.
     * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
     * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
     * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsCreate(complianceCheckId: string, complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Paginated list of compliance entity results (`{next_key,total_results,results}`).
     * @param {string} complianceCheckId 
     * @param {boolean} [hasBinder] 
     * @param {boolean} [hasComment] 
     * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
     * @param {number} [maxConfidence] 
     * @param {number} [minConfidence] 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {number} [pageSize] Number of results per page. Defaults to 50, capped at 200.
     * @param {string} [query] Search by result name or body text.
     * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
     * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
     * @param {'confidence' | 'found_at'} [sorting] 
     * @param {'ascending' | 'descending'} [sortingOrder] 
     * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsList(complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, nextKey?: string, pageSize?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sorting?: 'confidence' | 'found_at', sortingOrder?: 'ascending' | 'descending', sourceType?: Array<'enforcement' | 'icij' | 'news'>, options?: AxiosRequestConfig): AxiosPromise<PaginatedComplianceEntityResultList>;

    /**
     * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
     * @param {string} complianceCheckId 
     * @param {boolean} [hasBinder] 
     * @param {boolean} [hasComment] 
     * @param {boolean} [includeAutomatedFalsePositives] Force-include results marked as automated false positives. Defaults to false.
     * @param {number} [maxConfidence] 
     * @param {number} [minConfidence] 
     * @param {string} [query] Search by result name or body text.
     * @param {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>} [rating] Filter by the user\&#39;s false-positive rating.
     * @param {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>} [resultType] 
     * @param {Array<'enforcement' | 'icij' | 'news'>} [sourceType] Filter adverse media results by sub-source (news / icij / enforcement).
     * @param {PatchedComplianceEntityResultSelectAllRatingRequest} [patchedComplianceEntityResultSelectAllRatingRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsPartialUpdate(complianceCheckId: string, hasBinder?: boolean, hasComment?: boolean, includeAutomatedFalsePositives?: boolean, maxConfidence?: number, minConfidence?: number, query?: string, rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>, resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>, sourceType?: Array<'enforcement' | 'icij' | 'news'>, patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResultSelectAllRatingResponse>;

    /**
     * Retrieve or update a compliance entity result.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityResultId 
     * @param {PatchedComplianceEntityResultRequest} [patchedComplianceEntityResultRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsPartialUpdate2(complianceCheckId: string, complianceEntityResultId: string, patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResult>;

    /**
     * Retrieve or update a compliance entity result.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityResultId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsRetrieve(complianceCheckId: string, complianceEntityResultId: string, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResult>;

    /**
     * Retrieve or update a compliance entity result.
     * @param {string} complianceCheckId 
     * @param {string} complianceEntityResultId 
     * @param {ComplianceEntityResultRequest} complianceEntityResultRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceResultsUpdate(complianceCheckId: string, complianceEntityResultId: string, complianceEntityResultRequest: ComplianceEntityResultRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceEntityResult>;

    /**
     * Retrieve the results of a compliance check based on its id.
     * @param {string} complianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceRetrieve(complianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<ComplianceCheck>;

    /**
     * Return sanctions providers with their live listing stats.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSanctionsProvidersList(options?: AxiosRequestConfig): AxiosPromise<Array<SanctionProvider>>;

    /**
     * Queue an Excel export of the changelog for the active profile.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsChangelogExportCreate(options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List the active profile\'s compliance settings change history (read-only).  Optional ``source`` / ``field`` query params filter the list; the response carries ``source_counts`` for the filter chips. Ordered latest change first.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsChangelogList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedComplianceSettingsChangeLogList>;

    /**
     * Create one or many industry risk overrides.
     * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsIndustryRiskCreate(profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileIndustryRiskOverride>;

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {string} overrideId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsIndustryRiskDestroy(overrideId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List industry risk overrides, optionally filtered by ``query``.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsIndustryRiskList(nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedProfileIndustryRiskOverrideList>;

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {string} overrideId 
     * @param {PatchedProfileIndustryRiskOverrideRequest} [patchedProfileIndustryRiskOverrideRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsIndustryRiskPartialUpdate(overrideId: string, patchedProfileIndustryRiskOverrideRequest?: PatchedProfileIndustryRiskOverrideRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileIndustryRiskOverride>;

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {string} overrideId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsIndustryRiskRetrieve(overrideId: string, options?: AxiosRequestConfig): AxiosPromise<ProfileIndustryRiskOverride>;

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {string} overrideId 
     * @param {ProfileIndustryRiskOverrideRequest} profileIndustryRiskOverrideRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsIndustryRiskUpdate(overrideId: string, profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileIndustryRiskOverride>;

    /**
     * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
     * @param {PatchedComplianceSettingsRequest} [patchedComplianceSettingsRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsPartialUpdate(patchedComplianceSettingsRequest?: PatchedComplianceSettingsRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceSettings>;

    /**
     * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsRetrieve(options?: AxiosRequestConfig): AxiosPromise<ComplianceSettings>;

    /**
     * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
     * @param {ComplianceSettingsRequest} [complianceSettingsRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsUpdate(complianceSettingsRequest?: ComplianceSettingsRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceSettings>;

    /**
     * List and create world (country) risk overrides for the active profile.
     * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsWorldRiskCreate(profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileCountryRiskOverride>;

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {string} overrideId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsWorldRiskDestroy(overrideId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List and create world (country) risk overrides for the active profile.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsWorldRiskList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedProfileCountryRiskOverrideList>;

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {string} overrideId 
     * @param {PatchedProfileCountryRiskOverrideRequest} [patchedProfileCountryRiskOverrideRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsWorldRiskPartialUpdate(overrideId: string, patchedProfileCountryRiskOverrideRequest?: PatchedProfileCountryRiskOverrideRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileCountryRiskOverride>;

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {string} overrideId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsWorldRiskRetrieve(overrideId: string, options?: AxiosRequestConfig): AxiosPromise<ProfileCountryRiskOverride>;

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {string} overrideId 
     * @param {ProfileCountryRiskOverrideRequest} profileCountryRiskOverrideRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceSettingsWorldRiskUpdate(overrideId: string, profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileCountryRiskOverride>;

    /**
     * Start a compliance check.
     * @param {string} complianceCheckId 
     * @param {ComplianceCheckStartRequest} [complianceCheckStartRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceStartCreate(complianceCheckId: string, complianceCheckStartRequest?: ComplianceCheckStartRequest, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Start a compliance check.
     * @param {string} complianceCheckId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceStartDirectorsSearchCreate(complianceCheckId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Retrieve the results of a compliance check based on its id.
     * @param {string} complianceCheckId 
     * @param {ComplianceCheckRequest} [complianceCheckRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApiInterface
     */
    complianceUpdate(complianceCheckId: string, complianceCheckRequest?: ComplianceCheckRequest, options?: AxiosRequestConfig): AxiosPromise<ComplianceCheck>;

}

/**
 * Request parameters for complianceAdverseMediaKeywordsCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceAdverseMediaKeywordsCreateRequest
 */
export interface ComplianceApiComplianceAdverseMediaKeywordsCreateRequest {
    /**
     * 
     * @type {KeywordSetEntryCreateRequest}
     * @memberof ComplianceApiComplianceAdverseMediaKeywordsCreate
     */
    readonly keywordSetEntryCreateRequest: KeywordSetEntryCreateRequest
}

/**
 * Request parameters for complianceAdverseMediaKeywordsDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceAdverseMediaKeywordsDestroyRequest
 */
export interface ComplianceApiComplianceAdverseMediaKeywordsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceAdverseMediaKeywordsDestroy
     */
    readonly entryId: string
}

/**
 * Request parameters for complianceAdverseMediaKeywordsRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceAdverseMediaKeywordsRetrieveRequest
 */
export interface ComplianceApiComplianceAdverseMediaKeywordsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceAdverseMediaKeywordsRetrieve
     */
    readonly language: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceAdverseMediaKeywordsRetrieve
     */
    readonly entityType?: string
}

/**
 * Request parameters for complianceBatchesCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceBatchesCreateRequest
 */
export interface ComplianceApiComplianceBatchesCreateRequest {
    /**
     * 
     * @type {BatchComplianceCheckRequest}
     * @memberof ComplianceApiComplianceBatchesCreate
     */
    readonly batchComplianceCheckRequest?: BatchComplianceCheckRequest
}

/**
 * Request parameters for complianceBatchesPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceBatchesPartialUpdateRequest
 */
export interface ComplianceApiComplianceBatchesPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceBatchesPartialUpdate
     */
    readonly batchComplianceCheckId: string

    /**
     * 
     * @type {PatchedBatchComplianceCheckRequest}
     * @memberof ComplianceApiComplianceBatchesPartialUpdate
     */
    readonly patchedBatchComplianceCheckRequest?: PatchedBatchComplianceCheckRequest
}

/**
 * Request parameters for complianceBatchesRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceBatchesRetrieveRequest
 */
export interface ComplianceApiComplianceBatchesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceBatchesRetrieve
     */
    readonly batchComplianceCheckId: string
}

/**
 * Request parameters for complianceBatchesStartCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceBatchesStartCreateRequest
 */
export interface ComplianceApiComplianceBatchesStartCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceBatchesStartCreate
     */
    readonly batchComplianceCheckId: string
}

/**
 * Request parameters for complianceBatchesUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceBatchesUpdateRequest
 */
export interface ComplianceApiComplianceBatchesUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceBatchesUpdate
     */
    readonly batchComplianceCheckId: string

    /**
     * 
     * @type {BatchComplianceCheckRequest}
     * @memberof ComplianceApiComplianceBatchesUpdate
     */
    readonly batchComplianceCheckRequest?: BatchComplianceCheckRequest
}

/**
 * Request parameters for complianceCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceCreateRequest
 */
export interface ComplianceApiComplianceCreateRequest {
    /**
     * 
     * @type {ComplianceCheckRequestRequest}
     * @memberof ComplianceApiComplianceCreate
     */
    readonly complianceCheckRequestRequest?: ComplianceCheckRequestRequest
}

/**
 * Request parameters for complianceEntitiesAliasesCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesAliasesCreateRequest
 */
export interface ComplianceApiComplianceEntitiesAliasesCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesCreate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {ComplianceEntityAliasRequest}
     * @memberof ComplianceApiComplianceEntitiesAliasesCreate
     */
    readonly complianceEntityAliasRequest: ComplianceEntityAliasRequest
}

/**
 * Request parameters for complianceEntitiesAliasesDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesAliasesDestroyRequest
 */
export interface ComplianceApiComplianceEntitiesAliasesDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesDestroy
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesDestroy
     */
    readonly complianceEntityAliasId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesDestroy
     */
    readonly complianceEntityId: string
}

/**
 * Request parameters for complianceEntitiesAliasesList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesAliasesListRequest
 */
export interface ComplianceApiComplianceEntitiesAliasesListRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesList
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesList
     */
    readonly complianceEntityId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for complianceEntitiesAliasesPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesAliasesPartialUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesAliasesPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesPartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesPartialUpdate
     */
    readonly complianceEntityAliasId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesPartialUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {PatchedComplianceEntityAliasRequest}
     * @memberof ComplianceApiComplianceEntitiesAliasesPartialUpdate
     */
    readonly patchedComplianceEntityAliasRequest?: PatchedComplianceEntityAliasRequest
}

/**
 * Request parameters for complianceEntitiesAliasesRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesAliasesRetrieveRequest
 */
export interface ComplianceApiComplianceEntitiesAliasesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesRetrieve
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesRetrieve
     */
    readonly complianceEntityAliasId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesRetrieve
     */
    readonly complianceEntityId: string
}

/**
 * Request parameters for complianceEntitiesAliasesUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesAliasesUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesAliasesUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesUpdate
     */
    readonly complianceEntityAliasId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesAliasesUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {ComplianceEntityAliasRequest}
     * @memberof ComplianceApiComplianceEntitiesAliasesUpdate
     */
    readonly complianceEntityAliasRequest: ComplianceEntityAliasRequest
}

/**
 * Request parameters for complianceEntitiesBatchUpdatePartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesBatchUpdatePartialUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesBatchUpdatePartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesBatchUpdatePartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {Array<ComplianceEntityBatchUpdateRequest>}
     * @memberof ComplianceApiComplianceEntitiesBatchUpdatePartialUpdate
     */
    readonly complianceEntityBatchUpdateRequest: Array<ComplianceEntityBatchUpdateRequest>
}

/**
 * Request parameters for complianceEntitiesCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesCreateRequest
 */
export interface ComplianceApiComplianceEntitiesCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {ComplianceEntityRequest}
     * @memberof ComplianceApiComplianceEntitiesCreate
     */
    readonly complianceEntityRequest: ComplianceEntityRequest
}

/**
 * Request parameters for complianceEntitiesDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesDestroyRequest
 */
export interface ComplianceApiComplianceEntitiesDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesDestroy
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesDestroy
     */
    readonly complianceEntityId: string
}

/**
 * Request parameters for complianceEntitiesIdentifiersCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesIdentifiersCreateRequest
 */
export interface ComplianceApiComplianceEntitiesIdentifiersCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersCreate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {ComplianceEntityIdentifierRequest}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersCreate
     */
    readonly complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest
}

/**
 * Request parameters for complianceEntitiesIdentifiersDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesIdentifiersDestroyRequest
 */
export interface ComplianceApiComplianceEntitiesIdentifiersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersDestroy
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersDestroy
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersDestroy
     */
    readonly complianceEntityIdentifierId: string
}

/**
 * Request parameters for complianceEntitiesIdentifiersList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesIdentifiersListRequest
 */
export interface ComplianceApiComplianceEntitiesIdentifiersListRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersList
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersList
     */
    readonly complianceEntityId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for complianceEntitiesIdentifiersPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesIdentifiersPartialUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesIdentifiersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersPartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersPartialUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersPartialUpdate
     */
    readonly complianceEntityIdentifierId: string

    /**
     * 
     * @type {PatchedComplianceEntityIdentifierRequest}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersPartialUpdate
     */
    readonly patchedComplianceEntityIdentifierRequest?: PatchedComplianceEntityIdentifierRequest
}

/**
 * Request parameters for complianceEntitiesIdentifiersRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesIdentifiersRetrieveRequest
 */
export interface ComplianceApiComplianceEntitiesIdentifiersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersRetrieve
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersRetrieve
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersRetrieve
     */
    readonly complianceEntityIdentifierId: string
}

/**
 * Request parameters for complianceEntitiesIdentifiersUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesIdentifiersUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesIdentifiersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersUpdate
     */
    readonly complianceEntityIdentifierId: string

    /**
     * 
     * @type {ComplianceEntityIdentifierRequest}
     * @memberof ComplianceApiComplianceEntitiesIdentifiersUpdate
     */
    readonly complianceEntityIdentifierRequest: ComplianceEntityIdentifierRequest
}

/**
 * Request parameters for complianceEntitiesList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesListRequest
 */
export interface ComplianceApiComplianceEntitiesListRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesList
     */
    readonly complianceCheckId: string
}

/**
 * Request parameters for complianceEntitiesPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesPartialUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesPartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesPartialUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {PatchedComplianceEntityRequest}
     * @memberof ComplianceApiComplianceEntitiesPartialUpdate
     */
    readonly patchedComplianceEntityRequest?: PatchedComplianceEntityRequest
}

/**
 * Request parameters for complianceEntitiesResultsCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesResultsCreateRequest
 */
export interface ComplianceApiComplianceEntitiesResultsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {ComplianceEntityResultSelectAllBinderRequest}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly hasBinder?: boolean

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly hasComment?: boolean

    /**
     * Force-include results marked as automated false positives. Defaults to false.
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly includeAutomatedFalsePositives?: boolean

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly maxConfidence?: number

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly minConfidence?: number

    /**
     * Search by result name or body text.
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly query?: string

    /**
     * Filter by the user\&#39;s false-positive rating.
     * @type {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>

    /**
     * 
     * @type {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>

    /**
     * Filter adverse media results by sub-source (news / icij / enforcement).
     * @type {Array<'enforcement' | 'icij' | 'news'>}
     * @memberof ComplianceApiComplianceEntitiesResultsCreate
     */
    readonly sourceType?: Array<'enforcement' | 'icij' | 'news'>
}

/**
 * Request parameters for complianceEntitiesResultsList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesResultsListRequest
 */
export interface ComplianceApiComplianceEntitiesResultsListRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly hasBinder?: boolean

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly hasComment?: boolean

    /**
     * Force-include results marked as automated false positives. Defaults to false.
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly includeAutomatedFalsePositives?: boolean

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly maxConfidence?: number

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly minConfidence?: number

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly nextKey?: string

    /**
     * Number of results per page. Defaults to 50, capped at 200.
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly pageSize?: number

    /**
     * Search by result name or body text.
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly query?: string

    /**
     * Filter by the user\&#39;s false-positive rating.
     * @type {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>

    /**
     * 
     * @type {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>

    /**
     * 
     * @type {'confidence' | 'found_at'}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly sorting?: 'confidence' | 'found_at'

    /**
     * 
     * @type {'ascending' | 'descending'}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly sortingOrder?: 'ascending' | 'descending'

    /**
     * Filter adverse media results by sub-source (news / icij / enforcement).
     * @type {Array<'enforcement' | 'icij' | 'news'>}
     * @memberof ComplianceApiComplianceEntitiesResultsList
     */
    readonly sourceType?: Array<'enforcement' | 'icij' | 'news'>
}

/**
 * Request parameters for complianceEntitiesResultsPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesResultsPartialUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesResultsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly hasBinder?: boolean

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly hasComment?: boolean

    /**
     * Force-include results marked as automated false positives. Defaults to false.
     * @type {boolean}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly includeAutomatedFalsePositives?: boolean

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly maxConfidence?: number

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly minConfidence?: number

    /**
     * Search by result name or body text.
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly query?: string

    /**
     * Filter by the user\&#39;s false-positive rating.
     * @type {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>

    /**
     * 
     * @type {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>

    /**
     * Filter adverse media results by sub-source (news / icij / enforcement).
     * @type {Array<'enforcement' | 'icij' | 'news'>}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly sourceType?: Array<'enforcement' | 'icij' | 'news'>

    /**
     * 
     * @type {PatchedComplianceEntityResultSelectAllRatingRequest}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate
     */
    readonly patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest
}

/**
 * Request parameters for complianceEntitiesResultsPartialUpdate2 operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesResultsPartialUpdate2Request
 */
export interface ComplianceApiComplianceEntitiesResultsPartialUpdate2Request {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate2
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate2
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate2
     */
    readonly complianceEntityResultId: string

    /**
     * 
     * @type {PatchedComplianceEntityResultRequest}
     * @memberof ComplianceApiComplianceEntitiesResultsPartialUpdate2
     */
    readonly patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest
}

/**
 * Request parameters for complianceEntitiesResultsRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesResultsRetrieveRequest
 */
export interface ComplianceApiComplianceEntitiesResultsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsRetrieve
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsRetrieve
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsRetrieve
     */
    readonly complianceEntityResultId: string
}

/**
 * Request parameters for complianceEntitiesResultsUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesResultsUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesResultsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesResultsUpdate
     */
    readonly complianceEntityResultId: string

    /**
     * 
     * @type {ComplianceEntityResultRequest}
     * @memberof ComplianceApiComplianceEntitiesResultsUpdate
     */
    readonly complianceEntityResultRequest: ComplianceEntityResultRequest
}

/**
 * Request parameters for complianceEntitiesRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesRetrieveRequest
 */
export interface ComplianceApiComplianceEntitiesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesRetrieve
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesRetrieve
     */
    readonly complianceEntityId: string
}

/**
 * Request parameters for complianceEntitiesUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceEntitiesUpdateRequest
 */
export interface ComplianceApiComplianceEntitiesUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceEntitiesUpdate
     */
    readonly complianceEntityId: string

    /**
     * 
     * @type {ComplianceEntityRequest}
     * @memberof ComplianceApiComplianceEntitiesUpdate
     */
    readonly complianceEntityRequest: ComplianceEntityRequest
}

/**
 * Request parameters for complianceExportCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceExportCreateRequest
 */
export interface ComplianceApiComplianceExportCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceExportCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {ComplianceCheckExportRequest}
     * @memberof ComplianceApiComplianceExportCreate
     */
    readonly complianceCheckExportRequest?: ComplianceCheckExportRequest
}

/**
 * Request parameters for complianceList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceListRequest
 */
export interface ComplianceApiComplianceListRequest {
    /**
     * Filter by whether the check was closed by a user.
     * @type {boolean}
     * @memberof ComplianceApiComplianceList
     */
    readonly closed?: boolean

    /**
     * Filter by compliance score. Accepts multiple values.
     * @type {Array<'low' | 'medium' | 'high'>}
     * @memberof ComplianceApiComplianceList
     */
    readonly complianceScore?: Array<'low' | 'medium' | 'high'>

    /**
     * Return compliance checks created on or before this datetime (ISO 8601).
     * @type {string}
     * @memberof ComplianceApiComplianceList
     */
    readonly maxCreatedAt?: string

    /**
     * Return compliance checks finished on or before this datetime (ISO 8601).
     * @type {string}
     * @memberof ComplianceApiComplianceList
     */
    readonly maxFinishedAt?: string

    /**
     * Return compliance checks created on or after this datetime (ISO 8601).
     * @type {string}
     * @memberof ComplianceApiComplianceList
     */
    readonly minCreatedAt?: string

    /**
     * Return compliance checks finished on or after this datetime (ISO 8601).
     * @type {string}
     * @memberof ComplianceApiComplianceList
     */
    readonly minFinishedAt?: string

    /**
     * Filter by whether ongoing monitoring is enabled.
     * @type {boolean}
     * @memberof ComplianceApiComplianceList
     */
    readonly monitoringEnabled?: boolean

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceList
     */
    readonly nextKey?: string

    /**
     * Search compliance checks by company name.
     * @type {string}
     * @memberof ComplianceApiComplianceList
     */
    readonly query?: string

    /**
     * Field to sort results by.
     * @type {'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count'}
     * @memberof ComplianceApiComplianceList
     */
    readonly sorting?: 'company_name' | 'created_at' | 'finished_at' | 'monitoring_enabled' | 'reviewed_results_count' | 'status' | 'unreviewed_results_count'

    /**
     * Sort order. descending is newest/highest first, ascending is oldest/lowest first.
     * @type {'ascending' | 'descending'}
     * @memberof ComplianceApiComplianceList
     */
    readonly sortingOrder?: 'ascending' | 'descending'

    /**
     * Filter by compliance check status.
     * @type {Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>}
     * @memberof ComplianceApiComplianceList
     */
    readonly status?: Array<'queued' | 'in_progress' | 'searching_directors' | 'completed'>
}

/**
 * Request parameters for complianceMonitoringBatchUpdatePartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceMonitoringBatchUpdatePartialUpdateRequest
 */
export interface ComplianceApiComplianceMonitoringBatchUpdatePartialUpdateRequest {
    /**
     * 
     * @type {PatchedComplianceCheckMonitoringBulkRequest}
     * @memberof ComplianceApiComplianceMonitoringBatchUpdatePartialUpdate
     */
    readonly patchedComplianceCheckMonitoringBulkRequest?: PatchedComplianceCheckMonitoringBulkRequest
}

/**
 * Request parameters for complianceMonitoringSubscriptionBatchUpdatePartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceMonitoringSubscriptionBatchUpdatePartialUpdateRequest
 */
export interface ComplianceApiComplianceMonitoringSubscriptionBatchUpdatePartialUpdateRequest {
    /**
     * 
     * @type {PatchedComplianceCheckSubscriptionBulkRequest}
     * @memberof ComplianceApiComplianceMonitoringSubscriptionBatchUpdatePartialUpdate
     */
    readonly patchedComplianceCheckSubscriptionBulkRequest?: PatchedComplianceCheckSubscriptionBulkRequest
}

/**
 * Request parameters for complianceMonitoringSubscriptionCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceMonitoringSubscriptionCreateRequest
 */
export interface ComplianceApiComplianceMonitoringSubscriptionCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceMonitoringSubscriptionCreate
     */
    readonly complianceCheckId: string
}

/**
 * Request parameters for complianceMonitoringSubscriptionDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceMonitoringSubscriptionDestroyRequest
 */
export interface ComplianceApiComplianceMonitoringSubscriptionDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceMonitoringSubscriptionDestroy
     */
    readonly complianceCheckId: string
}

/**
 * Request parameters for compliancePartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiCompliancePartialUpdateRequest
 */
export interface ComplianceApiCompliancePartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiCompliancePartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {PatchedComplianceCheckRequest}
     * @memberof ComplianceApiCompliancePartialUpdate
     */
    readonly patchedComplianceCheckRequest?: PatchedComplianceCheckRequest
}

/**
 * Request parameters for complianceReportsRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceReportsRetrieveRequest
 */
export interface ComplianceApiComplianceReportsRetrieveRequest {
    /**
     * Day the period is anchored on. Defaults to today.
     * @type {string}
     * @memberof ComplianceApiComplianceReportsRetrieve
     */
    readonly anchorDate?: string

    /**
     * * &#x60;month&#x60; - Month * &#x60;quarter&#x60; - Quarter * &#x60;ytd&#x60; - Ytd * &#x60;year&#x60; - Year
     * @type {'month' | 'quarter' | 'ytd' | 'year'}
     * @memberof ComplianceApiComplianceReportsRetrieve
     */
    readonly period?: 'month' | 'quarter' | 'ytd' | 'year'

    /**
     * 
     * @type {Array<string>}
     * @memberof ComplianceApiComplianceReportsRetrieve
     */
    readonly portfolioId?: Array<string>

    /**
     * * &#x60;me&#x60; - Me * &#x60;organization&#x60; - Organization
     * @type {'me' | 'organization'}
     * @memberof ComplianceApiComplianceReportsRetrieve
     */
    readonly scope?: 'me' | 'organization'

    /**
     * 
     * @type {Array<string>}
     * @memberof ComplianceApiComplianceReportsRetrieve
     */
    readonly userId?: Array<string>
}

/**
 * Request parameters for complianceResultsBatchUpdatePartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsBatchUpdatePartialUpdateRequest
 */
export interface ComplianceApiComplianceResultsBatchUpdatePartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsBatchUpdatePartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {Array<ComplianceEntityResultBatchRequest>}
     * @memberof ComplianceApiComplianceResultsBatchUpdatePartialUpdate
     */
    readonly complianceEntityResultBatchRequest: Array<ComplianceEntityResultBatchRequest>
}

/**
 * Request parameters for complianceResultsCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsCreateRequest
 */
export interface ComplianceApiComplianceResultsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {ComplianceEntityResultSelectAllBinderRequest}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly complianceEntityResultSelectAllBinderRequest: ComplianceEntityResultSelectAllBinderRequest

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly hasBinder?: boolean

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly hasComment?: boolean

    /**
     * Force-include results marked as automated false positives. Defaults to false.
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly includeAutomatedFalsePositives?: boolean

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly maxConfidence?: number

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly minConfidence?: number

    /**
     * Search by result name or body text.
     * @type {string}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly query?: string

    /**
     * Filter by the user\&#39;s false-positive rating.
     * @type {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>

    /**
     * 
     * @type {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>

    /**
     * Filter adverse media results by sub-source (news / icij / enforcement).
     * @type {Array<'enforcement' | 'icij' | 'news'>}
     * @memberof ComplianceApiComplianceResultsCreate
     */
    readonly sourceType?: Array<'enforcement' | 'icij' | 'news'>
}

/**
 * Request parameters for complianceResultsList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsListRequest
 */
export interface ComplianceApiComplianceResultsListRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly hasBinder?: boolean

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly hasComment?: boolean

    /**
     * Force-include results marked as automated false positives. Defaults to false.
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly includeAutomatedFalsePositives?: boolean

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly maxConfidence?: number

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly minConfidence?: number

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly nextKey?: string

    /**
     * Number of results per page. Defaults to 50, capped at 200.
     * @type {number}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly pageSize?: number

    /**
     * Search by result name or body text.
     * @type {string}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly query?: string

    /**
     * Filter by the user\&#39;s false-positive rating.
     * @type {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>

    /**
     * 
     * @type {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>

    /**
     * 
     * @type {'confidence' | 'found_at'}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly sorting?: 'confidence' | 'found_at'

    /**
     * 
     * @type {'ascending' | 'descending'}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly sortingOrder?: 'ascending' | 'descending'

    /**
     * Filter adverse media results by sub-source (news / icij / enforcement).
     * @type {Array<'enforcement' | 'icij' | 'news'>}
     * @memberof ComplianceApiComplianceResultsList
     */
    readonly sourceType?: Array<'enforcement' | 'icij' | 'news'>
}

/**
 * Request parameters for complianceResultsPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsPartialUpdateRequest
 */
export interface ComplianceApiComplianceResultsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly hasBinder?: boolean

    /**
     * 
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly hasComment?: boolean

    /**
     * Force-include results marked as automated false positives. Defaults to false.
     * @type {boolean}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly includeAutomatedFalsePositives?: boolean

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly maxConfidence?: number

    /**
     * 
     * @type {number}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly minConfidence?: number

    /**
     * Search by result name or body text.
     * @type {string}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly query?: string

    /**
     * Filter by the user\&#39;s false-positive rating.
     * @type {Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly rating?: Array<'discard' | 'false_positive' | 'no_risk' | 'not_rated' | 'risk_confirmed' | 'to_investigate'>

    /**
     * 
     * @type {Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly resultType?: Array<'adverse_media' | 'enforcement' | 'govt_owned' | 'pep' | 'sanction'>

    /**
     * Filter adverse media results by sub-source (news / icij / enforcement).
     * @type {Array<'enforcement' | 'icij' | 'news'>}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly sourceType?: Array<'enforcement' | 'icij' | 'news'>

    /**
     * 
     * @type {PatchedComplianceEntityResultSelectAllRatingRequest}
     * @memberof ComplianceApiComplianceResultsPartialUpdate
     */
    readonly patchedComplianceEntityResultSelectAllRatingRequest?: PatchedComplianceEntityResultSelectAllRatingRequest
}

/**
 * Request parameters for complianceResultsPartialUpdate2 operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsPartialUpdate2Request
 */
export interface ComplianceApiComplianceResultsPartialUpdate2Request {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsPartialUpdate2
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsPartialUpdate2
     */
    readonly complianceEntityResultId: string

    /**
     * 
     * @type {PatchedComplianceEntityResultRequest}
     * @memberof ComplianceApiComplianceResultsPartialUpdate2
     */
    readonly patchedComplianceEntityResultRequest?: PatchedComplianceEntityResultRequest
}

/**
 * Request parameters for complianceResultsRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsRetrieveRequest
 */
export interface ComplianceApiComplianceResultsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsRetrieve
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsRetrieve
     */
    readonly complianceEntityResultId: string
}

/**
 * Request parameters for complianceResultsUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceResultsUpdateRequest
 */
export interface ComplianceApiComplianceResultsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceResultsUpdate
     */
    readonly complianceEntityResultId: string

    /**
     * 
     * @type {ComplianceEntityResultRequest}
     * @memberof ComplianceApiComplianceResultsUpdate
     */
    readonly complianceEntityResultRequest: ComplianceEntityResultRequest
}

/**
 * Request parameters for complianceRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceRetrieveRequest
 */
export interface ComplianceApiComplianceRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceRetrieve
     */
    readonly complianceCheckId: string
}

/**
 * Request parameters for complianceSettingsChangelogList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsChangelogListRequest
 */
export interface ComplianceApiComplianceSettingsChangelogListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsChangelogList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for complianceSettingsIndustryRiskCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsIndustryRiskCreateRequest
 */
export interface ComplianceApiComplianceSettingsIndustryRiskCreateRequest {
    /**
     * 
     * @type {ProfileIndustryRiskOverrideRequest}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskCreate
     */
    readonly profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest
}

/**
 * Request parameters for complianceSettingsIndustryRiskDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsIndustryRiskDestroyRequest
 */
export interface ComplianceApiComplianceSettingsIndustryRiskDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskDestroy
     */
    readonly overrideId: string
}

/**
 * Request parameters for complianceSettingsIndustryRiskList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsIndustryRiskListRequest
 */
export interface ComplianceApiComplianceSettingsIndustryRiskListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskList
     */
    readonly nextKey?: string

    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskList
     */
    readonly query?: string
}

/**
 * Request parameters for complianceSettingsIndustryRiskPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsIndustryRiskPartialUpdateRequest
 */
export interface ComplianceApiComplianceSettingsIndustryRiskPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskPartialUpdate
     */
    readonly overrideId: string

    /**
     * 
     * @type {PatchedProfileIndustryRiskOverrideRequest}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskPartialUpdate
     */
    readonly patchedProfileIndustryRiskOverrideRequest?: PatchedProfileIndustryRiskOverrideRequest
}

/**
 * Request parameters for complianceSettingsIndustryRiskRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsIndustryRiskRetrieveRequest
 */
export interface ComplianceApiComplianceSettingsIndustryRiskRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskRetrieve
     */
    readonly overrideId: string
}

/**
 * Request parameters for complianceSettingsIndustryRiskUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsIndustryRiskUpdateRequest
 */
export interface ComplianceApiComplianceSettingsIndustryRiskUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskUpdate
     */
    readonly overrideId: string

    /**
     * 
     * @type {ProfileIndustryRiskOverrideRequest}
     * @memberof ComplianceApiComplianceSettingsIndustryRiskUpdate
     */
    readonly profileIndustryRiskOverrideRequest: ProfileIndustryRiskOverrideRequest
}

/**
 * Request parameters for complianceSettingsPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsPartialUpdateRequest
 */
export interface ComplianceApiComplianceSettingsPartialUpdateRequest {
    /**
     * 
     * @type {PatchedComplianceSettingsRequest}
     * @memberof ComplianceApiComplianceSettingsPartialUpdate
     */
    readonly patchedComplianceSettingsRequest?: PatchedComplianceSettingsRequest
}

/**
 * Request parameters for complianceSettingsUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsUpdateRequest
 */
export interface ComplianceApiComplianceSettingsUpdateRequest {
    /**
     * 
     * @type {ComplianceSettingsRequest}
     * @memberof ComplianceApiComplianceSettingsUpdate
     */
    readonly complianceSettingsRequest?: ComplianceSettingsRequest
}

/**
 * Request parameters for complianceSettingsWorldRiskCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsWorldRiskCreateRequest
 */
export interface ComplianceApiComplianceSettingsWorldRiskCreateRequest {
    /**
     * 
     * @type {ProfileCountryRiskOverrideRequest}
     * @memberof ComplianceApiComplianceSettingsWorldRiskCreate
     */
    readonly profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest
}

/**
 * Request parameters for complianceSettingsWorldRiskDestroy operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsWorldRiskDestroyRequest
 */
export interface ComplianceApiComplianceSettingsWorldRiskDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsWorldRiskDestroy
     */
    readonly overrideId: string
}

/**
 * Request parameters for complianceSettingsWorldRiskList operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsWorldRiskListRequest
 */
export interface ComplianceApiComplianceSettingsWorldRiskListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsWorldRiskList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for complianceSettingsWorldRiskPartialUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsWorldRiskPartialUpdateRequest
 */
export interface ComplianceApiComplianceSettingsWorldRiskPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsWorldRiskPartialUpdate
     */
    readonly overrideId: string

    /**
     * 
     * @type {PatchedProfileCountryRiskOverrideRequest}
     * @memberof ComplianceApiComplianceSettingsWorldRiskPartialUpdate
     */
    readonly patchedProfileCountryRiskOverrideRequest?: PatchedProfileCountryRiskOverrideRequest
}

/**
 * Request parameters for complianceSettingsWorldRiskRetrieve operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsWorldRiskRetrieveRequest
 */
export interface ComplianceApiComplianceSettingsWorldRiskRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsWorldRiskRetrieve
     */
    readonly overrideId: string
}

/**
 * Request parameters for complianceSettingsWorldRiskUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceSettingsWorldRiskUpdateRequest
 */
export interface ComplianceApiComplianceSettingsWorldRiskUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceSettingsWorldRiskUpdate
     */
    readonly overrideId: string

    /**
     * 
     * @type {ProfileCountryRiskOverrideRequest}
     * @memberof ComplianceApiComplianceSettingsWorldRiskUpdate
     */
    readonly profileCountryRiskOverrideRequest: ProfileCountryRiskOverrideRequest
}

/**
 * Request parameters for complianceStartCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceStartCreateRequest
 */
export interface ComplianceApiComplianceStartCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceStartCreate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {ComplianceCheckStartRequest}
     * @memberof ComplianceApiComplianceStartCreate
     */
    readonly complianceCheckStartRequest?: ComplianceCheckStartRequest
}

/**
 * Request parameters for complianceStartDirectorsSearchCreate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceStartDirectorsSearchCreateRequest
 */
export interface ComplianceApiComplianceStartDirectorsSearchCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceStartDirectorsSearchCreate
     */
    readonly complianceCheckId: string
}

/**
 * Request parameters for complianceUpdate operation in ComplianceApi.
 * @export
 * @interface ComplianceApiComplianceUpdateRequest
 */
export interface ComplianceApiComplianceUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ComplianceApiComplianceUpdate
     */
    readonly complianceCheckId: string

    /**
     * 
     * @type {ComplianceCheckRequest}
     * @memberof ComplianceApiComplianceUpdate
     */
    readonly complianceCheckRequest?: ComplianceCheckRequest
}

/**
 * ComplianceApi - object-oriented interface
 * @export
 * @class ComplianceApi
 * @extends {BaseAPI}
 */
export class ComplianceApi extends BaseAPI implements ComplianceApiInterface {
    /**
     * Add an add/exclude keyword entry to the active profile\'s set.
     * @param {ComplianceApiComplianceAdverseMediaKeywordsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceAdverseMediaKeywordsCreate(requestParameters: ComplianceApiComplianceAdverseMediaKeywordsCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceAdverseMediaKeywordsCreate(requestParameters.keywordSetEntryCreateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete one add/exclude entry from the active profile\'s keyword set.
     * @param {ComplianceApiComplianceAdverseMediaKeywordsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceAdverseMediaKeywordsDestroy(requestParameters: ComplianceApiComplianceAdverseMediaKeywordsDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceAdverseMediaKeywordsDestroy(requestParameters.entryId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete every entry in the active profile\'s keyword set.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceAdverseMediaKeywordsResetCreate(options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceAdverseMediaKeywordsResetCreate(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Return the effective keyword list for a language plus per-language counts.  ``language`` is required: the full list is too large to return unfiltered, so the frontend fetches one language and uses ``language_counts`` for the rest.
     * @param {ComplianceApiComplianceAdverseMediaKeywordsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceAdverseMediaKeywordsRetrieve(requestParameters: ComplianceApiComplianceAdverseMediaKeywordsRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceAdverseMediaKeywordsRetrieve(requestParameters.language, requestParameters.entityType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new batch compliance check.  Supports either a list of company external_ids or a portfolio_id to derive companies. No processing/sending logic is executed here; this only creates the batch record.
     * @param {ComplianceApiComplianceBatchesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceBatchesCreate(requestParameters: ComplianceApiComplianceBatchesCreateRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceBatchesCreate(requestParameters.batchComplianceCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a batch compliance check.
     * @param {ComplianceApiComplianceBatchesPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceBatchesPartialUpdate(requestParameters: ComplianceApiComplianceBatchesPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceBatchesPartialUpdate(requestParameters.batchComplianceCheckId, requestParameters.patchedBatchComplianceCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a batch compliance check.
     * @param {ComplianceApiComplianceBatchesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceBatchesRetrieve(requestParameters: ComplianceApiComplianceBatchesRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceBatchesRetrieve(requestParameters.batchComplianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Start a compliance check.
     * @param {ComplianceApiComplianceBatchesStartCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceBatchesStartCreate(requestParameters: ComplianceApiComplianceBatchesStartCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceBatchesStartCreate(requestParameters.batchComplianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a batch compliance check.
     * @param {ComplianceApiComplianceBatchesUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceBatchesUpdate(requestParameters: ComplianceApiComplianceBatchesUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceBatchesUpdate(requestParameters.batchComplianceCheckId, requestParameters.batchComplianceCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new compliance check.
     * @param {ComplianceApiComplianceCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceCreate(requestParameters: ComplianceApiComplianceCreateRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceCreate(requestParameters.complianceCheckRequestRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Return enforcement providers with their live listing stats.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEnforcementProvidersList(options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEnforcementProvidersList(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create compliance entity aliases.
     * @param {ComplianceApiComplianceEntitiesAliasesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesAliasesCreate(requestParameters: ComplianceApiComplianceEntitiesAliasesCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesAliasesCreate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityAliasRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {ComplianceApiComplianceEntitiesAliasesDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesAliasesDestroy(requestParameters: ComplianceApiComplianceEntitiesAliasesDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesAliasesDestroy(requestParameters.complianceCheckId, requestParameters.complianceEntityAliasId, requestParameters.complianceEntityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create compliance entity aliases.
     * @param {ComplianceApiComplianceEntitiesAliasesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesAliasesList(requestParameters: ComplianceApiComplianceEntitiesAliasesListRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesAliasesList(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {ComplianceApiComplianceEntitiesAliasesPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesAliasesPartialUpdate(requestParameters: ComplianceApiComplianceEntitiesAliasesPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesAliasesPartialUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityAliasId, requestParameters.complianceEntityId, requestParameters.patchedComplianceEntityAliasRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {ComplianceApiComplianceEntitiesAliasesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesAliasesRetrieve(requestParameters: ComplianceApiComplianceEntitiesAliasesRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesAliasesRetrieve(requestParameters.complianceCheckId, requestParameters.complianceEntityAliasId, requestParameters.complianceEntityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity aliases.
     * @param {ComplianceApiComplianceEntitiesAliasesUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesAliasesUpdate(requestParameters: ComplianceApiComplianceEntitiesAliasesUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesAliasesUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityAliasId, requestParameters.complianceEntityId, requestParameters.complianceEntityAliasRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Batch update compliance entities.
     * @param {ComplianceApiComplianceEntitiesBatchUpdatePartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesBatchUpdatePartialUpdate(requestParameters: ComplianceApiComplianceEntitiesBatchUpdatePartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesBatchUpdatePartialUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityBatchUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve all compliance entities associated with a compliance check.
     * @param {ComplianceApiComplianceEntitiesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesCreate(requestParameters: ComplianceApiComplianceEntitiesCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesCreate(requestParameters.complianceCheckId, requestParameters.complianceEntityRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity.
     * @param {ComplianceApiComplianceEntitiesDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesDestroy(requestParameters: ComplianceApiComplianceEntitiesDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesDestroy(requestParameters.complianceCheckId, requestParameters.complianceEntityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create compliance entity identifiers.
     * @param {ComplianceApiComplianceEntitiesIdentifiersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesIdentifiersCreate(requestParameters: ComplianceApiComplianceEntitiesIdentifiersCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesIdentifiersCreate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityIdentifierRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {ComplianceApiComplianceEntitiesIdentifiersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesIdentifiersDestroy(requestParameters: ComplianceApiComplianceEntitiesIdentifiersDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesIdentifiersDestroy(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityIdentifierId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create compliance entity identifiers.
     * @param {ComplianceApiComplianceEntitiesIdentifiersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesIdentifiersList(requestParameters: ComplianceApiComplianceEntitiesIdentifiersListRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesIdentifiersList(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {ComplianceApiComplianceEntitiesIdentifiersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesIdentifiersPartialUpdate(requestParameters: ComplianceApiComplianceEntitiesIdentifiersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesIdentifiersPartialUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityIdentifierId, requestParameters.patchedComplianceEntityIdentifierRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {ComplianceApiComplianceEntitiesIdentifiersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesIdentifiersRetrieve(requestParameters: ComplianceApiComplianceEntitiesIdentifiersRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesIdentifiersRetrieve(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityIdentifierId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy compliance entity identifiers.
     * @param {ComplianceApiComplianceEntitiesIdentifiersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesIdentifiersUpdate(requestParameters: ComplianceApiComplianceEntitiesIdentifiersUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesIdentifiersUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityIdentifierId, requestParameters.complianceEntityIdentifierRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve all compliance entities associated with a compliance check.
     * @param {ComplianceApiComplianceEntitiesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesList(requestParameters: ComplianceApiComplianceEntitiesListRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesList(requestParameters.complianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity.
     * @param {ComplianceApiComplianceEntitiesPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesPartialUpdate(requestParameters: ComplianceApiComplianceEntitiesPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesPartialUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.patchedComplianceEntityRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
     * @param {ComplianceApiComplianceEntitiesResultsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesResultsCreate(requestParameters: ComplianceApiComplianceEntitiesResultsCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesResultsCreate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityResultSelectAllBinderRequest, requestParameters.hasBinder, requestParameters.hasComment, requestParameters.includeAutomatedFalsePositives, requestParameters.maxConfidence, requestParameters.minConfidence, requestParameters.query, requestParameters.rating, requestParameters.resultType, requestParameters.sourceType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Paginated list of compliance entity results (`{next_key,total_results,results}`).
     * @param {ComplianceApiComplianceEntitiesResultsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesResultsList(requestParameters: ComplianceApiComplianceEntitiesResultsListRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesResultsList(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.hasBinder, requestParameters.hasComment, requestParameters.includeAutomatedFalsePositives, requestParameters.maxConfidence, requestParameters.minConfidence, requestParameters.nextKey, requestParameters.pageSize, requestParameters.query, requestParameters.rating, requestParameters.resultType, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.sourceType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
     * @param {ComplianceApiComplianceEntitiesResultsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesResultsPartialUpdate(requestParameters: ComplianceApiComplianceEntitiesResultsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesResultsPartialUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.hasBinder, requestParameters.hasComment, requestParameters.includeAutomatedFalsePositives, requestParameters.maxConfidence, requestParameters.minConfidence, requestParameters.query, requestParameters.rating, requestParameters.resultType, requestParameters.sourceType, requestParameters.patchedComplianceEntityResultSelectAllRatingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity result.
     * @param {ComplianceApiComplianceEntitiesResultsPartialUpdate2Request} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesResultsPartialUpdate2(requestParameters: ComplianceApiComplianceEntitiesResultsPartialUpdate2Request, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesResultsPartialUpdate2(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityResultId, requestParameters.patchedComplianceEntityResultRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity result.
     * @param {ComplianceApiComplianceEntitiesResultsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesResultsRetrieve(requestParameters: ComplianceApiComplianceEntitiesResultsRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesResultsRetrieve(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityResultId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity result.
     * @param {ComplianceApiComplianceEntitiesResultsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesResultsUpdate(requestParameters: ComplianceApiComplianceEntitiesResultsUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesResultsUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityResultId, requestParameters.complianceEntityResultRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity.
     * @param {ComplianceApiComplianceEntitiesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesRetrieve(requestParameters: ComplianceApiComplianceEntitiesRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesRetrieve(requestParameters.complianceCheckId, requestParameters.complianceEntityId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity.
     * @param {ComplianceApiComplianceEntitiesUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceEntitiesUpdate(requestParameters: ComplianceApiComplianceEntitiesUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceEntitiesUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityId, requestParameters.complianceEntityRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Start a compliance check export with optional audit-trail filters.
     * @param {ComplianceApiComplianceExportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceExportCreate(requestParameters: ComplianceApiComplianceExportCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceExportCreate(requestParameters.complianceCheckId, requestParameters.complianceCheckExportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List compliance checks for the current user\'s profile.
     * @param {ComplianceApiComplianceListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceList(requestParameters: ComplianceApiComplianceListRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceList(requestParameters.closed, requestParameters.complianceScore, requestParameters.maxCreatedAt, requestParameters.maxFinishedAt, requestParameters.minCreatedAt, requestParameters.minFinishedAt, requestParameters.monitoringEnabled, requestParameters.nextKey, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Set `monitoring_enabled` on many checks; reconcile their entities.
     * @param {ComplianceApiComplianceMonitoringBatchUpdatePartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceMonitoringBatchUpdatePartialUpdate(requestParameters: ComplianceApiComplianceMonitoringBatchUpdatePartialUpdateRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceMonitoringBatchUpdatePartialUpdate(requestParameters.patchedComplianceCheckMonitoringBulkRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add/remove the current user as a monitoring subscriber on many checks.
     * @param {ComplianceApiComplianceMonitoringSubscriptionBatchUpdatePartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceMonitoringSubscriptionBatchUpdatePartialUpdate(requestParameters: ComplianceApiComplianceMonitoringSubscriptionBatchUpdatePartialUpdateRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceMonitoringSubscriptionBatchUpdatePartialUpdate(requestParameters.patchedComplianceCheckSubscriptionBulkRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Subscribe the current user (allowed even without monitoring enabled).
     * @param {ComplianceApiComplianceMonitoringSubscriptionCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceMonitoringSubscriptionCreate(requestParameters: ComplianceApiComplianceMonitoringSubscriptionCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceMonitoringSubscriptionCreate(requestParameters.complianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Unsubscribe the current user.
     * @param {ComplianceApiComplianceMonitoringSubscriptionDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceMonitoringSubscriptionDestroy(requestParameters: ComplianceApiComplianceMonitoringSubscriptionDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceMonitoringSubscriptionDestroy(requestParameters.complianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve the results of a compliance check based on its id.
     * @param {ComplianceApiCompliancePartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public compliancePartialUpdate(requestParameters: ComplianceApiCompliancePartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).compliancePartialUpdate(requestParameters.complianceCheckId, requestParameters.patchedComplianceCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Return the report for the requested scope and period.
     * @param {ComplianceApiComplianceReportsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceReportsRetrieve(requestParameters: ComplianceApiComplianceReportsRetrieveRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceReportsRetrieve(requestParameters.anchorDate, requestParameters.period, requestParameters.portfolioId, requestParameters.scope, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update entity results in batch.
     * @param {ComplianceApiComplianceResultsBatchUpdatePartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsBatchUpdatePartialUpdate(requestParameters: ComplianceApiComplianceResultsBatchUpdatePartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsBatchUpdatePartialUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityResultBatchRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add every result matching this list\'s filters (same query string as the GET) to a binder, in a single bulk create -- a select-all add-to-binder action, not a per-row batch. Results already in the binder are skipped, not duplicated.
     * @param {ComplianceApiComplianceResultsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsCreate(requestParameters: ComplianceApiComplianceResultsCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsCreate(requestParameters.complianceCheckId, requestParameters.complianceEntityResultSelectAllBinderRequest, requestParameters.hasBinder, requestParameters.hasComment, requestParameters.includeAutomatedFalsePositives, requestParameters.maxConfidence, requestParameters.minConfidence, requestParameters.query, requestParameters.rating, requestParameters.resultType, requestParameters.sourceType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Paginated list of compliance entity results (`{next_key,total_results,results}`).
     * @param {ComplianceApiComplianceResultsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsList(requestParameters: ComplianceApiComplianceResultsListRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsList(requestParameters.complianceCheckId, requestParameters.hasBinder, requestParameters.hasComment, requestParameters.includeAutomatedFalsePositives, requestParameters.maxConfidence, requestParameters.minConfidence, requestParameters.nextKey, requestParameters.pageSize, requestParameters.query, requestParameters.rating, requestParameters.resultType, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.sourceType, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Rate every result matching this list\'s filters (same query string as the GET) with one false_positive_rating, in a single bulk update -- a select-all rating action, not a per-row batch.
     * @param {ComplianceApiComplianceResultsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsPartialUpdate(requestParameters: ComplianceApiComplianceResultsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsPartialUpdate(requestParameters.complianceCheckId, requestParameters.hasBinder, requestParameters.hasComment, requestParameters.includeAutomatedFalsePositives, requestParameters.maxConfidence, requestParameters.minConfidence, requestParameters.query, requestParameters.rating, requestParameters.resultType, requestParameters.sourceType, requestParameters.patchedComplianceEntityResultSelectAllRatingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity result.
     * @param {ComplianceApiComplianceResultsPartialUpdate2Request} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsPartialUpdate2(requestParameters: ComplianceApiComplianceResultsPartialUpdate2Request, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsPartialUpdate2(requestParameters.complianceCheckId, requestParameters.complianceEntityResultId, requestParameters.patchedComplianceEntityResultRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity result.
     * @param {ComplianceApiComplianceResultsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsRetrieve(requestParameters: ComplianceApiComplianceResultsRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsRetrieve(requestParameters.complianceCheckId, requestParameters.complianceEntityResultId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve or update a compliance entity result.
     * @param {ComplianceApiComplianceResultsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceResultsUpdate(requestParameters: ComplianceApiComplianceResultsUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceResultsUpdate(requestParameters.complianceCheckId, requestParameters.complianceEntityResultId, requestParameters.complianceEntityResultRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve the results of a compliance check based on its id.
     * @param {ComplianceApiComplianceRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceRetrieve(requestParameters: ComplianceApiComplianceRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceRetrieve(requestParameters.complianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Return sanctions providers with their live listing stats.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSanctionsProvidersList(options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSanctionsProvidersList(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Queue an Excel export of the changelog for the active profile.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsChangelogExportCreate(options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsChangelogExportCreate(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List the active profile\'s compliance settings change history (read-only).  Optional ``source`` / ``field`` query params filter the list; the response carries ``source_counts`` for the filter chips. Ordered latest change first.
     * @param {ComplianceApiComplianceSettingsChangelogListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsChangelogList(requestParameters: ComplianceApiComplianceSettingsChangelogListRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsChangelogList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create one or many industry risk overrides.
     * @param {ComplianceApiComplianceSettingsIndustryRiskCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsIndustryRiskCreate(requestParameters: ComplianceApiComplianceSettingsIndustryRiskCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsIndustryRiskCreate(requestParameters.profileIndustryRiskOverrideRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {ComplianceApiComplianceSettingsIndustryRiskDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsIndustryRiskDestroy(requestParameters: ComplianceApiComplianceSettingsIndustryRiskDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsIndustryRiskDestroy(requestParameters.overrideId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List industry risk overrides, optionally filtered by ``query``.
     * @param {ComplianceApiComplianceSettingsIndustryRiskListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsIndustryRiskList(requestParameters: ComplianceApiComplianceSettingsIndustryRiskListRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsIndustryRiskList(requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {ComplianceApiComplianceSettingsIndustryRiskPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsIndustryRiskPartialUpdate(requestParameters: ComplianceApiComplianceSettingsIndustryRiskPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsIndustryRiskPartialUpdate(requestParameters.overrideId, requestParameters.patchedProfileIndustryRiskOverrideRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {ComplianceApiComplianceSettingsIndustryRiskRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsIndustryRiskRetrieve(requestParameters: ComplianceApiComplianceSettingsIndustryRiskRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsIndustryRiskRetrieve(requestParameters.overrideId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy an industry risk override.
     * @param {ComplianceApiComplianceSettingsIndustryRiskUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsIndustryRiskUpdate(requestParameters: ComplianceApiComplianceSettingsIndustryRiskUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsIndustryRiskUpdate(requestParameters.overrideId, requestParameters.profileIndustryRiskOverrideRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
     * @param {ComplianceApiComplianceSettingsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsPartialUpdate(requestParameters: ComplianceApiComplianceSettingsPartialUpdateRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsPartialUpdate(requestParameters.patchedComplianceSettingsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsRetrieve(options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsRetrieve(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Read / update the active profile\'s customer-managed compliance settings.  Reads open to any compliance-enabled member; writes need an org admin. Each PATCH records a changelog entry per changed field.
     * @param {ComplianceApiComplianceSettingsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsUpdate(requestParameters: ComplianceApiComplianceSettingsUpdateRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsUpdate(requestParameters.complianceSettingsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create world (country) risk overrides for the active profile.
     * @param {ComplianceApiComplianceSettingsWorldRiskCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsWorldRiskCreate(requestParameters: ComplianceApiComplianceSettingsWorldRiskCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsWorldRiskCreate(requestParameters.profileCountryRiskOverrideRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {ComplianceApiComplianceSettingsWorldRiskDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsWorldRiskDestroy(requestParameters: ComplianceApiComplianceSettingsWorldRiskDestroyRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsWorldRiskDestroy(requestParameters.overrideId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create world (country) risk overrides for the active profile.
     * @param {ComplianceApiComplianceSettingsWorldRiskListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsWorldRiskList(requestParameters: ComplianceApiComplianceSettingsWorldRiskListRequest = {}, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsWorldRiskList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {ComplianceApiComplianceSettingsWorldRiskPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsWorldRiskPartialUpdate(requestParameters: ComplianceApiComplianceSettingsWorldRiskPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsWorldRiskPartialUpdate(requestParameters.overrideId, requestParameters.patchedProfileCountryRiskOverrideRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {ComplianceApiComplianceSettingsWorldRiskRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsWorldRiskRetrieve(requestParameters: ComplianceApiComplianceSettingsWorldRiskRetrieveRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsWorldRiskRetrieve(requestParameters.overrideId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update, and destroy a world (country) risk override.
     * @param {ComplianceApiComplianceSettingsWorldRiskUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceSettingsWorldRiskUpdate(requestParameters: ComplianceApiComplianceSettingsWorldRiskUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceSettingsWorldRiskUpdate(requestParameters.overrideId, requestParameters.profileCountryRiskOverrideRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Start a compliance check.
     * @param {ComplianceApiComplianceStartCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceStartCreate(requestParameters: ComplianceApiComplianceStartCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceStartCreate(requestParameters.complianceCheckId, requestParameters.complianceCheckStartRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Start a compliance check.
     * @param {ComplianceApiComplianceStartDirectorsSearchCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceStartDirectorsSearchCreate(requestParameters: ComplianceApiComplianceStartDirectorsSearchCreateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceStartDirectorsSearchCreate(requestParameters.complianceCheckId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve the results of a compliance check based on its id.
     * @param {ComplianceApiComplianceUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ComplianceApi
     */
    public complianceUpdate(requestParameters: ComplianceApiComplianceUpdateRequest, options?: AxiosRequestConfig) {
        return ComplianceApiFp(this.configuration).complianceUpdate(requestParameters.complianceCheckId, requestParameters.complianceCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * DnbApi - axios parameter creator
 * @export
 */
export const DnbApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Get directors from Details API (costs credits).
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesDirectorsRetrieve: async (dunsNumber: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'dunsNumber' is not null or undefined
            assertParamExists('dnbCompaniesDirectorsRetrieve', 'dunsNumber', dunsNumber)
            const localVarPath = `/dnb/companies/{duns_number}/directors/`
                .replace(`{${"duns_number"}}`, encodeURIComponent(String(dunsNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all companies in corporate linkage.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesLinkageActiveCountriesRetrieve: async (dunsNumber: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'dunsNumber' is not null or undefined
            assertParamExists('dnbCompaniesLinkageActiveCountriesRetrieve', 'dunsNumber', dunsNumber)
            const localVarPath = `/dnb/companies/{duns_number}/linkage/active_countries/`
                .replace(`{${"duns_number"}}`, encodeURIComponent(String(dunsNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all companies in corporate linkage.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesLinkageRetrieve: async (dunsNumber: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'dunsNumber' is not null or undefined
            assertParamExists('dnbCompaniesLinkageRetrieve', 'dunsNumber', dunsNumber)
            const localVarPath = `/dnb/companies/{duns_number}/linkage/`
                .replace(`{${"duns_number"}}`, encodeURIComponent(String(dunsNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all companies by Query, using the Search API.
         * @param {string} [country] Country Code (ISO)
         * @param {Array<string>} [dunsNumbers] Duns Numbers
         * @param {string} [query] Search Query
         * @param {Array<string>} [registrationNumbers] Registration Numbers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesList: async (country?: string, dunsNumbers?: Array<string>, query?: string, registrationNumbers?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/dnb/companies/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (country !== undefined) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumbers) {
                localVarQueryParameter['duns_numbers'] = dunsNumbers;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumbers) {
                localVarQueryParameter['registration_numbers'] = registrationNumbers;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Company investigation.
         * @param {CompanyInvestigationRequest} companyInvestigationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesMissingCreate: async (companyInvestigationRequest: CompanyInvestigationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyInvestigationRequest' is not null or undefined
            assertParamExists('dnbCompaniesMissingCreate', 'companyInvestigationRequest', companyInvestigationRequest)
            const localVarPath = `/dnb/companies/missing`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(companyInvestigationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve an Company Investigation.
         * @param {string} investigationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesMissingRetrieve: async (investigationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'investigationId' is not null or undefined
            assertParamExists('dnbCompaniesMissingRetrieve', 'investigationId', investigationId)
            const localVarPath = `/dnb/companies/missing/{investigation_id}/`
                .replace(`{${"investigation_id"}}`, encodeURIComponent(String(investigationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all companies by Query, using the Search API.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesRetrieve: async (dunsNumber: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'dunsNumber' is not null or undefined
            assertParamExists('dnbCompaniesRetrieve', 'dunsNumber', dunsNumber)
            const localVarPath = `/dnb/companies/{duns_number}/`
                .replace(`{${"duns_number"}}`, encodeURIComponent(String(dunsNumber)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * DnbApi - functional programming interface
 * @export
 */
export const DnbApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = DnbApiAxiosParamCreator(configuration)
    return {
        /**
         * Get directors from Details API (costs credits).
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesDirectorsRetrieve(dunsNumber: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DnbDirector>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesDirectorsRetrieve(dunsNumber, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all companies in corporate linkage.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesLinkageActiveCountriesRetrieve(dunsNumber: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DnbActiveCountry>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesLinkageActiveCountriesRetrieve(dunsNumber, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all companies in corporate linkage.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesLinkageRetrieve(dunsNumber: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DnbLinkageCompany>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesLinkageRetrieve(dunsNumber, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all companies by Query, using the Search API.
         * @param {string} [country] Country Code (ISO)
         * @param {Array<string>} [dunsNumbers] Duns Numbers
         * @param {string} [query] Search Query
         * @param {Array<string>} [registrationNumbers] Registration Numbers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesList(country?: string, dunsNumbers?: Array<string>, query?: string, registrationNumbers?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DnbCompany>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesList(country, dunsNumbers, query, registrationNumbers, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Company investigation.
         * @param {CompanyInvestigationRequest} companyInvestigationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesMissingCreate(companyInvestigationRequest: CompanyInvestigationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyInvestigation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesMissingCreate(companyInvestigationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve an Company Investigation.
         * @param {string} investigationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesMissingRetrieve(investigationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyInvestigation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesMissingRetrieve(investigationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all companies by Query, using the Search API.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async dnbCompaniesRetrieve(dunsNumber: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DnbCompany>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.dnbCompaniesRetrieve(dunsNumber, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * DnbApi - factory interface
 * @export
 */
export const DnbApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = DnbApiFp(configuration)
    return {
        /**
         * Get directors from Details API (costs credits).
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesDirectorsRetrieve(dunsNumber: string, options?: any): AxiosPromise<Array<DnbDirector>> {
            return localVarFp.dnbCompaniesDirectorsRetrieve(dunsNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all companies in corporate linkage.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesLinkageActiveCountriesRetrieve(dunsNumber: string, options?: any): AxiosPromise<Array<DnbActiveCountry>> {
            return localVarFp.dnbCompaniesLinkageActiveCountriesRetrieve(dunsNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all companies in corporate linkage.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesLinkageRetrieve(dunsNumber: string, options?: any): AxiosPromise<Array<DnbLinkageCompany>> {
            return localVarFp.dnbCompaniesLinkageRetrieve(dunsNumber, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all companies by Query, using the Search API.
         * @param {string} [country] Country Code (ISO)
         * @param {Array<string>} [dunsNumbers] Duns Numbers
         * @param {string} [query] Search Query
         * @param {Array<string>} [registrationNumbers] Registration Numbers
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesList(country?: string, dunsNumbers?: Array<string>, query?: string, registrationNumbers?: Array<string>, options?: any): AxiosPromise<Array<DnbCompany>> {
            return localVarFp.dnbCompaniesList(country, dunsNumbers, query, registrationNumbers, options).then((request) => request(axios, basePath));
        },
        /**
         * Company investigation.
         * @param {CompanyInvestigationRequest} companyInvestigationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesMissingCreate(companyInvestigationRequest: CompanyInvestigationRequest, options?: any): AxiosPromise<CompanyInvestigation> {
            return localVarFp.dnbCompaniesMissingCreate(companyInvestigationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve an Company Investigation.
         * @param {string} investigationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesMissingRetrieve(investigationId: string, options?: any): AxiosPromise<CompanyInvestigation> {
            return localVarFp.dnbCompaniesMissingRetrieve(investigationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all companies by Query, using the Search API.
         * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        dnbCompaniesRetrieve(dunsNumber: string, options?: any): AxiosPromise<DnbCompany> {
            return localVarFp.dnbCompaniesRetrieve(dunsNumber, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * DnbApi - interface
 * @export
 * @interface DnbApi
 */
export interface DnbApiInterface {
    /**
     * Get directors from Details API (costs credits).
     * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesDirectorsRetrieve(dunsNumber: string, options?: AxiosRequestConfig): AxiosPromise<Array<DnbDirector>>;

    /**
     * Get all companies in corporate linkage.
     * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesLinkageActiveCountriesRetrieve(dunsNumber: string, options?: AxiosRequestConfig): AxiosPromise<Array<DnbActiveCountry>>;

    /**
     * Get all companies in corporate linkage.
     * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesLinkageRetrieve(dunsNumber: string, options?: AxiosRequestConfig): AxiosPromise<Array<DnbLinkageCompany>>;

    /**
     * Get all companies by Query, using the Search API.
     * @param {string} [country] Country Code (ISO)
     * @param {Array<string>} [dunsNumbers] Duns Numbers
     * @param {string} [query] Search Query
     * @param {Array<string>} [registrationNumbers] Registration Numbers
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesList(country?: string, dunsNumbers?: Array<string>, query?: string, registrationNumbers?: Array<string>, options?: AxiosRequestConfig): AxiosPromise<Array<DnbCompany>>;

    /**
     * Company investigation.
     * @param {CompanyInvestigationRequest} companyInvestigationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesMissingCreate(companyInvestigationRequest: CompanyInvestigationRequest, options?: AxiosRequestConfig): AxiosPromise<CompanyInvestigation>;

    /**
     * Retrieve an Company Investigation.
     * @param {string} investigationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesMissingRetrieve(investigationId: string, options?: AxiosRequestConfig): AxiosPromise<CompanyInvestigation>;

    /**
     * Get all companies by Query, using the Search API.
     * @param {string} dunsNumber 9-digit Dun And Bradstreet Number
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApiInterface
     */
    dnbCompaniesRetrieve(dunsNumber: string, options?: AxiosRequestConfig): AxiosPromise<DnbCompany>;

}

/**
 * Request parameters for dnbCompaniesDirectorsRetrieve operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesDirectorsRetrieveRequest
 */
export interface DnbApiDnbCompaniesDirectorsRetrieveRequest {
    /**
     * 9-digit Dun And Bradstreet Number
     * @type {string}
     * @memberof DnbApiDnbCompaniesDirectorsRetrieve
     */
    readonly dunsNumber: string
}

/**
 * Request parameters for dnbCompaniesLinkageActiveCountriesRetrieve operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesLinkageActiveCountriesRetrieveRequest
 */
export interface DnbApiDnbCompaniesLinkageActiveCountriesRetrieveRequest {
    /**
     * 9-digit Dun And Bradstreet Number
     * @type {string}
     * @memberof DnbApiDnbCompaniesLinkageActiveCountriesRetrieve
     */
    readonly dunsNumber: string
}

/**
 * Request parameters for dnbCompaniesLinkageRetrieve operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesLinkageRetrieveRequest
 */
export interface DnbApiDnbCompaniesLinkageRetrieveRequest {
    /**
     * 9-digit Dun And Bradstreet Number
     * @type {string}
     * @memberof DnbApiDnbCompaniesLinkageRetrieve
     */
    readonly dunsNumber: string
}

/**
 * Request parameters for dnbCompaniesList operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesListRequest
 */
export interface DnbApiDnbCompaniesListRequest {
    /**
     * Country Code (ISO)
     * @type {string}
     * @memberof DnbApiDnbCompaniesList
     */
    readonly country?: string

    /**
     * Duns Numbers
     * @type {Array<string>}
     * @memberof DnbApiDnbCompaniesList
     */
    readonly dunsNumbers?: Array<string>

    /**
     * Search Query
     * @type {string}
     * @memberof DnbApiDnbCompaniesList
     */
    readonly query?: string

    /**
     * Registration Numbers
     * @type {Array<string>}
     * @memberof DnbApiDnbCompaniesList
     */
    readonly registrationNumbers?: Array<string>
}

/**
 * Request parameters for dnbCompaniesMissingCreate operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesMissingCreateRequest
 */
export interface DnbApiDnbCompaniesMissingCreateRequest {
    /**
     * 
     * @type {CompanyInvestigationRequest}
     * @memberof DnbApiDnbCompaniesMissingCreate
     */
    readonly companyInvestigationRequest: CompanyInvestigationRequest
}

/**
 * Request parameters for dnbCompaniesMissingRetrieve operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesMissingRetrieveRequest
 */
export interface DnbApiDnbCompaniesMissingRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof DnbApiDnbCompaniesMissingRetrieve
     */
    readonly investigationId: string
}

/**
 * Request parameters for dnbCompaniesRetrieve operation in DnbApi.
 * @export
 * @interface DnbApiDnbCompaniesRetrieveRequest
 */
export interface DnbApiDnbCompaniesRetrieveRequest {
    /**
     * 9-digit Dun And Bradstreet Number
     * @type {string}
     * @memberof DnbApiDnbCompaniesRetrieve
     */
    readonly dunsNumber: string
}

/**
 * DnbApi - object-oriented interface
 * @export
 * @class DnbApi
 * @extends {BaseAPI}
 */
export class DnbApi extends BaseAPI implements DnbApiInterface {
    /**
     * Get directors from Details API (costs credits).
     * @param {DnbApiDnbCompaniesDirectorsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesDirectorsRetrieve(requestParameters: DnbApiDnbCompaniesDirectorsRetrieveRequest, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesDirectorsRetrieve(requestParameters.dunsNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all companies in corporate linkage.
     * @param {DnbApiDnbCompaniesLinkageActiveCountriesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesLinkageActiveCountriesRetrieve(requestParameters: DnbApiDnbCompaniesLinkageActiveCountriesRetrieveRequest, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesLinkageActiveCountriesRetrieve(requestParameters.dunsNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all companies in corporate linkage.
     * @param {DnbApiDnbCompaniesLinkageRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesLinkageRetrieve(requestParameters: DnbApiDnbCompaniesLinkageRetrieveRequest, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesLinkageRetrieve(requestParameters.dunsNumber, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all companies by Query, using the Search API.
     * @param {DnbApiDnbCompaniesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesList(requestParameters: DnbApiDnbCompaniesListRequest = {}, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesList(requestParameters.country, requestParameters.dunsNumbers, requestParameters.query, requestParameters.registrationNumbers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Company investigation.
     * @param {DnbApiDnbCompaniesMissingCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesMissingCreate(requestParameters: DnbApiDnbCompaniesMissingCreateRequest, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesMissingCreate(requestParameters.companyInvestigationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve an Company Investigation.
     * @param {DnbApiDnbCompaniesMissingRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesMissingRetrieve(requestParameters: DnbApiDnbCompaniesMissingRetrieveRequest, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesMissingRetrieve(requestParameters.investigationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all companies by Query, using the Search API.
     * @param {DnbApiDnbCompaniesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof DnbApi
     */
    public dnbCompaniesRetrieve(requestParameters: DnbApiDnbCompaniesRetrieveRequest, options?: AxiosRequestConfig) {
        return DnbApiFp(this.configuration).dnbCompaniesRetrieve(requestParameters.dunsNumber, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * FeedbackApi - axios parameter creator
 * @export
 */
export const FeedbackApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Post method.
         * @param {ArticleFeedbackWriteRequest} articleFeedbackWriteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackArticlesCreate: async (articleFeedbackWriteRequest: ArticleFeedbackWriteRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'articleFeedbackWriteRequest' is not null or undefined
            assertParamExists('feedbackArticlesCreate', 'articleFeedbackWriteRequest', articleFeedbackWriteRequest)
            const localVarPath = `/feedback/articles/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(articleFeedbackWriteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create or list Article Feedback.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackArticlesList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/feedback/articles/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create Company Feedback.
         * @param {CompanyFeedbackWriteRequest} companyFeedbackWriteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackCompaniesCreate: async (companyFeedbackWriteRequest: CompanyFeedbackWriteRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyFeedbackWriteRequest' is not null or undefined
            assertParamExists('feedbackCompaniesCreate', 'companyFeedbackWriteRequest', companyFeedbackWriteRequest)
            const localVarPath = `/feedback/companies/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(companyFeedbackWriteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create Company Feedback.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackCompaniesList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/feedback/companies/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Validate TradeName.
         * @param {TradeNameSuggestionRequest} tradeNameSuggestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackCompaniesTradeNamesCreate: async (tradeNameSuggestionRequest: TradeNameSuggestionRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tradeNameSuggestionRequest' is not null or undefined
            assertParamExists('feedbackCompaniesTradeNamesCreate', 'tradeNameSuggestionRequest', tradeNameSuggestionRequest)
            const localVarPath = `/feedback/companies/trade_names/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(tradeNameSuggestionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List or add comments on a support request.
         * @param {string} externalId 
         * @param {SupportRequestCommentRequest} supportRequestCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsCommentsCreate: async (externalId: string, supportRequestCommentRequest: SupportRequestCommentRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('feedbackSupportRequestsCommentsCreate', 'externalId', externalId)
            // verify required parameter 'supportRequestCommentRequest' is not null or undefined
            assertParamExists('feedbackSupportRequestsCommentsCreate', 'supportRequestCommentRequest', supportRequestCommentRequest)
            const localVarPath = `/feedback/support_requests/{external_id}/comments/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(supportRequestCommentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List or add comments on a support request.
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsCommentsList: async (externalId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('feedbackSupportRequestsCommentsList', 'externalId', externalId)
            const localVarPath = `/feedback/support_requests/{external_id}/comments/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a Support Request and return its canonical read representation.
         * @param {SupportRequestCreateRequest} supportRequestCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsCreate: async (supportRequestCreateRequest: SupportRequestCreateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'supportRequestCreateRequest' is not null or undefined
            assertParamExists('feedbackSupportRequestsCreate', 'supportRequestCreateRequest', supportRequestCreateRequest)
            const localVarPath = `/feedback/support_requests/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(supportRequestCreateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List accessible Support Requests in the selected scope.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {'mine' | 'profile'} [scope] List the current user\&#39;s requests, or all requests belonging to the active profile when the user is a profile admin.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort requests by creation date.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsList: async (nextKey?: string, scope?: 'mine' | 'profile', sortingOrder?: 'ascending' | 'descending', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/feedback/support_requests/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (scope !== undefined) {
                localVarQueryParameter['scope'] = scope;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve a single support request.
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsRetrieve: async (externalId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('feedbackSupportRequestsRetrieve', 'externalId', externalId)
            const localVarPath = `/feedback/support_requests/{external_id}/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FeedbackApi - functional programming interface
 * @export
 */
export const FeedbackApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FeedbackApiAxiosParamCreator(configuration)
    return {
        /**
         * Post method.
         * @param {ArticleFeedbackWriteRequest} articleFeedbackWriteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackArticlesCreate(articleFeedbackWriteRequest: ArticleFeedbackWriteRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ArticleFeedbackWrite>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackArticlesCreate(articleFeedbackWriteRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create or list Article Feedback.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackArticlesList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedArticleFeedbackReadList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackArticlesList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create Company Feedback.
         * @param {CompanyFeedbackWriteRequest} companyFeedbackWriteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackCompaniesCreate(companyFeedbackWriteRequest: CompanyFeedbackWriteRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CompanyFeedbackWrite>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackCompaniesCreate(companyFeedbackWriteRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create Company Feedback.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackCompaniesList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedCompanyFeedbackReadList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackCompaniesList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Validate TradeName.
         * @param {TradeNameSuggestionRequest} tradeNameSuggestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackCompaniesTradeNamesCreate(tradeNameSuggestionRequest: TradeNameSuggestionRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TradeNameSuggestion>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackCompaniesTradeNamesCreate(tradeNameSuggestionRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List or add comments on a support request.
         * @param {string} externalId 
         * @param {SupportRequestCommentRequest} supportRequestCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackSupportRequestsCommentsCreate(externalId: string, supportRequestCommentRequest: SupportRequestCommentRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportRequestComment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackSupportRequestsCommentsCreate(externalId, supportRequestCommentRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List or add comments on a support request.
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackSupportRequestsCommentsList(externalId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedSupportRequestCommentList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackSupportRequestsCommentsList(externalId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a Support Request and return its canonical read representation.
         * @param {SupportRequestCreateRequest} supportRequestCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackSupportRequestsCreate(supportRequestCreateRequest: SupportRequestCreateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportRequest>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackSupportRequestsCreate(supportRequestCreateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List accessible Support Requests in the selected scope.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {'mine' | 'profile'} [scope] List the current user\&#39;s requests, or all requests belonging to the active profile when the user is a profile admin.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort requests by creation date.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackSupportRequestsList(nextKey?: string, scope?: 'mine' | 'profile', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedSupportRequestList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackSupportRequestsList(nextKey, scope, sortingOrder, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve a single support request.
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async feedbackSupportRequestsRetrieve(externalId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SupportRequest>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.feedbackSupportRequestsRetrieve(externalId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * FeedbackApi - factory interface
 * @export
 */
export const FeedbackApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FeedbackApiFp(configuration)
    return {
        /**
         * Post method.
         * @param {ArticleFeedbackWriteRequest} articleFeedbackWriteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackArticlesCreate(articleFeedbackWriteRequest: ArticleFeedbackWriteRequest, options?: any): AxiosPromise<ArticleFeedbackWrite> {
            return localVarFp.feedbackArticlesCreate(articleFeedbackWriteRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create or list Article Feedback.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackArticlesList(nextKey?: string, options?: any): AxiosPromise<PaginatedArticleFeedbackReadList> {
            return localVarFp.feedbackArticlesList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Create Company Feedback.
         * @param {CompanyFeedbackWriteRequest} companyFeedbackWriteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackCompaniesCreate(companyFeedbackWriteRequest: CompanyFeedbackWriteRequest, options?: any): AxiosPromise<CompanyFeedbackWrite> {
            return localVarFp.feedbackCompaniesCreate(companyFeedbackWriteRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create Company Feedback.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackCompaniesList(nextKey?: string, options?: any): AxiosPromise<PaginatedCompanyFeedbackReadList> {
            return localVarFp.feedbackCompaniesList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Validate TradeName.
         * @param {TradeNameSuggestionRequest} tradeNameSuggestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackCompaniesTradeNamesCreate(tradeNameSuggestionRequest: TradeNameSuggestionRequest, options?: any): AxiosPromise<TradeNameSuggestion> {
            return localVarFp.feedbackCompaniesTradeNamesCreate(tradeNameSuggestionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List or add comments on a support request.
         * @param {string} externalId 
         * @param {SupportRequestCommentRequest} supportRequestCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsCommentsCreate(externalId: string, supportRequestCommentRequest: SupportRequestCommentRequest, options?: any): AxiosPromise<SupportRequestComment> {
            return localVarFp.feedbackSupportRequestsCommentsCreate(externalId, supportRequestCommentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List or add comments on a support request.
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsCommentsList(externalId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedSupportRequestCommentList> {
            return localVarFp.feedbackSupportRequestsCommentsList(externalId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a Support Request and return its canonical read representation.
         * @param {SupportRequestCreateRequest} supportRequestCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsCreate(supportRequestCreateRequest: SupportRequestCreateRequest, options?: any): AxiosPromise<SupportRequest> {
            return localVarFp.feedbackSupportRequestsCreate(supportRequestCreateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List accessible Support Requests in the selected scope.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {'mine' | 'profile'} [scope] List the current user\&#39;s requests, or all requests belonging to the active profile when the user is a profile admin.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort requests by creation date.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsList(nextKey?: string, scope?: 'mine' | 'profile', sortingOrder?: 'ascending' | 'descending', options?: any): AxiosPromise<PaginatedSupportRequestList> {
            return localVarFp.feedbackSupportRequestsList(nextKey, scope, sortingOrder, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve a single support request.
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        feedbackSupportRequestsRetrieve(externalId: string, options?: any): AxiosPromise<SupportRequest> {
            return localVarFp.feedbackSupportRequestsRetrieve(externalId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FeedbackApi - interface
 * @export
 * @interface FeedbackApi
 */
export interface FeedbackApiInterface {
    /**
     * Post method.
     * @param {ArticleFeedbackWriteRequest} articleFeedbackWriteRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackArticlesCreate(articleFeedbackWriteRequest: ArticleFeedbackWriteRequest, options?: AxiosRequestConfig): AxiosPromise<ArticleFeedbackWrite>;

    /**
     * Create or list Article Feedback.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackArticlesList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedArticleFeedbackReadList>;

    /**
     * Create Company Feedback.
     * @param {CompanyFeedbackWriteRequest} companyFeedbackWriteRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackCompaniesCreate(companyFeedbackWriteRequest: CompanyFeedbackWriteRequest, options?: AxiosRequestConfig): AxiosPromise<CompanyFeedbackWrite>;

    /**
     * Create Company Feedback.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackCompaniesList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedCompanyFeedbackReadList>;

    /**
     * Validate TradeName.
     * @param {TradeNameSuggestionRequest} tradeNameSuggestionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackCompaniesTradeNamesCreate(tradeNameSuggestionRequest: TradeNameSuggestionRequest, options?: AxiosRequestConfig): AxiosPromise<TradeNameSuggestion>;

    /**
     * List or add comments on a support request.
     * @param {string} externalId 
     * @param {SupportRequestCommentRequest} supportRequestCommentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackSupportRequestsCommentsCreate(externalId: string, supportRequestCommentRequest: SupportRequestCommentRequest, options?: AxiosRequestConfig): AxiosPromise<SupportRequestComment>;

    /**
     * List or add comments on a support request.
     * @param {string} externalId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackSupportRequestsCommentsList(externalId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedSupportRequestCommentList>;

    /**
     * Create a Support Request and return its canonical read representation.
     * @param {SupportRequestCreateRequest} supportRequestCreateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackSupportRequestsCreate(supportRequestCreateRequest: SupportRequestCreateRequest, options?: AxiosRequestConfig): AxiosPromise<SupportRequest>;

    /**
     * List accessible Support Requests in the selected scope.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {'mine' | 'profile'} [scope] List the current user\&#39;s requests, or all requests belonging to the active profile when the user is a profile admin.
     * @param {'ascending' | 'descending'} [sortingOrder] Sort requests by creation date.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackSupportRequestsList(nextKey?: string, scope?: 'mine' | 'profile', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): AxiosPromise<PaginatedSupportRequestList>;

    /**
     * Retrieve a single support request.
     * @param {string} externalId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApiInterface
     */
    feedbackSupportRequestsRetrieve(externalId: string, options?: AxiosRequestConfig): AxiosPromise<SupportRequest>;

}

/**
 * Request parameters for feedbackArticlesCreate operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackArticlesCreateRequest
 */
export interface FeedbackApiFeedbackArticlesCreateRequest {
    /**
     * 
     * @type {ArticleFeedbackWriteRequest}
     * @memberof FeedbackApiFeedbackArticlesCreate
     */
    readonly articleFeedbackWriteRequest: ArticleFeedbackWriteRequest
}

/**
 * Request parameters for feedbackArticlesList operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackArticlesListRequest
 */
export interface FeedbackApiFeedbackArticlesListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof FeedbackApiFeedbackArticlesList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for feedbackCompaniesCreate operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackCompaniesCreateRequest
 */
export interface FeedbackApiFeedbackCompaniesCreateRequest {
    /**
     * 
     * @type {CompanyFeedbackWriteRequest}
     * @memberof FeedbackApiFeedbackCompaniesCreate
     */
    readonly companyFeedbackWriteRequest: CompanyFeedbackWriteRequest
}

/**
 * Request parameters for feedbackCompaniesList operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackCompaniesListRequest
 */
export interface FeedbackApiFeedbackCompaniesListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof FeedbackApiFeedbackCompaniesList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for feedbackCompaniesTradeNamesCreate operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackCompaniesTradeNamesCreateRequest
 */
export interface FeedbackApiFeedbackCompaniesTradeNamesCreateRequest {
    /**
     * 
     * @type {TradeNameSuggestionRequest}
     * @memberof FeedbackApiFeedbackCompaniesTradeNamesCreate
     */
    readonly tradeNameSuggestionRequest: TradeNameSuggestionRequest
}

/**
 * Request parameters for feedbackSupportRequestsCommentsCreate operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackSupportRequestsCommentsCreateRequest
 */
export interface FeedbackApiFeedbackSupportRequestsCommentsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof FeedbackApiFeedbackSupportRequestsCommentsCreate
     */
    readonly externalId: string

    /**
     * 
     * @type {SupportRequestCommentRequest}
     * @memberof FeedbackApiFeedbackSupportRequestsCommentsCreate
     */
    readonly supportRequestCommentRequest: SupportRequestCommentRequest
}

/**
 * Request parameters for feedbackSupportRequestsCommentsList operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackSupportRequestsCommentsListRequest
 */
export interface FeedbackApiFeedbackSupportRequestsCommentsListRequest {
    /**
     * 
     * @type {string}
     * @memberof FeedbackApiFeedbackSupportRequestsCommentsList
     */
    readonly externalId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof FeedbackApiFeedbackSupportRequestsCommentsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for feedbackSupportRequestsCreate operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackSupportRequestsCreateRequest
 */
export interface FeedbackApiFeedbackSupportRequestsCreateRequest {
    /**
     * 
     * @type {SupportRequestCreateRequest}
     * @memberof FeedbackApiFeedbackSupportRequestsCreate
     */
    readonly supportRequestCreateRequest: SupportRequestCreateRequest
}

/**
 * Request parameters for feedbackSupportRequestsList operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackSupportRequestsListRequest
 */
export interface FeedbackApiFeedbackSupportRequestsListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof FeedbackApiFeedbackSupportRequestsList
     */
    readonly nextKey?: string

    /**
     * List the current user\&#39;s requests, or all requests belonging to the active profile when the user is a profile admin.
     * @type {'mine' | 'profile'}
     * @memberof FeedbackApiFeedbackSupportRequestsList
     */
    readonly scope?: 'mine' | 'profile'

    /**
     * Sort requests by creation date.
     * @type {'ascending' | 'descending'}
     * @memberof FeedbackApiFeedbackSupportRequestsList
     */
    readonly sortingOrder?: 'ascending' | 'descending'
}

/**
 * Request parameters for feedbackSupportRequestsRetrieve operation in FeedbackApi.
 * @export
 * @interface FeedbackApiFeedbackSupportRequestsRetrieveRequest
 */
export interface FeedbackApiFeedbackSupportRequestsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof FeedbackApiFeedbackSupportRequestsRetrieve
     */
    readonly externalId: string
}

/**
 * FeedbackApi - object-oriented interface
 * @export
 * @class FeedbackApi
 * @extends {BaseAPI}
 */
export class FeedbackApi extends BaseAPI implements FeedbackApiInterface {
    /**
     * Post method.
     * @param {FeedbackApiFeedbackArticlesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackArticlesCreate(requestParameters: FeedbackApiFeedbackArticlesCreateRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackArticlesCreate(requestParameters.articleFeedbackWriteRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create or list Article Feedback.
     * @param {FeedbackApiFeedbackArticlesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackArticlesList(requestParameters: FeedbackApiFeedbackArticlesListRequest = {}, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackArticlesList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create Company Feedback.
     * @param {FeedbackApiFeedbackCompaniesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackCompaniesCreate(requestParameters: FeedbackApiFeedbackCompaniesCreateRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackCompaniesCreate(requestParameters.companyFeedbackWriteRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create Company Feedback.
     * @param {FeedbackApiFeedbackCompaniesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackCompaniesList(requestParameters: FeedbackApiFeedbackCompaniesListRequest = {}, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackCompaniesList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Validate TradeName.
     * @param {FeedbackApiFeedbackCompaniesTradeNamesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackCompaniesTradeNamesCreate(requestParameters: FeedbackApiFeedbackCompaniesTradeNamesCreateRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackCompaniesTradeNamesCreate(requestParameters.tradeNameSuggestionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List or add comments on a support request.
     * @param {FeedbackApiFeedbackSupportRequestsCommentsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackSupportRequestsCommentsCreate(requestParameters: FeedbackApiFeedbackSupportRequestsCommentsCreateRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackSupportRequestsCommentsCreate(requestParameters.externalId, requestParameters.supportRequestCommentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List or add comments on a support request.
     * @param {FeedbackApiFeedbackSupportRequestsCommentsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackSupportRequestsCommentsList(requestParameters: FeedbackApiFeedbackSupportRequestsCommentsListRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackSupportRequestsCommentsList(requestParameters.externalId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a Support Request and return its canonical read representation.
     * @param {FeedbackApiFeedbackSupportRequestsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackSupportRequestsCreate(requestParameters: FeedbackApiFeedbackSupportRequestsCreateRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackSupportRequestsCreate(requestParameters.supportRequestCreateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List accessible Support Requests in the selected scope.
     * @param {FeedbackApiFeedbackSupportRequestsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackSupportRequestsList(requestParameters: FeedbackApiFeedbackSupportRequestsListRequest = {}, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackSupportRequestsList(requestParameters.nextKey, requestParameters.scope, requestParameters.sortingOrder, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve a single support request.
     * @param {FeedbackApiFeedbackSupportRequestsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FeedbackApi
     */
    public feedbackSupportRequestsRetrieve(requestParameters: FeedbackApiFeedbackSupportRequestsRetrieveRequest, options?: AxiosRequestConfig) {
        return FeedbackApiFp(this.configuration).feedbackSupportRequestsRetrieve(requestParameters.externalId, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * IntegrationsApi - axios parameter creator
 * @export
 */
export const IntegrationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * List all syncs across all integrations for the authenticated user\'s profile.
         * @summary List all syncs
         * @param {string} [integration] Filter by integration external_id
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [status] Filter by sync status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationSyncsList: async (integration?: string, nextKey?: string, status?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/integrations/integration-syncs/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (integration !== undefined) {
                localVarQueryParameter['integration'] = integration;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new integration with external system.
         * @summary Create integration
         * @param {IntegrationCreateRequest} integrationCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsCreate: async (integrationCreateRequest: IntegrationCreateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'integrationCreateRequest' is not null or undefined
            assertParamExists('integrationsIntegrationsCreate', 'integrationCreateRequest', integrationCreateRequest)
            const localVarPath = `/integrations/integrations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(integrationCreateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete an integration.
         * @summary Delete integration
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsDestroy: async (externalId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsDestroy', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new field mapping for an integration.
         * @summary Create field mapping
         * @param {string} integrationExternalId 
         * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsCreate: async (integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsCreate', 'integrationExternalId', integrationExternalId)
            // verify required parameter 'integrationFieldMappingRequest' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsCreate', 'integrationFieldMappingRequest', integrationFieldMappingRequest)
            const localVarPath = `/integrations/integrations/{integration_external_id}/field-mappings/`
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(integrationFieldMappingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete a field mapping.
         * @summary Delete field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsDestroy: async (id: number, integrationExternalId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsDestroy', 'id', id)
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsDestroy', 'integrationExternalId', integrationExternalId)
            const localVarPath = `/integrations/integrations/{integration_external_id}/field-mappings/{id}/`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List all field mappings for an integration.
         * @summary List field mappings
         * @param {string} integrationExternalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsList: async (integrationExternalId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsList', 'integrationExternalId', integrationExternalId)
            const localVarPath = `/integrations/integrations/{integration_external_id}/field-mappings/`
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update specific fields of a field mapping.
         * @summary Partially update field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {PatchedIntegrationFieldMappingRequest} [patchedIntegrationFieldMappingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsPartialUpdate: async (id: number, integrationExternalId: string, patchedIntegrationFieldMappingRequest?: PatchedIntegrationFieldMappingRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsPartialUpdate', 'id', id)
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsPartialUpdate', 'integrationExternalId', integrationExternalId)
            const localVarPath = `/integrations/integrations/{integration_external_id}/field-mappings/{id}/`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedIntegrationFieldMappingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get details of a specific field mapping.
         * @summary Get field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsRetrieve: async (id: number, integrationExternalId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsRetrieve', 'id', id)
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsRetrieve', 'integrationExternalId', integrationExternalId)
            const localVarPath = `/integrations/integrations/{integration_external_id}/field-mappings/{id}/`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update a field mapping.
         * @summary Update field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsUpdate: async (id: number, integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsUpdate', 'id', id)
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsUpdate', 'integrationExternalId', integrationExternalId)
            // verify required parameter 'integrationFieldMappingRequest' is not null or undefined
            assertParamExists('integrationsIntegrationsFieldMappingsUpdate', 'integrationFieldMappingRequest', integrationFieldMappingRequest)
            const localVarPath = `/integrations/integrations/{integration_external_id}/field-mappings/{id}/`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)))
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(integrationFieldMappingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all integrations for the authenticated user\'s profile.
         * @summary List integrations
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/integrations/integrations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get company mappings for this integration.
         * @summary Get company mappings
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [syncEnabled] Filter by sync enabled status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsMappingsList: async (externalId: string, nextKey?: string, syncEnabled?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsMappingsList', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/mappings/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (syncEnabled !== undefined) {
                localVarQueryParameter['sync_enabled'] = syncEnabled;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update specific fields of an integration.
         * @summary Partially update integration
         * @param {string} externalId 
         * @param {PatchedIntegrationRequest} [patchedIntegrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsPartialUpdate: async (externalId: string, patchedIntegrationRequest?: PatchedIntegrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsPartialUpdate', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedIntegrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get details of a specific integration.
         * @summary Get integration details
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsRetrieve: async (externalId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsRetrieve', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sync a single company from the external system.
         * @summary Sync single company
         * @param {string} integrationExternalId 
         * @param {SyncCompanyRequestRequest} syncCompanyRequestRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsSyncCompanyCreate: async (integrationExternalId: string, syncCompanyRequestRequest: SyncCompanyRequestRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'integrationExternalId' is not null or undefined
            assertParamExists('integrationsIntegrationsSyncCompanyCreate', 'integrationExternalId', integrationExternalId)
            // verify required parameter 'syncCompanyRequestRequest' is not null or undefined
            assertParamExists('integrationsIntegrationsSyncCompanyCreate', 'syncCompanyRequestRequest', syncCompanyRequestRequest)
            const localVarPath = `/integrations/integrations/{integration_external_id}/sync-company/`
                .replace(`{${"integration_external_id"}}`, encodeURIComponent(String(integrationExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(syncCompanyRequestRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Manually trigger a sync with the external system.
         * @summary Trigger integration sync
         * @param {string} externalId 
         * @param {SyncTriggerRequest} [syncTriggerRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsSyncCreate: async (externalId: string, syncTriggerRequest?: SyncTriggerRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsSyncCreate', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/sync/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(syncTriggerRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get sync history for this integration.
         * @summary Get sync history
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsSyncsList: async (externalId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsSyncsList', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/syncs/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Test the integration connection with the external system.
         * @summary Test integration connection
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsTestCreate: async (externalId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsTestCreate', 'externalId', externalId)
            const localVarPath = `/integrations/integrations/{external_id}/test/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update an integration\'s configuration or settings.
         * @summary Update integration
         * @param {string} externalId 
         * @param {IntegrationRequest} integrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsUpdate: async (externalId: string, integrationRequest: IntegrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalId' is not null or undefined
            assertParamExists('integrationsIntegrationsUpdate', 'externalId', externalId)
            // verify required parameter 'integrationRequest' is not null or undefined
            assertParamExists('integrationsIntegrationsUpdate', 'integrationRequest', integrationRequest)
            const localVarPath = `/integrations/integrations/{external_id}/`
                .replace(`{${"external_id"}}`, encodeURIComponent(String(externalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(integrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * IntegrationsApi - functional programming interface
 * @export
 */
export const IntegrationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = IntegrationsApiAxiosParamCreator(configuration)
    return {
        /**
         * List all syncs across all integrations for the authenticated user\'s profile.
         * @summary List all syncs
         * @param {string} [integration] Filter by integration external_id
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [status] Filter by sync status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationSyncsList(integration?: string, nextKey?: string, status?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIntegrationSyncList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationSyncsList(integration, nextKey, status, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a new integration with external system.
         * @summary Create integration
         * @param {IntegrationCreateRequest} integrationCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsCreate(integrationCreateRequest: IntegrationCreateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationCreate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsCreate(integrationCreateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete an integration.
         * @summary Delete integration
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsDestroy(externalId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsDestroy(externalId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a new field mapping for an integration.
         * @summary Create field mapping
         * @param {string} integrationExternalId 
         * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsFieldMappingsCreate(integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationFieldMapping>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsFieldMappingsCreate(integrationExternalId, integrationFieldMappingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete a field mapping.
         * @summary Delete field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsFieldMappingsDestroy(id: number, integrationExternalId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsFieldMappingsDestroy(id, integrationExternalId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List all field mappings for an integration.
         * @summary List field mappings
         * @param {string} integrationExternalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsFieldMappingsList(integrationExternalId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIntegrationFieldMappingList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsFieldMappingsList(integrationExternalId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update specific fields of a field mapping.
         * @summary Partially update field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {PatchedIntegrationFieldMappingRequest} [patchedIntegrationFieldMappingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsFieldMappingsPartialUpdate(id: number, integrationExternalId: string, patchedIntegrationFieldMappingRequest?: PatchedIntegrationFieldMappingRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationFieldMapping>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsFieldMappingsPartialUpdate(id, integrationExternalId, patchedIntegrationFieldMappingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get details of a specific field mapping.
         * @summary Get field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsFieldMappingsRetrieve(id: number, integrationExternalId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationFieldMapping>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsFieldMappingsRetrieve(id, integrationExternalId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update a field mapping.
         * @summary Update field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsFieldMappingsUpdate(id: number, integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationFieldMapping>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsFieldMappingsUpdate(id, integrationExternalId, integrationFieldMappingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Returns all integrations for the authenticated user\'s profile.
         * @summary List integrations
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIntegrationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get company mappings for this integration.
         * @summary Get company mappings
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [syncEnabled] Filter by sync enabled status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsMappingsList(externalId: string, nextKey?: string, syncEnabled?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIntegrationCompanyMappingList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsMappingsList(externalId, nextKey, syncEnabled, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update specific fields of an integration.
         * @summary Partially update integration
         * @param {string} externalId 
         * @param {PatchedIntegrationRequest} [patchedIntegrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsPartialUpdate(externalId: string, patchedIntegrationRequest?: PatchedIntegrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Integration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsPartialUpdate(externalId, patchedIntegrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get details of a specific integration.
         * @summary Get integration details
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsRetrieve(externalId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Integration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsRetrieve(externalId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Sync a single company from the external system.
         * @summary Sync single company
         * @param {string} integrationExternalId 
         * @param {SyncCompanyRequestRequest} syncCompanyRequestRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsSyncCompanyCreate(integrationExternalId: string, syncCompanyRequestRequest: SyncCompanyRequestRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationsIntegrationsSyncCreate200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsSyncCompanyCreate(integrationExternalId, syncCompanyRequestRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Manually trigger a sync with the external system.
         * @summary Trigger integration sync
         * @param {string} externalId 
         * @param {SyncTriggerRequest} [syncTriggerRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsSyncCreate(externalId: string, syncTriggerRequest?: SyncTriggerRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationsIntegrationsSyncCreate200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsSyncCreate(externalId, syncTriggerRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get sync history for this integration.
         * @summary Get sync history
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsSyncsList(externalId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedIntegrationSyncList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsSyncsList(externalId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Test the integration connection with the external system.
         * @summary Test integration connection
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsTestCreate(externalId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntegrationsIntegrationsSyncCreate200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsTestCreate(externalId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update an integration\'s configuration or settings.
         * @summary Update integration
         * @param {string} externalId 
         * @param {IntegrationRequest} integrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async integrationsIntegrationsUpdate(externalId: string, integrationRequest: IntegrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Integration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.integrationsIntegrationsUpdate(externalId, integrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * IntegrationsApi - factory interface
 * @export
 */
export const IntegrationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = IntegrationsApiFp(configuration)
    return {
        /**
         * List all syncs across all integrations for the authenticated user\'s profile.
         * @summary List all syncs
         * @param {string} [integration] Filter by integration external_id
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [status] Filter by sync status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationSyncsList(integration?: string, nextKey?: string, status?: string, options?: any): AxiosPromise<PaginatedIntegrationSyncList> {
            return localVarFp.integrationsIntegrationSyncsList(integration, nextKey, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new integration with external system.
         * @summary Create integration
         * @param {IntegrationCreateRequest} integrationCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsCreate(integrationCreateRequest: IntegrationCreateRequest, options?: any): AxiosPromise<IntegrationCreate> {
            return localVarFp.integrationsIntegrationsCreate(integrationCreateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete an integration.
         * @summary Delete integration
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsDestroy(externalId: string, options?: any): AxiosPromise<void> {
            return localVarFp.integrationsIntegrationsDestroy(externalId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new field mapping for an integration.
         * @summary Create field mapping
         * @param {string} integrationExternalId 
         * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsCreate(integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options?: any): AxiosPromise<IntegrationFieldMapping> {
            return localVarFp.integrationsIntegrationsFieldMappingsCreate(integrationExternalId, integrationFieldMappingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete a field mapping.
         * @summary Delete field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsDestroy(id: number, integrationExternalId: string, options?: any): AxiosPromise<void> {
            return localVarFp.integrationsIntegrationsFieldMappingsDestroy(id, integrationExternalId, options).then((request) => request(axios, basePath));
        },
        /**
         * List all field mappings for an integration.
         * @summary List field mappings
         * @param {string} integrationExternalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsList(integrationExternalId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedIntegrationFieldMappingList> {
            return localVarFp.integrationsIntegrationsFieldMappingsList(integrationExternalId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Update specific fields of a field mapping.
         * @summary Partially update field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {PatchedIntegrationFieldMappingRequest} [patchedIntegrationFieldMappingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsPartialUpdate(id: number, integrationExternalId: string, patchedIntegrationFieldMappingRequest?: PatchedIntegrationFieldMappingRequest, options?: any): AxiosPromise<IntegrationFieldMapping> {
            return localVarFp.integrationsIntegrationsFieldMappingsPartialUpdate(id, integrationExternalId, patchedIntegrationFieldMappingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get details of a specific field mapping.
         * @summary Get field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsRetrieve(id: number, integrationExternalId: string, options?: any): AxiosPromise<IntegrationFieldMapping> {
            return localVarFp.integrationsIntegrationsFieldMappingsRetrieve(id, integrationExternalId, options).then((request) => request(axios, basePath));
        },
        /**
         * Update a field mapping.
         * @summary Update field mapping
         * @param {number} id 
         * @param {string} integrationExternalId 
         * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsFieldMappingsUpdate(id: number, integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options?: any): AxiosPromise<IntegrationFieldMapping> {
            return localVarFp.integrationsIntegrationsFieldMappingsUpdate(id, integrationExternalId, integrationFieldMappingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all integrations for the authenticated user\'s profile.
         * @summary List integrations
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsList(nextKey?: string, options?: any): AxiosPromise<PaginatedIntegrationList> {
            return localVarFp.integrationsIntegrationsList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Get company mappings for this integration.
         * @summary Get company mappings
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [syncEnabled] Filter by sync enabled status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsMappingsList(externalId: string, nextKey?: string, syncEnabled?: boolean, options?: any): AxiosPromise<PaginatedIntegrationCompanyMappingList> {
            return localVarFp.integrationsIntegrationsMappingsList(externalId, nextKey, syncEnabled, options).then((request) => request(axios, basePath));
        },
        /**
         * Update specific fields of an integration.
         * @summary Partially update integration
         * @param {string} externalId 
         * @param {PatchedIntegrationRequest} [patchedIntegrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsPartialUpdate(externalId: string, patchedIntegrationRequest?: PatchedIntegrationRequest, options?: any): AxiosPromise<Integration> {
            return localVarFp.integrationsIntegrationsPartialUpdate(externalId, patchedIntegrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get details of a specific integration.
         * @summary Get integration details
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsRetrieve(externalId: string, options?: any): AxiosPromise<Integration> {
            return localVarFp.integrationsIntegrationsRetrieve(externalId, options).then((request) => request(axios, basePath));
        },
        /**
         * Sync a single company from the external system.
         * @summary Sync single company
         * @param {string} integrationExternalId 
         * @param {SyncCompanyRequestRequest} syncCompanyRequestRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsSyncCompanyCreate(integrationExternalId: string, syncCompanyRequestRequest: SyncCompanyRequestRequest, options?: any): AxiosPromise<IntegrationsIntegrationsSyncCreate200Response> {
            return localVarFp.integrationsIntegrationsSyncCompanyCreate(integrationExternalId, syncCompanyRequestRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Manually trigger a sync with the external system.
         * @summary Trigger integration sync
         * @param {string} externalId 
         * @param {SyncTriggerRequest} [syncTriggerRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsSyncCreate(externalId: string, syncTriggerRequest?: SyncTriggerRequest, options?: any): AxiosPromise<IntegrationsIntegrationsSyncCreate200Response> {
            return localVarFp.integrationsIntegrationsSyncCreate(externalId, syncTriggerRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get sync history for this integration.
         * @summary Get sync history
         * @param {string} externalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsSyncsList(externalId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedIntegrationSyncList> {
            return localVarFp.integrationsIntegrationsSyncsList(externalId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Test the integration connection with the external system.
         * @summary Test integration connection
         * @param {string} externalId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsTestCreate(externalId: string, options?: any): AxiosPromise<IntegrationsIntegrationsSyncCreate200Response> {
            return localVarFp.integrationsIntegrationsTestCreate(externalId, options).then((request) => request(axios, basePath));
        },
        /**
         * Update an integration\'s configuration or settings.
         * @summary Update integration
         * @param {string} externalId 
         * @param {IntegrationRequest} integrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        integrationsIntegrationsUpdate(externalId: string, integrationRequest: IntegrationRequest, options?: any): AxiosPromise<Integration> {
            return localVarFp.integrationsIntegrationsUpdate(externalId, integrationRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * IntegrationsApi - interface
 * @export
 * @interface IntegrationsApi
 */
export interface IntegrationsApiInterface {
    /**
     * List all syncs across all integrations for the authenticated user\'s profile.
     * @summary List all syncs
     * @param {string} [integration] Filter by integration external_id
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [status] Filter by sync status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationSyncsList(integration?: string, nextKey?: string, status?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedIntegrationSyncList>;

    /**
     * Create a new integration with external system.
     * @summary Create integration
     * @param {IntegrationCreateRequest} integrationCreateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsCreate(integrationCreateRequest: IntegrationCreateRequest, options?: AxiosRequestConfig): AxiosPromise<IntegrationCreate>;

    /**
     * Delete an integration.
     * @summary Delete integration
     * @param {string} externalId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsDestroy(externalId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Create a new field mapping for an integration.
     * @summary Create field mapping
     * @param {string} integrationExternalId 
     * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsFieldMappingsCreate(integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options?: AxiosRequestConfig): AxiosPromise<IntegrationFieldMapping>;

    /**
     * Delete a field mapping.
     * @summary Delete field mapping
     * @param {number} id 
     * @param {string} integrationExternalId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsFieldMappingsDestroy(id: number, integrationExternalId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List all field mappings for an integration.
     * @summary List field mappings
     * @param {string} integrationExternalId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsFieldMappingsList(integrationExternalId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedIntegrationFieldMappingList>;

    /**
     * Update specific fields of a field mapping.
     * @summary Partially update field mapping
     * @param {number} id 
     * @param {string} integrationExternalId 
     * @param {PatchedIntegrationFieldMappingRequest} [patchedIntegrationFieldMappingRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsFieldMappingsPartialUpdate(id: number, integrationExternalId: string, patchedIntegrationFieldMappingRequest?: PatchedIntegrationFieldMappingRequest, options?: AxiosRequestConfig): AxiosPromise<IntegrationFieldMapping>;

    /**
     * Get details of a specific field mapping.
     * @summary Get field mapping
     * @param {number} id 
     * @param {string} integrationExternalId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsFieldMappingsRetrieve(id: number, integrationExternalId: string, options?: AxiosRequestConfig): AxiosPromise<IntegrationFieldMapping>;

    /**
     * Update a field mapping.
     * @summary Update field mapping
     * @param {number} id 
     * @param {string} integrationExternalId 
     * @param {IntegrationFieldMappingRequest} integrationFieldMappingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsFieldMappingsUpdate(id: number, integrationExternalId: string, integrationFieldMappingRequest: IntegrationFieldMappingRequest, options?: AxiosRequestConfig): AxiosPromise<IntegrationFieldMapping>;

    /**
     * Returns all integrations for the authenticated user\'s profile.
     * @summary List integrations
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedIntegrationList>;

    /**
     * Get company mappings for this integration.
     * @summary Get company mappings
     * @param {string} externalId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {boolean} [syncEnabled] Filter by sync enabled status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsMappingsList(externalId: string, nextKey?: string, syncEnabled?: boolean, options?: AxiosRequestConfig): AxiosPromise<PaginatedIntegrationCompanyMappingList>;

    /**
     * Update specific fields of an integration.
     * @summary Partially update integration
     * @param {string} externalId 
     * @param {PatchedIntegrationRequest} [patchedIntegrationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsPartialUpdate(externalId: string, patchedIntegrationRequest?: PatchedIntegrationRequest, options?: AxiosRequestConfig): AxiosPromise<Integration>;

    /**
     * Get details of a specific integration.
     * @summary Get integration details
     * @param {string} externalId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsRetrieve(externalId: string, options?: AxiosRequestConfig): AxiosPromise<Integration>;

    /**
     * Sync a single company from the external system.
     * @summary Sync single company
     * @param {string} integrationExternalId 
     * @param {SyncCompanyRequestRequest} syncCompanyRequestRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsSyncCompanyCreate(integrationExternalId: string, syncCompanyRequestRequest: SyncCompanyRequestRequest, options?: AxiosRequestConfig): AxiosPromise<IntegrationsIntegrationsSyncCreate200Response>;

    /**
     * Manually trigger a sync with the external system.
     * @summary Trigger integration sync
     * @param {string} externalId 
     * @param {SyncTriggerRequest} [syncTriggerRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsSyncCreate(externalId: string, syncTriggerRequest?: SyncTriggerRequest, options?: AxiosRequestConfig): AxiosPromise<IntegrationsIntegrationsSyncCreate200Response>;

    /**
     * Get sync history for this integration.
     * @summary Get sync history
     * @param {string} externalId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsSyncsList(externalId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedIntegrationSyncList>;

    /**
     * Test the integration connection with the external system.
     * @summary Test integration connection
     * @param {string} externalId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsTestCreate(externalId: string, options?: AxiosRequestConfig): AxiosPromise<IntegrationsIntegrationsSyncCreate200Response>;

    /**
     * Update an integration\'s configuration or settings.
     * @summary Update integration
     * @param {string} externalId 
     * @param {IntegrationRequest} integrationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApiInterface
     */
    integrationsIntegrationsUpdate(externalId: string, integrationRequest: IntegrationRequest, options?: AxiosRequestConfig): AxiosPromise<Integration>;

}

/**
 * Request parameters for integrationsIntegrationSyncsList operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationSyncsListRequest
 */
export interface IntegrationsApiIntegrationsIntegrationSyncsListRequest {
    /**
     * Filter by integration external_id
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationSyncsList
     */
    readonly integration?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationSyncsList
     */
    readonly nextKey?: string

    /**
     * Filter by sync status
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationSyncsList
     */
    readonly status?: string
}

/**
 * Request parameters for integrationsIntegrationsCreate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsCreateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsCreateRequest {
    /**
     * 
     * @type {IntegrationCreateRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsCreate
     */
    readonly integrationCreateRequest: IntegrationCreateRequest
}

/**
 * Request parameters for integrationsIntegrationsDestroy operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsDestroyRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsDestroy
     */
    readonly externalId: string
}

/**
 * Request parameters for integrationsIntegrationsFieldMappingsCreate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsFieldMappingsCreateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsFieldMappingsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsCreate
     */
    readonly integrationExternalId: string

    /**
     * 
     * @type {IntegrationFieldMappingRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsCreate
     */
    readonly integrationFieldMappingRequest: IntegrationFieldMappingRequest
}

/**
 * Request parameters for integrationsIntegrationsFieldMappingsDestroy operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsFieldMappingsDestroyRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsFieldMappingsDestroyRequest {
    /**
     * 
     * @type {number}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsDestroy
     */
    readonly id: number

    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsDestroy
     */
    readonly integrationExternalId: string
}

/**
 * Request parameters for integrationsIntegrationsFieldMappingsList operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsFieldMappingsListRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsFieldMappingsListRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsList
     */
    readonly integrationExternalId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for integrationsIntegrationsFieldMappingsPartialUpdate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdateRequest {
    /**
     * 
     * @type {number}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdate
     */
    readonly id: number

    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdate
     */
    readonly integrationExternalId: string

    /**
     * 
     * @type {PatchedIntegrationFieldMappingRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdate
     */
    readonly patchedIntegrationFieldMappingRequest?: PatchedIntegrationFieldMappingRequest
}

/**
 * Request parameters for integrationsIntegrationsFieldMappingsRetrieve operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsFieldMappingsRetrieveRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsFieldMappingsRetrieveRequest {
    /**
     * 
     * @type {number}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsRetrieve
     */
    readonly id: number

    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsRetrieve
     */
    readonly integrationExternalId: string
}

/**
 * Request parameters for integrationsIntegrationsFieldMappingsUpdate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdateRequest {
    /**
     * 
     * @type {number}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdate
     */
    readonly id: number

    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdate
     */
    readonly integrationExternalId: string

    /**
     * 
     * @type {IntegrationFieldMappingRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdate
     */
    readonly integrationFieldMappingRequest: IntegrationFieldMappingRequest
}

/**
 * Request parameters for integrationsIntegrationsList operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsListRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for integrationsIntegrationsMappingsList operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsMappingsListRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsMappingsListRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsMappingsList
     */
    readonly externalId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsMappingsList
     */
    readonly nextKey?: string

    /**
     * Filter by sync enabled status
     * @type {boolean}
     * @memberof IntegrationsApiIntegrationsIntegrationsMappingsList
     */
    readonly syncEnabled?: boolean
}

/**
 * Request parameters for integrationsIntegrationsPartialUpdate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsPartialUpdateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsPartialUpdate
     */
    readonly externalId: string

    /**
     * 
     * @type {PatchedIntegrationRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsPartialUpdate
     */
    readonly patchedIntegrationRequest?: PatchedIntegrationRequest
}

/**
 * Request parameters for integrationsIntegrationsRetrieve operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsRetrieveRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsRetrieve
     */
    readonly externalId: string
}

/**
 * Request parameters for integrationsIntegrationsSyncCompanyCreate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsSyncCompanyCreateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsSyncCompanyCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsSyncCompanyCreate
     */
    readonly integrationExternalId: string

    /**
     * 
     * @type {SyncCompanyRequestRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsSyncCompanyCreate
     */
    readonly syncCompanyRequestRequest: SyncCompanyRequestRequest
}

/**
 * Request parameters for integrationsIntegrationsSyncCreate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsSyncCreateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsSyncCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsSyncCreate
     */
    readonly externalId: string

    /**
     * 
     * @type {SyncTriggerRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsSyncCreate
     */
    readonly syncTriggerRequest?: SyncTriggerRequest
}

/**
 * Request parameters for integrationsIntegrationsSyncsList operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsSyncsListRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsSyncsListRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsSyncsList
     */
    readonly externalId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsSyncsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for integrationsIntegrationsTestCreate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsTestCreateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsTestCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsTestCreate
     */
    readonly externalId: string
}

/**
 * Request parameters for integrationsIntegrationsUpdate operation in IntegrationsApi.
 * @export
 * @interface IntegrationsApiIntegrationsIntegrationsUpdateRequest
 */
export interface IntegrationsApiIntegrationsIntegrationsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof IntegrationsApiIntegrationsIntegrationsUpdate
     */
    readonly externalId: string

    /**
     * 
     * @type {IntegrationRequest}
     * @memberof IntegrationsApiIntegrationsIntegrationsUpdate
     */
    readonly integrationRequest: IntegrationRequest
}

/**
 * IntegrationsApi - object-oriented interface
 * @export
 * @class IntegrationsApi
 * @extends {BaseAPI}
 */
export class IntegrationsApi extends BaseAPI implements IntegrationsApiInterface {
    /**
     * List all syncs across all integrations for the authenticated user\'s profile.
     * @summary List all syncs
     * @param {IntegrationsApiIntegrationsIntegrationSyncsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationSyncsList(requestParameters: IntegrationsApiIntegrationsIntegrationSyncsListRequest = {}, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationSyncsList(requestParameters.integration, requestParameters.nextKey, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new integration with external system.
     * @summary Create integration
     * @param {IntegrationsApiIntegrationsIntegrationsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsCreate(requestParameters: IntegrationsApiIntegrationsIntegrationsCreateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsCreate(requestParameters.integrationCreateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete an integration.
     * @summary Delete integration
     * @param {IntegrationsApiIntegrationsIntegrationsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsDestroy(requestParameters: IntegrationsApiIntegrationsIntegrationsDestroyRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsDestroy(requestParameters.externalId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new field mapping for an integration.
     * @summary Create field mapping
     * @param {IntegrationsApiIntegrationsIntegrationsFieldMappingsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsFieldMappingsCreate(requestParameters: IntegrationsApiIntegrationsIntegrationsFieldMappingsCreateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsFieldMappingsCreate(requestParameters.integrationExternalId, requestParameters.integrationFieldMappingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete a field mapping.
     * @summary Delete field mapping
     * @param {IntegrationsApiIntegrationsIntegrationsFieldMappingsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsFieldMappingsDestroy(requestParameters: IntegrationsApiIntegrationsIntegrationsFieldMappingsDestroyRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsFieldMappingsDestroy(requestParameters.id, requestParameters.integrationExternalId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List all field mappings for an integration.
     * @summary List field mappings
     * @param {IntegrationsApiIntegrationsIntegrationsFieldMappingsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsFieldMappingsList(requestParameters: IntegrationsApiIntegrationsIntegrationsFieldMappingsListRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsFieldMappingsList(requestParameters.integrationExternalId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update specific fields of a field mapping.
     * @summary Partially update field mapping
     * @param {IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsFieldMappingsPartialUpdate(requestParameters: IntegrationsApiIntegrationsIntegrationsFieldMappingsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsFieldMappingsPartialUpdate(requestParameters.id, requestParameters.integrationExternalId, requestParameters.patchedIntegrationFieldMappingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get details of a specific field mapping.
     * @summary Get field mapping
     * @param {IntegrationsApiIntegrationsIntegrationsFieldMappingsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsFieldMappingsRetrieve(requestParameters: IntegrationsApiIntegrationsIntegrationsFieldMappingsRetrieveRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsFieldMappingsRetrieve(requestParameters.id, requestParameters.integrationExternalId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update a field mapping.
     * @summary Update field mapping
     * @param {IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsFieldMappingsUpdate(requestParameters: IntegrationsApiIntegrationsIntegrationsFieldMappingsUpdateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsFieldMappingsUpdate(requestParameters.id, requestParameters.integrationExternalId, requestParameters.integrationFieldMappingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all integrations for the authenticated user\'s profile.
     * @summary List integrations
     * @param {IntegrationsApiIntegrationsIntegrationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsList(requestParameters: IntegrationsApiIntegrationsIntegrationsListRequest = {}, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get company mappings for this integration.
     * @summary Get company mappings
     * @param {IntegrationsApiIntegrationsIntegrationsMappingsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsMappingsList(requestParameters: IntegrationsApiIntegrationsIntegrationsMappingsListRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsMappingsList(requestParameters.externalId, requestParameters.nextKey, requestParameters.syncEnabled, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update specific fields of an integration.
     * @summary Partially update integration
     * @param {IntegrationsApiIntegrationsIntegrationsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsPartialUpdate(requestParameters: IntegrationsApiIntegrationsIntegrationsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsPartialUpdate(requestParameters.externalId, requestParameters.patchedIntegrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get details of a specific integration.
     * @summary Get integration details
     * @param {IntegrationsApiIntegrationsIntegrationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsRetrieve(requestParameters: IntegrationsApiIntegrationsIntegrationsRetrieveRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsRetrieve(requestParameters.externalId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sync a single company from the external system.
     * @summary Sync single company
     * @param {IntegrationsApiIntegrationsIntegrationsSyncCompanyCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsSyncCompanyCreate(requestParameters: IntegrationsApiIntegrationsIntegrationsSyncCompanyCreateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsSyncCompanyCreate(requestParameters.integrationExternalId, requestParameters.syncCompanyRequestRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Manually trigger a sync with the external system.
     * @summary Trigger integration sync
     * @param {IntegrationsApiIntegrationsIntegrationsSyncCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsSyncCreate(requestParameters: IntegrationsApiIntegrationsIntegrationsSyncCreateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsSyncCreate(requestParameters.externalId, requestParameters.syncTriggerRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get sync history for this integration.
     * @summary Get sync history
     * @param {IntegrationsApiIntegrationsIntegrationsSyncsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsSyncsList(requestParameters: IntegrationsApiIntegrationsIntegrationsSyncsListRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsSyncsList(requestParameters.externalId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Test the integration connection with the external system.
     * @summary Test integration connection
     * @param {IntegrationsApiIntegrationsIntegrationsTestCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsTestCreate(requestParameters: IntegrationsApiIntegrationsIntegrationsTestCreateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsTestCreate(requestParameters.externalId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update an integration\'s configuration or settings.
     * @summary Update integration
     * @param {IntegrationsApiIntegrationsIntegrationsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof IntegrationsApi
     */
    public integrationsIntegrationsUpdate(requestParameters: IntegrationsApiIntegrationsIntegrationsUpdateRequest, options?: AxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).integrationsIntegrationsUpdate(requestParameters.externalId, requestParameters.integrationRequest, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * NewsApi - axios parameter creator
 * @export
 */
export const NewsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Get article annotations using POST request body for filters.
         * @param {ArticleFiltersRequest} [articleFiltersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesAnnotationsCreate: async (articleFiltersRequest?: ArticleFiltersRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/articles/annotations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(articleFiltersRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get article annotations using GET request query parameters for filters.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesAnnotationsList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/articles/annotations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List or Create Saved article filters view.
         * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersCreate: async (savedArticleFilterRequest: SavedArticleFilterRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterRequest' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersCreate', 'savedArticleFilterRequest', savedArticleFilterRequest)
            const localVarPath = `/news/articles/article_filters/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(savedArticleFilterRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersDestroy: async (savedArticleFilterId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersDestroy', 'savedArticleFilterId', savedArticleFilterId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Saved Article Filters instances for user.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search by query
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersList: async (nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/articles/article_filters/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {PatchedSavedArticleFilterRequest} [patchedSavedArticleFilterRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersPartialUpdate: async (savedArticleFilterId: string, patchedSavedArticleFilterRequest?: PatchedSavedArticleFilterRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersPartialUpdate', 'savedArticleFilterId', savedArticleFilterId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedSavedArticleFilterRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersRetrieve: async (savedArticleFilterId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersRetrieve', 'savedArticleFilterId', savedArticleFilterId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUpdate: async (savedArticleFilterId: string, savedArticleFilterRequest: SavedArticleFilterRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUpdate', 'savedArticleFilterId', savedArticleFilterId)
            // verify required parameter 'savedArticleFilterRequest' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUpdate', 'savedArticleFilterRequest', savedArticleFilterRequest)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(savedArticleFilterRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Saved Article Filters to different user.
         * @param {string} savedArticleFilterId 
         * @param {SavedArticleFilterUserRequest} savedArticleFilterUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersCreate: async (savedArticleFilterId: string, savedArticleFilterUserRequest: SavedArticleFilterUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersCreate', 'savedArticleFilterId', savedArticleFilterId)
            // verify required parameter 'savedArticleFilterUserRequest' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersCreate', 'savedArticleFilterUserRequest', savedArticleFilterUserRequest)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/users/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(savedArticleFilterUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersDestroy: async (savedArticleFilterId: string, userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersDestroy', 'savedArticleFilterId', savedArticleFilterId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersDestroy', 'userId', userId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/users/{user_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Saved Article Filters to different user.
         * @param {string} savedArticleFilterId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersList: async (savedArticleFilterId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersList', 'savedArticleFilterId', savedArticleFilterId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/users/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {PatchedSavedArticleFilterUserUpdateRequest} [patchedSavedArticleFilterUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersPartialUpdate: async (savedArticleFilterId: string, userId: string, patchedSavedArticleFilterUserUpdateRequest?: PatchedSavedArticleFilterUserUpdateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersPartialUpdate', 'savedArticleFilterId', savedArticleFilterId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersPartialUpdate', 'userId', userId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/users/{user_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedSavedArticleFilterUserUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersRetrieve: async (savedArticleFilterId: string, userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersRetrieve', 'savedArticleFilterId', savedArticleFilterId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersRetrieve', 'userId', userId)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/users/{user_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {SavedArticleFilterUserUpdateRequest} savedArticleFilterUserUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersUpdate: async (savedArticleFilterId: string, userId: string, savedArticleFilterUserUpdateRequest: SavedArticleFilterUserUpdateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'savedArticleFilterId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersUpdate', 'savedArticleFilterId', savedArticleFilterId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersUpdate', 'userId', userId)
            // verify required parameter 'savedArticleFilterUserUpdateRequest' is not null or undefined
            assertParamExists('newsArticlesArticleFiltersUsersUpdate', 'savedArticleFilterUserUpdateRequest', savedArticleFilterUserUpdateRequest)
            const localVarPath = `/news/articles/article_filters/{saved_article_filter_id}/users/{user_id}/`
                .replace(`{${"saved_article_filter_id"}}`, encodeURIComponent(String(savedArticleFilterId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(savedArticleFilterUserUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and search articles using POST request body for filters.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
         * @param {ArticleFiltersRequest} [articleFiltersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesCreate: async (nextKey?: string, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', articleFiltersRequest?: ArticleFiltersRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/articles/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(articleFiltersRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Articles Export View.
         * @param {ArticleExportRequest} articleExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesExportCreate: async (articleExportRequest: ArticleExportRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'articleExportRequest' is not null or undefined
            assertParamExists('newsArticlesExportCreate', 'articleExportRequest', articleExportRequest)
            const localVarPath = `/news/articles/export/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(articleExportRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and search articles.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesList: async (category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, nextKey?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/articles/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (category) {
                localVarQueryParameter['category'] = category;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }

            if (company) {
                localVarQueryParameter['company'] = company;
            }

            if (country) {
                localVarQueryParameter['country'] = country;
            }

            if (dunsNumber) {
                localVarQueryParameter['duns_number'] = dunsNumber;
            }

            if (excludeCategory) {
                localVarQueryParameter['exclude_category'] = excludeCategory;
            }

            if (excludeCompany) {
                localVarQueryParameter['exclude_company'] = excludeCompany;
            }

            if (excludeDunsNumber) {
                localVarQueryParameter['exclude_duns_number'] = excludeDunsNumber;
            }

            if (globalUltimate) {
                localVarQueryParameter['global_ultimate'] = globalUltimate;
            }

            if (includeClusteredArticles !== undefined) {
                localVarQueryParameter['include_clustered_articles'] = includeClusteredArticles;
            }

            if (industry) {
                localVarQueryParameter['industry'] = industry;
            }

            if (isMaterial !== undefined) {
                localVarQueryParameter['is_material'] = isMaterial;
            }

            if (language) {
                localVarQueryParameter['language'] = language;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (mediaType !== undefined) {
                localVarQueryParameter['media_type'] = mediaType;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (parentCategory !== undefined) {
                localVarQueryParameter['parent_category'] = parentCategory;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (registrationNumber) {
                localVarQueryParameter['registration_number'] = registrationNumber;
            }

            if (savedArticleFilterId !== undefined) {
                localVarQueryParameter['saved_article_filter_id'] = savedArticleFilterId;
            }

            if (sentiment !== undefined) {
                localVarQueryParameter['sentiment'] = sentiment;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Translation (cached).
         * @param {string} articleId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesRetrieve: async (articleId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'articleId' is not null or undefined
            assertParamExists('newsArticlesRetrieve', 'articleId', articleId)
            const localVarPath = `/news/articles/{article_id}/`
                .replace(`{${"article_id"}}`, encodeURIComponent(String(articleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} company Company ID
         * @param {string} maxPublicationDate Filter articles published before this date
         * @param {string} minPublicationDate Filter articles published after this date
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesSummaryCreate: async (company: string, maxPublicationDate: string, minPublicationDate: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'company' is not null or undefined
            assertParamExists('newsArticlesSummaryCreate', 'company', company)
            // verify required parameter 'maxPublicationDate' is not null or undefined
            assertParamExists('newsArticlesSummaryCreate', 'maxPublicationDate', maxPublicationDate)
            // verify required parameter 'minPublicationDate' is not null or undefined
            assertParamExists('newsArticlesSummaryCreate', 'minPublicationDate', minPublicationDate)
            const localVarPath = `/news/articles/summary/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (company !== undefined) {
                localVarQueryParameter['company'] = company;
            }

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Articles URL Retrieve View.
         * @param {string} articleId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesUrlRetrieve: async (articleId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'articleId' is not null or undefined
            assertParamExists('newsArticlesUrlRetrieve', 'articleId', articleId)
            const localVarPath = `/news/articles/{article_id}/url/`
                .replace(`{${"article_id"}}`, encodeURIComponent(String(articleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List All Categories for all category models flat.
         * @param {Array<string>} [categoryId] Category to filter
         * @param {string} [categorySet] Category set to filter
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsCategoriesList: async (categoryId?: Array<string>, categorySet?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/categories/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (categoryId) {
                localVarQueryParameter['category_id'] = categoryId;
            }

            if (categorySet !== undefined) {
                localVarQueryParameter['category_set'] = categorySet;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Category Sets.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsCategoriesSetsList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/categories/sets/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Category tree.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsCategoriesTreeList: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/news/categories/tree/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * NewsApi - functional programming interface
 * @export
 */
export const NewsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = NewsApiAxiosParamCreator(configuration)
    return {
        /**
         * Get article annotations using POST request body for filters.
         * @param {ArticleFiltersRequest} [articleFiltersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesAnnotationsCreate(articleFiltersRequest?: ArticleFiltersRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ArticlesAnnotations>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesAnnotationsCreate(articleFiltersRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get article annotations using GET request query parameters for filters.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesAnnotationsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ArticlesAnnotations>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesAnnotationsList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List or Create Saved article filters view.
         * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersCreate(savedArticleFilterRequest: SavedArticleFilterRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilter>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersCreate(savedArticleFilterRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersDestroy(savedArticleFilterId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersDestroy(savedArticleFilterId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Saved Article Filters instances for user.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search by query
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersList(nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedSavedArticleFilterList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersList(nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {PatchedSavedArticleFilterRequest} [patchedSavedArticleFilterRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersPartialUpdate(savedArticleFilterId: string, patchedSavedArticleFilterRequest?: PatchedSavedArticleFilterRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilter>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersPartialUpdate(savedArticleFilterId, patchedSavedArticleFilterRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersRetrieve(savedArticleFilterId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilter>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersRetrieve(savedArticleFilterId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUpdate(savedArticleFilterId: string, savedArticleFilterRequest: SavedArticleFilterRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilter>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUpdate(savedArticleFilterId, savedArticleFilterRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Saved Article Filters to different user.
         * @param {string} savedArticleFilterId 
         * @param {SavedArticleFilterUserRequest} savedArticleFilterUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUsersCreate(savedArticleFilterId: string, savedArticleFilterUserRequest: SavedArticleFilterUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilterUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUsersCreate(savedArticleFilterId, savedArticleFilterUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUsersDestroy(savedArticleFilterId: string, userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUsersDestroy(savedArticleFilterId, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Saved Article Filters to different user.
         * @param {string} savedArticleFilterId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUsersList(savedArticleFilterId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedSavedArticleFilterUserList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUsersList(savedArticleFilterId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {PatchedSavedArticleFilterUserUpdateRequest} [patchedSavedArticleFilterUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUsersPartialUpdate(savedArticleFilterId: string, userId: string, patchedSavedArticleFilterUserUpdateRequest?: PatchedSavedArticleFilterUserUpdateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilterUserUpdate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUsersPartialUpdate(savedArticleFilterId, userId, patchedSavedArticleFilterUserUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUsersRetrieve(savedArticleFilterId: string, userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilterUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUsersRetrieve(savedArticleFilterId, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {SavedArticleFilterUserUpdateRequest} savedArticleFilterUserUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesArticleFiltersUsersUpdate(savedArticleFilterId: string, userId: string, savedArticleFilterUserUpdateRequest: SavedArticleFilterUserUpdateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SavedArticleFilterUserUpdate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesArticleFiltersUsersUpdate(savedArticleFilterId, userId, savedArticleFilterUserUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and search articles using POST request body for filters.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
         * @param {ArticleFiltersRequest} [articleFiltersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesCreate(nextKey?: string, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', articleFiltersRequest?: ArticleFiltersRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedArticleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesCreate(nextKey, sorting, articleFiltersRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Articles Export View.
         * @param {ArticleExportRequest} articleExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesExportCreate(articleExportRequest: ArticleExportRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ArticleExport>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesExportCreate(articleExportRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and search articles.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, nextKey?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedArticleList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, nextKey, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, sorting, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Translation (cached).
         * @param {string} articleId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesRetrieve(articleId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ArticleDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesRetrieve(articleId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {string} company Company ID
         * @param {string} maxPublicationDate Filter articles published before this date
         * @param {string} minPublicationDate Filter articles published after this date
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesSummaryCreate(company: string, maxPublicationDate: string, minPublicationDate: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ArticleSummary>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesSummaryCreate(company, maxPublicationDate, minPublicationDate, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Articles URL Retrieve View.
         * @param {string} articleId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsArticlesUrlRetrieve(articleId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ArticleURL>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsArticlesUrlRetrieve(articleId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List All Categories for all category models flat.
         * @param {Array<string>} [categoryId] Category to filter
         * @param {string} [categorySet] Category set to filter
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsCategoriesList(categoryId?: Array<string>, categorySet?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<Category>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsCategoriesList(categoryId, categorySet, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Category Sets.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsCategoriesSetsList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CategorySet>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsCategoriesSetsList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Category tree.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async newsCategoriesTreeList(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CategoryTree>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.newsCategoriesTreeList(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * NewsApi - factory interface
 * @export
 */
export const NewsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = NewsApiFp(configuration)
    return {
        /**
         * Get article annotations using POST request body for filters.
         * @param {ArticleFiltersRequest} [articleFiltersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesAnnotationsCreate(articleFiltersRequest?: ArticleFiltersRequest, options?: any): AxiosPromise<ArticlesAnnotations> {
            return localVarFp.newsArticlesAnnotationsCreate(articleFiltersRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get article annotations using GET request query parameters for filters.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesAnnotationsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: any): AxiosPromise<Array<ArticlesAnnotations>> {
            return localVarFp.newsArticlesAnnotationsList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, options).then((request) => request(axios, basePath));
        },
        /**
         * List or Create Saved article filters view.
         * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersCreate(savedArticleFilterRequest: SavedArticleFilterRequest, options?: any): AxiosPromise<SavedArticleFilter> {
            return localVarFp.newsArticlesArticleFiltersCreate(savedArticleFilterRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersDestroy(savedArticleFilterId: string, options?: any): AxiosPromise<void> {
            return localVarFp.newsArticlesArticleFiltersDestroy(savedArticleFilterId, options).then((request) => request(axios, basePath));
        },
        /**
         * List Saved Article Filters instances for user.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search by query
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersList(nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedSavedArticleFilterList> {
            return localVarFp.newsArticlesArticleFiltersList(nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {PatchedSavedArticleFilterRequest} [patchedSavedArticleFilterRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersPartialUpdate(savedArticleFilterId: string, patchedSavedArticleFilterRequest?: PatchedSavedArticleFilterRequest, options?: any): AxiosPromise<SavedArticleFilter> {
            return localVarFp.newsArticlesArticleFiltersPartialUpdate(savedArticleFilterId, patchedSavedArticleFilterRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersRetrieve(savedArticleFilterId: string, options?: any): AxiosPromise<SavedArticleFilter> {
            return localVarFp.newsArticlesArticleFiltersRetrieve(savedArticleFilterId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve Update Destroy Saved Article Filters Instance.
         * @param {string} savedArticleFilterId 
         * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUpdate(savedArticleFilterId: string, savedArticleFilterRequest: SavedArticleFilterRequest, options?: any): AxiosPromise<SavedArticleFilter> {
            return localVarFp.newsArticlesArticleFiltersUpdate(savedArticleFilterId, savedArticleFilterRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Saved Article Filters to different user.
         * @param {string} savedArticleFilterId 
         * @param {SavedArticleFilterUserRequest} savedArticleFilterUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersCreate(savedArticleFilterId: string, savedArticleFilterUserRequest: SavedArticleFilterUserRequest, options?: any): AxiosPromise<SavedArticleFilterUser> {
            return localVarFp.newsArticlesArticleFiltersUsersCreate(savedArticleFilterId, savedArticleFilterUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersDestroy(savedArticleFilterId: string, userId: string, options?: any): AxiosPromise<void> {
            return localVarFp.newsArticlesArticleFiltersUsersDestroy(savedArticleFilterId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Saved Article Filters to different user.
         * @param {string} savedArticleFilterId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersList(savedArticleFilterId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedSavedArticleFilterUserList> {
            return localVarFp.newsArticlesArticleFiltersUsersList(savedArticleFilterId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {PatchedSavedArticleFilterUserUpdateRequest} [patchedSavedArticleFilterUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersPartialUpdate(savedArticleFilterId: string, userId: string, patchedSavedArticleFilterUserUpdateRequest?: PatchedSavedArticleFilterUserUpdateRequest, options?: any): AxiosPromise<SavedArticleFilterUserUpdate> {
            return localVarFp.newsArticlesArticleFiltersUsersPartialUpdate(savedArticleFilterId, userId, patchedSavedArticleFilterUserUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersRetrieve(savedArticleFilterId: string, userId: string, options?: any): AxiosPromise<SavedArticleFilterUser> {
            return localVarFp.newsArticlesArticleFiltersUsersRetrieve(savedArticleFilterId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Update or delete User of Article Filter.
         * @param {string} savedArticleFilterId 
         * @param {string} userId 
         * @param {SavedArticleFilterUserUpdateRequest} savedArticleFilterUserUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesArticleFiltersUsersUpdate(savedArticleFilterId: string, userId: string, savedArticleFilterUserUpdateRequest: SavedArticleFilterUserUpdateRequest, options?: any): AxiosPromise<SavedArticleFilterUserUpdate> {
            return localVarFp.newsArticlesArticleFiltersUsersUpdate(savedArticleFilterId, userId, savedArticleFilterUserUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get and search articles using POST request body for filters.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
         * @param {ArticleFiltersRequest} [articleFiltersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesCreate(nextKey?: string, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', articleFiltersRequest?: ArticleFiltersRequest, options?: any): AxiosPromise<PaginatedArticleList> {
            return localVarFp.newsArticlesCreate(nextKey, sorting, articleFiltersRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Articles Export View.
         * @param {ArticleExportRequest} articleExportRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesExportCreate(articleExportRequest: ArticleExportRequest, options?: any): AxiosPromise<ArticleExport> {
            return localVarFp.newsArticlesExportCreate(articleExportRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get and search articles.
         * @param {Array<string>} [category] Category ID to filter articles
         * @param {string} [categorySet] Category Set ID to filter articles
         * @param {Array<string>} [company] Company ID\&#39;s
         * @param {Array<string>} [country] ISO 2-letter Country Code
         * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [excludeCategory] Category ID to exclude articles
         * @param {Array<string>} [excludeCompany] Company ID\&#39;s
         * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
         * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
         * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
         * @param {Array<string>} [industry] Industry Codes to filter on.
         * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
         * @param {Array<string>} [language] ISO 2-letter Language Code
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [parentCategory] Parent Category ID to filter articles
         * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
         * @param {string} [query] Custom search filters to text search all articles.
         * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
         * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
         * @param {boolean} [sentiment] Filter articles with sentiment
         * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, nextKey?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', options?: any): AxiosPromise<PaginatedArticleList> {
            return localVarFp.newsArticlesList(category, categorySet, company, country, dunsNumber, excludeCategory, excludeCompany, excludeDunsNumber, globalUltimate, includeClusteredArticles, industry, isMaterial, language, maxPublicationDate, mediaType, minPublicationDate, nextKey, parentCategory, portfolioId, query, registrationNumber, savedArticleFilterId, sentiment, sorting, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Translation (cached).
         * @param {string} articleId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesRetrieve(articleId: string, options?: any): AxiosPromise<ArticleDetail> {
            return localVarFp.newsArticlesRetrieve(articleId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {string} company Company ID
         * @param {string} maxPublicationDate Filter articles published before this date
         * @param {string} minPublicationDate Filter articles published after this date
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesSummaryCreate(company: string, maxPublicationDate: string, minPublicationDate: string, options?: any): AxiosPromise<ArticleSummary> {
            return localVarFp.newsArticlesSummaryCreate(company, maxPublicationDate, minPublicationDate, options).then((request) => request(axios, basePath));
        },
        /**
         * Articles URL Retrieve View.
         * @param {string} articleId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsArticlesUrlRetrieve(articleId: string, options?: any): AxiosPromise<ArticleURL> {
            return localVarFp.newsArticlesUrlRetrieve(articleId, options).then((request) => request(axios, basePath));
        },
        /**
         * List All Categories for all category models flat.
         * @param {Array<string>} [categoryId] Category to filter
         * @param {string} [categorySet] Category set to filter
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsCategoriesList(categoryId?: Array<string>, categorySet?: string, options?: any): AxiosPromise<Array<Category>> {
            return localVarFp.newsCategoriesList(categoryId, categorySet, options).then((request) => request(axios, basePath));
        },
        /**
         * List Category Sets.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsCategoriesSetsList(options?: any): AxiosPromise<Array<CategorySet>> {
            return localVarFp.newsCategoriesSetsList(options).then((request) => request(axios, basePath));
        },
        /**
         * List Category tree.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        newsCategoriesTreeList(options?: any): AxiosPromise<Array<CategoryTree>> {
            return localVarFp.newsCategoriesTreeList(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * NewsApi - interface
 * @export
 * @interface NewsApi
 */
export interface NewsApiInterface {
    /**
     * Get article annotations using POST request body for filters.
     * @param {ArticleFiltersRequest} [articleFiltersRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesAnnotationsCreate(articleFiltersRequest?: ArticleFiltersRequest, options?: AxiosRequestConfig): AxiosPromise<ArticlesAnnotations>;

    /**
     * Get article annotations using GET request query parameters for filters.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesAnnotationsList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, options?: AxiosRequestConfig): AxiosPromise<Array<ArticlesAnnotations>>;

    /**
     * List or Create Saved article filters view.
     * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersCreate(savedArticleFilterRequest: SavedArticleFilterRequest, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilter>;

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {string} savedArticleFilterId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersDestroy(savedArticleFilterId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List Saved Article Filters instances for user.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Search by query
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersList(nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedSavedArticleFilterList>;

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {string} savedArticleFilterId 
     * @param {PatchedSavedArticleFilterRequest} [patchedSavedArticleFilterRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersPartialUpdate(savedArticleFilterId: string, patchedSavedArticleFilterRequest?: PatchedSavedArticleFilterRequest, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilter>;

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {string} savedArticleFilterId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersRetrieve(savedArticleFilterId: string, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilter>;

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {string} savedArticleFilterId 
     * @param {SavedArticleFilterRequest} savedArticleFilterRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUpdate(savedArticleFilterId: string, savedArticleFilterRequest: SavedArticleFilterRequest, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilter>;

    /**
     * Saved Article Filters to different user.
     * @param {string} savedArticleFilterId 
     * @param {SavedArticleFilterUserRequest} savedArticleFilterUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUsersCreate(savedArticleFilterId: string, savedArticleFilterUserRequest: SavedArticleFilterUserRequest, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilterUser>;

    /**
     * Get Update or delete User of Article Filter.
     * @param {string} savedArticleFilterId 
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUsersDestroy(savedArticleFilterId: string, userId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Saved Article Filters to different user.
     * @param {string} savedArticleFilterId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUsersList(savedArticleFilterId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedSavedArticleFilterUserList>;

    /**
     * Get Update or delete User of Article Filter.
     * @param {string} savedArticleFilterId 
     * @param {string} userId 
     * @param {PatchedSavedArticleFilterUserUpdateRequest} [patchedSavedArticleFilterUserUpdateRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUsersPartialUpdate(savedArticleFilterId: string, userId: string, patchedSavedArticleFilterUserUpdateRequest?: PatchedSavedArticleFilterUserUpdateRequest, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilterUserUpdate>;

    /**
     * Get Update or delete User of Article Filter.
     * @param {string} savedArticleFilterId 
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUsersRetrieve(savedArticleFilterId: string, userId: string, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilterUser>;

    /**
     * Get Update or delete User of Article Filter.
     * @param {string} savedArticleFilterId 
     * @param {string} userId 
     * @param {SavedArticleFilterUserUpdateRequest} savedArticleFilterUserUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesArticleFiltersUsersUpdate(savedArticleFilterId: string, userId: string, savedArticleFilterUserUpdateRequest: SavedArticleFilterUserUpdateRequest, options?: AxiosRequestConfig): AxiosPromise<SavedArticleFilterUserUpdate>;

    /**
     * Get and search articles using POST request body for filters.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
     * @param {ArticleFiltersRequest} [articleFiltersRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesCreate(nextKey?: string, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', articleFiltersRequest?: ArticleFiltersRequest, options?: AxiosRequestConfig): AxiosPromise<PaginatedArticleList>;

    /**
     * Articles Export View.
     * @param {ArticleExportRequest} articleExportRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesExportCreate(articleExportRequest: ArticleExportRequest, options?: AxiosRequestConfig): AxiosPromise<ArticleExport>;

    /**
     * Get and search articles.
     * @param {Array<string>} [category] Category ID to filter articles
     * @param {string} [categorySet] Category Set ID to filter articles
     * @param {Array<string>} [company] Company ID\&#39;s
     * @param {Array<string>} [country] ISO 2-letter Country Code
     * @param {Array<string>} [dunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [excludeCategory] Category ID to exclude articles
     * @param {Array<string>} [excludeCompany] Company ID\&#39;s
     * @param {Array<string>} [excludeDunsNumber] 9-digit Dun And Bradstreet Number
     * @param {Array<string>} [globalUltimate] 9-digit Dun And Bradstreet Number
     * @param {boolean} [includeClusteredArticles] If enabled clustered articles are included
     * @param {Array<string>} [industry] Industry Codes to filter on.
     * @param {boolean} [isMaterial] Filter articles by materiality flag (true/false)
     * @param {Array<string>} [language] ISO 2-letter Language Code
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {'GAZETTE' | 'MAINSTREAM'} [mediaType] Filter on source media type
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [parentCategory] Parent Category ID to filter articles
     * @param {Array<string>} [portfolioId] Portfolio ID to filter articles
     * @param {string} [query] Custom search filters to text search all articles.
     * @param {Array<string>} [registrationNumber] Local Registration Number (Country Parameter also required)
     * @param {string} [savedArticleFilterId] Filter articles on already saved article filter id
     * @param {boolean} [sentiment] Filter articles with sentiment
     * @param {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'} [sorting] Sort articles
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesList(category?: Array<string>, categorySet?: string, company?: Array<string>, country?: Array<string>, dunsNumber?: Array<string>, excludeCategory?: Array<string>, excludeCompany?: Array<string>, excludeDunsNumber?: Array<string>, globalUltimate?: Array<string>, includeClusteredArticles?: boolean, industry?: Array<string>, isMaterial?: boolean, language?: Array<string>, maxPublicationDate?: string, mediaType?: 'GAZETTE' | 'MAINSTREAM', minPublicationDate?: string, nextKey?: string, parentCategory?: string, portfolioId?: Array<string>, query?: string, registrationNumber?: Array<string>, savedArticleFilterId?: string, sentiment?: boolean, sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime', options?: AxiosRequestConfig): AxiosPromise<PaginatedArticleList>;

    /**
     * Get Translation (cached).
     * @param {string} articleId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesRetrieve(articleId: string, options?: AxiosRequestConfig): AxiosPromise<ArticleDetail>;

    /**
     * 
     * @param {string} company Company ID
     * @param {string} maxPublicationDate Filter articles published before this date
     * @param {string} minPublicationDate Filter articles published after this date
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesSummaryCreate(company: string, maxPublicationDate: string, minPublicationDate: string, options?: AxiosRequestConfig): AxiosPromise<ArticleSummary>;

    /**
     * Articles URL Retrieve View.
     * @param {string} articleId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsArticlesUrlRetrieve(articleId: string, options?: AxiosRequestConfig): AxiosPromise<ArticleURL>;

    /**
     * List All Categories for all category models flat.
     * @param {Array<string>} [categoryId] Category to filter
     * @param {string} [categorySet] Category set to filter
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsCategoriesList(categoryId?: Array<string>, categorySet?: string, options?: AxiosRequestConfig): AxiosPromise<Array<Category>>;

    /**
     * List Category Sets.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsCategoriesSetsList(options?: AxiosRequestConfig): AxiosPromise<Array<CategorySet>>;

    /**
     * List Category tree.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApiInterface
     */
    newsCategoriesTreeList(options?: AxiosRequestConfig): AxiosPromise<Array<CategoryTree>>;

}

/**
 * Request parameters for newsArticlesAnnotationsCreate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesAnnotationsCreateRequest
 */
export interface NewsApiNewsArticlesAnnotationsCreateRequest {
    /**
     * 
     * @type {ArticleFiltersRequest}
     * @memberof NewsApiNewsArticlesAnnotationsCreate
     */
    readonly articleFiltersRequest?: ArticleFiltersRequest
}

/**
 * Request parameters for newsArticlesAnnotationsList operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesAnnotationsListRequest
 */
export interface NewsApiNewsArticlesAnnotationsListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly minPublicationDate?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof NewsApiNewsArticlesAnnotationsList
     */
    readonly sentiment?: boolean
}

/**
 * Request parameters for newsArticlesArticleFiltersCreate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersCreateRequest
 */
export interface NewsApiNewsArticlesArticleFiltersCreateRequest {
    /**
     * 
     * @type {SavedArticleFilterRequest}
     * @memberof NewsApiNewsArticlesArticleFiltersCreate
     */
    readonly savedArticleFilterRequest: SavedArticleFilterRequest
}

/**
 * Request parameters for newsArticlesArticleFiltersDestroy operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersDestroyRequest
 */
export interface NewsApiNewsArticlesArticleFiltersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersDestroy
     */
    readonly savedArticleFilterId: string
}

/**
 * Request parameters for newsArticlesArticleFiltersList operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersListRequest
 */
export interface NewsApiNewsArticlesArticleFiltersListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersList
     */
    readonly nextKey?: string

    /**
     * Search by query
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersList
     */
    readonly query?: string
}

/**
 * Request parameters for newsArticlesArticleFiltersPartialUpdate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersPartialUpdateRequest
 */
export interface NewsApiNewsArticlesArticleFiltersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersPartialUpdate
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {PatchedSavedArticleFilterRequest}
     * @memberof NewsApiNewsArticlesArticleFiltersPartialUpdate
     */
    readonly patchedSavedArticleFilterRequest?: PatchedSavedArticleFilterRequest
}

/**
 * Request parameters for newsArticlesArticleFiltersRetrieve operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersRetrieveRequest
 */
export interface NewsApiNewsArticlesArticleFiltersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersRetrieve
     */
    readonly savedArticleFilterId: string
}

/**
 * Request parameters for newsArticlesArticleFiltersUpdate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUpdateRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUpdate
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {SavedArticleFilterRequest}
     * @memberof NewsApiNewsArticlesArticleFiltersUpdate
     */
    readonly savedArticleFilterRequest: SavedArticleFilterRequest
}

/**
 * Request parameters for newsArticlesArticleFiltersUsersCreate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUsersCreateRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUsersCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersCreate
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {SavedArticleFilterUserRequest}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersCreate
     */
    readonly savedArticleFilterUserRequest: SavedArticleFilterUserRequest
}

/**
 * Request parameters for newsArticlesArticleFiltersUsersDestroy operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUsersDestroyRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUsersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersDestroy
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersDestroy
     */
    readonly userId: string
}

/**
 * Request parameters for newsArticlesArticleFiltersUsersList operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUsersListRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUsersListRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersList
     */
    readonly savedArticleFilterId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for newsArticlesArticleFiltersUsersPartialUpdate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUsersPartialUpdateRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUsersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersPartialUpdate
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersPartialUpdate
     */
    readonly userId: string

    /**
     * 
     * @type {PatchedSavedArticleFilterUserUpdateRequest}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersPartialUpdate
     */
    readonly patchedSavedArticleFilterUserUpdateRequest?: PatchedSavedArticleFilterUserUpdateRequest
}

/**
 * Request parameters for newsArticlesArticleFiltersUsersRetrieve operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUsersRetrieveRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUsersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersRetrieve
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersRetrieve
     */
    readonly userId: string
}

/**
 * Request parameters for newsArticlesArticleFiltersUsersUpdate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesArticleFiltersUsersUpdateRequest
 */
export interface NewsApiNewsArticlesArticleFiltersUsersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersUpdate
     */
    readonly savedArticleFilterId: string

    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersUpdate
     */
    readonly userId: string

    /**
     * 
     * @type {SavedArticleFilterUserUpdateRequest}
     * @memberof NewsApiNewsArticlesArticleFiltersUsersUpdate
     */
    readonly savedArticleFilterUserUpdateRequest: SavedArticleFilterUserUpdateRequest
}

/**
 * Request parameters for newsArticlesCreate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesCreateRequest
 */
export interface NewsApiNewsArticlesCreateRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof NewsApiNewsArticlesCreate
     */
    readonly nextKey?: string

    /**
     * Sort articles
     * @type {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'}
     * @memberof NewsApiNewsArticlesCreate
     */
    readonly sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'

    /**
     * 
     * @type {ArticleFiltersRequest}
     * @memberof NewsApiNewsArticlesCreate
     */
    readonly articleFiltersRequest?: ArticleFiltersRequest
}

/**
 * Request parameters for newsArticlesExportCreate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesExportCreateRequest
 */
export interface NewsApiNewsArticlesExportCreateRequest {
    /**
     * 
     * @type {ArticleExportRequest}
     * @memberof NewsApiNewsArticlesExportCreate
     */
    readonly articleExportRequest: ArticleExportRequest
}

/**
 * Request parameters for newsArticlesList operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesListRequest
 */
export interface NewsApiNewsArticlesListRequest {
    /**
     * Category ID to filter articles
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly category?: Array<string>

    /**
     * Category Set ID to filter articles
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly categorySet?: string

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly company?: Array<string>

    /**
     * ISO 2-letter Country Code
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly country?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly dunsNumber?: Array<string>

    /**
     * Category ID to exclude articles
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly excludeCategory?: Array<string>

    /**
     * Company ID\&#39;s
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly excludeCompany?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly excludeDunsNumber?: Array<string>

    /**
     * 9-digit Dun And Bradstreet Number
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly globalUltimate?: Array<string>

    /**
     * If enabled clustered articles are included
     * @type {boolean}
     * @memberof NewsApiNewsArticlesList
     */
    readonly includeClusteredArticles?: boolean

    /**
     * Industry Codes to filter on.
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly industry?: Array<string>

    /**
     * Filter articles by materiality flag (true/false)
     * @type {boolean}
     * @memberof NewsApiNewsArticlesList
     */
    readonly isMaterial?: boolean

    /**
     * ISO 2-letter Language Code
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly language?: Array<string>

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter on source media type
     * @type {'GAZETTE' | 'MAINSTREAM'}
     * @memberof NewsApiNewsArticlesList
     */
    readonly mediaType?: 'GAZETTE' | 'MAINSTREAM'

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly minPublicationDate?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly nextKey?: string

    /**
     * Parent Category ID to filter articles
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly parentCategory?: string

    /**
     * Portfolio ID to filter articles
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly portfolioId?: Array<string>

    /**
     * Custom search filters to text search all articles.
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly query?: string

    /**
     * Local Registration Number (Country Parameter also required)
     * @type {Array<string>}
     * @memberof NewsApiNewsArticlesList
     */
    readonly registrationNumber?: Array<string>

    /**
     * Filter articles on already saved article filter id
     * @type {string}
     * @memberof NewsApiNewsArticlesList
     */
    readonly savedArticleFilterId?: string

    /**
     * Filter articles with sentiment
     * @type {boolean}
     * @memberof NewsApiNewsArticlesList
     */
    readonly sentiment?: boolean

    /**
     * Sort articles
     * @type {'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'}
     * @memberof NewsApiNewsArticlesList
     */
    readonly sorting?: 'creation_date' | 'publication_date_clustering' | 'publication_date_priority' | 'publication_date_source_references' | 'publication_datetime'
}

/**
 * Request parameters for newsArticlesRetrieve operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesRetrieveRequest
 */
export interface NewsApiNewsArticlesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesRetrieve
     */
    readonly articleId: string
}

/**
 * Request parameters for newsArticlesSummaryCreate operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesSummaryCreateRequest
 */
export interface NewsApiNewsArticlesSummaryCreateRequest {
    /**
     * Company ID
     * @type {string}
     * @memberof NewsApiNewsArticlesSummaryCreate
     */
    readonly company: string

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof NewsApiNewsArticlesSummaryCreate
     */
    readonly maxPublicationDate: string

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof NewsApiNewsArticlesSummaryCreate
     */
    readonly minPublicationDate: string
}

/**
 * Request parameters for newsArticlesUrlRetrieve operation in NewsApi.
 * @export
 * @interface NewsApiNewsArticlesUrlRetrieveRequest
 */
export interface NewsApiNewsArticlesUrlRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof NewsApiNewsArticlesUrlRetrieve
     */
    readonly articleId: string
}

/**
 * Request parameters for newsCategoriesList operation in NewsApi.
 * @export
 * @interface NewsApiNewsCategoriesListRequest
 */
export interface NewsApiNewsCategoriesListRequest {
    /**
     * Category to filter
     * @type {Array<string>}
     * @memberof NewsApiNewsCategoriesList
     */
    readonly categoryId?: Array<string>

    /**
     * Category set to filter
     * @type {string}
     * @memberof NewsApiNewsCategoriesList
     */
    readonly categorySet?: string
}

/**
 * NewsApi - object-oriented interface
 * @export
 * @class NewsApi
 * @extends {BaseAPI}
 */
export class NewsApi extends BaseAPI implements NewsApiInterface {
    /**
     * Get article annotations using POST request body for filters.
     * @param {NewsApiNewsArticlesAnnotationsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesAnnotationsCreate(requestParameters: NewsApiNewsArticlesAnnotationsCreateRequest = {}, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesAnnotationsCreate(requestParameters.articleFiltersRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get article annotations using GET request query parameters for filters.
     * @param {NewsApiNewsArticlesAnnotationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesAnnotationsList(requestParameters: NewsApiNewsArticlesAnnotationsListRequest = {}, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesAnnotationsList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List or Create Saved article filters view.
     * @param {NewsApiNewsArticlesArticleFiltersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersCreate(requestParameters: NewsApiNewsArticlesArticleFiltersCreateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersCreate(requestParameters.savedArticleFilterRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {NewsApiNewsArticlesArticleFiltersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersDestroy(requestParameters: NewsApiNewsArticlesArticleFiltersDestroyRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersDestroy(requestParameters.savedArticleFilterId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Saved Article Filters instances for user.
     * @param {NewsApiNewsArticlesArticleFiltersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersList(requestParameters: NewsApiNewsArticlesArticleFiltersListRequest = {}, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersList(requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {NewsApiNewsArticlesArticleFiltersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersPartialUpdate(requestParameters: NewsApiNewsArticlesArticleFiltersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersPartialUpdate(requestParameters.savedArticleFilterId, requestParameters.patchedSavedArticleFilterRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {NewsApiNewsArticlesArticleFiltersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersRetrieve(requestParameters: NewsApiNewsArticlesArticleFiltersRetrieveRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersRetrieve(requestParameters.savedArticleFilterId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve Update Destroy Saved Article Filters Instance.
     * @param {NewsApiNewsArticlesArticleFiltersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUpdate(requestParameters: NewsApiNewsArticlesArticleFiltersUpdateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUpdate(requestParameters.savedArticleFilterId, requestParameters.savedArticleFilterRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Saved Article Filters to different user.
     * @param {NewsApiNewsArticlesArticleFiltersUsersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUsersCreate(requestParameters: NewsApiNewsArticlesArticleFiltersUsersCreateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUsersCreate(requestParameters.savedArticleFilterId, requestParameters.savedArticleFilterUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Update or delete User of Article Filter.
     * @param {NewsApiNewsArticlesArticleFiltersUsersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUsersDestroy(requestParameters: NewsApiNewsArticlesArticleFiltersUsersDestroyRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUsersDestroy(requestParameters.savedArticleFilterId, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Saved Article Filters to different user.
     * @param {NewsApiNewsArticlesArticleFiltersUsersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUsersList(requestParameters: NewsApiNewsArticlesArticleFiltersUsersListRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUsersList(requestParameters.savedArticleFilterId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Update or delete User of Article Filter.
     * @param {NewsApiNewsArticlesArticleFiltersUsersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUsersPartialUpdate(requestParameters: NewsApiNewsArticlesArticleFiltersUsersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUsersPartialUpdate(requestParameters.savedArticleFilterId, requestParameters.userId, requestParameters.patchedSavedArticleFilterUserUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Update or delete User of Article Filter.
     * @param {NewsApiNewsArticlesArticleFiltersUsersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUsersRetrieve(requestParameters: NewsApiNewsArticlesArticleFiltersUsersRetrieveRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUsersRetrieve(requestParameters.savedArticleFilterId, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Update or delete User of Article Filter.
     * @param {NewsApiNewsArticlesArticleFiltersUsersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesArticleFiltersUsersUpdate(requestParameters: NewsApiNewsArticlesArticleFiltersUsersUpdateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesArticleFiltersUsersUpdate(requestParameters.savedArticleFilterId, requestParameters.userId, requestParameters.savedArticleFilterUserUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and search articles using POST request body for filters.
     * @param {NewsApiNewsArticlesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesCreate(requestParameters: NewsApiNewsArticlesCreateRequest = {}, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesCreate(requestParameters.nextKey, requestParameters.sorting, requestParameters.articleFiltersRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Articles Export View.
     * @param {NewsApiNewsArticlesExportCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesExportCreate(requestParameters: NewsApiNewsArticlesExportCreateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesExportCreate(requestParameters.articleExportRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and search articles.
     * @param {NewsApiNewsArticlesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesList(requestParameters: NewsApiNewsArticlesListRequest = {}, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesList(requestParameters.category, requestParameters.categorySet, requestParameters.company, requestParameters.country, requestParameters.dunsNumber, requestParameters.excludeCategory, requestParameters.excludeCompany, requestParameters.excludeDunsNumber, requestParameters.globalUltimate, requestParameters.includeClusteredArticles, requestParameters.industry, requestParameters.isMaterial, requestParameters.language, requestParameters.maxPublicationDate, requestParameters.mediaType, requestParameters.minPublicationDate, requestParameters.nextKey, requestParameters.parentCategory, requestParameters.portfolioId, requestParameters.query, requestParameters.registrationNumber, requestParameters.savedArticleFilterId, requestParameters.sentiment, requestParameters.sorting, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Translation (cached).
     * @param {NewsApiNewsArticlesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesRetrieve(requestParameters: NewsApiNewsArticlesRetrieveRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesRetrieve(requestParameters.articleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {NewsApiNewsArticlesSummaryCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesSummaryCreate(requestParameters: NewsApiNewsArticlesSummaryCreateRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesSummaryCreate(requestParameters.company, requestParameters.maxPublicationDate, requestParameters.minPublicationDate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Articles URL Retrieve View.
     * @param {NewsApiNewsArticlesUrlRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsArticlesUrlRetrieve(requestParameters: NewsApiNewsArticlesUrlRetrieveRequest, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsArticlesUrlRetrieve(requestParameters.articleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List All Categories for all category models flat.
     * @param {NewsApiNewsCategoriesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsCategoriesList(requestParameters: NewsApiNewsCategoriesListRequest = {}, options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsCategoriesList(requestParameters.categoryId, requestParameters.categorySet, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Category Sets.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsCategoriesSetsList(options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsCategoriesSetsList(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Category tree.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof NewsApi
     */
    public newsCategoriesTreeList(options?: AxiosRequestConfig) {
        return NewsApiFp(this.configuration).newsCategoriesTreeList(options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * PortfoliosApi - axios parameter creator
 * @export
 */
export const PortfoliosApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * List companies in batch registration.
         * @param {string} batchRegistrationId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search by name, DUNS, registration number, or customer reference
         * @param {'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status'} [sorting] Field to sort by
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {Array<string>} [status] Filter by registration status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsCompaniesList: async (batchRegistrationId: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchRegistrationId' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsCompaniesList', 'batchRegistrationId', batchRegistrationId)
            const localVarPath = `/portfolios/batch_registrations/{batch_registration_id}/companies/`
                .replace(`{${"batch_registration_id"}}`, encodeURIComponent(String(batchRegistrationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create new Portfolio BatchRegistration.
         * @param {BatchRegistrationRequest} batchRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsCreate: async (batchRegistrationRequest: BatchRegistrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchRegistrationRequest' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsCreate', 'batchRegistrationRequest', batchRegistrationRequest)
            const localVarPath = `/portfolios/batch_registrations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(batchRegistrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Post Batch Register Companies in a Portfolio.  API used for registering companies in a larger scale for example updating an entire portfolios or collection of portfolios.
         * @param {string} batchRegistrationId 
         * @param {Array<BatchCompanyRegistrationRequest>} batchCompanyRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsCreateRegistrations: async (batchRegistrationId: string, batchCompanyRegistrationRequest: Array<BatchCompanyRegistrationRequest>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchRegistrationId' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsCreateRegistrations', 'batchRegistrationId', batchRegistrationId)
            // verify required parameter 'batchCompanyRegistrationRequest' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsCreateRegistrations', 'batchCompanyRegistrationRequest', batchCompanyRegistrationRequest)
            const localVarPath = `/portfolios/batch_registrations/{batch_registration_id}/`
                .replace(`{${"batch_registration_id"}}`, encodeURIComponent(String(batchRegistrationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(batchCompanyRegistrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List skipped rows for a batch registration.
         * @param {string} batchRegistrationId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsErrorsList: async (batchRegistrationId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchRegistrationId' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsErrorsList', 'batchRegistrationId', batchRegistrationId)
            const localVarPath = `/portfolios/batch_registrations/{batch_registration_id}/errors/`
                .replace(`{${"batch_registration_id"}}`, encodeURIComponent(String(batchRegistrationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List all batch registrations for the authenticated user.
         * @param {number} [limit] Number of results per page (max 100)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [portfolioId] Filter by portfolio ID
         * @param {'created_at' | 'finished_at' | 'status'} [sorting] Field to sort by
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {Array<string>} [status] Filter by batch registration status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsList: async (limit?: number, nextKey?: string, portfolioId?: string, sorting?: 'created_at' | 'finished_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/portfolios/batch_registrations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (portfolioId !== undefined) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }

            if (status) {
                localVarQueryParameter['status'] = status;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Upload Field mapping for Batch registration.
         * @param {string} batchRegistrationId 
         * @param {BatchRegistrationFieldMappingRequest} [batchRegistrationFieldMappingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsMappingCreate: async (batchRegistrationId: string, batchRegistrationFieldMappingRequest?: BatchRegistrationFieldMappingRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchRegistrationId' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsMappingCreate', 'batchRegistrationId', batchRegistrationId)
            const localVarPath = `/portfolios/batch_registrations/{batch_registration_id}/mapping/`
                .replace(`{${"batch_registration_id"}}`, encodeURIComponent(String(batchRegistrationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(batchRegistrationFieldMappingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Batch Registration Information.
         * @param {string} batchRegistrationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsRetrieve: async (batchRegistrationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchRegistrationId' is not null or undefined
            assertParamExists('portfoliosBatchRegistrationsRetrieve', 'batchRegistrationId', batchRegistrationId)
            const localVarPath = `/portfolios/batch_registrations/{batch_registration_id}/`
                .replace(`{${"batch_registration_id"}}`, encodeURIComponent(String(batchRegistrationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Portfolios.
         * @param {PortfolioBatchResetRequest} portfolioBatchResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchResetCreate: async (portfolioBatchResetRequest: PortfolioBatchResetRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioBatchResetRequest' is not null or undefined
            assertParamExists('portfoliosBatchResetCreate', 'portfolioBatchResetRequest', portfolioBatchResetRequest)
            const localVarPath = `/portfolios/batch_reset/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioBatchResetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Portfolios.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchResetList: async (nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/portfolios/batch_reset/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Register a new Portfolio Company.
         * @param {string} portfolioId 
         * @param {RegistrationRequest} [registrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesCreate: async (portfolioId: string, registrationRequest?: RegistrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosCompaniesCreate', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/companies/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(registrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete Portfolio Company Details
         * @param {string} companyId 
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesDestroy: async (companyId: string, portfolioId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('portfoliosCompaniesDestroy', 'companyId', companyId)
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosCompaniesDestroy', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/companies/{company_id}/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)))
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Portfolio Companies.
         * @param {string} portfolioId 
         * @param {Array<string>} [companyCountry] Filter by country.
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckActivityScore] Filter by activity score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckAdverseMediaScore] Filter by adverse media score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckComplianceScore] Filter by overall compliance score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckCountryScore] Filter by country score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckPepScore] Filter by PEP score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckSanctionScore] Filter by sanction score (low, medium, high).
         * @param {string} [customerReference] Filter portfolios based on the customer_reference.
         * @param {string} [nextKey] The next_key is a cursor used to make it possible to paginate to the next results, pass the next_key from the previous request to retrieve next results.
         * @param {string} [query] Search companies by name, DUNS number, registration number, or customer reference.
         * @param {'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] 
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesList: async (portfolioId: string, companyCountry?: Array<string>, complianceCheckActivityScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckAdverseMediaScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckComplianceScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckCountryScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckPepScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckSanctionScore?: Array<'high' | 'low' | 'medium' | 'null'>, customerReference?: string, nextKey?: string, query?: string, sorting?: 'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'asc' | 'desc', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosCompaniesList', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/companies/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (companyCountry) {
                localVarQueryParameter['company__country'] = companyCountry;
            }

            if (complianceCheckActivityScore) {
                localVarQueryParameter['compliance_check__activity_score'] = complianceCheckActivityScore;
            }

            if (complianceCheckAdverseMediaScore) {
                localVarQueryParameter['compliance_check__adverse_media_score'] = complianceCheckAdverseMediaScore;
            }

            if (complianceCheckComplianceScore) {
                localVarQueryParameter['compliance_check__compliance_score'] = complianceCheckComplianceScore;
            }

            if (complianceCheckCountryScore) {
                localVarQueryParameter['compliance_check__country_score'] = complianceCheckCountryScore;
            }

            if (complianceCheckPepScore) {
                localVarQueryParameter['compliance_check__pep_score'] = complianceCheckPepScore;
            }

            if (complianceCheckSanctionScore) {
                localVarQueryParameter['compliance_check__sanction_score'] = complianceCheckSanctionScore;
            }

            if (customerReference !== undefined) {
                localVarQueryParameter['customer_reference'] = customerReference;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove all companies from this portfolio.
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesReset: async (portfolioId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosCompaniesReset', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/companies/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Portfolio Company Details
         * @param {string} companyId 
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesRetrieve: async (companyId: string, portfolioId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyId' is not null or undefined
            assertParamExists('portfoliosCompaniesRetrieve', 'companyId', companyId)
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosCompaniesRetrieve', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/companies/{company_id}/`
                .replace(`{${"company_id"}}`, encodeURIComponent(String(companyId)))
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new portfolio.
         * @param {PortfolioRequest} portfolioRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCreate: async (portfolioRequest: PortfolioRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioRequest' is not null or undefined
            assertParamExists('portfoliosCreate', 'portfolioRequest', portfolioRequest)
            const localVarPath = `/portfolios/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete Portfolio.
         * @param {string} portfolioId 
         * @param {boolean} [confirmed] Force delete portfolio and all associated alerts.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosDestroy: async (portfolioId: string, confirmed?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosDestroy', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (confirmed !== undefined) {
                localVarQueryParameter['confirmed'] = confirmed;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Portfolios.
         * @param {string} [annotateCompanyId] Company external_id. When given, each result carries contains_company (bool) indicating whether the portfolio already contains that company. Omitted -&gt; contains_company is null.
         * @param {string} [customerReference] Filter portfolio by customer_reference.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [ownedPortfolios] Only included portfolios owned by yourself.
         * @param {Array<string>} [portfolioId] Portfolio to filter
         * @param {string} [query] Filter profiles based on (part of) name.
         * @param {'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] Sort portfolios
         * @param {'ascending' | 'descending'} [sortingOrder] Sort portfolios order, descending is from high to low and ascending from high to low.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosList: async (annotateCompanyId?: string, customerReference?: string, nextKey?: string, ownedPortfolios?: boolean, portfolioId?: Array<string>, query?: string, sorting?: 'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/portfolios/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (annotateCompanyId !== undefined) {
                localVarQueryParameter['annotate_company_id'] = annotateCompanyId;
            }

            if (customerReference !== undefined) {
                localVarQueryParameter['customer_reference'] = customerReference;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (ownedPortfolios !== undefined) {
                localVarQueryParameter['owned_portfolios'] = ownedPortfolios;
            }

            if (portfolioId) {
                localVarQueryParameter['portfolio_id'] = portfolioId;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Portfolio Details API.
         * @param {string} portfolioId 
         * @param {PatchedPortfolioRequest} [patchedPortfolioRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosPartialUpdate: async (portfolioId: string, patchedPortfolioRequest?: PatchedPortfolioRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosPartialUpdate', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedPortfolioRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Or Create Portfolio Profile User Groups.
         * @param {string} portfolioId 
         * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsCreate: async (portfolioId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsCreate', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioProfileUserGroupRequest' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsCreate', 'portfolioProfileUserGroupRequest', portfolioProfileUserGroupRequest)
            const localVarPath = `/portfolios/{portfolio_id}/profile_user_groups/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsDestroy: async (portfolioId: string, profileUserGroupId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsDestroy', 'portfolioId', portfolioId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsDestroy', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/portfolios/{portfolio_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List portfolios.
         * @param {string} portfolioId 
         * @param {'created_at' | 'updated_at'} sorting Sort portfolio user groups
         * @param {'ascending' | 'descending'} sortingOrder Sort portfolio user groups order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Portfolio profile user groups using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsList: async (portfolioId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsList', 'portfolioId', portfolioId)
            // verify required parameter 'sorting' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsList', 'sorting', sorting)
            // verify required parameter 'sortingOrder' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsList', 'sortingOrder', sortingOrder)
            const localVarPath = `/portfolios/{portfolio_id}/profile_user_groups/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {PatchedPortfolioProfileUserGroupRequest} [patchedPortfolioProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsPartialUpdate: async (portfolioId: string, profileUserGroupId: string, patchedPortfolioProfileUserGroupRequest?: PatchedPortfolioProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsPartialUpdate', 'portfolioId', portfolioId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsPartialUpdate', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/portfolios/{portfolio_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedPortfolioProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsRetrieve: async (portfolioId: string, profileUserGroupId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsRetrieve', 'portfolioId', portfolioId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsRetrieve', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/portfolios/{portfolio_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsUpdate: async (portfolioId: string, profileUserGroupId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsUpdate', 'portfolioId', portfolioId)
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsUpdate', 'profileUserGroupId', profileUserGroupId)
            // verify required parameter 'portfolioProfileUserGroupRequest' is not null or undefined
            assertParamExists('portfoliosProfileUserGroupsUpdate', 'portfolioProfileUserGroupRequest', portfolioProfileUserGroupRequest)
            const localVarPath = `/portfolios/{portfolio_id}/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get portfolio details.
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosRetrieve: async (portfolioId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosRetrieve', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update a Portfolio.
         * @param {string} portfolioId 
         * @param {PortfolioRequest} portfolioRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUpdate: async (portfolioId: string, portfolioRequest: PortfolioRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUpdate', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioRequest' is not null or undefined
            assertParamExists('portfoliosUpdate', 'portfolioRequest', portfolioRequest)
            const localVarPath = `/portfolios/{portfolio_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create new Portfolio User.
         * @param {string} portfolioId 
         * @param {PortfolioUserRequest} portfolioUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersCreate: async (portfolioId: string, portfolioUserRequest: PortfolioUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUsersCreate', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioUserRequest' is not null or undefined
            assertParamExists('portfoliosUsersCreate', 'portfolioUserRequest', portfolioUserRequest)
            const localVarPath = `/portfolios/{portfolio_id}/users/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove User Access from Portfolio.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersDestroy: async (portfolioId: string, portfolioUserId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUsersDestroy', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioUserId' is not null or undefined
            assertParamExists('portfoliosUsersDestroy', 'portfolioUserId', portfolioUserId)
            const localVarPath = `/portfolios/{portfolio_id}/users/{portfolio_user_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"portfolio_user_id"}}`, encodeURIComponent(String(portfolioUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Portfolio Users.
         * @param {string} portfolioId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter portfolios based on (part of) name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersList: async (portfolioId: string, nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUsersList', 'portfolioId', portfolioId)
            const localVarPath = `/portfolios/{portfolio_id}/users/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update Portfolio User (patch).
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {PatchedPortfolioUserUpdateRequest} [patchedPortfolioUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersPartialUpdate: async (portfolioId: string, portfolioUserId: string, patchedPortfolioUserUpdateRequest?: PatchedPortfolioUserUpdateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUsersPartialUpdate', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioUserId' is not null or undefined
            assertParamExists('portfoliosUsersPartialUpdate', 'portfolioUserId', portfolioUserId)
            const localVarPath = `/portfolios/{portfolio_id}/users/{portfolio_user_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"portfolio_user_id"}}`, encodeURIComponent(String(portfolioUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedPortfolioUserUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get Portfolio User Details.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersRetrieve: async (portfolioId: string, portfolioUserId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUsersRetrieve', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioUserId' is not null or undefined
            assertParamExists('portfoliosUsersRetrieve', 'portfolioUserId', portfolioUserId)
            const localVarPath = `/portfolios/{portfolio_id}/users/{portfolio_user_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"portfolio_user_id"}}`, encodeURIComponent(String(portfolioUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update Portfolio User.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {PortfolioUserUpdateRequest} [portfolioUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersUpdate: async (portfolioId: string, portfolioUserId: string, portfolioUserUpdateRequest?: PortfolioUserUpdateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'portfolioId' is not null or undefined
            assertParamExists('portfoliosUsersUpdate', 'portfolioId', portfolioId)
            // verify required parameter 'portfolioUserId' is not null or undefined
            assertParamExists('portfoliosUsersUpdate', 'portfolioUserId', portfolioUserId)
            const localVarPath = `/portfolios/{portfolio_id}/users/{portfolio_user_id}/`
                .replace(`{${"portfolio_id"}}`, encodeURIComponent(String(portfolioId)))
                .replace(`{${"portfolio_user_id"}}`, encodeURIComponent(String(portfolioUserId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(portfolioUserUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * PortfoliosApi - functional programming interface
 * @export
 */
export const PortfoliosApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = PortfoliosApiAxiosParamCreator(configuration)
    return {
        /**
         * List companies in batch registration.
         * @param {string} batchRegistrationId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search by name, DUNS, registration number, or customer reference
         * @param {'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status'} [sorting] Field to sort by
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {Array<string>} [status] Filter by registration status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsCompaniesList(batchRegistrationId: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBatchRegistrationCompanyStatusList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsCompaniesList(batchRegistrationId, nextKey, query, sorting, sortingOrder, status, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create new Portfolio BatchRegistration.
         * @param {BatchRegistrationRequest} batchRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsCreate(batchRegistrationRequest: BatchRegistrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchRegistration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsCreate(batchRegistrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Post Batch Register Companies in a Portfolio.  API used for registering companies in a larger scale for example updating an entire portfolios or collection of portfolios.
         * @param {string} batchRegistrationId 
         * @param {Array<BatchCompanyRegistrationRequest>} batchCompanyRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsCreateRegistrations(batchRegistrationId: string, batchCompanyRegistrationRequest: Array<BatchCompanyRegistrationRequest>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<BatchCompanyRegistration>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsCreateRegistrations(batchRegistrationId, batchCompanyRegistrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List skipped rows for a batch registration.
         * @param {string} batchRegistrationId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsErrorsList(batchRegistrationId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBatchRegistrationErrorList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsErrorsList(batchRegistrationId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List all batch registrations for the authenticated user.
         * @param {number} [limit] Number of results per page (max 100)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [portfolioId] Filter by portfolio ID
         * @param {'created_at' | 'finished_at' | 'status'} [sorting] Field to sort by
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {Array<string>} [status] Filter by batch registration status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsList(limit?: number, nextKey?: string, portfolioId?: string, sorting?: 'created_at' | 'finished_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedBatchRegistrationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsList(limit, nextKey, portfolioId, sorting, sortingOrder, status, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Upload Field mapping for Batch registration.
         * @param {string} batchRegistrationId 
         * @param {BatchRegistrationFieldMappingRequest} [batchRegistrationFieldMappingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsMappingCreate(batchRegistrationId: string, batchRegistrationFieldMappingRequest?: BatchRegistrationFieldMappingRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchRegistrationFieldMapping>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsMappingCreate(batchRegistrationId, batchRegistrationFieldMappingRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Batch Registration Information.
         * @param {string} batchRegistrationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchRegistrationsRetrieve(batchRegistrationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BatchRegistration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchRegistrationsRetrieve(batchRegistrationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Portfolios.
         * @param {PortfolioBatchResetRequest} portfolioBatchResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchResetCreate(portfolioBatchResetRequest: PortfolioBatchResetRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioBatchReset>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchResetCreate(portfolioBatchResetRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Portfolios.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosBatchResetList(nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedPortfolioBatchResetList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosBatchResetList(nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Register a new Portfolio Company.
         * @param {string} portfolioId 
         * @param {RegistrationRequest} [registrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosCompaniesCreate(portfolioId: string, registrationRequest?: RegistrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Registration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosCompaniesCreate(portfolioId, registrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete Portfolio Company Details
         * @param {string} companyId 
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosCompaniesDestroy(companyId: string, portfolioId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosCompaniesDestroy(companyId, portfolioId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Portfolio Companies.
         * @param {string} portfolioId 
         * @param {Array<string>} [companyCountry] Filter by country.
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckActivityScore] Filter by activity score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckAdverseMediaScore] Filter by adverse media score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckComplianceScore] Filter by overall compliance score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckCountryScore] Filter by country score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckPepScore] Filter by PEP score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckSanctionScore] Filter by sanction score (low, medium, high).
         * @param {string} [customerReference] Filter portfolios based on the customer_reference.
         * @param {string} [nextKey] The next_key is a cursor used to make it possible to paginate to the next results, pass the next_key from the previous request to retrieve next results.
         * @param {string} [query] Search companies by name, DUNS number, registration number, or customer reference.
         * @param {'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] 
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosCompaniesList(portfolioId: string, companyCountry?: Array<string>, complianceCheckActivityScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckAdverseMediaScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckComplianceScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckCountryScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckPepScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckSanctionScore?: Array<'high' | 'low' | 'medium' | 'null'>, customerReference?: string, nextKey?: string, query?: string, sorting?: 'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'asc' | 'desc', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedPortfolioCompanyList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosCompaniesList(portfolioId, companyCountry, complianceCheckActivityScore, complianceCheckAdverseMediaScore, complianceCheckComplianceScore, complianceCheckCountryScore, complianceCheckPepScore, complianceCheckSanctionScore, customerReference, nextKey, query, sorting, sortingOrder, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Remove all companies from this portfolio.
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosCompaniesReset(portfolioId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosCompaniesReset(portfolioId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Portfolio Company Details
         * @param {string} companyId 
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosCompaniesRetrieve(companyId: string, portfolioId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioCompany>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosCompaniesRetrieve(companyId, portfolioId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a new portfolio.
         * @param {PortfolioRequest} portfolioRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosCreate(portfolioRequest: PortfolioRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Portfolio>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosCreate(portfolioRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete Portfolio.
         * @param {string} portfolioId 
         * @param {boolean} [confirmed] Force delete portfolio and all associated alerts.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosDestroy(portfolioId: string, confirmed?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosDestroy(portfolioId, confirmed, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Portfolios.
         * @param {string} [annotateCompanyId] Company external_id. When given, each result carries contains_company (bool) indicating whether the portfolio already contains that company. Omitted -&gt; contains_company is null.
         * @param {string} [customerReference] Filter portfolio by customer_reference.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [ownedPortfolios] Only included portfolios owned by yourself.
         * @param {Array<string>} [portfolioId] Portfolio to filter
         * @param {string} [query] Filter profiles based on (part of) name.
         * @param {'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] Sort portfolios
         * @param {'ascending' | 'descending'} [sortingOrder] Sort portfolios order, descending is from high to low and ascending from high to low.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosList(annotateCompanyId?: string, customerReference?: string, nextKey?: string, ownedPortfolios?: boolean, portfolioId?: Array<string>, query?: string, sorting?: 'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedPortfolioList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosList(annotateCompanyId, customerReference, nextKey, ownedPortfolios, portfolioId, query, sorting, sortingOrder, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Portfolio Details API.
         * @param {string} portfolioId 
         * @param {PatchedPortfolioRequest} [patchedPortfolioRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosPartialUpdate(portfolioId: string, patchedPortfolioRequest?: PatchedPortfolioRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Portfolio>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosPartialUpdate(portfolioId, patchedPortfolioRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Or Create Portfolio Profile User Groups.
         * @param {string} portfolioId 
         * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosProfileUserGroupsCreate(portfolioId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosProfileUserGroupsCreate(portfolioId, portfolioProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosProfileUserGroupsDestroy(portfolioId: string, profileUserGroupId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosProfileUserGroupsDestroy(portfolioId, profileUserGroupId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List portfolios.
         * @param {string} portfolioId 
         * @param {'created_at' | 'updated_at'} sorting Sort portfolio user groups
         * @param {'ascending' | 'descending'} sortingOrder Sort portfolio user groups order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Portfolio profile user groups using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosProfileUserGroupsList(portfolioId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedPortfolioProfileUserGroupList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosProfileUserGroupsList(portfolioId, sorting, sortingOrder, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {PatchedPortfolioProfileUserGroupRequest} [patchedPortfolioProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosProfileUserGroupsPartialUpdate(portfolioId: string, profileUserGroupId: string, patchedPortfolioProfileUserGroupRequest?: PatchedPortfolioProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosProfileUserGroupsPartialUpdate(portfolioId, profileUserGroupId, patchedPortfolioProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosProfileUserGroupsRetrieve(portfolioId: string, profileUserGroupId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosProfileUserGroupsRetrieve(portfolioId, profileUserGroupId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosProfileUserGroupsUpdate(portfolioId: string, profileUserGroupId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosProfileUserGroupsUpdate(portfolioId, profileUserGroupId, portfolioProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get portfolio details.
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosRetrieve(portfolioId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Portfolio>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosRetrieve(portfolioId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update a Portfolio.
         * @param {string} portfolioId 
         * @param {PortfolioRequest} portfolioRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUpdate(portfolioId: string, portfolioRequest: PortfolioRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Portfolio>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUpdate(portfolioId, portfolioRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create new Portfolio User.
         * @param {string} portfolioId 
         * @param {PortfolioUserRequest} portfolioUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUsersCreate(portfolioId: string, portfolioUserRequest: PortfolioUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUsersCreate(portfolioId, portfolioUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Remove User Access from Portfolio.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUsersDestroy(portfolioId: string, portfolioUserId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUsersDestroy(portfolioId, portfolioUserId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Portfolio Users.
         * @param {string} portfolioId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter portfolios based on (part of) name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUsersList(portfolioId: string, nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedPortfolioUserList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUsersList(portfolioId, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update Portfolio User (patch).
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {PatchedPortfolioUserUpdateRequest} [patchedPortfolioUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUsersPartialUpdate(portfolioId: string, portfolioUserId: string, patchedPortfolioUserUpdateRequest?: PatchedPortfolioUserUpdateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioUserUpdate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUsersPartialUpdate(portfolioId, portfolioUserId, patchedPortfolioUserUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get Portfolio User Details.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUsersRetrieve(portfolioId: string, portfolioUserId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUsersRetrieve(portfolioId, portfolioUserId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Update Portfolio User.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {PortfolioUserUpdateRequest} [portfolioUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async portfoliosUsersUpdate(portfolioId: string, portfolioUserId: string, portfolioUserUpdateRequest?: PortfolioUserUpdateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PortfolioUserUpdate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.portfoliosUsersUpdate(portfolioId, portfolioUserId, portfolioUserUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * PortfoliosApi - factory interface
 * @export
 */
export const PortfoliosApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = PortfoliosApiFp(configuration)
    return {
        /**
         * List companies in batch registration.
         * @param {string} batchRegistrationId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Search by name, DUNS, registration number, or customer reference
         * @param {'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status'} [sorting] Field to sort by
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {Array<string>} [status] Filter by registration status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsCompaniesList(batchRegistrationId: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options?: any): AxiosPromise<PaginatedBatchRegistrationCompanyStatusList> {
            return localVarFp.portfoliosBatchRegistrationsCompaniesList(batchRegistrationId, nextKey, query, sorting, sortingOrder, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Create new Portfolio BatchRegistration.
         * @param {BatchRegistrationRequest} batchRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsCreate(batchRegistrationRequest: BatchRegistrationRequest, options?: any): AxiosPromise<BatchRegistration> {
            return localVarFp.portfoliosBatchRegistrationsCreate(batchRegistrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Post Batch Register Companies in a Portfolio.  API used for registering companies in a larger scale for example updating an entire portfolios or collection of portfolios.
         * @param {string} batchRegistrationId 
         * @param {Array<BatchCompanyRegistrationRequest>} batchCompanyRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsCreateRegistrations(batchRegistrationId: string, batchCompanyRegistrationRequest: Array<BatchCompanyRegistrationRequest>, options?: any): AxiosPromise<Array<BatchCompanyRegistration>> {
            return localVarFp.portfoliosBatchRegistrationsCreateRegistrations(batchRegistrationId, batchCompanyRegistrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List skipped rows for a batch registration.
         * @param {string} batchRegistrationId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsErrorsList(batchRegistrationId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedBatchRegistrationErrorList> {
            return localVarFp.portfoliosBatchRegistrationsErrorsList(batchRegistrationId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * List all batch registrations for the authenticated user.
         * @param {number} [limit] Number of results per page (max 100)
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [portfolioId] Filter by portfolio ID
         * @param {'created_at' | 'finished_at' | 'status'} [sorting] Field to sort by
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {Array<string>} [status] Filter by batch registration status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsList(limit?: number, nextKey?: string, portfolioId?: string, sorting?: 'created_at' | 'finished_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options?: any): AxiosPromise<PaginatedBatchRegistrationList> {
            return localVarFp.portfoliosBatchRegistrationsList(limit, nextKey, portfolioId, sorting, sortingOrder, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Upload Field mapping for Batch registration.
         * @param {string} batchRegistrationId 
         * @param {BatchRegistrationFieldMappingRequest} [batchRegistrationFieldMappingRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsMappingCreate(batchRegistrationId: string, batchRegistrationFieldMappingRequest?: BatchRegistrationFieldMappingRequest, options?: any): AxiosPromise<BatchRegistrationFieldMapping> {
            return localVarFp.portfoliosBatchRegistrationsMappingCreate(batchRegistrationId, batchRegistrationFieldMappingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Batch Registration Information.
         * @param {string} batchRegistrationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchRegistrationsRetrieve(batchRegistrationId: string, options?: any): AxiosPromise<BatchRegistration> {
            return localVarFp.portfoliosBatchRegistrationsRetrieve(batchRegistrationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Portfolios.
         * @param {PortfolioBatchResetRequest} portfolioBatchResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchResetCreate(portfolioBatchResetRequest: PortfolioBatchResetRequest, options?: any): AxiosPromise<PortfolioBatchReset> {
            return localVarFp.portfoliosBatchResetCreate(portfolioBatchResetRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Portfolios.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosBatchResetList(nextKey?: string, options?: any): AxiosPromise<PaginatedPortfolioBatchResetList> {
            return localVarFp.portfoliosBatchResetList(nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Register a new Portfolio Company.
         * @param {string} portfolioId 
         * @param {RegistrationRequest} [registrationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesCreate(portfolioId: string, registrationRequest?: RegistrationRequest, options?: any): AxiosPromise<Registration> {
            return localVarFp.portfoliosCompaniesCreate(portfolioId, registrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete Portfolio Company Details
         * @param {string} companyId 
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesDestroy(companyId: string, portfolioId: string, options?: any): AxiosPromise<void> {
            return localVarFp.portfoliosCompaniesDestroy(companyId, portfolioId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Portfolio Companies.
         * @param {string} portfolioId 
         * @param {Array<string>} [companyCountry] Filter by country.
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckActivityScore] Filter by activity score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckAdverseMediaScore] Filter by adverse media score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckComplianceScore] Filter by overall compliance score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckCountryScore] Filter by country score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckPepScore] Filter by PEP score (low, medium, high).
         * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckSanctionScore] Filter by sanction score (low, medium, high).
         * @param {string} [customerReference] Filter portfolios based on the customer_reference.
         * @param {string} [nextKey] The next_key is a cursor used to make it possible to paginate to the next results, pass the next_key from the previous request to retrieve next results.
         * @param {string} [query] Search companies by name, DUNS number, registration number, or customer reference.
         * @param {'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] 
         * @param {'asc' | 'desc'} [sortingOrder] Sort order
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesList(portfolioId: string, companyCountry?: Array<string>, complianceCheckActivityScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckAdverseMediaScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckComplianceScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckCountryScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckPepScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckSanctionScore?: Array<'high' | 'low' | 'medium' | 'null'>, customerReference?: string, nextKey?: string, query?: string, sorting?: 'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'asc' | 'desc', options?: any): AxiosPromise<PaginatedPortfolioCompanyList> {
            return localVarFp.portfoliosCompaniesList(portfolioId, companyCountry, complianceCheckActivityScore, complianceCheckAdverseMediaScore, complianceCheckComplianceScore, complianceCheckCountryScore, complianceCheckPepScore, complianceCheckSanctionScore, customerReference, nextKey, query, sorting, sortingOrder, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove all companies from this portfolio.
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesReset(portfolioId: string, options?: any): AxiosPromise<void> {
            return localVarFp.portfoliosCompaniesReset(portfolioId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Portfolio Company Details
         * @param {string} companyId 
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCompaniesRetrieve(companyId: string, portfolioId: string, options?: any): AxiosPromise<PortfolioCompany> {
            return localVarFp.portfoliosCompaniesRetrieve(companyId, portfolioId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new portfolio.
         * @param {PortfolioRequest} portfolioRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosCreate(portfolioRequest: PortfolioRequest, options?: any): AxiosPromise<Portfolio> {
            return localVarFp.portfoliosCreate(portfolioRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete Portfolio.
         * @param {string} portfolioId 
         * @param {boolean} [confirmed] Force delete portfolio and all associated alerts.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosDestroy(portfolioId: string, confirmed?: boolean, options?: any): AxiosPromise<void> {
            return localVarFp.portfoliosDestroy(portfolioId, confirmed, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Portfolios.
         * @param {string} [annotateCompanyId] Company external_id. When given, each result carries contains_company (bool) indicating whether the portfolio already contains that company. Omitted -&gt; contains_company is null.
         * @param {string} [customerReference] Filter portfolio by customer_reference.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {boolean} [ownedPortfolios] Only included portfolios owned by yourself.
         * @param {Array<string>} [portfolioId] Portfolio to filter
         * @param {string} [query] Filter profiles based on (part of) name.
         * @param {'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] Sort portfolios
         * @param {'ascending' | 'descending'} [sortingOrder] Sort portfolios order, descending is from high to low and ascending from high to low.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosList(annotateCompanyId?: string, customerReference?: string, nextKey?: string, ownedPortfolios?: boolean, portfolioId?: Array<string>, query?: string, sorting?: 'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options?: any): AxiosPromise<PaginatedPortfolioList> {
            return localVarFp.portfoliosList(annotateCompanyId, customerReference, nextKey, ownedPortfolios, portfolioId, query, sorting, sortingOrder, options).then((request) => request(axios, basePath));
        },
        /**
         * Portfolio Details API.
         * @param {string} portfolioId 
         * @param {PatchedPortfolioRequest} [patchedPortfolioRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosPartialUpdate(portfolioId: string, patchedPortfolioRequest?: PatchedPortfolioRequest, options?: any): AxiosPromise<Portfolio> {
            return localVarFp.portfoliosPartialUpdate(portfolioId, patchedPortfolioRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List Or Create Portfolio Profile User Groups.
         * @param {string} portfolioId 
         * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsCreate(portfolioId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options?: any): AxiosPromise<PortfolioProfileUserGroup> {
            return localVarFp.portfoliosProfileUserGroupsCreate(portfolioId, portfolioProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsDestroy(portfolioId: string, profileUserGroupId: string, options?: any): AxiosPromise<void> {
            return localVarFp.portfoliosProfileUserGroupsDestroy(portfolioId, profileUserGroupId, options).then((request) => request(axios, basePath));
        },
        /**
         * List portfolios.
         * @param {string} portfolioId 
         * @param {'created_at' | 'updated_at'} sorting Sort portfolio user groups
         * @param {'ascending' | 'descending'} sortingOrder Sort portfolio user groups order, descending is from high to low and ascending from high to low.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter Portfolio profile user groups using Query parameter, name search.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsList(portfolioId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedPortfolioProfileUserGroupList> {
            return localVarFp.portfoliosProfileUserGroupsList(portfolioId, sorting, sortingOrder, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {PatchedPortfolioProfileUserGroupRequest} [patchedPortfolioProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsPartialUpdate(portfolioId: string, profileUserGroupId: string, patchedPortfolioProfileUserGroupRequest?: PatchedPortfolioProfileUserGroupRequest, options?: any): AxiosPromise<PortfolioProfileUserGroup> {
            return localVarFp.portfoliosProfileUserGroupsPartialUpdate(portfolioId, profileUserGroupId, patchedPortfolioProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsRetrieve(portfolioId: string, profileUserGroupId: string, options?: any): AxiosPromise<PortfolioProfileUserGroup> {
            return localVarFp.portfoliosProfileUserGroupsRetrieve(portfolioId, profileUserGroupId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve, update or destroy Portfolio User Group.
         * @param {string} portfolioId 
         * @param {string} profileUserGroupId 
         * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosProfileUserGroupsUpdate(portfolioId: string, profileUserGroupId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options?: any): AxiosPromise<PortfolioProfileUserGroup> {
            return localVarFp.portfoliosProfileUserGroupsUpdate(portfolioId, profileUserGroupId, portfolioProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get portfolio details.
         * @param {string} portfolioId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosRetrieve(portfolioId: string, options?: any): AxiosPromise<Portfolio> {
            return localVarFp.portfoliosRetrieve(portfolioId, options).then((request) => request(axios, basePath));
        },
        /**
         * Update a Portfolio.
         * @param {string} portfolioId 
         * @param {PortfolioRequest} portfolioRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUpdate(portfolioId: string, portfolioRequest: PortfolioRequest, options?: any): AxiosPromise<Portfolio> {
            return localVarFp.portfoliosUpdate(portfolioId, portfolioRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create new Portfolio User.
         * @param {string} portfolioId 
         * @param {PortfolioUserRequest} portfolioUserRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersCreate(portfolioId: string, portfolioUserRequest: PortfolioUserRequest, options?: any): AxiosPromise<PortfolioUser> {
            return localVarFp.portfoliosUsersCreate(portfolioId, portfolioUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove User Access from Portfolio.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersDestroy(portfolioId: string, portfolioUserId: string, options?: any): AxiosPromise<void> {
            return localVarFp.portfoliosUsersDestroy(portfolioId, portfolioUserId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Portfolio Users.
         * @param {string} portfolioId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter portfolios based on (part of) name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersList(portfolioId: string, nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedPortfolioUserList> {
            return localVarFp.portfoliosUsersList(portfolioId, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Update Portfolio User (patch).
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {PatchedPortfolioUserUpdateRequest} [patchedPortfolioUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersPartialUpdate(portfolioId: string, portfolioUserId: string, patchedPortfolioUserUpdateRequest?: PatchedPortfolioUserUpdateRequest, options?: any): AxiosPromise<PortfolioUserUpdate> {
            return localVarFp.portfoliosUsersPartialUpdate(portfolioId, portfolioUserId, patchedPortfolioUserUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get Portfolio User Details.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersRetrieve(portfolioId: string, portfolioUserId: string, options?: any): AxiosPromise<PortfolioUser> {
            return localVarFp.portfoliosUsersRetrieve(portfolioId, portfolioUserId, options).then((request) => request(axios, basePath));
        },
        /**
         * Update Portfolio User.
         * @param {string} portfolioId 
         * @param {string} portfolioUserId 
         * @param {PortfolioUserUpdateRequest} [portfolioUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        portfoliosUsersUpdate(portfolioId: string, portfolioUserId: string, portfolioUserUpdateRequest?: PortfolioUserUpdateRequest, options?: any): AxiosPromise<PortfolioUserUpdate> {
            return localVarFp.portfoliosUsersUpdate(portfolioId, portfolioUserId, portfolioUserUpdateRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * PortfoliosApi - interface
 * @export
 * @interface PortfoliosApi
 */
export interface PortfoliosApiInterface {
    /**
     * List companies in batch registration.
     * @param {string} batchRegistrationId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Search by name, DUNS, registration number, or customer reference
     * @param {'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status'} [sorting] Field to sort by
     * @param {'asc' | 'desc'} [sortingOrder] Sort order
     * @param {Array<string>} [status] Filter by registration status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsCompaniesList(batchRegistrationId: string, nextKey?: string, query?: string, sorting?: 'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options?: AxiosRequestConfig): AxiosPromise<PaginatedBatchRegistrationCompanyStatusList>;

    /**
     * Create new Portfolio BatchRegistration.
     * @param {BatchRegistrationRequest} batchRegistrationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsCreate(batchRegistrationRequest: BatchRegistrationRequest, options?: AxiosRequestConfig): AxiosPromise<BatchRegistration>;

    /**
     * Post Batch Register Companies in a Portfolio.  API used for registering companies in a larger scale for example updating an entire portfolios or collection of portfolios.
     * @param {string} batchRegistrationId 
     * @param {Array<BatchCompanyRegistrationRequest>} batchCompanyRegistrationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsCreateRegistrations(batchRegistrationId: string, batchCompanyRegistrationRequest: Array<BatchCompanyRegistrationRequest>, options?: AxiosRequestConfig): AxiosPromise<Array<BatchCompanyRegistration>>;

    /**
     * List skipped rows for a batch registration.
     * @param {string} batchRegistrationId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsErrorsList(batchRegistrationId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedBatchRegistrationErrorList>;

    /**
     * List all batch registrations for the authenticated user.
     * @param {number} [limit] Number of results per page (max 100)
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [portfolioId] Filter by portfolio ID
     * @param {'created_at' | 'finished_at' | 'status'} [sorting] Field to sort by
     * @param {'asc' | 'desc'} [sortingOrder] Sort order
     * @param {Array<string>} [status] Filter by batch registration status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsList(limit?: number, nextKey?: string, portfolioId?: string, sorting?: 'created_at' | 'finished_at' | 'status', sortingOrder?: 'asc' | 'desc', status?: Array<string>, options?: AxiosRequestConfig): AxiosPromise<PaginatedBatchRegistrationList>;

    /**
     * Upload Field mapping for Batch registration.
     * @param {string} batchRegistrationId 
     * @param {BatchRegistrationFieldMappingRequest} [batchRegistrationFieldMappingRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsMappingCreate(batchRegistrationId: string, batchRegistrationFieldMappingRequest?: BatchRegistrationFieldMappingRequest, options?: AxiosRequestConfig): AxiosPromise<BatchRegistrationFieldMapping>;

    /**
     * Get Batch Registration Information.
     * @param {string} batchRegistrationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchRegistrationsRetrieve(batchRegistrationId: string, options?: AxiosRequestConfig): AxiosPromise<BatchRegistration>;

    /**
     * Get all Portfolios.
     * @param {PortfolioBatchResetRequest} portfolioBatchResetRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchResetCreate(portfolioBatchResetRequest: PortfolioBatchResetRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioBatchReset>;

    /**
     * Get all Portfolios.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosBatchResetList(nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedPortfolioBatchResetList>;

    /**
     * Register a new Portfolio Company.
     * @param {string} portfolioId 
     * @param {RegistrationRequest} [registrationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosCompaniesCreate(portfolioId: string, registrationRequest?: RegistrationRequest, options?: AxiosRequestConfig): AxiosPromise<Registration>;

    /**
     * Delete Portfolio Company Details
     * @param {string} companyId 
     * @param {string} portfolioId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosCompaniesDestroy(companyId: string, portfolioId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get all Portfolio Companies.
     * @param {string} portfolioId 
     * @param {Array<string>} [companyCountry] Filter by country.
     * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckActivityScore] Filter by activity score (low, medium, high).
     * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckAdverseMediaScore] Filter by adverse media score (low, medium, high).
     * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckComplianceScore] Filter by overall compliance score (low, medium, high).
     * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckCountryScore] Filter by country score (low, medium, high).
     * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckPepScore] Filter by PEP score (low, medium, high).
     * @param {Array<'high' | 'low' | 'medium' | 'null'>} [complianceCheckSanctionScore] Filter by sanction score (low, medium, high).
     * @param {string} [customerReference] Filter portfolios based on the customer_reference.
     * @param {string} [nextKey] The next_key is a cursor used to make it possible to paginate to the next results, pass the next_key from the previous request to retrieve next results.
     * @param {string} [query] Search companies by name, DUNS number, registration number, or customer reference.
     * @param {'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] 
     * @param {'asc' | 'desc'} [sortingOrder] Sort order
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosCompaniesList(portfolioId: string, companyCountry?: Array<string>, complianceCheckActivityScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckAdverseMediaScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckComplianceScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckCountryScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckPepScore?: Array<'high' | 'low' | 'medium' | 'null'>, complianceCheckSanctionScore?: Array<'high' | 'low' | 'medium' | 'null'>, customerReference?: string, nextKey?: string, query?: string, sorting?: 'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'asc' | 'desc', options?: AxiosRequestConfig): AxiosPromise<PaginatedPortfolioCompanyList>;

    /**
     * Remove all companies from this portfolio.
     * @param {string} portfolioId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosCompaniesReset(portfolioId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get Portfolio Company Details
     * @param {string} companyId 
     * @param {string} portfolioId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosCompaniesRetrieve(companyId: string, portfolioId: string, options?: AxiosRequestConfig): AxiosPromise<PortfolioCompany>;

    /**
     * Create a new portfolio.
     * @param {PortfolioRequest} portfolioRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosCreate(portfolioRequest: PortfolioRequest, options?: AxiosRequestConfig): AxiosPromise<Portfolio>;

    /**
     * Delete Portfolio.
     * @param {string} portfolioId 
     * @param {boolean} [confirmed] Force delete portfolio and all associated alerts.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosDestroy(portfolioId: string, confirmed?: boolean, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get all Portfolios.
     * @param {string} [annotateCompanyId] Company external_id. When given, each result carries contains_company (bool) indicating whether the portfolio already contains that company. Omitted -&gt; contains_company is null.
     * @param {string} [customerReference] Filter portfolio by customer_reference.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {boolean} [ownedPortfolios] Only included portfolios owned by yourself.
     * @param {Array<string>} [portfolioId] Portfolio to filter
     * @param {string} [query] Filter profiles based on (part of) name.
     * @param {'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at'} [sorting] Sort portfolios
     * @param {'ascending' | 'descending'} [sortingOrder] Sort portfolios order, descending is from high to low and ascending from high to low.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosList(annotateCompanyId?: string, customerReference?: string, nextKey?: string, ownedPortfolios?: boolean, portfolioId?: Array<string>, query?: string, sorting?: 'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): AxiosPromise<PaginatedPortfolioList>;

    /**
     * Portfolio Details API.
     * @param {string} portfolioId 
     * @param {PatchedPortfolioRequest} [patchedPortfolioRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosPartialUpdate(portfolioId: string, patchedPortfolioRequest?: PatchedPortfolioRequest, options?: AxiosRequestConfig): AxiosPromise<Portfolio>;

    /**
     * List Or Create Portfolio Profile User Groups.
     * @param {string} portfolioId 
     * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosProfileUserGroupsCreate(portfolioId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioProfileUserGroup>;

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {string} portfolioId 
     * @param {string} profileUserGroupId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosProfileUserGroupsDestroy(portfolioId: string, profileUserGroupId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List portfolios.
     * @param {string} portfolioId 
     * @param {'created_at' | 'updated_at'} sorting Sort portfolio user groups
     * @param {'ascending' | 'descending'} sortingOrder Sort portfolio user groups order, descending is from high to low and ascending from high to low.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter Portfolio profile user groups using Query parameter, name search.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosProfileUserGroupsList(portfolioId: string, sorting: 'created_at' | 'updated_at', sortingOrder: 'ascending' | 'descending', nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedPortfolioProfileUserGroupList>;

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {string} portfolioId 
     * @param {string} profileUserGroupId 
     * @param {PatchedPortfolioProfileUserGroupRequest} [patchedPortfolioProfileUserGroupRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosProfileUserGroupsPartialUpdate(portfolioId: string, profileUserGroupId: string, patchedPortfolioProfileUserGroupRequest?: PatchedPortfolioProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioProfileUserGroup>;

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {string} portfolioId 
     * @param {string} profileUserGroupId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosProfileUserGroupsRetrieve(portfolioId: string, profileUserGroupId: string, options?: AxiosRequestConfig): AxiosPromise<PortfolioProfileUserGroup>;

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {string} portfolioId 
     * @param {string} profileUserGroupId 
     * @param {PortfolioProfileUserGroupRequest} portfolioProfileUserGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosProfileUserGroupsUpdate(portfolioId: string, profileUserGroupId: string, portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioProfileUserGroup>;

    /**
     * Get portfolio details.
     * @param {string} portfolioId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosRetrieve(portfolioId: string, options?: AxiosRequestConfig): AxiosPromise<Portfolio>;

    /**
     * Update a Portfolio.
     * @param {string} portfolioId 
     * @param {PortfolioRequest} portfolioRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUpdate(portfolioId: string, portfolioRequest: PortfolioRequest, options?: AxiosRequestConfig): AxiosPromise<Portfolio>;

    /**
     * Create new Portfolio User.
     * @param {string} portfolioId 
     * @param {PortfolioUserRequest} portfolioUserRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUsersCreate(portfolioId: string, portfolioUserRequest: PortfolioUserRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioUser>;

    /**
     * Remove User Access from Portfolio.
     * @param {string} portfolioId 
     * @param {string} portfolioUserId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUsersDestroy(portfolioId: string, portfolioUserId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get all Portfolio Users.
     * @param {string} portfolioId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter portfolios based on (part of) name.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUsersList(portfolioId: string, nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedPortfolioUserList>;

    /**
     * Update Portfolio User (patch).
     * @param {string} portfolioId 
     * @param {string} portfolioUserId 
     * @param {PatchedPortfolioUserUpdateRequest} [patchedPortfolioUserUpdateRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUsersPartialUpdate(portfolioId: string, portfolioUserId: string, patchedPortfolioUserUpdateRequest?: PatchedPortfolioUserUpdateRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioUserUpdate>;

    /**
     * Get Portfolio User Details.
     * @param {string} portfolioId 
     * @param {string} portfolioUserId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUsersRetrieve(portfolioId: string, portfolioUserId: string, options?: AxiosRequestConfig): AxiosPromise<PortfolioUser>;

    /**
     * Update Portfolio User.
     * @param {string} portfolioId 
     * @param {string} portfolioUserId 
     * @param {PortfolioUserUpdateRequest} [portfolioUserUpdateRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApiInterface
     */
    portfoliosUsersUpdate(portfolioId: string, portfolioUserId: string, portfolioUserUpdateRequest?: PortfolioUserUpdateRequest, options?: AxiosRequestConfig): AxiosPromise<PortfolioUserUpdate>;

}

/**
 * Request parameters for portfoliosBatchRegistrationsCompaniesList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsCompaniesListRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsCompaniesListRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCompaniesList
     */
    readonly batchRegistrationId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCompaniesList
     */
    readonly nextKey?: string

    /**
     * Search by name, DUNS, registration number, or customer reference
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCompaniesList
     */
    readonly query?: string

    /**
     * Field to sort by
     * @type {'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status'}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCompaniesList
     */
    readonly sorting?: 'created_at' | 'failed_at' | 'finished_at' | 'primary_name' | 'progress' | 'started_at' | 'status'

    /**
     * Sort order
     * @type {'asc' | 'desc'}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCompaniesList
     */
    readonly sortingOrder?: 'asc' | 'desc'

    /**
     * Filter by registration status
     * @type {Array<string>}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCompaniesList
     */
    readonly status?: Array<string>
}

/**
 * Request parameters for portfoliosBatchRegistrationsCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsCreateRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsCreateRequest {
    /**
     * 
     * @type {BatchRegistrationRequest}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCreate
     */
    readonly batchRegistrationRequest: BatchRegistrationRequest
}

/**
 * Request parameters for portfoliosBatchRegistrationsCreateRegistrations operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsCreateRegistrationsRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsCreateRegistrationsRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCreateRegistrations
     */
    readonly batchRegistrationId: string

    /**
     * 
     * @type {Array<BatchCompanyRegistrationRequest>}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsCreateRegistrations
     */
    readonly batchCompanyRegistrationRequest: Array<BatchCompanyRegistrationRequest>
}

/**
 * Request parameters for portfoliosBatchRegistrationsErrorsList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsErrorsListRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsErrorsListRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsErrorsList
     */
    readonly batchRegistrationId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsErrorsList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for portfoliosBatchRegistrationsList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsListRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsListRequest {
    /**
     * Number of results per page (max 100)
     * @type {number}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsList
     */
    readonly limit?: number

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsList
     */
    readonly nextKey?: string

    /**
     * Filter by portfolio ID
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsList
     */
    readonly portfolioId?: string

    /**
     * Field to sort by
     * @type {'created_at' | 'finished_at' | 'status'}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsList
     */
    readonly sorting?: 'created_at' | 'finished_at' | 'status'

    /**
     * Sort order
     * @type {'asc' | 'desc'}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsList
     */
    readonly sortingOrder?: 'asc' | 'desc'

    /**
     * Filter by batch registration status
     * @type {Array<string>}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsList
     */
    readonly status?: Array<string>
}

/**
 * Request parameters for portfoliosBatchRegistrationsMappingCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsMappingCreateRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsMappingCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsMappingCreate
     */
    readonly batchRegistrationId: string

    /**
     * 
     * @type {BatchRegistrationFieldMappingRequest}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsMappingCreate
     */
    readonly batchRegistrationFieldMappingRequest?: BatchRegistrationFieldMappingRequest
}

/**
 * Request parameters for portfoliosBatchRegistrationsRetrieve operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchRegistrationsRetrieveRequest
 */
export interface PortfoliosApiPortfoliosBatchRegistrationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchRegistrationsRetrieve
     */
    readonly batchRegistrationId: string
}

/**
 * Request parameters for portfoliosBatchResetCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchResetCreateRequest
 */
export interface PortfoliosApiPortfoliosBatchResetCreateRequest {
    /**
     * 
     * @type {PortfolioBatchResetRequest}
     * @memberof PortfoliosApiPortfoliosBatchResetCreate
     */
    readonly portfolioBatchResetRequest: PortfolioBatchResetRequest
}

/**
 * Request parameters for portfoliosBatchResetList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosBatchResetListRequest
 */
export interface PortfoliosApiPortfoliosBatchResetListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosBatchResetList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for portfoliosCompaniesCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosCompaniesCreateRequest
 */
export interface PortfoliosApiPortfoliosCompaniesCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesCreate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {RegistrationRequest}
     * @memberof PortfoliosApiPortfoliosCompaniesCreate
     */
    readonly registrationRequest?: RegistrationRequest
}

/**
 * Request parameters for portfoliosCompaniesDestroy operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosCompaniesDestroyRequest
 */
export interface PortfoliosApiPortfoliosCompaniesDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesDestroy
     */
    readonly companyId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesDestroy
     */
    readonly portfolioId: string
}

/**
 * Request parameters for portfoliosCompaniesList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosCompaniesListRequest
 */
export interface PortfoliosApiPortfoliosCompaniesListRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly portfolioId: string

    /**
     * Filter by country.
     * @type {Array<string>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly companyCountry?: Array<string>

    /**
     * Filter by activity score (low, medium, high).
     * @type {Array<'high' | 'low' | 'medium' | 'null'>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly complianceCheckActivityScore?: Array<'high' | 'low' | 'medium' | 'null'>

    /**
     * Filter by adverse media score (low, medium, high).
     * @type {Array<'high' | 'low' | 'medium' | 'null'>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly complianceCheckAdverseMediaScore?: Array<'high' | 'low' | 'medium' | 'null'>

    /**
     * Filter by overall compliance score (low, medium, high).
     * @type {Array<'high' | 'low' | 'medium' | 'null'>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly complianceCheckComplianceScore?: Array<'high' | 'low' | 'medium' | 'null'>

    /**
     * Filter by country score (low, medium, high).
     * @type {Array<'high' | 'low' | 'medium' | 'null'>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly complianceCheckCountryScore?: Array<'high' | 'low' | 'medium' | 'null'>

    /**
     * Filter by PEP score (low, medium, high).
     * @type {Array<'high' | 'low' | 'medium' | 'null'>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly complianceCheckPepScore?: Array<'high' | 'low' | 'medium' | 'null'>

    /**
     * Filter by sanction score (low, medium, high).
     * @type {Array<'high' | 'low' | 'medium' | 'null'>}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly complianceCheckSanctionScore?: Array<'high' | 'low' | 'medium' | 'null'>

    /**
     * Filter portfolios based on the customer_reference.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly customerReference?: string

    /**
     * The next_key is a cursor used to make it possible to paginate to the next results, pass the next_key from the previous request to retrieve next results.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly nextKey?: string

    /**
     * Search companies by name, DUNS number, registration number, or customer reference.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly query?: string

    /**
     * 
     * @type {'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at'}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly sorting?: 'company__address_place' | 'company__country' | 'company__name' | 'compliance_check__activity_score' | 'compliance_check__adverse_media_score' | 'compliance_check__compliance_score' | 'compliance_check__country_score' | 'compliance_check__pep_score' | 'compliance_check__sanction_score' | 'created_at' | 'customer_reference' | 'updated_at'

    /**
     * Sort order
     * @type {'asc' | 'desc'}
     * @memberof PortfoliosApiPortfoliosCompaniesList
     */
    readonly sortingOrder?: 'asc' | 'desc'
}

/**
 * Request parameters for portfoliosCompaniesReset operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosCompaniesResetRequest
 */
export interface PortfoliosApiPortfoliosCompaniesResetRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesReset
     */
    readonly portfolioId: string
}

/**
 * Request parameters for portfoliosCompaniesRetrieve operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosCompaniesRetrieveRequest
 */
export interface PortfoliosApiPortfoliosCompaniesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesRetrieve
     */
    readonly companyId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosCompaniesRetrieve
     */
    readonly portfolioId: string
}

/**
 * Request parameters for portfoliosCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosCreateRequest
 */
export interface PortfoliosApiPortfoliosCreateRequest {
    /**
     * 
     * @type {PortfolioRequest}
     * @memberof PortfoliosApiPortfoliosCreate
     */
    readonly portfolioRequest: PortfolioRequest
}

/**
 * Request parameters for portfoliosDestroy operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosDestroyRequest
 */
export interface PortfoliosApiPortfoliosDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosDestroy
     */
    readonly portfolioId: string

    /**
     * Force delete portfolio and all associated alerts.
     * @type {boolean}
     * @memberof PortfoliosApiPortfoliosDestroy
     */
    readonly confirmed?: boolean
}

/**
 * Request parameters for portfoliosList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosListRequest
 */
export interface PortfoliosApiPortfoliosListRequest {
    /**
     * Company external_id. When given, each result carries contains_company (bool) indicating whether the portfolio already contains that company. Omitted -&gt; contains_company is null.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly annotateCompanyId?: string

    /**
     * Filter portfolio by customer_reference.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly customerReference?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly nextKey?: string

    /**
     * Only included portfolios owned by yourself.
     * @type {boolean}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly ownedPortfolios?: boolean

    /**
     * Portfolio to filter
     * @type {Array<string>}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly portfolioId?: Array<string>

    /**
     * Filter profiles based on (part of) name.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly query?: string

    /**
     * Sort portfolios
     * @type {'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at'}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly sorting?: 'alphabetical' | 'companies_count' | 'created_at' | 'customer_reference' | 'updated_at'

    /**
     * Sort portfolios order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof PortfoliosApiPortfoliosList
     */
    readonly sortingOrder?: 'ascending' | 'descending'
}

/**
 * Request parameters for portfoliosPartialUpdate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosPartialUpdateRequest
 */
export interface PortfoliosApiPortfoliosPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosPartialUpdate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {PatchedPortfolioRequest}
     * @memberof PortfoliosApiPortfoliosPartialUpdate
     */
    readonly patchedPortfolioRequest?: PatchedPortfolioRequest
}

/**
 * Request parameters for portfoliosProfileUserGroupsCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosProfileUserGroupsCreateRequest
 */
export interface PortfoliosApiPortfoliosProfileUserGroupsCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsCreate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {PortfolioProfileUserGroupRequest}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsCreate
     */
    readonly portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest
}

/**
 * Request parameters for portfoliosProfileUserGroupsDestroy operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosProfileUserGroupsDestroyRequest
 */
export interface PortfoliosApiPortfoliosProfileUserGroupsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsDestroy
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsDestroy
     */
    readonly profileUserGroupId: string
}

/**
 * Request parameters for portfoliosProfileUserGroupsList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosProfileUserGroupsListRequest
 */
export interface PortfoliosApiPortfoliosProfileUserGroupsListRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsList
     */
    readonly portfolioId: string

    /**
     * Sort portfolio user groups
     * @type {'created_at' | 'updated_at'}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsList
     */
    readonly sorting: 'created_at' | 'updated_at'

    /**
     * Sort portfolio user groups order, descending is from high to low and ascending from high to low.
     * @type {'ascending' | 'descending'}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsList
     */
    readonly sortingOrder: 'ascending' | 'descending'

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsList
     */
    readonly nextKey?: string

    /**
     * Filter Portfolio profile user groups using Query parameter, name search.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsList
     */
    readonly query?: string
}

/**
 * Request parameters for portfoliosProfileUserGroupsPartialUpdate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosProfileUserGroupsPartialUpdateRequest
 */
export interface PortfoliosApiPortfoliosProfileUserGroupsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsPartialUpdate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsPartialUpdate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {PatchedPortfolioProfileUserGroupRequest}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsPartialUpdate
     */
    readonly patchedPortfolioProfileUserGroupRequest?: PatchedPortfolioProfileUserGroupRequest
}

/**
 * Request parameters for portfoliosProfileUserGroupsRetrieve operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosProfileUserGroupsRetrieveRequest
 */
export interface PortfoliosApiPortfoliosProfileUserGroupsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsRetrieve
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsRetrieve
     */
    readonly profileUserGroupId: string
}

/**
 * Request parameters for portfoliosProfileUserGroupsUpdate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosProfileUserGroupsUpdateRequest
 */
export interface PortfoliosApiPortfoliosProfileUserGroupsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsUpdate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsUpdate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {PortfolioProfileUserGroupRequest}
     * @memberof PortfoliosApiPortfoliosProfileUserGroupsUpdate
     */
    readonly portfolioProfileUserGroupRequest: PortfolioProfileUserGroupRequest
}

/**
 * Request parameters for portfoliosRetrieve operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosRetrieveRequest
 */
export interface PortfoliosApiPortfoliosRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosRetrieve
     */
    readonly portfolioId: string
}

/**
 * Request parameters for portfoliosUpdate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUpdateRequest
 */
export interface PortfoliosApiPortfoliosUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUpdate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {PortfolioRequest}
     * @memberof PortfoliosApiPortfoliosUpdate
     */
    readonly portfolioRequest: PortfolioRequest
}

/**
 * Request parameters for portfoliosUsersCreate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUsersCreateRequest
 */
export interface PortfoliosApiPortfoliosUsersCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersCreate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {PortfolioUserRequest}
     * @memberof PortfoliosApiPortfoliosUsersCreate
     */
    readonly portfolioUserRequest: PortfolioUserRequest
}

/**
 * Request parameters for portfoliosUsersDestroy operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUsersDestroyRequest
 */
export interface PortfoliosApiPortfoliosUsersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersDestroy
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersDestroy
     */
    readonly portfolioUserId: string
}

/**
 * Request parameters for portfoliosUsersList operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUsersListRequest
 */
export interface PortfoliosApiPortfoliosUsersListRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersList
     */
    readonly portfolioId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersList
     */
    readonly nextKey?: string

    /**
     * Filter portfolios based on (part of) name.
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersList
     */
    readonly query?: string
}

/**
 * Request parameters for portfoliosUsersPartialUpdate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUsersPartialUpdateRequest
 */
export interface PortfoliosApiPortfoliosUsersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersPartialUpdate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersPartialUpdate
     */
    readonly portfolioUserId: string

    /**
     * 
     * @type {PatchedPortfolioUserUpdateRequest}
     * @memberof PortfoliosApiPortfoliosUsersPartialUpdate
     */
    readonly patchedPortfolioUserUpdateRequest?: PatchedPortfolioUserUpdateRequest
}

/**
 * Request parameters for portfoliosUsersRetrieve operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUsersRetrieveRequest
 */
export interface PortfoliosApiPortfoliosUsersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersRetrieve
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersRetrieve
     */
    readonly portfolioUserId: string
}

/**
 * Request parameters for portfoliosUsersUpdate operation in PortfoliosApi.
 * @export
 * @interface PortfoliosApiPortfoliosUsersUpdateRequest
 */
export interface PortfoliosApiPortfoliosUsersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersUpdate
     */
    readonly portfolioId: string

    /**
     * 
     * @type {string}
     * @memberof PortfoliosApiPortfoliosUsersUpdate
     */
    readonly portfolioUserId: string

    /**
     * 
     * @type {PortfolioUserUpdateRequest}
     * @memberof PortfoliosApiPortfoliosUsersUpdate
     */
    readonly portfolioUserUpdateRequest?: PortfolioUserUpdateRequest
}

/**
 * PortfoliosApi - object-oriented interface
 * @export
 * @class PortfoliosApi
 * @extends {BaseAPI}
 */
export class PortfoliosApi extends BaseAPI implements PortfoliosApiInterface {
    /**
     * List companies in batch registration.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsCompaniesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsCompaniesList(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsCompaniesListRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsCompaniesList(requestParameters.batchRegistrationId, requestParameters.nextKey, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create new Portfolio BatchRegistration.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsCreate(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsCreate(requestParameters.batchRegistrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Post Batch Register Companies in a Portfolio.  API used for registering companies in a larger scale for example updating an entire portfolios or collection of portfolios.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsCreateRegistrationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsCreateRegistrations(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsCreateRegistrationsRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsCreateRegistrations(requestParameters.batchRegistrationId, requestParameters.batchCompanyRegistrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List skipped rows for a batch registration.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsErrorsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsErrorsList(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsErrorsListRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsErrorsList(requestParameters.batchRegistrationId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List all batch registrations for the authenticated user.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsList(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsListRequest = {}, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsList(requestParameters.limit, requestParameters.nextKey, requestParameters.portfolioId, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Upload Field mapping for Batch registration.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsMappingCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsMappingCreate(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsMappingCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsMappingCreate(requestParameters.batchRegistrationId, requestParameters.batchRegistrationFieldMappingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Batch Registration Information.
     * @param {PortfoliosApiPortfoliosBatchRegistrationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchRegistrationsRetrieve(requestParameters: PortfoliosApiPortfoliosBatchRegistrationsRetrieveRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchRegistrationsRetrieve(requestParameters.batchRegistrationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Portfolios.
     * @param {PortfoliosApiPortfoliosBatchResetCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchResetCreate(requestParameters: PortfoliosApiPortfoliosBatchResetCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchResetCreate(requestParameters.portfolioBatchResetRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Portfolios.
     * @param {PortfoliosApiPortfoliosBatchResetListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosBatchResetList(requestParameters: PortfoliosApiPortfoliosBatchResetListRequest = {}, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosBatchResetList(requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Register a new Portfolio Company.
     * @param {PortfoliosApiPortfoliosCompaniesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosCompaniesCreate(requestParameters: PortfoliosApiPortfoliosCompaniesCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosCompaniesCreate(requestParameters.portfolioId, requestParameters.registrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete Portfolio Company Details
     * @param {PortfoliosApiPortfoliosCompaniesDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosCompaniesDestroy(requestParameters: PortfoliosApiPortfoliosCompaniesDestroyRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosCompaniesDestroy(requestParameters.companyId, requestParameters.portfolioId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Portfolio Companies.
     * @param {PortfoliosApiPortfoliosCompaniesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosCompaniesList(requestParameters: PortfoliosApiPortfoliosCompaniesListRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosCompaniesList(requestParameters.portfolioId, requestParameters.companyCountry, requestParameters.complianceCheckActivityScore, requestParameters.complianceCheckAdverseMediaScore, requestParameters.complianceCheckComplianceScore, requestParameters.complianceCheckCountryScore, requestParameters.complianceCheckPepScore, requestParameters.complianceCheckSanctionScore, requestParameters.customerReference, requestParameters.nextKey, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove all companies from this portfolio.
     * @param {PortfoliosApiPortfoliosCompaniesResetRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosCompaniesReset(requestParameters: PortfoliosApiPortfoliosCompaniesResetRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosCompaniesReset(requestParameters.portfolioId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Portfolio Company Details
     * @param {PortfoliosApiPortfoliosCompaniesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosCompaniesRetrieve(requestParameters: PortfoliosApiPortfoliosCompaniesRetrieveRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosCompaniesRetrieve(requestParameters.companyId, requestParameters.portfolioId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new portfolio.
     * @param {PortfoliosApiPortfoliosCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosCreate(requestParameters: PortfoliosApiPortfoliosCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosCreate(requestParameters.portfolioRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete Portfolio.
     * @param {PortfoliosApiPortfoliosDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosDestroy(requestParameters: PortfoliosApiPortfoliosDestroyRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosDestroy(requestParameters.portfolioId, requestParameters.confirmed, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Portfolios.
     * @param {PortfoliosApiPortfoliosListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosList(requestParameters: PortfoliosApiPortfoliosListRequest = {}, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosList(requestParameters.annotateCompanyId, requestParameters.customerReference, requestParameters.nextKey, requestParameters.ownedPortfolios, requestParameters.portfolioId, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Portfolio Details API.
     * @param {PortfoliosApiPortfoliosPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosPartialUpdate(requestParameters: PortfoliosApiPortfoliosPartialUpdateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosPartialUpdate(requestParameters.portfolioId, requestParameters.patchedPortfolioRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Or Create Portfolio Profile User Groups.
     * @param {PortfoliosApiPortfoliosProfileUserGroupsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosProfileUserGroupsCreate(requestParameters: PortfoliosApiPortfoliosProfileUserGroupsCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosProfileUserGroupsCreate(requestParameters.portfolioId, requestParameters.portfolioProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {PortfoliosApiPortfoliosProfileUserGroupsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosProfileUserGroupsDestroy(requestParameters: PortfoliosApiPortfoliosProfileUserGroupsDestroyRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosProfileUserGroupsDestroy(requestParameters.portfolioId, requestParameters.profileUserGroupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List portfolios.
     * @param {PortfoliosApiPortfoliosProfileUserGroupsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosProfileUserGroupsList(requestParameters: PortfoliosApiPortfoliosProfileUserGroupsListRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosProfileUserGroupsList(requestParameters.portfolioId, requestParameters.sorting, requestParameters.sortingOrder, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {PortfoliosApiPortfoliosProfileUserGroupsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosProfileUserGroupsPartialUpdate(requestParameters: PortfoliosApiPortfoliosProfileUserGroupsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosProfileUserGroupsPartialUpdate(requestParameters.portfolioId, requestParameters.profileUserGroupId, requestParameters.patchedPortfolioProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {PortfoliosApiPortfoliosProfileUserGroupsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosProfileUserGroupsRetrieve(requestParameters: PortfoliosApiPortfoliosProfileUserGroupsRetrieveRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosProfileUserGroupsRetrieve(requestParameters.portfolioId, requestParameters.profileUserGroupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve, update or destroy Portfolio User Group.
     * @param {PortfoliosApiPortfoliosProfileUserGroupsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosProfileUserGroupsUpdate(requestParameters: PortfoliosApiPortfoliosProfileUserGroupsUpdateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosProfileUserGroupsUpdate(requestParameters.portfolioId, requestParameters.profileUserGroupId, requestParameters.portfolioProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get portfolio details.
     * @param {PortfoliosApiPortfoliosRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosRetrieve(requestParameters: PortfoliosApiPortfoliosRetrieveRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosRetrieve(requestParameters.portfolioId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update a Portfolio.
     * @param {PortfoliosApiPortfoliosUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUpdate(requestParameters: PortfoliosApiPortfoliosUpdateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUpdate(requestParameters.portfolioId, requestParameters.portfolioRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create new Portfolio User.
     * @param {PortfoliosApiPortfoliosUsersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUsersCreate(requestParameters: PortfoliosApiPortfoliosUsersCreateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUsersCreate(requestParameters.portfolioId, requestParameters.portfolioUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove User Access from Portfolio.
     * @param {PortfoliosApiPortfoliosUsersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUsersDestroy(requestParameters: PortfoliosApiPortfoliosUsersDestroyRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUsersDestroy(requestParameters.portfolioId, requestParameters.portfolioUserId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Portfolio Users.
     * @param {PortfoliosApiPortfoliosUsersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUsersList(requestParameters: PortfoliosApiPortfoliosUsersListRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUsersList(requestParameters.portfolioId, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update Portfolio User (patch).
     * @param {PortfoliosApiPortfoliosUsersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUsersPartialUpdate(requestParameters: PortfoliosApiPortfoliosUsersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUsersPartialUpdate(requestParameters.portfolioId, requestParameters.portfolioUserId, requestParameters.patchedPortfolioUserUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get Portfolio User Details.
     * @param {PortfoliosApiPortfoliosUsersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUsersRetrieve(requestParameters: PortfoliosApiPortfoliosUsersRetrieveRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUsersRetrieve(requestParameters.portfolioId, requestParameters.portfolioUserId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update Portfolio User.
     * @param {PortfoliosApiPortfoliosUsersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof PortfoliosApi
     */
    public portfoliosUsersUpdate(requestParameters: PortfoliosApiPortfoliosUsersUpdateRequest, options?: AxiosRequestConfig) {
        return PortfoliosApiFp(this.configuration).portfoliosUsersUpdate(requestParameters.portfolioId, requestParameters.portfolioUserId, requestParameters.portfolioUserUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * ProblemsApi - axios parameter creator
 * @export
 */
export const ProblemsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} code 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        problemsRetrieve: async (code: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'code' is not null or undefined
            assertParamExists('problemsRetrieve', 'code', code)
            const localVarPath = `/problems/{code}/`
                .replace(`{${"code"}}`, encodeURIComponent(String(code)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ProblemsApi - functional programming interface
 * @export
 */
export const ProblemsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ProblemsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} code 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async problemsRetrieve(code: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ErrorDescription>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.problemsRetrieve(code, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * ProblemsApi - factory interface
 * @export
 */
export const ProblemsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ProblemsApiFp(configuration)
    return {
        /**
         * 
         * @param {string} code 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        problemsRetrieve(code: string, options?: any): AxiosPromise<ErrorDescription> {
            return localVarFp.problemsRetrieve(code, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ProblemsApi - interface
 * @export
 * @interface ProblemsApi
 */
export interface ProblemsApiInterface {
    /**
     * 
     * @param {string} code 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProblemsApiInterface
     */
    problemsRetrieve(code: string, options?: AxiosRequestConfig): AxiosPromise<ErrorDescription>;

}

/**
 * Request parameters for problemsRetrieve operation in ProblemsApi.
 * @export
 * @interface ProblemsApiProblemsRetrieveRequest
 */
export interface ProblemsApiProblemsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ProblemsApiProblemsRetrieve
     */
    readonly code: string
}

/**
 * ProblemsApi - object-oriented interface
 * @export
 * @class ProblemsApi
 * @extends {BaseAPI}
 */
export class ProblemsApi extends BaseAPI implements ProblemsApiInterface {
    /**
     * 
     * @param {ProblemsApiProblemsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProblemsApi
     */
    public problemsRetrieve(requestParameters: ProblemsApiProblemsRetrieveRequest, options?: AxiosRequestConfig) {
        return ProblemsApiFp(this.configuration).problemsRetrieve(requestParameters.code, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * ProfilesApi - axios parameter creator
 * @export
 */
export const ProfilesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Change user password by password reset.
         * @param {string} passwordResetId 
         * @param {PasswordChangeRequest} passwordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        passwordResetSubmit: async (passwordResetId: string, passwordChangeRequest: PasswordChangeRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordResetId' is not null or undefined
            assertParamExists('passwordResetSubmit', 'passwordResetId', passwordResetId)
            // verify required parameter 'passwordChangeRequest' is not null or undefined
            assertParamExists('passwordResetSubmit', 'passwordChangeRequest', passwordChangeRequest)
            const localVarPath = `/profiles/password_resets/{password_reset_id}/`
                .replace(`{${"password_reset_id"}}`, encodeURIComponent(String(passwordResetId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(passwordChangeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {PageVisitRequest} pageVisitRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesAnalyticsPageVisitsCreate: async (pageVisitRequest: PageVisitRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'pageVisitRequest' is not null or undefined
            assertParamExists('profilesAnalyticsPageVisitsCreate', 'pageVisitRequest', pageVisitRequest)
            const localVarPath = `/profiles/analytics/page_visits/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(pageVisitRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create A New Profile.
         * @param {ProfileCreateRequest} profileCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesCreate: async (profileCreateRequest: ProfileCreateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileCreateRequest' is not null or undefined
            assertParamExists('profilesCreate', 'profileCreateRequest', profileCreateRequest)
            const localVarPath = `/profiles/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileCreateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Check Email Verification.
         * @param {string} emailVerificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesEmailVerificationCheck: async (emailVerificationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'emailVerificationId' is not null or undefined
            assertParamExists('profilesEmailVerificationCheck', 'emailVerificationId', emailVerificationId)
            const localVarPath = `/profiles/email_verifications/{email_verification_id}/`
                .replace(`{${"email_verification_id"}}`, encodeURIComponent(String(emailVerificationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create new Email Verification Request.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesEmailVerificationsCreate: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/email_verifications/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create A New Invitation.
         * @param {InvitationRequest} invitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsCreate: async (invitationRequest: InvitationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationRequest' is not null or undefined
            assertParamExists('profilesInvitationsCreate', 'invitationRequest', invitationRequest)
            const localVarPath = `/profiles/invitations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(invitationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete an invitation.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsDestroy: async (invitationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('profilesInvitationsDestroy', 'invitationId', invitationId)
            const localVarPath = `/profiles/invitations/{invitation_id}/`
                .replace(`{${"invitation_id"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Invitations.
         * @param {boolean} [includeExpired] Also return invitations that have already expired.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profiles based on (part of) email address.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsList: async (includeExpired?: boolean, nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/invitations/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (includeExpired !== undefined) {
                localVarQueryParameter['include_expired'] = includeExpired;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a user based on send invitation.
         * @param {string} invitationId 
         * @param {UserRegistrationRequest} userRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsRegister: async (invitationId: string, userRegistrationRequest: UserRegistrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('profilesInvitationsRegister', 'invitationId', invitationId)
            // verify required parameter 'userRegistrationRequest' is not null or undefined
            assertParamExists('profilesInvitationsRegister', 'userRegistrationRequest', userRegistrationRequest)
            const localVarPath = `/profiles/invitations/{invitation_id}/`
                .replace(`{${"invitation_id"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userRegistrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Re-send an invitation and refresh its expiry date.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsResendCreate: async (invitationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('profilesInvitationsResendCreate', 'invitationId', invitationId)
            const localVarPath = `/profiles/invitations/{invitation_id}/resend/`
                .replace(`{${"invitation_id"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get invitation details.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsRetrieve: async (invitationId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('profilesInvitationsRetrieve', 'invitationId', invitationId)
            const localVarPath = `/profiles/invitations/{invitation_id}/`
                .replace(`{${"invitation_id"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Profiles.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profiles based on (part of) name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesList: async (nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {PatchedProfileRequest} [patchedProfileRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPartialUpdate: async (profileId: string, patchedProfileRequest?: PatchedProfileRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileId' is not null or undefined
            assertParamExists('profilesPartialUpdate', 'profileId', profileId)
            const localVarPath = `/profiles/{profile_id}/`
                .replace(`{${"profile_id"}}`, encodeURIComponent(String(profileId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedProfileRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create Password Reset [Unauthenticated].
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPasswordResetsCreate: async (passwordResetRequest: PasswordResetRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordResetRequest' is not null or undefined
            assertParamExists('profilesPasswordResetsCreate', 'passwordResetRequest', passwordResetRequest)
            const localVarPath = `/profiles/password_resets/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(passwordResetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get password reset details.
         * @param {string} passwordResetId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPasswordResetsRetrieve: async (passwordResetId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordResetId' is not null or undefined
            assertParamExists('profilesPasswordResetsRetrieve', 'passwordResetId', passwordResetId)
            const localVarPath = `/profiles/password_resets/{password_reset_id}/`
                .replace(`{${"password_reset_id"}}`, encodeURIComponent(String(passwordResetId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Check Phone Verification.
         * @param {PhoneVerificationCheckRequest} phoneVerificationCheckRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPhoneVerificationCheck: async (phoneVerificationCheckRequest: PhoneVerificationCheckRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'phoneVerificationCheckRequest' is not null or undefined
            assertParamExists('profilesPhoneVerificationCheck', 'phoneVerificationCheckRequest', phoneVerificationCheckRequest)
            const localVarPath = `/profiles/phone_verifications/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(phoneVerificationCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create Profile User Groups.
         * @param {ProfileUserGroupRequest} profileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsCreate: async (profileUserGroupRequest: ProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupRequest' is not null or undefined
            assertParamExists('profilesProfileUserGroupsCreate', 'profileUserGroupRequest', profileUserGroupRequest)
            const localVarPath = `/profiles/profile_user_groups/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsDestroy: async (profileUserGroupId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsDestroy', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Profile User Groups.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profile user groups based on (part of) the name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsList: async (nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/profile_user_groups/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {PatchedProfileUserGroupRequest} [patchedProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsPartialUpdate: async (profileUserGroupId: string, patchedProfileUserGroupRequest?: PatchedProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsPartialUpdate', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedProfileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsRetrieve: async (profileUserGroupId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsRetrieve', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {ProfileUserGroupRequest} profileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUpdate: async (profileUserGroupId: string, profileUserGroupRequest: ProfileUserGroupRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsUpdate', 'profileUserGroupId', profileUserGroupId)
            // verify required parameter 'profileUserGroupRequest' is not null or undefined
            assertParamExists('profilesProfileUserGroupsUpdate', 'profileUserGroupRequest', profileUserGroupRequest)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileUserGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List and create users in Profile User Group.
         * @param {string} profileUserGroupId 
         * @param {ProfileUserGroupUserRequest} [profileUserGroupUserRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUsersCreate: async (profileUserGroupId: string, profileUserGroupUserRequest?: ProfileUserGroupUserRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsUsersCreate', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/users/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileUserGroupUserRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove user from Profile User Group.
         * @param {string} profileUserGroupId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUsersDestroy: async (profileUserGroupId: string, userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsUsersDestroy', 'profileUserGroupId', profileUserGroupId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsUsersDestroy', 'userId', userId)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/users/{user_id}/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List Profile User Group Users.
         * @param {string} profileUserGroupId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter group members based on (part of) first name, last name or email address.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUsersList: async (profileUserGroupId: string, nextKey?: string, query?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileUserGroupId' is not null or undefined
            assertParamExists('profilesProfileUserGroupsUsersList', 'profileUserGroupId', profileUserGroupId)
            const localVarPath = `/profiles/profile_user_groups/{profile_user_group_id}/users/`
                .replace(`{${"profile_user_group_id"}}`, encodeURIComponent(String(profileUserGroupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesRetrieve: async (profileId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileId' is not null or undefined
            assertParamExists('profilesRetrieve', 'profileId', profileId)
            const localVarPath = `/profiles/{profile_id}/`
                .replace(`{${"profile_id"}}`, encodeURIComponent(String(profileId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {ProfileRequest} profileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUpdate: async (profileId: string, profileRequest: ProfileRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'profileId' is not null or undefined
            assertParamExists('profilesUpdate', 'profileId', profileId)
            // verify required parameter 'profileRequest' is not null or undefined
            assertParamExists('profilesUpdate', 'profileRequest', profileRequest)
            const localVarPath = `/profiles/{profile_id}/`
                .replace(`{${"profile_id"}}`, encodeURIComponent(String(profileId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(profileRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and/or Update user details.
         * @param {PatchedUserUpdateRequest} [patchedUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsPartialUpdate: async (patchedUserUpdateRequest?: PatchedUserUpdateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/user_details/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedUserUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get User Information.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsRetrieve: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/user_details/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get and/or Update user details.
         * @param {UserUpdateRequest} userUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsUpdate: async (userUpdateRequest: UserUpdateRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userUpdateRequest' is not null or undefined
            assertParamExists('profilesUserDetailsUpdate', 'userUpdateRequest', userUpdateRequest)
            const localVarPath = `/profiles/user_details/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Change User Password.
         * @param {UserPasswordChangeRequest} userPasswordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsUpdatePasswordUpdate: async (userPasswordChangeRequest: UserPasswordChangeRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userPasswordChangeRequest' is not null or undefined
            assertParamExists('profilesUserDetailsUpdatePasswordUpdate', 'userPasswordChangeRequest', userPasswordChangeRequest)
            const localVarPath = `/profiles/user_details/update_password/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userPasswordChangeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersDestroy: async (userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('profilesUsersDestroy', 'userId', userId)
            const localVarPath = `/profiles/users/{user_id}/`
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all Users.
         * @param {string} [customerReference] Filter users based on customer reference.
         * @param {boolean} [isAdmin] Filter users based on their profile admin flag.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter users based on (part of) username, first name, last name, email address or customer reference.
         * @param {'first_name' | 'is_admin' | 'last_login'} [sorting] Sort users. Without this parameter the existing default ordering is kept.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort users order, descending is from high to low and ascending from low to high.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersList: async (customerReference?: string, isAdmin?: boolean, nextKey?: string, query?: string, sorting?: 'first_name' | 'is_admin' | 'last_login', sortingOrder?: 'ascending' | 'descending', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/profiles/users/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (customerReference !== undefined) {
                localVarQueryParameter['customer_reference'] = customerReference;
            }

            if (isAdmin !== undefined) {
                localVarQueryParameter['is_admin'] = isAdmin;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }

            if (query !== undefined) {
                localVarQueryParameter['query'] = query;
            }

            if (sorting !== undefined) {
                localVarQueryParameter['sorting'] = sorting;
            }

            if (sortingOrder !== undefined) {
                localVarQueryParameter['sorting_order'] = sortingOrder;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {PatchedUserProfileDetailRequest} [patchedUserProfileDetailRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersPartialUpdate: async (userId: string, patchedUserProfileDetailRequest?: PatchedUserProfileDetailRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('profilesUsersPartialUpdate', 'userId', userId)
            const localVarPath = `/profiles/users/{user_id}/`
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(patchedUserProfileDetailRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * User Registration view.
         * @param {UserExternalRegistrationRequest} userExternalRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersRegisterCreate: async (userExternalRegistrationRequest: UserExternalRegistrationRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userExternalRegistrationRequest' is not null or undefined
            assertParamExists('profilesUsersRegisterCreate', 'userExternalRegistrationRequest', userExternalRegistrationRequest)
            const localVarPath = `/profiles/users/register/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userExternalRegistrationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersRetrieve: async (userId: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('profilesUsersRetrieve', 'userId', userId)
            const localVarPath = `/profiles/users/{user_id}/`
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {UserProfileDetailRequest} [userProfileDetailRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersUpdate: async (userId: string, userProfileDetailRequest?: UserProfileDetailRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('profilesUsersUpdate', 'userId', userId)
            const localVarPath = `/profiles/users/{user_id}/`
                .replace(`{${"user_id"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userProfileDetailRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ProfilesApi - functional programming interface
 * @export
 */
export const ProfilesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ProfilesApiAxiosParamCreator(configuration)
    return {
        /**
         * Change user password by password reset.
         * @param {string} passwordResetId 
         * @param {PasswordChangeRequest} passwordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async passwordResetSubmit(passwordResetId: string, passwordChangeRequest: PasswordChangeRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TokenRefresh>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.passwordResetSubmit(passwordResetId, passwordChangeRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * 
         * @param {PageVisitRequest} pageVisitRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesAnalyticsPageVisitsCreate(pageVisitRequest: PageVisitRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PageVisit>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesAnalyticsPageVisitsCreate(pageVisitRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create A New Profile.
         * @param {ProfileCreateRequest} profileCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesCreate(profileCreateRequest: ProfileCreateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Profile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesCreate(profileCreateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Check Email Verification.
         * @param {string} emailVerificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesEmailVerificationCheck(emailVerificationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesEmailVerificationCheck(emailVerificationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create new Email Verification Request.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesEmailVerificationsCreate(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesEmailVerificationsCreate(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create A New Invitation.
         * @param {InvitationRequest} invitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesInvitationsCreate(invitationRequest: InvitationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Invitation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesInvitationsCreate(invitationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Delete an invitation.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesInvitationsDestroy(invitationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesInvitationsDestroy(invitationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Invitations.
         * @param {boolean} [includeExpired] Also return invitations that have already expired.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profiles based on (part of) email address.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesInvitationsList(includeExpired?: boolean, nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedInvitationList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesInvitationsList(includeExpired, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create a user based on send invitation.
         * @param {string} invitationId 
         * @param {UserRegistrationRequest} userRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesInvitationsRegister(invitationId: string, userRegistrationRequest: UserRegistrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserRegistration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesInvitationsRegister(invitationId, userRegistrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Re-send an invitation and refresh its expiry date.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesInvitationsResendCreate(invitationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Invitation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesInvitationsResendCreate(invitationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get invitation details.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesInvitationsRetrieve(invitationId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Invitation>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesInvitationsRetrieve(invitationId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Profiles.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profiles based on (part of) name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesList(nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedProfileList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesList(nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {PatchedProfileRequest} [patchedProfileRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesPartialUpdate(profileId: string, patchedProfileRequest?: PatchedProfileRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Profile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesPartialUpdate(profileId, patchedProfileRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Create Password Reset [Unauthenticated].
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesPasswordResetsCreate(passwordResetRequest: PasswordResetRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PasswordReset>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesPasswordResetsCreate(passwordResetRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get password reset details.
         * @param {string} passwordResetId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesPasswordResetsRetrieve(passwordResetId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PasswordReset>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesPasswordResetsRetrieve(passwordResetId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Check Phone Verification.
         * @param {PhoneVerificationCheckRequest} phoneVerificationCheckRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesPhoneVerificationCheck(phoneVerificationCheckRequest: PhoneVerificationCheckRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PhoneVerificationCheck>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesPhoneVerificationCheck(phoneVerificationCheckRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create Profile User Groups.
         * @param {ProfileUserGroupRequest} profileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsCreate(profileUserGroupRequest: ProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsCreate(profileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsDestroy(profileUserGroupId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsDestroy(profileUserGroupId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Profile User Groups.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profile user groups based on (part of) the name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsList(nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedProfileUserGroupList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsList(nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {PatchedProfileUserGroupRequest} [patchedProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsPartialUpdate(profileUserGroupId: string, patchedProfileUserGroupRequest?: PatchedProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsPartialUpdate(profileUserGroupId, patchedProfileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsRetrieve(profileUserGroupId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsRetrieve(profileUserGroupId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {ProfileUserGroupRequest} profileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsUpdate(profileUserGroupId: string, profileUserGroupRequest: ProfileUserGroupRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileUserGroup>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsUpdate(profileUserGroupId, profileUserGroupRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List and create users in Profile User Group.
         * @param {string} profileUserGroupId 
         * @param {ProfileUserGroupUserRequest} [profileUserGroupUserRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsUsersCreate(profileUserGroupId: string, profileUserGroupUserRequest?: ProfileUserGroupUserRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ProfileUserGroupUser>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsUsersCreate(profileUserGroupId, profileUserGroupUserRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Remove user from Profile User Group.
         * @param {string} profileUserGroupId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsUsersDestroy(profileUserGroupId: string, userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsUsersDestroy(profileUserGroupId, userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * List Profile User Group Users.
         * @param {string} profileUserGroupId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter group members based on (part of) first name, last name or email address.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesProfileUserGroupsUsersList(profileUserGroupId: string, nextKey?: string, query?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedProfileUserGroupUserList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesProfileUserGroupsUsersList(profileUserGroupId, nextKey, query, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesRetrieve(profileId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Profile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesRetrieve(profileId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {ProfileRequest} profileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUpdate(profileId: string, profileRequest: ProfileRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Profile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUpdate(profileId, profileRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and/or Update user details.
         * @param {PatchedUserUpdateRequest} [patchedUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUserDetailsPartialUpdate(patchedUserUpdateRequest?: PatchedUserUpdateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUserDetailsPartialUpdate(patchedUserUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get User Information.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUserDetailsRetrieve(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUserDetailsRetrieve(options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get and/or Update user details.
         * @param {UserUpdateRequest} userUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUserDetailsUpdate(userUpdateRequest: UserUpdateRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUserDetailsUpdate(userUpdateRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Change User Password.
         * @param {UserPasswordChangeRequest} userPasswordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUserDetailsUpdatePasswordUpdate(userPasswordChangeRequest: UserPasswordChangeRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUserDetailsUpdatePasswordUpdate(userPasswordChangeRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUsersDestroy(userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUsersDestroy(userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get all Users.
         * @param {string} [customerReference] Filter users based on customer reference.
         * @param {boolean} [isAdmin] Filter users based on their profile admin flag.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter users based on (part of) username, first name, last name, email address or customer reference.
         * @param {'first_name' | 'is_admin' | 'last_login'} [sorting] Sort users. Without this parameter the existing default ordering is kept.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort users order, descending is from high to low and ascending from low to high.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUsersList(customerReference?: string, isAdmin?: boolean, nextKey?: string, query?: string, sorting?: 'first_name' | 'is_admin' | 'last_login', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedProfileUserListList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUsersList(customerReference, isAdmin, nextKey, query, sorting, sortingOrder, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {PatchedUserProfileDetailRequest} [patchedUserProfileDetailRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUsersPartialUpdate(userId: string, patchedUserProfileDetailRequest?: PatchedUserProfileDetailRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfileDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUsersPartialUpdate(userId, patchedUserProfileDetailRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * User Registration view.
         * @param {UserExternalRegistrationRequest} userExternalRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUsersRegisterCreate(userExternalRegistrationRequest: UserExternalRegistrationRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserExternalRegistration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUsersRegisterCreate(userExternalRegistrationRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUsersRetrieve(userId: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfileDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUsersRetrieve(userId, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {UserProfileDetailRequest} [userProfileDetailRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async profilesUsersUpdate(userId: string, userProfileDetailRequest?: UserProfileDetailRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfileDetail>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.profilesUsersUpdate(userId, userProfileDetailRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * ProfilesApi - factory interface
 * @export
 */
export const ProfilesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ProfilesApiFp(configuration)
    return {
        /**
         * Change user password by password reset.
         * @param {string} passwordResetId 
         * @param {PasswordChangeRequest} passwordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        passwordResetSubmit(passwordResetId: string, passwordChangeRequest: PasswordChangeRequest, options?: any): AxiosPromise<TokenRefresh> {
            return localVarFp.passwordResetSubmit(passwordResetId, passwordChangeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {PageVisitRequest} pageVisitRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesAnalyticsPageVisitsCreate(pageVisitRequest: PageVisitRequest, options?: any): AxiosPromise<PageVisit> {
            return localVarFp.profilesAnalyticsPageVisitsCreate(pageVisitRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create A New Profile.
         * @param {ProfileCreateRequest} profileCreateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesCreate(profileCreateRequest: ProfileCreateRequest, options?: any): AxiosPromise<Profile> {
            return localVarFp.profilesCreate(profileCreateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Check Email Verification.
         * @param {string} emailVerificationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesEmailVerificationCheck(emailVerificationId: string, options?: any): AxiosPromise<void> {
            return localVarFp.profilesEmailVerificationCheck(emailVerificationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create new Email Verification Request.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesEmailVerificationsCreate(options?: any): AxiosPromise<void> {
            return localVarFp.profilesEmailVerificationsCreate(options).then((request) => request(axios, basePath));
        },
        /**
         * Create A New Invitation.
         * @param {InvitationRequest} invitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsCreate(invitationRequest: InvitationRequest, options?: any): AxiosPromise<Invitation> {
            return localVarFp.profilesInvitationsCreate(invitationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete an invitation.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsDestroy(invitationId: string, options?: any): AxiosPromise<void> {
            return localVarFp.profilesInvitationsDestroy(invitationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Invitations.
         * @param {boolean} [includeExpired] Also return invitations that have already expired.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profiles based on (part of) email address.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsList(includeExpired?: boolean, nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedInvitationList> {
            return localVarFp.profilesInvitationsList(includeExpired, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a user based on send invitation.
         * @param {string} invitationId 
         * @param {UserRegistrationRequest} userRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsRegister(invitationId: string, userRegistrationRequest: UserRegistrationRequest, options?: any): AxiosPromise<UserRegistration> {
            return localVarFp.profilesInvitationsRegister(invitationId, userRegistrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Re-send an invitation and refresh its expiry date.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsResendCreate(invitationId: string, options?: any): AxiosPromise<Invitation> {
            return localVarFp.profilesInvitationsResendCreate(invitationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get invitation details.
         * @param {string} invitationId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesInvitationsRetrieve(invitationId: string, options?: any): AxiosPromise<Invitation> {
            return localVarFp.profilesInvitationsRetrieve(invitationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Profiles.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profiles based on (part of) name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesList(nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedProfileList> {
            return localVarFp.profilesList(nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {PatchedProfileRequest} [patchedProfileRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPartialUpdate(profileId: string, patchedProfileRequest?: PatchedProfileRequest, options?: any): AxiosPromise<Profile> {
            return localVarFp.profilesPartialUpdate(profileId, patchedProfileRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Create Password Reset [Unauthenticated].
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPasswordResetsCreate(passwordResetRequest: PasswordResetRequest, options?: any): AxiosPromise<PasswordReset> {
            return localVarFp.profilesPasswordResetsCreate(passwordResetRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get password reset details.
         * @param {string} passwordResetId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPasswordResetsRetrieve(passwordResetId: string, options?: any): AxiosPromise<PasswordReset> {
            return localVarFp.profilesPasswordResetsRetrieve(passwordResetId, options).then((request) => request(axios, basePath));
        },
        /**
         * Check Phone Verification.
         * @param {PhoneVerificationCheckRequest} phoneVerificationCheckRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesPhoneVerificationCheck(phoneVerificationCheckRequest: PhoneVerificationCheckRequest, options?: any): AxiosPromise<PhoneVerificationCheck> {
            return localVarFp.profilesPhoneVerificationCheck(phoneVerificationCheckRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create Profile User Groups.
         * @param {ProfileUserGroupRequest} profileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsCreate(profileUserGroupRequest: ProfileUserGroupRequest, options?: any): AxiosPromise<ProfileUserGroup> {
            return localVarFp.profilesProfileUserGroupsCreate(profileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsDestroy(profileUserGroupId: string, options?: any): AxiosPromise<void> {
            return localVarFp.profilesProfileUserGroupsDestroy(profileUserGroupId, options).then((request) => request(axios, basePath));
        },
        /**
         * List Profile User Groups.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter profile user groups based on (part of) the name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsList(nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedProfileUserGroupList> {
            return localVarFp.profilesProfileUserGroupsList(nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {PatchedProfileUserGroupRequest} [patchedProfileUserGroupRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsPartialUpdate(profileUserGroupId: string, patchedProfileUserGroupRequest?: PatchedProfileUserGroupRequest, options?: any): AxiosPromise<ProfileUserGroup> {
            return localVarFp.profilesProfileUserGroupsPartialUpdate(profileUserGroupId, patchedProfileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsRetrieve(profileUserGroupId: string, options?: any): AxiosPromise<ProfileUserGroup> {
            return localVarFp.profilesProfileUserGroupsRetrieve(profileUserGroupId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, update and delete Profile User Groups.
         * @param {string} profileUserGroupId 
         * @param {ProfileUserGroupRequest} profileUserGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUpdate(profileUserGroupId: string, profileUserGroupRequest: ProfileUserGroupRequest, options?: any): AxiosPromise<ProfileUserGroup> {
            return localVarFp.profilesProfileUserGroupsUpdate(profileUserGroupId, profileUserGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * List and create users in Profile User Group.
         * @param {string} profileUserGroupId 
         * @param {ProfileUserGroupUserRequest} [profileUserGroupUserRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUsersCreate(profileUserGroupId: string, profileUserGroupUserRequest?: ProfileUserGroupUserRequest, options?: any): AxiosPromise<ProfileUserGroupUser> {
            return localVarFp.profilesProfileUserGroupsUsersCreate(profileUserGroupId, profileUserGroupUserRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove user from Profile User Group.
         * @param {string} profileUserGroupId 
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUsersDestroy(profileUserGroupId: string, userId: string, options?: any): AxiosPromise<void> {
            return localVarFp.profilesProfileUserGroupsUsersDestroy(profileUserGroupId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * List Profile User Group Users.
         * @param {string} profileUserGroupId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter group members based on (part of) first name, last name or email address.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesProfileUserGroupsUsersList(profileUserGroupId: string, nextKey?: string, query?: string, options?: any): AxiosPromise<PaginatedProfileUserGroupUserList> {
            return localVarFp.profilesProfileUserGroupsUsersList(profileUserGroupId, nextKey, query, options).then((request) => request(axios, basePath));
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesRetrieve(profileId: string, options?: any): AxiosPromise<Profile> {
            return localVarFp.profilesRetrieve(profileId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get and/or Update Profile details.
         * @param {string} profileId 
         * @param {ProfileRequest} profileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUpdate(profileId: string, profileRequest: ProfileRequest, options?: any): AxiosPromise<Profile> {
            return localVarFp.profilesUpdate(profileId, profileRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get and/or Update user details.
         * @param {PatchedUserUpdateRequest} [patchedUserUpdateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsPartialUpdate(patchedUserUpdateRequest?: PatchedUserUpdateRequest, options?: any): AxiosPromise<UserDetail> {
            return localVarFp.profilesUserDetailsPartialUpdate(patchedUserUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get User Information.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsRetrieve(options?: any): AxiosPromise<UserDetail> {
            return localVarFp.profilesUserDetailsRetrieve(options).then((request) => request(axios, basePath));
        },
        /**
         * Get and/or Update user details.
         * @param {UserUpdateRequest} userUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsUpdate(userUpdateRequest: UserUpdateRequest, options?: any): AxiosPromise<UserDetail> {
            return localVarFp.profilesUserDetailsUpdate(userUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Change User Password.
         * @param {UserPasswordChangeRequest} userPasswordChangeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUserDetailsUpdatePasswordUpdate(userPasswordChangeRequest: UserPasswordChangeRequest, options?: any): AxiosPromise<UserDetail> {
            return localVarFp.profilesUserDetailsUpdatePasswordUpdate(userPasswordChangeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersDestroy(userId: string, options?: any): AxiosPromise<void> {
            return localVarFp.profilesUsersDestroy(userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all Users.
         * @param {string} [customerReference] Filter users based on customer reference.
         * @param {boolean} [isAdmin] Filter users based on their profile admin flag.
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {string} [query] Filter users based on (part of) username, first name, last name, email address or customer reference.
         * @param {'first_name' | 'is_admin' | 'last_login'} [sorting] Sort users. Without this parameter the existing default ordering is kept.
         * @param {'ascending' | 'descending'} [sortingOrder] Sort users order, descending is from high to low and ascending from low to high.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersList(customerReference?: string, isAdmin?: boolean, nextKey?: string, query?: string, sorting?: 'first_name' | 'is_admin' | 'last_login', sortingOrder?: 'ascending' | 'descending', options?: any): AxiosPromise<PaginatedProfileUserListList> {
            return localVarFp.profilesUsersList(customerReference, isAdmin, nextKey, query, sorting, sortingOrder, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {PatchedUserProfileDetailRequest} [patchedUserProfileDetailRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersPartialUpdate(userId: string, patchedUserProfileDetailRequest?: PatchedUserProfileDetailRequest, options?: any): AxiosPromise<UserProfileDetail> {
            return localVarFp.profilesUsersPartialUpdate(userId, patchedUserProfileDetailRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * User Registration view.
         * @param {UserExternalRegistrationRequest} userExternalRegistrationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersRegisterCreate(userExternalRegistrationRequest: UserExternalRegistrationRequest, options?: any): AxiosPromise<UserExternalRegistration> {
            return localVarFp.profilesUsersRegisterCreate(userExternalRegistrationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersRetrieve(userId: string, options?: any): AxiosPromise<UserProfileDetail> {
            return localVarFp.profilesUsersRetrieve(userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Get, Update and delete User.
         * @param {string} userId 
         * @param {UserProfileDetailRequest} [userProfileDetailRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        profilesUsersUpdate(userId: string, userProfileDetailRequest?: UserProfileDetailRequest, options?: any): AxiosPromise<UserProfileDetail> {
            return localVarFp.profilesUsersUpdate(userId, userProfileDetailRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ProfilesApi - interface
 * @export
 * @interface ProfilesApi
 */
export interface ProfilesApiInterface {
    /**
     * Change user password by password reset.
     * @param {string} passwordResetId 
     * @param {PasswordChangeRequest} passwordChangeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    passwordResetSubmit(passwordResetId: string, passwordChangeRequest: PasswordChangeRequest, options?: AxiosRequestConfig): AxiosPromise<TokenRefresh>;

    /**
     * 
     * @param {PageVisitRequest} pageVisitRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesAnalyticsPageVisitsCreate(pageVisitRequest: PageVisitRequest, options?: AxiosRequestConfig): AxiosPromise<PageVisit>;

    /**
     * Create A New Profile.
     * @param {ProfileCreateRequest} profileCreateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesCreate(profileCreateRequest: ProfileCreateRequest, options?: AxiosRequestConfig): AxiosPromise<Profile>;

    /**
     * Check Email Verification.
     * @param {string} emailVerificationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesEmailVerificationCheck(emailVerificationId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Create new Email Verification Request.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesEmailVerificationsCreate(options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Create A New Invitation.
     * @param {InvitationRequest} invitationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesInvitationsCreate(invitationRequest: InvitationRequest, options?: AxiosRequestConfig): AxiosPromise<Invitation>;

    /**
     * Delete an invitation.
     * @param {string} invitationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesInvitationsDestroy(invitationId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get all Invitations.
     * @param {boolean} [includeExpired] Also return invitations that have already expired.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter profiles based on (part of) email address.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesInvitationsList(includeExpired?: boolean, nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedInvitationList>;

    /**
     * Create a user based on send invitation.
     * @param {string} invitationId 
     * @param {UserRegistrationRequest} userRegistrationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesInvitationsRegister(invitationId: string, userRegistrationRequest: UserRegistrationRequest, options?: AxiosRequestConfig): AxiosPromise<UserRegistration>;

    /**
     * Re-send an invitation and refresh its expiry date.
     * @param {string} invitationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesInvitationsResendCreate(invitationId: string, options?: AxiosRequestConfig): AxiosPromise<Invitation>;

    /**
     * Get invitation details.
     * @param {string} invitationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesInvitationsRetrieve(invitationId: string, options?: AxiosRequestConfig): AxiosPromise<Invitation>;

    /**
     * Get all Profiles.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter profiles based on (part of) name.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesList(nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedProfileList>;

    /**
     * Get and/or Update Profile details.
     * @param {string} profileId 
     * @param {PatchedProfileRequest} [patchedProfileRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesPartialUpdate(profileId: string, patchedProfileRequest?: PatchedProfileRequest, options?: AxiosRequestConfig): AxiosPromise<Profile>;

    /**
     * Create Password Reset [Unauthenticated].
     * @param {PasswordResetRequest} passwordResetRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesPasswordResetsCreate(passwordResetRequest: PasswordResetRequest, options?: AxiosRequestConfig): AxiosPromise<PasswordReset>;

    /**
     * Get password reset details.
     * @param {string} passwordResetId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesPasswordResetsRetrieve(passwordResetId: string, options?: AxiosRequestConfig): AxiosPromise<PasswordReset>;

    /**
     * Check Phone Verification.
     * @param {PhoneVerificationCheckRequest} phoneVerificationCheckRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesPhoneVerificationCheck(phoneVerificationCheckRequest: PhoneVerificationCheckRequest, options?: AxiosRequestConfig): AxiosPromise<PhoneVerificationCheck>;

    /**
     * List and create Profile User Groups.
     * @param {ProfileUserGroupRequest} profileUserGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsCreate(profileUserGroupRequest: ProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileUserGroup>;

    /**
     * Get, update and delete Profile User Groups.
     * @param {string} profileUserGroupId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsDestroy(profileUserGroupId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List Profile User Groups.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter profile user groups based on (part of) the name.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsList(nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedProfileUserGroupList>;

    /**
     * Get, update and delete Profile User Groups.
     * @param {string} profileUserGroupId 
     * @param {PatchedProfileUserGroupRequest} [patchedProfileUserGroupRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsPartialUpdate(profileUserGroupId: string, patchedProfileUserGroupRequest?: PatchedProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileUserGroup>;

    /**
     * Get, update and delete Profile User Groups.
     * @param {string} profileUserGroupId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsRetrieve(profileUserGroupId: string, options?: AxiosRequestConfig): AxiosPromise<ProfileUserGroup>;

    /**
     * Get, update and delete Profile User Groups.
     * @param {string} profileUserGroupId 
     * @param {ProfileUserGroupRequest} profileUserGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsUpdate(profileUserGroupId: string, profileUserGroupRequest: ProfileUserGroupRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileUserGroup>;

    /**
     * List and create users in Profile User Group.
     * @param {string} profileUserGroupId 
     * @param {ProfileUserGroupUserRequest} [profileUserGroupUserRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsUsersCreate(profileUserGroupId: string, profileUserGroupUserRequest?: ProfileUserGroupUserRequest, options?: AxiosRequestConfig): AxiosPromise<ProfileUserGroupUser>;

    /**
     * Remove user from Profile User Group.
     * @param {string} profileUserGroupId 
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsUsersDestroy(profileUserGroupId: string, userId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * List Profile User Group Users.
     * @param {string} profileUserGroupId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter group members based on (part of) first name, last name or email address.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesProfileUserGroupsUsersList(profileUserGroupId: string, nextKey?: string, query?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedProfileUserGroupUserList>;

    /**
     * Get and/or Update Profile details.
     * @param {string} profileId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesRetrieve(profileId: string, options?: AxiosRequestConfig): AxiosPromise<Profile>;

    /**
     * Get and/or Update Profile details.
     * @param {string} profileId 
     * @param {ProfileRequest} profileRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUpdate(profileId: string, profileRequest: ProfileRequest, options?: AxiosRequestConfig): AxiosPromise<Profile>;

    /**
     * Get and/or Update user details.
     * @param {PatchedUserUpdateRequest} [patchedUserUpdateRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUserDetailsPartialUpdate(patchedUserUpdateRequest?: PatchedUserUpdateRequest, options?: AxiosRequestConfig): AxiosPromise<UserDetail>;

    /**
     * Get User Information.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUserDetailsRetrieve(options?: AxiosRequestConfig): AxiosPromise<UserDetail>;

    /**
     * Get and/or Update user details.
     * @param {UserUpdateRequest} userUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUserDetailsUpdate(userUpdateRequest: UserUpdateRequest, options?: AxiosRequestConfig): AxiosPromise<UserDetail>;

    /**
     * Change User Password.
     * @param {UserPasswordChangeRequest} userPasswordChangeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUserDetailsUpdatePasswordUpdate(userPasswordChangeRequest: UserPasswordChangeRequest, options?: AxiosRequestConfig): AxiosPromise<UserDetail>;

    /**
     * Get, Update and delete User.
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUsersDestroy(userId: string, options?: AxiosRequestConfig): AxiosPromise<void>;

    /**
     * Get all Users.
     * @param {string} [customerReference] Filter users based on customer reference.
     * @param {boolean} [isAdmin] Filter users based on their profile admin flag.
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {string} [query] Filter users based on (part of) username, first name, last name, email address or customer reference.
     * @param {'first_name' | 'is_admin' | 'last_login'} [sorting] Sort users. Without this parameter the existing default ordering is kept.
     * @param {'ascending' | 'descending'} [sortingOrder] Sort users order, descending is from high to low and ascending from low to high.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUsersList(customerReference?: string, isAdmin?: boolean, nextKey?: string, query?: string, sorting?: 'first_name' | 'is_admin' | 'last_login', sortingOrder?: 'ascending' | 'descending', options?: AxiosRequestConfig): AxiosPromise<PaginatedProfileUserListList>;

    /**
     * Get, Update and delete User.
     * @param {string} userId 
     * @param {PatchedUserProfileDetailRequest} [patchedUserProfileDetailRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUsersPartialUpdate(userId: string, patchedUserProfileDetailRequest?: PatchedUserProfileDetailRequest, options?: AxiosRequestConfig): AxiosPromise<UserProfileDetail>;

    /**
     * User Registration view.
     * @param {UserExternalRegistrationRequest} userExternalRegistrationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUsersRegisterCreate(userExternalRegistrationRequest: UserExternalRegistrationRequest, options?: AxiosRequestConfig): AxiosPromise<UserExternalRegistration>;

    /**
     * Get, Update and delete User.
     * @param {string} userId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUsersRetrieve(userId: string, options?: AxiosRequestConfig): AxiosPromise<UserProfileDetail>;

    /**
     * Get, Update and delete User.
     * @param {string} userId 
     * @param {UserProfileDetailRequest} [userProfileDetailRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApiInterface
     */
    profilesUsersUpdate(userId: string, userProfileDetailRequest?: UserProfileDetailRequest, options?: AxiosRequestConfig): AxiosPromise<UserProfileDetail>;

}

/**
 * Request parameters for passwordResetSubmit operation in ProfilesApi.
 * @export
 * @interface ProfilesApiPasswordResetSubmitRequest
 */
export interface ProfilesApiPasswordResetSubmitRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiPasswordResetSubmit
     */
    readonly passwordResetId: string

    /**
     * 
     * @type {PasswordChangeRequest}
     * @memberof ProfilesApiPasswordResetSubmit
     */
    readonly passwordChangeRequest: PasswordChangeRequest
}

/**
 * Request parameters for profilesAnalyticsPageVisitsCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesAnalyticsPageVisitsCreateRequest
 */
export interface ProfilesApiProfilesAnalyticsPageVisitsCreateRequest {
    /**
     * 
     * @type {PageVisitRequest}
     * @memberof ProfilesApiProfilesAnalyticsPageVisitsCreate
     */
    readonly pageVisitRequest: PageVisitRequest
}

/**
 * Request parameters for profilesCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesCreateRequest
 */
export interface ProfilesApiProfilesCreateRequest {
    /**
     * 
     * @type {ProfileCreateRequest}
     * @memberof ProfilesApiProfilesCreate
     */
    readonly profileCreateRequest: ProfileCreateRequest
}

/**
 * Request parameters for profilesEmailVerificationCheck operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesEmailVerificationCheckRequest
 */
export interface ProfilesApiProfilesEmailVerificationCheckRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesEmailVerificationCheck
     */
    readonly emailVerificationId: string
}

/**
 * Request parameters for profilesInvitationsCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesInvitationsCreateRequest
 */
export interface ProfilesApiProfilesInvitationsCreateRequest {
    /**
     * 
     * @type {InvitationRequest}
     * @memberof ProfilesApiProfilesInvitationsCreate
     */
    readonly invitationRequest: InvitationRequest
}

/**
 * Request parameters for profilesInvitationsDestroy operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesInvitationsDestroyRequest
 */
export interface ProfilesApiProfilesInvitationsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesInvitationsDestroy
     */
    readonly invitationId: string
}

/**
 * Request parameters for profilesInvitationsList operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesInvitationsListRequest
 */
export interface ProfilesApiProfilesInvitationsListRequest {
    /**
     * Also return invitations that have already expired.
     * @type {boolean}
     * @memberof ProfilesApiProfilesInvitationsList
     */
    readonly includeExpired?: boolean

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ProfilesApiProfilesInvitationsList
     */
    readonly nextKey?: string

    /**
     * Filter profiles based on (part of) email address.
     * @type {string}
     * @memberof ProfilesApiProfilesInvitationsList
     */
    readonly query?: string
}

/**
 * Request parameters for profilesInvitationsRegister operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesInvitationsRegisterRequest
 */
export interface ProfilesApiProfilesInvitationsRegisterRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesInvitationsRegister
     */
    readonly invitationId: string

    /**
     * 
     * @type {UserRegistrationRequest}
     * @memberof ProfilesApiProfilesInvitationsRegister
     */
    readonly userRegistrationRequest: UserRegistrationRequest
}

/**
 * Request parameters for profilesInvitationsResendCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesInvitationsResendCreateRequest
 */
export interface ProfilesApiProfilesInvitationsResendCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesInvitationsResendCreate
     */
    readonly invitationId: string
}

/**
 * Request parameters for profilesInvitationsRetrieve operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesInvitationsRetrieveRequest
 */
export interface ProfilesApiProfilesInvitationsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesInvitationsRetrieve
     */
    readonly invitationId: string
}

/**
 * Request parameters for profilesList operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesListRequest
 */
export interface ProfilesApiProfilesListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ProfilesApiProfilesList
     */
    readonly nextKey?: string

    /**
     * Filter profiles based on (part of) name.
     * @type {string}
     * @memberof ProfilesApiProfilesList
     */
    readonly query?: string
}

/**
 * Request parameters for profilesPartialUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesPartialUpdateRequest
 */
export interface ProfilesApiProfilesPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesPartialUpdate
     */
    readonly profileId: string

    /**
     * 
     * @type {PatchedProfileRequest}
     * @memberof ProfilesApiProfilesPartialUpdate
     */
    readonly patchedProfileRequest?: PatchedProfileRequest
}

/**
 * Request parameters for profilesPasswordResetsCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesPasswordResetsCreateRequest
 */
export interface ProfilesApiProfilesPasswordResetsCreateRequest {
    /**
     * 
     * @type {PasswordResetRequest}
     * @memberof ProfilesApiProfilesPasswordResetsCreate
     */
    readonly passwordResetRequest: PasswordResetRequest
}

/**
 * Request parameters for profilesPasswordResetsRetrieve operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesPasswordResetsRetrieveRequest
 */
export interface ProfilesApiProfilesPasswordResetsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesPasswordResetsRetrieve
     */
    readonly passwordResetId: string
}

/**
 * Request parameters for profilesPhoneVerificationCheck operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesPhoneVerificationCheckRequest
 */
export interface ProfilesApiProfilesPhoneVerificationCheckRequest {
    /**
     * 
     * @type {PhoneVerificationCheckRequest}
     * @memberof ProfilesApiProfilesPhoneVerificationCheck
     */
    readonly phoneVerificationCheckRequest: PhoneVerificationCheckRequest
}

/**
 * Request parameters for profilesProfileUserGroupsCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsCreateRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsCreateRequest {
    /**
     * 
     * @type {ProfileUserGroupRequest}
     * @memberof ProfilesApiProfilesProfileUserGroupsCreate
     */
    readonly profileUserGroupRequest: ProfileUserGroupRequest
}

/**
 * Request parameters for profilesProfileUserGroupsDestroy operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsDestroyRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsDestroy
     */
    readonly profileUserGroupId: string
}

/**
 * Request parameters for profilesProfileUserGroupsList operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsListRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsListRequest {
    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsList
     */
    readonly nextKey?: string

    /**
     * Filter profile user groups based on (part of) the name.
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsList
     */
    readonly query?: string
}

/**
 * Request parameters for profilesProfileUserGroupsPartialUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsPartialUpdateRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsPartialUpdate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {PatchedProfileUserGroupRequest}
     * @memberof ProfilesApiProfilesProfileUserGroupsPartialUpdate
     */
    readonly patchedProfileUserGroupRequest?: PatchedProfileUserGroupRequest
}

/**
 * Request parameters for profilesProfileUserGroupsRetrieve operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsRetrieveRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsRetrieve
     */
    readonly profileUserGroupId: string
}

/**
 * Request parameters for profilesProfileUserGroupsUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsUpdateRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUpdate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {ProfileUserGroupRequest}
     * @memberof ProfilesApiProfilesProfileUserGroupsUpdate
     */
    readonly profileUserGroupRequest: ProfileUserGroupRequest
}

/**
 * Request parameters for profilesProfileUserGroupsUsersCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsUsersCreateRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsUsersCreateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersCreate
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {ProfileUserGroupUserRequest}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersCreate
     */
    readonly profileUserGroupUserRequest?: ProfileUserGroupUserRequest
}

/**
 * Request parameters for profilesProfileUserGroupsUsersDestroy operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsUsersDestroyRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsUsersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersDestroy
     */
    readonly profileUserGroupId: string

    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersDestroy
     */
    readonly userId: string
}

/**
 * Request parameters for profilesProfileUserGroupsUsersList operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesProfileUserGroupsUsersListRequest
 */
export interface ProfilesApiProfilesProfileUserGroupsUsersListRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersList
     */
    readonly profileUserGroupId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersList
     */
    readonly nextKey?: string

    /**
     * Filter group members based on (part of) first name, last name or email address.
     * @type {string}
     * @memberof ProfilesApiProfilesProfileUserGroupsUsersList
     */
    readonly query?: string
}

/**
 * Request parameters for profilesRetrieve operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesRetrieveRequest
 */
export interface ProfilesApiProfilesRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesRetrieve
     */
    readonly profileId: string
}

/**
 * Request parameters for profilesUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUpdateRequest
 */
export interface ProfilesApiProfilesUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesUpdate
     */
    readonly profileId: string

    /**
     * 
     * @type {ProfileRequest}
     * @memberof ProfilesApiProfilesUpdate
     */
    readonly profileRequest: ProfileRequest
}

/**
 * Request parameters for profilesUserDetailsPartialUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUserDetailsPartialUpdateRequest
 */
export interface ProfilesApiProfilesUserDetailsPartialUpdateRequest {
    /**
     * 
     * @type {PatchedUserUpdateRequest}
     * @memberof ProfilesApiProfilesUserDetailsPartialUpdate
     */
    readonly patchedUserUpdateRequest?: PatchedUserUpdateRequest
}

/**
 * Request parameters for profilesUserDetailsUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUserDetailsUpdateRequest
 */
export interface ProfilesApiProfilesUserDetailsUpdateRequest {
    /**
     * 
     * @type {UserUpdateRequest}
     * @memberof ProfilesApiProfilesUserDetailsUpdate
     */
    readonly userUpdateRequest: UserUpdateRequest
}

/**
 * Request parameters for profilesUserDetailsUpdatePasswordUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUserDetailsUpdatePasswordUpdateRequest
 */
export interface ProfilesApiProfilesUserDetailsUpdatePasswordUpdateRequest {
    /**
     * 
     * @type {UserPasswordChangeRequest}
     * @memberof ProfilesApiProfilesUserDetailsUpdatePasswordUpdate
     */
    readonly userPasswordChangeRequest: UserPasswordChangeRequest
}

/**
 * Request parameters for profilesUsersDestroy operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUsersDestroyRequest
 */
export interface ProfilesApiProfilesUsersDestroyRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesUsersDestroy
     */
    readonly userId: string
}

/**
 * Request parameters for profilesUsersList operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUsersListRequest
 */
export interface ProfilesApiProfilesUsersListRequest {
    /**
     * Filter users based on customer reference.
     * @type {string}
     * @memberof ProfilesApiProfilesUsersList
     */
    readonly customerReference?: string

    /**
     * Filter users based on their profile admin flag.
     * @type {boolean}
     * @memberof ProfilesApiProfilesUsersList
     */
    readonly isAdmin?: boolean

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ProfilesApiProfilesUsersList
     */
    readonly nextKey?: string

    /**
     * Filter users based on (part of) username, first name, last name, email address or customer reference.
     * @type {string}
     * @memberof ProfilesApiProfilesUsersList
     */
    readonly query?: string

    /**
     * Sort users. Without this parameter the existing default ordering is kept.
     * @type {'first_name' | 'is_admin' | 'last_login'}
     * @memberof ProfilesApiProfilesUsersList
     */
    readonly sorting?: 'first_name' | 'is_admin' | 'last_login'

    /**
     * Sort users order, descending is from high to low and ascending from low to high.
     * @type {'ascending' | 'descending'}
     * @memberof ProfilesApiProfilesUsersList
     */
    readonly sortingOrder?: 'ascending' | 'descending'
}

/**
 * Request parameters for profilesUsersPartialUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUsersPartialUpdateRequest
 */
export interface ProfilesApiProfilesUsersPartialUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesUsersPartialUpdate
     */
    readonly userId: string

    /**
     * 
     * @type {PatchedUserProfileDetailRequest}
     * @memberof ProfilesApiProfilesUsersPartialUpdate
     */
    readonly patchedUserProfileDetailRequest?: PatchedUserProfileDetailRequest
}

/**
 * Request parameters for profilesUsersRegisterCreate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUsersRegisterCreateRequest
 */
export interface ProfilesApiProfilesUsersRegisterCreateRequest {
    /**
     * 
     * @type {UserExternalRegistrationRequest}
     * @memberof ProfilesApiProfilesUsersRegisterCreate
     */
    readonly userExternalRegistrationRequest: UserExternalRegistrationRequest
}

/**
 * Request parameters for profilesUsersRetrieve operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUsersRetrieveRequest
 */
export interface ProfilesApiProfilesUsersRetrieveRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesUsersRetrieve
     */
    readonly userId: string
}

/**
 * Request parameters for profilesUsersUpdate operation in ProfilesApi.
 * @export
 * @interface ProfilesApiProfilesUsersUpdateRequest
 */
export interface ProfilesApiProfilesUsersUpdateRequest {
    /**
     * 
     * @type {string}
     * @memberof ProfilesApiProfilesUsersUpdate
     */
    readonly userId: string

    /**
     * 
     * @type {UserProfileDetailRequest}
     * @memberof ProfilesApiProfilesUsersUpdate
     */
    readonly userProfileDetailRequest?: UserProfileDetailRequest
}

/**
 * ProfilesApi - object-oriented interface
 * @export
 * @class ProfilesApi
 * @extends {BaseAPI}
 */
export class ProfilesApi extends BaseAPI implements ProfilesApiInterface {
    /**
     * Change user password by password reset.
     * @param {ProfilesApiPasswordResetSubmitRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public passwordResetSubmit(requestParameters: ProfilesApiPasswordResetSubmitRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).passwordResetSubmit(requestParameters.passwordResetId, requestParameters.passwordChangeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {ProfilesApiProfilesAnalyticsPageVisitsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesAnalyticsPageVisitsCreate(requestParameters: ProfilesApiProfilesAnalyticsPageVisitsCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesAnalyticsPageVisitsCreate(requestParameters.pageVisitRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create A New Profile.
     * @param {ProfilesApiProfilesCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesCreate(requestParameters: ProfilesApiProfilesCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesCreate(requestParameters.profileCreateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Check Email Verification.
     * @param {ProfilesApiProfilesEmailVerificationCheckRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesEmailVerificationCheck(requestParameters: ProfilesApiProfilesEmailVerificationCheckRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesEmailVerificationCheck(requestParameters.emailVerificationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create new Email Verification Request.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesEmailVerificationsCreate(options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesEmailVerificationsCreate(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create A New Invitation.
     * @param {ProfilesApiProfilesInvitationsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesInvitationsCreate(requestParameters: ProfilesApiProfilesInvitationsCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesInvitationsCreate(requestParameters.invitationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete an invitation.
     * @param {ProfilesApiProfilesInvitationsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesInvitationsDestroy(requestParameters: ProfilesApiProfilesInvitationsDestroyRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesInvitationsDestroy(requestParameters.invitationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Invitations.
     * @param {ProfilesApiProfilesInvitationsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesInvitationsList(requestParameters: ProfilesApiProfilesInvitationsListRequest = {}, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesInvitationsList(requestParameters.includeExpired, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a user based on send invitation.
     * @param {ProfilesApiProfilesInvitationsRegisterRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesInvitationsRegister(requestParameters: ProfilesApiProfilesInvitationsRegisterRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesInvitationsRegister(requestParameters.invitationId, requestParameters.userRegistrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Re-send an invitation and refresh its expiry date.
     * @param {ProfilesApiProfilesInvitationsResendCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesInvitationsResendCreate(requestParameters: ProfilesApiProfilesInvitationsResendCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesInvitationsResendCreate(requestParameters.invitationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get invitation details.
     * @param {ProfilesApiProfilesInvitationsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesInvitationsRetrieve(requestParameters: ProfilesApiProfilesInvitationsRetrieveRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesInvitationsRetrieve(requestParameters.invitationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Profiles.
     * @param {ProfilesApiProfilesListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesList(requestParameters: ProfilesApiProfilesListRequest = {}, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesList(requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and/or Update Profile details.
     * @param {ProfilesApiProfilesPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesPartialUpdate(requestParameters: ProfilesApiProfilesPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesPartialUpdate(requestParameters.profileId, requestParameters.patchedProfileRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create Password Reset [Unauthenticated].
     * @param {ProfilesApiProfilesPasswordResetsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesPasswordResetsCreate(requestParameters: ProfilesApiProfilesPasswordResetsCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesPasswordResetsCreate(requestParameters.passwordResetRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get password reset details.
     * @param {ProfilesApiProfilesPasswordResetsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesPasswordResetsRetrieve(requestParameters: ProfilesApiProfilesPasswordResetsRetrieveRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesPasswordResetsRetrieve(requestParameters.passwordResetId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Check Phone Verification.
     * @param {ProfilesApiProfilesPhoneVerificationCheckRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesPhoneVerificationCheck(requestParameters: ProfilesApiProfilesPhoneVerificationCheckRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesPhoneVerificationCheck(requestParameters.phoneVerificationCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create Profile User Groups.
     * @param {ProfilesApiProfilesProfileUserGroupsCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsCreate(requestParameters: ProfilesApiProfilesProfileUserGroupsCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsCreate(requestParameters.profileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, update and delete Profile User Groups.
     * @param {ProfilesApiProfilesProfileUserGroupsDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsDestroy(requestParameters: ProfilesApiProfilesProfileUserGroupsDestroyRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsDestroy(requestParameters.profileUserGroupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Profile User Groups.
     * @param {ProfilesApiProfilesProfileUserGroupsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsList(requestParameters: ProfilesApiProfilesProfileUserGroupsListRequest = {}, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsList(requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, update and delete Profile User Groups.
     * @param {ProfilesApiProfilesProfileUserGroupsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsPartialUpdate(requestParameters: ProfilesApiProfilesProfileUserGroupsPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsPartialUpdate(requestParameters.profileUserGroupId, requestParameters.patchedProfileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, update and delete Profile User Groups.
     * @param {ProfilesApiProfilesProfileUserGroupsRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsRetrieve(requestParameters: ProfilesApiProfilesProfileUserGroupsRetrieveRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsRetrieve(requestParameters.profileUserGroupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, update and delete Profile User Groups.
     * @param {ProfilesApiProfilesProfileUserGroupsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsUpdate(requestParameters: ProfilesApiProfilesProfileUserGroupsUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsUpdate(requestParameters.profileUserGroupId, requestParameters.profileUserGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List and create users in Profile User Group.
     * @param {ProfilesApiProfilesProfileUserGroupsUsersCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsUsersCreate(requestParameters: ProfilesApiProfilesProfileUserGroupsUsersCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsUsersCreate(requestParameters.profileUserGroupId, requestParameters.profileUserGroupUserRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove user from Profile User Group.
     * @param {ProfilesApiProfilesProfileUserGroupsUsersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsUsersDestroy(requestParameters: ProfilesApiProfilesProfileUserGroupsUsersDestroyRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsUsersDestroy(requestParameters.profileUserGroupId, requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List Profile User Group Users.
     * @param {ProfilesApiProfilesProfileUserGroupsUsersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesProfileUserGroupsUsersList(requestParameters: ProfilesApiProfilesProfileUserGroupsUsersListRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesProfileUserGroupsUsersList(requestParameters.profileUserGroupId, requestParameters.nextKey, requestParameters.query, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and/or Update Profile details.
     * @param {ProfilesApiProfilesRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesRetrieve(requestParameters: ProfilesApiProfilesRetrieveRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesRetrieve(requestParameters.profileId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and/or Update Profile details.
     * @param {ProfilesApiProfilesUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUpdate(requestParameters: ProfilesApiProfilesUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUpdate(requestParameters.profileId, requestParameters.profileRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and/or Update user details.
     * @param {ProfilesApiProfilesUserDetailsPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUserDetailsPartialUpdate(requestParameters: ProfilesApiProfilesUserDetailsPartialUpdateRequest = {}, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUserDetailsPartialUpdate(requestParameters.patchedUserUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get User Information.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUserDetailsRetrieve(options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUserDetailsRetrieve(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get and/or Update user details.
     * @param {ProfilesApiProfilesUserDetailsUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUserDetailsUpdate(requestParameters: ProfilesApiProfilesUserDetailsUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUserDetailsUpdate(requestParameters.userUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Change User Password.
     * @param {ProfilesApiProfilesUserDetailsUpdatePasswordUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUserDetailsUpdatePasswordUpdate(requestParameters: ProfilesApiProfilesUserDetailsUpdatePasswordUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUserDetailsUpdatePasswordUpdate(requestParameters.userPasswordChangeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, Update and delete User.
     * @param {ProfilesApiProfilesUsersDestroyRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUsersDestroy(requestParameters: ProfilesApiProfilesUsersDestroyRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUsersDestroy(requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all Users.
     * @param {ProfilesApiProfilesUsersListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUsersList(requestParameters: ProfilesApiProfilesUsersListRequest = {}, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUsersList(requestParameters.customerReference, requestParameters.isAdmin, requestParameters.nextKey, requestParameters.query, requestParameters.sorting, requestParameters.sortingOrder, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, Update and delete User.
     * @param {ProfilesApiProfilesUsersPartialUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUsersPartialUpdate(requestParameters: ProfilesApiProfilesUsersPartialUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUsersPartialUpdate(requestParameters.userId, requestParameters.patchedUserProfileDetailRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * User Registration view.
     * @param {ProfilesApiProfilesUsersRegisterCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUsersRegisterCreate(requestParameters: ProfilesApiProfilesUsersRegisterCreateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUsersRegisterCreate(requestParameters.userExternalRegistrationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, Update and delete User.
     * @param {ProfilesApiProfilesUsersRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUsersRetrieve(requestParameters: ProfilesApiProfilesUsersRetrieveRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUsersRetrieve(requestParameters.userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get, Update and delete User.
     * @param {ProfilesApiProfilesUsersUpdateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ProfilesApi
     */
    public profilesUsersUpdate(requestParameters: ProfilesApiProfilesUsersUpdateRequest, options?: AxiosRequestConfig) {
        return ProfilesApiFp(this.configuration).profilesUsersUpdate(requestParameters.userId, requestParameters.userProfileDetailRequest, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * SchemaApi - axios parameter creator
 * @export
 */
export const SchemaApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * OpenApi3 schema for this API. Format can be selected via content negotiation.  - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
         * @param {'json' | 'yaml'} [format] 
         * @param {'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh'} [lang] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        schemaRetrieve: async (format?: 'json' | 'yaml', lang?: 'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh', options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/schema/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            if (lang !== undefined) {
                localVarQueryParameter['lang'] = lang;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SchemaApi - functional programming interface
 * @export
 */
export const SchemaApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SchemaApiAxiosParamCreator(configuration)
    return {
        /**
         * OpenApi3 schema for this API. Format can be selected via content negotiation.  - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
         * @param {'json' | 'yaml'} [format] 
         * @param {'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh'} [lang] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async schemaRetrieve(format?: 'json' | 'yaml', lang?: 'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh', options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<{ [key: string]: any; }>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.schemaRetrieve(format, lang, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * SchemaApi - factory interface
 * @export
 */
export const SchemaApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SchemaApiFp(configuration)
    return {
        /**
         * OpenApi3 schema for this API. Format can be selected via content negotiation.  - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
         * @param {'json' | 'yaml'} [format] 
         * @param {'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh'} [lang] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        schemaRetrieve(format?: 'json' | 'yaml', lang?: 'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh', options?: any): AxiosPromise<{ [key: string]: any; }> {
            return localVarFp.schemaRetrieve(format, lang, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SchemaApi - interface
 * @export
 * @interface SchemaApi
 */
export interface SchemaApiInterface {
    /**
     * OpenApi3 schema for this API. Format can be selected via content negotiation.  - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
     * @param {'json' | 'yaml'} [format] 
     * @param {'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh'} [lang] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SchemaApiInterface
     */
    schemaRetrieve(format?: 'json' | 'yaml', lang?: 'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh', options?: AxiosRequestConfig): AxiosPromise<{ [key: string]: any; }>;

}

/**
 * Request parameters for schemaRetrieve operation in SchemaApi.
 * @export
 * @interface SchemaApiSchemaRetrieveRequest
 */
export interface SchemaApiSchemaRetrieveRequest {
    /**
     * 
     * @type {'json' | 'yaml'}
     * @memberof SchemaApiSchemaRetrieve
     */
    readonly format?: 'json' | 'yaml'

    /**
     * 
     * @type {'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh'}
     * @memberof SchemaApiSchemaRetrieve
     */
    readonly lang?: 'af' | 'ar' | 'az' | 'be' | 'bg' | 'bn' | 'br' | 'bs' | 'ca' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fa' | 'fi' | 'fr' | 'fy' | 'ga' | 'gd' | 'gl' | 'he' | 'hi' | 'hr' | 'hu' | 'hy' | 'ia' | 'id' | 'ig' | 'io' | 'is' | 'it' | 'ja' | 'ka' | 'kk' | 'km' | 'kn' | 'ko' | 'ky' | 'lb' | 'lt' | 'lv' | 'mk' | 'ml' | 'mn' | 'mr' | 'my' | 'ne' | 'nl' | 'no' | 'os' | 'pa' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'sw' | 'ta' | 'te' | 'tg' | 'th' | 'tk' | 'tr' | 'tt' | 'uk' | 'ur' | 'uz' | 'vi' | 'zh'
}

/**
 * SchemaApi - object-oriented interface
 * @export
 * @class SchemaApi
 * @extends {BaseAPI}
 */
export class SchemaApi extends BaseAPI implements SchemaApiInterface {
    /**
     * OpenApi3 schema for this API. Format can be selected via content negotiation.  - YAML: application/vnd.oai.openapi - JSON: application/vnd.oai.openapi+json
     * @param {SchemaApiSchemaRetrieveRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SchemaApi
     */
    public schemaRetrieve(requestParameters: SchemaApiSchemaRetrieveRequest = {}, options?: AxiosRequestConfig) {
        return SchemaApiFp(this.configuration).schemaRetrieve(requestParameters.format, requestParameters.lang, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * ScoringApi - axios parameter creator
 * @export
 */
export const ScoringApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Retrieve list all Scoring.
         * @param {string} companyExternalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        scoringList: async (companyExternalId: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyExternalId' is not null or undefined
            assertParamExists('scoringList', 'companyExternalId', companyExternalId)
            const localVarPath = `/scoring/{company_external_id}`
                .replace(`{${"company_external_id"}}`, encodeURIComponent(String(companyExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieve list all Scoring Signals.
         * @param {string} companyExternalId 
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        scoringSignalsList: async (companyExternalId: string, maxPublicationDate?: string, minPublicationDate?: string, nextKey?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'companyExternalId' is not null or undefined
            assertParamExists('scoringSignalsList', 'companyExternalId', companyExternalId)
            const localVarPath = `/scoring/{company_external_id}/signals`
                .replace(`{${"company_external_id"}}`, encodeURIComponent(String(companyExternalId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (maxPublicationDate !== undefined) {
                localVarQueryParameter['max_publication_date'] = (maxPublicationDate as any instanceof Date) ?
                    (maxPublicationDate as any).toISOString() :
                    maxPublicationDate;
            }

            if (minPublicationDate !== undefined) {
                localVarQueryParameter['min_publication_date'] = (minPublicationDate as any instanceof Date) ?
                    (minPublicationDate as any).toISOString() :
                    minPublicationDate;
            }

            if (nextKey !== undefined) {
                localVarQueryParameter['next_key'] = nextKey;
            }


    
            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ScoringApi - functional programming interface
 * @export
 */
export const ScoringApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ScoringApiAxiosParamCreator(configuration)
    return {
        /**
         * Retrieve list all Scoring.
         * @param {string} companyExternalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async scoringList(companyExternalId: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedCompanyScoringList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.scoringList(companyExternalId, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Retrieve list all Scoring Signals.
         * @param {string} companyExternalId 
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async scoringSignalsList(companyExternalId: string, maxPublicationDate?: string, minPublicationDate?: string, nextKey?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PaginatedCompanyScoringSignalList>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.scoringSignalsList(companyExternalId, maxPublicationDate, minPublicationDate, nextKey, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * ScoringApi - factory interface
 * @export
 */
export const ScoringApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ScoringApiFp(configuration)
    return {
        /**
         * Retrieve list all Scoring.
         * @param {string} companyExternalId 
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        scoringList(companyExternalId: string, nextKey?: string, options?: any): AxiosPromise<PaginatedCompanyScoringList> {
            return localVarFp.scoringList(companyExternalId, nextKey, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieve list all Scoring Signals.
         * @param {string} companyExternalId 
         * @param {string} [maxPublicationDate] Filter articles published before this date
         * @param {string} [minPublicationDate] Filter articles published after this date
         * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        scoringSignalsList(companyExternalId: string, maxPublicationDate?: string, minPublicationDate?: string, nextKey?: string, options?: any): AxiosPromise<PaginatedCompanyScoringSignalList> {
            return localVarFp.scoringSignalsList(companyExternalId, maxPublicationDate, minPublicationDate, nextKey, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ScoringApi - interface
 * @export
 * @interface ScoringApi
 */
export interface ScoringApiInterface {
    /**
     * Retrieve list all Scoring.
     * @param {string} companyExternalId 
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ScoringApiInterface
     */
    scoringList(companyExternalId: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedCompanyScoringList>;

    /**
     * Retrieve list all Scoring Signals.
     * @param {string} companyExternalId 
     * @param {string} [maxPublicationDate] Filter articles published before this date
     * @param {string} [minPublicationDate] Filter articles published after this date
     * @param {string} [nextKey] A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ScoringApiInterface
     */
    scoringSignalsList(companyExternalId: string, maxPublicationDate?: string, minPublicationDate?: string, nextKey?: string, options?: AxiosRequestConfig): AxiosPromise<PaginatedCompanyScoringSignalList>;

}

/**
 * Request parameters for scoringList operation in ScoringApi.
 * @export
 * @interface ScoringApiScoringListRequest
 */
export interface ScoringApiScoringListRequest {
    /**
     * 
     * @type {string}
     * @memberof ScoringApiScoringList
     */
    readonly companyExternalId: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ScoringApiScoringList
     */
    readonly nextKey?: string
}

/**
 * Request parameters for scoringSignalsList operation in ScoringApi.
 * @export
 * @interface ScoringApiScoringSignalsListRequest
 */
export interface ScoringApiScoringSignalsListRequest {
    /**
     * 
     * @type {string}
     * @memberof ScoringApiScoringSignalsList
     */
    readonly companyExternalId: string

    /**
     * Filter articles published before this date
     * @type {string}
     * @memberof ScoringApiScoringSignalsList
     */
    readonly maxPublicationDate?: string

    /**
     * Filter articles published after this date
     * @type {string}
     * @memberof ScoringApiScoringSignalsList
     */
    readonly minPublicationDate?: string

    /**
     * A cursor value used for pagination. Include the &#x60;next_key&#x60; value from your previous request to retrieve the subsequent page of results. If this value is &#x60;null&#x60;, the first page of results is returned.
     * @type {string}
     * @memberof ScoringApiScoringSignalsList
     */
    readonly nextKey?: string
}

/**
 * ScoringApi - object-oriented interface
 * @export
 * @class ScoringApi
 * @extends {BaseAPI}
 */
export class ScoringApi extends BaseAPI implements ScoringApiInterface {
    /**
     * Retrieve list all Scoring.
     * @param {ScoringApiScoringListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ScoringApi
     */
    public scoringList(requestParameters: ScoringApiScoringListRequest, options?: AxiosRequestConfig) {
        return ScoringApiFp(this.configuration).scoringList(requestParameters.companyExternalId, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieve list all Scoring Signals.
     * @param {ScoringApiScoringSignalsListRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ScoringApi
     */
    public scoringSignalsList(requestParameters: ScoringApiScoringSignalsListRequest, options?: AxiosRequestConfig) {
        return ScoringApiFp(this.configuration).scoringSignalsList(requestParameters.companyExternalId, requestParameters.maxPublicationDate, requestParameters.minPublicationDate, requestParameters.nextKey, options).then((request) => request(this.axios, this.basePath));
    }
}


/**
 * TokenApi - axios parameter creator
 * @export
 */
export const TokenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Takes a set of user credentials and returns an access and refresh JSON web token pair to prove the authentication of those credentials.
         * @param {TokenObtainPairRequest} tokenObtainPairRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tokenCreate: async (tokenObtainPairRequest: TokenObtainPairRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tokenObtainPairRequest' is not null or undefined
            assertParamExists('tokenCreate', 'tokenObtainPairRequest', tokenObtainPairRequest)
            const localVarPath = `/token/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(tokenObtainPairRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Takes a refresh type JSON web token and returns an access type JSON web token if the refresh token is valid.
         * @param {TokenRefreshRequest} tokenRefreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tokenRefreshCreate: async (tokenRefreshRequest: TokenRefreshRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tokenRefreshRequest' is not null or undefined
            assertParamExists('tokenRefreshCreate', 'tokenRefreshRequest', tokenRefreshRequest)
            const localVarPath = `/token/refresh/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(tokenRefreshRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Takes a token and indicates if it is valid.  This view provides no information about a token\'s fitness for a particular use.
         * @param {TokenVerifyRequest} tokenVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tokenVerifyCreate: async (tokenVerifyRequest: TokenVerifyRequest, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tokenVerifyRequest' is not null or undefined
            assertParamExists('tokenVerifyCreate', 'tokenVerifyRequest', tokenVerifyRequest)
            const localVarPath = `/token/verify/`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication tokenAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


    
            localVarHeaderParameter['Content-Type'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(tokenVerifyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TokenApi - functional programming interface
 * @export
 */
export const TokenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TokenApiAxiosParamCreator(configuration)
    return {
        /**
         * Takes a set of user credentials and returns an access and refresh JSON web token pair to prove the authentication of those credentials.
         * @param {TokenObtainPairRequest} tokenObtainPairRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async tokenCreate(tokenObtainPairRequest: TokenObtainPairRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TokenObtainPair>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.tokenCreate(tokenObtainPairRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Takes a refresh type JSON web token and returns an access type JSON web token if the refresh token is valid.
         * @param {TokenRefreshRequest} tokenRefreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async tokenRefreshCreate(tokenRefreshRequest: TokenRefreshRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TokenRefresh>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.tokenRefreshCreate(tokenRefreshRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
        /**
         * Takes a token and indicates if it is valid.  This view provides no information about a token\'s fitness for a particular use.
         * @param {TokenVerifyRequest} tokenVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async tokenVerifyCreate(tokenVerifyRequest: TokenVerifyRequest, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.tokenVerifyCreate(tokenVerifyRequest, options);
            return createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration);
        },
    }
};

/**
 * TokenApi - factory interface
 * @export
 */
export const TokenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TokenApiFp(configuration)
    return {
        /**
         * Takes a set of user credentials and returns an access and refresh JSON web token pair to prove the authentication of those credentials.
         * @param {TokenObtainPairRequest} tokenObtainPairRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tokenCreate(tokenObtainPairRequest: TokenObtainPairRequest, options?: any): AxiosPromise<TokenObtainPair> {
            return localVarFp.tokenCreate(tokenObtainPairRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Takes a refresh type JSON web token and returns an access type JSON web token if the refresh token is valid.
         * @param {TokenRefreshRequest} tokenRefreshRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tokenRefreshCreate(tokenRefreshRequest: TokenRefreshRequest, options?: any): AxiosPromise<TokenRefresh> {
            return localVarFp.tokenRefreshCreate(tokenRefreshRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Takes a token and indicates if it is valid.  This view provides no information about a token\'s fitness for a particular use.
         * @param {TokenVerifyRequest} tokenVerifyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        tokenVerifyCreate(tokenVerifyRequest: TokenVerifyRequest, options?: any): AxiosPromise<void> {
            return localVarFp.tokenVerifyCreate(tokenVerifyRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * TokenApi - interface
 * @export
 * @interface TokenApi
 */
export interface TokenApiInterface {
    /**
     * Takes a set of user credentials and returns an access and refresh JSON web token pair to prove the authentication of those credentials.
     * @param {TokenObtainPairRequest} tokenObtainPairRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TokenApiInterface
     */
    tokenCreate(tokenObtainPairRequest: TokenObtainPairRequest, options?: AxiosRequestConfig): AxiosPromise<TokenObtainPair>;

    /**
     * Takes a refresh type JSON web token and returns an access type JSON web token if the refresh token is valid.
     * @param {TokenRefreshRequest} tokenRefreshRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TokenApiInterface
     */
    tokenRefreshCreate(tokenRefreshRequest: TokenRefreshRequest, options?: AxiosRequestConfig): AxiosPromise<TokenRefresh>;

    /**
     * Takes a token and indicates if it is valid.  This view provides no information about a token\'s fitness for a particular use.
     * @param {TokenVerifyRequest} tokenVerifyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TokenApiInterface
     */
    tokenVerifyCreate(tokenVerifyRequest: TokenVerifyRequest, options?: AxiosRequestConfig): AxiosPromise<void>;

}

/**
 * Request parameters for tokenCreate operation in TokenApi.
 * @export
 * @interface TokenApiTokenCreateRequest
 */
export interface TokenApiTokenCreateRequest {
    /**
     * 
     * @type {TokenObtainPairRequest}
     * @memberof TokenApiTokenCreate
     */
    readonly tokenObtainPairRequest: TokenObtainPairRequest
}

/**
 * Request parameters for tokenRefreshCreate operation in TokenApi.
 * @export
 * @interface TokenApiTokenRefreshCreateRequest
 */
export interface TokenApiTokenRefreshCreateRequest {
    /**
     * 
     * @type {TokenRefreshRequest}
     * @memberof TokenApiTokenRefreshCreate
     */
    readonly tokenRefreshRequest: TokenRefreshRequest
}

/**
 * Request parameters for tokenVerifyCreate operation in TokenApi.
 * @export
 * @interface TokenApiTokenVerifyCreateRequest
 */
export interface TokenApiTokenVerifyCreateRequest {
    /**
     * 
     * @type {TokenVerifyRequest}
     * @memberof TokenApiTokenVerifyCreate
     */
    readonly tokenVerifyRequest: TokenVerifyRequest
}

/**
 * TokenApi - object-oriented interface
 * @export
 * @class TokenApi
 * @extends {BaseAPI}
 */
export class TokenApi extends BaseAPI implements TokenApiInterface {
    /**
     * Takes a set of user credentials and returns an access and refresh JSON web token pair to prove the authentication of those credentials.
     * @param {TokenApiTokenCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TokenApi
     */
    public tokenCreate(requestParameters: TokenApiTokenCreateRequest, options?: AxiosRequestConfig) {
        return TokenApiFp(this.configuration).tokenCreate(requestParameters.tokenObtainPairRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Takes a refresh type JSON web token and returns an access type JSON web token if the refresh token is valid.
     * @param {TokenApiTokenRefreshCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TokenApi
     */
    public tokenRefreshCreate(requestParameters: TokenApiTokenRefreshCreateRequest, options?: AxiosRequestConfig) {
        return TokenApiFp(this.configuration).tokenRefreshCreate(requestParameters.tokenRefreshRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Takes a token and indicates if it is valid.  This view provides no information about a token\'s fitness for a particular use.
     * @param {TokenApiTokenVerifyCreateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TokenApi
     */
    public tokenVerifyCreate(requestParameters: TokenApiTokenVerifyCreateRequest, options?: AxiosRequestConfig) {
        return TokenApiFp(this.configuration).tokenVerifyCreate(requestParameters.tokenVerifyRequest, options).then((request) => request(this.axios, this.basePath));
    }
}
